// InstaGuide.ai - Web Dashboard
// Frontend React Application

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import DocumentationPage from './DocumentationPage';
// PrivacyPage moved to landing site (instaguide.ai/privacy)
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  Handle,
  useNodesState,
  useEdgesState,
  MarkerType,
  Position
} from 'reactflow';
import 'reactflow/dist/style.css';

const API_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:3001';

// Optional override for the meeting-import upload endpoint. Set to
// bypass Cloudflare (which has a 100 MB body limit on Free/Pro and
// 200 MB on Business) for large video uploads. Typical prod setup:
//   1. DNS: upload.instaguide.ai → same origin, grey cloud (DNS-only, not proxied)
//   2. Azure App Service Custom Domains: add upload.instaguide.ai with a managed cert
//   3. Build with REACT_APP_MEETING_UPLOAD_API_URL=https://upload.instaguide.ai
// When unset, uploads go to API_URL like everything else. Only the
// multipart POST to /api/meeting-imports uses this — status polling,
// review-page fetches, and analyze-endpoint stay on the main domain
// so they still benefit from Cloudflare's CDN + WAF.
const MEETING_UPLOAD_API_URL = process.env.REACT_APP_MEETING_UPLOAD_API_URL || API_URL;

// ============================================
// Guide-card / list-row date formatting helpers
// ============================================
// Tile view shows the short form ("Jun 26") with the long form in the tooltip.
// List view shows date + time-to-minute ("Jun 26, 2026, 3:45 PM") — the
// `title` tooltip uses the most-detailed localized format.
function formatGuideDateShort(d) {
  if (!d) return '-';
  const t = new Date(d);
  if (Number.isNaN(t.getTime())) return '-';
  return t.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
function formatGuideDateWithTime(d) {
  if (!d) return '-';
  const t = new Date(d);
  if (Number.isNaN(t.getTime())) return '-';
  return t.toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}
function formatGuideDateTooltip(d) {
  if (!d) return '';
  const t = new Date(d);
  if (Number.isNaN(t.getTime())) return '';
  // Full localized weekday + date + time-to-minute, used as the
  // browser tooltip on date cells. Seconds intentionally omitted —
  // the user asked for "until min, no seconds".
  return t.toLocaleString(undefined, {
    weekday: 'long',
    year: 'numeric', month: 'long', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

// ============================================
// THEME CONFIGURATION - Change CURRENT_THEME to switch colors
// ============================================
// Single professional theme — deep indigo
const theme = {
  name: 'Indigo',
  primary: '#6366f1',
  secondary: '#4f46e5',
  gradient: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
  gradientLight: 'linear-gradient(135deg, #6366f115 0%, #4f46e515 100%)',
};

// Legacy THEMES kept for backward compatibility (extension sync, stored preferences)
const THEMES = { indigo: theme, purple: theme, ocean: theme, emerald: theme, slate: theme, blue: theme, blueGreen: theme };
const CURRENT_THEME = 'indigo';

// Chrome Web Store URL for the InstaGuide extension
const CHROME_STORE_URL = 'https://chromewebstore.google.com/detail/ghjacplbijhbgbijepboadpdlpgladpn';
// ============================================

// Custom Confirm Dialog Component
function ConfirmDialog({ title, message, confirmText, cancelText, type, onConfirm, onCancel }) {
  const inputRef = useRef(null);

  useEffect(() => {
    // Focus the confirm button when dialog opens
    inputRef.current?.focus();

    // Handle Escape key
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onCancel();
      if (e.key === 'Enter') onConfirm();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCancel, onConfirm]);

  // Inline SVG icons — replaces the heavy filled-square glyph.
  // Trash for danger (delete flows), checkmark for success, info-circle
  // for the default/prompt case. Rendered with a soft tinted background
  // instead of a saturated gradient so delete dialogs no longer read
  // as an alarm.
  const iconSvg = type === 'danger' ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="3 6 5 6 21 6"/>
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
      <path d="M10 11v6"/>
      <path d="M14 11v6"/>
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    </svg>
  ) : type === 'success' ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10"/>
      <line x1="12" y1="8" x2="12" y2="12"/>
      <line x1="12" y1="16" x2="12" y2="16"/>
    </svg>
  );

  return (
    <div className="custom-dialog-overlay" onClick={onCancel}>
      <div className="custom-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="custom-dialog-header">
          <div className={`custom-dialog-icon ${type}`}>
            {iconSvg}
          </div>
          <h3 className="custom-dialog-title">{title}</h3>
        </div>
        <div className="custom-dialog-body">
          <p className="custom-dialog-message">{message}</p>
        </div>
        <div className="custom-dialog-footer">
          <button className="custom-dialog-btn custom-dialog-btn-cancel" onClick={onCancel}>
            {cancelText}
          </button>
          <button
            ref={inputRef}
            className={`custom-dialog-btn ${type === 'danger' ? 'custom-dialog-btn-danger' : 'custom-dialog-btn-confirm'}`}
            onClick={onConfirm}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

// Plan Upgrade Prompt Dialog — shown when a server endpoint returns 403 with
// code PLAN_LIMIT or FEATURE_NOT_AVAILABLE. Click "View Plans" opens the
// pricing page in a new tab; "Maybe later" simply dismisses.
function UpgradePromptDialog({ title, message, upgradeUrl, onClose, onUpgrade }) {
  const primaryRef = useRef(null);

  useEffect(() => {
    primaryRef.current?.focus();
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div className="custom-dialog-overlay" onClick={onClose}>
      <style>{`
        .upgrade-dialog {
          background: #fff;
          border-radius: 14px;
          max-width: 460px;
          width: 92%;
          box-shadow: 0 24px 60px rgba(15, 23, 42, 0.25), 0 0 0 1px rgba(15, 23, 42, 0.04);
          overflow: hidden;
          animation: slideIn 0.2s ease;
        }
        .upgrade-dialog-banner {
          /* Dark navy panel — matches the sidebar / hero surface treatment
           * used elsewhere in the app, so the indigo-gradient favicon tile
           * sitting on top pops the same way the brand logo does in the
           * sidebar. */
          background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
          color: #fff;
          padding: 24px 24px 20px;
          position: relative;
          overflow: hidden;
        }
        .upgrade-dialog-banner::before {
          content: '';
          position: absolute;
          top: -60px; right: -50px;
          width: 220px; height: 220px;
          background: radial-gradient(circle, rgba(99,102,241,0.28) 0%, transparent 65%);
          pointer-events: none;
        }
        .upgrade-dialog-icon {
          width: 44px; height: 44px;
          border-radius: 11px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 12px;
          position: relative;
          overflow: hidden;
        }
        .upgrade-dialog-icon img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .upgrade-dialog-title {
          font-size: 18px;
          font-weight: 700;
          margin: 0;
          line-height: 1.3;
          letter-spacing: -0.2px;
          position: relative;
        }
        .upgrade-dialog-body {
          padding: 18px 24px 4px;
          color: #475569;
          font-size: 14px;
          line-height: 1.55;
        }
        .upgrade-dialog-footer {
          display: flex;
          justify-content: flex-end;
          gap: 8px;
          padding: 16px 24px 20px;
        }
        .upgrade-dialog-btn {
          padding: 10px 18px;
          border: none;
          border-radius: 9px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          font-family: inherit;
          transition: all 0.15s;
        }
        .upgrade-dialog-btn-secondary {
          background: #f1f5f9;
          color: #475569;
        }
        .upgrade-dialog-btn-secondary:hover { background: #e2e8f0; color: #0f172a; }
        .upgrade-dialog-btn-primary {
          background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
          color: #fff;
          box-shadow: 0 4px 14px -2px rgba(99, 102, 241, 0.45);
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .upgrade-dialog-btn-primary:hover {
          transform: translateY(-1px);
          box-shadow: 0 8px 20px -2px rgba(99, 102, 241, 0.55);
        }
      `}</style>
      <div className="upgrade-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="upgrade-dialog-banner">
          <div className="upgrade-dialog-icon">
            <img src="/favicon.png" alt="" aria-hidden="true" />
          </div>
          <h3 className="upgrade-dialog-title">{title}</h3>
        </div>
        <div className="upgrade-dialog-body">
          <p>{message}</p>
        </div>
        <div className="upgrade-dialog-footer">
          <button className="upgrade-dialog-btn upgrade-dialog-btn-secondary" onClick={onClose}>Maybe later</button>
          <button
            ref={primaryRef}
            className="upgrade-dialog-btn upgrade-dialog-btn-primary"
            onClick={() => {
              onClose();
              // If parent provided an in-app handler (router-aware), use it.
              // Otherwise fall back to opening the upgradeUrl in a new tab.
              if (onUpgrade) {
                onUpgrade();
              } else {
                window.open(upgradeUrl || 'https://instaguide.ai/pricing', '_blank', 'noopener,noreferrer');
              }
            }}
          >
            View Plans
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M7 17L17 7M17 7H7M17 7V17"/></svg>
          </button>
        </div>
      </div>
    </div>
  );
}

// Custom Prompt Dialog Component
function PromptDialog({ title, message, defaultValue, placeholder, submitText, cancelText, onSubmit, onCancel }) {
  const [value, setValue] = useState(defaultValue);
  const inputRef = useRef(null);

  useEffect(() => {
    // Focus the input when dialog opens
    inputRef.current?.focus();
    inputRef.current?.select();

    // Handle Escape key
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  const handleSubmit = () => {
    if (value.trim()) {
      onSubmit(value.trim());
    }
  };

  return (
    <div className="custom-dialog-overlay" onClick={onCancel}>
      <div className="custom-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="custom-dialog-header">
          <div className="custom-dialog-icon prompt">
            <span style={{ color: 'white' }}>+</span>
          </div>
          <h3 className="custom-dialog-title">{title}</h3>
        </div>
        <div className="custom-dialog-body">
          {message && <p className="custom-dialog-message">{message}</p>}
          <input
            ref={inputRef}
            type="text"
            className="custom-dialog-input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSubmit();
            }}
          />
        </div>
        <div className="custom-dialog-footer">
          <button className="custom-dialog-btn custom-dialog-btn-cancel" onClick={onCancel}>
            {cancelText}
          </button>
          <button
            className="custom-dialog-btn custom-dialog-btn-confirm"
            onClick={handleSubmit}
            disabled={!value.trim()}
          >
            {submitText}
          </button>
        </div>
      </div>
    </div>
  );
}

// Toast Notification Component

function ToastContainer({ toasts, removeToast }) {
  return (
    <div className="toast-container">
      <style>{`
        .toast-container {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 10000;
          display: flex;
          flex-direction: column;
          gap: 10px;
          pointer-events: none;
        }
        .toast {
          min-width: 300px;
          max-width: 450px;
          padding: 14px 18px;
          border-radius: 10px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          display: flex;
          align-items: flex-start;
          gap: 12px;
          animation: toastSlideIn 0.3s ease;
          pointer-events: auto;
        }
        .toast.exiting {
          animation: toastSlideOut 0.3s ease forwards;
        }
        @keyframes toastSlideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes toastSlideOut {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(100%); opacity: 0; }
        }
        .toast-success {
          background: #ecfdf5;
          border: 1px solid #a7f3d0;
          color: #065f46;
        }
        .toast-error {
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #991b1b;
        }
        .toast-warning {
          background: #fffbeb;
          border: 1px solid #fde68a;
          color: #92400e;
        }
        .toast-info {
          background: #eff6ff;
          border: 1px solid #bfdbfe;
          color: #1e40af;
        }
        .toast-icon {
          flex-shrink: 0;
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .toast-content {
          flex: 1;
          font-size: 14px;
          line-height: 1.5;
        }
        .toast-title {
          font-weight: 600;
          margin-bottom: 2px;
        }
        .toast-message {
          opacity: 0.9;
        }
        .toast-close {
          flex-shrink: 0;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
          opacity: 0.5;
          transition: opacity 0.2s;
          font-size: 18px;
          line-height: 1;
        }
        .toast-close:hover {
          opacity: 1;
        }
      `}</style>
      {toasts.map((toast) => (
        <div key={toast.id} className={`toast toast-${toast.type} ${toast.exiting ? 'exiting' : ''}`}>
          <div className="toast-icon">
            {toast.type === 'success' && (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M20 6L9 17l-5-5"/>
              </svg>
            )}
            {toast.type === 'error' && (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
            )}
            {toast.type === 'warning' && (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            )}
            {toast.type === 'info' && (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="16" x2="12" y2="12"/>
                <line x1="12" y1="8" x2="12.01" y2="8"/>
              </svg>
            )}
          </div>
          <div className="toast-content">
            {toast.title && <div className="toast-title">{toast.title}</div>}
            <div className="toast-message">{toast.message}</div>
          </div>
          <button className="toast-close" onClick={() => removeToast(toast.id)}>×</button>
        </div>
      ))}
    </div>
  );
}

// Cache helpers — stale-while-revalidate pattern. On every successful loadGuides
// we persist guides/sharedGuides/projects to localStorage, and on app mount we
// hydrate state from cache so returning users see their data instantly while a
// fresh fetch happens in the background.
const CACHE_KEY = 'instaguide-cache-v1';
const readCachedDashboard = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch { return null; }
};
const writeCachedDashboard = (data) => {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch {}
};
const clearCachedDashboard = () => { try { localStorage.removeItem(CACHE_KEY); } catch {} };

// ============================================
// EMBED VIEW — public, iframeable, token-gated
// ============================================
//
// Renders a minimal step-list viewer for a single guide inside an iframe
// hosted on third-party sites (Zendesk, Notion, customer help centers).
// No app chrome, no auth, no edit controls — just screenshots, descriptions,
// click indicators, and (optionally) the process flow. Driven entirely by
// the URL: /embed/<guideId>?t=<token>[&theme=light|dark][&flow=hide].
//
// Branding overrides (logo + primary color) come from the guide's
// embed_settings when the owner is on Enterprise. Light/dark theme can be
// forced via URL param or fall back to the owner's saved default.
function EmbedView({ guideId, token, themeOverride, hideFlowOverride }) {
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!guideId || !token) {
      setError("This embed link is missing required information.");
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/embed/guides/${encodeURIComponent(guideId)}?t=${encodeURIComponent(token)}`);
        if (!r.ok) {
          setError(r.status === 404
            ? "This guide isn't available for embedding. The owner may have disabled the embed or revoked the link."
            : `Failed to load (HTTP ${r.status})`);
        } else {
          setGuide(await r.json());
        }
      } catch (e) {
        setError(e.message || 'Network error');
      } finally {
        setLoading(false);
      }
    })();
  }, [guideId, token]);

  // Effective theme: URL override > guide owner's saved default > light.
  const savedTheme = guide?.embedSettings?.theme || 'light';
  const theme = (themeOverride === 'light' || themeOverride === 'dark') ? themeOverride : savedTheme;
  // hideFlow: URL override true (?flow=hide) wins; otherwise owner's saved default.
  const hideFlow = hideFlowOverride || !!guide?.embedSettings?.hideFlow;
  const primary = guide?.embedSettings?.brandingPrimaryColor || '#6366f1';
  const logoUrl = guide?.embedSettings?.brandingLogoUrl || null;

  const isDark = theme === 'dark';
  const bg = isDark ? '#0f172a' : '#f8fafc';
  const surface = isDark ? '#1e293b' : '#ffffff';
  const textColor = isDark ? '#e2e8f0' : '#1e293b';
  const mutedText = isDark ? '#94a3b8' : '#64748b';
  const borderColor = isDark ? '#334155' : '#e2e8f0';

  if (loading) {
    return (
      <div style={{ background: bg, color: textColor, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          <svg width="32" height="32" viewBox="0 0 50 50" aria-hidden="true">
            <circle cx="25" cy="25" r="20" fill="none" stroke={primary} strokeOpacity="0.2" strokeWidth="4"/>
            <circle cx="25" cy="25" r="20" fill="none" stroke={primary} strokeWidth="4" strokeLinecap="round" strokeDasharray="80 200">
              <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="0.9s" repeatCount="indefinite"/>
            </circle>
          </svg>
          <span style={{ fontSize: 13, color: mutedText }}>Loading guide…</span>
        </div>
      </div>
    );
  }

  if (error || !guide) {
    return (
      <div style={{ background: bg, color: textColor, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', padding: 24 }}>
        <div style={{ background: surface, border: `1px solid ${borderColor}`, borderRadius: 12, padding: 24, maxWidth: 480, textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>🔒</div>
          <h2 style={{ margin: '0 0 8px', fontSize: 18, color: textColor }}>Guide unavailable</h2>
          <p style={{ margin: 0, color: mutedText, fontSize: 13, lineHeight: 1.5 }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: bg,
      color: textColor,
      minHeight: '100vh',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      padding: '0 20px 32px',
    }}>
      {/* Embed header — brand mark, guide name. Custom logo overrides our
          brand mark when the owner's on Enterprise + has set one. */}
      <header style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '16px 0',
        borderBottom: `1px solid ${borderColor}`,
        marginBottom: 24,
      }}>
        {logoUrl ? (
          <img src={logoUrl} alt="" style={{ width: 32, height: 32, borderRadius: 6, objectFit: 'cover' }} />
        ) : (
          <span style={{
            width: 32, height: 32, borderRadius: 8,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            background: `linear-gradient(135deg, ${primary}, ${primary})`,
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="white" aria-hidden="true">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
            </svg>
          </span>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: textColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {guide.name}
          </div>
          {guide.domain && (
            <div style={{ fontSize: 12, color: mutedText, marginTop: 2 }}>{guide.domain}</div>
          )}
        </div>
        <a
          href="https://instaguide.ai"
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontSize: 11, color: mutedText, textDecoration: 'none' }}
        >
          Made with InstaGuide
        </a>
      </header>

      {/* Description (if set) */}
      {guide.description && (
        <p style={{ margin: '0 0 24px', color: mutedText, fontSize: 14, lineHeight: 1.6 }}>
          {guide.description}
        </p>
      )}

      {/* Process flow diagram — shown by default for any guide with cached
          workflowData. Hidden when the owner toggled off in embed settings
          or when ?flow=hide is in the URL. WorkflowFlowchart short-circuits
          on cached data (no API call) so it works in the no-auth embed
          context as long as the guide has generated workflowData. */}
      {!hideFlow && guide.workflowData?.summaries?.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <WorkflowFlowchart
            steps={guide.steps || []}
            guideId={guide.id}
            workflowData={guide.workflowData}
            onStepClick={() => {}}
          />
        </div>
      )}

      {/* Steps — minimal step-card list. Reuses the same indigo/amber/green
          palette for the step-number badge so the embed feels consistent
          with the dashboard. Renders ALL step types (click + manual). */}
      <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 24 }}>
        {(() => {
          // Non-consuming numbering — section dividers don't consume a
          // step number in the visible sequence. Same logic as the
          // dashboard renderer above so the embed reads identically.
          let n = 0;
          return (guide.steps || []).map((step) => {
            const isSection = (step.stepType || 'click') === 'section_divider';
            if (!isSection) n += 1;
            const withDisplay = { ...step, displayNumber: isSection ? null : n };
            return (
              <EmbedStep
                key={step.id}
                step={withDisplay}
                primary={primary}
                surface={surface}
                textColor={textColor}
                mutedText={mutedText}
                borderColor={borderColor}
                isDark={isDark}
              />
            );
          });
        })()}
      </ol>
    </div>
  );
}

// Detects the required-field marker (trailing `*` inside a quoted UI
// element name) that web forms conventionally use to mark required
// inputs. Extension capture faithfully includes the asterisk in the
// clicked-element text, which reads as visual clutter in generated
// action labels — e.g. `Click on "First Name *"`. This helper strips
// the asterisk from the visible text and returns an `isRequired` flag
// the renderer uses to show a small red "Required" pill instead.
//
// Purely a render-time transform — no DB migration, applies to
// existing guides retroactively. Only strips asterisks that sit at
// the trailing boundary of a quoted label (with optional whitespace),
// so mid-word asterisks and lone-asterisk labels (`Enter "*"`) are
// preserved.
function parseRequiredFromAction(action) {
  if (typeof action !== 'string' || !action) return { text: action || '', isRequired: false };
  const RE = /"([^"]*?)\s*\*"/g;
  if (!RE.test(action)) return { text: action, isRequired: false };
  // Reset lastIndex — /g regex is stateful when reused.
  RE.lastIndex = 0;
  const text = action.replace(RE, '"$1"');
  return { text, isRequired: true };
}

function EmbedStep({ step, primary, surface, textColor, mutedText, borderColor, isDark }) {
  const sType = step.stepType || 'click';

  // Step-number badge color — mirrors the PDF/HTML export palette.
  let badge = `linear-gradient(135deg, ${primary}, ${primary})`;
  if (step.eventType === 'keyboard') badge = 'linear-gradient(135deg, #f59e0b, #d97706)';
  else if (step.eventType === 'scroll') badge = 'linear-gradient(135deg, #10b981, #059669)';
  if (sType === 'rich_text') badge = 'linear-gradient(135deg, #818cf8, #6366f1)';
  else if (sType === 'callout') badge = 'linear-gradient(135deg, #fbbf24, #d97706)';
  else if (sType === 'image') badge = 'linear-gradient(135deg, #2dd4bf, #0f766e)';
  else if (sType === 'audio_note') badge = 'linear-gradient(135deg, #a78bfa, #7c3aed)';
  else if (sType === 'section_divider') badge = 'linear-gradient(135deg, #94a3b8, #475569)';

  // Click indicator overlay (SVG with viewport-scaled coords) — same shape
  // the dashboard renders for click steps.
  let clickOverlay = null;
  if (sType === 'click' && step.eventType === 'click' && step.clickData) {
    const { x, y, viewportSize } = step.clickData;
    if (viewportSize) {
      const dpr = viewportSize.devicePixelRatio || 1;
      clickOverlay = (
        <svg
          viewBox={`0 0 ${viewportSize.width * dpr} ${viewportSize.height * dpr}`}
          preserveAspectRatio="none"
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
        >
          <circle cx={x * dpr} cy={y * dpr} r={22 * dpr} fill="none" stroke="#ef4444" strokeWidth={3 * dpr} opacity="0.7"/>
          <circle cx={x * dpr} cy={y * dpr} r={8 * dpr} fill="#ef4444"/>
        </svg>
      );
    }
  }

  // Section dividers render as chapter breaks — no number badge, no
  // step-card chrome. Left accent bar in the primary color, bold H2,
  // optional description below. Consistent with the dashboard's
  // .step-type-section_divider styling so all three surfaces
  // (dashboard, embed, export) read the same way.
  if (sType === 'section_divider') {
    return (
      <li style={{
        display: 'block',
        listStyle: 'none',
        margin: '28px 0 8px',
        paddingLeft: 16,
        borderLeft: `4px solid ${primary}`,
      }}>
        {step.action && (
          <h3 style={{
            margin: 0, padding: 0,
            fontSize: 22, fontWeight: 800,
            color: textColor,
            letterSpacing: '-0.3px',
          }}>
            {step.action}
          </h3>
        )}
        {step.description && (
          <p style={{ margin: '4px 0 0', fontSize: 13.5, color: mutedText, lineHeight: 1.5 }}>
            {step.description}
          </p>
        )}
      </li>
    );
  }

  return (
    <li style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      <div style={{
        flexShrink: 0,
        width: 36, height: 36, borderRadius: '50%',
        background: badge, color: 'white',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 700, fontSize: 14,
        boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
      }}>{step.displayNumber != null ? step.displayNumber : step.number}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        {step.action && (() => {
          const { text, isRequired } = parseRequiredFromAction(step.action);
          return (
            <h3 style={{ margin: '4px 0 8px', fontSize: 15, color: textColor, fontWeight: 700 }}>
              {text}
              {isRequired && (
                <span style={{
                  display: 'inline-flex',
                  padding: '2px 7px',
                  borderRadius: 4,
                  background: '#fef2f2',
                  color: '#dc2626',
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginLeft: 8,
                  border: '1px solid #fecaca',
                  verticalAlign: 'middle',
                }}>Required</span>
              )}
            </h3>
          );
        })()}
        {/* Body content varies by step type */}
        {sType === 'click' && step.screenshot && (
          <div style={{
            position: 'relative',
            borderRadius: 8,
            border: `1px solid ${borderColor}`,
            overflow: 'hidden',
            background: isDark ? '#0f172a' : '#fff',
          }}>
            <img src={step.screenshot} alt={step.action || ''} style={{ display: 'block', width: '100%' }} />
            {clickOverlay}
          </div>
        )}
        {sType === 'image' && step.screenshot && (
          <img src={step.screenshot} alt={step.action || ''} style={{ display: 'block', maxWidth: '100%', borderRadius: 8, border: `1px solid ${borderColor}` }} />
        )}
        {sType === 'audio_note' && step.audioUrl && (
          <audio src={step.audioUrl} controls style={{ width: '100%', marginTop: 6 }} />
        )}
        {sType === 'callout' && (
          <div style={{
            padding: '10px 14px', borderRadius: 8,
            background: step.severity === 'critical' ? '#fef2f2'
              : step.severity === 'warning' ? '#fffbeb' : '#eff6ff',
            borderLeft: `4px solid ${step.severity === 'critical' ? '#ef4444'
              : step.severity === 'warning' ? '#f59e0b' : '#3b82f6'}`,
            color: step.severity === 'critical' ? '#7f1d1d'
              : step.severity === 'warning' ? '#78350f' : '#1e3a8a',
            fontSize: 14, lineHeight: 1.55,
          }}>
            {step.description}
          </div>
        )}
        {sType === 'rich_text' && step.description && (
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.7, color: textColor, whiteSpace: 'pre-wrap' }}>
            {step.description}
          </p>
        )}
        {/* section_divider rendered above via early-return; falls through here for others. */}
        {(sType === 'click' || sType === 'image' || sType === 'audio_note') && step.description && (
          <p style={{ margin: '12px 0 0', fontSize: 14, lineHeight: 1.65, color: mutedText, whiteSpace: 'pre-wrap' }}>
            {step.description}
          </p>
        )}
      </div>
    </li>
  );
}

export default function App() {
  const cachedDashboard = readCachedDashboard();
  // Hydrate the pending-deletes set from sessionStorage before any UI
  // state derives from it, so the initial cached guides list is
  // already filtered on first paint. Otherwise the just-deleted guide
  // would flash in for one render before loadGuides() overwrites the
  // list with the filtered fresh copy.
  const initialPendingDeletes = (() => {
    try {
      const raw = sessionStorage.getItem('instaguide-pending-deletes-v1');
      if (!raw) return new Set();
      const arr = JSON.parse(raw);
      if (!Array.isArray(arr)) return new Set();
      const now = Date.now();
      const fresh = arr.filter((r) => r && r.id && (now - (r.at || 0)) < (10 * 60 * 1000));
      return new Set(fresh.map((r) => r.id));
    } catch (_) { return new Set(); }
  })();
  const [user, setUser] = useState(null);
  const [planFeatures, setPlanFeatures] = useState({});
  const [planGuideLimit, setPlanGuideLimit] = useState(null); // effective guide limit from server (null = use fallback)
  const [guides, setGuides] = useState(() => {
    const cached = cachedDashboard?.guides || [];
    if (initialPendingDeletes.size === 0) return cached;
    return cached.filter((g) => !initialPendingDeletes.has(g.id));
  });
  const [selectedGuide, setSelectedGuide] = useState(null);
  // ID of the meeting import the user is currently reviewing segments for.
  // Set by ImportsPage's "Review segments" button; cleared when the user
  // cancels or submits their picks (which moves the row to `processing`).
  const [reviewingImportId, setReviewingImportId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(() => {
    const path = window.location.pathname.replace(/\/$/, '');
    if (path === '/forgot-password') return 'forgot-password';
    if (path.match(/^\/reset-password\/[^/]+$/)) return 'reset-password';
    if (path === '/2fa-forgot') return '2fa-forgot';
    if (path.match(/^\/2fa-recovery\/[^/]+$/)) return '2fa-recovery';
    if (path === '/signup') return 'signup';
    if (path.match(/^\/verify-email\/[^/]+$/)) return 'verify-email';
    return 'login';
  });
  const [resetToken, setResetToken] = useState(() => {
    const path = window.location.pathname.replace(/\/$/, '');
    const m1 = path.match(/^\/reset-password\/([^/]+)$/);
    if (m1) return m1[1];
    const m2 = path.match(/^\/2fa-recovery\/([^/]+)$/);
    if (m2) return m2[1];
    return null;
  });
  // Raw verification token from /verify-email/<token>. Consumed by
  // VerifyEmailView on mount which POSTs it to /api/auth/verify-email;
  // company-email signups get a session token back + auto-login,
  // personal-email signups see a "waiting for admin" confirmation.
  const [verifyEmailToken, setVerifyEmailToken] = useState(() => {
    const path = window.location.pathname.replace(/\/$/, '');
    const m = path.match(/^\/verify-email\/([^/]+)$/);
    return m ? m[1] : null;
  });
  const [invitationData, setInvitationData] = useState(null);
  // 2FA challenge state — set after a successful password check; cleared
  // once the challenge token is exchanged for a real session token (or on
  // logout / cancel). Lives in component state, never localStorage.
  const [mfaChallenge, setMfaChallenge] = useState(null); // { token, email, mode: 'setup'|'verify' }
  const [projects, setProjects] = useState(() => cachedDashboard?.projects || []);
  const [selectedProjectId, setSelectedProjectId] = useState('all');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentPage, setCurrentPage] = useState(() => {
    const p = window.location.pathname.replace(/\/$/, '');
    if (p === '/privacy') { window.location.href = 'https://instaguide.ai/privacy'; return 'home'; }
    if (p === '/docs') return 'docs';
    if (p === '/billing/success') return 'billing-success';
    // Auth / guide-detail / invite paths are handled by their respective views,
    // not by currentPage — leave home as the default for those.
    if (p === '' || p === '/' || p === '/login' || p === '/forgot-password' || p === '/2fa-forgot' || p.startsWith('/guide/') || p.startsWith('/invite/') || p.startsWith('/reset-password/') || p.startsWith('/2fa-recovery/')) return 'home';
    // Otherwise treat the first path segment as the page id (e.g. /myguides → 'myguides')
    return p.replace(/^\//, '').split('/')[0] || 'home';
  });
  const [recentGuides, setRecentGuides] = useState([]);
  const [favoriteGuideIds, setFavoriteGuideIds] = useState([]);

  // Shared filter state across all guide pages
  const [sharedSourceFilter, setSharedSourceFilter] = useState('all');
  const [sharedOwnershipFilter, setSharedOwnershipFilter] = useState('all');
  const [sharedProjectFilter, setSharedProjectFilter] = useState('all');
  const [sharedSortBy, setSharedSortBy] = useState('newest');
  const [sharedSearchQuery, setSharedSearchQuery] = useState('');

  // Toast notifications state
  const [toasts, setToasts] = useState([]);
  const toastIdRef = useRef(0);
  const prevPageRef = useRef(currentPage);

  const showToast = useCallback((message, type = 'info', title = null, duration = 5000) => {
    const id = ++toastIdRef.current;
    setToasts(prev => [...prev, { id, message, type, title }]);

    // Auto remove after duration
    if (duration > 0) {
      setTimeout(() => {
        setToasts(prev => prev.map(t => t.id === id ? { ...t, exiting: true } : t));
        setTimeout(() => {
          setToasts(prev => prev.filter(t => t.id !== id));
        }, 300);
      }, duration);
    }
  }, []);

  // Background meeting-import uploads. Kicked off from the
  // MeetingImportModal (which closes immediately after Start),
  // continue running here at App scope so the user can navigate
  // freely while the video streams to Azure Blob (or falls back to
  // legacy multipart when Blob isn't configured). The ImportsPage
  // IN PROGRESS panel subscribes via prop and renders active
  // uploads as imp-rows, so the upload + server-processing halves
  // live in one unified list instead of a separate floating widget.
  const [backgroundUploads, setBackgroundUploads] = useState([]);
  const uploadIdRef = useRef(0);

  const startBackgroundUpload = useCallback(({ video, transcript, meetingTopic, appHint, reviewMode, talkOnly, projectId, onSubmitted }) => {
    if (!video) return;
    const localId = `bgu-${++uploadIdRef.current}`;
    const token = localStorage.getItem('authToken');

    setBackgroundUploads((prev) => [
      ...prev,
      {
        localId,
        filename: video.name,
        sizeBytes: video.size,
        phase: 'starting',
        progress: 0,
        error: null,
        xhr: null,
      },
    ]);

    const update = (patch) => {
      setBackgroundUploads((prev) => prev.map((u) => (u.localId === localId ? { ...u, ...patch } : u)));
    };

    (async () => {
      try {
        // ── Step 1: ask backend for a SAS URL ──
        const urlRes = await fetch(`${API_URL}/api/meeting-imports/upload-url`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ filename: video.name, sizeBytes: video.size }),
        });
        const urlData = await urlRes.json().catch(() => ({}));

        // Fallback path — Blob not configured (local dev, or Azure auth
        // failed). Upload via legacy multipart. No progress events on
        // this path — show an indeterminate "uploading" state.
        if (urlRes.status === 503 && urlData?.code === 'BLOB_NOT_CONFIGURED') {
          update({ phase: 'uploading', progress: null });
          const fd = new FormData();
          fd.append('video', video);
          if (transcript) fd.append('transcript', transcript);
          if (meetingTopic && meetingTopic.trim()) fd.append('meetingTopic', meetingTopic.trim());
          if (appHint && appHint.trim()) fd.append('appOrDomainHint', appHint.trim());
          if (reviewMode) fd.append('reviewMode', 'true');
          if (talkOnly) fd.append('talkOnly', 'true');
          if (projectId) fd.append('projectId', projectId);
          const legacyRes = await fetch(`${MEETING_UPLOAD_API_URL}/api/meeting-imports`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: fd,
          });
          const legacyData = await legacyRes.json().catch(() => ({}));
          if (!legacyRes.ok) throw new Error(legacyData?.error || 'Upload failed');
          update({ phase: 'complete', progress: 100 });
          try { onSubmitted?.(legacyData); } catch { /* ignore */ }
          showToast(
            reviewMode
              ? 'Upload received. When Insta finishes analyzing, we\'ll notify you to pick segments to include.'
              : 'Upload received — processing started.',
            'success'
          );
          return;
        }

        if (!urlRes.ok) throw new Error(urlData?.error || 'Could not start upload');

        // ── Step 2: upload to Azure Blob via SAS, with progress ──
        update({ phase: 'uploading', progress: 0 });
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('PUT', urlData.uploadUrl);
          Object.entries(urlData.requiredHeaders || { 'x-ms-blob-type': 'BlockBlob' }).forEach(([k, v]) => {
            xhr.setRequestHeader(k, v);
          });
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) update({ progress: Math.round((e.loaded / e.total) * 100) });
          };
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) resolve();
            else reject(new Error(`Upload to storage failed (HTTP ${xhr.status})`));
          };
          xhr.onerror = () => reject(new Error('Network error during upload'));
          xhr.onabort = () => reject(new Error('Upload cancelled'));
          // Track xhr on the upload row so a cancel button can abort it.
          update({ xhr });
          xhr.send(video);
        });

        // ── Step 3: read transcript text (if any) ──
        let transcriptText = null;
        if (transcript) {
          try { transcriptText = await transcript.text(); } catch { /* skip */ }
        }

        // ── Step 4: finalize ──
        update({ phase: 'finalizing', progress: 100 });
        const finRes = await fetch(`${API_URL}/api/meeting-imports/finalize`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            importId: urlData.importId,
            blobPath: urlData.blobPath,
            filename: video.name,
            sizeBytes: video.size,
            transcriptText,
            meetingTopic: meetingTopic && meetingTopic.trim() || null,
            appOrDomainHint: appHint && appHint.trim() || null,
            reviewMode,
            talkOnly,
            projectId: projectId || null,
          }),
        });
        const finData = await finRes.json().catch(() => ({}));
        if (!finRes.ok) throw new Error(finData?.error || 'Finalize failed');

        update({ phase: 'complete', progress: 100 });
        try { onSubmitted?.(finData); } catch { /* ignore */ }
        showToast(
          reviewMode
            ? 'Upload complete. When Insta finishes analyzing, we\'ll notify you to pick segments.'
            : 'Upload complete — processing started.',
          'success'
        );
      } catch (err) {
        update({ phase: 'error', error: err.message || 'Upload failed' });
        showToast(`Upload failed: ${err.message || 'unknown error'}`, 'error');
      }
    })();

    return localId;
  }, [showToast]);

  const dismissBackgroundUpload = useCallback((localId) => {
    setBackgroundUploads((prev) => prev.filter((u) => u.localId !== localId));
  }, []);

  const cancelBackgroundUpload = useCallback((localId) => {
    setBackgroundUploads((prev) => {
      const target = prev.find((u) => u.localId === localId);
      if (target?.xhr && typeof target.xhr.abort === 'function') {
        try { target.xhr.abort(); } catch { /* best effort */ }
      }
      return prev.filter((u) => u.localId !== localId);
    });
  }, []);

  // Upgrade-prompt state — shown when the API returns a plan-related 403
  // (codes: PLAN_LIMIT, FEATURE_NOT_AVAILABLE). Tagged from the backend with
  // human-readable feature labels and a target upgrade URL.
  const [upgradePrompt, setUpgradePrompt] = useState({ open: false, title: '', message: '', upgradeUrl: 'https://instaguide.ai/pricing' });

  const showUpgradePrompt = useCallback(({ title, message, upgradeUrl }) => {
    setUpgradePrompt({
      open: true,
      title: title || 'Upgrade your plan',
      message: message || 'This feature is not available on your current plan.',
      upgradeUrl: upgradeUrl || 'https://instaguide.ai/pricing'
    });
  }, []);

  // Inspect a fetch Response: if it's a plan-related 403, surface the upgrade
  // prompt and return true so callers can short-circuit. Otherwise return false
  // and let the caller handle the error normally.
  const handlePlanError = useCallback(async (response) => {
    if (!response || response.ok) return false;
    if (response.status !== 403) return false;
    let body = null;
    try { body = await response.clone().json(); } catch { return false; }
    if (!body || (body.code !== 'PLAN_LIMIT' && body.code !== 'FEATURE_NOT_AVAILABLE')) return false;
    let title = 'Upgrade your plan';
    let message = body.error || 'This feature is not available on your current plan.';
    if (body.code === 'PLAN_LIMIT') {
      // Human-friendly scope labels. `guides_monthly` fires when an
      // Enterprise Managed org hits its `guidesPerLicensePerMonth × licenses`
      // fair-use cap (see server.js checkPlanLimits); other scopes flow
      // through as-is. Underscores in raw scope keys render awkwardly
      // ("guides_monthly limit") without this map.
      const SCOPE_LABELS = {
        guides:          'guide',
        guides_monthly:  'monthly guide creation',
        exports:         'export',
        usage:           'usage',
      };
      const scopeLabel = SCOPE_LABELS[body.scope] || body.scope || 'usage';
      title = `You've reached your plan's ${scopeLabel} limit`;
    } else if (body.featureLabel) {
      title = `Upgrade to unlock ${body.featureLabel}`;
    }
    showUpgradePrompt({ title, message, upgradeUrl: body.upgrade_url });
    return true;
  }, [showUpgradePrompt]);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.map(t => t.id === id ? { ...t, exiting: true } : t));
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 300);
  }, []);

  useEffect(() => {
    // Skip all auth/invite/guide setup on /embed/ paths — the embed view
    // is its own self-contained tree with its own data fetch, and any
    // background /api/auth/me or /api/guides calls here are wasted work
    // (and worse, can fire SSE connections that fight with the embed's
    // public token-auth).
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/embed/')) {
      setLoading(false);
      return;
    }
    // Impersonation deep-link — /impersonate/<jwt> arrives from the
    // License Admin panel's "Sign in as user" button. We stash the
    // current authToken (if any) as `preImpersonationToken` so the
    // "Return to admin" button can restore the admin's own web
    // session, then swap the impersonation token in as `authToken`
    // and reload cleanly at `/`. Return early to avoid running the
    // normal auth flow with the OLD token.
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/impersonate/')) {
      const t = decodeURIComponent(window.location.pathname.slice('/impersonate/'.length));
      if (t) {
        try {
          const prior = localStorage.getItem('authToken');
          if (prior && prior !== t) sessionStorage.setItem('preImpersonationToken', prior);
          localStorage.setItem('authToken', t);
        } catch { /* private-mode / quota — impersonation still works, just no restore */ }
      }
      // Hard-navigate so every module (SSE clients, guide cache, etc.)
      // re-initializes with the fresh token.
      window.location.replace('/');
      return;
    }
    checkInviteUrl();
    checkAuth();
    checkGuideUrl();
  }, []);

  // Check if URL is a guide deep link
  const checkGuideUrl = () => {
    const path = window.location.pathname.replace(/\/$/, '');
    const match = path.match(/^\/guide\/([^/]+)$/);
    if (match) {
      const guideId = match[1];
      // Store guideId to load after auth check completes
      sessionStorage.setItem('pendingGuideId', guideId);
      // Also capture ?step=N if present — Ask Insta's step-thumbnail
      // cards link with this shape so the guide can auto-scroll to the
      // cited step. Read here + consumed once the guide's steps have
      // rendered (see the scrollToStep effect on GuideDetailView).
      const params = new URLSearchParams(window.location.search);
      const stepParam = params.get('step');
      if (stepParam && /^\d+$/.test(stepParam)) {
        sessionStorage.setItem('pendingStepNumber', stepParam);
      } else {
        sessionStorage.removeItem('pendingStepNumber');
      }
    }
  };


  // SSE connection for real-time updates
  useEffect(() => {
    if (!user) return;

    const token = localStorage.getItem('authToken');
    if (!token) return;

    let eventSource = null;
    let reconnectTimeout = null;

    const connectSSE = () => {
      // Create EventSource with auth token in URL (SSE doesn't support headers)
      eventSource = new EventSource(`${API_URL}/api/events?token=${token}`);

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('[SSE] Received:', data.type);

          if (data.type === 'guide_created') {
            // Add new guide to the list
            setGuides(prev => [data.guide, ...prev]);
          } else if (data.type === 'guide_deleted') {
            // Remove deleted guide from the list
            setGuides(prev => prev.filter(g => g.id !== data.guideId));
          } else if (data.type === 'guide_updated') {
            // Update guide in the list
            setGuides(prev => prev.map(g => g.id === data.guide.id ? data.guide : g));
            // ALSO refresh the currently-open guide detail if it
            // matches. The SSE payload only carries top-level guide
            // metadata (no steps blob — that would blow up SSE), so
            // fire a fresh GET to pull the full step list. Fixes the
            // "improved step names show up only after page refresh"
            // bug where auto-apply mutated steps server-side but the
            // dashboard's selectedGuide state was still holding the
            // pre-apply steps.
            setSelectedGuide(prev => {
              if (!prev || prev.id !== data.guide.id) return prev;
              // Fetch full detail out-of-band; setSelectedGuide is
              // called again with the fresh payload once it lands.
              const authToken = localStorage.getItem('authToken');
              if (authToken) {
                fetch(`${API_URL}/api/guides/${data.guide.id}`, {
                  headers: { Authorization: `Bearer ${authToken}` },
                })
                  .then(r => r.ok ? r.json() : null)
                  .then(fresh => { if (fresh) setSelectedGuide(fresh); })
                  .catch(() => { /* silent — user can still refresh manually */ });
              }
              return prev;
            });
          }
        } catch (e) {
          console.log('[SSE] Parse error:', e);
        }
      };

      eventSource.onerror = () => {
        console.log('[SSE] Connection error, reconnecting in 5s...');
        eventSource.close();
        reconnectTimeout = setTimeout(connectSSE, 5000);
      };

      eventSource.onopen = () => {
        console.log('[SSE] Connected');
      };
    };

    connectSSE();

    return () => {
      if (eventSource) eventSource.close();
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
    };
  }, [user]);

  // Check if URL is an invitation link
  const checkInviteUrl = async () => {
    const path = window.location.pathname.replace(/\/$/, '');
    const match = path.match(/^\/invite\/([^/]+)$/);
    if (match) {
      const token = match[1];
      try {
        const response = await fetch(`${API_URL}/api/invitations/${token}`);
        if (response.ok) {
          const data = await response.json();
          setInvitationData({ ...data, token });
        } else {
          const error = await response.json();
          showToast(error.error || 'Invalid invitation link', 'error');
          window.history.replaceState({}, '', '/');
        }
      } catch (error) {
        showToast('Failed to load invitation', 'error');
        window.history.replaceState({}, '', '/');
      }
    }
  };

  const checkAuth = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setLoading(false);
      // Preserve special views that don't require auth
      setView(prev => ['reset-password', 'forgot-password', 'signup', 'verify-email'].includes(prev) ? prev : 'login');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        if (userData.planFeatures) setPlanFeatures(userData.planFeatures);
        if (userData.planLimits) setPlanGuideLimit(userData.planLimits.guides === -1 ? Infinity : userData.planLimits.guides);
        loadGuides(token);

        // Load favorites and recent guides from API response (with localStorage fallback)
        if (userData.favoriteGuideIds && userData.favoriteGuideIds.length > 0) {
          setFavoriteGuideIds(userData.favoriteGuideIds);
          localStorage.setItem('favoriteGuideIds', JSON.stringify(userData.favoriteGuideIds));
        } else {
          const storedFavorites = localStorage.getItem('favoriteGuideIds');
          if (storedFavorites) setFavoriteGuideIds(JSON.parse(storedFavorites));
        }
        if (userData.recentGuideIds && userData.recentGuideIds.length > 0) {
          setRecentGuides(userData.recentGuideIds);
          localStorage.setItem('recentGuides', JSON.stringify(userData.recentGuideIds));
        } else {
          const storedRecent = localStorage.getItem('recentGuides');
          if (storedRecent) setRecentGuides(JSON.parse(storedRecent));
        }

        // Theme is now hardcoded — no sync needed

        // Check if there's a pending guide to load. Read from sessionStorage
        // (set by checkGuideUrl on /guide/<id> deep links), and ALSO derive
        // from the URL directly as a fallback.
        //
        // The fallback is critical because React.StrictMode in dev double-
        // invokes this useEffect. The first checkAuth() removes pendingGuideId
        // from sessionStorage; the second one would otherwise find it missing,
        // fall into the else branch, and setView('dashboard') — overriding the
        // first invocation's setView('guide-detail') and putting the user on
        // the home page despite the URL staying at /guide/<id>. By reading
        // the guide ID from the URL too, both invocations agree on what to
        // load and never bounce to dashboard.
        const path = window.location.pathname.replace(/\/$/, '');
        const urlGuideMatch = path.match(/^\/guide\/([^/]+)$/);
        const pendingGuideId = sessionStorage.getItem('pendingGuideId') || urlGuideMatch?.[1];
        // Check if there's a view to restore after theme change
        const restoreView = localStorage.getItem('instaguide-restore-view');
        if (pendingGuideId) {
          sessionStorage.removeItem('pendingGuideId');
          loadGuideDetails(pendingGuideId);
        } else if (restoreView) {
          localStorage.removeItem('instaguide-restore-view');
          setView(restoreView);
        } else {
          setView(prev => ['reset-password', 'forgot-password', 'signup'].includes(prev) ? prev : 'dashboard');
        }
      } else {
        localStorage.removeItem('authToken');
        setView(prev => ['reset-password', 'forgot-password', 'signup', 'verify-email'].includes(prev) ? prev : 'login');
      }
    } catch (error) {
      setView(prev => ['reset-password', 'forgot-password', 'signup', 'verify-email'].includes(prev) ? prev : 'login');
    } finally {
      setLoading(false);
    }
  };

  // Add guide to recent list
  const addToRecentGuides = (guideId) => {
    setRecentGuides(prev => {
      const filtered = prev.filter(id => id !== guideId);
      const updated = [guideId, ...filtered].slice(0, 20); // Keep last 20
      localStorage.setItem('recentGuides', JSON.stringify(updated));
      // Save to API (fire and forget)
      const token = localStorage.getItem('authToken');
      if (token) {
        fetch(`${API_URL}/api/user/recent`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ recentGuideIds: updated })
        }).catch(() => {}); // Ignore errors, localStorage is the fallback
      }
      return updated;
    });
  };

  // Toggle favorite status
  const toggleFavorite = (guideId) => {
    setFavoriteGuideIds(prev => {
      const updated = prev.includes(guideId)
        ? prev.filter(id => id !== guideId)
        : [...prev, guideId];
      localStorage.setItem('favoriteGuideIds', JSON.stringify(updated));
      // Save to API (fire and forget)
      const token = localStorage.getItem('authToken');
      if (token) {
        fetch(`${API_URL}/api/user/favorites`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ favoriteGuideIds: updated })
        }).catch(() => {}); // Ignore errors, localStorage is the fallback
      }
      return updated;
    });
  };

  // Re-fetch /api/auth/me — used by BillingSuccessPage to detect when the
  // Stripe webhook has applied the plan change. Returns the fresh user (or null).
  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) return null;
    try {
      const r = await fetch(`${API_URL}/api/auth/me`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!r.ok) return null;
      const userData = await r.json();
      setUser(userData);
      if (userData.planFeatures) setPlanFeatures(userData.planFeatures);
      if (userData.planLimits) setPlanGuideLimit(userData.planLimits.guides === -1 ? Infinity : userData.planLimits.guides);
      return userData;
    } catch { return null; }
  }, []);

  // Guides currently being deleted. We hide these from any /api/guides
  // response until the DELETE round-trips, so a tab-focus refresh can't
  // race a slow FK cascade and resurrect the row in the UI. Persisted
  // to sessionStorage so a BROWSER refresh (F5 / Ctrl-R) also keeps the
  // guide hidden — otherwise the fresh mount would re-fetch the list
  // while the backend is still finishing the cascade delete, briefly
  // showing the "deleted" row again. Cleared per-tab when the tab closes
  // (that's fine — a brand-new tab load after a completed delete just
  // won't see the row from the backend anyway).
  const PENDING_DELETES_KEY = 'instaguide-pending-deletes-v1';
  // Auto-expire pending-delete entries after 10 minutes. Guards against
  // a client that crashed / dropped the DELETE response mid-flight from
  // permanently hiding a guide that actually still exists server-side.
  const PENDING_DELETE_TTL_MS = 10 * 60 * 1000;
  const readPendingDeletes = () => {
    try {
      const raw = sessionStorage.getItem(PENDING_DELETES_KEY);
      if (!raw) return new Set();
      const arr = JSON.parse(raw);
      if (!Array.isArray(arr)) return new Set();
      const now = Date.now();
      const fresh = arr.filter((r) => r && r.id && (now - (r.at || 0)) < PENDING_DELETE_TTL_MS);
      if (fresh.length !== arr.length) {
        sessionStorage.setItem(PENDING_DELETES_KEY, JSON.stringify(fresh));
      }
      return new Set(fresh.map((r) => r.id));
    } catch (_) { return new Set(); }
  };
  const writePendingDelete = (id, present) => {
    try {
      const raw = sessionStorage.getItem(PENDING_DELETES_KEY);
      const arr = raw ? (JSON.parse(raw) || []) : [];
      const filtered = arr.filter((r) => r && r.id && r.id !== id);
      if (present) filtered.push({ id, at: Date.now() });
      sessionStorage.setItem(PENDING_DELETES_KEY, JSON.stringify(filtered));
    } catch (_) { /* quota / private mode — silently no-op */ }
  };
  const pendingDeletesRef = useRef(readPendingDeletes());

  const loadGuides = async (token) => {
    setLoadingGuides(true);
    try {
      const authHeader = { 'Authorization': `Bearer ${token || localStorage.getItem('authToken')}` };
      // Load user's guides, shared guides, and projects in parallel
      const [guidesRes, sharedRes, projectsRes] = await Promise.all([
        fetch(`${API_URL}/api/guides`, { headers: authHeader }),
        fetch(`${API_URL}/api/guides/shared`, { headers: authHeader }),
        fetch(`${API_URL}/api/projects`, { headers: authHeader })
      ]);

      let freshGuides = null, freshShared = null, freshProjects = null;
      if (guidesRes.ok) {
        freshGuides = await guidesRes.json();
        // Filter out anything mid-delete so a slow backend can't undo the
        // optimistic removal in the UI.
        const pending = pendingDeletesRef.current;
        if (pending.size > 0) freshGuides = freshGuides.filter(g => !pending.has(g.id));
        setGuides(freshGuides);
      }
      if (sharedRes.ok) {
        freshShared = await sharedRes.json();
        const pending = pendingDeletesRef.current;
        if (pending.size > 0) freshShared = freshShared.filter(g => !pending.has(g.id));
        setSharedGuides(freshShared);
      }
      if (projectsRes.ok) { freshProjects = await projectsRes.json(); setProjects(freshProjects); }
      // Persist to localStorage so the next mount can hydrate instantly (SWR pattern).
      // Only write if we successfully refreshed at least the guides list.
      if (freshGuides) {
        writeCachedDashboard({
          guides: freshGuides,
          sharedGuides: freshShared || [],
          projects: freshProjects || [],
          cachedAt: Date.now()
        });
      }
    } catch (error) {
      console.error('Failed to load guides:', error);
    } finally {
      setLoadingGuides(false);
    }
  };

  // Refresh data when tab becomes visible (e.g., after recording or
  // inserting a step in the extension). Two cases:
  //   1. Dashboard view → reload the guides list so new captures appear.
  //   2. Guide-detail view → reload the OPEN guide so steps the extension
  //      just inserted via /steps/insert show up without a manual refresh.
  // Without case 2, the user on a guide-detail page would stay on stale
  // step data until they hit reload — which is exactly what they reported.
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (!localStorage.getItem('authToken')) return;
      if (view === 'dashboard') {
        loadGuides();
      } else if (view === 'guide-detail' && selectedGuide?.id) {
        loadGuideDetails(selectedGuide.id);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [view, selectedGuide?.id]);

  // Helper used by login + 2FA flows after a successful session token is
  // issued — finalizes the auth state and routes to the dashboard.
  const finalizeSession = (data) => {
    localStorage.setItem('authToken', data.token);
    // Merge organization + effectivePlanKey onto the user object so the
    // pricing page's currentPlan resolver
    //   user?.effectivePlanKey || user?.organization?.plan || user?.plan
    // returns the right answer immediately. Without these on the login
    // response payload, the frontend used to fall back to raw
    // user.plan (which is 'free' when the plan lives on the org row)
    // until checkAuth's /api/auth/me ran on the next full page load.
    const enrichedUser = {
      ...data.user,
      organization: data.organization ?? data.user?.organization ?? null,
      effectivePlanKey: data.effectivePlanKey ?? data.user?.effectivePlanKey ?? null,
      // /api/auth/me returns planFeatures on the user object itself, but
      // /api/auth/login (and the 2fa/verify path) return it as a
      // top-level field on the response. Fold it onto the user so
      // components that read `user.planFeatures.<flag>` (AskInstaChatbot,
      // Meeting Mode gate, etc.) work on the FIRST render after login —
      // not just after a subsequent full page refresh.
      planFeatures: data.planFeatures ?? data.user?.planFeatures ?? {},
      planLimits: data.planLimits ?? data.user?.planLimits ?? null,
    };
    setUser(enrichedUser);
    if (data.planFeatures) setPlanFeatures(data.planFeatures);
    if (data.planLimits) setPlanGuideLimit(data.planLimits.guides === -1 ? Infinity : data.planLimits.guides);

    // If the user landed on a /guide/<id> URL while logged out, checkGuideUrl()
    // stashed the id in sessionStorage. Honor it now that auth is complete —
    // otherwise the user logs in and gets dumped on the dashboard, losing the
    // deep link they originally clicked (e.g. extension's "View Guide").
    const pendingGuideId = sessionStorage.getItem('pendingGuideId');
    if (pendingGuideId) {
      sessionStorage.removeItem('pendingGuideId');
      loadGuides(data.token);
      loadGuideDetails(pendingGuideId);
      return;
    }

    setView('dashboard');
    window.history.replaceState({}, '', '/');
    loadGuides(data.token);
  };

  const handleLogin = async (email, password) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, client: 'web' })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Invalid credentials');
      }

      const data = await response.json();

      // 2FA gate. Server returns a short-lived challengeToken and either:
      //   - requires2FASetup: existing user without 2FA — must set it up now
      //   - requires2FA: user already has 2FA — must enter a TOTP code
      // The full session token is only issued by /api/auth/2fa/{enable,verify}.
      if (data.challengeToken && (data.requires2FASetup || data.requires2FA)) {
        setMfaChallenge({
          token: data.challengeToken,
          email: data.email || email,
          mode: data.requires2FASetup ? 'setup' : 'verify'
        });
        setView(data.requires2FASetup ? '2fa-setup' : '2fa-challenge');
        return;
      }

      // Backwards-compat: backend somehow returned a direct session (e.g. an
      // older instance). Honor it.
      if (data.token) {
        finalizeSession(data);
        return;
      }

      throw new Error('Unexpected login response');
    } catch (error) {
      throw error; // Re-throw to be handled by LoginView
    }
  };

  const handleRegister = async (email, password, name, accountType = 'personal', organizationName = '', inviteToken = null) => {
    try {
      // If registering via invitation, use the accept invitation endpoint
      if (inviteToken) {
        const response = await fetch(`${API_URL}/api/invitations/${inviteToken}/accept`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password, name, client: 'web' })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Registration failed');
        }

        const data = await response.json();
        // Invited users are routed straight into 2FA setup — no exception.
        if (data.challengeToken && data.requires2FASetup) {
          setInvitationData(null);
          setMfaChallenge({ token: data.challengeToken, email: data.email || email, mode: 'setup' });
          setView('2fa-setup');
          window.history.replaceState({}, '', '/');
          return;
        }
        // Backwards-compat fallback
        if (data.token) {
          setInvitationData(null);
          finalizeSession(data);
          return;
        }
        throw new Error('Unexpected invitation response');
      }

      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name, accountType, organizationName, client: 'web' })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Registration failed');
      }

      const data = await response.json();

      // New flow — server always returns pendingVerification for a
      // fresh signup. The user gets an email; what happens after they
      // click depends on emailType (company → auto-login, personal →
      // admin queue). Backwards-compat: old server builds still
      // return `pendingApproval` — treat it as a pending state.
      if (data.pendingVerification || data.pendingApproval) {
        return {
          pendingVerification: !!data.pendingVerification,
          pendingApproval: !!data.pendingApproval,
          emailType: data.emailType || null,
          message: data.message,
          email,
        };
      }

      localStorage.setItem('authToken', data.token);
      setUser(data.user);
      setView('dashboard');
      loadGuides(data.token);
    } catch (error) {
      showToast(error.message, 'error');
      throw error;
    }
  };

  const handleLogout = async () => {
    const token = localStorage.getItem('authToken');
    if (token) {
      try {
        await fetch(`${API_URL}/api/auth/logout`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } catch (e) { /* best effort */ }
    }
    localStorage.removeItem('authToken');
    // Wipe the persisted Ask Insta conversation so the next user to sign in
    // on this browser doesn't inherit the previous user's chat history.
    localStorage.removeItem('ask-insta-conversation-v1');
    clearCachedDashboard();
    setUser(null);
    setGuides([]);
    setSharedGuides([]);
    setProjects([]);
    setCurrentPage('home');
    setView('login');
    window.history.replaceState({}, '', '/login');
  };

  // Silent token refresh — extends session every hour while user is active
  useEffect(() => {
    if (!user) return;
    let lastActivity = Date.now();
    const onActivity = () => { lastActivity = Date.now(); };
    window.addEventListener('mousemove', onActivity);
    window.addEventListener('keydown', onActivity);
    window.addEventListener('click', onActivity);

    const refreshInterval = setInterval(async () => {
      const idleMs = Date.now() - lastActivity;
      // If user has been idle for more than 1 hour, log them out
      if (idleMs > 60 * 60 * 1000) {
        handleLogout();
        return;
      }
      // Otherwise refresh the token
      const token = localStorage.getItem('authToken');
      if (!token) return;
      try {
        const res = await fetch(`${API_URL}/api/auth/refresh`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          localStorage.setItem('authToken', data.token);
        } else if (res.status === 403) {
          // Token expired or revoked
          handleLogout();
        }
      } catch (e) { /* network error, try again next cycle */ }
    }, 60 * 60 * 1000); // Every 1 hour

    return () => {
      window.removeEventListener('mousemove', onActivity);
      window.removeEventListener('keydown', onActivity);
      window.removeEventListener('click', onActivity);
      clearInterval(refreshInterval);
    };
  }, [user]); // eslint-disable-line

  const [deletingGuideId, setDeletingGuideId] = useState(null);
  const [deletingStepId, setDeletingStepId] = useState(null);
  const [exportingGuide, setExportingGuide] = useState({ id: null, format: null });
  const [sharedGuides, setSharedGuides] = useState(() => cachedDashboard?.sharedGuides || []);
  const [loadingGuides, setLoadingGuides] = useState(false);
  const [loadingGuideDetails, setLoadingGuideDetails] = useState(false);
  const [showSharedGuides, setShowSharedGuides] = useState(false);
  const [autoOpenShare, setAutoOpenShare] = useState(false);
  const [quickShareGuideId, setQuickShareGuideId] = useState(null);
  const [quickShareGuideName, setQuickShareGuideName] = useState('');
  const [quickShareShares, setQuickShareShares] = useState([]);
  const [quickShareMembers, setQuickShareMembers] = useState([]);
  const [quickShareSelected, setQuickShareSelected] = useState([]);
  const [quickSharePermission, setQuickSharePermission] = useState('view');
  const [quickShareLoading, setQuickShareLoading] = useState(false);
  const cachedOrgMembers = useRef(null);

  // Custom dialog state
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null, confirmText: 'Confirm', cancelText: 'Cancel', type: 'default' });
  const [promptDialog, setPromptDialog] = useState({ open: false, title: '', message: '', defaultValue: '', onSubmit: null, placeholder: '', submitText: 'OK', cancelText: 'Cancel' });

  // Helper to show confirm dialog
  const showConfirm = (title, message, onConfirm, options = {}) => {
    setConfirmDialog({
      open: true,
      title,
      message,
      onConfirm,
      confirmText: options.confirmText || 'Confirm',
      cancelText: options.cancelText || 'Cancel',
      type: options.type || 'default' // 'default', 'danger'
    });
  };

  // Helper to show prompt dialog
  const showPrompt = (title, message, onSubmit, options = {}) => {
    setPromptDialog({
      open: true,
      title,
      message,
      defaultValue: options.defaultValue || '',
      onSubmit,
      placeholder: options.placeholder || '',
      submitText: options.submitText || 'OK',
      cancelText: options.cancelText || 'Cancel'
    });
  };

  // (loadSharedGuides + its on-login useEffect were removed — shared guides
  // are already fetched in parallel by loadGuides() during checkAuth/login.)

  // Close dialogs when navigating between pages (via sidebar)
  // Only close if the page actually changed (not on initial render or re-renders)
  useEffect(() => {
    if (prevPageRef.current !== currentPage) {
      setConfirmDialog(prev => ({ ...prev, open: false }));
      setPromptDialog(prev => ({ ...prev, open: false }));
      prevPageRef.current = currentPage;
    }
  }, [currentPage]);

  // Sync URL with currentPage when on the regular dashboard.
  // Skip when viewing a specific guide (URL is set to /guide/<id> elsewhere)
  // or during auth flows (login/forgot-password/reset-password set their own URLs).
  useEffect(() => {
    if (view !== 'dashboard') return;
    const desired = currentPage === 'home'
      ? '/'
      : currentPage === 'billing-success'
        ? '/billing/success'
        : `/${currentPage}`;
    if (window.location.pathname !== desired) {
      // Preserve query string + hash across the pathname normalization.
      // Without this, a deep-link like /admin?tab=redaction loses its
      // ?tab=redaction the first time this effect fires, and the
      // OrganizationAdminView's `URLSearchParams` read comes back empty
      // — landing the user on Overview instead of the requested tab.
      window.history.replaceState({}, '', desired + window.location.search + window.location.hash);
    }
  }, [currentPage, view]);

  // Sync URL when transitioning to the login view (e.g., logout, token expiry,
  // initial load without a token). Don't overwrite URLs owned by other auth
  // flows (forgot-password, reset-password/<token>, invite/<token>) or the
  // public /embed/<guideId> viewer — view defaults to 'login' on mount, and
  // without the /embed/ guard this effect would rewrite an embed URL to
  // /login on first paint, hiding the embed view behind the login screen.
  useEffect(() => {
    if (view !== 'login') return;
    // While the initial auth check is in flight, `view` defaults to
    // 'login'. If we rewrite the URL during that window, any dashboard
    // deep-link (e.g. /admin?tab=redaction) gets clobbered to /login
    // and its query string is lost — by the time view flips to
    // 'dashboard' and the OrganizationAdminView mounts, there's no
    // ?tab=redaction left to read. Skip the rewrite while loading;
    // it'll re-evaluate once auth resolves and `loading` flips false.
    if (loading) return;
    const path = window.location.pathname;
    // Paths we DON'T overwrite when view is briefly 'login' (e.g. during
    // initial auth check). Each is owned by its own loading / routing
    // logic — clobbering them to /login on initial paint would flash the
    // login screen for a beat before the real view renders. The embed
    // case bit us in 1.15.1; the guide-detail case was bit reported by
    // a user noticing the URL bar flicker /guide/<id> → /login → back.
    const isOtherAuthPath = path === '/forgot-password'
      || path === '/2fa-forgot'
      || path === '/signup'   // signup shares the LoginView component; don't clobber /signup back to /login
      || path.startsWith('/reset-password/')
      || path.startsWith('/2fa-recovery/')
      || path.startsWith('/verify-email/')  // don't clobber the confirmation link back to /login
      || path.startsWith('/invite/')
      || path.startsWith('/embed/')
      || path.startsWith('/impersonate/')  // License Admin "Sign in as user" deep-link
      || path.startsWith('/guide/');
    if (!isOtherAuthPath && path !== '/login') {
      window.history.replaceState({}, '', '/login');
    }
  }, [view, loading]);

  // Handle browser back/forward — sync currentPage from URL pathname.
  useEffect(() => {
    const onPopState = () => {
      const p = window.location.pathname.replace(/\/$/, '');
      if (p === '' || p === '/') { setCurrentPage('home'); return; }
      if (p === '/docs') { setCurrentPage('docs'); return; }
      if (p === '/billing/success') { setCurrentPage('billing-success'); return; }
      if (p.startsWith('/guide/') || p.startsWith('/invite/') || p.startsWith('/reset-password/') || p.startsWith('/2fa-recovery/') || p === '/login' || p === '/forgot-password' || p === '/2fa-forgot' || p === '/privacy') return;
      // Strip leading slash; pages are single-segment ids
      const candidate = p.replace(/^\//, '');
      if (candidate) setCurrentPage(candidate);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // Share guide with users
  const handleShareGuide = async (guideId, userIds, permission = 'view') => {
    try {
      const response = await fetch(`${API_URL}/api/guides/${guideId}/share`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ userIds, permission })
      });
      // Plan-related 403 → show upgrade prompt
      if (await handlePlanError(response)) return null;
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error);
      }
      return await response.json();
    } catch (error) {
      showToast(error.message, 'error');
      return null;
    }
  };

  // Get shares for a guide
  const getGuideShares = async (guideId) => {
    try {
      const response = await fetch(`${API_URL}/api/guides/${guideId}/shares`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.error('Failed to get shares:', error);
    }
    return [];
  };

  // Remove a share
  const handleRemoveShare = async (guideId, userId) => {
    try {
      await fetch(`${API_URL}/api/guides/${guideId}/shares/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });
      return true;
    } catch (error) {
      showToast('Failed to remove share', 'error');
      return false;
    }
  };

  // Get organization members
  const getOrgMembers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/organization/members`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.error('Failed to get org members:', error);
    }
    return [];
  };

  // Comments functions
  const getGuideComments = async (guideId) => {
    try {
      const response = await fetch(`${API_URL}/api/guides/${guideId}/comments`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.error('Failed to get comments:', error);
    }
    return [];
  };

  // Guide change history
  const getGuideChanges = async (guideId) => {
    try {
      const response = await fetch(`${API_URL}/api/guides/${guideId}/changes`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.error('Failed to get guide changes:', error);
    }
    return [];
  };

  const handleAddComment = async (guideId, content, stepId = null) => {
    try {
      const response = await fetch(`${API_URL}/api/guides/${guideId}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ content, stepId })
      });
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      showToast('Failed to add comment', 'error');
    }
    return null;
  };

  const handleDeleteComment = async (guideId, commentId) => {
    try {
      await fetch(`${API_URL}/api/guides/${guideId}/comments/${commentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });
      return true;
    } catch (error) {
      showToast('Failed to delete comment', 'error');
      return false;
    }
  };

  const handleDeleteGuide = (guideId, skipConfirm = false, silent = false) => {
    const doDelete = async () => {
      // Snapshot for restore-on-failure
      const guideSnapshot = guides.find(g => g.id === guideId);
      // Mark in-flight so loadGuides() (e.g. from visibilitychange OR
      // a browser-level refresh) won't resurrect the row if its fetch
      // races a slow DELETE / FK cascade. Persisted to sessionStorage
      // so F5 doesn't wipe the pending state before the backend has
      // finished the cascade delete.
      pendingDeletesRef.current.add(guideId);
      writePendingDelete(guideId, true);

      // Optimistic: remove from UI immediately
      setGuides(prev => prev.filter(g => g.id !== guideId));
      if (selectedGuide?.id === guideId) {
        setSelectedGuide(null);
        setView('dashboard');
        window.history.replaceState({}, '', '/');
      }
      if (!silent) showToast('Guide deleted', 'success');

      try {
        const res = await fetch(`${API_URL}/api/guides/${guideId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
        });
        if (!res.ok) {
          // Backend rejected the delete — surface it and restore the row
          throw new Error(`HTTP ${res.status}`);
        }
      } catch (error) {
        // Restore the guide so the user isn't left thinking it was deleted
        if (guideSnapshot) {
          setGuides(prev => prev.some(g => g.id === guideId) ? prev : [...prev, guideSnapshot]);
        }
        if (!silent) showToast('Failed to delete guide. Please try again.', 'error');
      } finally {
        pendingDeletesRef.current.delete(guideId);
        writePendingDelete(guideId, false);
      }
    };

    if (skipConfirm) {
      doDelete();
    } else {
      showConfirm('Delete Guide', 'Are you sure you want to delete this guide? This action cannot be undone.', doDelete, { type: 'danger', confirmText: 'Delete', cancelText: 'Cancel' });
    }
  };

  const handleExport = async (guideId, format) => {
    setExportingGuide({ id: guideId, format });
    showToast(
      `${format.toUpperCase()} export requested. Your download will start automatically.`,
      'info',
      'Export in progress',
      8000
    );
    try {
      const response = await fetch(`${API_URL}/api/guides/${guideId}/export/${format}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });

      // Plan-related 403 → show upgrade prompt instead of generic error toast
      if (await handlePlanError(response)) return;

      if (!response.ok) {
        const error = await response.json();
        showToast(error.error || 'Export failed', 'error');
        return;
      }

      // Get filename from Content-Disposition header (backend sends safe filename)
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `guide.${format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (match && match[1]) {
          filename = match[1];
        }
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      showToast('Export ready! Download started.', 'success', null, 3000);
    } catch (error) {
      showToast('Export failed. Please try again.', 'error');
    } finally {
      setExportingGuide({ id: null, format: null });
    }
  };

  const loadGuideDetails = async (guideId) => {
    setLoadingGuideDetails(true);
    setView('guide-detail');
    try {
      const response = await fetch(`${API_URL}/api/guides/${guideId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });

      if (response.ok) {
        const guide = await response.json();
        setSelectedGuide(guide);
        // Track as recently opened
        addToRecentGuides(guideId);
        // Update URL without adding to history
        window.history.replaceState({}, '', `/guide/${guideId}`);
      } else if (response.status === 403 || response.status === 404) {
        // Guide isn't accessible to the current user (deleted, revoked, or
        // belongs to a different account). Bail back to the dashboard
        // immediately and surface a toast — never block the render thread
        // with window.confirm(), because a blocked / dismissed confirm
        // freezes the entire app on the "Loading guide…" early-return.
        // sessionStorage.pendingGuideId is cleared so a subsequent mount
        // can't replay the same failure.
        sessionStorage.removeItem('pendingGuideId');
        setView('dashboard');
        window.history.replaceState({}, '', '/');
        showToast(
          response.status === 404
            ? 'That guide no longer exists or you no longer have access.'
            : 'You do not have access to that guide.',
          'warning'
        );
      } else {
        setView('dashboard');
        // Clear /guide/<id> from the address bar — leaving it leaves the user
        // on the dashboard with a confusing URL and a broken back-button.
        window.history.replaceState({}, '', '/');
        showToast('Failed to load guide', 'error');
      }
    } catch (error) {
      setView('dashboard');
      window.history.replaceState({}, '', '/');
      showToast('Failed to load guide', 'error');
    } finally {
      setLoadingGuideDetails(false);
    }
  };

  // Quick share - opens inline share modal without navigating to guide detail
  const handleQuickShare = async (guideId) => {
    const guide = guides.find(g => g.id === guideId) || sharedGuides.find(g => g.id === guideId);
    setQuickShareGuideId(guideId);
    setQuickShareGuideName(guide?.name || 'Guide');
    setQuickShareSelected([]);
    setQuickSharePermission('view');
    setQuickShareShares([]);
    setQuickShareMembers([]);
    setQuickShareLoading(true);

    // Use cached org members if available, fetch shares in parallel
    const [shares, members] = await Promise.all([
      getGuideShares(guideId),
      cachedOrgMembers.current ? Promise.resolve(cachedOrgMembers.current) : getOrgMembers()
    ]);
    cachedOrgMembers.current = members;
    setQuickShareShares(shares);
    const sharedUserIds = shares.map(s => s.sharedWithUserId);
    const guideOwnerId = guide?.userId;
    setQuickShareMembers(members.filter(m => m.id !== user?.id && m.id !== guideOwnerId && !sharedUserIds.includes(m.id)));
    setQuickShareLoading(false);
  };

  const handleQuickShareSubmit = async () => {
    if (quickShareSelected.length === 0 || !quickShareGuideId) return;
    setQuickShareLoading(true);
    const result = await handleShareGuide(quickShareGuideId, quickShareSelected, quickSharePermission);
    if (result) {
      setGuides(prev => prev.map(g => g.id === quickShareGuideId ? { ...g, shareCount: (g.shareCount || 0) + quickShareSelected.length } : g));
    }
    showToast('Guide shared successfully!', 'success');
    setQuickShareGuideId(null);
    setQuickShareLoading(false);
  };

  const handleQuickShareClose = () => {
    setQuickShareGuideId(null);
  };

  const handleUpdateStep = async (guideId, stepId, updates) => {
    try {
      const response = await fetch(`${API_URL}/api/guides/${guideId}/steps/${stepId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        const updatedStep = await response.json();
        setSelectedGuide(prev => prev ? ({
          ...prev,
          steps: (prev.steps || []).map(s => s.id === stepId ? { ...s, ...updatedStep } : s)
        }) : prev);
      }
    } catch (error) {
      showToast('Failed to update step', 'error');
    }
  };

  const handleUpdateGuide = async (guideId, updates) => {
    try {
      const response = await fetch(`${API_URL}/api/guides/${guideId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        const updatedGuide = await response.json();
        // Preserve steps when updating guide metadata
        setSelectedGuide(prev => prev ? ({ ...prev, ...updatedGuide, steps: prev.steps || [] }) : prev);
        // Also update in guides list
        setGuides(prev => prev.map(g => g.id === guideId ? { ...g, ...updatedGuide } : g));
        return true;
      }
    } catch (error) {
      showToast('Failed to update guide', 'error');
    }
    return false
  };

  const handleMoveGuide = async (guideId, targetProjectId) => {
    try {
      const response = await fetch(`${API_URL}/api/guides/${guideId}/move`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ projectId: targetProjectId })
      });

      if (response.ok) {
        const result = await response.json();
        // Update the guide's project in local state
        setSelectedGuide(prev => ({ ...prev, projectId: targetProjectId }));
        setGuides(prev => prev.map(g => g.id === guideId ? { ...g, projectId: targetProjectId } : g));
        return true;
      } else {
        const error = await response.json();
        showToast(error.error || 'Failed to move guide', 'error');
      }
    } catch (error) {
      showToast('Failed to move guide', 'error');
    }
    return false;
  };

  const handleMoveGuides = async (guideIds, targetProjectId) => {
    let successCount = 0;
    let failCount = 0;

    for (const guideId of guideIds) {
      try {
        const response = await fetch(`${API_URL}/api/guides/${guideId}/move`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          },
          body: JSON.stringify({ projectId: targetProjectId })
        });

        if (response.ok) {
          successCount++;
          // Update the guide's project in local state
          setGuides(prev => prev.map(g => g.id === guideId ? { ...g, projectId: targetProjectId } : g));
        } else {
          failCount++;
        }
      } catch (error) {
        failCount++;
      }
    }

    if (failCount > 0) {
      showToast(`Moved ${successCount} guide${successCount !== 1 ? 's' : ''}. Failed to move ${failCount}.`, 'warning');
    } else if (successCount > 0) {
      showToast(`Moved ${successCount} guide${successCount !== 1 ? 's' : ''} successfully`, 'success');
    }

    return successCount > 0;
  };

  // Project management functions
  const handleCreateProject = async (name, description) => {
    try {
      const response = await fetch(`${API_URL}/api/projects`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ name, description })
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create project');
      }
      const newProject = await response.json();
      setProjects(prev => [...prev, newProject]);
      return newProject;
    } catch (error) {
      showToast(error.message, 'error');
      return null;
    }
  };

  const handleUpdateProject = async (projectId, name, description) => {
    try {
      const response = await fetch(`${API_URL}/api/projects/${projectId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ name, description })
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update project');
      }
      const updatedProject = await response.json();
      setProjects(prev => prev.map(p => p.id === projectId ? { ...p, ...updatedProject } : p));
      return updatedProject;
    } catch (error) {
      showToast(error.message, 'error');
      return null;
    }
  };

  const handleDeleteProject = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    if (project?.isDefault) {
      showToast('Cannot delete the default project', 'warning');
      return;
    }
    showConfirm('Delete Project', 'This will also delete all guides in this project. Are you sure?', async () => {
      try {
        const response = await fetch(`${API_URL}/api/projects/${projectId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        });
        if (!response.ok) {
          throw new Error('Failed to delete project');
        }
        setProjects(prev => prev.filter(p => p.id !== projectId));
        // Also remove guides from deleted project
        setGuides(prev => prev.filter(g => g.projectId !== projectId));
      } catch (error) {
        showToast(error.message, 'error');
      }
    }, { type: 'danger', confirmText: 'Delete Project', cancelText: 'Cancel' });
  };

  const handleDeleteStep = (guideId, stepId) => {
    showConfirm('Delete Step', 'Are you sure you want to delete this step?', async () => {
      setDeletingStepId(stepId);
      try {
        const response = await fetch(`${API_URL}/api/guides/${guideId}/steps/${stepId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        });

        if (response.ok) {
          // Prune the deleted step from BOTH the steps list AND the cached
          // workflowData (summaries + appChanges keyed by stepId). Without
          // this, the Process Flow keeps rendering the old layout because
          // WorkflowFlowchart hydrates from workflowData when it's present
          // and never re-fetches. We keep the rest of workflowData so we
          // don't trigger an expensive AI re-generation for surviving steps.
          setSelectedGuide(prev => {
            if (!prev) return prev;
            const remainingSteps = (prev.steps || [])
              .filter(s => s.id !== stepId)
              .map((s, i) => ({ ...s, number: i + 1 }));
            const remainingIds = new Set(remainingSteps.map(s => s.id));
            let newWorkflow = prev.workflowData;
            if (newWorkflow) {
              newWorkflow = {
                ...newWorkflow,
                summaries: (newWorkflow.summaries || []).filter(s => remainingIds.has(s.stepId)),
                appChanges: (newWorkflow.appChanges || []).filter(c => !c.stepId || remainingIds.has(c.stepId)),
              };
            }
            return { ...prev, steps: remainingSteps, workflowData: newWorkflow };
          });
        }
      } catch (error) {
        showToast('Failed to delete step', 'error');
      } finally {
        setDeletingStepId(null);
      }
    }, { type: 'danger', confirmText: 'Delete', cancelText: 'Cancel' });
  };

  const handleReorderSteps = async (guideId, stepId, direction) => {
    const currentIndex = selectedGuide.steps.findIndex(s => s.id === stepId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= selectedGuide.steps.length) return;

    const newSteps = [...selectedGuide.steps];
    [newSteps[currentIndex], newSteps[newIndex]] = [newSteps[newIndex], newSteps[currentIndex]];

    // Update numbers
    const reorderedSteps = newSteps.map((s, i) => ({ ...s, number: i + 1 }));

    // Optimistically update UI
    setSelectedGuide(prev => ({ ...prev, steps: reorderedSteps }));

    try {
      await fetch(`${API_URL}/api/guides/${guideId}/steps/reorder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ stepIds: reorderedSteps.map(s => s.id) })
      });
    } catch (error) {
      // Revert on error
      loadGuideDetails(guideId);
    }
  };

  // /embed/:guideId — public iframeable view. Must run BEFORE the global
  // loading gate: checkAuth() may take seconds (especially with a stale token
  // in localStorage from a prior same-origin session), and during that window
  // we'd otherwise render <LoadingView /> — the InstaGuide-branded spinner.
  // In a SharePoint / Notion / Confluence iframe that branded spinner reads
  // as "InstaGuide is asking me to sign in", which is exactly the bug report
  // we got. The embed has its own auth (token in ?t=) and its own loading
  // state, so it doesn't need any of the dashboard's auth machinery.
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/embed/')) {
    const guideId = window.location.pathname.replace(/^\/embed\//, '').split('/')[0];
    const params = new URLSearchParams(window.location.search);
    return (
      <EmbedView
        guideId={guideId}
        token={params.get('t') || ''}
        themeOverride={params.get('theme')}   /* null | 'light' | 'dark' */
        hideFlowOverride={params.get('flow') === 'hide'}
      />
    );
  }

  if (loading) {
    // Allow docs page to render while auth is loading
    if (currentPage === 'docs') return <DocumentationPage />;
    return <LoadingView />;
  }

  // Public pages accessible without authentication
  if (currentPage === 'docs' && view === 'login') {
    return <DocumentationPage />;
  }
  if (view === 'forgot-password') {
    return <ForgotPasswordView onBackToLogin={() => { setView('login'); window.history.replaceState({}, '', '/'); }} />;
  }

  if (view === 'reset-password' && resetToken) {
    return <ResetPasswordView token={resetToken} onBackToLogin={() => { setView('login'); setResetToken(null); window.history.replaceState({}, '', '/'); }} />;
  }

  if (view === 'verify-email' && verifyEmailToken) {
    return (
      <VerifyEmailView
        token={verifyEmailToken}
        onAutoLogin={(data) => {
          // Company-email path — server handed us a session token, drop
          // the user straight into the dashboard (same as a fresh login).
          setVerifyEmailToken(null);
          finalizeSession(data);
        }}
        onBackToLogin={() => {
          setVerifyEmailToken(null);
          setView('login');
          window.history.replaceState({}, '', '/login');
        }}
      />
    );
  }

  if (view === 'login' || view === 'signup') {
    return <LoginView onLogin={handleLogin} onRegister={handleRegister} invitation={invitationData} defaultSignup={view === 'signup'} onForgotPassword={() => { setView('forgot-password'); window.history.pushState({}, '', '/forgot-password'); }} />;
  }

  if (view === '2fa-setup' && mfaChallenge?.token) {
    return (
      <TwoFactorSetupView
        challengeToken={mfaChallenge.token}
        email={mfaChallenge.email}
        onComplete={(data) => { setMfaChallenge(null); finalizeSession(data); }}
        onCancel={() => { setMfaChallenge(null); setView('login'); }}
      />
    );
  }

  if (view === '2fa-challenge' && mfaChallenge?.token) {
    return (
      <TwoFactorChallengeView
        challengeToken={mfaChallenge.token}
        email={mfaChallenge.email}
        onComplete={(data) => { setMfaChallenge(null); finalizeSession(data); }}
        onCancel={() => { setMfaChallenge(null); setView('login'); }}
        onForgotAuthenticator={() => {
          // Stash the email so the recovery form prefills it; clear the
          // challenge so the user can't fall back into 2fa-challenge view.
          setMfaChallenge(null);
          setView('2fa-forgot');
          window.history.pushState({}, '', '/2fa-forgot');
        }}
      />
    );
  }

  if (view === '2fa-forgot') {
    return (
      <TwoFactorForgotView
        initialEmail={mfaChallenge?.email}
        onBackToLogin={() => { setView('login'); window.history.replaceState({}, '', '/'); }}
      />
    );
  }

  if (view === '2fa-recovery' && resetToken) {
    return (
      <TwoFactorRecoveryView
        token={resetToken}
        onComplete={() => { setResetToken(null); setView('login'); window.history.replaceState({}, '', '/'); }}
      />
    );
  }

  // While the guide fetch is in flight (selectedGuide still null), show a
  // standalone loading screen instead of mounting GuideDetailView with a null
  // guide (that component returns early before its useState calls, so a
  // null-then-loaded transition would change its hook count and crash React).
  // This also prevents the "home page flash" on deep links — the user sees
  // a spinner at /guide/<id>, then the real page, never the dashboard.
  if (view === 'guide-detail' && (!selectedGuide || loadingGuideDetails)) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        gap: '20px',
        background: '#f8fafc'
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: '3px solid #e2e8f0',
          borderTopColor: theme.primary,
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite'
        }} />
        <p style={{ color: '#64748b', fontSize: '15px', fontWeight: 500, margin: 0 }}>Loading guide...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (view === 'guide-detail' && selectedGuide) {
    return (
      <>
        <GuideDetailView
          guide={selectedGuide}
          loading={loadingGuideDetails}
          user={user}
          planFeatures={planFeatures}
          projects={projects}
          onBack={() => {
            setSelectedGuide(null);
            setView('dashboard');
            // Clear URL back to root
            window.history.replaceState({}, '', '/');
            // Refresh guides list to get accurate step counts
            const token = localStorage.getItem('authToken');
            if (token) loadGuides(token);
          }}
          onExport={handleExport}
          exportingGuide={exportingGuide}
          onDelete={handleDeleteGuide}
          deletingGuideId={deletingGuideId}
          onUpdateGuide={handleUpdateGuide}
          onMoveGuide={handleMoveGuide}
          onUpdateStep={handleUpdateStep}
          onDeleteStep={handleDeleteStep}
          deletingStepId={deletingStepId}
          onReorderSteps={handleReorderSteps}
          onShareGuide={handleShareGuide}
          getGuideShares={getGuideShares}
          onRemoveShare={handleRemoveShare}
          getOrgMembers={getOrgMembers}
          getGuideComments={getGuideComments}
          getGuideChanges={getGuideChanges}
          onAddComment={handleAddComment}
          onDeleteComment={handleDeleteComment}
          showConfirm={showConfirm}
          showToast={showToast}
          setSelectedGuide={setSelectedGuide}
          autoOpenShare={autoOpenShare}
          onClearAutoOpenShare={() => setAutoOpenShare(false)}
          onPlanError={handlePlanError}
        />
        {confirmDialog.open && (
          <ConfirmDialog
            title={confirmDialog.title}
            message={confirmDialog.message}
            confirmText={confirmDialog.confirmText}
            cancelText={confirmDialog.cancelText}
            type={confirmDialog.type}
            onConfirm={() => {
              confirmDialog.onConfirm?.();
              setConfirmDialog({ ...confirmDialog, open: false });
            }}
            onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
          />
        )}
        {promptDialog.open && (
          <PromptDialog
            title={promptDialog.title}
            message={promptDialog.message}
            defaultValue={promptDialog.defaultValue}
            placeholder={promptDialog.placeholder}
            submitText={promptDialog.submitText}
            cancelText={promptDialog.cancelText}
            onSubmit={(value) => {
              promptDialog.onSubmit?.(value);
              setPromptDialog({ ...promptDialog, open: false });
            }}
            onCancel={() => setPromptDialog({ ...promptDialog, open: false })}
          />
        )}
        <ToastContainer toasts={toasts} removeToast={removeToast} />
        {/* Ask Insta is mounted here so it's visible on the guide-detail
            view too — that branch has its own return at the top of App, so
            the chatbot at the bottom of the main return path doesn't apply. */}
        {user && (
          <AskInstaChatbot user={user} apiUrl={API_URL} showToast={showToast} />
        )}
      </>
    );
  }

  if (view === 'org-admin' && user?.organizationId) {
    return (
      <>
        <OrganizationAdminView
          user={user}
          onBack={() => setView('dashboard')}
          showConfirm={showConfirm}
          showToast={showToast}
        />
        {confirmDialog.open && (
          <ConfirmDialog
            title={confirmDialog.title}
            message={confirmDialog.message}
            confirmText={confirmDialog.confirmText}
            cancelText={confirmDialog.cancelText}
            type={confirmDialog.type}
            onConfirm={() => {
              confirmDialog.onConfirm?.();
              setConfirmDialog({ ...confirmDialog, open: false });
            }}
            onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
          />
        )}
        {promptDialog.open && (
          <PromptDialog
            title={promptDialog.title}
            message={promptDialog.message}
            defaultValue={promptDialog.defaultValue}
            placeholder={promptDialog.placeholder}
            submitText={promptDialog.submitText}
            cancelText={promptDialog.cancelText}
            onSubmit={(value) => {
              promptDialog.onSubmit?.(value);
              setPromptDialog({ ...promptDialog, open: false });
            }}
            onCancel={() => setPromptDialog({ ...promptDialog, open: false })}
          />
        )}
        <ToastContainer toasts={toasts} removeToast={removeToast} />
        {/* Ask Insta — also mounted on the org-admin view's early-return path. */}
        {user && (
          <AskInstaChatbot user={user} apiUrl={API_URL} showToast={showToast} />
        )}
      </>
    );
  }

  // Get favorite guides
  const favoriteGuides = [...guides, ...sharedGuides].filter(g => favoriteGuideIds.includes(g.id));

  // Render page content based on currentPage
  const renderPageContent = () => {
    switch (currentPage) {
      case 'home':
        return (
          <HomePage
            guides={guides}
            sharedGuides={sharedGuides}
            recentGuideIds={recentGuides}
            onSelectGuide={loadGuideDetails}
            onDeleteGuide={handleDeleteGuide}
            deletingGuideId={deletingGuideId}
            onExport={handleExport}
            exportingGuide={exportingGuide}
            favoriteGuideIds={favoriteGuideIds}
            onToggleFavorite={toggleFavorite}
            projects={projects}
            onQuickShare={handleQuickShare}
            user={user}
            loadingGuides={loadingGuides}
          />
        );
      case 'projects':
        return (
          <ProjectsExplorerPage
            projects={projects}
            guides={guides}
            onSelectGuide={loadGuideDetails}
            onDeleteGuide={handleDeleteGuide}
            deletingGuideId={deletingGuideId}
            onExport={handleExport}
            exportingGuide={exportingGuide}
            favoriteGuideIds={favoriteGuideIds}
            onToggleFavorite={toggleFavorite}
            onMoveGuides={handleMoveGuides}
            showConfirm={showConfirm}
            showToast={showToast}
            loadingGuides={loadingGuides}
            onCreateProject={handleCreateProject}
            onUpdateProject={handleUpdateProject}
            onDeleteProject={handleDeleteProject}
            onQuickShare={handleQuickShare}
            user={user}
            sourceFilter={sharedSourceFilter} setSourceFilter={setSharedSourceFilter}
            ownershipFilter={sharedOwnershipFilter} setOwnershipFilter={setSharedOwnershipFilter}
            sortBy={sharedSortBy} setSortBy={setSharedSortBy}
            searchQuery={sharedSearchQuery} setSearchQuery={setSharedSearchQuery}
          />
        );
      case 'myguides':
        return (
          <GuidesListPage
            title="My Guides"
            icon={Icons.file}
            guides={guides}
            onSelectGuide={loadGuideDetails}
            onDeleteGuide={handleDeleteGuide}
            deletingGuideId={deletingGuideId}
            onExport={handleExport}
            exportingGuide={exportingGuide}
            favoriteGuideIds={favoriteGuideIds}
            onToggleFavorite={toggleFavorite}
            projects={projects}
            emptyMessage="No guides yet. Start recording with the Chrome extension!"
            emptyIcon={Icons.inbox}
            showDelete={true}
            onMoveGuides={handleMoveGuides}
            showConfirm={showConfirm}
            showToast={showToast}
            loadingGuides={loadingGuides}
            onQuickShare={handleQuickShare}
            user={user}
            sourceFilter={sharedSourceFilter} setSourceFilter={setSharedSourceFilter}
            ownershipFilter={sharedOwnershipFilter} setOwnershipFilter={setSharedOwnershipFilter}
            projectFilter={sharedProjectFilter} setProjectFilter={setSharedProjectFilter}
            sortBy={sharedSortBy} setSortBy={setSharedSortBy}
            searchQuery={sharedSearchQuery} setSearchQuery={setSharedSearchQuery}
          />
        );
      case 'shared':
        return (
          <GuidesListPage
            title="Shared With Me"
            icon={Icons.users}
            guides={sharedGuides}
            onSelectGuide={loadGuideDetails}
            onDeleteGuide={handleDeleteGuide}
            deletingGuideId={deletingGuideId}
            onExport={handleExport}
            exportingGuide={exportingGuide}
            favoriteGuideIds={favoriteGuideIds}
            onToggleFavorite={toggleFavorite}
            projects={projects}
            emptyMessage="No guides shared with you yet"
            emptyIcon={Icons.share}
            showDelete={false}
            onMoveGuides={handleMoveGuides}
            showConfirm={showConfirm}
            showToast={showToast}
            loadingGuides={loadingGuides}
            onQuickShare={handleQuickShare}
            user={user}
            sourceFilter={sharedSourceFilter} setSourceFilter={setSharedSourceFilter}
            ownershipFilter={sharedOwnershipFilter} setOwnershipFilter={setSharedOwnershipFilter}
            projectFilter={sharedProjectFilter} setProjectFilter={setSharedProjectFilter}
            sortBy={sharedSortBy} setSortBy={setSharedSortBy}
            searchQuery={sharedSearchQuery} setSearchQuery={setSharedSearchQuery}
          />
        );
      case 'favorites':
        return (
          <GuidesListPage
            title="Favorites"
            icon={Icons.star}
            guides={favoriteGuides}
            onSelectGuide={loadGuideDetails}
            onDeleteGuide={handleDeleteGuide}
            deletingGuideId={deletingGuideId}
            onExport={handleExport}
            exportingGuide={exportingGuide}
            favoriteGuideIds={favoriteGuideIds}
            onToggleFavorite={toggleFavorite}
            projects={projects}
            emptyMessage="No favorite guides. Star a guide to add it here!"
            emptyIcon={Icons.starLarge}
            showDelete={false}
            onMoveGuides={handleMoveGuides}
            showConfirm={showConfirm}
            showToast={showToast}
            loadingGuides={loadingGuides}
            onQuickShare={handleQuickShare}
            user={user}
            sourceFilter={sharedSourceFilter} setSourceFilter={setSharedSourceFilter}
            ownershipFilter={sharedOwnershipFilter} setOwnershipFilter={setSharedOwnershipFilter}
            projectFilter={sharedProjectFilter} setProjectFilter={setSharedProjectFilter}
            sortBy={sharedSortBy} setSortBy={setSharedSortBy}
            searchQuery={sharedSearchQuery} setSearchQuery={setSharedSearchQuery}
          />
        );
      case 'docs':
        return <DocumentationPage />;
      case 'imports':
        // Sprint K Phase 2 — uploaded-meeting flow. Lives off its own
        // sidebar item because uploads aren't a "guide" yet (the guide
        // gets created at the end of processing). Sidebar item is itself
        // gated by planFeatures.meetingMode, but the page tolerates a
        // direct URL hit even if the user's plan downgraded — they just
        // can't start new uploads from there.
        return (
          <ImportsPage
            showToast={showToast}
            showConfirm={showConfirm}
            projects={projects}
            startBackgroundUpload={startBackgroundUpload}
            backgroundUploads={backgroundUploads}
            dismissBackgroundUpload={dismissBackgroundUpload}
            cancelBackgroundUpload={cancelBackgroundUpload}
            onOpenGuide={loadGuideDetails}
            onOpenReview={(id) => { setReviewingImportId(id); setCurrentPage('segment-review'); }}
            onGuidesChanged={() => {
              // Force a fresh guide list so My Guides doesn't render
              // stale after an import completes. loadGuides overwrites
              // the App-level `guides` state.
              const token = localStorage.getItem('authToken');
              if (token) loadGuides(token);
            }}
            onNavigateToMyGuides={() => {
              window.history.pushState({}, '', '/myguides');
              setCurrentPage('myguides');
            }}
          />
        );
      case 'segment-review':
        // Sprint K Phase 2 V4 — segment picker for review-mode imports.
        // Rendered inline as its own page (not a modal) because the
        // segment list can be long and multi-tab (chapters/topics/scenes).
        if (!reviewingImportId) { setCurrentPage('imports'); return null; }
        return (
          <SegmentReviewPage
            importId={reviewingImportId}
            showToast={showToast}
            showConfirm={showConfirm}
            onCancel={() => { setReviewingImportId(null); setCurrentPage('imports'); }}
            onSubmitted={() => { setReviewingImportId(null); setCurrentPage('imports'); }}
          />
        );
      case 'admin':
        return (
          <OrganizationAdminView
            user={user}
            onBack={() => setCurrentPage('home')}
            showConfirm={showConfirm}
            showToast={showToast}
            loadingGuides={loadingGuides}
            guides={guides}
            embedded={true}
          />
        );
      case 'profile':
        return (
          <ProfilePage user={user} setCurrentPage={setCurrentPage} showToast={showToast} />
        );
      case 'pricing':
        return (
          <PricingPage user={user} showToast={showToast} onPlanError={handlePlanError} />
        );
      case 'billing-success':
        return (
          <BillingSuccessPage
            user={user}
            onContinue={() => setCurrentPage('home')}
            refreshUser={refreshUser}
          />
        );
      default:
        return (
          <HomePage
            guides={guides}
            sharedGuides={sharedGuides}
            recentGuideIds={recentGuides}
            onSelectGuide={loadGuideDetails}
            onDeleteGuide={handleDeleteGuide}
            deletingGuideId={deletingGuideId}
            onExport={handleExport}
            exportingGuide={exportingGuide}
            favoriteGuideIds={favoriteGuideIds}
            onToggleFavorite={toggleFavorite}
            projects={projects}
            onQuickShare={handleQuickShare}
            user={user}
            loadingGuides={loadingGuides}
          />
        );
    }
  };

  // Public pages render without MainLayout (they have their own layout)
  if (currentPage === 'docs') return <DocumentationPage />;

  return (
    <>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .btn-spinner {
          display: inline-block; width: 14px; height: 14px;
          border: 2.5px solid rgba(255,255,255,0.3); border-top-color: white;
          border-radius: 50%; animation: spin 0.8s linear infinite;
          vertical-align: middle; flex-shrink: 0;
        }
        .btn-spinner-sm {
          display: inline-block; width: 14px; height: 14px;
          border: 2.5px solid #e2e8f0; border-top-color: ${theme.primary};
          border-radius: 50%; animation: spin 0.8s linear infinite;
          vertical-align: middle; flex-shrink: 0;
        }
      `}</style>
      <MainLayout
        user={user}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        sidebarCollapsed={sidebarCollapsed}
        setSidebarCollapsed={setSidebarCollapsed}
        onLogout={handleLogout}
        planFeatures={planFeatures}
      >
        {/* Impersonation banner (License Admin "Sign in as user").
            Renders only when user.impersonation is set. Persistent amber
            top-of-page bar with the target user's identity + a "Return
            to admin" button that ends the session cleanly. */}
        <ImpersonationBanner user={user} />
        {/* Top-of-content reminder banner (free-near-limit / canceling-soon).
            Self-hides when nothing applies or user dismissed within cooldown. */}
        <BillingReminderBanner
          user={user}
          guideCount={guides.length}
          planLimit={planGuideLimit}
          setCurrentPage={setCurrentPage}
        />
        {/* Trial banner — visible only on days 8+ of a Pro (15-day)
            or Business (30-day) trial, on a 3-day re-show cadence
            after dismissal. Self-hides on days 1-7 to give the user
            a quiet onboarding window. Reads org trial state from
            user.organization (populated by /api/auth/me). */}
        <TrialBanner
          user={user}
          setCurrentPage={setCurrentPage}
        />
        {/* Page shell renders immediately; guides + projects + shared list stream in
            from loadGuides() in the background. Inline empty-state loaders inside
            each page surface the loading state where the data goes. */}
        {renderPageContent()}
      </MainLayout>

      {/* Custom Confirm Dialog */}
      {confirmDialog.open && (
        <ConfirmDialog
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmText={confirmDialog.confirmText}
          cancelText={confirmDialog.cancelText}
          type={confirmDialog.type}
          onConfirm={() => {
            confirmDialog.onConfirm?.();
            setConfirmDialog({ ...confirmDialog, open: false });
          }}
          onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
        />
      )}

      {/* Plan Upgrade Prompt — shown when a 403 with code PLAN_LIMIT or FEATURE_NOT_AVAILABLE is returned */}
      {upgradePrompt.open && (
        <UpgradePromptDialog
          title={upgradePrompt.title}
          message={upgradePrompt.message}
          upgradeUrl={upgradePrompt.upgradeUrl}
          onClose={() => setUpgradePrompt(prev => ({ ...prev, open: false }))}
          onUpgrade={() => setCurrentPage('pricing')}
        />
      )}

      {/* Custom Prompt Dialog */}
      {promptDialog.open && (
        <PromptDialog
          title={promptDialog.title}
          message={promptDialog.message}
          defaultValue={promptDialog.defaultValue}
          placeholder={promptDialog.placeholder}
          submitText={promptDialog.submitText}
          cancelText={promptDialog.cancelText}
          onSubmit={(value) => {
            promptDialog.onSubmit?.(value);
            setPromptDialog({ ...promptDialog, open: false });
          }}
          onCancel={() => setPromptDialog({ ...promptDialog, open: false })}
        />
      )}

      {/* Quick Share Modal */}
      {quickShareGuideId && (
        <>
          <style>{`
            .qs-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.4); z-index: 9999; display: flex; align-items: center; justify-content: center; animation: qsFadeIn 0.15s ease; }
            @keyframes qsFadeIn { from { opacity: 0; } to { opacity: 1; } }
            .qs-modal { background: white; border-radius: 16px; width: 420px; max-width: 90vw; box-shadow: 0 20px 60px rgba(0,0,0,0.2); animation: qsSlideIn 0.2s ease; }
            @keyframes qsSlideIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
            .qs-header { padding: 20px 24px 16px; border-bottom: 1px solid #f1f5f9; display: flex; align-items: center; justify-content: space-between; }
            .qs-header h3 { font-size: 16px; font-weight: 700; color: #1e293b; margin: 0; }
            .qs-close { background: none; border: none; font-size: 20px; color: #94a3b8; cursor: pointer; padding: 4px 8px; border-radius: 6px; }
            .qs-close:hover { background: #f1f5f9; color: #64748b; }
            .qs-body { padding: 16px 24px; max-height: 400px; overflow-y: auto; }
            .qs-section-title { font-size: 12px; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.4px; margin: 0 0 10px 0; }
            .qs-member { display: flex; align-items: center; gap: 10px; padding: 8px 0; }
            .qs-member + .qs-member { border-top: 1px solid #f1f5f9; }
            .qs-avatar { width: 32px; height: 32px; border-radius: 50%; background: ${theme.gradient}; color: white; display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 600; flex-shrink: 0; }
            .qs-member-info { flex: 1; min-width: 0; }
            .qs-member-name { font-size: 13px; font-weight: 500; color: #1e293b; }
            .qs-member-email { font-size: 11px; color: #94a3b8; }
            .qs-member-remove { background: none; border: none; color: #ef4444; font-size: 12px; cursor: pointer; padding: 4px 8px; border-radius: 4px; }
            .qs-member-remove:hover { background: #fef2f2; }
            .qs-member input[type="checkbox"] { accent-color: ${theme.primary}; width: 16px; height: 16px; }
            .qs-permission { padding: 6px 10px; border: 1px solid #e2e8f0; border-radius: 6px; font-size: 12px; color: #64748b; background: white; }
            .qs-empty { text-align: center; padding: 20px; color: #94a3b8; font-size: 13px; }
            .qs-footer { padding: 16px 24px; border-top: 1px solid #f1f5f9; display: flex; justify-content: flex-end; gap: 8px; }
            .qs-btn-cancel { padding: 8px 16px; background: white; border: 1px solid #e2e8f0; border-radius: 8px; font-size: 13px; cursor: pointer; color: #64748b; }
            .qs-btn-share { padding: 8px 20px; background: ${theme.gradient}; border: none; border-radius: 8px; font-size: 13px; font-weight: 600; color: white; cursor: pointer; }
            .qs-btn-share:disabled { opacity: 0.5; cursor: not-allowed; }
            .qs-divider { height: 1px; background: #e2e8f0; margin: 14px 0; }
          `}</style>
          <div className="qs-overlay" onClick={handleQuickShareClose}>
            <div className="qs-modal" onClick={(e) => e.stopPropagation()}>
              <div className="qs-header">
                <h3>Share "{quickShareGuideName}"</h3>
                <button className="qs-close" onClick={handleQuickShareClose}>×</button>
              </div>
              <div className="qs-body">
                {quickShareShares.length > 0 && (
                  <>
                    <p className="qs-section-title">Currently shared with</p>
                    {quickShareShares.map(share => (
                      <div key={share.id} className="qs-member">
                        <div className="qs-avatar">{share.sharedWithName?.charAt(0).toUpperCase() || 'U'}</div>
                        <div className="qs-member-info">
                          <div className="qs-member-name">{share.sharedWithName}</div>
                          <div className="qs-member-email">{share.sharedWithEmail}</div>
                        </div>
                        <span style={{ fontSize: '11px', color: '#94a3b8', padding: '3px 8px', background: '#f1f5f9', borderRadius: '4px' }}>
                          {share.permission === 'edit' ? 'Can edit' : 'Can view'}
                        </span>
                      </div>
                    ))}
                    <div className="qs-divider" />
                  </>
                )}
                <p className="qs-section-title">Share with team members</p>
                {quickShareLoading ? (
                  <div className="qs-empty">Loading...</div>
                ) : quickShareMembers.length === 0 ? (
                  <div className="qs-empty">No more team members to share with</div>
                ) : (
                  <>
                    {quickShareMembers.map(member => (
                      <div key={member.id} className="qs-member">
                        <input
                          type="checkbox"
                          checked={quickShareSelected.includes(member.id)}
                          onChange={(e) => {
                            if (e.target.checked) setQuickShareSelected(prev => [...prev, member.id]);
                            else setQuickShareSelected(prev => prev.filter(id => id !== member.id));
                          }}
                        />
                        <div className="qs-avatar">{member.name?.charAt(0).toUpperCase() || 'U'}</div>
                        <div className="qs-member-info">
                          <div className="qs-member-name">{member.name}</div>
                          <div className="qs-member-email">{member.email}</div>
                        </div>
                      </div>
                    ))}
                    <div style={{ marginTop: 12 }}>
                      <select className="qs-permission" value={quickSharePermission} onChange={(e) => setQuickSharePermission(e.target.value)}>
                        <option value="view">Can view</option>
                        <option value="edit">Can edit</option>
                      </select>
                    </div>
                  </>
                )}
              </div>
              <div className="qs-footer">
                <button className="qs-btn-cancel" onClick={handleQuickShareClose}>Cancel</button>
                <button className="qs-btn-share" onClick={handleQuickShareSubmit} disabled={quickShareSelected.length === 0 || quickShareLoading}>
                  {quickShareLoading ? 'Sharing...' : 'Share'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      {/* Ask Insta — persistent in-app chatbot. Floats bottom-right when
          minimized; expands to a right-side panel on click. Only renders
          when the user is authenticated. Reads authToken from localStorage
          on each request, matching the rest of App.jsx's auth pattern. */}
      {user && (
        <AskInstaChatbot user={user} apiUrl={API_URL} showToast={showToast} />
      )}
    </>
  );
}

// ============================================
// Ask Insta — chat with your guides
// ============================================
// In-app chatbot that searches across the user's accessible guides (own +
// shared), picks the relevant ones, and answers from their actual content
// with clickable citations back to the guide. Backed by
// POST /api/ai/ask-guides which routes through the org's AI provider.
// Storage key for the persisted conversation. Bumping the suffix invalidates
// any old shape from previous releases — current shape is { messages, focusGuideId }.
const ASK_INSTA_STORAGE_KEY = 'ask-insta-conversation-v1';

function AskInstaChatbot({ user, apiUrl, showToast }) {
  // Plan gate. We DON'T early-return here (would violate rules-of-
  // hooks if the plan changed mid-session). Instead, all hooks run
  // unconditionally and the final JSX return checks the flag —
  // free + starter accounts see nothing, Pro+/Business/Enterprise
  // see the chat tab. Same flag lives on backend FEATURE_LABELS so
  // an upgrade dialog renders the friendly label if a Free user
  // somehow hits the API directly.
  const hasAskInstaAccess = !!user?.planFeatures?.askInstaChatbot;

  const [isOpen, setIsOpen] = useState(false);
  // Hydrate from localStorage on first render so the conversation survives
  // page navigation, refresh, and any React remounts above us. We only clear
  // it on explicit user action ("Clear chat" button) or on logout — see
  // handleLogout in App() which removes the same key.
  const initial = (() => {
    try {
      const raw = localStorage.getItem(ASK_INSTA_STORAGE_KEY);
      if (!raw) return { messages: [], focusGuideId: null };
      const parsed = JSON.parse(raw);
      return {
        messages: Array.isArray(parsed.messages) ? parsed.messages : [],
        focusGuideId: parsed.focusGuideId || null,
      };
    } catch { return { messages: [], focusGuideId: null }; }
  })();
  const [messages, setMessages] = useState(initial.messages);
  const [input, setInput] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [focusGuideId, setFocusGuideId] = useState(initial.focusGuideId);
  const messagesEndRef = useRef(null);
  // Ref to the DOM node of the most-recent user message. When a fresh
  // answer arrives, we scroll that node to the top of the panel so the
  // question sits at the top and the answer flows downward from its
  // beginning — otherwise long answers land with the last line pinned
  // to the bottom of the viewport and the user has to scroll UP to
  // find where the answer starts.
  const lastUserMsgRef = useRef(null);

  // Persist on every change. Cheap — the conversation is small (we trim the
  // tail to 8 messages on the server anyway), and stringifying is fast.
  useEffect(() => {
    try {
      localStorage.setItem(ASK_INSTA_STORAGE_KEY, JSON.stringify({ messages, focusGuideId }));
    } catch { /* quota exceeded / private mode — fall back to in-memory only */ }
  }, [messages, focusGuideId]);

  useEffect(() => {
    // Two-mode scroll:
    //   (a) While thinking OR the newest message is from the user:
    //       stick to the bottom so the thinking indicator / just-sent
    //       question stays visible.
    //   (b) Otherwise (assistant reply just landed): scroll the
    //       matching user question to the TOP of the viewport so the
    //       answer reads top-to-bottom from its opening line.
    const last = messages[messages.length - 1];
    if (isThinking || last?.role === 'user') {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    } else if (last?.role === 'assistant' && lastUserMsgRef.current) {
      lastUserMsgRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isThinking]);

  // Toggle a body class so the page's scroll-to-top button (and any other
  // bottom-right floaters) can be hidden via CSS while the chat panel is
  // open — prevents the "ghost icon peeking through the panel" issue.
  useEffect(() => {
    document.body.classList.toggle('ask-insta-open', isOpen);
    return () => document.body.classList.remove('ask-insta-open');
  }, [isOpen]);

  const send = async () => {
    const q = input.trim();
    if (!q || isThinking) return;
    setMessages((m) => [...m, { role: 'user', content: q }]);
    setInput('');
    setIsThinking(true);
    try {
      const response = await fetch(`${apiUrl}/api/ai/ask-guides`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
        body: JSON.stringify({
          question: q,
          conversationHistory: messages.slice(-8),
          focusGuideId
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to get an answer');

      setMessages((m) => [...m, {
        role: 'assistant',
        content: data.answer,
        citedGuides: data.citedGuides || [],
        // Sprint L — step-level citations render as rich cards with
        // screenshot thumbnails below the message bubble. Backend
        // attaches these when the AI answers with [guide:ID#step:N]
        // markers during a drill-down turn.
        citedSteps: data.citedSteps || [],
      }]);
      // If exactly one guide was cited, lock that as the follow-up focus so
      // the next question can drill into its full step details server-side.
      if (data.citedGuides?.length === 1) setFocusGuideId(data.citedGuides[0].id);
      else if (!data.citedGuides?.length) setFocusGuideId(null);
    } catch (error) {
      setMessages((m) => [...m, {
        role: 'assistant',
        content: `Sorry, I hit an error: ${error.message}`,
        citedGuides: [],
        citedSteps: []
      }]);
      if (showToast) showToast(error.message, 'error');
    } finally {
      setIsThinking(false);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  // Strip [guide:ID] and [guide:ID#step:N] markers from user-visible
  // text — the server parses them into citedGuides / citedSteps and
  // the frontend renders them as cards below the bubble.
  const cleanText = (text) => String(text || '')
    .replace(/\[guide:[\w-]+(?:#step:\d+)?\]/g, '')
    .replace(/[ \t]+/g, ' ')
    .replace(/ ?\n ?/g, '\n')
    .trim();

  // Lightweight markdown → JSX renderer for chat bubbles. Handles
  // the shapes the AI actually emits:
  //   - Bullet lists (lines starting with `* ` or `- `)
  //   - Numbered lists (lines starting with `1. `, `2. `, ...)
  //   - **bold**, *italic*, `code` inline
  //   - Paragraph breaks on blank lines
  // Intentionally minimal — no headings, no links, no tables.
  // Anything more elaborate is a signal the AI is over-formatting.
  const renderInline = (line, keyPrefix) => {
    // Split on inline markdown tokens while keeping the delimiters.
    // Regex ordered so **bold** wins over *italic*.
    const parts = line.split(/(\*\*[^*]+\*\*|\*[^*\n]+\*|`[^`\n]+`)/g);
    return parts.map((p, i) => {
      const key = `${keyPrefix}-${i}`;
      if (/^\*\*[^*]+\*\*$/.test(p)) {
        return <strong key={key}>{p.slice(2, -2)}</strong>;
      }
      if (/^\*[^*\n]+\*$/.test(p)) {
        return <em key={key}>{p.slice(1, -1)}</em>;
      }
      if (/^`[^`\n]+`$/.test(p)) {
        return <code key={key}>{p.slice(1, -1)}</code>;
      }
      return <span key={key}>{p}</span>;
    });
  };

  const renderMarkdown = (raw) => {
    const cleaned = cleanText(raw);
    if (!cleaned) return null;
    // Split on lines; group consecutive bullet lines into a single <ul>.
    const lines = cleaned.split('\n');
    const blocks = [];
    let listBuffer = null;   // { type: 'ul' | 'ol', items: [] }
    let paraBuffer = [];

    const flushPara = () => {
      if (paraBuffer.length) {
        blocks.push({ kind: 'p', lines: paraBuffer });
        paraBuffer = [];
      }
    };
    const flushList = () => {
      if (listBuffer) {
        blocks.push({ kind: listBuffer.type, items: listBuffer.items });
        listBuffer = null;
      }
    };

    for (const rawLine of lines) {
      const line = rawLine.replace(/\s+$/, '');
      const trimmed = line.trim();
      const isBullet = /^[*-]\s+/.test(trimmed);
      const olMatch = trimmed.match(/^(\d+)\.\s+/);
      if (isBullet) {
        flushPara();
        if (!listBuffer || listBuffer.type !== 'ul') {
          flushList();
          listBuffer = { type: 'ul', items: [] };
        }
        listBuffer.items.push(trimmed.replace(/^[*-]\s+/, ''));
      } else if (olMatch) {
        flushPara();
        if (!listBuffer || listBuffer.type !== 'ol') {
          flushList();
          listBuffer = { type: 'ol', items: [] };
        }
        listBuffer.items.push(trimmed.slice(olMatch[0].length));
      } else if (trimmed === '') {
        flushPara();
        flushList();
      } else {
        flushList();
        paraBuffer.push(trimmed);
      }
    }
    flushPara();
    flushList();

    return blocks.map((b, i) => {
      if (b.kind === 'p') {
        return (
          <p key={`p-${i}`} className="ai-chat-p">
            {b.lines.map((ln, li) => (
              <span key={li}>
                {renderInline(ln, `p${i}-${li}`)}
                {li < b.lines.length - 1 && <br />}
              </span>
            ))}
          </p>
        );
      }
      if (b.kind === 'ul' || b.kind === 'ol') {
        const Tag = b.kind === 'ol' ? 'ol' : 'ul';
        return (
          <Tag key={`l-${i}`} className={`ai-chat-${b.kind}`}>
            {b.items.map((item, ii) => (
              <li key={ii}>{renderInline(item, `l${i}-${ii}`)}</li>
            ))}
          </Tag>
        );
      }
      return null;
    });
  };

  if (!user) return null;
  // Plan gate from hasAskInstaAccess (computed at the top of the
  // function). Placed AFTER all hooks have run so the hook order
  // stays stable even if plan flips during the session.
  if (!hasAskInstaAccess) return null;

  return (
    <>
      {!isOpen && (
        <button
          className="ai-chat-fab"
          onClick={() => setIsOpen(true)}
          title="Ask Insta — chat with your guides"
          aria-label="Open Ask Insta"
        >
          {/* Chat bubble with a sparkle accent — combines the two cues that
              describe this widget: it's a chat surface AND it's powered by
              AI. The bubble shape distinguishes it from the recording
              floater's lightning bolt; the sparkle marks it as an AI
              chat (vs. a generic support widget). */}
          <span className="ai-chat-fab-iconwrap" aria-hidden="true">
            <svg className="ai-chat-fab-bubble" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
            </svg>
            <svg className="ai-chat-fab-sparkle" width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
              <path d="M11 3 L13 8.5 L18.5 10.5 L13 12.5 L11 18 L9 12.5 L3.5 10.5 L9 8.5 Z" />
            </svg>
          </span>
        </button>
      )}

      {isOpen && (
        <div className="ai-chat-panel" role="dialog" aria-label="Ask Insta">
          <div className="ai-chat-header">
            <div className="ai-chat-title">
              <span className="ai-chat-avatar" aria-hidden="true">
                {/* Chat bubble + sparkle accent — mirrors the collapsed
                    right-edge tab so the open/closed states read as the
                    same surface morphing rather than two different
                    widgets. Brand context ("Ask Insta") is carried by
                    the title text right next to this icon; the icon's
                    job here is to communicate function (AI chat). */}
                <span className="ai-chat-avatar-iconwrap">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
                  </svg>
                  <svg className="ai-chat-avatar-sparkle" width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11 3 L13 8.5 L18.5 10.5 L13 12.5 L11 18 L9 12.5 L3.5 10.5 L9 8.5 Z" />
                  </svg>
                </span>
              </span>
              <span className="ai-chat-titletext">
                <span className="ai-chat-name">Ask Insta</span>
                <span className="ai-chat-sub">Chat with your guides</span>
              </span>
            </div>
            <div className="ai-chat-header-actions">
              {/* Clear chat — resets the conversation in place. Disabled when
                  there's nothing to clear (no messages, no input). */}
              <button
                className="ai-chat-icon-btn"
                onClick={() => {
                  setMessages([]);
                  setInput('');
                  setFocusGuideId(null);
                }}
                disabled={messages.length === 0 && !input}
                aria-label="Start new chat"
                title="Start new chat"
              >
                {/* Circular reset arrow — reads as "start over" instead of
                    the delete/trash icon we used before, which readers
                    confused with "delete this message". */}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="1 4 1 10 7 10"/>
                  <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
                </svg>
              </button>
              {/* Minimize — collapses the panel back to the floating "Ask
                  Insta" button. Conversation is preserved (we persist to
                  localStorage); only "Clear chat" wipes it, or logging out. */}
              <button
                className="ai-chat-icon-btn"
                onClick={() => setIsOpen(false)}
                aria-label="Minimize"
                title="Minimize (your conversation is saved)"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
              </button>
            </div>
          </div>

          <div className="ai-chat-messages">
            {messages.length === 0 && (
              <div className="ai-chat-empty">
                <p>Ask me anything about your guides — I'll search across your library and answer from their actual content.</p>
                <div className="ai-chat-suggestions">
                  {[
                    'Which guides cover Salesforce?',
                    'Summarize my most recently updated guide',
                    'What workflows do I have for onboarding?'
                  ].map((s) => (
                    <button key={s} onClick={() => setInput(s)}>{s}</button>
                  ))}
                </div>
              </div>
            )}
            {(() => {
              // Precompute the index of the LAST user message so we can
              // attach lastUserMsgRef only to that one node — used by the
              // "scroll question to top after answer" effect above.
              let lastUserIdx = -1;
              for (let j = messages.length - 1; j >= 0; j--) {
                if (messages[j].role === 'user') { lastUserIdx = j; break; }
              }
              return messages.map((msg, i) => (
              <div
                key={i}
                ref={i === lastUserIdx ? lastUserMsgRef : null}
                className={`ai-chat-msg ${msg.role}`}
              >
                <div className="ai-chat-bubble">
                  {msg.role === 'assistant'
                    ? renderMarkdown(msg.content)
                    : cleanText(msg.content)}
                </div>
                {msg.role === 'assistant' && msg.citedSteps?.length > 0 && (
                  <div className="ai-chat-step-cards">
                    {msg.citedSteps.map((s) => (
                      <a
                        key={`${s.guideId}#${s.stepNumber}`}
                        href={`/guide/${s.guideId}?step=${s.stepNumber}`}
                        className="ai-chat-step-card"
                      >
                        {s.screenshot ? (
                          <div className="ai-chat-step-thumb">
                            <img src={s.screenshot} alt={`Step ${s.stepNumber}`} />
                            {s.clickData?.x != null && s.clickData?.viewportSize?.width && s.clickData?.viewportSize?.height && (
                              <span
                                className="ai-chat-step-click-dot"
                                style={{
                                  left: `${(s.clickData.x / s.clickData.viewportSize.width) * 100}%`,
                                  top: `${(s.clickData.y / s.clickData.viewportSize.height) * 100}%`,
                                }}
                              />
                            )}
                          </div>
                        ) : (
                          <div className="ai-chat-step-thumb ai-chat-step-thumb-placeholder">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                          </div>
                        )}
                        <div className="ai-chat-step-meta">
                          <div className="ai-chat-step-header">
                            <span className="ai-chat-step-num">Step {s.stepNumber}</span>
                            <span className="ai-chat-step-guide">{s.guideName}</span>
                          </div>
                          {s.action && <div className="ai-chat-step-action">{s.action}</div>}
                          {s.description && <div className="ai-chat-step-desc">{s.description}</div>}
                          <div className="ai-chat-step-open">
                            Open in guide
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <line x1="5" y1="12" x2="19" y2="12"/>
                              <polyline points="12 5 19 12 12 19"/>
                            </svg>
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                )}
                {msg.role === 'assistant' && msg.citedGuides?.length > 0 && (
                  <div className="ai-chat-cites">
                    {msg.citedGuides.map((g) => (
                      <a key={g.id} href={`/guide/${g.id}`} className="ai-chat-cite">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                          <polyline points="14 2 14 8 20 8"/>
                        </svg>
                        <span className="ai-chat-cite-name">{g.name}</span>
                        {g.domain && <span className="ai-chat-cite-domain">{g.domain}</span>}
                      </a>
                    ))}
                  </div>
                )}
              </div>
              ));
            })()}
            {isThinking && (
              <div className="ai-chat-msg assistant">
                <div className="ai-chat-bubble ai-chat-thinking">
                  <span className="ai-chat-dot"></span>
                  <span className="ai-chat-dot"></span>
                  <span className="ai-chat-dot"></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="ai-chat-input-row">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder="Ask about your guides…"
              rows={1}
              className="ai-chat-input"
              disabled={isThinking}
            />
            <button
              onClick={send}
              disabled={!input.trim() || isThinking}
              className="ai-chat-send"
              aria-label="Send message"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M2 21l21-9L2 3v7l15 2-15 2v7z"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      <style>{`
        /* Right-edge tab — icon-only, hugs the right edge of the viewport.
           Purple gradient + sparkles icon to differentiate from the indigo
           lightning-bolt recording floater that lives on the same edge.
           Both can be visible together; the color/icon shift makes them
           read as two distinct affordances (AI assistant vs. recording CTA)
           instead of two instances of the same widget.
           Vertically positioned around 40% from the top so it's visible
           without colliding with the bottom-right scroll-to-top button or
           floating recorder.
           Rounded only on the LEFT side so it visually attaches to the
           edge. Shadow casts to the left. Slides 4px out on hover. */
        .ai-chat-fab {
          /* Positioned in the lower third of the viewport (top: 70%) so
             it stays clearly above the bottom-right recording floater +
             scroll-to-top button but doesn't sit dead-center where the
             user's attention is on the content. */
          position: fixed; top: 70%; right: 0;
          display: inline-flex; align-items: center; justify-content: center;
          width: 44px; height: 52px;
          padding: 0;
          border-radius: 12px 0 0 12px;
          background: linear-gradient(135deg, #8b5cf6, #7c3aed);
          color: white; border: none; cursor: pointer;
          box-shadow: -6px 4px 18px -4px rgba(124,58,237,0.45),
                      -2px 2px 6px rgba(0,0,0,0.08);
          z-index: 9998;
          transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
        }
        /* Icon overlay — chat bubble with a sparkle in the top-right
           corner of the bubble. The sparkle uses currentColor (white)
           and overlaps the bubble's edge to read as a "this chat is AI"
           accent rather than a separate icon. */
        .ai-chat-fab-iconwrap {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 22px;
          height: 22px;
        }
        .ai-chat-fab-sparkle {
          position: absolute;
          top: -3px;
          right: -4px;
          filter: drop-shadow(0 0 1.5px rgba(124,58,237,0.9));
        }
        .ai-chat-fab:hover {
          transform: translateX(-4px);
          box-shadow: -10px 6px 24px -6px rgba(124,58,237,0.55),
                      -4px 4px 10px rgba(0,0,0,0.10);
          filter: brightness(1.06);
        }
        .ai-chat-fab:focus-visible {
          outline: 2px solid #7c3aed;
          outline-offset: 2px;
        }
        /* The label span lives in the markup for backward compatibility but
           the new tab UI is icon-only — keep it visually hidden but still
           accessible to screen readers. */
        .ai-chat-fab-label {
          position: absolute;
          width: 1px; height: 1px;
          margin: -1px; padding: 0;
          border: 0; overflow: hidden;
          clip: rect(0 0 0 0); clip-path: inset(50%);
          white-space: nowrap;
        }

        .ai-chat-panel {
          position: fixed; bottom: 0; right: 0;
          width: 420px; max-width: calc(100vw - 32px);
          height: min(720px, calc(100vh - 32px));
          margin: 16px;
          background: white;
          border-radius: 16px;
          box-shadow: 0 25px 60px -10px rgba(0,0,0,0.25),
                      0 10px 25px rgba(0,0,0,0.10);
          display: flex; flex-direction: column;
          /* Highest stacking on the page so no other fixed-position widget
             (scroll-to-top, extension floater, etc.) peeks through. */
          z-index: 2147483000;
          isolation: isolate;
          overflow: hidden;
          animation: ai-chat-slide-in 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        }
        /* When the panel is open, hide bottom-right floaters that would
           otherwise show through the panel's rounded corner. */
        body.ask-insta-open .scroll-to-top-btn,
        body.ask-insta-open .insta-floater,
        body.ask-insta-open .floating-recorder { display: none !important; }

        @keyframes ai-chat-slide-in {
          /* Slide in from the right edge so the open animation visually
             continues from the right-edge tab's position. */
          from { transform: translateX(30px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        /* Dark navy header with subtle indigo glow — matches the extension
           popup and the /login splash. Lets the brand-coloured mark on the
           left actually pop as the indigo gradient it should be. */
        .ai-chat-header {
          position: relative;
          display: flex; align-items: center; justify-content: space-between;
          padding: 16px 18px;
          background:
            radial-gradient(120% 100% at 0% 0%, rgba(99,102,241,0.35) 0%, transparent 60%),
            linear-gradient(180deg, #1e293b 0%, #0f172a 100%);
          color: white;
          flex-shrink: 0;
          border-bottom: 1px solid rgba(255,255,255,0.06);
        }
        .ai-chat-title { display: flex; align-items: center; gap: 12px; }
        /* Purple gradient rounded square with chat + sparkle — matches
           the collapsed right-edge tab so open/closed feels like one
           surface in two states. Gradient + shadow tint kept in sync
           with the .ai-chat-fab class. */
        .ai-chat-avatar {
          width: 36px; height: 36px; border-radius: 10px;
          background: linear-gradient(135deg, #8b5cf6, #7c3aed);
          display: inline-flex; align-items: center; justify-content: center;
          color: white;
          box-shadow:
            0 6px 14px -2px rgba(124,58,237,0.55),
            inset 0 1px rgba(255,255,255,0.35);
        }
        /* Inner icon-overlay positioning — sparkle sits in the top-right
           corner of the chat bubble, slightly bleeding outside the bubble
           so it reads as an AI accent on the chat icon. */
        .ai-chat-avatar-iconwrap {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
        }
        .ai-chat-avatar-sparkle {
          position: absolute;
          top: -2px;
          right: -3px;
          filter: drop-shadow(0 0 1.5px rgba(124,58,237,0.95));
        }
        .ai-chat-titletext { display: flex; flex-direction: column; line-height: 1.2; }
        .ai-chat-name { font-weight: 800; font-size: 14.5px; color: #ffffff; letter-spacing: -0.2px; }
        .ai-chat-sub { font-size: 11.5px; color: #94a3b8; margin-top: 2px; }
        .ai-chat-header-actions { display: inline-flex; align-items: center; gap: 4px; }
        .ai-chat-icon-btn {
          background: transparent; border: none; color: #cbd5e1; cursor: pointer;
          width: 30px; height: 30px;
          display: inline-flex; align-items: center; justify-content: center;
          border-radius: 8px; opacity: 0.85;
          transition: all 0.15s;
        }
        .ai-chat-icon-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.10); color: white; opacity: 1;
        }
        .ai-chat-icon-btn:disabled {
          opacity: 0.35; cursor: not-allowed;
        }

        .ai-chat-messages {
          flex: 1; overflow-y: auto;
          padding: 16px;
          display: flex; flex-direction: column; gap: 12px;
          background: #f8fafc;
        }
        .ai-chat-empty {
          color: #64748b; font-size: 13.5px; line-height: 1.55;
          padding: 16px 8px;
        }
        .ai-chat-empty p { margin: 0 0 14px; }
        .ai-chat-suggestions { display: flex; flex-direction: column; gap: 6px; }
        .ai-chat-suggestions button {
          padding: 9px 13px; border-radius: 10px;
          background: white; border: 1px solid #e2e8f0;
          color: #475569; font-size: 12.5px; font-weight: 500;
          text-align: left; cursor: pointer;
          transition: all 0.15s;
        }
        .ai-chat-suggestions button:hover {
          background: #eef2ff; border-color: #c7d2fe; color: #4f46e5;
        }

        .ai-chat-msg { display: flex; flex-direction: column; gap: 6px; max-width: 100%; }
        .ai-chat-msg.user { align-items: flex-end; }
        .ai-chat-msg.assistant { align-items: flex-start; }
        .ai-chat-bubble {
          padding: 10px 14px; border-radius: 14px;
          font-size: 13.5px; line-height: 1.5;
          max-width: 86%; white-space: pre-wrap; word-wrap: break-word;
        }
        .ai-chat-msg.user .ai-chat-bubble {
          background: linear-gradient(135deg, #6366f1, #4f46e5);
          color: white; border-bottom-right-radius: 4px;
        }
        .ai-chat-msg.assistant .ai-chat-bubble {
          background: white; color: #0f172a;
          border: 1px solid #e2e8f0;
          border-bottom-left-radius: 4px;
        }
        .ai-chat-thinking {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 14px 16px;
        }
        .ai-chat-dot {
          width: 6px; height: 6px; border-radius: 50%;
          background: #6366f1;
          animation: ai-chat-dot 1.4s ease-in-out infinite;
        }
        .ai-chat-dot:nth-child(2) { animation-delay: 0.2s; }
        .ai-chat-dot:nth-child(3) { animation-delay: 0.4s; }
        @keyframes ai-chat-dot {
          0%, 60%, 100% { opacity: 0.3; transform: scale(0.85); }
          30% { opacity: 1; transform: scale(1.15); }
        }

        .ai-chat-cites {
          display: flex; flex-direction: column; gap: 5px;
          max-width: 86%;
        }
        .ai-chat-cite {
          display: flex; align-items: center; gap: 8px;
          padding: 8px 12px; border-radius: 10px;
          background: #eef2ff; border: 1px solid #c7d2fe;
          color: #4f46e5; font-size: 12.5px; font-weight: 600;
          text-decoration: none;
          transition: all 0.15s;
        }
        .ai-chat-cite:hover {
          background: #e0e7ff; border-color: #818cf8;
          transform: translateY(-1px);
          box-shadow: 0 4px 10px -2px rgba(99,102,241,0.25);
        }
        .ai-chat-cite-name { flex: 1; }
        .ai-chat-cite-domain {
          font-size: 10.5px; font-weight: 500; color: #6366f1; opacity: 0.7;
        }

        /* Markdown rendering inside assistant bubbles. Kept tight so
           the chat stays compact — no giant paragraph gaps, no double
           line-heights. */
        .ai-chat-p { margin: 0 0 8px 0; line-height: 1.5; }
        .ai-chat-p:last-child { margin-bottom: 0; }
        .ai-chat-ul, .ai-chat-ol {
          margin: 6px 0 8px 0; padding-left: 20px; line-height: 1.5;
        }
        .ai-chat-ul:last-child, .ai-chat-ol:last-child { margin-bottom: 0; }
        .ai-chat-ul li, .ai-chat-ol li { margin: 3px 0; }
        .ai-chat-bubble strong { font-weight: 700; color: inherit; }
        .ai-chat-bubble em { font-style: italic; }
        .ai-chat-bubble code {
          font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', monospace;
          font-size: 0.9em;
          padding: 1px 5px; border-radius: 4px;
          background: rgba(99,102,241,0.10);
          color: #4f46e5;
        }

        /* Step-level citation cards — richer than guide cites since they
           include a screenshot thumbnail with click indicator. Rendered
           by the frontend when the backend returns citedSteps[] on
           drill-down turns. */
        .ai-chat-step-cards {
          display: flex; flex-direction: column; gap: 8px;
          max-width: 100%;
        }
        .ai-chat-step-card {
          display: flex; gap: 10px;
          padding: 8px; border-radius: 12px;
          background: white; border: 1px solid #e2e8f0;
          text-decoration: none; color: inherit;
          transition: all 0.15s;
        }
        .ai-chat-step-card:hover {
          border-color: #a78bfa;
          box-shadow: 0 6px 16px -4px rgba(139,92,246,0.20);
          transform: translateY(-1px);
        }
        .ai-chat-step-thumb {
          position: relative; flex-shrink: 0;
          width: 96px; height: 64px;
          border-radius: 8px; overflow: hidden;
          background: #f1f5f9; border: 1px solid #e2e8f0;
        }
        .ai-chat-step-thumb img {
          width: 100%; height: 100%; object-fit: cover; display: block;
        }
        .ai-chat-step-thumb-placeholder {
          display: flex; align-items: center; justify-content: center;
          color: #94a3b8;
        }
        .ai-chat-step-click-dot {
          position: absolute;
          width: 12px; height: 12px; border-radius: 50%;
          background: #22c55e; border: 2px solid white;
          box-shadow: 0 0 0 3px rgba(34,197,94,0.35);
          transform: translate(-50%, -50%);
          pointer-events: none;
        }
        .ai-chat-step-meta {
          flex: 1; min-width: 0;
          display: flex; flex-direction: column; gap: 2px;
        }
        .ai-chat-step-header {
          display: flex; align-items: center; gap: 6px;
          font-size: 10.5px;
        }
        .ai-chat-step-num {
          font-weight: 700; color: #7c3aed;
          text-transform: uppercase; letter-spacing: 0.06em;
        }
        .ai-chat-step-guide {
          color: #64748b; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .ai-chat-step-action {
          font-size: 12.5px; font-weight: 600; color: #0f172a;
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .ai-chat-step-desc {
          font-size: 11.5px; color: #475569; line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .ai-chat-step-open {
          display: inline-flex; align-items: center; gap: 4px;
          font-size: 10.5px; font-weight: 700; color: #6366f1;
          margin-top: 2px;
        }

        .ai-chat-input-row {
          display: flex; gap: 8px; padding: 12px;
          background: white; border-top: 1px solid #e2e8f0;
          align-items: flex-end;
          flex-shrink: 0;
        }
        .ai-chat-input {
          flex: 1;
          padding: 10px 12px; border-radius: 10px;
          border: 1px solid #e2e8f0;
          font: 13.5px/1.4 'Inter', system-ui, sans-serif;
          resize: none; min-height: 40px; max-height: 120px;
          color: #0f172a; background: #f8fafc;
          font-family: inherit;
        }
        .ai-chat-input:focus {
          outline: none; border-color: #6366f1;
          box-shadow: 0 0 0 3px rgba(99,102,241,0.15);
          background: white;
        }
        .ai-chat-input:disabled { opacity: 0.6; cursor: not-allowed; }
        .ai-chat-send {
          width: 40px; height: 40px; border-radius: 10px;
          background: linear-gradient(135deg, #6366f1, #4f46e5);
          color: white; border: none; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          transition: all 0.15s;
          flex-shrink: 0;
        }
        .ai-chat-send:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(99,102,241,0.4);
        }
        .ai-chat-send:disabled { opacity: 0.4; cursor: not-allowed; }

        @media (max-width: 520px) {
          .ai-chat-panel {
            width: 100vw; height: 100vh; margin: 0; border-radius: 0;
          }
          .ai-chat-fab-label { display: none; }
          .ai-chat-fab { padding: 14px; }
        }
      `}</style>
    </>
  );
}

// Loading Component
function LoadingView() {
  return (
    <div className="loading-container">
      <style>{`
        .loading-container {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: ${theme.gradient};
          color: white;
        }
        .spinner {
          width: 50px;
          height: 50px;
          border: 4px solid rgba(255,255,255,0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 20px;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      <div className="spinner"></div>
      <p>Loading InstaGuide...</p>
    </div>
  );
}

// Skeleton cards shown while guides are loading. Same dimensions as real guide
// cards so the layout doesn't shift when data arrives. Used across HomePage,
// GuidesListPage, and ProjectsExplorerPage.
function GuideSkeletonGrid({ count = 6 }) {
  return (
    <div className="guides-grid">
      <style>{`
        @keyframes skeleton-pulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
        .skeleton-card {
          background: white;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          padding: 16px;
          height: 220px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .skeleton-block {
          background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
          background-size: 200% 100%;
          animation: skeleton-shine 1.4s ease-in-out infinite, skeleton-pulse 1.4s ease-in-out infinite;
          border-radius: 6px;
        }
        @keyframes skeleton-shine {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .skeleton-thumb { height: 110px; width: 100%; border-radius: 8px; }
        .skeleton-line { height: 12px; width: 100%; }
        .skeleton-line.short { width: 60%; }
        .skeleton-line.tiny { width: 40%; height: 10px; }
      `}</style>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="skeleton-card" aria-busy="true" aria-label="Loading guide">
          <div className="skeleton-block skeleton-thumb"></div>
          <div className="skeleton-block skeleton-line"></div>
          <div className="skeleton-block skeleton-line short"></div>
          <div style={{ marginTop: 'auto', display: 'flex', gap: 8 }}>
            <div className="skeleton-block skeleton-line tiny" style={{ flex: 1 }}></div>
            <div className="skeleton-block skeleton-line tiny" style={{ width: 30 }}></div>
          </div>
        </div>
      ))}
    </div>
  );
}

function GuideSkeletonList({ count = 5 }) {
  return (
    <div className="skeleton-list">
      <style>{`
        .skeleton-list { display: flex; flex-direction: column; gap: 10px; padding: 4px 0; }
        .skeleton-row {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 14px 20px;
          background: white;
          border-bottom: 1px solid #f1f5f9;
        }
        .skeleton-row .skeleton-block {
          background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
          background-size: 200% 100%;
          animation: skeleton-shine 1.4s ease-in-out infinite;
          border-radius: 6px;
        }
        .skeleton-favicon { width: 24px; height: 24px; border-radius: 6px; flex-shrink: 0; }
        .skeleton-row .skeleton-name { height: 14px; flex: 2; }
        .skeleton-row .skeleton-meta { height: 12px; flex: 1; }
      `}</style>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="skeleton-row" aria-busy="true" aria-label="Loading guide">
          <div className="skeleton-block skeleton-favicon"></div>
          <div className="skeleton-block skeleton-name"></div>
          <div className="skeleton-block skeleton-meta"></div>
          <div className="skeleton-block skeleton-meta"></div>
          <div className="skeleton-block skeleton-meta" style={{ flex: 0.5 }}></div>
        </div>
      ))}
    </div>
  );
}

// Sprint K Phase 3 — VTT/SRT enrichment modal. Owner uploads the meeting
// transcript exported from Zoom / Teams / Meet / Webex; backend parses it,
// derives participants, and emits per-step speaker attribution suggestions
// (visible in the Improvements panel — never auto-applied, per Sprint K
// principle). Module scope so the parent re-render that fires after upload
// (guide refetch → setState) doesn't unmount it mid-XHR.
function MeetingEnrichModal({ guideId, hasParticipants, onClose, onComplete, showToast }) {
  const [file, setFile] = React.useState(null);
  const [dragOver, setDragOver] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');
  const fileInputRef = React.useRef(null);

  const accept = '.vtt,.srt,text/vtt,application/x-subrip,text/plain';

  const onPick = (f) => {
    if (!f) return;
    // Cap at 2 MB — server enforces too, but reject early so the user
    // doesn't sit through a network round-trip for a 5 MB transcript.
    if (f.size > 2 * 1024 * 1024) {
      setError('File is too large (max 2 MB). Most VTT transcripts are under 100 KB.');
      return;
    }
    setError('');
    setFile(f);
  };

  const submit = async () => {
    if (!file || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('transcript', file);
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/api/guides/${guideId}/meeting/enrich`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || 'Could not process transcript');
      }
      // Sprint K Phase 3 — alignment-mismatch path. Backend ran the parse
      // + participants extraction (those are correct), but detected the
      // pattern that signals the transcript's clock doesn't share an
      // origin with the recording's clock — every attributed step landed
      // on the same speaker. Surface this distinctly instead of claiming
      // "0 attributions ready" (which reads like a quiet bug).
      if (data.alignmentMismatch) {
        showToast?.(
          `Participants imported (${data.participants?.length || 0}), but the transcript timeline doesn't match the recording. Re-upload with a transcript from the same meeting where you captured this guide.`,
          'warning',
          10000,
        );
      } else {
        showToast?.(
          `Transcript processed — ${data.participants?.length || 0} participant${(data.participants?.length || 0) === 1 ? '' : 's'}, ${data.suggestionsCreated || 0} step attribution${data.suggestionsCreated === 1 ? '' : 's'} ready in the Improvements panel.`,
          'success',
        );
      }
      onComplete?.(data);
      onClose?.();
    } catch (e) {
      setError(e.message || 'Upload failed');
    } finally {
      setSubmitting(false);
    }
  };

  const dropProps = {
    onDragOver: (e) => { e.preventDefault(); setDragOver(true); },
    onDragLeave: () => setDragOver(false),
    onDrop: (e) => {
      e.preventDefault();
      setDragOver(false);
      const f = e.dataTransfer?.files?.[0];
      onPick(f);
    },
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1100, padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 14, width: '100%', maxWidth: 480,
          boxShadow: '0 24px 60px rgba(15, 23, 42, 0.25)',
          overflow: 'hidden',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', gap: 14,
          padding: '18px 22px', borderBottom: '1px solid #f1f5f9',
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: 9,
            background: '#f1f5f9', border: '1px solid #e2e8f0',
            color: '#475569', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <path d="M14 2v6h6"/>
              <path d="M10 14l2 2 4-4"/>
            </svg>
          </div>
          <div>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: '#0f172a', margin: 0 }}>
              {hasParticipants ? 'Update meeting transcript' : 'Add meeting transcript'}
            </h2>
            <p style={{ fontSize: 12, color: '#64748b', margin: '2px 0 0' }}>
              Upload the VTT or SRT exported from Zoom, Teams, Meet, or Webex.
            </p>
          </div>
        </div>

        <div style={{ padding: '18px 22px' }}>
          <div
            {...dropProps}
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: `1.5px dashed ${dragOver ? '#6366f1' : '#cbd5e1'}`,
              background: dragOver ? 'rgba(99, 102, 241, 0.06)' : '#f8fafc',
              borderRadius: 12,
              padding: '22px 16px',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >
            <div style={{ fontSize: 28, lineHeight: 1, color: '#94a3b8', marginBottom: 6 }}>
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block' }}>
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
            </div>
            {file ? (
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{file.name}</div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                  {(file.size / 1024).toFixed(1)} KB · click to choose a different file
                </div>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>
                  Drop transcript here, or click to browse
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                  Supports .vtt and .srt — up to 2 MB
                </div>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept={accept}
              onChange={(e) => onPick(e.target.files?.[0])}
              style={{ display: 'none' }}
            />
          </div>

          {error && (
            <div style={{
              marginTop: 12, padding: '10px 12px',
              background: '#fef2f2', border: '1px solid #fecaca',
              borderRadius: 8, fontSize: 12, color: '#b91c1c',
            }}>
              {error}
            </div>
          )}

          {hasParticipants && !error && (
            <div style={{
              marginTop: 12, padding: '10px 12px',
              background: '#fffbeb', border: '1px solid #fde68a',
              borderRadius: 8, fontSize: 12, color: '#92400e', lineHeight: 1.5,
            }}>
              This guide already has participants from a previous upload. Re-uploading will replace the participant list and propose fresh per-step attributions in the Improvements panel.
            </div>
          )}

          <p style={{ fontSize: 12, color: '#64748b', marginTop: 14, lineHeight: 1.5 }}>
            Speaker badges appear in the Improvements panel for review — we never auto-apply attribution.
          </p>
        </div>

        <div style={{
          display: 'flex', gap: 8, justifyContent: 'flex-end',
          padding: '12px 22px', borderTop: '1px solid #f1f5f9',
          background: '#fafbfc',
        }}>
          <button onClick={onClose} disabled={submitting} style={EMBED_BTN_SECONDARY}>
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!file || submitting}
            style={{
              ...EMBED_BTN_PRIMARY,
              opacity: !file || submitting ? 0.55 : 1,
              cursor: !file || submitting ? 'not-allowed' : 'pointer',
              background: !file || submitting
                ? '#94a3b8'
                : 'linear-gradient(135deg, #6366f1, #4f46e5)',
            }}
          >
            {submitting ? 'Processing…' : 'Upload transcript'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Sprint K Phase 3 — Participants manager. Lets the owner rename, recolor,
// and toggle host on the speakers derived from a meeting transcript.
// Module scope (like MeetingEnrichModal) so the parent state churn after
// save doesn't unmount it mid-XHR. Deletion is intentionally NOT here for
// v1 — step-level meetingAttribution references survive a rename / color
// change but would orphan on delete; if a user really wants to drop a
// participant they can rename it to something innocuous and the badges
// keep working until they re-upload a clean transcript.
const PARTICIPANT_PALETTE = [
  '#8b5cf6', '#f59e0b', '#10b981', '#3b82f6',
  '#ec4899', '#06b6d4', '#84cc16', '#f97316',
  '#ef4444', '#a855f7', '#14b8a6', '#64748b',
];

function ParticipantsPanel({ guideId, participants, onClose, onSaved, showToast }) {
  // Local copy lets us mutate freely until the user clicks Save.
  const [draft, setDraft] = React.useState(() =>
    (participants || []).map(p => ({ ...p })),
  );
  const [saving, setSaving] = React.useState(false);

  const update = (idx, patch) => {
    setDraft(prev => prev.map((p, i) => i === idx ? { ...p, ...patch } : p));
  };

  const setHost = (idx) => {
    // One host at a time. Clicking the host badge on another row reassigns.
    setDraft(prev => prev.map((p, i) => ({ ...p, isHost: i === idx })));
  };

  const save = async () => {
    // Drop empty names defensively — the backend rejects them anyway,
    // but a local guard means the user gets immediate feedback.
    const cleaned = draft
      .map(p => ({ ...p, displayName: (p.displayName || '').trim() }))
      .filter(p => p.displayName.length > 0);
    if (cleaned.length !== draft.length) {
      showToast?.('Every participant needs a name.', 'warning');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/api/guides/${guideId}/participants`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ participants: cleaned }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Could not save participants');
      showToast?.('Participants updated.', 'success');
      onSaved?.(data.participants || cleaned);
      onClose?.();
    } catch (e) {
      showToast?.(e.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1100, padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 14, width: '100%', maxWidth: 520,
          maxHeight: '85vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 24px 60px rgba(15, 23, 42, 0.25)',
          overflow: 'hidden',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', gap: 14,
          padding: '18px 22px', borderBottom: '1px solid #f1f5f9',
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: 9,
            background: '#f1f5f9', border: '1px solid #e2e8f0',
            color: '#475569', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="9" cy="8" r="3.2"/>
              <path d="M2.5 19c.6-3.2 3.3-5.3 6.5-5.3s5.9 2.1 6.5 5.3"/>
              <circle cx="17" cy="6.5" r="2.4"/>
              <path d="M15 12.4c2.5-.2 4.6 1.3 5.5 3.6"/>
            </svg>
          </div>
          <div>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: '#0f172a', margin: 0 }}>
              Meeting participants
            </h2>
            <p style={{ fontSize: 12, color: '#64748b', margin: '2px 0 0' }}>
              Edit names, swap colors, mark the host. Speaker badges on steps update automatically.
            </p>
          </div>
        </div>

        <div style={{ padding: '6px 22px 12px', overflowY: 'auto', flex: 1 }}>
          {draft.length === 0 ? (
            <p style={{ padding: '24px 0', textAlign: 'center', color: '#64748b', fontSize: 13 }}>
              No participants yet. Upload a meeting transcript first.
            </p>
          ) : draft.map((p, idx) => (
            <div key={p.id} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '12px 0', borderBottom: idx === draft.length - 1 ? 'none' : '1px solid #f1f5f9',
            }}>
              {/* Color swatch + palette popover (inline expansion on click) */}
              <details style={{ position: 'relative', flexShrink: 0 }}>
                <summary
                  style={{
                    width: 26, height: 26, borderRadius: 8,
                    background: p.color, cursor: 'pointer',
                    listStyle: 'none', boxShadow: '0 0 0 2px #fff, 0 0 0 3px #e2e8f0',
                    display: 'inline-block',
                  }}
                  title="Change color"
                />
                <div style={{
                  position: 'absolute', top: 32, left: 0, zIndex: 5,
                  background: '#fff', borderRadius: 10, padding: 8,
                  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.18)',
                  display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 6,
                  border: '1px solid #e2e8f0',
                }}>
                  {PARTICIPANT_PALETTE.map(c => (
                    <button
                      key={c}
                      type="button"
                      onClick={(e) => {
                        update(idx, { color: c });
                        // Close the <details> by toggling its open attribute
                        const det = e.currentTarget.closest('details');
                        if (det) det.open = false;
                      }}
                      style={{
                        width: 22, height: 22, borderRadius: 6,
                        background: c, border: p.color === c ? '2px solid #0f172a' : '2px solid transparent',
                        cursor: 'pointer', padding: 0,
                      }}
                      title={c}
                    />
                  ))}
                </div>
              </details>

              <input
                type="text"
                value={p.displayName}
                onChange={(e) => update(idx, { displayName: e.target.value })}
                placeholder="Speaker name"
                style={{
                  flex: 1, minWidth: 0,
                  padding: '8px 10px', borderRadius: 8,
                  border: '1px solid #e2e8f0', fontSize: 14,
                  outline: 'none', background: '#fff', color: '#0f172a',
                }}
                onFocus={(e) => { e.currentTarget.style.borderColor = '#6366f1'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; }}
              />

              <button
                type="button"
                onClick={() => setHost(idx)}
                title={p.isHost ? 'Host' : 'Mark as host'}
                style={{
                  padding: '6px 10px', borderRadius: 999,
                  border: '1px solid',
                  borderColor: p.isHost ? '#6366f1' : '#e2e8f0',
                  background: p.isHost ? 'rgba(99, 102, 241, 0.12)' : '#fff',
                  color: p.isHost ? '#4338ca' : '#94a3b8',
                  fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
                  textTransform: 'uppercase', cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                Host
              </button>
            </div>
          ))}
        </div>

        <div style={{
          display: 'flex', gap: 8, justifyContent: 'flex-end',
          padding: '12px 22px', borderTop: '1px solid #f1f5f9',
          background: '#fafbfc',
        }}>
          <button onClick={onClose} disabled={saving} style={EMBED_BTN_SECONDARY}>
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving || draft.length === 0}
            style={{
              ...EMBED_BTN_PRIMARY,
              opacity: saving || draft.length === 0 ? 0.55 : 1,
              cursor: saving || draft.length === 0 ? 'not-allowed' : 'pointer',
              background: saving || draft.length === 0
                ? '#94a3b8'
                : 'linear-gradient(135deg, #f59e0b, #d97706)',
            }}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Sprint K Phase 2 — Meeting upload modal. User drops in a meeting
// recording (.mp4/.mov/.mkv) + the meeting's transcript (.vtt/.srt) and
// the backend turns it into a guide via the vision-AI pipeline. Module
// scope so the page-level re-render after upload submit doesn't
// dismount the form mid-XHR. Drag-drop UX mirrors MeetingEnrichModal so
// the two flows feel consistent.
const IMPORT_VIDEO_ACCEPT = '.mp4,.mov,.mkv,.webm,video/mp4,video/quicktime,video/x-matroska,video/webm';
const IMPORT_TRANSCRIPT_ACCEPT = '.vtt,.srt,text/vtt,application/x-subrip,text/plain';
const IMPORT_MAX_VIDEO_BYTES = 1024 * 1024 * 1024;     // 1 GB — matches multer limit on the server

function MeetingImportModal({ onClose, onSubmitted, showToast, initialVideo = null, prefill = null, startBackgroundUpload, projects = [] }) {
  // Pre-select the project the guide will land under. Preference order:
  //   1. A project literally named "General" (case-insensitive)
  //   2. The user's `isDefault` project (auto-provisioned personal projects
  //      carry this flag; org accounts may have one too)
  //   3. First project in the list
  //   4. null — backend fills in the default in that case.
  const defaultProjectId = React.useMemo(() => {
    if (!Array.isArray(projects) || projects.length === 0) return null;
    const general = projects.find((p) => (p.name || '').trim().toLowerCase() === 'general');
    if (general) return general.id;
    const def = projects.find((p) => p.isDefault);
    if (def) return def.id;
    return projects[0].id;
  }, [projects]);
  const [projectId, setProjectId] = React.useState(defaultProjectId);
  React.useEffect(() => { setProjectId(defaultProjectId); }, [defaultProjectId]);
  // `initialVideo` lets the ImportsPage page-level dropzone pre-load a
  // dragged file into this modal so the user doesn't have to re-select
  // it here. The rest of the form (topic, app hint, consent) still
  // needs their input — we open the modal instead of skipping straight
  // to submit because the consent checkbox is a legal gate.
  const [video, setVideo] = React.useState(initialVideo);
  const [transcript, setTranscript] = React.useState(null);
  // `prefill` lets a failed-import retry re-open the modal with the
  // topic / app-hint / review-mode / project the user already
  // entered — nobody wants to retype text after a backend blip.
  const [meetingTopic, setMeetingTopic] = React.useState(prefill?.meetingTopic || '');
  const [appHint, setAppHint] = React.useState(prefill?.appOrDomainHint || '');
  const [consent, setConsent] = React.useState(false);
  // Sprint K Phase 2 V4 — opt-in segmentation review. When ON, the
  // worker stops at `awaiting_review` after the analysis and hands
  // the user a segment-picker before building any guide. When OFF,
  // the pipeline runs end-to-end as before (single guide from the
  // full recording). Default OFF to preserve the low-friction "upload
  // and get a guide" experience for casual users.
  const [reviewMode, setReviewMode] = React.useState(!!prefill?.reviewMode);
  // Talk-only mode — flag the recording as narration with no meaningful
  // screen content. When ON, the backend skips frame extraction and
  // produces a transcript-first guide (steps with no screenshots).
  // Prevents the "presenter's screen didn't match his narration" symptom
  // where every step ended up with the same irrelevant background image.
  const [talkOnly, setTalkOnly] = React.useState(!!prefill?.talkOnly);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');
  const videoInputRef = React.useRef(null);
  const transcriptInputRef = React.useRef(null);

  const onPickVideo = (f) => {
    if (!f) return;
    if (f.size > IMPORT_MAX_VIDEO_BYTES) {
      setError('Video is larger than 1 GB. Trim or compress before uploading.');
      return;
    }
    setError('');
    setVideo(f);
  };
  const onPickTranscript = (f) => {
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) {
      setError('Transcript file is too large (max 2 MB).');
      return;
    }
    setError('');
    setTranscript(f);
  };

  const submit = () => {
    if (!video || !consent || submitting) return;
    if (typeof startBackgroundUpload !== 'function') {
      setError('Upload manager unavailable. Please refresh the page and try again.');
      return;
    }
    startBackgroundUpload({
      video,
      transcript,
      meetingTopic: meetingTopic.trim() || null,
      appHint: appHint.trim() || null,
      reviewMode,
      talkOnly,
      projectId: projectId || null,
      onSubmitted,
    });
    showToast?.(
      'Upload started — track progress on the Imports page.',
      'info'
    );
    onClose?.();
  };

  const canSubmit = !!video && consent && !submitting;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1100, padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 14, width: '100%', maxWidth: 560,
          maxHeight: '90vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 24px 60px rgba(15, 23, 42, 0.25)',
          overflow: 'hidden',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', gap: 14,
          padding: '18px 22px', borderBottom: '1px solid #f1f5f9',
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: 9,
            background: '#f1f5f9', border: '1px solid #e2e8f0',
            color: '#475569', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 16l-4-4-4 4"/>
              <path d="M12 12v9"/>
              <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
            </svg>
          </div>
          <div>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: '#0f172a', margin: 0 }}>
              Import meeting recording
            </h2>
            <p style={{ fontSize: 12, color: '#64748b', margin: '2px 0 0' }}>
              Upload a video + transcript. Insta extracts frames, infers steps, and assembles a guide.
            </p>
          </div>
        </div>

        <div style={{ padding: '14px 22px', overflowY: 'auto', flex: 1 }}>
          {/* Video drop zone */}
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Video file
          </label>
          <div
            onClick={() => videoInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); onPickVideo(e.dataTransfer?.files?.[0]); }}
            style={{
              border: '1.5px dashed #cbd5e1', background: '#f8fafc',
              borderRadius: 10, padding: 16, marginBottom: 14,
              cursor: 'pointer', textAlign: 'center',
            }}
          >
            {video ? (
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{video.name}</div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                  {(video.size / (1024 * 1024)).toFixed(1)} MB · click to choose a different file
                </div>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>Drop a .mp4 / .mov / .mkv / .webm — or click to browse</div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                  Up to 1 GB · 2 hr max
                  {transcript
                    ? <span style={{ color: '#059669', marginLeft: 4 }}>(transcript attached)</span>
                    : <span style={{ marginLeft: 4 }}>(no transcript? Insta will auto-transcribe)</span>}
                </div>
              </div>
            )}
            <input ref={videoInputRef} type="file" accept={IMPORT_VIDEO_ACCEPT} onChange={(e) => onPickVideo(e.target.files?.[0])} style={{ display: 'none' }} />
          </div>

          {/* Transcript drop zone — optional in V3 (Gemini transcribes from
              the audio track natively). Supplying a VTT/SRT still helps with
              speaker labeling when the provider's auto-transcript is good. */}
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Transcript file <span style={{ textTransform: 'none', fontWeight: 500, color: '#94a3b8' }}>(optional)</span>
          </label>
          <div
            onClick={() => transcriptInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); onPickTranscript(e.dataTransfer?.files?.[0]); }}
            style={{
              border: '1.5px dashed #cbd5e1', background: '#f8fafc',
              borderRadius: 10, padding: 14, marginBottom: 14,
              cursor: 'pointer', textAlign: 'center',
            }}
          >
            {transcript ? (
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{transcript.name}</div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                  {(transcript.size / 1024).toFixed(1)} KB · click to choose a different file
                </div>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>Drop the .vtt or .srt — or skip</div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>Insta transcribes from the video's audio if you don't have one. Supply a VTT/SRT for sharper speaker labels.</div>
              </div>
            )}
            <input ref={transcriptInputRef} type="file" accept={IMPORT_TRANSCRIPT_ACCEPT} onChange={(e) => onPickTranscript(e.target.files?.[0])} style={{ display: 'none' }} />
          </div>

          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Meeting topic <span style={{ textTransform: 'none', fontWeight: 500, color: '#94a3b8' }}>(optional)</span>
          </label>
          <input
            type="text"
            value={meetingTopic}
            onChange={(e) => setMeetingTopic(e.target.value)}
            placeholder="e.g. ACME refund workflow demo"
            style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', marginBottom: 12, boxSizing: 'border-box' }}
          />

          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            App being demoed <span style={{ textTransform: 'none', fontWeight: 500, color: '#94a3b8' }}>(optional hint)</span>
          </label>
          <input
            type="text"
            value={appHint}
            onChange={(e) => setAppHint(e.target.value)}
            placeholder="e.g. Salesforce, HubSpot, an internal admin tool"
            style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', marginBottom: 14, boxSizing: 'border-box' }}
          />

          {/* Project — where the resulting guide will live in the
              user's library. Defaults to a project named "General" if
              one exists (created for every account); users can move
              it later from My Guides. */}
          {Array.isArray(projects) && projects.length > 0 && (
            <>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Project
              </label>
              <select
                value={projectId || ''}
                onChange={(e) => setProjectId(e.target.value || null)}
                style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', marginBottom: 14, boxSizing: 'border-box', background: '#fff', cursor: 'pointer' }}
              >
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </>
          )}

          {/* Sprint K Phase 2 V4 — opt-in segment review. Off by default;
              enable when the user wants control over which parts of the
              recording become the guide (or wants to split one recording
              into multiple topic-specific guides). */}
          <div style={{
            marginBottom: 12,
            padding: '10px 12px',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            background: reviewMode ? '#eef2ff' : '#f8fafc',
            transition: 'background 0.15s',
          }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: '#334155', cursor: 'pointer', userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={reviewMode}
                onChange={(e) => setReviewMode(e.target.checked)}
                style={{ marginTop: 2 }}
              />
              <span>
                <span style={{ fontWeight: 600, color: '#0f172a' }}>Review segments before creating guide</span>
                <span style={{ display: 'block', marginTop: 3, color: '#64748b' }}>
                  Insta will still analyze the recording, then hand you a segment picker so you can drop chit-chat, focus on specific topics, or split one recording into several guides.
                </span>
              </span>
            </label>
          </div>

          {/* Talk-only mode. When ON, backend skips frame extraction and
              produces a transcript-first guide (steps with no
              screenshots). Prevents the "presenter's screen didn't
              match narration" symptom where every step ended up with an
              irrelevant screenshot. */}
          <div style={{
            marginBottom: 12,
            padding: '10px 12px',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            background: talkOnly ? '#f0f9ff' : '#f8fafc',
            transition: 'background 0.15s',
          }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: '#334155', cursor: 'pointer', userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={talkOnly}
                onChange={(e) => setTalkOnly(e.target.checked)}
                style={{ marginTop: 2 }}
              />
              <span>
                <span style={{ fontWeight: 600, color: '#0f172a' }}>Talk-only meeting — skip screenshots</span>
                <span style={{ display: 'block', marginTop: 3, color: '#64748b' }}>
                  Check this if the recording is narration or discussion without a meaningful screen (e.g. audio-first calls, whiteboard sessions, all-hands). Insta will build a transcript-based guide with no screenshots.
                </span>
              </span>
            </label>
          </div>

          {/* Screen-match reminder — surfaced only when talkOnly is OFF,
              since it's redundant otherwise. Nudges users to record with
              the app visible; the "18 identical screenshots" bug we
              investigated for the SaligPay upload was because the
              presenter's screen showed a different app than his audio. */}
          {!talkOnly && (
            <div style={{
              marginBottom: 12,
              padding: '10px 12px',
              background: '#fffbeb',
              border: '1px solid #fde68a',
              borderRadius: 8,
              fontSize: 12,
              color: '#78350f',
              lineHeight: 1.5,
            }}>
              <span style={{ fontWeight: 600 }}>Tip:</span>{' '}
              Insta pulls screenshots from your recording at each step's timestamp. Make sure the app or tool you're describing is visible on screen while you narrate — otherwise every step will show whatever app was on screen at that moment.
            </div>
          )}

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: '#475569', cursor: 'pointer', userSelect: 'none' }}>
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              style={{ marginTop: 2 }}
            />
            <span>I confirm I have permission from all attendees to upload and process this recording. The source video is deleted within 24 hours of processing.</span>
          </label>

          {error && (
            <div style={{ marginTop: 12, padding: '10px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: 12, color: '#b91c1c' }}>
              {error}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '12px 22px', borderTop: '1px solid #f1f5f9', background: '#fafbfc' }}>
          <button onClick={onClose} disabled={submitting} style={EMBED_BTN_SECONDARY}>Cancel</button>
          <button
            onClick={submit}
            disabled={!canSubmit}
            style={{
              ...EMBED_BTN_PRIMARY,
              opacity: canSubmit ? 1 : 0.55,
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              background: canSubmit
                ? 'linear-gradient(135deg, #6366f1, #4f46e5)'
                : '#94a3b8',
            }}
          >
            Start processing
          </button>
        </div>
      </div>
    </div>
  );
}

// Segmentation review page. Only rendered when the user opted into
// review mode on upload; Insta finished analyzing but hasn't built a
// guide yet — the user picks WHICH topics become the guide + whether
// they're bundled into one guide or one guide per topic.
//
// The pipeline runs a PER-TOPIC deep-dive AI call for every topic the
// user picks — that's what unlocks the "detailed enough" guide quality
// (the initial analysis just identifies topic boundaries; the depth
// happens at finalize time based on the user's picks).
function SegmentReviewPage({ importId, onCancel, onSubmitted, showToast, showConfirm }) {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [meta, setMeta] = React.useState(null);       // { sourceFilename, meetingTopic, sourceDurationSeconds }
  const [segments, setSegments] = React.useState(null); // { topics, totalTopics, durationSeconds }
  const [selectedTopicIds, setSelectedTopicIds] = React.useState(() => new Set());
  const [mode, setMode] = React.useState('per-segment');  // default: separate guide per topic
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(`${API_URL}/api/meeting-imports/${importId}/review`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          throw new Error(d.error || `Server returned ${res.status}`);
        }
        const data = await res.json();
        setMeta({
          sourceFilename: data.sourceFilename,
          meetingTopic: data.meetingTopic,
          sourceDurationSeconds: data.sourceDurationSeconds,
        });
        setSegments(data.segments);
        // Default: all topics selected. Users unselect what they don't
        // want — matches the "trim chit-chat" mental model.
        const all = new Set((data.segments?.topics || []).map((t) => t.id));
        setSelectedTopicIds(all);
      } catch (e) {
        setError(e.message || 'Could not load segments');
      } finally {
        setLoading(false);
      }
    })();
  }, [importId]);

  const topics = segments?.topics || [];
  const selectedCount = selectedTopicIds.size;
  const totalCount = topics.length;

  const toggleTopic = (id) => {
    setSelectedTopicIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = (on) => {
    setSelectedTopicIds(on ? new Set(topics.map((t) => t.id)) : new Set());
  };

  // Groups for the submit payload. `single` mode bundles every selected
  // topic into one group; `per-segment` mode emits one group per
  // selected topic (→ N guides).
  const buildGroups = () => {
    const orderedSelected = topics.filter((t) => selectedTopicIds.has(t.id));
    if (orderedSelected.length === 0) return [];
    if (mode === 'single') {
      return [{ title: null, topicIds: orderedSelected.map((t) => t.id) }];
    }
    return orderedSelected.map((t) => ({ title: t.title, topicIds: [t.id] }));
  };

  const submit = async () => {
    const groups = buildGroups();
    if (groups.length === 0) {
      showToast?.('Pick at least one topic to include.', 'warning');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/api/meeting-imports/${importId}/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ mode, groups }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || `Server returned ${res.status}`);
      }
      showToast?.(
        mode === 'per-segment'
          ? `Building ${groups.length} detailed guide${groups.length === 1 ? '' : 's'} — we'll notify you as each one is ready.`
          : 'Building your guide — this will take a couple of minutes.',
        'success'
      );
      onSubmitted?.();
    } catch (e) {
      setError(e.message || 'Could not start the guide build');
      showToast?.(e.message || 'Something went wrong', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="main-header">
        <h1 className="main-header-title">
          Review topics
          {meta?.sourceFilename && <span style={{ marginLeft: 12, color: '#94a3b8', fontWeight: 500, fontSize: 15 }}>· {meta.sourceFilename}</span>}
        </h1>
      </div>
      <div className="page-content">
        <style>{`
          .segrev-intro { color: #475569; font-size: 14px; line-height: 1.55; margin: 0 0 20px 0; max-width: 760px; }
          .segrev-toolbar {
            display: flex; align-items: center; justify-content: space-between; gap: 16px;
            margin-bottom: 16px; padding: 12px 16px;
            background: #ffffff; border: 1px solid #e2e8f0; border-radius: 12px;
          }
          .segrev-count { font-size: 13px; color: #64748b; font-variant-numeric: tabular-nums; }
          .segrev-select-all {
            font-size: 13px; color: ${theme.primary}; background: transparent; border: none;
            cursor: pointer; font-weight: 500;
          }
          .segrev-select-all:hover { text-decoration: underline; }

          .segrev-list { display: flex; flex-direction: column; gap: 8px; margin-bottom: 24px; }
          .segrev-item {
            display: flex; align-items: flex-start; gap: 14px;
            padding: 14px 16px;
            background: #ffffff; border: 1px solid #e2e8f0; border-radius: 12px;
            transition: border-color 0.12s, box-shadow 0.12s;
            cursor: pointer;
          }
          .segrev-item:hover { border-color: #cbd5e1; }
          .segrev-item.selected { border-color: ${theme.primary}55; background: ${theme.primary}05; }
          .segrev-check {
            flex: 0 0 auto; width: 20px; height: 20px; margin-top: 2px;
            border: 1.8px solid #cbd5e1; border-radius: 5px;
            display: inline-flex; align-items: center; justify-content: center;
            transition: all 0.12s; background: #ffffff;
          }
          .segrev-check.checked { background: ${theme.primary}; border-color: ${theme.primary}; }
          .segrev-check svg { color: #ffffff; opacity: 0; transition: opacity 0.1s; }
          .segrev-check.checked svg { opacity: 1; }

          .segrev-body { flex: 1 1 auto; min-width: 0; }
          .segrev-title { font-size: 15px; font-weight: 600; color: #0f172a; margin: 0 0 4px 0; }
          .segrev-meta { display: flex; align-items: center; gap: 10px; font-size: 12px; color: #64748b; margin-bottom: 6px; flex-wrap: wrap; }
          .segrev-tag { display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px; border-radius: 999px; font-size: 11px; font-weight: 600; }
          .segrev-tag.demo { background: #dcfce7; color: #166534; }
          .segrev-tag.discussion { background: #e0e7ff; color: #4338ca; }
          .segrev-tag.mixed { background: #fef3c7; color: #92400e; }
          .segrev-summary { font-size: 13px; color: #475569; line-height: 1.5; margin: 0; }

          .segrev-footer {
            position: sticky; bottom: 0; margin: 0 -24px -24px -24px;
            background: #ffffff; border-top: 1px solid #e2e8f0;
            padding: 16px 24px; display: flex; align-items: center; justify-content: space-between; gap: 20px;
            box-shadow: 0 -8px 20px rgba(15, 23, 42, 0.04);
          }
          .segrev-mode { display: flex; align-items: center; gap: 14px; }
          .segrev-mode-option {
            display: inline-flex; align-items: center; gap: 8px; padding: 6px 12px; border-radius: 8px;
            cursor: pointer; font-size: 13px; color: #475569; border: 1px solid transparent;
          }
          .segrev-mode-option.active { border-color: ${theme.primary}55; background: ${theme.primary}08; color: ${theme.primary}; font-weight: 600; }
          .segrev-mode-option input { margin: 0; }
          .segrev-submit {
            display: inline-flex; align-items: center; gap: 8px;
            padding: 11px 22px; border-radius: 10px;
            background: ${theme.gradient}; color: #fff; border: none; font-size: 14px; font-weight: 600; cursor: pointer;
            box-shadow: 0 4px 12px ${theme.primary}35;
          }
          .segrev-submit:hover:not(:disabled) { transform: translateY(-1px); }
          .segrev-submit:disabled { opacity: 0.55; cursor: not-allowed; }
          .segrev-cancel {
            background: transparent; border: none; color: #64748b; font-size: 13px; cursor: pointer;
          }
          .segrev-cancel:hover { color: #334155; }

          .segrev-loading, .segrev-error {
            display: flex; align-items: center; justify-content: center;
            padding: 60px 24px; color: #94a3b8;
          }
          .segrev-spinner { width: 32px; height: 32px; border: 3px solid #e2e8f0; border-top-color: ${theme.primary}; border-radius: 50%; animation: segrev-spin 0.9s linear infinite; }
          @keyframes segrev-spin { to { transform: rotate(360deg); } }
        `}</style>

        {loading && (
          <div className="segrev-loading">
            <div className="segrev-spinner" />
            <span style={{ marginLeft: 12 }}>Loading topics…</span>
          </div>
        )}

        {!loading && error && (
          <div className="segrev-error" style={{ color: '#b91c1c' }}>{error}</div>
        )}

        {!loading && !error && segments && (
          <>
            <p className="segrev-intro">
              Insta identified {topics.length} topic{topics.length === 1 ? '' : 's'} in your recording. Pick the ones you want turned into guides. For each selected topic, Insta will do a focused deep-dive to produce a step-by-step guide with rich detail and screenshots.
            </p>

            <div className="segrev-toolbar">
              <span className="segrev-count">{selectedCount} of {totalCount} topics selected</span>
              <button className="segrev-select-all" onClick={() => toggleAll(selectedCount < totalCount)}>
                {selectedCount < totalCount ? 'Select all' : 'Clear all'}
              </button>
            </div>

            <div className="segrev-list">
              {topics.map((t) => {
                const on = selectedTopicIds.has(t.id);
                return (
                  <SegRevTopicItem
                    key={t.id}
                    checked={on}
                    onToggle={() => toggleTopic(t.id)}
                    topic={t}
                  />
                );
              })}
            </div>

            <div className="segrev-footer">
              <div className="segrev-mode">
                <label className={`segrev-mode-option${mode === 'per-segment' ? ' active' : ''}`}>
                  <input type="radio" checked={mode === 'per-segment'} onChange={() => setMode('per-segment')} />
                  Separate guide per topic ({selectedCount})
                </label>
                <label className={`segrev-mode-option${mode === 'single' ? ' active' : ''}`}>
                  <input type="radio" checked={mode === 'single'} onChange={() => setMode('single')} />
                  Combine into one guide
                </label>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <button
                  className="segrev-cancel"
                  disabled={submitting}
                  onClick={() => {
                    // Warn before dropping the analysis. Discarding here
                    // deletes the import row + the on-disk video (the
                    // Cancel button on the Imports page's awaiting-review
                    // row handles the actual DELETE). This page's Cancel
                    // just navigates back to the imports list — the row
                    // stays in awaiting_review so the user can come back.
                    // Confirmation still worthwhile so it doesn't
                    // feel like a data-loss escape.
                    if (showConfirm) {
                      showConfirm(
                        'Leave without building?',
                        'You can come back to this analysis later — it stays in "Ready for your review" on the Imports page until you build a guide or cancel it.',
                        () => onCancel?.(),
                        { confirmText: 'Leave', cancelText: 'Keep reviewing' }
                      );
                    } else {
                      onCancel?.();
                    }
                  }}
                >Cancel</button>
                <button className="segrev-submit" onClick={submit} disabled={submitting || selectedCount === 0}>
                  {submitting ? 'Starting…' : (mode === 'per-segment' && selectedCount > 1 ? `Build ${selectedCount} guides` : 'Build guide')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// Presentational sub-component for a topic row. Module-scope so React
// doesn't remount it on every parent re-render.
function SegRevTopicItem({ checked, onToggle, topic }) {
  const fmt = (secs) => {
    if (secs == null || !Number.isFinite(secs)) return '';
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  const durationMin = Math.round(Math.max(0, (topic.endSeconds || 0) - (topic.startSeconds || 0)) / 60);
  return (
    <div className={`segrev-item${checked ? ' selected' : ''}`} onClick={onToggle}>
      <div className={`segrev-check ${checked ? 'checked' : ''}`} role="checkbox" aria-checked={checked}>
        {checked && (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        )}
      </div>
      <div className="segrev-body">
        <p className="segrev-title">{topic.title || 'Untitled'}</p>
        <div className="segrev-meta">
          {topic.kind && <span className={`segrev-tag ${topic.kind}`}>{topic.kind}</span>}
          <span>{fmt(topic.startSeconds)} – {fmt(topic.endSeconds)}</span>
          {durationMin > 0 && <span>~{durationMin} min</span>}
          {topic.keySpeakerName && <span>{topic.keySpeakerName}</span>}
        </div>
        {topic.summary && <p className="segrev-summary">{topic.summary}</p>}
      </div>
    </div>
  );
}


// Import recording page — a queue, not a library. The finished guide
// belongs in My Guides; this page exists for one job: turn a video
// into a guide. Layout is inverted from the previous design:
//   1. Dropzone is the primary content (the whole point of the page).
//   2. "In progress" panel — only rows that need eyes right now.
//   3. "Needs attention" panel — failed rows the user must resolve.
//   4. Recently imported (collapsed) — 7-day audit-trail escape hatch.
// Completed rows fade off the visible surface — the guide is the
// receipt, and it lives in the library. Server keeps the meeting_imports
// row for internal cost telemetry.
function ImportsPage({ showToast, showConfirm, onOpenGuide, onOpenReview, onGuidesChanged, onNavigateToMyGuides, startBackgroundUpload, backgroundUploads = [], dismissBackgroundUpload, cancelBackgroundUpload, projects = [] }) {
  const [imports, setImports] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showUpload, setShowUpload] = React.useState(false);
  const [pendingUploadFile, setPendingUploadFile] = React.useState(null);
  // When retry hits SOURCE_GONE we open the upload modal pre-filled
  // with the failed row's meeting-topic / app-hint / project / review
  // mode so the user only re-attaches the video (and transcript, if
  // applicable). Cleared when the modal closes.
  const [prefillFromRow, setPrefillFromRow] = React.useState(null);
  const [showRecent, setShowRecent] = React.useState(false);
  // Track which imports were still processing at the last poll — used
  // to detect the transition to `completed` so we can fire the "guide
  // ready" toast + auto-open. Ref (not state) so we don't re-render
  // when it changes.
  const previouslyInFlightRef = React.useRef(new Set());
  // Session-scoped "hide" set — completed rows leave the visible panels
  // once the user acknowledges them (auto-hidden after the "ready" toast
  // fires, or immediately dismissed via the row's Dismiss button).
  const [hiddenIds, setHiddenIds] = React.useState(() => new Set());
  const hideId = React.useCallback((id) => {
    setHiddenIds((prev) => { const next = new Set(prev); next.add(id); return next; });
  }, []);
  // Global drag-hover state for the whole page — turns the dropzone
  // into an active-target style even when the user is dragging the
  // file outside its bounds. Small delight, meaningful signal.
  const [dragging, setDragging] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/api/meeting-imports`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setImports(Array.isArray(data) ? data : []);
    } catch (_) { /* network blip — keep showing what we have */ } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  // Detect processing → completed transitions on each fresh imports
  // payload. Anything that flipped state THIS poll gets a "ready"
  // toast + is added to hiddenIds so the "In progress" panel
  // doesn't leave a stale row behind. Uses a ref for prior-state so
  // we don't cause a render loop.
  React.useEffect(() => {
    const nowInFlight = new Set();
    imports.forEach((r) => {
      if (r.status === 'processing' || r.status === 'uploaded') nowInFlight.add(r.id);
    });
    imports.forEach((r) => {
      if (r.status === 'completed' && r.guideId && previouslyInFlightRef.current.has(r.id) && !hiddenIds.has(r.id)) {
        // Any completion (review or not) means the guides list is stale
        // at the App level. Trigger a refresh so navigating to My Guides
        // doesn't require an F5 to see the new rows.
        if (onGuidesChanged) onGuidesChanged();

        const guideLabel = r.meetingTopic || r.sourceFilename || 'Your meeting';

        // Route on guide COUNT, not on review mode. A review-mode
        // import that produced a single guide (single mode, or per-
        // segment mode with one topic picked) should open that guide
        // directly — no reason to route through My Guides when we
        // know exactly where to land. Only multi-guide outputs go to
        // My Guides, since meeting_imports.guide_id only points at
        // one of the N and we can't auto-open all of them.
        const guideCount = r.resultingGuideCount || (r.guideId ? 1 : 0);
        if (guideCount > 1) {
          showToast?.(`${guideCount} guides ready from "${guideLabel}" — opening My Guides.`, 'success');
          if (onNavigateToMyGuides) {
            setTimeout(() => onNavigateToMyGuides(), 400);
          }
        } else if (guideCount === 1) {
          showToast?.(`"${guideLabel}" is ready — opening now`, 'success');
          if (onOpenGuide && r.guideId) {
            setTimeout(() => onOpenGuide(r.guideId), 250);
          }
        } else {
          // Import completed but produced no guide (shouldn't happen —
          // the row would be `failed`, not `completed` — but handle
          // it gracefully rather than showing an empty toast).
          showToast?.(`"${guideLabel}" finished processing, but no guide was produced.`, 'warning');
        }
        hideId(r.id);
      }
    });
    previouslyInFlightRef.current = nowInFlight;
  }, [imports, hiddenIds, hideId, onOpenGuide, showToast, onGuidesChanged, onNavigateToMyGuides]);

  // Polling — only runs while there's an in-flight import. As soon as
  // every row settles into completed/failed/cancelled, the interval is
  // a no-op (still fires but no UI change). Cheap, kept simple.
  React.useEffect(() => {
    const anyInFlight = imports.some((r) => r.status === 'processing' || r.status === 'uploaded');
    if (!anyInFlight) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [imports, load]);

  const onDelete = async (id) => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/api/meeting-imports/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Delete failed');
      setImports((prev) => prev.filter((r) => r.id !== id));
    } catch (e) {
      showToast?.(e.message || 'Could not remove import', 'error');
    }
  };

  // Retry a failed import. Server keeps the source video + transcript
  // + all metadata on the meeting_imports row, so the retry runs
  // without asking the user to re-upload anything. Fallback: if the
  // server responds 410 SOURCE_GONE (blob TTL expired), open the
  // upload modal with the previously-entered topic / hint / review
  // mode pre-filled so at least the text fields don't need retyping.
  const onRetry = async (row) => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/api/meeting-imports/${row.id}/retry`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 410) {
        // Source gone — fall through to the upload modal, pre-filled
        // with the row's metadata so the user only has to re-attach
        // the video (and transcript, if they had one).
        showToast?.('Recording expired on our servers — please re-attach the video.', 'warning');
        setPrefillFromRow(row);
        setPendingUploadFile(null);
        setShowUpload(true);
        return;
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Retry failed');
      }
      const fresh = await res.json();
      // Splice the reset row back into the list so the UI moves it
      // out of the "Needs attention" bucket immediately.
      setImports((prev) => prev.map((r) => (r.id === fresh.id ? fresh : r)));
      showToast?.('Retrying your import — no re-upload needed.', 'success');
    } catch (e) {
      showToast?.(e.message || 'Could not retry import', 'error');
    }
  };

  const openUpload = (file) => {
    setPendingUploadFile(file || null);
    setShowUpload(true);
  };

  const handlePageDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    if (file.size > IMPORT_MAX_VIDEO_BYTES) {
      showToast?.('Video is larger than 1 GB. Trim or compress before uploading.', 'error');
      return;
    }
    openUpload(file);
  };

  // Partition rows into the visible buckets. Anything the user has
  // already dismissed is hidden regardless of status.
  const visibleImports = imports.filter((r) => !hiddenIds.has(r.id));
  const inFlight = visibleImports.filter((r) => r.status === 'processing' || r.status === 'uploaded');
  const awaitingReview = visibleImports.filter((r) => r.status === 'awaiting_review');
  const failed = visibleImports.filter((r) => r.status === 'failed');
  // "Recently imported" — completed within the last 7 days, sorted
  // newest first, capped to 20 rows so a heavy user doesn't see an
  // ever-growing scroll. Anything older than 7 days is only in
  // My Guides.
  const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
  const nowMs = Date.now();
  const recent = imports
    .filter((r) => r.status === 'completed' && r.guideId && r.createdAt && (nowMs - new Date(r.createdAt).getTime()) < SEVEN_DAYS_MS)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 20);

  return (
    <>
      <div className="main-header">
        <h1 className="main-header-title">Import recording</h1>
      </div>
      <div
        className="page-content"
        onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={(e) => {
          // Only clear when leaving the page-content root — child-node
          // dragleaves fire constantly and would flicker the state.
          if (e.currentTarget === e.target) setDragging(false);
        }}
        onDrop={handlePageDrop}
      >
        <style>{`
          .imp-hero {
            position: relative;
            border: 2px dashed #cbd5e1; background: #ffffff;
            border-radius: 18px; padding: 44px 28px;
            text-align: center;
            transition: border-color 0.15s, background 0.15s, box-shadow 0.15s, transform 0.15s;
            cursor: pointer;
            overflow: hidden;
          }
          .imp-hero::before {
            content: ''; position: absolute; inset: 0;
            background: radial-gradient(circle at 50% 0%, ${theme.primary}08 0%, transparent 60%);
            pointer-events: none;
          }
          .imp-hero:hover, .imp-hero.dragging {
            border-color: ${theme.primary};
            background: ${theme.primary}08;
            box-shadow: 0 12px 40px ${theme.primary}18;
          }
          .imp-hero.dragging { transform: scale(1.005); }
          .imp-hero-icon {
            width: 68px; height: 68px; border-radius: 16px;
            background: ${theme.gradient}; color: #fff;
            display: inline-flex; align-items: center; justify-content: center;
            margin-bottom: 18px;
            box-shadow: 0 10px 28px ${theme.primary}40;
          }
          .imp-hero h2 { font-size: 20px; font-weight: 700; color: #0f172a; margin: 0 0 6px 0; letter-spacing: -0.3px; }
          .imp-hero .imp-hero-sub { color: #64748b; font-size: 14px; margin: 0 0 6px 0; }
          .imp-hero .imp-hero-meta { color: #94a3b8; font-size: 12px; margin: 0; }
          .imp-hero-blurb {
            margin-top: 20px; font-size: 13px; color: #64748b; line-height: 1.55;
            max-width: 560px; margin-left: auto; margin-right: auto;
          }

          .imp-panel { margin-top: 28px; }
          .imp-panel-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; padding: 0 4px; }
          .imp-panel-title { display: inline-flex; align-items: center; gap: 8px; font-size: 12px; font-weight: 700; letter-spacing: 0.4px; text-transform: uppercase; color: #64748b; }
          .imp-panel-count { display: inline-flex; align-items: center; justify-content: center; min-width: 20px; height: 20px; padding: 0 6px; border-radius: 999px; font-size: 11px; font-weight: 700; background: #e2e8f0; color: #334155; }
          .imp-panel-count.attn { background: #fee2e2; color: #b91c1c; }

          .imp-row {
            display: flex; align-items: center; gap: 14px;
            padding: 14px 18px;
            background: #ffffff; border: 1px solid #e2e8f0;
            border-radius: 12px; margin-bottom: 10px;
            transition: box-shadow 0.15s, border-color 0.15s;
          }
          .imp-row:hover { box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06); }
          .imp-row.attn { border-color: #fecaca; background: #fffafa; }

          .imp-row-icon {
            flex: 0 0 auto; width: 40px; height: 40px; border-radius: 10px;
            display: inline-flex; align-items: center; justify-content: center;
          }
          .imp-row-icon.processing { background: ${theme.primary}12; color: ${theme.primary}; }
          .imp-row-icon.attn { background: #fee2e2; color: #b91c1c; }
          .imp-row-icon.completed { background: #dcfce7; color: #166534; }

          .imp-row-body { flex: 1 1 auto; min-width: 0; }
          .imp-row-title { font-size: 14px; font-weight: 600; color: #0f172a; margin: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
          .imp-row-sub { font-size: 12px; color: #64748b; margin: 3px 0 0 0; }
          .imp-row-error { font-size: 12px; color: #b91c1c; margin: 4px 0 0 0; }

          .imp-row-progress { margin-top: 8px; display: flex; align-items: center; gap: 10px; }
          .imp-row-progress-bar { flex: 1 1 auto; height: 4px; background: #f1f5f9; border-radius: 999px; overflow: hidden; }
          .imp-row-progress-bar > div { height: 100%; background: ${theme.gradient}; transition: width 0.4s; }
          .imp-row-progress-pct { font-size: 11px; color: #64748b; font-variant-numeric: tabular-nums; min-width: 32px; text-align: right; }

          .imp-row-actions { flex: 0 0 auto; display: flex; gap: 8px; }
          .imp-row-btn {
            background: transparent; border: 1px solid #e2e8f0; color: #475569;
            padding: 7px 14px; border-radius: 8px;
            font-size: 12px; font-weight: 600; cursor: pointer;
            transition: all 0.12s;
          }
          .imp-row-btn:hover { background: #f8fafc; border-color: #cbd5e1; }
          .imp-row-btn.primary { background: ${theme.primary}; border-color: ${theme.primary}; color: #fff; }
          .imp-row-btn.primary:hover { background: ${theme.secondary}; border-color: ${theme.secondary}; }
          .imp-row-btn.danger:hover { background: #fef2f2; color: #b91c1c; border-color: #fecaca; }

          .imp-recent-toggle {
            background: transparent; border: none; padding: 12px 4px; margin-top: 16px;
            display: flex; align-items: center; gap: 10px; cursor: pointer;
            font-size: 13px; font-weight: 600; color: #64748b;
          }
          .imp-recent-toggle:hover { color: #334155; }
          .imp-recent-toggle svg { transition: transform 0.15s; }
          .imp-recent-toggle.open svg { transform: rotate(90deg); }

          .imp-recent-list { padding: 4px 0; }
          .imp-recent-row {
            display: flex; align-items: center; gap: 12px;
            padding: 10px 12px; border-radius: 8px;
            font-size: 13px; color: #334155;
            cursor: pointer; transition: background 0.12s;
          }
          .imp-recent-row:hover { background: #f8fafc; }
          .imp-recent-name { flex: 1 1 auto; font-weight: 500; color: #1e293b; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
          .imp-recent-when { flex: 0 0 auto; color: #94a3b8; font-size: 12px; }

          .imp-loading { display: flex; align-items: center; gap: 10px; color: #94a3b8; padding: 12px 4px; font-size: 13px; }
          .imp-loading-spinner { width: 16px; height: 16px; border: 2px solid #e2e8f0; border-top-color: ${theme.primary}; border-radius: 50%; animation: imp-spin 0.8s linear infinite; }
          @keyframes imp-spin { to { transform: rotate(360deg); } }
          @keyframes imp-pulse { 0%, 100% { opacity: 0.85; } 50% { opacity: 1; } }
        `}</style>

        {/* HERO DROPZONE */}
        <div
          className={`imp-hero${dragging ? ' dragging' : ''}`}
          onClick={() => openUpload(null)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openUpload(null); } }}
        >
          <div className="imp-hero-icon" aria-hidden="true">
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 16l-4-4-4 4"/>
              <path d="M12 12v9"/>
              <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
            </svg>
          </div>
          <h2>Drop a meeting recording to import</h2>
          <p className="imp-hero-sub">.mp4 · .mov · .mkv · .webm — up to 1 GB. Or click to browse.</p>
          <p className="imp-hero-meta">Insta transcribes, identifies scenes, and assembles a guide. Transcript file is optional.</p>
        </div>

        {loading && imports.length === 0 && (
          <div className="imp-loading">
            <div className="imp-loading-spinner" />
            <span>Checking your imports…</span>
          </div>
        )}

        {/* AWAITING-REVIEW PANEL — imports the user opted to review before
            we build the guide. Renders at the top so it's the first thing
            they see (it's the row that needs their action). */}
        {awaitingReview.length > 0 && (
          <div className="imp-panel">
            <div className="imp-panel-header">
              <span className="imp-panel-title" style={{ color: '#4338ca' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 12l2 2 4-4"/>
                  <path d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3z"/>
                  <path d="M3 12c1 0 3-1 3-3s-2-3-3-3-3 1-3 3 2 3 3 3z"/>
                  <path d="M3 12c1 0 3 1 3 3s-2 3-3 3-3-1-3-3 2-3 3-3z"/>
                  <path d="M21 12c-1 0-3 1-3 3s2 3 3 3 3-1 3-3-2-3-3-3z"/>
                </svg>
                Ready for your review
              </span>
              <span className="imp-panel-count">{awaitingReview.length}</span>
            </div>
            {awaitingReview.map((r) => (
              <div
                key={r.id}
                className="imp-row"
                style={{ borderColor: '#c7d2fe', background: '#eef2ff', cursor: 'pointer' }}
                onClick={() => onOpenReview?.(r.id)}
              >
                <div className="imp-row-icon" style={{ background: '#c7d2fe', color: '#4338ca' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/>
                    <line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
                  </svg>
                </div>
                <div className="imp-row-body">
                  <div className="imp-row-title">{r.sourceFilename || r.id}</div>
                  <div className="imp-row-sub">
                    {r.meetingTopic ? `${r.meetingTopic} · ` : ''}Insta finished analyzing — pick which parts to include in your guide.
                  </div>
                </div>
                <div className="imp-row-actions">
                  <button className="imp-row-btn primary" onClick={(e) => { e.stopPropagation(); onOpenReview?.(r.id); }}>
                    Review segments
                  </button>
                  <button
                    className="imp-row-btn danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Awaiting-review rows still hold the uploaded
                      // video on disk plus the paid-for analysis
                      // output — worth a real branded confirmation
                      // before we drop it.
                      const go = () => onDelete(r.id);
                      if (showConfirm) {
                        showConfirm(
                          'Cancel this import?',
                          'The uploaded video and its analysis will be discarded. You will need to re-upload to try again.',
                          go,
                          { type: 'danger', confirmText: 'Discard import', cancelText: 'Keep it' }
                        );
                      } else {
                        go();
                      }
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* IN-PROGRESS PANEL */}
        {/* Client-side uploads still streaming to Blob storage — rendered
            in the SAME panel as server-side in-flight imports so the user
            sees one unified progress list (upload → transcript → analysis
            → segments) instead of a floating widget for the upload half
            and an inline list for the server half. Completed / errored
            uploads keep showing here until the user dismisses them. */}
        {(() => {
          const activeUploads = (backgroundUploads || []).filter((u) => u.phase && u.phase !== 'complete');
          const showPanel = inFlight.length > 0 || activeUploads.length > 0;
          if (!showPanel) return null;
          return (
          <div className="imp-panel">
            <div className="imp-panel-header">
              <span className="imp-panel-title">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'imp-spin 1.4s linear infinite' }}>
                  <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                </svg>
                In progress
              </span>
              <span className="imp-panel-count">{inFlight.length + activeUploads.length}</span>
            </div>
            {activeUploads.map((u) => {
              const isError = u.phase === 'error';
              const isIndeterminate = u.progress === null;
              const sub =
                isError ? (u.error || 'Upload failed') :
                u.phase === 'finalizing' ? 'Finalizing upload' :
                u.phase === 'uploading' ? 'Uploading video' :
                'Starting upload';
              return (
                <div key={u.localId} className={`imp-row ${isError ? 'attn' : ''}`}>
                  <div className={`imp-row-icon ${isError ? 'attn' : 'processing'}`}>
                    {isError ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                    )}
                  </div>
                  <div className="imp-row-body">
                    <div className="imp-row-title" title={u.filename}>{u.filename}</div>
                    <div className="imp-row-sub">{sub}</div>
                    {!isError && (
                      <div className="imp-row-progress">
                        <div className="imp-row-progress-bar"><div style={{ width: isIndeterminate ? '40%' : `${u.progress ?? 0}%` }} /></div>
                        <div className="imp-row-progress-pct">{isIndeterminate ? '—' : `${u.progress ?? 0}%`}</div>
                      </div>
                    )}
                  </div>
                  <div className="imp-row-actions">
                    <button
                      className="imp-row-btn danger"
                      onClick={() => (isError ? dismissBackgroundUpload?.(u.localId) : cancelBackgroundUpload?.(u.localId))}
                    >
                      {isError ? 'Dismiss' : 'Cancel'}
                    </button>
                  </div>
                </div>
              );
            })}
            {inFlight.map((r) => (
              <div key={r.id} className="imp-row" style={{ animation: 'imp-pulse 2.4s ease-in-out infinite' }}>
                <div className="imp-row-icon processing">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="23 7 16 12 23 17 23 7"/>
                    <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                  </svg>
                </div>
                <div className="imp-row-body">
                  <div className="imp-row-title">{r.sourceFilename || r.id}</div>
                  <div className="imp-row-sub">
                    {r.meetingTopic ? `${r.meetingTopic} · ` : ''}{r.progressStage || (r.status === 'uploaded' ? 'Queued for processing' : 'Working…')}
                  </div>
                  <div className="imp-row-progress">
                    <div className="imp-row-progress-bar"><div style={{ width: `${r.progressPercent || 4}%` }} /></div>
                    <div className="imp-row-progress-pct">{r.progressPercent != null ? `${r.progressPercent}%` : '—'}</div>
                  </div>
                </div>
                <div className="imp-row-actions">
                  {/* Cancel a mid-flight import. The worker is fire-
                      and-forget on the server — deleting the row removes
                      it from the UI + on-disk video, but the in-memory
                      worker task keeps running until it hits its next
                      DB write, at which point it no-ops (row gone).
                      Costs paid up to that point are lost. */}
                  <button
                    className="imp-row-btn danger"
                    onClick={() => {
                      const go = () => onDelete(r.id);
                      if (showConfirm) {
                        showConfirm(
                          'Cancel this in-progress import?',
                          'The upload and any partial analysis will be discarded. Any AI cost incurred so far is not refunded.',
                          go,
                          { type: 'danger', confirmText: 'Cancel import', cancelText: 'Keep processing' }
                        );
                      } else {
                        go();
                      }
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ))}
          </div>
          );
        })()}

        {/* NEEDS-ATTENTION PANEL */}
        {failed.length > 0 && (
          <div className="imp-panel">
            <div className="imp-panel-header">
              <span className="imp-panel-title">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                Needs attention
              </span>
              <span className="imp-panel-count attn">{failed.length}</span>
            </div>
            {failed.map((r) => (
              <div key={r.id} className="imp-row attn">
                <div className="imp-row-icon attn">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                </div>
                <div className="imp-row-body">
                  <div className="imp-row-title">{r.sourceFilename || r.id}</div>
                  {r.meetingTopic && <div className="imp-row-sub">{r.meetingTopic}</div>}
                  {r.errorMessage && <div className="imp-row-error" title={r.errorMessage}>{r.errorMessage}</div>}
                </div>
                <div className="imp-row-actions">
                  <button className="imp-row-btn primary" onClick={() => onRetry(r)}>Try again</button>
                  <button className="imp-row-btn danger" onClick={() => onDelete(r.id)}>Remove</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* RECENTLY IMPORTED — collapsed audit trail */}
        {recent.length > 0 && (
          <>
            <button
              type="button"
              className={`imp-recent-toggle${showRecent ? ' open' : ''}`}
              onClick={() => setShowRecent((v) => !v)}
              aria-expanded={showRecent}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
              Recently imported <span style={{ color: '#94a3b8', fontWeight: 500 }}>· last 7 days · {recent.length}</span>
            </button>
            {showRecent && (
              <div className="imp-recent-list">
                {recent.map((r) => (
                  <div key={r.id} className="imp-recent-row" onClick={() => onOpenGuide?.(r.guideId)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#166534" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    <span className="imp-recent-name">{r.meetingTopic || r.sourceFilename || r.id}</span>
                    <span className="imp-recent-when">{new Date(r.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {showUpload && (
          <MeetingImportModal
            initialVideo={pendingUploadFile}
            prefill={prefillFromRow}
            onClose={() => { setShowUpload(false); setPendingUploadFile(null); setPrefillFromRow(null); }}
            onSubmitted={(row) => {
              // Inject the new row at the top instantly so the "In
              // progress" panel updates without waiting for the next poll.
              setImports((prev) => [row, ...prev]);
              // Seed the previously-in-flight set so the completion
              // transition detector picks up this row too.
              previouslyInFlightRef.current.add(row.id);
              setPendingUploadFile(null);
              setPrefillFromRow(null);
            }}
            showToast={showToast}
            projects={projects}
            startBackgroundUpload={startBackgroundUpload}
          />
        )}
      </div>
    </>
  );
}

// Sprint K — Meeting Mode badge. Renders next to the desktop/extension
// source badge on every guide card surface (Home, My Guides, Shared With Me,
// Favorites, Projects Explorer, Guide Detail). Amber chip, mirrors the
// extension floater's in-progress badge so "this is a meeting capture"
// reads consistently from recording → library → detail page. Renders
// nothing when guide.meetingMode is falsy, so dropping it next to existing
// source badges has zero impact on legacy guides.
function MeetingBadge({ guide, compact = false }) {
  if (!guide || !guide.meetingMode) return null;
  const topic = guide?.meetingMetadata?.topic;
  const tooltip = topic ? `Meeting demo: ${topic}` : 'Captured during a meeting demo';
  return (
    <span
      className={`guide-meeting-badge${compact ? ' compact' : ''}`}
      title={tooltip}
      aria-label={tooltip}
    >
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="9" cy="8" r="3.2" />
        <path d="M2.5 19c.6-3.2 3.3-5.3 6.5-5.3s5.9 2.1 6.5 5.3" />
        <circle cx="17" cy="6.5" r="2.4" />
        <path d="M15 12.4c2.5-.2 4.6 1.3 5.5 3.6" />
      </svg>
      {!compact && <span>Meeting</span>}
    </span>
  );
}

// Login Component
function LoginView({ onLogin, onRegister, invitation, defaultSignup, onForgotPassword }) {
  const [isLogin, setIsLogin] = useState(!invitation && !defaultSignup); // Auto-switch to signup if invitation or /signup URL
  const [email, setEmail] = useState(invitation?.email || '');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [accountType, setAccountType] = useState('personal');
  const [organizationName, setOrganizationName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingMessage, setPendingMessage] = useState('');
  // Set when a fresh signup returns pendingVerification. Drives the
  // "check your inbox" success screen with Resend button. `email` is
  // used by the Resend endpoint; `emailType` decides whether we
  // mention admin approval in the follow-up copy.
  const [pendingVerify, setPendingVerify] = useState(null); // { email, emailType, message } | null
  const [resendState, setResendState] = useState({ status: 'idle', error: null }); // 'idle' | 'sending' | 'sent' | 'error'

  // Update email when invitation changes
  useEffect(() => {
    if (invitation) {
      setEmail(invitation.email);
      setIsLogin(false);
    }
  }, [invitation]);

  // Clear error when switching between login/signup or changing inputs
  useEffect(() => {
    setError('');
  }, [isLogin, email, password]);

  // Keep isLogin in sync with the URL on browser back/forward — the
  // toggle button pushes /login or /signup, and popstate should flip
  // isLogin back so the form matches the URL after a nav gesture.
  // Skipped when an invitation flow is active; invitations always show
  // the signup form regardless of URL.
  useEffect(() => {
    if (invitation) return undefined;
    const onPopState = () => {
      const p = window.location.pathname;
      if (p === '/signup') setIsLogin(false);
      else if (p === '/login') setIsLogin(true);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [invitation]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setPendingMessage('');
    setPendingVerify(null);
    setResendState({ status: 'idle', error: null });
    try {
      if (isLogin) {
        await onLogin(email, password);
      } else {
        const result = await onRegister(email, password, name, accountType, organizationName, invitation?.token);
        if (result && result.pendingVerification) {
          // Every new signup lands here now — company OR personal.
          // Show the check-your-inbox screen; the difference between
          // the two paths is what the user sees AFTER the click,
          // handled by VerifyEmailView.
          setPendingVerify({
            email: result.email,
            emailType: result.emailType || 'company',
            message: result.message,
          });
          setPassword('');
        } else if (result && result.pendingApproval) {
          // Legacy server response (pre-verification-flow). Kept
          // for graceful behavior during a staggered deploy.
          setPendingMessage(result.message);
          setIsLogin(true);
          setPassword('');
        }
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!pendingVerify?.email) return;
    setResendState({ status: 'sending', error: null });
    try {
      const response = await fetch(`${API_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: pendingVerify.email }),
      });
      // Endpoint is enumeration-safe (always returns 200 with the
      // same shape). We treat non-OK as a transport error only.
      if (!response.ok) throw new Error('Network error — try again in a moment.');
      setResendState({ status: 'sent', error: null });
    } catch (err) {
      setResendState({ status: 'error', error: err.message || 'Failed to resend' });
    }
  };

  // Signup mode gets a distinct layout — centered card, no marketing
  // brand panel. Signup is a conversion moment; put the form dead-center
  // where the eye lands rather than making it compete with the product
  // demo. Invitation-accept keeps the two-column layout (the left panel's
  // team context is useful there). Toggle CSS class controls the switch;
  // JSX below stays a single tree — same form logic in both layouts.
  const isSignupLayout = !isLogin && !invitation;
  return (
    <div className={`auth-container${isSignupLayout ? ' auth-container-signup' : ''}`}>
      <style>{`
        /* Signup-mode overrides — hide brand panel, center the form
           column, cap card width so it doesn't stretch on huge screens. */
        .auth-container-signup .auth-brand-panel { display: none; }
        .auth-container-signup .auth-form-panel {
          width: 100%;
          max-width: none;
          justify-content: center;
          align-items: center;
          min-height: 100vh;
        }
        .auth-container-signup .auth-card {
          max-width: 480px;
          width: 100%;
          margin: 0 auto;
        }
        .auth-container-signup {
          background: linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #f5f3ff 100%);
        }

        @keyframes auth-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        @keyframes auth-pulse-ring {
          0% { transform: scale(0.9); opacity: 0.4; }
          50% { transform: scale(1.05); opacity: 0.15; }
          100% { transform: scale(0.9); opacity: 0.4; }
        }
        @keyframes auth-fade-up {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes auth-slide-in {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes auth-shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes auth-sparkle-pulse {
          0%, 100% { opacity: 0.7; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.15); }
        }
        .auth-ai-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 14px;
          border-radius: 20px;
          background: rgba(99, 102, 241, 0.15);
          border: 1px solid rgba(99, 102, 241, 0.25);
          margin-bottom: 24px;
          animation: auth-fade-up 0.6s ease-out 0.05s both;
        }
        .auth-ai-badge svg {
          animation: auth-sparkle-pulse 2s ease-in-out infinite;
        }
        .auth-ai-badge span {
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.2px;
          background: linear-gradient(90deg, #a5b4fc, #c4b5fd, #a5b4fc);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: auth-shimmer 3s linear infinite;
        }
        .auth-container {
          min-height: 100vh;
          display: flex;
          background: #0f172a;
        }
        .auth-brand-panel {
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding: 60px;
          position: relative;
          overflow: hidden;
          background: linear-gradient(160deg, #1e293b 0%, #0f172a 50%, ${theme.secondary}22 100%);
        }
        .auth-brand-panel::before {
          content: '';
          position: absolute;
          top: -20%;
          right: -10%;
          width: 500px;
          height: 500px;
          border-radius: 50%;
          background: radial-gradient(circle, ${theme.primary}18 0%, transparent 70%);
          animation: auth-pulse-ring 6s ease-in-out infinite;
        }
        .auth-brand-panel::after {
          content: '';
          position: absolute;
          bottom: -15%;
          left: -5%;
          width: 400px;
          height: 400px;
          border-radius: 50%;
          background: radial-gradient(circle, ${theme.secondary}12 0%, transparent 70%);
          animation: auth-pulse-ring 8s ease-in-out infinite 2s;
        }
        .auth-brand-content {
          position: relative;
          z-index: 1;
          max-width: 480px;
          flex-shrink: 0;
        }
        .auth-brand-logo {
          display: flex;
          align-items: center;
          gap: 14px;
          margin-bottom: 48px;
          animation: auth-slide-in 0.6s ease-out;
        }
        .auth-brand-logo-mark {
          width: 44px;
          height: 44px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .auth-brand-logo-mark img {
          display: block;
        }
        .auth-brand-logo-text {
          font-size: 22px;
          font-weight: 700;
          color: white;
          letter-spacing: -0.5px;
        }
        .auth-brand-heading {
          font-size: 40px;
          font-weight: 800;
          color: white;
          line-height: 1.15;
          margin: 0 0 20px 0;
          letter-spacing: -1px;
          animation: auth-fade-up 0.6s ease-out 0.1s both;
        }
        .auth-brand-heading span {
          background: ${theme.gradient};
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .auth-brand-desc {
          font-size: 17px;
          color: #94a3b8;
          line-height: 1.7;
          margin: 0 0 40px 0;
          animation: auth-fade-up 0.6s ease-out 0.2s both;
        }
        .auth-brand-features {
          display: flex;
          flex-direction: column;
          gap: 16px;
          animation: auth-fade-up 0.6s ease-out 0.3s both;
        }
        .auth-brand-feature {
          display: flex;
          align-items: center;
          gap: 14px;
          color: #cbd5e1;
          font-size: 15px;
        }
        .auth-brand-feature-icon {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.08);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          color: ${theme.primary};
        }
        .auth-form-panel {
          width: 520px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 40px;
          background: white;
          position: relative;
        }
        .auth-form-panel::before {
          content: '';
          position: absolute;
          left: 0;
          top: 10%;
          bottom: 10%;
          width: 1px;
          background: linear-gradient(to bottom, transparent, #e2e8f0, transparent);
        }
        .auth-card {
          width: 100%;
          max-width: 400px;
          animation: auth-fade-up 0.5s ease-out;
        }
        .auth-header {
          margin-bottom: 32px;
        }
        .auth-title {
          font-size: 26px;
          font-weight: 700;
          color: #0f172a;
          margin: 0 0 6px 0;
          letter-spacing: -0.5px;
        }
        .auth-subtitle {
          color: #64748b;
          font-size: 15px;
          margin: 0;
        }
        .input-group {
          margin-bottom: 20px;
        }
        .input-group label {
          display: block;
          font-size: 13px;
          font-weight: 600;
          color: #475569;
          margin-bottom: 6px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .input-group input {
          width: 100%;
          padding: 13px 16px;
          border: 1.5px solid #e2e8f0;
          border-radius: 10px;
          font-size: 15px;
          transition: all 0.2s;
          outline: none;
          background: #f8fafc;
        }
        .input-group input:focus {
          border-color: ${theme.primary};
          background: white;
          box-shadow: 0 0 0 3px ${theme.primary}15;
        }
        .primary-btn {
          width: 100%;
          padding: 14px;
          background: ${theme.gradient};
          color: white;
          border: none;
          border-radius: 10px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          letter-spacing: 0.3px;
        }
        .primary-btn:hover {
          box-shadow: 0 6px 20px ${theme.primary}40;
          filter: brightness(1.05);
        }
        .primary-btn:active {
          transform: scale(0.98);
        }
        .primary-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          transform: none;
          filter: none;
          box-shadow: none;
        }
        .switch-btn {
          display: block;
          width: 100%;
          text-align: center;
          background: none;
          border: none;
          color: #64748b;
          font-size: 14px;
          cursor: pointer;
          margin-top: 20px;
        }
        .switch-btn strong {
          color: ${theme.primary};
          font-weight: 600;
        }
        .auth-error {
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #dc2626;
          padding: 10px 14px;
          border-radius: 8px;
          font-size: 13px;
          margin-top: 8px;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .auth-error svg {
          flex-shrink: 0;
        }
        .auth-pending {
          background: #fffbeb;
          border: 1px solid #fde68a;
          color: #92400e;
          padding: 14px 16px;
          border-radius: 10px;
          font-size: 13px;
          margin-bottom: 20px;
          display: flex;
          align-items: flex-start;
          gap: 10px;
          line-height: 1.5;
        }
        .auth-pending svg {
          flex-shrink: 0;
          margin-top: 2px;
        }
        .account-type-selector {
          margin-bottom: 24px;
        }
        .account-type-selector label {
          display: block;
          font-size: 13px;
          font-weight: 600;
          color: #475569;
          margin-bottom: 8px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .account-type-options {
          display: flex;
          gap: 10px;
        }
        .account-type-option {
          flex: 1;
          padding: 14px 12px;
          border: 1.5px solid #e2e8f0;
          border-radius: 10px;
          cursor: pointer;
          text-align: center;
          transition: all 0.2s;
          background: #f8fafc;
        }
        .account-type-option:hover {
          border-color: #cbd5e1;
          background: white;
        }
        .account-type-option.selected {
          border-color: ${theme.primary};
          background: ${theme.primary}08;
          box-shadow: 0 0 0 3px ${theme.primary}10;
        }
        .account-type-option .icon {
          font-size: 24px;
          margin-bottom: 6px;
        }
        .account-type-option .title {
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 2px;
          font-size: 14px;
        }
        .account-type-option .desc {
          font-size: 12px;
          color: #64748b;
        }
        .account-type-option .plans {
          font-size: 11px;
          color: #94a3b8;
          margin-top: 6px;
        }
        .extension-box {
          margin-top: 28px;
          padding: 16px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .extension-box-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background: ${theme.gradientLight};
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          color: ${theme.primary};
        }
        .extension-box-text {
          flex: 1;
        }
        .extension-box-text p {
          margin: 0;
          color: #475569;
          font-size: 13px;
          font-weight: 500;
        }
        .extension-btn {
          padding: 8px 16px;
          background: white;
          color: ${theme.primary};
          border: 1.5px solid ${theme.primary};
          border-radius: 8px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          text-decoration: none;
          white-space: nowrap;
          transition: all 0.2s;
        }
        .extension-btn:hover {
          background: ${theme.primary};
          color: white;
        }
        .auth-divider {
          display: flex;
          align-items: center;
          gap: 16px;
          margin: 24px 0;
          color: #94a3b8;
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        .auth-divider::before, .auth-divider::after {
          content: '';
          flex: 1;
          height: 1px;
          background: #e2e8f0;
        }
        /* ========== PRODUCT DEMO ANIMATION (20s, 5 steps) ========== */
        @keyframes demo-cursor-journey {
          0% { left: 35%; top: 15%; opacity: 0; }
          2% { opacity: 1; left: 35%; top: 15%; }
          /* Click 1: sidebar "Tickets" item */
          5% { left: 5%; top: 15%; }
          6% { left: 5%; top: 15%; transform: scale(0.75); }
          7%, 17% { left: 5%; top: 15%; transform: scale(1); }
          /* Click 2: priority dropdown (right field in row) */
          21% { left: 72%; top: 30%; }
          22% { left: 72%; top: 30%; transform: scale(0.75); }
          23%, 33% { left: 72%; top: 30%; transform: scale(1); }
          /* Click 3: description textarea */
          37% { left: 50%; top: 55%; }
          38% { left: 50%; top: 55%; transform: scale(0.75); }
          39%, 49% { left: 50%; top: 55%; transform: scale(1); }
          /* Click 4: attach screenshot area */
          53% { left: 24%; top: 70%; }
          54% { left: 24%; top: 70%; transform: scale(0.75); }
          55%, 64% { left: 24%; top: 70%; transform: scale(1); }
          /* Click 5: submit button */
          68% { left: 22%; top: 85%; }
          69% { left: 22%; top: 85%; transform: scale(0.75); }
          70%, 88% { left: 22%; top: 85%; transform: scale(1); }
          93%, 100% { left: 22%; top: 85%; opacity: 0; }
        }
        @keyframes demo-float-gentle {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        @keyframes demo-dot-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.5); }
          50% { box-shadow: 0 0 0 4px rgba(239,68,68,0); }
        }
        /* 5-step loops (20s cycle). Clicks at 6%, 22%, 38%, 54%, 69% */
        @keyframes demo-s1 { 0%,8% { opacity:0; transform:translateX(14px); } 11% { opacity:1; transform:translateX(0); } 88% { opacity:1; } 94%,100% { opacity:0; } }
        @keyframes demo-s2 { 0%,24% { opacity:0; transform:translateX(14px); } 27% { opacity:1; transform:translateX(0); } 88% { opacity:1; } 94%,100% { opacity:0; } }
        @keyframes demo-s3 { 0%,40% { opacity:0; transform:translateX(14px); } 43% { opacity:1; transform:translateX(0); } 88% { opacity:1; } 94%,100% { opacity:0; } }
        @keyframes demo-s4 { 0%,56% { opacity:0; transform:translateX(14px); } 59% { opacity:1; transform:translateX(0); } 88% { opacity:1; } 94%,100% { opacity:0; } }
        @keyframes demo-s5 { 0%,71% { opacity:0; transform:translateX(14px); } 74% { opacity:1; transform:translateX(0); } 88% { opacity:1; } 94%,100% { opacity:0; } }
        @keyframes demo-t1 { 0%,10% { width:0; } 14% { width:100%; } 88% { width:100%; } 94%,100% { width:0; } }
        @keyframes demo-t2 { 0%,26% { width:0; } 30% { width:100%; } 88% { width:100%; } 94%,100% { width:0; } }
        @keyframes demo-t3 { 0%,42% { width:0; } 46% { width:100%; } 88% { width:100%; } 94%,100% { width:0; } }
        @keyframes demo-t4 { 0%,58% { width:0; } 62% { width:100%; } 88% { width:100%; } 94%,100% { width:0; } }
        @keyframes demo-t5 { 0%,73% { width:0; } 77% { width:100%; } 88% { width:100%; } 94%,100% { width:0; } }
        @keyframes demo-b1 { 0%,12% { opacity:0; } 14% { opacity:1; } 88% { opacity:1; } 94%,100% { opacity:0; } }
        @keyframes demo-b2 { 0%,28% { opacity:0; } 30% { opacity:1; } 88% { opacity:1; } 94%,100% { opacity:0; } }
        @keyframes demo-b3 { 0%,44% { opacity:0; } 46% { opacity:1; } 88% { opacity:1; } 94%,100% { opacity:0; } }
        @keyframes demo-b4 { 0%,60% { opacity:0; } 62% { opacity:1; } 88% { opacity:1; } 94%,100% { opacity:0; } }
        @keyframes demo-b5 { 0%,75% { opacity:0; } 77% { opacity:1; } 88% { opacity:1; } 94%,100% { opacity:0; } }
        @keyframes demo-r1 { 0%,5.9% { transform:translate(-50%,-50%) scale(0); opacity:0; } 6% { transform:translate(-50%,-50%) scale(.5); opacity:.9; } 9% { transform:translate(-50%,-50%) scale(3); opacity:0; } 100% { opacity:0; } }
        @keyframes demo-r2 { 0%,21.9% { transform:translate(-50%,-50%) scale(0); opacity:0; } 22% { transform:translate(-50%,-50%) scale(.5); opacity:.9; } 25% { transform:translate(-50%,-50%) scale(3); opacity:0; } 100% { opacity:0; } }
        @keyframes demo-r3 { 0%,37.9% { transform:translate(-50%,-50%) scale(0); opacity:0; } 38% { transform:translate(-50%,-50%) scale(.5); opacity:.9; } 41% { transform:translate(-50%,-50%) scale(3); opacity:0; } 100% { opacity:0; } }
        @keyframes demo-r4 { 0%,53.9% { transform:translate(-50%,-50%) scale(0); opacity:0; } 54% { transform:translate(-50%,-50%) scale(.5); opacity:.9; } 57% { transform:translate(-50%,-50%) scale(3); opacity:0; } 100% { opacity:0; } }
        @keyframes demo-r5 { 0%,68.9% { transform:translate(-50%,-50%) scale(0); opacity:0; } 69% { transform:translate(-50%,-50%) scale(.5); opacity:.9; } 72% { transform:translate(-50%,-50%) scale(3); opacity:0; } 100% { opacity:0; } }
        @keyframes demo-f1 { 0%,5.9% { opacity:0; } 6% { opacity:.35; } 9% { opacity:0; } 100% { opacity:0; } }
        @keyframes demo-f2 { 0%,21.9% { opacity:0; } 22% { opacity:.35; } 25% { opacity:0; } 100% { opacity:0; } }
        @keyframes demo-f3 { 0%,37.9% { opacity:0; } 38% { opacity:.35; } 41% { opacity:0; } 100% { opacity:0; } }
        @keyframes demo-f4 { 0%,53.9% { opacity:0; } 54% { opacity:.35; } 57% { opacity:0; } 100% { opacity:0; } }
        @keyframes demo-f5 { 0%,68.9% { opacity:0; } 69% { opacity:.35; } 72% { opacity:0; } 100% { opacity:0; } }
        @keyframes demo-dot-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.5); }
          50% { box-shadow: 0 0 0 5px rgba(239,68,68,0); }
        }
        @keyframes demo-badge-slide {
          0% { opacity: 0; transform: translateX(-8px); }
          100% { opacity: 1; transform: translateX(0); }
        }
        @keyframes demo-sequence-loop {
          0%, 5% { opacity: 1; }
          88%, 95% { opacity: 1; }
          97% { opacity: 0; }
          100% { opacity: 0; }
        }

        .auth-brand-inner {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          position: relative;
          z-index: 1;
        }
        .auth-demo {
          flex: 0 1 440px;
          min-width: 340px;
          max-width: 440px;
          margin-right: 100px;
          position: relative;
          animation: auth-fade-up 0.8s ease-out 0.4s both;
        }
        .auth-demo::before {
          content: '';
          position: absolute;
          top: 30%;
          left: 50%;
          width: 280px;
          height: 280px;
          transform: translate(-50%, -50%);
          border-radius: 50%;
          background: radial-gradient(circle, ${theme.primary}15 0%, ${theme.secondary}08 40%, transparent 70%);
          filter: blur(50px);
          pointer-events: none;
          z-index: -1;
          animation: auth-pulse-ring 10s ease-in-out infinite;
        }

        /* Mock Browser Window */
        .demo-browser {
          background: #1a1f2e;
          border-radius: 12px;
          border: 1px solid rgba(255,255,255,0.08);
          overflow: hidden;
          box-shadow: 0 25px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04);
          position: relative;
          animation: demo-float-gentle 8s ease-in-out infinite;
        }
        .demo-browser-bar {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 14px;
          background: #151924;
          border-bottom: 1px solid rgba(255,255,255,0.06);
        }
        .demo-browser-dots {
          display: flex;
          gap: 5px;
        }
        .demo-browser-dots i {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          display: block;
        }
        .demo-browser-dots i:nth-child(1) { background: #ff5f57; }
        .demo-browser-dots i:nth-child(2) { background: #ffbd2e; }
        .demo-browser-dots i:nth-child(3) { background: #28ca42; }
        .demo-browser-url {
          flex: 1;
          background: rgba(255,255,255,0.06);
          border-radius: 6px;
          padding: 4px 10px;
          font-size: 10px;
          color: #64748b;
          font-family: 'SF Mono', 'Fira Code', monospace;
          display: flex;
          align-items: center;
          gap: 5px;
        }
        .demo-browser-url svg {
          opacity: 0.4;
        }
        .demo-browser-body {
          position: relative;
          height: 220px;
          padding: 16px;
          overflow: hidden;
        }

        /* Mock UI inside browser */
        .demo-ui-sidebar {
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 44px;
          background: rgba(255,255,255,0.03);
          border-right: 1px solid rgba(255,255,255,0.05);
          display: flex;
          flex-direction: column;
          align-items: center;
          padding-top: 14px;
          gap: 12px;
        }
        .demo-ui-sidebar-item {
          width: 22px;
          height: 22px;
          border-radius: 6px;
          background: rgba(255,255,255,0.06);
        }
        .demo-ui-sidebar-item.active {
          background: ${theme.primary}30;
          border: 1px solid ${theme.primary}50;
        }
        .demo-ui-main {
          margin-left: 56px;
        }
        .demo-ui-heading {
          width: 45%;
          height: 8px;
          background: rgba(255,255,255,0.15);
          border-radius: 4px;
          margin-bottom: 14px;
        }
        .demo-ui-row {
          display: flex;
          gap: 10px;
          margin-bottom: 10px;
        }
        .demo-ui-field {
          flex: 1;
          height: 24px;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 5px;
        }
        .demo-ui-label {
          width: 30%;
          height: 6px;
          background: rgba(255,255,255,0.1);
          border-radius: 3px;
          margin-bottom: 6px;
        }
        .demo-ui-btn {
          display: inline-block;
          width: 72px;
          height: 24px;
          background: ${theme.gradient};
          border-radius: 5px;
          margin-top: 8px;
          opacity: 0.7;
        }
        .demo-ui-toggle-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 8px;
        }
        .demo-ui-toggle {
          width: 28px;
          height: 14px;
          border-radius: 7px;
          background: ${theme.primary}60;
          position: relative;
        }
        .demo-ui-toggle::after {
          content: '';
          position: absolute;
          right: 2px;
          top: 2px;
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background: white;
        }

        /* Animated cursor */
        .demo-cursor {
          position: absolute;
          width: 16px;
          height: 20px;
          z-index: 10;
          filter: drop-shadow(0 2px 4px rgba(0,0,0,0.5));
          animation: demo-cursor-journey 20s ease-in-out infinite;
          pointer-events: none;
        }

        /* Click ripple effects at each click position */
        .demo-click-ring {
          position: absolute;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          pointer-events: none;
          transform: translate(-50%, -50%);
        }
        .demo-click-ring::before,
        .demo-click-ring::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 50%;
          border: 2px solid #ef4444;
          opacity: 0;
        }
        .demo-click-ring::before {
          background: rgba(239, 68, 68, 0.35);
          border: none;
        }
        /* Click rings — exact same positions as cursor journey */
        .demo-click-ring-1 { left: 5%; top: 15%; }
        .demo-click-ring-1::before, .demo-click-ring-1::after { animation: demo-r1 20s ease-out infinite; }
        .demo-click-ring-2 { left: 72%; top: 30%; }
        .demo-click-ring-2::before, .demo-click-ring-2::after { animation: demo-r2 20s ease-out infinite; }
        .demo-click-ring-3 { left: 50%; top: 55%; }
        .demo-click-ring-3::before, .demo-click-ring-3::after { animation: demo-r3 20s ease-out infinite; }
        .demo-click-ring-4 { left: 24%; top: 70%; }
        .demo-click-ring-4::before, .demo-click-ring-4::after { animation: demo-r4 20s ease-out infinite; }
        .demo-click-ring-5 { left: 22%; top: 85%; }
        .demo-click-ring-5::before, .demo-click-ring-5::after { animation: demo-r5 20s ease-out infinite; }

        /* Capture flash overlay */
        .demo-capture-flash {
          position: absolute;
          inset: 0;
          background: white;
          border-radius: 0 0 12px 12px;
          pointer-events: none;
          z-index: 5;
        }
        .demo-flash-1 { animation: demo-f1 20s ease-out infinite; }
        .demo-flash-2 { animation: demo-f2 20s ease-out infinite; }
        .demo-flash-3 { animation: demo-f3 20s ease-out infinite; }
        .demo-flash-4 { animation: demo-f4 20s ease-out infinite; }
        .demo-flash-5 { animation: demo-f5 20s ease-out infinite; }

        /* Recording indicator */
        .demo-rec-badge {
          position: absolute;
          top: 44px;
          right: 10px;
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 3px 8px;
          background: rgba(239, 68, 68, 0.15);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: 10px;
          z-index: 6;
          animation: auth-fade-up 0.4s ease-out 0.3s both;
        }
        .demo-rec-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #ef4444;
          animation: demo-dot-pulse 1.5s ease-in-out infinite;
        }
        .demo-rec-text {
          font-size: 9px;
          font-weight: 700;
          color: #ef4444;
          letter-spacing: 0.5px;
          text-transform: uppercase;
        }

        /* Step Cards Area */
        .demo-steps {
          margin-top: 10px;
          display: flex;
          flex-direction: column;
          gap: 5px;
        }
        .demo-step-card {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          padding: 8px 10px;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 10px;
          opacity: 0;
          backdrop-filter: blur(8px);
          transition: border-color 0.3s, background 0.3s;
        }
        .demo-step-card:hover {
          border-color: rgba(255,255,255,0.15);
          background: rgba(255,255,255,0.06);
        }
        .demo-step-card-1 { animation: demo-s1 20s ease-in-out infinite; }
        .demo-step-card-2 { animation: demo-s2 20s ease-in-out infinite; }
        .demo-step-card-3 { animation: demo-s3 20s ease-in-out infinite; }
        .demo-step-card-4 { animation: demo-s4 20s ease-in-out infinite; }
        .demo-step-card-5 { animation: demo-s5 20s ease-in-out infinite; }

        /* Step number badge */
        .demo-step-num {
          width: 28px;
          height: 28px;
          background: ${theme.gradient};
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: 800;
          color: white;
          flex-shrink: 0;
          box-shadow: 0 2px 8px ${theme.primary}40;
        }
        .demo-step-num.keyboard {
          background: linear-gradient(135deg, #f59e0b, #d97706);
          box-shadow: 0 2px 8px rgba(245,158,11,0.4);
        }

        /* Step screenshot thumbnail */
        .demo-step-thumb {
          width: 52px;
          height: 36px;
          border-radius: 5px;
          flex-shrink: 0;
          position: relative;
          overflow: hidden;
          border: 1px solid rgba(255,255,255,0.08);
        }
        .demo-step-thumb-img {
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #1e293b, #334155);
        }
        .demo-step-card-2 .demo-step-thumb-img {
          background: linear-gradient(135deg, #1e293b, #1e3a5f);
        }
        .demo-step-card-3 .demo-step-thumb-img {
          background: linear-gradient(135deg, #1e293b, #1a2e1a);
        }
        .demo-step-card-4 .demo-step-thumb-img {
          background: linear-gradient(135deg, #1e293b, #2d1e3b);
        }
        .demo-step-card-5 .demo-step-thumb-img {
          background: linear-gradient(135deg, #1e293b, #3b2e1e);
        }

        /* Red click indicator on thumbnail */
        .demo-click-dot {
          position: absolute;
          width: 8px;
          height: 8px;
          background: #ef4444;
          border-radius: 50%;
          border: 1.5px solid white;
          box-shadow: 0 0 6px rgba(239,68,68,0.6);
          z-index: 2;
        }
        .demo-step-card-1 .demo-click-dot { top: 40%; left: 60%; }
        .demo-step-card-2 .demo-click-dot { top: 55%; left: 35%; }
        .demo-step-card-3 .demo-click-dot { top: 65%; left: 70%; }
        .demo-step-card-4 .demo-click-dot { top: 30%; left: 50%; }
        .demo-step-card-5 .demo-click-dot { top: 70%; left: 40%; }

        /* Step text content */
        .demo-step-info {
          flex: 1;
          min-width: 0;
        }
        .demo-step-action-row {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 3px;
        }
        .demo-step-action-text {
          font-size: 11px;
          font-weight: 700;
          color: #e2e8f0;
          white-space: nowrap;
          overflow: hidden;
        }
        .demo-step-card-1 .demo-step-action-text { animation: demo-t1 20s steps(20) infinite; }
        .demo-step-card-2 .demo-step-action-text { animation: demo-t2 20s steps(20) infinite; }
        .demo-step-card-3 .demo-step-action-text { animation: demo-t3 20s steps(20) infinite; }
        .demo-step-card-4 .demo-step-action-text { animation: demo-t4 20s steps(20) infinite; }
        .demo-step-card-5 .demo-step-action-text { animation: demo-t5 20s steps(20) infinite; }

        /* Event type badge */
        .demo-event-badge {
          font-size: 8px;
          font-weight: 700;
          padding: 1px 5px;
          border-radius: 4px;
          text-transform: uppercase;
          letter-spacing: 0.3px;
          flex-shrink: 0;
          opacity: 0;
        }
        .demo-event-badge.click {
          background: ${theme.primary}25;
          color: ${theme.primary};
          border: 1px solid ${theme.primary}35;
        }
        .demo-event-badge.keyboard {
          background: rgba(245,158,11,0.2);
          color: #f59e0b;
          border: 1px solid rgba(245,158,11,0.3);
        }
        .demo-step-card-1 .demo-event-badge { animation: demo-b1 20s ease-out infinite; }
        .demo-step-card-2 .demo-event-badge { animation: demo-b2 20s ease-out infinite; }
        .demo-step-card-3 .demo-event-badge { animation: demo-b3 20s ease-out infinite; }
        .demo-step-card-4 .demo-event-badge { animation: demo-b4 20s ease-out infinite; }
        .demo-step-card-5 .demo-event-badge { animation: demo-b5 20s ease-out infinite; }

        /* AI description text */
        .demo-step-desc {
          font-size: 10px;
          color: #94a3b8;
          white-space: nowrap;
          overflow: hidden;
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .demo-step-desc-text {
          overflow: hidden;
        }
        .demo-step-card-1 .demo-step-desc-text { animation: demo-t1 20s steps(30) infinite; }
        .demo-step-card-2 .demo-step-desc-text { animation: demo-t2 20s steps(30) infinite; }
        .demo-step-card-3 .demo-step-desc-text { animation: demo-t3 20s steps(30) infinite; }
        .demo-step-card-4 .demo-step-desc-text { animation: demo-t4 20s steps(30) infinite; }
        .demo-step-card-5 .demo-step-desc-text { animation: demo-t5 20s steps(30) infinite; }

        /* AI sparkle on each step */
        .demo-ai-sparkle {
          flex-shrink: 0;
          color: ${theme.primary};
          opacity: 0;
        }
        .demo-step-card-1 .demo-ai-sparkle { animation: demo-b1 20s ease-in-out infinite; }
        .demo-step-card-2 .demo-ai-sparkle { animation: demo-b2 20s ease-in-out infinite; }
        .demo-step-card-3 .demo-ai-sparkle { animation: demo-b3 20s ease-in-out infinite; }
        .demo-step-card-4 .demo-ai-sparkle { animation: demo-b4 20s ease-in-out infinite; }
        .demo-step-card-5 .demo-ai-sparkle { animation: demo-b5 20s ease-in-out infinite; }

        /* Connection line between steps */
        .demo-connector {
          position: absolute;
          left: 23px;
          width: 2px;
          background: repeating-linear-gradient(
            to bottom,
            ${theme.primary}30 0px,
            ${theme.primary}30 4px,
            transparent 4px,
            transparent 10px
          );
          z-index: 0;
        }
        .demo-connector-1 { height: 6px; margin-top: -1px; animation: demo-b2 20s ease-out infinite; }
        .demo-connector-2 { height: 6px; margin-top: -1px; animation: demo-b3 20s ease-out infinite; }
        .demo-connector-3 { height: 6px; margin-top: -1px; animation: demo-b4 20s ease-out infinite; }
        .demo-connector-4 { height: 6px; margin-top: -1px; animation: demo-b5 20s ease-out infinite; }

        /* Completion glow */
        .demo-complete-glow {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 200px;
          height: 200px;
          transform: translate(-50%, -50%);
          border-radius: 50%;
          background: radial-gradient(circle, ${theme.primary}12 0%, transparent 70%);
          pointer-events: none;
          opacity: 0;
          animation: demo-f5 20s ease-in-out infinite;
          filter: blur(30px);
        }

        @media (max-width: 1200px) {
          .auth-demo { display: none; }
        }
        @media (max-width: 960px) {
          .auth-container { flex-direction: column; }
          .auth-brand-panel { display: none; }
          .auth-form-panel { width: 100%; min-height: 100vh; }
        }
      `}</style>

      <div className="auth-brand-panel">
        <div className="auth-brand-inner">
        <div className="auth-brand-content">
          <div className="auth-brand-logo">
            <div className="auth-brand-logo-mark">
              <img src="/favicon.png" alt="InstaGuide" width="44" height="44" style={{ borderRadius: 12 }} />
            </div>
            <span className="auth-brand-logo-text">InstaGuide</span>
          </div>
          <div className="auth-ai-badge">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#a5b4fc" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3l1.912 5.813a2 2 0 0 0 1.275 1.275L21 12l-5.813 1.912a2 2 0 0 0-1.275 1.275L12 21l-1.912-5.813a2 2 0 0 0-1.275-1.275L3 12l5.813-1.912a2 2 0 0 0 1.275-1.275L12 3z"/>
            </svg>
            <span>Any app. Any click. It writes itself.</span>
          </div>
          <h1 className="auth-brand-heading">
            Turn every process<br/>into a <span>visual guide</span>
          </h1>
          <p className="auth-brand-desc">
            Record your workflow once — on any browser or desktop app. InstaGuide generates polished, step-by-step guides automatically.
          </p>
          <div className="auth-brand-features">
            <div className="auth-brand-feature">
              <div className="auth-brand-feature-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
              </div>
              Auto-capture clicks, keystrokes, and scrolls with screenshots
            </div>
            <div className="auth-brand-feature">
              <div className="auth-brand-feature-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3l1.912 5.813a2 2 0 0 0 1.275 1.275L21 12l-5.813 1.912a2 2 0 0 0-1.275 1.275L12 21l-1.912-5.813a2 2 0 0 0-1.275-1.275L3 12l5.813-1.912a2 2 0 0 0 1.275-1.275L12 3z"/></svg>
              </div>
              InstaGuide writes clear, concise instructions for each step
            </div>
            <div className="auth-brand-feature">
              <div className="auth-brand-feature-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
              </div>
              Embed any guide in Notion, Confluence, SharePoint, or your wiki
            </div>
            <div className="auth-brand-feature">
              <div className="auth-brand-feature-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              </div>
              Share with your team or export as PDF & HTML
            </div>
          </div>
        </div>

        {/* ===== PRODUCT DEMO ANIMATION ===== */}
        <div className="auth-demo">
          {/* Mock Browser Window */}
          <div className="demo-browser">
            <div className="demo-browser-bar">
              <div className="demo-browser-dots"><i></i><i></i><i></i></div>
              <div className="demo-browser-url">
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                support.acme.com/tickets/new
              </div>
            </div>
            <div className="demo-browser-body">
              {/* Mock Ticket Form UI */}
              <div className="demo-ui-sidebar">
                <div className="demo-ui-sidebar-item active"></div>
                <div className="demo-ui-sidebar-item"></div>
                <div className="demo-ui-sidebar-item"></div>
                <div className="demo-ui-sidebar-item"></div>
              </div>
              <div className="demo-ui-main">
                <div className="demo-ui-heading"></div>
                <div className="demo-ui-row">
                  <div style={{flex:1}}>
                    <div className="demo-ui-label" style={{width:'40%'}}></div>
                    <div className="demo-ui-field"></div>
                  </div>
                  <div style={{flex:1}}>
                    <div className="demo-ui-label" style={{width:'35%'}}></div>
                    <div className="demo-ui-field"></div>
                  </div>
                </div>
                <div className="demo-ui-label" style={{width:'30%'}}></div>
                <div className="demo-ui-field" style={{height:40,marginBottom:8}}></div>
                <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:8}}>
                  <div style={{width:50,height:18,borderRadius:4,border:'1px dashed rgba(255,255,255,0.1)',background:'rgba(255,255,255,0.02)'}}></div>
                  <div className="demo-ui-label" style={{width:'20%',marginBottom:0}}></div>
                </div>
                <div className="demo-ui-btn"></div>
              </div>

              {/* Animated Cursor */}
              <div className="demo-cursor">
                <svg width="16" height="20" viewBox="0 0 16 20" fill="none">
                  <path d="M1 1L1 14.5L4.5 11L8.5 18.5L11 17L7 9.5L12 9.5L1 1Z" fill="white" stroke="#1e293b" strokeWidth="1.2"/>
                </svg>
              </div>

              {/* Click ripples at each target position */}
              <div className="demo-click-ring demo-click-ring-1"></div>
              <div className="demo-click-ring demo-click-ring-2"></div>
              <div className="demo-click-ring demo-click-ring-3"></div>
              <div className="demo-click-ring demo-click-ring-4"></div>
              <div className="demo-click-ring demo-click-ring-5"></div>

              {/* Capture flash overlays */}
              <div className="demo-capture-flash demo-flash-1"></div>
              <div className="demo-capture-flash demo-flash-2"></div>
              <div className="demo-capture-flash demo-flash-3"></div>
              <div className="demo-capture-flash demo-flash-4"></div>
              <div className="demo-capture-flash demo-flash-5"></div>
            </div>

            {/* Recording badge */}
            <div className="demo-rec-badge">
              <div className="demo-rec-dot"></div>
              <span className="demo-rec-text">REC</span>
            </div>
          </div>

          {/* Step Cards — "Creating a Support Ticket" */}
          <div className="demo-steps">
            {/* Step 1 - Click */}
            <div className="demo-step-card demo-step-card-1">
              <div className="demo-step-num">1</div>
              <div className="demo-step-thumb">
                <div className="demo-step-thumb-img"></div>
                <div className="demo-click-dot"></div>
              </div>
              <div className="demo-step-info">
                <div className="demo-step-action-row">
                  <span className="demo-step-action-text">Click "New Ticket" button</span>
                  <span className="demo-event-badge click">click</span>
                </div>
                <div className="demo-step-desc">
                  <svg className="demo-ai-sparkle" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3l1.912 5.813a2 2 0 0 0 1.275 1.275L21 12l-5.813 1.912a2 2 0 0 0-1.275 1.275L12 21l-1.912-5.813a2 2 0 0 0-1.275-1.275L3 12l5.813-1.912a2 2 0 0 0 1.275-1.275L12 3z"/></svg>
                  <span className="demo-step-desc-text">Open the new ticket form from the support dashboard</span>
                </div>
              </div>
            </div>

            <div style={{position:'relative'}}><div className="demo-connector demo-connector-1"></div></div>

            {/* Step 2 - Click */}
            <div className="demo-step-card demo-step-card-2">
              <div className="demo-step-num">2</div>
              <div className="demo-step-thumb">
                <div className="demo-step-thumb-img"></div>
                <div className="demo-click-dot"></div>
              </div>
              <div className="demo-step-info">
                <div className="demo-step-action-row">
                  <span className="demo-step-action-text">Select priority "High"</span>
                  <span className="demo-event-badge click">click</span>
                </div>
                <div className="demo-step-desc">
                  <svg className="demo-ai-sparkle" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3l1.912 5.813a2 2 0 0 0 1.275 1.275L21 12l-5.813 1.912a2 2 0 0 0-1.275 1.275L12 21l-1.912-5.813a2 2 0 0 0-1.275-1.275L3 12l5.813-1.912a2 2 0 0 0 1.275-1.275L12 3z"/></svg>
                  <span className="demo-step-desc-text">Set the ticket priority level to High from the dropdown</span>
                </div>
              </div>
            </div>

            <div style={{position:'relative'}}><div className="demo-connector demo-connector-2"></div></div>

            {/* Step 3 - Keyboard */}
            <div className="demo-step-card demo-step-card-3">
              <div className="demo-step-num keyboard">3</div>
              <div className="demo-step-thumb">
                <div className="demo-step-thumb-img"></div>
                <div className="demo-click-dot"></div>
              </div>
              <div className="demo-step-info">
                <div className="demo-step-action-row">
                  <span className="demo-step-action-text">Type issue description</span>
                  <span className="demo-event-badge keyboard">key</span>
                </div>
                <div className="demo-step-desc">
                  <svg className="demo-ai-sparkle" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3l1.912 5.813a2 2 0 0 0 1.275 1.275L21 12l-5.813 1.912a2 2 0 0 0-1.275 1.275L12 21l-1.912-5.813a2 2 0 0 0-1.275-1.275L3 12l5.813-1.912a2 2 0 0 0 1.275-1.275L12 3z"/></svg>
                  <span className="demo-step-desc-text">Enter a detailed description of the reported issue</span>
                </div>
              </div>
            </div>

            <div style={{position:'relative'}}><div className="demo-connector demo-connector-3"></div></div>

            {/* Step 4 - Click */}
            <div className="demo-step-card demo-step-card-4">
              <div className="demo-step-num">4</div>
              <div className="demo-step-thumb">
                <div className="demo-step-thumb-img"></div>
                <div className="demo-click-dot"></div>
              </div>
              <div className="demo-step-info">
                <div className="demo-step-action-row">
                  <span className="demo-step-action-text">Click "Attach Screenshot"</span>
                  <span className="demo-event-badge click">click</span>
                </div>
                <div className="demo-step-desc">
                  <svg className="demo-ai-sparkle" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3l1.912 5.813a2 2 0 0 0 1.275 1.275L21 12l-5.813 1.912a2 2 0 0 0-1.275 1.275L12 21l-1.912-5.813a2 2 0 0 0-1.275-1.275L3 12l5.813-1.912a2 2 0 0 0 1.275-1.275L12 3z"/></svg>
                  <span className="demo-step-desc-text">Upload a screenshot as evidence for the support ticket</span>
                </div>
              </div>
            </div>

            <div style={{position:'relative'}}><div className="demo-connector demo-connector-4"></div></div>

            {/* Step 5 - Click */}
            <div className="demo-step-card demo-step-card-5">
              <div className="demo-step-num">5</div>
              <div className="demo-step-thumb">
                <div className="demo-step-thumb-img"></div>
                <div className="demo-click-dot"></div>
              </div>
              <div className="demo-step-info">
                <div className="demo-step-action-row">
                  <span className="demo-step-action-text">Click "Submit Ticket"</span>
                  <span className="demo-event-badge click">click</span>
                </div>
                <div className="demo-step-desc">
                  <svg className="demo-ai-sparkle" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3l1.912 5.813a2 2 0 0 0 1.275 1.275L21 12l-5.813 1.912a2 2 0 0 0-1.275 1.275L12 21l-1.912-5.813a2 2 0 0 0-1.275-1.275L3 12l5.813-1.912a2 2 0 0 0 1.275-1.275L12 3z"/></svg>
                  <span className="demo-step-desc-text">Submit the ticket and assign it to the support queue</span>
                </div>
              </div>
            </div>
          </div>

          {/* Completion glow */}
          <div className="demo-complete-glow"></div>
        </div>
        </div>{/* /auth-brand-inner */}
      </div>

      <div className="auth-form-panel">
      <div className="auth-card">
        <div className="auth-header">
          <h1 className="auth-title">
            {pendingVerify
              ? 'Check your inbox'
              : (isLogin ? 'Welcome back' : (invitation ? 'Join your team' : 'Create an account'))}
          </h1>
          <p className="auth-subtitle">
            {pendingVerify
              ? null
              : (invitation ? `You've been invited to ${invitation.organizationName}` : (isLogin ? 'Sign in to your InstaGuide account' : 'Get started with InstaGuide for free'))}
          </p>
        </div>

        {invitation && (
          <div style={{
            background: theme.gradientLight,
            border: '1px solid ${theme.primary}40',
            borderRadius: 12,
            padding: 16,
            marginBottom: 24,
            textAlign: 'center'
          }}>
            <div style={{ fontSize: 14, color: '#64748b', marginBottom: 4 }}>
              {invitation.inviterName} invited you to join
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#1e293b' }}>
              {invitation.organizationName}
            </div>
            {invitation.message && (
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 8, fontStyle: 'italic' }}>
                "{invitation.message}"
              </div>
            )}
          </div>
        )}

        {pendingMessage && !pendingVerify && (
          <div className="auth-pending">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
            </svg>
            <span>{pendingMessage}</span>
          </div>
        )}

        {pendingVerify && (
          <div style={{
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: 12,
            padding: '20px 20px 22px 20px',
            marginBottom: 20,
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 56, height: 56, borderRadius: '50%',
              background: 'linear-gradient(135deg, #eef2ff, #e0e7ff)',
              margin: '0 auto 14px auto',
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.15)',
            }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
            </div>
            <p style={{
              margin: '0 0 10px 0', color: '#1e293b',
              fontSize: 15, lineHeight: 1.55, textAlign: 'center', fontWeight: 500,
            }}>
              {pendingVerify.message || (
                <>Almost there. Check your inbox at <strong style={{ color: '#0f172a' }}>{pendingVerify.email}</strong> for a confirmation link to activate your account.</>
              )}
            </p>
            {pendingVerify.emailType === 'personal' && (
              <p style={{
                margin: '0 0 12px 0', color: '#64748b',
                fontSize: 13, lineHeight: 1.55, textAlign: 'center',
              }}>
                Once you confirm, an InstaGuide teammate will take a quick look and open your workspace. We'll email you the moment you're in.
              </p>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={resendState.status === 'sending' || resendState.status === 'sent'}
                style={{
                  background: resendState.status === 'sent' ? '#e0f2fe' : 'white',
                  border: '1px solid #cbd5e1',
                  borderRadius: 8,
                  padding: '10px 14px',
                  fontSize: 14,
                  fontWeight: 600,
                  color: resendState.status === 'sent' ? '#0369a1' : '#334155',
                  cursor: (resendState.status === 'sending' || resendState.status === 'sent') ? 'default' : 'pointer',
                }}
              >
                {resendState.status === 'sending' && 'Sending…'}
                {resendState.status === 'sent' && 'Sent — check your inbox'}
                {(resendState.status === 'idle' || resendState.status === 'error') && 'Resend confirmation email'}
              </button>
              {resendState.status === 'error' && (
                <div style={{ color: '#dc2626', fontSize: 13, textAlign: 'center' }}>{resendState.error}</div>
              )}
              <button
                type="button"
                onClick={() => {
                  setPendingVerify(null);
                  setResendState({ status: 'idle', error: null });
                  setIsLogin(true);
                  if (window.location.pathname !== '/login') window.history.pushState({}, '', '/login');
                }}
                style={{
                  background: 'none', border: 'none',
                  color: '#6366f1', fontSize: 13, fontWeight: 500,
                  cursor: 'pointer', padding: 0,
                }}
              >
                Back to sign in
              </button>
            </div>
          </div>
        )}

        {!pendingVerify && (
        <form onSubmit={handleSubmit}>
          {!isLogin && (
            <>
              <div className="input-group">
                <label>Your Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  required
                />
              </div>

              {!invitation && (
                <div className="account-type-selector">
                  <label>Account Type</label>
                  <div className="account-type-options">
                    <div
                      className={`account-type-option ${accountType === 'personal' ? 'selected' : ''}`}
                      onClick={() => setAccountType('personal')}
                    >
                      <div className="icon">{Icons.user}</div>
                      <div className="title">Personal</div>
                      <div className="desc">For individual use</div>
                      <div className="plans">Free, Starter, Pro</div>
                    </div>
                    <div
                      className={`account-type-option ${accountType === 'organization' ? 'selected' : ''}`}
                      onClick={() => setAccountType('organization')}
                    >
                      <div className="icon">{Icons.building}</div>
                      <div className="title">Organization</div>
                      <div className="desc">For teams & companies</div>
                      <div className="plans">Enterprise</div>
                    </div>
                  </div>
                </div>
              )}

              {!invitation && accountType === 'organization' && (
                <div className="input-group">
                  <label>Organization Name</label>
                  <input
                    type="text"
                    value={organizationName}
                    onChange={(e) => setOrganizationName(e.target.value)}
                    placeholder="Acme Inc."
                    required
                  />
                </div>
              )}
            </>
          )}

          <div className="input-group">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => !invitation && setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              readOnly={!!invitation}
              style={invitation ? { backgroundColor: '#f1f5f9', cursor: 'not-allowed' } : {}}
            />
          </div>

          <div className="input-group">
            <label>Password{!isLogin && invitation ? ' (create a password)' : ''}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />
            {error && (
              <div className="auth-error">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                {error}
              </div>
            )}
          </div>

          {isLogin && (
            <div style={{ textAlign: 'right', marginBottom: 16, marginTop: -12 }}>
              <button
                type="button"
                onClick={onForgotPassword}
                style={{
                  background: 'none', border: 'none', color: theme.primary,
                  fontSize: 13, fontWeight: 500, cursor: 'pointer', padding: 0
                }}
              >
                Forgot password?
              </button>
            </div>
          )}

          <button type="submit" className="primary-btn" disabled={loading}>
            {loading ? (isLogin ? 'Signing in...' : 'Creating account...') : (isLogin ? 'Login' : (invitation ? 'Join Organization' : (accountType === 'organization' ? 'Create Organization' : 'Create Account')))}
          </button>
        </form>
        )}

        {!invitation && !pendingVerify && (
          <button
            className="switch-btn"
            onClick={() => {
              // Keep the URL in sync with the toggle so /login and
              // /signup are shareable, bookmarkable, and the browser
              // back button works. Flipping isLogin without touching
              // the URL was a UX gap — users saw the signup form at
              // /login and couldn't distinguish.
              const nextIsLogin = !isLogin;
              setIsLogin(nextIsLogin);
              const nextPath = nextIsLogin ? '/login' : '/signup';
              if (window.location.pathname !== nextPath) {
                window.history.pushState({}, '', nextPath);
              }
            }}
          >
            {isLogin ? <>Don't have an account? <strong>Sign up</strong></> : <>Already have an account? <strong>Login</strong></>}
          </button>
        )}

        <div className="extension-box">
          <div className="extension-box-icon">
            {Icons.package}
          </div>
          <div className="extension-box-text">
            <p>Get the Chrome Extension to start capturing</p>
          </div>
          <a href={CHROME_STORE_URL} target="_blank" rel="noopener noreferrer" className="extension-btn">Install</a>
        </div>
      </div>
      </div>
    </div>
  );
}

// ============================================
// 2FA — shared visual styles + helpers
// ============================================
// Both setup and challenge views render on the same auth-card surface as
// LoginView (dark navy split-banner). The inputs are 6-digit TOTP codes;
// challenge view also accepts an 8-char hyphenated backup code.

const twoFactorCardStyles = `
  .tfa-page { min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 24px; background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%); }
  .tfa-card { background: white; border-radius: 16px; box-shadow: 0 24px 60px rgba(15, 23, 42, 0.35); width: 100%; max-width: 460px; overflow: hidden; animation: slideIn 0.3s ease; }
  .tfa-banner { background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%); color: white; padding: 28px 32px; }
  .tfa-banner h1 { font-size: 22px; font-weight: 700; margin: 0 0 4px; letter-spacing: -0.3px; }
  .tfa-banner p { font-size: 13px; margin: 0; opacity: 0.85; }
  .tfa-body { padding: 28px 32px 26px; color: #475569; font-size: 14px; line-height: 1.55; }
  .tfa-section { margin-bottom: 18px; }
  .tfa-label { font-size: 11px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 7px; }
  .tfa-input { width: 100%; padding: 11px 14px; border: 1px solid #e2e8f0; border-radius: 9px; font-size: 16px; font-family: 'SF Mono', ui-monospace, monospace; letter-spacing: 0.4em; text-align: center; box-sizing: border-box; outline: none; transition: border-color 0.15s, box-shadow 0.15s; }
  .tfa-input:focus { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15); }
  .tfa-input.backup { letter-spacing: 0.1em; }
  .tfa-error { background: #fef2f2; border: 1px solid #fecaca; color: #991b1b; padding: 9px 12px; border-radius: 8px; font-size: 12px; margin-bottom: 12px; }
  .tfa-info { background: #eef2ff; border: 1px solid #c7d2fe; color: #3730a3; padding: 10px 12px; border-radius: 8px; font-size: 12px; margin-bottom: 14px; line-height: 1.5; }
  .tfa-actions { display: flex; gap: 10px; margin-top: 4px; }
  .tfa-btn { flex: 1; padding: 11px 14px; border-radius: 10px; font-size: 14px; font-weight: 600; cursor: pointer; border: none; font-family: inherit; transition: all 0.15s; }
  .tfa-btn-primary { background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%); color: white; box-shadow: 0 4px 14px -2px rgba(99, 102, 241, 0.45); }
  .tfa-btn-primary:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 8px 20px -2px rgba(99, 102, 241, 0.55); }
  .tfa-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
  .tfa-btn-secondary { background: white; color: #475569; border: 1px solid #e2e8f0; }
  .tfa-btn-secondary:hover { background: #f8fafc; color: #0f172a; }
  .tfa-link { background: none; border: none; color: #6366f1; font-size: 12px; font-weight: 600; cursor: pointer; padding: 4px 0; text-decoration: underline; font-family: inherit; }
  .tfa-qr { display: block; width: 200px; height: 200px; margin: 0 auto 12px; border-radius: 10px; background: white; padding: 4px; border: 1px solid #e2e8f0; }
  .tfa-secret { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 12px; font-family: 'SF Mono', ui-monospace, monospace; font-size: 13px; letter-spacing: 0.05em; color: #0f172a; word-break: break-all; text-align: center; }
  .tfa-codes-list { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px; font-family: 'SF Mono', ui-monospace, monospace; font-size: 13px; line-height: 1.9; color: #0f172a; }
  .tfa-codes-list li { list-style: none; padding: 0; }
`;

// Two-factor SETUP — shown to a freshly-authenticated user who hasn't yet
// configured 2FA. Forces them through scan-QR → enter-first-code → save
// backup-codes before they can reach the dashboard.
function TwoFactorSetupView({ challengeToken, email, onComplete, onCancel }) {
  const [stage, setStage] = useState('init'); // 'init' | 'verify' | 'codes'
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [pendingComplete, setPendingComplete] = useState(null); // session payload to deliver on Continue
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Kick off setup on mount — gets the QR + secret from the backend.
  useEffect(() => {
    let alive = true;
    (async () => {
      setBusy(true);
      try {
        const res = await fetch(`${API_URL}/api/auth/2fa/setup`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${challengeToken}` }
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || 'Could not start 2FA setup');
        }
        const data = await res.json();
        if (!alive) return;
        setQrDataUrl(data.qrDataUrl);
        setSecret(data.secret);
        setStage('verify');
      } catch (e) {
        if (alive) setError(e.message || 'Setup failed');
      } finally {
        if (alive) setBusy(false);
      }
    })();
    return () => { alive = false; };
  }, [challengeToken]);

  const handleEnable = async (e) => {
    e?.preventDefault?.();
    if (!code) return;
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/auth/2fa/enable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${challengeToken}` },
        body: JSON.stringify({ code })
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Verification failed');
      setBackupCodes(body.backupCodes || []);
      setPendingComplete(body); // contains the session token
      setStage('codes');
    } catch (e) {
      setError(e.message || 'Verification failed');
    } finally {
      setBusy(false);
    }
  };

  const handleContinue = () => {
    if (pendingComplete) onComplete(pendingComplete);
  };

  const downloadBackupCodes = () => {
    const blob = new Blob([
      `InstaGuide.ai — 2FA Backup Codes\nAccount: ${email}\nGenerated: ${new Date().toISOString()}\n\nEach code can be used ONCE if you lose access to your authenticator. Keep them secret.\n\n` + backupCodes.join('\n') + '\n'
    ], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `instaguide-backup-codes-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <style>{twoFactorCardStyles}</style>
      <div className="tfa-page">
        <div className="tfa-card">
          <div className="tfa-banner">
            <h1>{stage === 'codes' ? 'Save your backup codes' : 'Set up two-factor authentication'}</h1>
            <p>{stage === 'codes' ? 'You can use these to sign in if you lose your device.' : `Required for ${email}`}</p>
          </div>
          <div className="tfa-body">
            {error && <div className="tfa-error">{error}</div>}

            {stage === 'init' && (
              <p style={{ margin: 0 }}>{busy ? 'Generating your secret…' : 'Loading…'}</p>
            )}

            {stage === 'verify' && (
              <form onSubmit={handleEnable}>
                <div className="tfa-info">
                  Open <strong>Google Authenticator</strong>, <strong>1Password</strong>, <strong>Authy</strong>, or any TOTP app and scan this QR code. Then enter the 6-digit code it shows.
                </div>
                <div className="tfa-section" style={{ textAlign: 'center' }}>
                  {qrDataUrl && <img src={qrDataUrl} alt="2FA QR code" className="tfa-qr" />}
                  <div className="tfa-label">Or enter this secret manually</div>
                  <div className="tfa-secret">{secret}</div>
                </div>
                <div className="tfa-section">
                  <div className="tfa-label">Code from your app</div>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="123456"
                    className="tfa-input"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    autoFocus
                  />
                </div>
                <div className="tfa-actions">
                  <button type="button" className="tfa-btn tfa-btn-secondary" onClick={onCancel} disabled={busy}>Sign out</button>
                  <button type="submit" className="tfa-btn tfa-btn-primary" disabled={busy || code.length !== 6}>
                    {busy ? 'Verifying…' : 'Verify & Enable'}
                  </button>
                </div>
              </form>
            )}

            {stage === 'codes' && (
              <>
                <div className="tfa-info" style={{ background: '#fff7ed', borderColor: '#fed7aa', color: '#9a3412' }}>
                  <strong>Save these now.</strong> Each code works once and lets you sign in without your authenticator. They will not be shown again.
                </div>
                <ul className="tfa-codes-list">
                  {backupCodes.map(c => <li key={c}>{c}</li>)}
                </ul>
                <div className="tfa-actions" style={{ marginTop: 16 }}>
                  <button type="button" className="tfa-btn tfa-btn-secondary" onClick={downloadBackupCodes}>Download .txt</button>
                  <button type="button" className="tfa-btn tfa-btn-primary" onClick={handleContinue}>I've saved them</button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// Two-factor CHALLENGE — shown to a returning user who already has 2FA set up.
// Accepts a 6-digit TOTP code OR (via a fallback link) one of the backup codes.
function TwoFactorChallengeView({ challengeToken, email, onComplete, onCancel, onForgotAuthenticator }) {
  const [code, setCode] = useState('');
  const [isBackup, setIsBackup] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!code) return;
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/auth/2fa/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${challengeToken}` },
        body: JSON.stringify({ code: code.trim(), isBackupCode: isBackup })
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Verification failed');
      onComplete(body);
    } catch (e) {
      setError(e.message || 'Verification failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <style>{twoFactorCardStyles}</style>
      <div className="tfa-page">
        <div className="tfa-card">
          <div className="tfa-banner">
            <h1>Two-factor authentication</h1>
            <p>Signing in as {email}</p>
          </div>
          <div className="tfa-body">
            {error && <div className="tfa-error">{error}</div>}
            <form onSubmit={submit}>
              <div className="tfa-section">
                <div className="tfa-label">{isBackup ? 'Backup code' : 'Code from your authenticator app'}</div>
                <input
                  type="text"
                  inputMode={isBackup ? 'text' : 'numeric'}
                  autoComplete="one-time-code"
                  maxLength={isBackup ? 24 : 6}
                  placeholder={isBackup ? 'xxxxxxxx-xxxxxxxx' : '123456'}
                  className={`tfa-input ${isBackup ? 'backup' : ''}`}
                  value={code}
                  onChange={(e) => setCode(isBackup ? e.target.value : e.target.value.replace(/\D/g, '').slice(0, 6))}
                  autoFocus
                />
              </div>
              <div className="tfa-actions">
                <button type="button" className="tfa-btn tfa-btn-secondary" onClick={onCancel} disabled={busy}>Cancel</button>
                <button type="submit" className="tfa-btn tfa-btn-primary" disabled={busy || !code}>
                  {busy ? 'Verifying…' : 'Verify'}
                </button>
              </div>
              <div style={{ marginTop: 14, textAlign: 'center' }}>
                <button type="button" className="tfa-link" onClick={() => { setIsBackup(!isBackup); setCode(''); setError(''); }}>
                  {isBackup ? 'Use authenticator code instead' : 'Use a backup code'}
                </button>
              </div>
              {onForgotAuthenticator && (
                <div style={{ marginTop: 8, textAlign: 'center' }}>
                  <button
                    type="button"
                    className="tfa-link"
                    style={{ color: '#94a3b8', fontSize: 12 }}
                    onClick={onForgotAuthenticator}
                  >
                    Lost your authenticator?
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

// Self-service 2FA recovery — request email step. Personal accounts only;
// org members are silently no-op'd server-side and told to ask their admin.
function TwoFactorForgotView({ initialEmail, onBackToLogin }) {
  const [email, setEmail] = useState(initialEmail || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/auth/2fa/forgot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Request failed');
      setSent(true);
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <style>{twoFactorCardStyles}</style>
      <div className="tfa-page">
        <div className="tfa-card">
          <div className="tfa-banner">
            <h1>Reset two-factor auth</h1>
            <p>{sent ? 'Check your email' : 'Personal accounts only'}</p>
          </div>
          <div className="tfa-body">
            {sent ? (
              <>
                <p style={{ color: '#475569', fontSize: 14, lineHeight: 1.6, margin: '0 0 18px' }}>
                  If a personal account with two-factor authentication exists for that email, we've sent a reset link. The link expires in 60 minutes.
                </p>
                <p style={{ color: '#94a3b8', fontSize: 13, lineHeight: 1.6, margin: '0 0 24px' }}>
                  If you're a member of an organization, ask your organization admin to reset 2FA for you instead.
                </p>
                <div className="tfa-actions">
                  <button type="button" className="tfa-btn tfa-btn-primary" onClick={onBackToLogin}>Back to sign in</button>
                </div>
              </>
            ) : (
              <form onSubmit={submit}>
                {error && <div className="tfa-error">{error}</div>}
                <p style={{ color: '#475569', fontSize: 14, lineHeight: 1.6, margin: '0 0 16px' }}>
                  We'll email you a link to remove the authenticator from your account so you can sign in and re-enroll.
                </p>
                <div className="tfa-section">
                  <div className="tfa-label">Email address</div>
                  <input
                    type="email"
                    className="tfa-input"
                    style={{ letterSpacing: 'normal', textAlign: 'left', fontFamily: 'inherit' }}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    autoFocus
                  />
                </div>
                <div className="tfa-actions">
                  <button type="button" className="tfa-btn tfa-btn-secondary" onClick={onBackToLogin} disabled={busy}>Cancel</button>
                  <button type="submit" className="tfa-btn tfa-btn-primary" disabled={busy || !email}>
                    {busy ? 'Sending…' : 'Send reset link'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// Self-service 2FA recovery — token confirmation step (email link landing page).
function TwoFactorRecoveryView({ token, onComplete }) {
  const [state, setState] = useState('confirming'); // 'confirming' | 'success' | 'error'
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/auth/2fa/reset`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token })
        });
        const body = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok) {
          setError(body.error || 'Reset failed');
          setState('error');
        } else {
          setState('success');
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message || 'Reset failed');
          setState('error');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  return (
    <>
      <style>{twoFactorCardStyles}</style>
      <div className="tfa-page">
        <div className="tfa-card">
          <div className="tfa-banner">
            <h1>Two-factor reset</h1>
            <p>
              {state === 'confirming' && 'Processing…'}
              {state === 'success' && 'All set'}
              {state === 'error' && 'Reset failed'}
            </p>
          </div>
          <div className="tfa-body">
            {state === 'confirming' && (
              <p style={{ color: '#64748b', textAlign: 'center', margin: 0 }}>Verifying your reset link…</p>
            )}
            {state === 'success' && (
              <>
                <p style={{ color: '#15803d', background: '#f0fdf4', border: '1px solid #bbf7d0', padding: 14, borderRadius: 10, fontSize: 14, lineHeight: 1.6, margin: '0 0 20px' }}>
                  Your authenticator and backup codes have been removed. Sign in with your password and you'll be prompted to set up two-factor authentication again.
                </p>
                <div className="tfa-actions">
                  <button type="button" className="tfa-btn tfa-btn-primary" onClick={onComplete}>Continue to sign in</button>
                </div>
              </>
            )}
            {state === 'error' && (
              <>
                <div className="tfa-error">{error}</div>
                <div className="tfa-actions" style={{ marginTop: 16 }}>
                  <button type="button" className="tfa-btn tfa-btn-primary" onClick={onComplete}>Back to sign in</button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// Forgot Password View - request a password reset email
function ForgotPasswordView({ onBackToLogin }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Server error. Please ensure the backend is running.');
      }
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Something went wrong');
      setSent(true);
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <style>{`
        .auth-container { min-height: 100vh; display: flex; background: #0f172a; }
        .auth-brand-panel { flex: 1; display: flex; flex-direction: column; justify-content: center; padding: 60px; position: relative; overflow: hidden; background: linear-gradient(160deg, #1e293b 0%, #0f172a 50%, ${theme.secondary}22 100%); }
        .auth-brand-panel::before { content: ''; position: absolute; top: -20%; right: -10%; width: 500px; height: 500px; border-radius: 50%; background: radial-gradient(circle, ${theme.primary}18 0%, transparent 70%); animation: auth-pulse-ring 6s ease-in-out infinite; }
        .auth-brand-panel::after { content: ''; position: absolute; bottom: -15%; left: -5%; width: 400px; height: 400px; border-radius: 50%; background: radial-gradient(circle, ${theme.secondary}12 0%, transparent 70%); animation: auth-pulse-ring 8s ease-in-out infinite 2s; }
        @keyframes auth-pulse-ring { 0% { transform: scale(0.9); opacity: 0.4; } 50% { transform: scale(1.05); opacity: 0.15; } 100% { transform: scale(0.9); opacity: 0.4; } }
        @keyframes auth-fade-up { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes auth-slide-in { from { opacity: 0; transform: translateX(-20px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes auth-shimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
        @keyframes auth-sparkle-pulse { 0%, 100% { opacity: 0.7; transform: scale(1); } 50% { opacity: 1; transform: scale(1.15); } }
        .auth-ai-badge { display: inline-flex; align-items: center; gap: 6px; padding: 6px 14px; border-radius: 20px; background: rgba(99, 102, 241, 0.15); border: 1px solid rgba(99, 102, 241, 0.25); margin-bottom: 24px; animation: auth-fade-up 0.6s ease-out 0.05s both; }
        .auth-ai-badge svg { animation: auth-sparkle-pulse 2s ease-in-out infinite; }
        .auth-ai-badge span { font-size: 13px; font-weight: 600; letter-spacing: 0.2px; background: linear-gradient(90deg, #a5b4fc, #c4b5fd, #a5b4fc); background-size: 200% auto; -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; animation: auth-shimmer 3s linear infinite; }
        .auth-brand-content { position: relative; z-index: 1; max-width: 480px; flex-shrink: 0; }
        .auth-brand-logo { display: flex; align-items: center; gap: 14px; margin-bottom: 48px; animation: auth-slide-in 0.6s ease-out; }
        .auth-brand-logo-mark { width: 44px; height: 44px; display: flex; align-items: center; justify-content: center; } .auth-brand-logo-mark img { display: block; }
        .auth-brand-logo-text { font-size: 22px; font-weight: 700; color: white; letter-spacing: -0.5px; }
        .auth-brand-heading { font-size: 40px; font-weight: 800; color: white; line-height: 1.15; margin: 0 0 20px 0; letter-spacing: -1px; animation: auth-fade-up 0.6s ease-out 0.1s both; }
        .auth-brand-heading span { background: ${theme.gradient}; -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
        .auth-brand-desc { font-size: 17px; color: #94a3b8; line-height: 1.7; margin: 0 0 40px 0; animation: auth-fade-up 0.6s ease-out 0.2s both; }
        .auth-brand-features { display: flex; flex-direction: column; gap: 16px; animation: auth-fade-up 0.6s ease-out 0.3s both; }
        .auth-brand-feature { display: flex; align-items: center; gap: 14px; color: #cbd5e1; font-size: 15px; }
        .auth-brand-feature-icon { width: 36px; height: 36px; border-radius: 10px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.08); display: flex; align-items: center; justify-content: center; flex-shrink: 0; color: ${theme.primary}; }
        .auth-form-panel { width: 520px; display: flex; align-items: center; justify-content: center; padding: 40px; background: white; position: relative; }
        .auth-form-panel::before { content: ''; position: absolute; left: 0; top: 10%; bottom: 10%; width: 1px; background: linear-gradient(to bottom, transparent, #e2e8f0, transparent); }
        .auth-card { width: 100%; max-width: 400px; animation: auth-fade-up 0.5s ease-out; }
        .auth-header { margin-bottom: 32px; }
        .auth-title { font-size: 26px; font-weight: 700; color: #0f172a; margin: 0 0 6px 0; letter-spacing: -0.5px; }
        .auth-subtitle { color: #64748b; font-size: 15px; margin: 0; }
        .input-group { margin-bottom: 20px; }
        .input-group label { display: block; font-size: 13px; font-weight: 600; color: #475569; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px; }
        .input-group input { width: 100%; padding: 13px 16px; border: 1.5px solid #e2e8f0; border-radius: 10px; font-size: 15px; transition: all 0.2s; outline: none; background: #f8fafc; }
        .input-group input:focus { border-color: ${theme.primary}; background: white; box-shadow: 0 0 0 3px ${theme.primary}15; }
        .primary-btn { width: 100%; padding: 14px; background: ${theme.gradient}; color: white; border: none; border-radius: 10px; font-size: 15px; font-weight: 600; cursor: pointer; transition: all 0.2s; letter-spacing: 0.3px; }
        .primary-btn:hover { box-shadow: 0 6px 20px ${theme.primary}40; filter: brightness(1.05); }
        .primary-btn:disabled { opacity: 0.7; cursor: not-allowed; transform: none; filter: none; box-shadow: none; }
        .switch-btn { display: block; width: 100%; text-align: center; background: none; border: none; color: #64748b; font-size: 14px; cursor: pointer; margin-top: 20px; }
        .switch-btn strong { color: ${theme.primary}; font-weight: 600; }
        .auth-error { background: #fef2f2; border: 1px solid #fecaca; color: #dc2626; padding: 10px 14px; border-radius: 8px; font-size: 13px; margin-top: 8px; display: flex; align-items: center; gap: 8px; }
        .auth-error svg { flex-shrink: 0; }
        .auth-success { background: #f0fdf4; border: 1px solid #bbf7d0; color: #15803d; padding: 14px 16px; border-radius: 10px; font-size: 13px; margin-bottom: 20px; display: flex; align-items: flex-start; gap: 10px; line-height: 1.5; }
        .auth-success svg { flex-shrink: 0; margin-top: 2px; }
        @media (max-width: 960px) {
          .auth-container { flex-direction: column; }
          .auth-brand-panel { display: none; }
          .auth-form-panel { width: 100%; min-height: 100vh; }
        }
      `}</style>

      <div className="auth-brand-panel">
        <div className="auth-brand-content">
          <div className="auth-brand-logo">
            <div className="auth-brand-logo-mark">
              <img src="/favicon.png" alt="InstaGuide" width="44" height="44" style={{ borderRadius: 12 }} />
            </div>
            <span className="auth-brand-logo-text">InstaGuide</span>
          </div>
          <div className="auth-ai-badge">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#a5b4fc" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3l1.912 5.813a2 2 0 0 0 1.275 1.275L21 12l-5.813 1.912a2 2 0 0 0-1.275 1.275L12 21l-1.912-5.813a2 2 0 0 0-1.275-1.275L3 12l5.813-1.912a2 2 0 0 0 1.275-1.275L12 3z"/>
            </svg>
            <span>Any app. Any click. It writes itself.</span>
          </div>
          <h1 className="auth-brand-heading">
            Turn every process<br/>into a <span>visual guide</span>
          </h1>
          <p className="auth-brand-desc">
            Record your workflow once — on any browser or desktop app. InstaGuide generates polished, step-by-step guides automatically.
          </p>
          <div className="auth-brand-features">
            <div className="auth-brand-feature">
              <div className="auth-brand-feature-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
              </div>
              Auto-capture clicks, keystrokes, and scrolls with screenshots
            </div>
            <div className="auth-brand-feature">
              <div className="auth-brand-feature-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3l1.912 5.813a2 2 0 0 0 1.275 1.275L21 12l-5.813 1.912a2 2 0 0 0-1.275 1.275L12 21l-1.912-5.813a2 2 0 0 0-1.275-1.275L3 12l5.813-1.912a2 2 0 0 0 1.275-1.275L12 3z"/></svg>
              </div>
              InstaGuide writes clear, concise instructions for each step
            </div>
            <div className="auth-brand-feature">
              <div className="auth-brand-feature-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
              </div>
              Embed any guide in Notion, Confluence, SharePoint, or your wiki
            </div>
            <div className="auth-brand-feature">
              <div className="auth-brand-feature-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              </div>
              Share with your team or export as PDF & HTML
            </div>
          </div>
        </div>
      </div>

      <div className="auth-form-panel">
      <div className="auth-card">
        <div className="auth-header">
          <div style={{ width: 48, height: 48, borderRadius: 12, background: `${theme.primary}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={theme.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </div>
          <h1 className="auth-title">Forgot password?</h1>
          <p className="auth-subtitle">No worries, we'll send you a reset link</p>
        </div>

        {sent ? (
          <>
            <div className="auth-success">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
              <span>If an account exists with <strong>{email}</strong>, a password reset link has been sent. Please check your inbox and spam folder.</span>
            </div>
            <button className="primary-btn" onClick={onBackToLogin}>
              Back to Login
            </button>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="input-group">
              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoFocus
              />
              {error && (
                <div className="auth-error">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  {error}
                </div>
              )}
            </div>

            <button type="submit" className="primary-btn" disabled={loading}>
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>
        )}

        {!sent && (
          <button className="switch-btn" onClick={onBackToLogin}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginRight: 6 }}>
              <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
            </svg>
            Back to <strong>Login</strong>
          </button>
        )}
      </div>
      </div>
    </div>
  );
}

// Reset Password View - set a new password using a reset token
function ResetPasswordView({ token, onBackToLogin }) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password })
      });
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Server error. Please ensure the backend is running.');
      }
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Something went wrong');
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const isExpiredError = error.includes('expired') || error.includes('Invalid');

  return (
    <div className="auth-container">
      <style>{`
        .auth-container { min-height: 100vh; display: flex; background: #0f172a; }
        .auth-brand-panel { flex: 1; display: flex; flex-direction: column; justify-content: center; padding: 60px; position: relative; overflow: hidden; background: linear-gradient(160deg, #1e293b 0%, #0f172a 50%, ${theme.secondary}22 100%); }
        .auth-brand-panel::before { content: ''; position: absolute; top: -20%; right: -10%; width: 500px; height: 500px; border-radius: 50%; background: radial-gradient(circle, ${theme.primary}18 0%, transparent 70%); animation: auth-pulse-ring 6s ease-in-out infinite; }
        .auth-brand-panel::after { content: ''; position: absolute; bottom: -15%; left: -5%; width: 400px; height: 400px; border-radius: 50%; background: radial-gradient(circle, ${theme.secondary}12 0%, transparent 70%); animation: auth-pulse-ring 8s ease-in-out infinite 2s; }
        @keyframes auth-pulse-ring { 0% { transform: scale(0.9); opacity: 0.4; } 50% { transform: scale(1.05); opacity: 0.15; } 100% { transform: scale(0.9); opacity: 0.4; } }
        @keyframes auth-fade-up { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes auth-slide-in { from { opacity: 0; transform: translateX(-20px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes auth-shimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
        @keyframes auth-sparkle-pulse { 0%, 100% { opacity: 0.7; transform: scale(1); } 50% { opacity: 1; transform: scale(1.15); } }
        .auth-ai-badge { display: inline-flex; align-items: center; gap: 6px; padding: 6px 14px; border-radius: 20px; background: rgba(99, 102, 241, 0.15); border: 1px solid rgba(99, 102, 241, 0.25); margin-bottom: 24px; animation: auth-fade-up 0.6s ease-out 0.05s both; }
        .auth-ai-badge svg { animation: auth-sparkle-pulse 2s ease-in-out infinite; }
        .auth-ai-badge span { font-size: 13px; font-weight: 600; letter-spacing: 0.2px; background: linear-gradient(90deg, #a5b4fc, #c4b5fd, #a5b4fc); background-size: 200% auto; -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; animation: auth-shimmer 3s linear infinite; }
        .auth-brand-content { position: relative; z-index: 1; max-width: 480px; flex-shrink: 0; }
        .auth-brand-logo { display: flex; align-items: center; gap: 14px; margin-bottom: 48px; animation: auth-slide-in 0.6s ease-out; }
        .auth-brand-logo-mark { width: 44px; height: 44px; display: flex; align-items: center; justify-content: center; } .auth-brand-logo-mark img { display: block; }
        .auth-brand-logo-text { font-size: 22px; font-weight: 700; color: white; letter-spacing: -0.5px; }
        .auth-brand-heading { font-size: 40px; font-weight: 800; color: white; line-height: 1.15; margin: 0 0 20px 0; letter-spacing: -1px; animation: auth-fade-up 0.6s ease-out 0.1s both; }
        .auth-brand-heading span { background: ${theme.gradient}; -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
        .auth-brand-desc { font-size: 17px; color: #94a3b8; line-height: 1.7; margin: 0 0 40px 0; animation: auth-fade-up 0.6s ease-out 0.2s both; }
        .auth-brand-features { display: flex; flex-direction: column; gap: 16px; animation: auth-fade-up 0.6s ease-out 0.3s both; }
        .auth-brand-feature { display: flex; align-items: center; gap: 14px; color: #cbd5e1; font-size: 15px; }
        .auth-brand-feature-icon { width: 36px; height: 36px; border-radius: 10px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.08); display: flex; align-items: center; justify-content: center; flex-shrink: 0; color: ${theme.primary}; }
        .auth-form-panel { width: 520px; display: flex; align-items: center; justify-content: center; padding: 40px; background: white; position: relative; }
        .auth-form-panel::before { content: ''; position: absolute; left: 0; top: 10%; bottom: 10%; width: 1px; background: linear-gradient(to bottom, transparent, #e2e8f0, transparent); }
        .auth-card { width: 100%; max-width: 400px; animation: auth-fade-up 0.5s ease-out; }
        .auth-header { margin-bottom: 32px; }
        .auth-title { font-size: 26px; font-weight: 700; color: #0f172a; margin: 0 0 6px 0; letter-spacing: -0.5px; }
        .auth-subtitle { color: #64748b; font-size: 15px; margin: 0; }
        .input-group { margin-bottom: 20px; }
        .input-group label { display: block; font-size: 13px; font-weight: 600; color: #475569; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px; }
        .input-group input { width: 100%; padding: 13px 16px; border: 1.5px solid #e2e8f0; border-radius: 10px; font-size: 15px; transition: all 0.2s; outline: none; background: #f8fafc; }
        .input-group input:focus { border-color: ${theme.primary}; background: white; box-shadow: 0 0 0 3px ${theme.primary}15; }
        .primary-btn { width: 100%; padding: 14px; background: ${theme.gradient}; color: white; border: none; border-radius: 10px; font-size: 15px; font-weight: 600; cursor: pointer; transition: all 0.2s; letter-spacing: 0.3px; }
        .primary-btn:hover { box-shadow: 0 6px 20px ${theme.primary}40; filter: brightness(1.05); }
        .primary-btn:disabled { opacity: 0.7; cursor: not-allowed; transform: none; filter: none; box-shadow: none; }
        .switch-btn { display: block; width: 100%; text-align: center; background: none; border: none; color: #64748b; font-size: 14px; cursor: pointer; margin-top: 20px; }
        .switch-btn strong { color: ${theme.primary}; font-weight: 600; }
        .auth-error { background: #fef2f2; border: 1px solid #fecaca; color: #dc2626; padding: 10px 14px; border-radius: 8px; font-size: 13px; margin-top: 8px; display: flex; align-items: center; gap: 8px; }
        .auth-error svg { flex-shrink: 0; }
        .auth-success { background: #f0fdf4; border: 1px solid #bbf7d0; color: #15803d; padding: 14px 16px; border-radius: 10px; font-size: 13px; margin-bottom: 20px; display: flex; align-items: flex-start; gap: 10px; line-height: 1.5; }
        .auth-success svg { flex-shrink: 0; margin-top: 2px; }
        .auth-error-block { background: #fef2f2; border: 1px solid #fecaca; color: #dc2626; padding: 14px 16px; border-radius: 10px; font-size: 13px; margin-bottom: 20px; display: flex; align-items: flex-start; gap: 10px; line-height: 1.5; }
        .auth-error-block svg { flex-shrink: 0; margin-top: 2px; }
        @media (max-width: 960px) {
          .auth-container { flex-direction: column; }
          .auth-brand-panel { display: none; }
          .auth-form-panel { width: 100%; min-height: 100vh; }
        }
      `}</style>

      <div className="auth-brand-panel">
        <div className="auth-brand-content">
          <div className="auth-brand-logo">
            <div className="auth-brand-logo-mark">
              <img src="/favicon.png" alt="InstaGuide" width="44" height="44" style={{ borderRadius: 12 }} />
            </div>
            <span className="auth-brand-logo-text">InstaGuide</span>
          </div>
          <div className="auth-ai-badge">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#a5b4fc" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3l1.912 5.813a2 2 0 0 0 1.275 1.275L21 12l-5.813 1.912a2 2 0 0 0-1.275 1.275L12 21l-1.912-5.813a2 2 0 0 0-1.275-1.275L3 12l5.813-1.912a2 2 0 0 0 1.275-1.275L12 3z"/>
            </svg>
            <span>Any app. Any click. It writes itself.</span>
          </div>
          <h1 className="auth-brand-heading">
            Turn every process<br/>into a <span>visual guide</span>
          </h1>
          <p className="auth-brand-desc">
            Record your workflow once — on any browser or desktop app. InstaGuide generates polished, step-by-step guides automatically.
          </p>
          <div className="auth-brand-features">
            <div className="auth-brand-feature">
              <div className="auth-brand-feature-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
              </div>
              Auto-capture clicks, keystrokes, and scrolls with screenshots
            </div>
            <div className="auth-brand-feature">
              <div className="auth-brand-feature-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3l1.912 5.813a2 2 0 0 0 1.275 1.275L21 12l-5.813 1.912a2 2 0 0 0-1.275 1.275L12 21l-1.912-5.813a2 2 0 0 0-1.275-1.275L3 12l5.813-1.912a2 2 0 0 0 1.275-1.275L12 3z"/></svg>
              </div>
              InstaGuide writes clear, concise instructions for each step
            </div>
            <div className="auth-brand-feature">
              <div className="auth-brand-feature-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
              </div>
              Embed any guide in Notion, Confluence, SharePoint, or your wiki
            </div>
            <div className="auth-brand-feature">
              <div className="auth-brand-feature-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              </div>
              Share with your team or export as PDF & HTML
            </div>
          </div>
        </div>
      </div>

      <div className="auth-form-panel">
      <div className="auth-card">
        <div className="auth-header">
          <div style={{ width: 48, height: 48, borderRadius: 12, background: success ? '#f0fdf418' : `${theme.primary}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
            {success ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#15803d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={theme.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            )}
          </div>
          <h1 className="auth-title">{success ? 'Password reset!' : 'Set new password'}</h1>
          <p className="auth-subtitle">{success ? 'Your password has been successfully reset' : 'Must be at least 8 characters'}</p>
        </div>

        {success ? (
          <>
            <div className="auth-success">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
              <span>Your password has been reset successfully. You can now log in with your new password.</span>
            </div>
            <button className="primary-btn" onClick={onBackToLogin}>
              Go to Login
            </button>
          </>
        ) : isExpiredError ? (
          <>
            <div className="auth-error-block">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <span>{error}</span>
            </div>
            <button className="primary-btn" onClick={onBackToLogin} style={{ marginBottom: 8 }}>
              Request New Link
            </button>
            <button className="switch-btn" onClick={onBackToLogin}>
              Back to <strong>Login</strong>
            </button>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="input-group">
              <label>New Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                placeholder="••••••••"
                required
                autoFocus
              />
            </div>

            <div className="input-group">
              <label>Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                placeholder="••••••••"
                required
              />
              {error && (
                <div className="auth-error">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  {error}
                </div>
              )}
            </div>

            <button type="submit" className="primary-btn" disabled={loading}>
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        )}

        {!success && !isExpiredError && (
          <button className="switch-btn" onClick={onBackToLogin}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginRight: 6 }}>
              <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
            </svg>
            Back to <strong>Login</strong>
          </button>
        )}
      </div>
      </div>
    </div>
  );
}

// Email confirmation landing page. Hit when the user clicks the link
// in their signup verification email. Fires POST /api/auth/verify-email
// on mount:
//   company email → server responds with { autoLoggedIn: true, token,
//     user, org, ... } — same shape as a login response. We drop them
//     straight into the dashboard via onAutoLogin().
//   personal email → server responds with { autoLoggedIn: false,
//     message } — we show the "your email is confirmed, admin is
//     reviewing" screen so they know what happens next.
// Invalid/expired token → error state with "back to sign in" +
// "resend confirmation" fallback (resend requires the email, so we
// route them back to the signup form to trigger a fresh attempt).
function VerifyEmailView({ token, onAutoLogin, onBackToLogin }) {
  const [status, setStatus] = useState('verifying'); // 'verifying' | 'personal_ok' | 'error'
  const [message, setMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const hasVerifiedRef = React.useRef(false);

  useEffect(() => {
    // Guard against React strict-mode's double-invocation in dev,
    // which would fire two verify requests and burn the token on the
    // first one — the second one hits "invalid or expired token".
    if (hasVerifiedRef.current) return;
    hasVerifiedRef.current = true;
    (async () => {
      try {
        const response = await fetch(`${API_URL}/api/auth/verify-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, client: 'web' }),
        });
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error('Server error. Please try again in a moment.');
        }
        const data = await response.json();
        if (!response.ok) {
          setStatus('error');
          setErrorMessage(data.error || 'This confirmation link is invalid or has expired.');
          return;
        }
        if (data.autoLoggedIn && data.token) {
          // Company email — App-level handler stashes the token and
          // navigates to the dashboard. Nothing else to render here.
          onAutoLogin(data);
          return;
        }
        // Personal email — verified, but awaiting admin.
        setStatus('personal_ok');
        setMessage(data.message || 'Thanks — your email is confirmed. Your workspace will open once we review your request.');
      } catch (err) {
        setStatus('error');
        setErrorMessage(err.message || 'Something went wrong verifying your email.');
      }
    })();
    // Intentionally empty — token is captured on mount; we don't
    // want to re-verify on every render.
  }, []); // eslint-disable-line

  return (
    <div className="auth-container">
      <style>{`
        .verify-container { min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 40px 20px; background: linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #f5f3ff 100%); }
        .verify-card { max-width: 460px; width: 100%; background: white; border-radius: 16px; padding: 40px 36px; box-shadow: 0 20px 60px rgba(15, 23, 42, 0.08); text-align: center; animation: auth-fade-up 0.4s ease-out; }
        .verify-icon-wrap { width: 72px; height: 72px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; margin: 0 0 20px 0; }
        .verify-title { font-size: 24px; font-weight: 700; color: #0f172a; margin: 0 0 10px 0; letter-spacing: -0.5px; }
        .verify-desc { font-size: 15px; color: #475569; line-height: 1.6; margin: 0 0 28px 0; }
        .verify-primary { display: inline-block; padding: 12px 28px; background: linear-gradient(135deg, #6366f1, #4f46e5); color: white; text-decoration: none; border: none; border-radius: 10px; font-size: 15px; font-weight: 600; cursor: pointer; }
        .verify-primary:hover { filter: brightness(1.05); box-shadow: 0 6px 20px rgba(99, 102, 241, 0.35); }
        .verify-secondary { display: inline-block; margin-top: 14px; background: none; border: none; color: #64748b; font-size: 13px; cursor: pointer; }
        @keyframes auth-fade-up { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes verify-spin { to { transform: rotate(360deg); } }
      `}</style>
      <div className="verify-container">
        <div className="verify-card">
          {status === 'verifying' && (
            <>
              <div className="verify-icon-wrap" style={{ background: '#eef2ff' }}>
                <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'verify-spin 1s linear infinite' }}>
                  <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                </svg>
              </div>
              <h1 className="verify-title">Confirming your email…</h1>
              <p className="verify-desc">One moment — we're activating your account.</p>
            </>
          )}
          {status === 'personal_ok' && (
            <>
              <div className="verify-icon-wrap" style={{ background: '#ecfdf5' }}>
                <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
              <h1 className="verify-title">Email confirmed</h1>
              <p className="verify-desc">{message}</p>
              <button className="verify-primary" onClick={onBackToLogin}>Back to sign in</button>
            </>
          )}
          {status === 'error' && (
            <>
              <div className="verify-icon-wrap" style={{ background: '#fef2f2' }}>
                <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              </div>
              <h1 className="verify-title">Link didn't work</h1>
              <p className="verify-desc">{errorMessage}</p>
              <button className="verify-primary" onClick={onBackToLogin}>Back to sign in</button>
              <div>
                <button className="verify-secondary" onClick={onBackToLogin}>
                  Or start a fresh signup if the link expired
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Organization Settings View (Users, Roles, Projects)
function OrganizationAdminView({ user, onBack, showConfirm, showToast, guides = [], embedded = false }) {
  const [organization, setOrganization] = useState(null);
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  // Per-resource loading flags — set true while their bgFetch is in flight,
  // flipped to false on success or error. Used to show a loading state in the
  // corresponding tab when the user clicks before the data has arrived.
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [loadingInvitations, setLoadingInvitations] = useState(true);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviting, setInviting] = useState(false);
  // Tab initial value: pick from `?tab=…` query string if present so deep-
  // links (e.g. the extension's "Manage in dashboard" affordance pointing
  // at the redaction tab) land on the right pane without an extra click.
  // Falls back to 'overview' when the query is missing / unrecognized.
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const qs = new URLSearchParams(window.location.search);
      const t = qs.get('tab');
      if (t && ['overview', 'members', 'invitations', 'roles', 'projects', 'redaction', 'ai', 'storage', 'embed'].includes(t)) {
        return t;
      }
    } catch { /* SSR / non-window — fall through */ }
    return 'overview';
  });

  // Usage stats
  const planLimitsFallback = { free: 10, starter: 50, pro: 200, enterprise: Infinity };
  const serverLimit = user?.planLimits?.guides;
  // `-1` and `null` both mean unlimited (Enterprise / custom plans). The
  // backend serializes `null` → `-1` post 2026-06-23; clients hitting an
  // older backend would see raw `null` so we treat both as Infinity here.
  // `0` is a real (if unusual) cap so we don't lump it into the unlimited
  // bucket — use explicit equality, not truthiness.
  const limit = (serverLimit === -1 || serverLimit === null)
    ? Infinity
    : (typeof serverLimit === 'number' ? serverLimit : (planLimitsFallback[user?.plan] || 10));
  const usagePercent = limit === Infinity ? 0 : (guides.length / limit) * 100;

  // Role creation state
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDescription, setNewRoleDescription] = useState('');
  const [newRolePermissions, setNewRolePermissions] = useState([]);

  // Project state
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectMembers, setProjectMembers] = useState([]);
  const [addMemberUserId, setAddMemberUserId] = useState('');
  const [addMemberRoleId, setAddMemberRoleId] = useState('role-editor');

  // AI Provider state. `default` means "use InstaGuide's built-in
  // AI" — no BYO credentials required, billed to InstaGuide.
  const [aiSettings, setAiSettings] = useState({
    ai_service: 'default',
    claude: { api_key: '', model: '', allowed_models: [] },
    azure_openai: { endpoint: '', api_key: '', deployment_name: '', api_version: '2024-02-15-preview', allowed_models: [] },
    snowflake_cortex: { account: '', username: '', password: '', warehouse: '', database: '', schema: '', model: '', allowed_models: [] },
    gemini: { api_key: '', model: '' },
    openai: { api_key: '', model: 'gpt-5.4', allowed_models: ['gpt-5.4', 'gpt-5.4-mini'] }
  });
  const [systemAllowedModels, setSystemAllowedModels] = useState({
    claude: [],
    azure_openai: [],
    snowflake_cortex: [],
    gemini: [],
    openai: []
  });
  const [aiSettingsLoading, setAiSettingsLoading] = useState(false);
  const [aiSettingsSaving, setAiSettingsSaving] = useState(false);

  // Redaction settings state (Sprint J). Lives in
  // organizations.settings.redaction on the backend. The endpoint
  // returns a merged object (defaults + stored values), so the
  // initial state here is just a placeholder until loadRedactionSettings()
  // runs.
  const [redactionSettings, setRedactionSettings] = useState({
    enabled: true,
    mode: 'permanent',
    style: 'blur',
    useAITier: true,
    enabledCategories: null,        // null = all built-in categories on
    customPatterns: [],
  });
  const [redactionSettingsLoading, setRedactionSettingsLoading] = useState(false);
  const [redactionSettingsSaving, setRedactionSettingsSaving] = useState(false);

  // Customer-facing category GROUPS. Each one bundles related backend
  // patternIds — the UI shows 4 toggles instead of 14, but detection
  // granularity is preserved (the backend still scans for each
  // underlying patternId separately). Keep `patterns` in sync with
  // backend/redaction/patterns.js if new patterns are added there.
  const REDACTION_CATEGORY_GROUPS = [
    {
      id: 'contact',
      label: 'Contact info',
      description: 'Email addresses, phone numbers.',
      patterns: ['email', 'phone'],
    },
    {
      id: 'government_id',
      label: 'Government IDs',
      description: "SSN / national IDs, driver's license numbers (detected when labeled, e.g. \"DL: A1234567\").",
      patterns: ['us_ssn', 'driver_license'],
    },
    {
      id: 'financial',
      label: 'Financial',
      description: 'Credit card numbers (Luhn-validated), IBAN / bank account numbers.',
      patterns: ['credit_card', 'iban'],
    },
    {
      id: 'credentials',
      label: 'Credentials',
      description: 'API keys, JWTs, private keys, and secrets in URL query strings.',
      patterns: ['stripe_key', 'aws_access_key', 'github_token', 'google_oauth', 'openai_key', 'anthropic_key', 'jwt', 'private_key', 'url_secret_param'],
      // Subgroups consolidate the 9 individual patterns into 4 logical
      // toggles so admins don't have to think about each vendor's key
      // format. Toggling a subgroup off removes ALL its patternIds from
      // the enabled list; toggling on re-adds them. The legacy flat
      // `patterns` array above stays for backward compatibility with
      // anything that iterates the full set.
      subgroups: [
        { id: 'api_keys',     label: 'API keys',                description: 'Stripe, AWS, GitHub, Google OAuth, OpenAI, Anthropic.', patterns: ['stripe_key', 'aws_access_key', 'github_token', 'google_oauth', 'openai_key', 'anthropic_key'] },
        { id: 'jwt_tokens',   label: 'JWT tokens',              description: 'Three-segment base64 JSON Web Tokens.',                  patterns: ['jwt'] },
        { id: 'private_keys', label: 'Private keys (PEM)',      description: 'RSA, EC, DSA, OpenSSH, PGP block headers.',             patterns: ['private_key'] },
        { id: 'url_secrets',  label: 'URL secret parameters',   description: '?api_key=, ?token=, ?access_token=, ?secret= in URLs.', patterns: ['url_secret_param'] },
      ],
    },
    {
      id: 'hipaa',
      label: 'HIPAA / Health data',
      description: "Protected Health Information: medical record numbers, provider IDs, insurance member IDs, diagnosis codes, and date of birth. Detected when labeled in the UI (e.g. \"MRN: 8231490\", \"DOB: 03/12/1978\"). Contact info and SSN are handled by their own categories.",
      patterns: ['mrn', 'npi', 'dea_number', 'insurance_member_id', 'icd10_code', 'dob'],
      subgroups: [
        { id: 'health_records',  label: 'Medical record numbers',     description: 'MRN, MR#, Medical Record Number labels.',       patterns: ['mrn'] },
        { id: 'provider_ids',    label: 'Provider IDs (NPI, DEA)',    description: 'National Provider Identifiers and DEA registration numbers.', patterns: ['npi', 'dea_number'] },
        { id: 'insurance_ids',   label: 'Insurance member IDs',       description: 'Member ID, Policy #, Subscriber ID, Group # labels.',  patterns: ['insurance_member_id'] },
        { id: 'diagnosis_codes', label: 'Diagnosis codes (ICD-10)',   description: 'ICD-10, Dx, Diagnosis labels — condition reveals PHI even without a name.', patterns: ['icd10_code'] },
        { id: 'birth_date',      label: 'Date of birth',              description: 'DOB, Date of Birth, Birthdate labels.',          patterns: ['dob'] },
      ],
    },
  ];
  // Flat list of every underlying patternId — used when normalizing
  // the "all-on" state back to `null` so the saved JSON stays compact.
  const ALL_REDACTION_PATTERN_IDS = REDACTION_CATEGORY_GROUPS.flatMap((g) => g.patterns);

  // BYO cloud storage state (Enterprise plan, org owners/admins only).
  // Mirrors the AI Provider shape: provider dropdown + conditional credential
  // fields. Secrets come back from the backend already masked (`***xxxx` or
  // `***(saved)` for the GCS JSON) so we never display them in plaintext.
  const [storageSettings, setStorageSettings] = useState({
    service: 'default',
    aws_s3: { region: '', bucket: '', access_key_id: '', secret_access_key: '', prefix: '' },
    azure_blob: { account_name: '', account_key: '', container: '', prefix: '' },
    gcs: { project_id: '', bucket: '', service_account_key: '', prefix: '' }
  });
  const [storageLoading, setStorageLoading] = useState(false);
  const [storageSaving, setStorageSaving] = useState(false);
  const [storageTesting, setStorageTesting] = useState(false);
  const [storageTestResult, setStorageTestResult] = useState(null);

  const token = localStorage.getItem('authToken');

  const PERMISSION_OPTIONS = [
    { id: 'guide_view', label: 'View Guides', description: 'Can view guides in the project' },
    { id: 'guide_create', label: 'Create Guides', description: 'Can create new guides' },
    { id: 'guide_edit', label: 'Edit Guides', description: 'Can edit guide content and annotations' },
    { id: 'guide_delete', label: 'Delete Guides', description: 'Can delete guides' },
    { id: 'guide_share', label: 'Share Guides', description: 'Can share guides with other members' },
    { id: 'guide_export', label: 'Export Guides', description: 'Can export guides to PDF/HTML' },
    { id: 'project_manage', label: 'Manage Project', description: 'Can edit project settings' },
    { id: 'member_manage', label: 'Manage Members', description: 'Can add/remove project members' },
    // Sprint J P2 — gates the "Reveal original" affordance on
    // permission-based-redacted steps. Org owners/admins always have
    // access; this permission only matters for project-member roles.
    { id: 'redaction.view_original', label: 'View original (redacted)', description: 'Can reveal the original screenshot on permission-based redacted steps' }
  ];

  // Check if personal account (has org record but with personal type)
  const isPersonalAccount = user?.accountType === 'personal';

  useEffect(() => {
    loadOrganizationData();
  }, []);

  // Auto-load the redaction settings when the page lands on the
  // redaction tab via deep-link (?tab=redaction). The click-handler
  // path already does this, but a URL-driven initial mount needs its
  // own trigger. Intentionally only depending on activeTab — adding
  // `loadRedactionSettings` to the dep array would re-run on every
  // render (the function isn't memoized), which isn't desired here.
  useEffect(() => {
    if (activeTab === 'redaction') loadRedactionSettings();
  }, [activeTab]);

  const loadOrganizationData = async () => {
    const headers = { 'Authorization': `Bearer ${token}` };

    // Helper: fire-and-forget background fetch that sets state when it arrives.
    // Flips the loading flag to false on completion so the corresponding tab
    // can stop showing a "loading" placeholder.
    const bgFetch = (url, setter, loadingSetter, label) => {
      fetch(url, { headers })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d != null) setter(d); })
        .catch(e => console.error(`Failed to load ${label}:`, e))
        .finally(() => loadingSetter(false));
    };

    // 1) Block ONLY on /api/organization — the Overview tab needs this to render.
    //    As soon as it resolves, the page shows up; remaining endpoints stream in
    //    independently in the background so other tabs are usually ready by the
    //    time the user clicks them.
    try {
      const orgRes = await fetch(`${API_URL}/api/organization`, { headers });
      if (orgRes.ok) setOrganization(await orgRes.json());
    } catch (error) {
      console.error('Failed to load organization:', error);
    }
    setLoading(false);

    // 2) Background fetches — don't await; each populates its own state slice when ready.
    bgFetch(`${API_URL}/api/projects`, setProjects, setLoadingProjects, 'projects');
    if (!isPersonalAccount) {
      bgFetch(`${API_URL}/api/organization/members`, setMembers, setLoadingMembers, 'members');
      bgFetch(`${API_URL}/api/organization/invitations`, setInvitations, setLoadingInvitations, 'invitations');
      bgFetch(`${API_URL}/api/roles`, setRoles, setLoadingRoles, 'roles');
    } else {
      // Personal accounts don't have these tabs; flip loading off so any conditional UI is consistent.
      setLoadingMembers(false);
      setLoadingInvitations(false);
      setLoadingRoles(false);
    }
  };

  const sendInvitation = async (e) => {
    e.preventDefault();
    if (!inviteEmail) return;

    setInviting(true);
    try {
      const response = await fetch(`${API_URL}/api/organization/invitations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ email: inviteEmail, role: inviteRole })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to send invitation');
      }

      const invitation = await response.json();
      setInvitations([invitation, ...invitations]);
      setInviteEmail('');
      // Copy invite URL to clipboard
      navigator.clipboard.writeText(invitation.inviteUrl).catch(() => {});
      showToast('Invitation sent! Link copied to clipboard.', 'success');
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setInviting(false);
    }
  };

  const cancelInvitation = (invitationId) => {
    showConfirm('Cancel Invitation', 'Are you sure you want to cancel this invitation?', async () => {
      try {
        await fetch(`${API_URL}/api/organization/invitations/${invitationId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setInvitations(invitations.filter(i => i.id !== invitationId));
      } catch (error) {
        showToast('Failed to cancel invitation', 'error');
      }
    }, { type: 'danger', confirmText: 'Cancel Invitation', cancelText: 'Keep' });
  };

  const updateMemberRole = async (memberId, newRole) => {
    try {
      const response = await fetch(`${API_URL}/api/organization/members/${memberId}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ role: newRole })
      });

      if (!response.ok) throw new Error('Failed to update role');

      setMembers(members.map(m => m.id === memberId ? { ...m, orgRole: newRole } : m));
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const removeMember = (memberId) => {
    showConfirm('Remove Member', 'Are you sure you want to remove this member from the organization?', async () => {
      try {
        await fetch(`${API_URL}/api/organization/members/${memberId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setMembers(members.filter(m => m.id !== memberId));
      } catch (error) {
        showToast('Failed to remove member', 'error');
      }
    }, { type: 'danger', confirmText: 'Remove', cancelText: 'Cancel' });
  };

  const resetMember2FA = (member) => {
    showConfirm(
      'Reset Two-Factor Authentication',
      `This will remove ${member.name || member.email}'s authenticator and any backup codes. They'll be prompted to set up 2FA again on their next login. Use this when they've lost access to their authenticator app.`,
      async () => {
        try {
          const response = await fetch(`${API_URL}/api/organization/members/${member.id}/reset-2fa`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const data = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(data.error || 'Reset failed');
          showToast(data.message || `2FA reset for ${member.email}`, 'success');
        } catch (error) {
          showToast(error.message || 'Failed to reset 2FA', 'error');
        }
      },
      { type: 'danger', confirmText: 'Reset 2FA', cancelText: 'Cancel' }
    );
  };

  // Role management functions
  const openRoleModal = (role = null) => {
    if (role) {
      setEditingRole(role);
      setNewRoleName(role.name);
      setNewRoleDescription(role.description || '');
      setNewRolePermissions(role.permissions || []);
    } else {
      setEditingRole(null);
      setNewRoleName('');
      setNewRoleDescription('');
      setNewRolePermissions(['guide_view']);
    }
    setShowRoleModal(true);
  };

  const saveRole = async () => {
    if (!newRoleName.trim()) {
      showToast('Role name is required', 'warning');
      return;
    }

    try {
      if (editingRole) {
        const response = await fetch(`${API_URL}/api/roles/${editingRole.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ name: newRoleName, description: newRoleDescription, permissions: newRolePermissions })
        });
        if (!response.ok) throw new Error('Failed to update role');
        const updated = await response.json();
        setRoles(roles.map(r => r.id === updated.id ? updated : r));
      } else {
        const response = await fetch(`${API_URL}/api/roles`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ name: newRoleName, description: newRoleDescription, permissions: newRolePermissions })
        });
        if (!response.ok) throw new Error('Failed to create role');
        const newRole = await response.json();
        setRoles([...roles, newRole]);
      }
      setShowRoleModal(false);
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const deleteRole = (roleId) => {
    showConfirm('Delete Role', 'Are you sure you want to delete this role?', async () => {
      try {
        await fetch(`${API_URL}/api/roles/${roleId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setRoles(roles.filter(r => r.id !== roleId));
      } catch (error) {
        showToast('Failed to delete role', 'error');
      }
    }, { type: 'danger', confirmText: 'Delete', cancelText: 'Cancel' });
  };

  // Project management functions
  const openProjectModal = (project = null) => {
    if (project) {
      setEditingProject(project);
      setNewProjectName(project.name);
      setNewProjectDescription(project.description || '');
    } else {
      setEditingProject(null);
      setNewProjectName('');
      setNewProjectDescription('');
    }
    setShowProjectModal(true);
  };

  const saveProject = async () => {
    if (!newProjectName.trim()) {
      showToast('Project name is required', 'warning');
      return;
    }

    try {
      if (editingProject) {
        const response = await fetch(`${API_URL}/api/projects/${editingProject.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ name: newProjectName, description: newProjectDescription })
        });
        if (!response.ok) throw new Error('Failed to update project');
        const updated = await response.json();
        setProjects(projects.map(p => p.id === updated.id ? { ...p, ...updated } : p));
      } else {
        const response = await fetch(`${API_URL}/api/projects`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ name: newProjectName, description: newProjectDescription })
        });
        if (!response.ok) throw new Error('Failed to create project');
        const newProject = await response.json();
        setProjects([...projects, newProject]);
      }
      setShowProjectModal(false);
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const deleteProject = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    if (project?.isDefault) {
      showToast('Cannot delete the default project', 'warning');
      return;
    }
    showConfirm('Delete Project', 'This will also delete all guides in this project. Are you sure?', async () => {
      try {
        await fetch(`${API_URL}/api/projects/${projectId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setProjects(projects.filter(p => p.id !== projectId));
        if (selectedProject?.id === projectId) setSelectedProject(null);
      } catch (error) {
        showToast('Failed to delete project', 'error');
      }
    }, { type: 'danger', confirmText: 'Delete Project', cancelText: 'Cancel' });
  };

  const selectProject = async (project) => {
    setSelectedProject(project);
    try {
      const response = await fetch(`${API_URL}/api/projects/${project.id}/members`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setProjectMembers(await response.json());
      }
    } catch (error) {
      console.error('Failed to load project members:', error);
    }
  };

  const addProjectMember = async () => {
    if (!addMemberUserId || !addMemberRoleId) {
      showToast('Please select a user and role', 'warning');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/projects/${selectedProject.id}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ userId: addMemberUserId, roleId: addMemberRoleId })
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to add member');
      }
      const newMember = await response.json();
      setProjectMembers([...projectMembers, newMember]);
      setAddMemberUserId('');
      // Update project member count
      setProjects(projects.map(p => p.id === selectedProject.id ? { ...p, memberCount: p.memberCount + 1 } : p));
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const updateProjectMemberRole = async (userId, newRoleId) => {
    try {
      await fetch(`${API_URL}/api/projects/${selectedProject.id}/members/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ roleId: newRoleId })
      });
      setProjectMembers(projectMembers.map(m => m.userId === userId ? { ...m, roleId: newRoleId, roleName: roles.find(r => r.id === newRoleId)?.name } : m));
    } catch (error) {
      showToast('Failed to update role', 'error');
    }
  };

  const removeProjectMember = (userId) => {
    showConfirm('Remove from Project', 'Remove this member from the project?', async () => {
      try {
        await fetch(`${API_URL}/api/projects/${selectedProject.id}/members/${userId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setProjectMembers(projectMembers.filter(m => m.userId !== userId));
        setProjects(projects.map(p => p.id === selectedProject.id ? { ...p, memberCount: p.memberCount - 1 } : p));
      } catch (error) {
        showToast('Failed to remove member', 'error');
      }
    }, { type: 'danger', confirmText: 'Remove', cancelText: 'Cancel' });
  };

  const togglePermission = (permId) => {
    if (newRolePermissions.includes(permId)) {
      setNewRolePermissions(newRolePermissions.filter(p => p !== permId));
    } else {
      setNewRolePermissions([...newRolePermissions, permId]);
    }
  };

  // AI Settings functions
  const loadAiSettings = async () => {
    setAiSettingsLoading(true);
    try {
      // Fetch both AI settings and system allowed models in parallel
      const [settingsRes, modelsRes] = await Promise.all([
        fetch(`${API_URL}/api/organization/ai-settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/ai-providers/allowed-models`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (modelsRes.ok) {
        const models = await modelsRes.json();
        setSystemAllowedModels(models);
      }

      if (settingsRes.ok) {
        const settings = await settingsRes.json();
        setAiSettings(settings);
      }
    } catch (error) {
      console.error('Failed to load AI settings:', error);
    } finally {
      setAiSettingsLoading(false);
    }
  };

  const saveAiSettings = async () => {
    setAiSettingsSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/organization/ai-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(aiSettings)
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to save AI settings');
      }
      showToast('AI settings saved successfully', 'success');
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setAiSettingsSaving(false);
    }
  };

  // Sprint J — redaction settings load + save. Endpoint returns a
  // merged object (defaults + stored values), so a fresh org reads
  // sane defaults without needing a separate "initialize" step.
  const loadRedactionSettings = async () => {
    setRedactionSettingsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/organization/redaction-settings`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) setRedactionSettings(await res.json());
    } catch (error) {
      console.error('Failed to load redaction settings:', error);
    } finally {
      setRedactionSettingsLoading(false);
    }
  };

  const saveRedactionSettings = async () => {
    setRedactionSettingsSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/organization/redaction-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(redactionSettings),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to save redaction settings');
      }
      showToast('Redaction settings saved', 'success');
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setRedactionSettingsSaving(false);
    }
  };

  // Toggle a category GROUP — expands to its constituent patternIds.
  // `null` means "all on" (backend default). When toggling for the
  // first time we promote `null` to an explicit list of every
  // patternId, then add/remove the group's patterns.
  const toggleRedactionGroup = (groupId, isOn) => {
    const group = REDACTION_CATEGORY_GROUPS.find((g) => g.id === groupId);
    if (!group) return;
    setRedactionSettings((prev) => {
      const cur = prev.enabledCategories;
      const base = cur === null || cur === undefined ? ALL_REDACTION_PATTERN_IDS.slice() : cur.slice();
      const next = isOn
        ? Array.from(new Set([...base, ...group.patterns]))
        : base.filter((id) => !group.patterns.includes(id));
      // Normalize "all on" back to null so the saved JSON stays small
      // and matches the backend's default semantics.
      const normalized = ALL_REDACTION_PATTERN_IDS.every((id) => next.includes(id))
        ? null
        : next;
      return { ...prev, enabledCategories: normalized };
    });
  };

  // A group is considered ON when all its constituent patternIds are
  // in the enabledCategories list (or the list is `null` = all on).
  // If a group is partially enabled (mixed state from legacy data),
  // we treat it as ON for the checkbox — user can flip it off and
  // re-on to normalize.
  const isGroupEnabled = (groupId) => {
    const group = REDACTION_CATEGORY_GROUPS.find((g) => g.id === groupId);
    if (!group) return false;
    const cur = redactionSettings.enabledCategories;
    if (cur === null || cur === undefined) return true;
    if (!Array.isArray(cur)) return true;
    // Group is "on" if AT LEAST ONE of its patterns is enabled. Mixed
    // state reads as on to avoid confusing UI; admin can toggle off
    // to wipe the group entirely.
    return group.patterns.some((id) => cur.includes(id));
  };

  // Per-pattern accessors. The group toggles above flip an entire
  // category at once; these let admins refine which individual
  // detectors run (e.g. enable email but not phone within Contact info).
  const isPatternEnabled = (patternId) => {
    const cur = redactionSettings.enabledCategories;
    if (cur === null || cur === undefined) return true;
    if (!Array.isArray(cur)) return true;
    return cur.includes(patternId);
  };

  const toggleRedactionPattern = (patternId, isOn) => {
    setRedactionSettings((prev) => {
      const cur = prev.enabledCategories;
      // Promote the "null = all on" shorthand to an explicit array the
      // first time we customize a single pattern, otherwise removing
      // one would leave us with a misleading null that the backend
      // would interpret as "all defaults."
      const base = cur === null || cur === undefined ? ALL_REDACTION_PATTERN_IDS.slice() : cur.slice();
      const next = isOn
        ? Array.from(new Set([...base, patternId]))
        : base.filter((id) => id !== patternId);
      const normalized = ALL_REDACTION_PATTERN_IDS.every((id) => next.includes(id))
        ? null
        : next;
      return { ...prev, enabledCategories: normalized };
    });
  };

  // Friendly per-pattern labels for the granular admin view. Match the
  // extension's customize popup so behavior reads the same on both
  // surfaces.
  const PATTERN_LABELS = {
    email: 'Email addresses', phone: 'Phone numbers',
    us_ssn: 'US SSN', driver_license: "Driver's license number",
    credit_card: 'Credit card number', iban: 'IBAN / bank account',
    stripe_key: 'Stripe API keys', aws_access_key: 'AWS access keys',
    github_token: 'GitHub tokens', google_oauth: 'Google OAuth tokens',
    openai_key: 'OpenAI API keys', anthropic_key: 'Anthropic API keys',
    jwt: 'JWT tokens', private_key: 'Private keys (PEM)',
    url_secret_param: 'URL secret params (?token=, ?api_key=, …)',
  };

  // Probe the current AI provider creds without saving. Used by the
  // "Test connection" button to give users immediate feedback before they
  // realise mid-recording that their key is wrong.
  const [aiSettingsTesting, setAiSettingsTesting] = useState(false);
  const [aiTestResult, setAiTestResult] = useState(null); // { success, error?, sample?, elapsedMs? }
  const testAiSettings = async () => {
    setAiSettingsTesting(true);
    setAiTestResult(null);
    try {
      const response = await fetch(`${API_URL}/api/organization/ai-settings/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(aiSettings)
      });
      const data = await response.json();
      setAiTestResult(data);
      if (data.success) {
        showToast(`Connection OK (${data.elapsedMs}ms)`, 'success');
      } else {
        showToast(data.error || 'Connection failed', 'error');
      }
    } catch (error) {
      setAiTestResult({ success: false, error: error.message });
      showToast(error.message, 'error');
    } finally {
      setAiSettingsTesting(false);
    }
  };

  // Inline SVG spinner — self-animating via SMIL, so it works on any page
  // regardless of which CSS style blocks are mounted. Used in the AI
  // Provider + Storage Provider tabs while settings are loading.
  const InlineSpinner = ({ size = 22, color = '#6366f1', label }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '40px 0', color: '#64748b' }}>
      <svg width={size} height={size} viewBox="0 0 50 50" aria-hidden="true">
        <circle cx="25" cy="25" r="20" fill="none" stroke={color} strokeOpacity="0.18" strokeWidth="4"/>
        <circle cx="25" cy="25" r="20" fill="none" stroke={color} strokeWidth="4" strokeLinecap="round" strokeDasharray="80 200" strokeDashoffset="0">
          <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="0.9s" repeatCount="indefinite"/>
        </circle>
      </svg>
      {label && <span style={{ fontSize: 14 }}>{label}</span>}
    </div>
  );

  // ── Storage Provider (BYO cloud storage) ─────────────────────────────────
  // Storage settings are Enterprise-only. The backend gates with 403
  // FEATURE_NOT_AVAILABLE, which we surface as a graceful "not available"
  // message — no toast spam.
  const loadStorageSettings = async () => {
    setStorageLoading(true);
    setStorageTestResult(null);
    try {
      const response = await fetch(`${API_URL}/api/organization/storage-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const settings = await response.json();
        setStorageSettings(settings);
      } else if (response.status === 403) {
        // Plan gate — leave defaults so the tab can render a CTA panel
        setStorageSettings(prev => ({ ...prev, _planGated: true }));
      }
    } catch (error) {
      console.error('Failed to load storage settings:', error);
    } finally {
      setStorageLoading(false);
    }
  };

  const saveStorageSettings = async () => {
    setStorageSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/organization/storage-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(storageSettings)
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to save storage settings');
      }
      showToast('Storage settings saved', 'success');
      // Reload so any newly-typed secrets come back masked
      await loadStorageSettings();
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setStorageSaving(false);
    }
  };

  const testStorageSettings = async () => {
    setStorageTesting(true);
    setStorageTestResult(null);
    try {
      if (!storageSettings.service || storageSettings.service === 'default') {
        showToast('Pick a provider before testing', 'warning');
        return;
      }
      const response = await fetch(`${API_URL}/api/organization/storage-settings/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(storageSettings)
      });
      const data = await response.json();
      setStorageTestResult(data);
      if (data.success) {
        showToast(`Storage connection OK (${data.elapsedMs}ms)`, 'success');
      } else {
        showToast(data.error || 'Connection failed', 'error');
      }
    } catch (error) {
      setStorageTestResult({ success: false, error: error.message });
      showToast(error.message, 'error');
    } finally {
      setStorageTesting(false);
    }
  };

  const updateStorageField = (service, key, value) => {
    setStorageSettings(prev => ({
      ...prev,
      [service]: { ...prev[service], [key]: value }
    }));
  };

  const updateAiServiceSetting = (service, key, value) => {
    setAiSettings(prev => ({
      ...prev,
      [service]: {
        ...prev[service],
        [key]: value
      }
    }));
  };

  // Note: we do NOT gate the entire admin render on `loading`. The page shell
  // (header, tabs, Overview stats) renders immediately with progressively-
  // populated data. Per-tab loading indicators (Users / Roles / Projects)
  // handle the data-empty states. This matches how the rest of the dashboard
  // pages render instantly — no full-page loading screen for one tab change.

  return (
    <div className="org-admin">
      <style>{`
        .org-admin {
          min-height: 100vh;
          background: #f8fafc;
        }
        .org-header {
          background: ${theme.gradient};
          padding: 20px 32px;
          color: white;
        }
        .org-header-content {
          max-width: 1600px;
          margin: 0 auto;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .org-header h1 {
          margin: 0;
          font-size: 24px;
        }
        .org-header .back-btn {
          background: rgba(255,255,255,0.2);
          border: none;
          color: white;
          padding: 10px 20px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
        }
        .org-content {
          max-width: 1600px;
          margin: 0 auto;
          padding: 32px;
        }
        .org-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
          gap: 16px;
          margin-bottom: 32px;
        }
        .stat-card {
          background: white;
          padding: 24px 28px;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          position: relative;
          overflow: hidden;
          transition: border-color 0.2s;
        }
        .stat-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 3px;
          height: 100%;
          background: ${theme.gradient};
          border-radius: 0 2px 2px 0;
        }
        .stat-card:hover {
          border-color: ${theme.primary}30;
        }
        .stat-card .label {
          color: #64748b;
          font-size: 13px;
          font-weight: 500;
          letter-spacing: 0.02em;
          margin-bottom: 10px;
        }
        .stat-card .value {
          font-size: 24px;
          font-weight: 600;
          color: #1e293b;
          letter-spacing: -0.01em;
        }
        .tabs {
          display: flex;
          gap: 0;
          margin-bottom: 24px;
          border-bottom: 1px solid #e2e8f0;
        }
        .tab {
          padding: 12px 24px;
          background: transparent;
          border: none;
          border-bottom: 2px solid transparent;
          cursor: pointer;
          font-weight: 600;
          font-size: 14px;
          color: #94a3b8;
          transition: all 0.2s;
          margin-bottom: -1px;
        }
        .tab:hover {
          color: #475569;
        }
        .tab.active {
          background: transparent;
          color: ${theme.primary};
          border-bottom-color: ${theme.primary};
        }
        .panel {
          background: white;
          border-radius: 12px;
          padding: 28px;
          border: 1px solid #e2e8f0;
        }
        .invite-form {
          display: flex;
          gap: 12px;
          margin-bottom: 24px;
        }
        .invite-form input {
          flex: 1;
          padding: 12px 16px;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          font-size: 14px;
        }
        .invite-form select {
          padding: 12px 16px;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          font-size: 14px;
        }
        .invite-form button {
          padding: 12px 24px;
          background: ${theme.gradient};
          color: white;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
        }
        .member-list, .invite-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .member-item, .invite-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 20px;
          background: white;
          border-radius: 10px;
          border: 1px solid #e2e8f0;
          transition: border-color 0.15s;
        }
        .member-item:hover, .invite-item:hover {
          border-color: #cbd5e1;
        }
        .member-info, .invite-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .member-avatar {
          width: 40px;
          height: 40px;
          background: ${theme.gradient};
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 700;
        }
        .member-details .name {
          font-weight: 600;
          color: #1e293b;
        }
        .member-details .email {
          font-size: 13px;
          color: #64748b;
        }
        .role-badge {
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
        }
        .role-badge.owner {
          background: #fef3c7;
          color: #d97706;
        }
        .role-badge.admin {
          background: #dbeafe;
          color: #2563eb;
        }
        .role-badge.member {
          background: #e2e8f0;
          color: #475569;
        }
        .role-badge.pending {
          background: #fce7f3;
          color: #db2777;
        }
        .member-actions {
          display: flex;
          gap: 8px;
        }
        .member-actions select {
          padding: 6px 10px;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          font-size: 13px;
        }
        .member-actions button {
          padding: 6px 12px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 13px;
        }
        .remove-btn {
          padding: 6px 14px;
          background: #fee2e2;
          color: #dc2626;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 500;
        }
        .remove-btn:hover {
          background: #fecaca;
        }
        .cancel-btn {
          padding: 6px 14px;
          background: #fee2e2;
          color: #dc2626;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 13px;
        }
        .primary-btn {
          padding: 10px 20px;
          background: ${theme.gradient};
          color: white;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
        }
        .edit-btn {
          padding: 6px 14px;
          background: #e2e8f0;
          color: #475569;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 500;
        }
        .edit-btn:hover {
          background: #cbd5e1;
        }
        .back-link {
          background: none;
          border: none;
          color: #64748b;
          cursor: pointer;
          font-weight: 600;
          padding: 0;
        }
        .role-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .project-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .role-item {
          display: flex;
          flex-direction: column;
          background: white;
          border-radius: 10px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
          transition: box-shadow 0.2s, border-color 0.2s;
        }
        .role-item:hover {
          border-color: #cbd5e1;
        }
        .role-item[data-role="Admin"] { border-left: 4px solid #ef4444; }
        .role-item[data-role="Admin"]:hover { box-shadow: 0 4px 16px rgba(239,68,68,0.08); }
        .role-item[data-role="Editor"] { border-left: 4px solid #6366f1; }
        .role-item[data-role="Editor"]:hover { box-shadow: 0 4px 16px rgba(99,102,241,0.08); }
        .role-item[data-role="Viewer"] { border-left: 4px solid #22c55e; }
        .role-item[data-role="Viewer"]:hover { box-shadow: 0 4px 16px rgba(34,197,94,0.08); }
        .role-item[data-role="custom"] { border-left: 4px solid #8b5cf6; }
        .role-item[data-role="custom"]:hover { box-shadow: 0 4px 16px rgba(139,92,246,0.08); }
        .role-item-header {
          padding: 14px 18px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-bottom: 1px solid #e2e8f0;
        }
        .role-item[data-role="Admin"] .role-item-header { background: linear-gradient(135deg, #fef2f2, #fee2e2); }
        .role-item[data-role="Editor"] .role-item-header { background: linear-gradient(135deg, #eef2ff, #e0e7ff); }
        .role-item[data-role="Viewer"] .role-item-header { background: linear-gradient(135deg, #f0fdf4, #dcfce7); }
        .role-item[data-role="custom"] .role-item-header { background: linear-gradient(135deg, #f5f3ff, #ede9fe); }
        .role-icon {
          width: 34px;
          height: 34px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .role-item[data-role="Admin"] .role-icon { background: #ef4444; }
        .role-item[data-role="Editor"] .role-icon { background: #6366f1; }
        .role-item[data-role="Viewer"] .role-icon { background: #22c55e; }
        .role-item[data-role="custom"] .role-icon { background: #8b5cf6; }
        .role-item-body {
          padding: 14px 18px;
        }
        .project-item {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          padding: 18px 20px;
          background: white;
          border-radius: 10px;
          border: 1px solid #e2e8f0;
          cursor: pointer;
          transition: background 0.2s, border-color 0.15s;
        }
        .project-item:hover {
          background: #f1f5f9;
          border-color: #cbd5e1;
        }
        .role-info, .project-info {
          flex: 1;
        }
        .role-name, .project-name {
          font-weight: 600;
          color: #1e293b;
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 4px;
        }
        .role-name { margin-bottom: 2px; }
        .role-description, .project-description {
          font-size: 13px;
          color: #64748b;
          margin-bottom: 8px;
        }
        .role-description { font-size: 12px; margin-bottom: 0; }
        .role-permissions {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }
        .role-perm-group {
          background: #f8fafc;
          border-radius: 8px;
          padding: 8px 12px;
          border: 1px solid #e2e8f020;
        }
        .role-perm-group[data-cat="guide"] { background: #eef2ff; border-color: rgba(99,102,241,0.12); }
        .role-perm-group[data-cat="project"] { background: #ecfdf5; border-color: rgba(16,185,129,0.12); }
        .role-perm-group[data-cat="member"] { background: #f5f3ff; border-color: rgba(139,92,246,0.12); }
        .role-perm-group-label {
          font-size: 10px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 4px;
        }
        .role-perm-group[data-cat="guide"] .role-perm-group-label { color: #6366f1; }
        .role-perm-group[data-cat="project"] .role-perm-group-label { color: #10b981; }
        .role-perm-group[data-cat="member"] .role-perm-group-label { color: #8b5cf6; }
        .role-perm-group .perm-badge {
          background: rgba(255,255,255,0.8);
          border: none;
        }
        .role-perm-group[data-cat="guide"] .perm-badge { color: #6366f1; }
        .role-perm-group[data-cat="project"] .perm-badge { color: #10b981; }
        .role-perm-group[data-cat="member"] .perm-badge { color: #8b5cf6; }
        .perm-badge {
          font-size: 11px;
          padding: 3px 10px;
          background: #f1f5f9;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          color: #475569;
          text-transform: capitalize;
          font-weight: 500;
          letter-spacing: 0.01em;
        }
        .perm-badge[data-type="view"] {
          background: #f0fdf4;
          border-color: #bbf7d0;
          color: #166534;
        }
        .perm-badge[data-type="create"] {
          background: #eff6ff;
          border-color: #bfdbfe;
          color: #1e40af;
        }
        .perm-badge[data-type="edit"] {
          background: #eef2ff;
          border-color: #c7d2fe;
          color: #4338ca;
        }
        .perm-badge[data-type="delete"] {
          background: #fef2f2;
          border-color: #fecaca;
          color: #991b1b;
        }
        .perm-badge[data-type="manage"] {
          background: #faf5ff;
          border-color: #e9d5ff;
          color: #6b21a8;
        }
        .system-badge {
          font-size: 10px;
          padding: 1px 7px;
          background: rgba(255,255,255,0.7);
          border-radius: 4px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .role-item[data-role="Admin"] .system-badge { color: #dc2626; border: 1px solid rgba(239,68,68,0.2); }
        .role-item[data-role="Editor"] .system-badge { color: #4f46e5; border: 1px solid rgba(99,102,241,0.2); }
        .role-item[data-role="Viewer"] .system-badge { color: #16a34a; border: 1px solid rgba(34,197,94,0.2); }
        .role-member-count {
          padding: 3px 10px;
          background: rgba(255,255,255,0.8);
          border-radius: 12px;
          font-size: 11px;
          color: #64748b;
          font-weight: 500;
        }
        .role-perm-count {
          font-size: 11px;
          font-weight: 600;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 10px;
        }
        .default-badge {
          font-size: 11px;
          padding: 2px 8px;
          background: #dbeafe;
          border-radius: 10px;
          color: #2563eb;
        }
        .project-stats {
          display: flex;
          gap: 16px;
          font-size: 13px;
          color: #64748b;
        }
        .role-actions, .project-actions {
          display: flex;
          gap: 8px;
          align-items: flex-start;
        }
        /* Custom Dialog Styles */
        .custom-dialog-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          animation: dialogFadeIn 0.2s ease;
        }
        @keyframes dialogFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .custom-dialog {
          background: white;
          border-radius: 16px;
          width: 400px;
          max-width: 90vw;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          animation: dialogSlideIn 0.2s ease;
          overflow: hidden;
        }
        @keyframes dialogSlideIn {
          from { transform: scale(0.95) translateY(-10px); opacity: 0; }
          to { transform: scale(1) translateY(0); opacity: 1; }
        }
        .custom-dialog-header {
          padding: 20px 24px 16px;
          display: flex;
          align-items: center;
          gap: 12px;
        }
        /* Softer confirm-dialog icon treatment.
           Previously used a saturated gradient background with a big
           text glyph inside (e.g. filled red square + "!"). The red
           box read as an alarm even for routine "delete this guide"
           confirmations, so the whole family — danger / success /
           default — is now a lighter tint with the color-carrying SVG
           icon inline instead of a text glyph. */
        .custom-dialog-icon {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .custom-dialog-icon.default {
          background: #eef2ff;
          color: #4f46e5;
        }
        .custom-dialog-icon.danger {
          background: #fef2f2;
          color: #dc2626;
        }
        .custom-dialog-icon.prompt {
          background: #ecfdf5;
          color: #059669;
        }
        .custom-dialog-icon.success {
          background: #ecfdf5;
          color: #059669;
        }
        .custom-dialog-title {
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
        }
        .custom-dialog-body {
          padding: 0 24px 20px;
        }
        .custom-dialog-message {
          font-size: 14px;
          color: #64748b;
          line-height: 1.5;
          margin: 0;
        }
        .custom-dialog-input {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          font-size: 15px;
          transition: all 0.2s;
          box-sizing: border-box;
          font-family: inherit;
        }
        .custom-dialog-input:focus {
          outline: none;
          border-color: #64748b;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }
        .custom-dialog-footer {
          padding: 16px 24px;
          background: #f8fafc;
          display: flex;
          justify-content: flex-end;
          gap: 12px;
        }
        .custom-dialog-btn {
          padding: 10px 20px;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          border: none;
        }
        .custom-dialog-btn-cancel {
          background: white;
          color: #64748b;
          border: 1px solid #e2e8f0;
        }
        .custom-dialog-btn-cancel:hover {
          background: #f1f5f9;
          color: #475569;
        }
        .custom-dialog-btn-confirm {
          background: ${theme.gradient};
          color: white;
        }
        .custom-dialog-btn-confirm:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }
        .custom-dialog-btn-confirm:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }
        .custom-dialog-btn-danger {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
        }
        .custom-dialog-btn-danger:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
        }
      `}</style>

      {embedded ? (
        <div className="main-header">
          <h1 className="main-header-title">Administration</h1>
        </div>
      ) : (
        <div className="org-header">
          <div className="org-header-content">
            <h1 style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>{Icons.settings} Settings</h1>
            <button className="back-btn" onClick={onBack}>← Back to Dashboard</button>
          </div>
        </div>
      )}

      <div className={embedded ? "page-content" : "org-content"}>
        {/* Tabs - hide Users, Roles, Projects for personal accounts */}
        <div className="tabs" style={{ marginBottom: '24px' }}>
          <button className={`tab ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>
            Overview
          </button>
          {!isPersonalAccount && (
            <>
              <button className={`tab ${activeTab === 'users' ? 'active' : ''}`} onClick={() => setActiveTab('users')}>
                Users
              </button>
              <button className={`tab ${activeTab === 'roles' ? 'active' : ''}`} onClick={() => setActiveTab('roles')}>
                Roles
              </button>
              <button className={`tab ${activeTab === 'projects' ? 'active' : ''}`} onClick={() => { setActiveTab('projects'); setSelectedProject(null); }}>
                Projects
              </button>
            </>
          )}
          <button className={`tab ${activeTab === 'ai-provider' ? 'active' : ''}`} onClick={() => { setActiveTab('ai-provider'); loadAiSettings(); }}>
            AI Provider
          </button>
          {!isPersonalAccount && (
            <button className={`tab ${activeTab === 'storage-provider' ? 'active' : ''}`} onClick={() => { setActiveTab('storage-provider'); loadStorageSettings(); }}>
              Storage Provider
            </button>
          )}
          <button className={`tab ${activeTab === 'redaction' ? 'active' : ''}`} onClick={() => { setActiveTab('redaction'); loadRedactionSettings(); }}>
            Redaction
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="panel">
            <h3 style={{ margin: '0 0 20px 0', color: '#1e293b' }}>{isPersonalAccount ? 'Account Overview' : 'Organization Overview'}</h3>

            <div className="org-stats" style={{ marginBottom: '32px' }}>
              {!isPersonalAccount && (
                <div className="stat-card">
                  <div className="label">Team Members</div>
                  <div className="value">{members.length}</div>
                </div>
              )}
              <div className="stat-card">
                <div className="label">Total Guides</div>
                <div className="value">{guides.length}</div>
              </div>
              <div className="stat-card">
                <div className="label">Projects</div>
                <div className="value">{projects.length}</div>
              </div>
              <div className="stat-card">
                <div className="label">Plan</div>
                <div className="value" style={{ textTransform: 'capitalize' }}>{organization?.plan || user?.plan}</div>
              </div>
            </div>

            <h4 style={{ margin: '0 0 16px 0', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>{Icons.barChart} Usage</h4>
            <div style={{
              background: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: '24px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                <span style={{ fontWeight: '600', color: '#1e293b' }}>Guides Used</span>
                <span style={{ color: '#64748b' }}>
                  {guides.length} / {limit === Infinity ? '∞' : limit}
                </span>
              </div>
              <div style={{
                height: '8px',
                background: '#f1f5f9',
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{
                  height: '100%',
                  width: `${Math.min(usagePercent, 100)}%`,
                  background: usagePercent > 90
                    ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                    : theme.gradient,
                  borderRadius: '4px',
                  transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
                }} />
              </div>
              {usagePercent >= 80 && (
                <p style={{ color: '#f59e0b', fontSize: '14px', marginTop: '12px', marginBottom: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {React.cloneElement(Icons.alertCircle, { width: 16, height: 16 })} You're approaching your plan limit. Consider upgrading for more guides.
                </p>
              )}
            </div>

          </div>
        )}

        {/* Users Tab - Organization accounts only */}
        {!isPersonalAccount && activeTab === 'users' && (
          <div className="panel">
            <h3 style={{ margin: '0 0 16px 0', color: '#1e293b' }}>Invite New Member</h3>
            <form className="invite-form" onSubmit={sendInvitation}>
              <input
                type="email"
                placeholder="Email address to invite"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                required
              />
              <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
                <option value="member">Member</option>
                <option value="admin">Admin</option>
                <option value="viewer">Viewer</option>
              </select>
              <button type="submit" disabled={inviting}>
                {inviting ? 'Sending...' : 'Send Invite'}
              </button>
            </form>

            {invitations.filter(i => i.status === 'pending').length > 0 && (
              <>
                <h4 style={{ margin: '24px 0 12px 0', color: '#64748b' }}>Pending Invitations</h4>
                <div className="invite-list" style={{ marginBottom: 24 }}>
                  {invitations.filter(i => i.status === 'pending').map(invite => (
                    <div key={invite.id} className="invite-item">
                      <div className="invite-info">
                        <div className="member-avatar">✉</div>
                        <div className="member-details">
                          <div className="name">{invite.email}</div>
                          <div className="email">Expires {new Date(invite.expiresAt).toLocaleDateString()}</div>
                        </div>
                        <span className="role-badge pending">{invite.role}</span>
                      </div>
                      <button className="cancel-btn" onClick={() => cancelInvitation(invite.id)}>Cancel</button>
                    </div>
                  ))}
                </div>
              </>
            )}

            <h4 style={{ margin: '0 0 12px 0', color: '#64748b' }}>Team Members</h4>
            <div className="member-list">
              {loadingMembers && members.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px 16px', color: '#94a3b8', fontSize: 13 }}>Loading members…</div>
              ) : members.map(member => (
                <div key={member.id} className="member-item">
                  <div className="member-info">
                    <div className="member-avatar">{member.name?.charAt(0)?.toUpperCase()}</div>
                    <div className="member-details">
                      <div className="name">{member.name} {member.id === user.id && '(You)'}</div>
                      <div className="email">{member.email}</div>
                    </div>
                    <span className={`role-badge ${member.orgRole}`}>{member.orgRole}</span>
                  </div>
                  {member.orgRole !== 'owner' && member.id !== user.id && (
                    <div className="member-actions">
                      <select value={member.orgRole} onChange={(e) => updateMemberRole(member.id, e.target.value)}>
                        <option value="admin">Admin</option>
                        <option value="member">Member</option>
                        <option value="viewer">Viewer</option>
                      </select>
                      <button
                        className="remove-btn"
                        onClick={() => resetMember2FA(member)}
                        title="Wipe their 2FA secret + backup codes so they can re-enroll on next login"
                        style={{ background: '#fef3c7', color: '#92400e', borderColor: '#fde68a' }}
                      >
                        Reset 2FA
                      </button>
                      <button className="remove-btn" onClick={() => removeMember(member.id)}>Remove</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Roles Tab - Organization accounts only */}
        {!isPersonalAccount && activeTab === 'roles' && (
          <div className="panel">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div>
                <h3 style={{ margin: 0, color: '#1e293b' }}>Roles & Permissions</h3>
                <p style={{ fontSize: 13, color: '#64748b', margin: '4px 0 0' }}>Manage access levels for your organization</p>
              </div>
              <button className="primary-btn" onClick={() => openRoleModal()}>+ Create Role</button>
            </div>

            <div className="role-list">
              {loadingRoles && roles.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px 16px', color: '#94a3b8', fontSize: 13 }}>Loading roles…</div>
              ) : roles.map(role => {
                const dataRole = ['Admin', 'Editor', 'Viewer'].includes(role.name) ? role.name : 'custom';
                const permGroups = {};
                (role.permissions || []).forEach(p => {
                  const cat = p.split('_')[0];
                  if (!permGroups[cat]) permGroups[cat] = [];
                  permGroups[cat].push(p);
                });
                const catLabels = { guide: 'Guide', project: 'Project', member: 'Team' };
                const memberCount = members.filter(m => m.roleId === role.id).length;
                return (
                <div key={role.id} className="role-item" data-role={dataRole}>
                  <div className="role-item-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div className="role-icon">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                        </svg>
                      </div>
                      <div>
                        <div className="role-name" style={{ marginBottom: 0 }}>
                          {role.name}
                          {role.isSystem && <span className="system-badge">System</span>}
                        </div>
                        <div className="role-description" style={{ marginBottom: 0 }}>{role.description}</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span className="role-member-count">{memberCount} {memberCount === 1 ? 'member' : 'members'}</span>
                      {!role.isSystem && (
                        <div className="role-actions">
                          <button className="edit-btn" onClick={() => openRoleModal(role)}>Edit</button>
                          <button className="remove-btn" onClick={() => deleteRole(role.id)}>Delete</button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="role-item-body">
                    <div className="role-perm-count">Permissions ({(role.permissions || []).length})</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                      {Object.entries(permGroups).map(([cat, perms]) => (
                        <div key={cat} className="role-perm-group" data-cat={cat}>
                          <div className="role-perm-group-label">{catLabels[cat] || cat}</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {perms.map(p => (
                              <span key={p} className="perm-badge">{p.split('_').slice(1).join(' ')}</span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Projects Tab - Organization accounts only */}
        {!isPersonalAccount && activeTab === 'projects' && !selectedProject && (
          <div className="panel">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, color: '#1e293b' }}>Manage Projects</h3>
              <button className="primary-btn" onClick={() => openProjectModal()}>+ Create Project</button>
            </div>

            <div className="project-list">
              {loadingProjects && projects.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px 16px', color: '#94a3b8', fontSize: 13 }}>Loading projects…</div>
              ) : projects.map(project => (
                <div key={project.id} className="project-item" onClick={() => selectProject(project)}>
                  <div className="project-info">
                    <div className="project-name">
                      {project.name}
                      {project.isDefault && <span className="default-badge">Default</span>}
                    </div>
                    <div className="project-description">{project.description || 'No description'}</div>
                    <div className="project-stats">
                      <span>{project.memberCount || 0} members</span>
                      <span>{project.guideCount || 0} guides</span>
                    </div>
                  </div>
                  <div className="project-actions" onClick={e => e.stopPropagation()}>
                    <button className="edit-btn" onClick={() => openProjectModal(project)}>Edit</button>
                    {!project.isDefault && (
                      <button className="remove-btn" onClick={() => deleteProject(project.id)}>Delete</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Project Members View - Organization accounts only */}
        {!isPersonalAccount && activeTab === 'projects' && selectedProject && (
          <div className="panel">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <button className="back-link" onClick={() => setSelectedProject(null)}>← Back to Projects</button>
              <h3 style={{ margin: 0, color: '#1e293b' }}>{selectedProject.name} - Members</h3>
            </div>

            <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
              <select value={addMemberUserId} onChange={(e) => setAddMemberUserId(e.target.value)} style={{ flex: 1, padding: '10px 12px', borderRadius: 8, border: '2px solid #e2e8f0' }}>
                <option value="">Select a user to add...</option>
                {members.filter(m => !projectMembers.find(pm => pm.userId === m.id)).map(m => (
                  <option key={m.id} value={m.id}>{m.name} ({m.email})</option>
                ))}
              </select>
              <select value={addMemberRoleId} onChange={(e) => setAddMemberRoleId(e.target.value)} style={{ padding: '10px 12px', borderRadius: 8, border: '2px solid #e2e8f0' }}>
                {roles.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
              <button className="primary-btn" onClick={addProjectMember}>Add Member</button>
            </div>

            <div className="member-list">
              {projectMembers.length === 0 ? (
                <p style={{ color: '#64748b', textAlign: 'center', padding: 20 }}>No members in this project yet</p>
              ) : (
                projectMembers.map(pm => (
                  <div key={pm.userId} className="member-item">
                    <div className="member-info">
                      <div className="member-avatar">{pm.userName?.charAt(0)?.toUpperCase()}</div>
                      <div className="member-details">
                        <div className="name">{pm.userName}</div>
                        <div className="email">{pm.userEmail}</div>
                      </div>
                      <span className="role-badge member">{pm.roleName}</span>
                    </div>
                    <div className="member-actions">
                      <select value={pm.roleId} onChange={(e) => updateProjectMemberRole(pm.userId, e.target.value)}>
                        {roles.map(r => (
                          <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                      </select>
                      <button className="remove-btn" onClick={() => removeProjectMember(pm.userId)}>Remove</button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* AI Provider Tab - Organization accounts only */}
        {activeTab === 'ai-provider' && (
          <div className="panel">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <div>
                <h3 style={{ margin: 0, color: '#1e293b' }}>AI Provider Settings</h3>
                <p style={{ margin: '8px 0 0 0', color: '#64748b', fontSize: '14px' }}>
                  Configure the AI service used for generating step descriptions
                </p>
              </div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button
                  className="secondary-btn"
                  onClick={testAiSettings}
                  disabled={aiSettingsTesting || aiSettingsSaving}
                  style={{
                    opacity: (aiSettingsTesting || aiSettingsSaving) ? 0.6 : 1,
                    padding: '10px 18px', borderRadius: '8px',
                    border: '1px solid #cbd5e1', background: 'white',
                    color: '#475569', fontWeight: 600, cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 8
                  }}
                  title="Send a tiny probe to the configured provider to confirm the credentials work"
                >
                  {aiSettingsTesting ? (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ animation: 'ai-spin 0.9s linear infinite' }}>
                        <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                      </svg>
                      Testing…
                    </>
                  ) : (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                        <polyline points="22 4 12 14.01 9 11.01"/>
                      </svg>
                      Test connection
                    </>
                  )}
                </button>
                <button
                  className="primary-btn"
                  onClick={saveAiSettings}
                  disabled={aiSettingsSaving}
                  style={{ opacity: aiSettingsSaving ? 0.6 : 1 }}
                >
                  {aiSettingsSaving ? 'Validating & saving…' : 'Save Settings'}
                </button>
              </div>
            </div>

            {/* Test result banner */}
            {aiTestResult && (
              <div style={{
                marginTop: 12, marginBottom: 16,
                padding: '12px 14px', borderRadius: 10,
                background: aiTestResult.success ? '#ecfdf5' : '#fef2f2',
                border: `1px solid ${aiTestResult.success ? '#a7f3d0' : '#fca5a5'}`,
                color: aiTestResult.success ? '#065f46' : '#991b1b',
                display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
                  {aiTestResult.success ? (
                    <><circle cx="12" cy="12" r="10"/><polyline points="9 12 12 15 16 10"/></>
                  ) : (
                    <><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></>
                  )}
                </svg>
                <div style={{ flex: 1 }}>
                  {aiTestResult.success ? (
                    <>
                      <strong>Connection verified.</strong> {aiTestResult.provider} responded in {aiTestResult.elapsedMs}ms.
                      {aiTestResult.sample && <span style={{ color: '#475569', display: 'block', marginTop: 4 }}>Sample: <code style={{ background: 'rgba(0,0,0,0.05)', padding: '1px 6px', borderRadius: 4 }}>{aiTestResult.sample}</code></span>}
                    </>
                  ) : (
                    <>
                      <strong>Connection failed.</strong> {aiTestResult.error || 'No response from provider.'}
                    </>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setAiTestResult(null)}
                  style={{ background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 16, opacity: 0.6 }}
                  aria-label="Dismiss"
                >×</button>
              </div>
            )}

            {aiSettingsLoading ? (
              <InlineSpinner label="Loading AI settings…" />
            ) : (
              <>
                {/* Service Selection */}
                <div style={{ marginBottom: '32px' }}>
                  <label style={{ display: 'block', marginBottom: '12px', fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
                    Active AI Service
                  </label>
                  {/* Brand SVGs sourced from Simple Icons (CC0 public domain
                      — https://simpleicons.org/). Same approach as the
                      Storage Provider picker: inlined so we don't pull
                      external assets, each rendered in the brand's official
                      primary color. */}
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    {[
                      {
                        id: 'default', name: 'InstaGuide (default)',
                        description: 'Use InstaGuide’s built-in AI. No key required — we cover the cost.',
                        iconBg: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                        // Lightning bolt — same mark as the top-nav brand.
                        icon: (
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
                            <path d="M13 2 L4 14 h6 l-2 8 9-12 h-6 l2-8z"/>
                          </svg>
                        )
                      },
                      {
                        id: 'claude', name: 'Claude (Anthropic)', description: 'Claude AI models from Anthropic. API key required.',
                        iconBg: '#FFFFFF',
                        // Simple Icons "anthropic" — the double-A Anthropic mark, brand color #191919.
                        icon: (
                          <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
                            <path fill="#191919" d="M17.3041 3.541h-3.6718l6.696 16.918H24Zm-10.6082 0L0 20.459h3.7442l1.3693-3.5527h7.0052l1.3693 3.5527h3.7442L10.5363 3.541Zm-.3712 10.2627 2.2914-5.9456 2.2914 5.9456Z"/>
                          </svg>
                        )
                      },
                      {
                        id: 'openai', name: 'OpenAI', description: 'GPT models direct from OpenAI. API key required.',
                        iconBg: '#FFFFFF',
                        // Simple Icons "openai" — the six-pointed knot mark, brand black #000000.
                        icon: (
                          <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
                            <path fill="#000000" d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z"/>
                          </svg>
                        )
                      },
                      {
                        id: 'azure_openai', name: 'Azure OpenAI', description: 'Any model your Azure OpenAI Service exposes. API key required.',
                        iconBg: '#FFFFFF',
                        // Simple Icons "microsoftazure" — official Azure logo in Azure blue #0078D4.
                        icon: (
                          <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
                            <path fill="#0078D4" d="M5.483 21.3H24l-9.42-16.27-3.088 8.227 5.49 6.738L5.483 21.3zM10.197 3L0 19.876l5.97-.7L13.5 7.582 10.197 3z"/>
                          </svg>
                        )
                      },
                      {
                        id: 'snowflake_cortex', name: 'Snowflake Cortex', description: 'AI models via Snowflake Cortex. Credentials required.',
                        iconBg: '#FFFFFF',
                        // Official Snowflake Inc. logo (provided by customer, #249EDC).
                        icon: (
                          <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
                            <path fillRule="evenodd" clipRule="evenodd" fill="#249EDC" d="M23.252 10.365l-2.843 1.636 2.843 1.631a1.47 1.47 0 01.697.903 1.492 1.492 0 01-.15 1.135c-.202.342-.53.591-.912.693a1.498 1.498 0 01-1.132-.15l-5.09-2.924a1.473 1.473 0 01-.68-.851 1.446 1.446 0 01-.068-.485 1.5 1.5 0 01.745-1.248l5.09-2.921a1.496 1.496 0 012.044.547 1.479 1.479 0 01-.544 2.034zm-2.692 7.927l-5.087-2.92a1.477 1.477 0 00-.867-.195 1.478 1.478 0 00-.982.468c-.257.276-.4.639-.403 1.017v5.847A1.49 1.49 0 0014.718 24c.828 0 1.497-.668 1.497-1.491v-3.27l2.849 1.636a1.493 1.493 0 002.044-.544 1.49 1.49 0 00-.548-2.04zm-5.87-5.719l-2.116 2.102a.42.42 0 01-.265.112h-.621a.427.427 0 01-.265-.112l-2.115-2.102a.42.42 0 01-.11-.262v-.62a.43.43 0 01.11-.265l2.114-2.102a.426.426 0 01.264-.11h.623a.422.422 0 01.265.11l2.116 2.102a.43.43 0 01.109.265v.62a.428.428 0 01-.11.262zM13 11.99a.442.442 0 00-.113-.266l-.612-.607a.431.431 0 00-.266-.11h-.024a.426.426 0 00-.264.11l-.612.607a.436.436 0 00-.107.266v.024c0 .085.047.202.107.262l.612.61c.061.06.179.11.264.11h.024a.434.434 0 00.266-.11l.612-.61a.429.429 0 00.112-.262v-.024zM3.436 5.704l5.089 2.924c.274.157.578.219.868.195.375-.026.726-.194.983-.47.256-.275.4-.64.403-1.017V1.489C10.78.667 10.11 0 9.284 0c-.829 0-1.498.667-1.498 1.49v3.27l-2.85-1.639a1.496 1.496 0 00-2.045.546 1.489 1.489 0 00.546 2.037zm11.17 3.119c.29.024.594-.038.866-.195l5.087-2.923a1.474 1.474 0 00.697-.903 1.496 1.496 0 00-.149-1.135 1.496 1.496 0 00-2.044-.545L16.215 4.76V1.489C16.215.667 15.546 0 14.718 0c-.83 0-1.497.667-1.497 1.49v5.845a1.491 1.491 0 001.385 1.487zm-5.213 6.354a1.479 1.479 0 00-.868.194l-5.089 2.92a1.476 1.476 0 00-.696.905 1.498 1.498 0 00.148 1.135 1.496 1.496 0 002.044.543l2.851-1.636v3.27c0 .825.67 1.491 1.498 1.491.826 0 1.496-.667 1.496-1.49v-5.847a1.5 1.5 0 00-.401-1.017 1.477 1.477 0 00-.982-.468zm-1.38-2.74c.05-.156.072-.32.068-.484a1.497 1.497 0 00-.751-1.248l-5.084-2.92a1.499 1.499 0 00-2.045.547 1.481 1.481 0 00.549 2.034l2.841 1.636L.75 13.633a1.47 1.47 0 00-.698.903 1.492 1.492 0 00.15 1.135c.202.343.53.592.912.693.382.102.789.048 1.132-.15l5.086-2.924c.345-.195.577-.505.684-.852z"/>
                          </svg>
                        )
                      },
                      {
                        id: 'gemini', name: 'Google Gemini', description: 'Gemini AI models from Google. API key required.',
                        iconBg: '#FFFFFF',
                        // Simple Icons "googlegemini" — the 4-point sparkle. Filled with the
                        // official Gemini blue→purple gradient so it matches Google's marketing.
                        icon: (
                          <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
                            <defs>
                              <linearGradient id="geminiGrad" x1="0" y1="0" x2="1" y2="1">
                                <stop offset="0%" stopColor="#4796E3"/>
                                <stop offset="55%" stopColor="#7C5DDC"/>
                                <stop offset="100%" stopColor="#C16AD0"/>
                              </linearGradient>
                            </defs>
                            <path fill="url(#geminiGrad)" d="M12 24A14.304 14.304 0 0 0 0 12 14.304 14.304 0 0 0 12 0a14.304 14.304 0 0 0 12 12 14.304 14.304 0 0 0-12 12"/>
                          </svg>
                        )
                      }
                    ].map(service => (
                      <div
                        key={service.id}
                        onClick={() => setAiSettings(prev => ({ ...prev, ai_service: service.id }))}
                        style={{
                          flex: '1',
                          minWidth: '200px',
                          padding: '16px',
                          borderRadius: '12px',
                          border: aiSettings.ai_service === service.id ? `2px solid ${theme.primary}` : '2px solid #e2e8f0',
                          background: aiSettings.ai_service === service.id ? `${theme.primary}10` : 'white',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 36, height: 36, borderRadius: 8,
                            background: service.iconBg,
                            border: '1px solid #e2e8f0',
                            flexShrink: 0
                          }}>
                            {service.icon}
                          </span>
                          <span style={{ fontWeight: '600', color: '#1e293b' }}>{service.name}</span>
                          {aiSettings.ai_service === service.id && (
                            <span style={{
                              marginLeft: 'auto',
                              background: theme.gradient,
                              color: 'white',
                              padding: '2px 8px',
                              borderRadius: '10px',
                              fontSize: '11px',
                              fontWeight: '600'
                            }}>Active</span>
                          )}
                        </div>
                        <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>{service.description}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Claude Configuration */}
                {aiSettings.ai_service === 'claude' && (
                  <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
                    <h4 style={{ margin: '0 0 16px 0', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path fill="#191919" d="M17.3041 3.541h-3.6718l6.696 16.918H24Zm-10.6082 0L0 20.459h3.7442l1.3693-3.5527h7.0052l1.3693 3.5527h3.7442L10.5363 3.541Zm-.3712 10.2627 2.2914-5.9456 2.2914 5.9456Z"/></svg>
                      Claude Configuration
                    </h4>
                    <div style={{ display: 'grid', gap: '16px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                          API Key
                        </label>
                        <input
                          type="password"
                          className="custom-dialog-input"
                          value={aiSettings.claude?.api_key || ''}
                          onChange={(e) => updateAiServiceSetting('claude', 'api_key', e.target.value)}
                          placeholder="sk-ant-api03-..."
                          style={{ background: 'white' }}
                        />
                        <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: '#64748b' }}>
                          Leave empty to use the system default API key
                        </p>
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                          Model
                        </label>
                        <select
                          value={aiSettings.claude?.model || systemAllowedModels.claude[0] || ''}
                          onChange={(e) => updateAiServiceSetting('claude', 'model', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            borderRadius: '10px',
                            border: '2px solid #e2e8f0',
                            fontSize: '14px',
                            background: 'white'
                          }}
                        >
                          {systemAllowedModels.claude.map(model => (
                            <option key={model} value={model}>{model}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {/* Azure OpenAI Configuration */}
                {aiSettings.ai_service === 'azure_openai' && (
                  <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
                    <h4 style={{ margin: '0 0 16px 0', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path fill="#0078D4" d="M5.483 21.3H24l-9.42-16.27-3.088 8.227 5.49 6.738L5.483 21.3zM10.197 3L0 19.876l5.97-.7L13.5 7.582 10.197 3z"/></svg>
                      Azure OpenAI Configuration
                    </h4>
                    <div style={{ display: 'grid', gap: '16px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                          Endpoint URL
                        </label>
                        <input
                          type="text"
                          className="custom-dialog-input"
                          value={aiSettings.azure_openai?.endpoint || ''}
                          onChange={(e) => updateAiServiceSetting('azure_openai', 'endpoint', e.target.value)}
                          placeholder="https://your-resource.openai.azure.com/"
                          style={{ background: 'white' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                          API Key
                        </label>
                        <input
                          type="password"
                          className="custom-dialog-input"
                          value={aiSettings.azure_openai?.api_key || ''}
                          onChange={(e) => updateAiServiceSetting('azure_openai', 'api_key', e.target.value)}
                          placeholder="Your Azure OpenAI API key"
                          style={{ background: 'white' }}
                        />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                            Deployment Name
                          </label>
                          <select
                            value={aiSettings.azure_openai?.deployment_name || systemAllowedModels.azure_openai[0] || ''}
                            onChange={(e) => updateAiServiceSetting('azure_openai', 'deployment_name', e.target.value)}
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              borderRadius: '10px',
                              border: '2px solid #e2e8f0',
                              fontSize: '14px',
                              background: 'white'
                            }}
                          >
                            {systemAllowedModels.azure_openai.map(model => (
                              <option key={model} value={model}>{model}</option>
                            ))}
                          </select>
                          <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: '#64748b' }}>
                            Select the active deployment to use
                          </p>
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                            API Version
                          </label>
                          <input
                            type="text"
                            className="custom-dialog-input"
                            value={aiSettings.azure_openai?.api_version || '2024-02-15-preview'}
                            onChange={(e) => updateAiServiceSetting('azure_openai', 'api_version', e.target.value)}
                            placeholder="2024-02-15-preview"
                            style={{ background: 'white' }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Snowflake Cortex Configuration */}
                {aiSettings.ai_service === 'snowflake_cortex' && (
                  <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
                    <h4 style={{ margin: '0 0 16px 0', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path fillRule="evenodd" clipRule="evenodd" fill="#249EDC" d="M23.252 10.365l-2.843 1.636 2.843 1.631a1.47 1.47 0 01.697.903 1.492 1.492 0 01-.15 1.135c-.202.342-.53.591-.912.693a1.498 1.498 0 01-1.132-.15l-5.09-2.924a1.473 1.473 0 01-.68-.851 1.446 1.446 0 01-.068-.485 1.5 1.5 0 01.745-1.248l5.09-2.921a1.496 1.496 0 012.044.547 1.479 1.479 0 01-.544 2.034zm-2.692 7.927l-5.087-2.92a1.477 1.477 0 00-.867-.195 1.478 1.478 0 00-.982.468c-.257.276-.4.639-.403 1.017v5.847A1.49 1.49 0 0014.718 24c.828 0 1.497-.668 1.497-1.491v-3.27l2.849 1.636a1.493 1.493 0 002.044-.544 1.49 1.49 0 00-.548-2.04zm-5.87-5.719l-2.116 2.102a.42.42 0 01-.265.112h-.621a.427.427 0 01-.265-.112l-2.115-2.102a.42.42 0 01-.11-.262v-.62a.43.43 0 01.11-.265l2.114-2.102a.426.426 0 01.264-.11h.623a.422.422 0 01.265.11l2.116 2.102a.43.43 0 01.109.265v.62a.428.428 0 01-.11.262zM13 11.99a.442.442 0 00-.113-.266l-.612-.607a.431.431 0 00-.266-.11h-.024a.426.426 0 00-.264.11l-.612.607a.436.436 0 00-.107.266v.024c0 .085.047.202.107.262l.612.61c.061.06.179.11.264.11h.024a.434.434 0 00.266-.11l.612-.61a.429.429 0 00.112-.262v-.024zM3.436 5.704l5.089 2.924c.274.157.578.219.868.195.375-.026.726-.194.983-.47.256-.275.4-.64.403-1.017V1.489C10.78.667 10.11 0 9.284 0c-.829 0-1.498.667-1.498 1.49v3.27l-2.85-1.639a1.496 1.496 0 00-2.045.546 1.489 1.489 0 00.546 2.037zm11.17 3.119c.29.024.594-.038.866-.195l5.087-2.923a1.474 1.474 0 00.697-.903 1.496 1.496 0 00-.149-1.135 1.496 1.496 0 00-2.044-.545L16.215 4.76V1.489C16.215.667 15.546 0 14.718 0c-.83 0-1.497.667-1.497 1.49v5.845a1.491 1.491 0 001.385 1.487zm-5.213 6.354a1.479 1.479 0 00-.868.194l-5.089 2.92a1.476 1.476 0 00-.696.905 1.498 1.498 0 00.148 1.135 1.496 1.496 0 002.044.543l2.851-1.636v3.27c0 .825.67 1.491 1.498 1.491.826 0 1.496-.667 1.496-1.49v-5.847a1.5 1.5 0 00-.401-1.017 1.477 1.477 0 00-.982-.468zm-1.38-2.74c.05-.156.072-.32.068-.484a1.497 1.497 0 00-.751-1.248l-5.084-2.92a1.499 1.499 0 00-2.045.547 1.481 1.481 0 00.549 2.034l2.841 1.636L.75 13.633a1.47 1.47 0 00-.698.903 1.492 1.492 0 00.15 1.135c.202.343.53.592.912.693.382.102.789.048 1.132-.15l5.086-2.924c.345-.195.577-.505.684-.852z"/></svg>
                      Snowflake Cortex Configuration
                    </h4>
                    <div style={{
                      background: '#e0f2fe',
                      border: '1px solid #7dd3fc',
                      borderRadius: '8px',
                      padding: '12px 16px',
                      marginBottom: '16px',
                      fontSize: '13px',
                      color: '#0369a1'
                    }}>
                      <strong>Note:</strong> Snowflake Cortex uses text-based AI models. Image analysis is not supported - step descriptions will be generated from action text only.
                    </div>
                    <div style={{ display: 'grid', gap: '16px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                            Account Identifier
                          </label>
                          <input
                            type="text"
                            className="custom-dialog-input"
                            value={aiSettings.snowflake_cortex?.account || ''}
                            onChange={(e) => updateAiServiceSetting('snowflake_cortex', 'account', e.target.value)}
                            placeholder="xy12345.us-east-1"
                            style={{ background: 'white' }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                            Username
                          </label>
                          <input
                            type="text"
                            className="custom-dialog-input"
                            value={aiSettings.snowflake_cortex?.username || ''}
                            onChange={(e) => updateAiServiceSetting('snowflake_cortex', 'username', e.target.value)}
                            placeholder="your_username"
                            style={{ background: 'white' }}
                          />
                        </div>
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                          Password
                        </label>
                        <input
                          type="password"
                          className="custom-dialog-input"
                          value={aiSettings.snowflake_cortex?.password || ''}
                          onChange={(e) => updateAiServiceSetting('snowflake_cortex', 'password', e.target.value)}
                          placeholder="Your Snowflake password"
                          style={{ background: 'white' }}
                        />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                            Warehouse
                          </label>
                          <input
                            type="text"
                            className="custom-dialog-input"
                            value={aiSettings.snowflake_cortex?.warehouse || ''}
                            onChange={(e) => updateAiServiceSetting('snowflake_cortex', 'warehouse', e.target.value)}
                            placeholder="COMPUTE_WH"
                            style={{ background: 'white' }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                            Database
                          </label>
                          <input
                            type="text"
                            className="custom-dialog-input"
                            value={aiSettings.snowflake_cortex?.database || ''}
                            onChange={(e) => updateAiServiceSetting('snowflake_cortex', 'database', e.target.value)}
                            placeholder="MY_DATABASE"
                            style={{ background: 'white' }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                            Schema
                          </label>
                          <input
                            type="text"
                            className="custom-dialog-input"
                            value={aiSettings.snowflake_cortex?.schema || ''}
                            onChange={(e) => updateAiServiceSetting('snowflake_cortex', 'schema', e.target.value)}
                            placeholder="PUBLIC"
                            style={{ background: 'white' }}
                          />
                        </div>
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                          Model
                        </label>
                        <select
                          value={aiSettings.snowflake_cortex?.model || systemAllowedModels.snowflake_cortex[0] || ''}
                          onChange={(e) => updateAiServiceSetting('snowflake_cortex', 'model', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            borderRadius: '10px',
                            border: '2px solid #e2e8f0',
                            fontSize: '14px',
                            background: 'white'
                          }}
                        >
                          {systemAllowedModels.snowflake_cortex.map(model => (
                            <option key={model} value={model}>{model}</option>
                          ))}
                        </select>
                        <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: '#64748b' }}>
                          Select the active model to use
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Gemini Configuration */}
                {aiSettings.ai_service === 'openai' && (
                  <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
                    <h4 style={{ margin: '0 0 16px 0', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                        <path fill="#000000" d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z"/>
                      </svg>
                      OpenAI Configuration
                    </h4>
                    <div style={{ display: 'grid', gap: '16px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                          API Key
                        </label>
                        <input
                          type="password"
                          className="custom-dialog-input"
                          value={aiSettings.openai?.api_key || ''}
                          onChange={(e) => updateAiServiceSetting('openai', 'api_key', e.target.value)}
                          placeholder="sk-..."
                          style={{ background: 'white' }}
                        />
                        <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: '#64748b' }}>
                          Create a key at platform.openai.com — starts with <code>sk-</code>.
                        </p>
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                          Model
                        </label>
                        <select
                          value={aiSettings.openai?.model || (systemAllowedModels.openai && systemAllowedModels.openai[0]) || 'gpt-5.4'}
                          onChange={(e) => updateAiServiceSetting('openai', 'model', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            borderRadius: '10px',
                            border: '2px solid #e2e8f0',
                            fontSize: '14px',
                            background: 'white'
                          }}
                        >
                          {(systemAllowedModels.openai || ['gpt-5.4', 'gpt-5.4-mini']).map(model => (
                            <option key={model} value={model}>{model}</option>
                          ))}
                        </select>
                        <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: '#64748b' }}>
                          Pick the OpenAI model to route step descriptions + AI features through.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {aiSettings.ai_service === 'gemini' && (
                  <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
                    <h4 style={{ margin: '0 0 16px 0', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                        <defs>
                          <linearGradient id="geminiGradH" x1="0" y1="0" x2="1" y2="1">
                            <stop offset="0%" stopColor="#4796E3"/>
                            <stop offset="55%" stopColor="#7C5DDC"/>
                            <stop offset="100%" stopColor="#C16AD0"/>
                          </linearGradient>
                        </defs>
                        <path fill="url(#geminiGradH)" d="M12 24A14.304 14.304 0 0 0 0 12 14.304 14.304 0 0 0 12 0a14.304 14.304 0 0 0 12 12 14.304 14.304 0 0 0-12 12"/>
                      </svg>
                      Google Gemini Configuration
                    </h4>
                    <div style={{ display: 'grid', gap: '16px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                          API Key
                        </label>
                        <input
                          type="password"
                          className="custom-dialog-input"
                          value={aiSettings.gemini?.api_key || ''}
                          onChange={(e) => updateAiServiceSetting('gemini', 'api_key', e.target.value)}
                          placeholder="Your Google AI API key"
                          style={{ background: 'white' }}
                        />
                        <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: '#64748b' }}>
                          Get your API key from Google AI Studio
                        </p>
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                          Model
                        </label>
                        <select
                          value={aiSettings.gemini?.model || systemAllowedModels.gemini[0] || ''}
                          onChange={(e) => updateAiServiceSetting('gemini', 'model', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            borderRadius: '10px',
                            border: '2px solid #e2e8f0',
                            fontSize: '14px',
                            background: 'white'
                          }}
                        >
                          {systemAllowedModels.gemini.map(model => (
                            <option key={model} value={model}>{model}</option>
                          ))}
                        </select>
                        <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: '#64748b' }}>
                          Select the active model to use
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Info Section */}
                <div style={{
                  background: '#eff6ff',
                  border: '1px solid #bfdbfe',
                  borderRadius: '8px',
                  padding: '16px',
                  display: 'flex',
                  gap: '12px'
                }}>
                  <span style={{ fontSize: '20px' }}>💡</span>
                  <div>
                    <p style={{ margin: 0, color: '#1e40af', fontSize: '14px', fontWeight: '600' }}>
                      About AI Providers
                    </p>
                    <p style={{ margin: '8px 0 0 0', color: '#3b82f6', fontSize: '13px', lineHeight: '1.5' }}>
                      Leave the default (InstaGuide) if you want us to handle the AI — no key setup, no bills on your side. Picking any other provider means bring-your-own credentials: you must supply the API key and your account will be billed by that provider for every step description, meeting import, and Insta chat call your team makes. We will not fall back to our internal key.
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Storage Provider Tab — BYO cloud storage (Enterprise plan only).
            Tab is hidden for personal accounts in the nav row; the tab body
            also handles the plan-gated case where the backend returned 403. */}
        {activeTab === 'storage-provider' && (
          <div className="panel">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <h3 style={{ margin: 0, color: '#1e293b' }}>Storage Provider <span style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8', marginLeft: 6 }}>· Optional</span></h3>
                <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '13px' }}>
                  By default, screenshots and audio live in our database. Enterprise orgs can optionally point new captures at their own AWS&nbsp;S3, Azure&nbsp;Blob, or Google&nbsp;Cloud Storage bucket.
                </p>
              </div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button
                  className="secondary-btn"
                  onClick={testStorageSettings}
                  disabled={storageTesting || storageSaving || storageSettings._planGated || storageSettings.service === 'default'}
                  style={{
                    opacity: (storageTesting || storageSaving || storageSettings._planGated || storageSettings.service === 'default') ? 0.6 : 1,
                    padding: '10px 18px', borderRadius: '8px',
                    border: '1px solid #cbd5e1', background: 'white',
                    color: '#475569', fontWeight: 600, cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 8
                  }}
                  title="Upload a tiny probe file with the configured credentials"
                >
                  {storageTesting ? (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ animation: 'ai-spin 0.9s linear infinite' }}>
                        <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                      </svg>
                      Testing…
                    </>
                  ) : (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                        <polyline points="22 4 12 14.01 9 11.01"/>
                      </svg>
                      Test connection
                    </>
                  )}
                </button>
                <button
                  className="primary-btn"
                  onClick={saveStorageSettings}
                  disabled={storageSaving || storageSettings._planGated}
                  style={{ opacity: (storageSaving || storageSettings._planGated) ? 0.6 : 1 }}
                >
                  {storageSaving ? 'Saving…' : 'Save Settings'}
                </button>
              </div>
            </div>

            {storageLoading ? (
              <InlineSpinner label="Loading storage settings…" />
            ) : storageSettings._planGated ? (
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: 20 }}>
                <h4 style={{ margin: '0 0 6px', color: '#92400e' }}>Enterprise plan required</h4>
                <p style={{ margin: '0 0 12px', color: '#78350f', fontSize: 14, lineHeight: 1.55 }}>
                  Bring-your-own cloud storage is part of the Enterprise plan. Contact us if you'd like to enable it for your organization.
                </p>
                <a href="mailto:hello@instaguide.ai" style={{ color: '#b45309', fontWeight: 600 }}>hello@instaguide.ai</a>
              </div>
            ) : (
              <>
                {/* Provider picker.
                    Brand SVGs sourced from Simple Icons (CC0, public domain
                    — https://simpleicons.org/). Each path is the official
                    company mark; we render in the brand's published primary
                    color. Inlined so we don't pull external assets and the
                    page works offline / behind a CSP. */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
                  {[
                    {
                      id: 'default', label: 'InstaGuide (default)', sub: 'Stored in our database.',
                      iconBg: theme.gradient,
                      icon: (
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="white" aria-hidden="true">
                          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                        </svg>
                      )
                    },
                    {
                      // AWS S3 — provided by customer (Streamline). Has its own green
                      // gradient background so it fills the badge edge-to-edge; iconBg
                      // is set to the dark-green stop color to blend any uncovered pixels.
                      id: 'aws_s3', label: 'AWS S3', sub: 'Amazon Simple Storage Service.',
                      iconBg: '#1b660f',
                      iconFill: true, // signal to render at full badge size, no padding
                      icon: (
                        <svg width="36" height="36" viewBox="0 0 96 96" fill="none" aria-hidden="true">
                          <defs>
                            <linearGradient id="awsS3Grad" x1="1" x2="9401" y1="9401" y2="1" gradientUnits="userSpaceOnUse">
                              <stop stopColor="#1b660f"/>
                              <stop offset="1" stopColor="#6cae3e"/>
                            </linearGradient>
                          </defs>
                          <path fill="url(#awsS3Grad)" d="M95 1H1v94h94V1Z"/>
                          <path fill="#ffffff" d="m72.4817 51.3989.4512-3.1772c4.1607 2.4922 4.2147 3.5215 4.2136 3.5497-.0071.0058-.7168.598-4.6648-.3725Zm-2.283-.6345c-7.191-2.1761-17.2055-6.7704-21.2581-8.6856 0-.0165.0047-.0317.0047-.0482 0-1.5569-1.2667-2.8235-2.8247-2.8235-1.5557 0-2.8224 1.2666-2.8224 2.8235 0 1.5569 1.2667 2.8235 2.8224 2.8235.6838 0 1.3042-.2549 1.7942-.6603 4.7682 2.2572 14.7051 6.7844 21.949 8.9229l-2.8646 20.2171c-.0083.0552-.0118.1104-.0118.1657 0 1.7801-7.8807 5.0501-20.7575 5.0501-13.0132 0-20.9773-3.27-20.9773-5.0501 0-.0541-.0035-.107-.0106-.1598l-5.9854-43.7183c5.1805 3.5662 16.3231 5.4532 26.9803 5.4532 10.6408 0 21.7645-1.88 26.9557-5.4355l-2.9939 21.1253ZM18.6244 25.0613c.0846-1.5463 8.97-7.6117 27.6125-7.6117 18.6402 0 27.5267 6.0642 27.6125 7.6117v.5275c-1.0222 3.4675-12.5372 7.1358-27.6125 7.1358-15.1011 0-26.622-3.6801-27.6125-7.1522v-.5111Zm57.575.0258c0-4.0714-11.6724-9.9875-29.9625-9.9875-18.29 0-29.9625 5.9161-29.9625 9.9875l.1105.886 6.5189 47.6157c.1562 5.3251 14.3573 7.3108 23.3261 7.3108 11.1296 0 22.9536-2.5591 23.1063-7.3073l2.8153-19.854c1.5663.3748 2.8553.5664 3.8905.5664 1.39 0 2.33-.3396 2.8999-1.0188.4676-.5569.6462-1.2314.5123-1.9493-.3044-1.6262-2.2349-3.3793-6.1664-5.6224l2.7918-19.6953.1198-.9318Z"/>
                        </svg>
                      )
                    },
                    {
                      // Simple Icons "microsoftazure" — official Microsoft Azure logo.
                      id: 'azure_blob', label: 'Azure Blob', sub: 'Azure Storage account.',
                      iconBg: '#FFFFFF',
                      icon: (
                        <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
                          <path fill="#0078D4" d="M5.483 21.3H24l-9.42-16.27-3.088 8.227 5.49 6.738L5.483 21.3zM10.197 3L0 19.876l5.97-.7L13.5 7.582 10.197 3z"/>
                        </svg>
                      )
                    },
                    {
                      // Google Cloud — provided by customer (Streamline) with the
                      // official 4-color palette (red/blue/green/yellow).
                      id: 'gcs', label: 'Google Cloud Storage', sub: 'GCS bucket.',
                      iconBg: '#FFFFFF',
                      icon: (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path fill="#ea4335" strokeWidth="0.25" d="m15.878525 7.770025 2.04275 -2.04275 0.13615 -0.860125C14.334975 1.482325 8.417675 1.866125 5.06205 5.667975c-0.9321 1.055925 -1.6236 2.3724 -1.99235 3.7318l0.731625 -0.103075 4.0854 -0.6737 0.315425 -0.322575c1.817275 -1.99585 4.89 -2.26435 6.988275 -0.5662l0.6881 0.0358Z"/>
                          <path fill="#4285f4" strokeWidth="0.25" d="M20.83135 9.3397c-0.46955 -1.729075 -1.4335 -3.283475 -2.773825 -4.4725L15.19055 7.734175c1.2106 0.9892 1.900275 2.479425 1.870725 4.04245v0.508925c1.40915 0 2.55165 1.1424 2.55165 2.551575 0 1.40925 -1.1425 2.523025 -2.55165 2.523025H11.95085l-0.50175 0.544725v3.0605l0.50175 0.480175h5.110425c3.665425 0.02855 6.660025 -2.891025 6.688575 -6.556475 0.01725 -2.22175 -1.078175 -4.304625 -2.9185 -5.549375Z"/>
                          <path fill="#34a853" strokeWidth="0.25" d="M6.847475 21.4456h5.10325v-4.0855h-5.10325c-0.363625 -0.000075 -0.71575 -0.0782 -1.0464 -0.2293l-0.724 0.22215 -2.057075 2.04275 -0.179175 0.69525c1.1536 0.871075 2.561125 1.3609 4.00665 1.35465Z"/>
                          <path fill="#fbbc05" strokeWidth="0.25" d="M6.84745 8.192925C3.181925 8.21475 0.22827275 11.204025 0.25012 14.869475c0.01221 2.046875 0.96818 3.973675 2.59068 5.221475l2.96025 -2.960175c-1.284325 -0.58025 -1.855025 -2.091675 -1.274775 -3.3759 0.58015 -1.28425 2.091675 -1.85495 3.375825 -1.274775 0.565925 0.25565 1.019125 0.708925 1.274775 1.274775l2.96025 -2.960175c-1.259525 -1.64655 -3.216625 -2.609225 -5.289675 -2.601775Z"/>
                        </svg>
                      )
                    }
                  ].map(opt => {
                    const isActive = storageSettings.service === opt.id;
                    return (
                      <button
                        key={opt.id}
                        onClick={() => setStorageSettings(prev => ({ ...prev, service: opt.id }))}
                        style={{
                          textAlign: 'left',
                          padding: 14,
                          borderRadius: 12,
                          border: isActive ? `2px solid ${theme.primary}` : '2px solid #e2e8f0',
                          background: isActive ? `${theme.primary}10` : 'white',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          display: 'flex', flexDirection: 'column', gap: 8
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 36, height: 36, borderRadius: 8,
                            background: opt.iconBg,
                            border: opt.iconBg === '#FFFFFF' ? '1px solid #e2e8f0' : 'none',
                            flexShrink: 0,
                            overflow: 'hidden' // clip the AWS green icon corners to the badge radius
                          }}>
                            {opt.icon}
                          </span>
                          <span style={{ fontWeight: 700, color: '#1e293b' }}>{opt.label}</span>
                          {isActive && (
                            <span style={{
                              marginLeft: 'auto',
                              background: theme.gradient,
                              color: 'white',
                              padding: '2px 8px',
                              borderRadius: 10,
                              fontSize: 11,
                              fontWeight: 600
                            }}>Active</span>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>{opt.sub}</div>
                      </button>
                    );
                  })}
                </div>

                {/* Provider-specific credential forms.
                    Uses the same `custom-dialog-input` class + grid pattern
                    as the AI Provider tab so we get the focus ring + spacing
                    automatically — `.manual-form` was defined inside the
                    guide-detail style block and doesn't load on Admin. */}
                {storageSettings.service !== 'default' && (
                  <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
                    {storageSettings.service === 'aws_s3' && (
                      <>
                        <h4 style={{ margin: '0 0 16px 0', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <svg width="20" height="20" viewBox="0 0 96 96" fill="none" aria-hidden="true" style={{ borderRadius: 4 }}>
                            <defs>
                              <linearGradient id="awsS3GradH" x1="1" x2="9401" y1="9401" y2="1" gradientUnits="userSpaceOnUse">
                                <stop stopColor="#1b660f"/>
                                <stop offset="1" stopColor="#6cae3e"/>
                              </linearGradient>
                            </defs>
                            <path fill="url(#awsS3GradH)" d="M95 1H1v94h94V1Z"/>
                            <path fill="#ffffff" d="m72.4817 51.3989.4512-3.1772c4.1607 2.4922 4.2147 3.5215 4.2136 3.5497-.0071.0058-.7168.598-4.6648-.3725Zm-2.283-.6345c-7.191-2.1761-17.2055-6.7704-21.2581-8.6856 0-.0165.0047-.0317.0047-.0482 0-1.5569-1.2667-2.8235-2.8247-2.8235-1.5557 0-2.8224 1.2666-2.8224 2.8235 0 1.5569 1.2667 2.8235 2.8224 2.8235.6838 0 1.3042-.2549 1.7942-.6603 4.7682 2.2572 14.7051 6.7844 21.949 8.9229l-2.8646 20.2171c-.0083.0552-.0118.1104-.0118.1657 0 1.7801-7.8807 5.0501-20.7575 5.0501-13.0132 0-20.9773-3.27-20.9773-5.0501 0-.0541-.0035-.107-.0106-.1598l-5.9854-43.7183c5.1805 3.5662 16.3231 5.4532 26.9803 5.4532 10.6408 0 21.7645-1.88 26.9557-5.4355l-2.9939 21.1253ZM18.6244 25.0613c.0846-1.5463 8.97-7.6117 27.6125-7.6117 18.6402 0 27.5267 6.0642 27.6125 7.6117v.5275c-1.0222 3.4675-12.5372 7.1358-27.6125 7.1358-15.1011 0-26.622-3.6801-27.6125-7.1522v-.5111Zm57.575.0258c0-4.0714-11.6724-9.9875-29.9625-9.9875-18.29 0-29.9625 5.9161-29.9625 9.9875l.1105.886 6.5189 47.6157c.1562 5.3251 14.3573 7.3108 23.3261 7.3108 11.1296 0 22.9536-2.5591 23.1063-7.3073l2.8153-19.854c1.5663.3748 2.8553.5664 3.8905.5664 1.39 0 2.33-.3396 2.8999-1.0188.4676-.5569.6462-1.2314.5123-1.9493-.3044-1.6262-2.2349-3.3793-6.1664-5.6224l2.7918-19.6953.1198-.9318Z"/>
                          </svg>
                          AWS S3 Configuration
                        </h4>
                        <div style={{ display: 'grid', gap: '16px' }}>
                          {[
                            ['region', 'Region', 'e.g. us-east-1', false],
                            ['bucket', 'Bucket name', 'my-instaguide-bucket', false],
                            ['access_key_id', 'Access key ID', 'AKIA…', false],
                            ['secret_access_key', 'Secret access key', '', true],
                            ['prefix', 'Key prefix (optional)', 'optional/folder/path', false]
                          ].map(([key, label, ph, isSecret]) => (
                            <div key={key}>
                              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>{label}</label>
                              <input
                                type={isSecret ? 'password' : 'text'}
                                className="custom-dialog-input"
                                value={storageSettings.aws_s3?.[key] || ''}
                                onChange={(e) => updateStorageField('aws_s3', key, e.target.value)}
                                placeholder={isSecret && storageSettings.aws_s3?.[key]?.startsWith?.('***') ? 'Saved — leave blank to keep' : ph}
                                style={{ background: 'white' }}
                              />
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    {storageSettings.service === 'azure_blob' && (
                      <>
                        <h4 style={{ margin: '0 0 16px 0', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path fill="#0078D4" d="M5.483 21.3H24l-9.42-16.27-3.088 8.227 5.49 6.738L5.483 21.3zM10.197 3L0 19.876l5.97-.7L13.5 7.582 10.197 3z"/></svg>
                          Azure Blob Configuration
                        </h4>
                        <div style={{ display: 'grid', gap: '16px' }}>
                          {[
                            ['account_name', 'Storage account name', 'mystorageaccount', false],
                            ['account_key', 'Account key', '', true],
                            ['container', 'Container', 'instaguide-assets', false],
                            ['prefix', 'Blob prefix (optional)', 'optional/folder/path', false]
                          ].map(([key, label, ph, isSecret]) => (
                            <div key={key}>
                              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>{label}</label>
                              <input
                                type={isSecret ? 'password' : 'text'}
                                className="custom-dialog-input"
                                value={storageSettings.azure_blob?.[key] || ''}
                                onChange={(e) => updateStorageField('azure_blob', key, e.target.value)}
                                placeholder={isSecret && storageSettings.azure_blob?.[key]?.startsWith?.('***') ? 'Saved — leave blank to keep' : ph}
                                style={{ background: 'white' }}
                              />
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    {storageSettings.service === 'gcs' && (
                      <>
                        <h4 style={{ margin: '0 0 16px 0', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path fill="#ea4335" strokeWidth="0.25" d="m15.878525 7.770025 2.04275 -2.04275 0.13615 -0.860125C14.334975 1.482325 8.417675 1.866125 5.06205 5.667975c-0.9321 1.055925 -1.6236 2.3724 -1.99235 3.7318l0.731625 -0.103075 4.0854 -0.6737 0.315425 -0.322575c1.817275 -1.99585 4.89 -2.26435 6.988275 -0.5662l0.6881 0.0358Z"/>
                            <path fill="#4285f4" strokeWidth="0.25" d="M20.83135 9.3397c-0.46955 -1.729075 -1.4335 -3.283475 -2.773825 -4.4725L15.19055 7.734175c1.2106 0.9892 1.900275 2.479425 1.870725 4.04245v0.508925c1.40915 0 2.55165 1.1424 2.55165 2.551575 0 1.40925 -1.1425 2.523025 -2.55165 2.523025H11.95085l-0.50175 0.544725v3.0605l0.50175 0.480175h5.110425c3.665425 0.02855 6.660025 -2.891025 6.688575 -6.556475 0.01725 -2.22175 -1.078175 -4.304625 -2.9185 -5.549375Z"/>
                            <path fill="#34a853" strokeWidth="0.25" d="M6.847475 21.4456h5.10325v-4.0855h-5.10325c-0.363625 -0.000075 -0.71575 -0.0782 -1.0464 -0.2293l-0.724 0.22215 -2.057075 2.04275 -0.179175 0.69525c1.1536 0.871075 2.561125 1.3609 4.00665 1.35465Z"/>
                            <path fill="#fbbc05" strokeWidth="0.25" d="M6.84745 8.192925C3.181925 8.21475 0.22827275 11.204025 0.25012 14.869475c0.01221 2.046875 0.96818 3.973675 2.59068 5.221475l2.96025 -2.960175c-1.284325 -0.58025 -1.855025 -2.091675 -1.274775 -3.3759 0.58015 -1.28425 2.091675 -1.85495 3.375825 -1.274775 0.565925 0.25565 1.019125 0.708925 1.274775 1.274775l2.96025 -2.960175c-1.259525 -1.64655 -3.216625 -2.609225 -5.289675 -2.601775Z"/>
                          </svg>
                          Google Cloud Storage Configuration
                        </h4>
                        <div style={{ display: 'grid', gap: '16px' }}>
                          <div>
                            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>Project ID (optional — falls back to key)</label>
                            <input className="custom-dialog-input" type="text"
                              value={storageSettings.gcs?.project_id || ''}
                              onChange={(e) => updateStorageField('gcs', 'project_id', e.target.value)}
                              placeholder="my-gcp-project" style={{ background: 'white' }} />
                          </div>
                          <div>
                            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>Bucket name</label>
                            <input className="custom-dialog-input" type="text"
                              value={storageSettings.gcs?.bucket || ''}
                              onChange={(e) => updateStorageField('gcs', 'bucket', e.target.value)}
                              placeholder="my-instaguide-bucket" style={{ background: 'white' }} />
                          </div>
                          <div>
                            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>Service account key (JSON)</label>
                            <textarea
                              rows={8}
                              value={storageSettings.gcs?.service_account_key || ''}
                              onChange={(e) => updateStorageField('gcs', 'service_account_key', e.target.value)}
                              placeholder={storageSettings.gcs?.service_account_key?.startsWith?.('***') ? 'Saved — leave blank to keep' : '{ "type": "service_account", "project_id": ... }'}
                              style={{
                                width: '100%', boxSizing: 'border-box',
                                padding: '10px 14px', borderRadius: 8,
                                border: '1px solid #e2e8f0', background: 'white',
                                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                fontSize: 12, color: '#0f172a',
                                resize: 'vertical', minHeight: 140, lineHeight: 1.5,
                                outline: 'none'
                              }}
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>Object prefix (optional)</label>
                            <input className="custom-dialog-input" type="text"
                              value={storageSettings.gcs?.prefix || ''}
                              onChange={(e) => updateStorageField('gcs', 'prefix', e.target.value)}
                              placeholder="optional/folder/path" style={{ background: 'white' }} />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {storageTestResult && (
                  <div style={{
                    marginTop: 16, padding: 12, borderRadius: 8,
                    background: storageTestResult.success ? '#ecfdf5' : '#fef2f2',
                    border: `1px solid ${storageTestResult.success ? '#a7f3d0' : '#fecaca'}`,
                    color: storageTestResult.success ? '#065f46' : '#991b1b',
                    fontSize: 13
                  }}>
                    {storageTestResult.success
                      ? `✓ Connection succeeded in ${storageTestResult.elapsedMs}ms.`
                      : `✗ ${storageTestResult.error || 'Connection failed.'}`}
                  </div>
                )}

                <div style={{ marginTop: 24, padding: 14, background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, fontSize: 13, color: '#0c4a6e', lineHeight: 1.55 }}>
                  <strong>How this works:</strong> when set to a non-default provider, every new screenshot and audio note recorded by your team will upload to your bucket and only a URL is stored in our database. Existing guides are not migrated. Your bucket must allow public-read on these objects for HTML/PDF exports to display them.
                </div>
              </>
            )}
          </div>
        )}

        {/* Redaction Tab (Sprint J) — auto-detect + redact sensitive
            data in captured screenshots. Defaults are safe (enabled,
            permanent, blur, all categories on) so the protection
            applies before any admin even opens this tab. */}
        {activeTab === 'redaction' && (
          <div className="panel">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
              <div>
                <h3 style={{ margin: 0, color: '#1e293b' }}>Screenshot Redaction</h3>
                <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: 13 }}>
                  Automatically detect sensitive data in every captured screenshot and redact it before saving. Protects emails, credit cards, API keys, and more — by default, on every plan.
                </p>
              </div>
              <button
                className="primary-btn"
                onClick={saveRedactionSettings}
                disabled={redactionSettingsSaving || redactionSettingsLoading}
                style={{ minWidth: 120 }}
              >
                {redactionSettingsSaving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>

            {redactionSettingsLoading ? (
              <div style={{ padding: 32, textAlign: 'center', color: '#64748b' }}>Loading…</div>
            ) : (
              <>
                {/* Master toggle */}
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: 16, background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0',
                  marginBottom: 20,
                }}>
                  <div>
                    <div style={{ fontWeight: 600, color: '#1e293b' }}>Auto-detect sensitive data</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                      Runs on every captured step. Turning this off removes the protection — we strongly recommend leaving it on.
                    </div>
                  </div>
                  <label style={{ position: 'relative', display: 'inline-block', width: 44, height: 24 }}>
                    <input
                      type="checkbox"
                      checked={redactionSettings.enabled !== false}
                      onChange={(e) => setRedactionSettings((p) => ({ ...p, enabled: e.target.checked }))}
                      style={{ opacity: 0, width: 0, height: 0 }}
                    />
                    <span style={{
                      position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                      background: redactionSettings.enabled !== false ? '#6366f1' : '#cbd5e1',
                      borderRadius: 24, transition: '.2s',
                    }}>
                      <span style={{
                        position: 'absolute', height: 18, width: 18, left: redactionSettings.enabled !== false ? 22 : 3, top: 3,
                        background: '#fff', borderRadius: '50%', transition: '.2s',
                      }} />
                    </span>
                  </label>
                </div>

                {/* Mode + style. The detection-tier choice
                    (regex vs AI vision) is intentionally NOT exposed
                    here — it's our implementation detail. The org
                    setting `useAITier` stays in the saved JSON for
                    future internal toggling but doesn't appear in the
                    UI. Customers think of redaction as "on or off",
                    not "which tier". */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16, marginBottom: 24 }}>
                  <div style={{ padding: 16, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
                      Redaction mode
                    </div>
                    {[
                      { v: 'permanent',        label: 'Permanent',        sub: 'Pixels destroyed. Same view for everyone. Safest for public guides.' },
                      { v: 'permission_based', label: 'Permission-based', sub: 'Original is kept and revealed to users whose role grants "View original (redacted)".' },
                    ].map((opt) => {
                      const isComing = false;
                      const isSelected = redactionSettings.mode === opt.v;
                      return (
                        <label
                          key={opt.v}
                          style={{
                            display: 'flex', alignItems: 'flex-start', gap: 10,
                            padding: '10px 12px', borderRadius: 8, cursor: isComing ? 'not-allowed' : 'pointer',
                            background: isSelected ? '#f0f4ff' : 'transparent',
                            border: isSelected ? '1px solid #c7d2fe' : '1px solid transparent',
                            marginBottom: 6, opacity: isComing ? 0.55 : 1,
                          }}
                        >
                          <input
                            type="radio"
                            name="redactionMode"
                            value={opt.v}
                            checked={isSelected}
                            disabled={isComing}
                            onChange={() => setRedactionSettings((p) => ({ ...p, mode: opt.v }))}
                            style={{ marginTop: 3, accentColor: '#6366f1' }}
                          />
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>{opt.label}</div>
                            <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{opt.sub}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>

                  <div style={{ padding: 16, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
                      Redaction style
                    </div>
                    {[
                      { v: 'blur',  label: 'Gaussian blur', sub: 'Preserves layout context — viewers can see field shapes.' },
                      { v: 'black', label: 'Black rectangle', sub: 'Maximum hiding. Leaves no recoverable detail.' },
                    ].map((opt) => (
                      <label
                        key={opt.v}
                        style={{
                          display: 'flex', alignItems: 'flex-start', gap: 10,
                          padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                          background: redactionSettings.style === opt.v ? '#f0f4ff' : 'transparent',
                          border: redactionSettings.style === opt.v ? '1px solid #c7d2fe' : '1px solid transparent',
                          marginBottom: 6,
                        }}
                      >
                        <input
                          type="radio"
                          name="redactionStyle"
                          value={opt.v}
                          checked={redactionSettings.style === opt.v}
                          onChange={() => setRedactionSettings((p) => ({ ...p, style: opt.v }))}
                          style={{ marginTop: 3, accentColor: '#6366f1' }}
                        />
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>{opt.label}</div>
                          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{opt.sub}</div>
                        </div>
                      </label>
                    ))}
                  </div>

                </div>

                {/* Categories grid. Each group renders a header row
                    with a master toggle (turn the whole group on/off)
                    and individual per-pattern checkboxes underneath
                    for fine-grained control — e.g. enable Email but
                    keep Phone off within Contact info. Matches the
                    extension's customize popup so admins see the same
                    granularity on both surfaces. */}
                <div style={{ padding: 16, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Categories to detect
                      </div>
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                        Toggle the group header to flip every detector in that category at once, or fine-tune which individual fields are redacted.
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
                    {REDACTION_CATEGORY_GROUPS.map((group) => {
                      const isOn = isGroupEnabled(group.id);
                      return (
                        <div
                          key={group.id}
                          style={{
                            padding: '12px 14px', borderRadius: 10,
                            background: isOn ? '#f0f4ff' : '#f8fafc',
                            border: isOn ? '1px solid #c7d2fe' : '1px solid #e2e8f0',
                            transition: 'background 0.15s, border-color 0.15s',
                          }}
                        >
                          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={isOn}
                              onChange={(e) => toggleRedactionGroup(group.id, e.target.checked)}
                              style={{ marginTop: 2, accentColor: '#6366f1' }}
                            />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{group.label}</div>
                              <div style={{ fontSize: 11, color: '#64748b', marginTop: 3, lineHeight: 1.45 }}>
                                {group.description}
                              </div>
                            </div>
                          </label>
                          {/* Per-pattern or per-subgroup checkboxes.
                              When a group declares `subgroups`, render
                              one toggle per subgroup that bundles
                              several patternIds (e.g. "API keys"
                              covers Stripe + AWS + GitHub + …). Otherwise
                              fall back to one toggle per individual
                              patternId. Greyed out when the group master
                              is OFF; flipping any child back on restores
                              the group toggle since isGroupEnabled treats
                              "any pattern on" as the group being on. */}
                          <div style={{
                            marginTop: 10, paddingTop: 10, paddingLeft: 26,
                            borderTop: '1px dashed #e2e8f0',
                            display: 'flex', flexDirection: 'column', gap: 6,
                            opacity: isOn ? 1 : 0.6,
                          }}>
                            {Array.isArray(group.subgroups) && group.subgroups.length > 0
                              ? group.subgroups.map((sub) => {
                                  // Subgroup is "on" when ANY of its
                                  // constituent patterns is enabled. Same
                                  // semantics as the top-level group
                                  // toggle for visual consistency.
                                  const subOn = sub.patterns.some((id) => isPatternEnabled(id));
                                  return (
                                    <label
                                      key={sub.id}
                                      title={sub.description || ''}
                                      style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12, color: '#334155' }}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={subOn}
                                        onChange={(e) => {
                                          // Toggle every pattern in the subgroup at once.
                                          sub.patterns.forEach((id) => toggleRedactionPattern(id, e.target.checked));
                                        }}
                                        style={{ accentColor: '#6366f1' }}
                                      />
                                      <span>{sub.label}</span>
                                    </label>
                                  );
                                })
                              : group.patterns.map((patternId) => (
                                  <label
                                    key={patternId}
                                    style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12, color: '#334155' }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isPatternEnabled(patternId)}
                                      onChange={(e) => toggleRedactionPattern(patternId, e.target.checked)}
                                      style={{ accentColor: '#6366f1' }}
                                    />
                                    <span>{PATTERN_LABELS[patternId] || patternId}</span>
                                  </label>
                                ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div style={{
                  background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 8,
                  padding: 12, fontSize: 12, color: '#3730a3', lineHeight: 1.5,
                }}>
                  <strong>How redaction works:</strong> InstaGuide scans every captured screenshot for the categories you've enabled and redacts matching regions before the step is saved. Each step keeps a private list of what was redacted (categories only — never the raw values) so admins can audit coverage without exposing the underlying data.
                </div>
                {redactionSettings.mode === 'permission_based' && (
                  <div style={{
                    marginTop: 12,
                    background: '#fefce8', border: '1px solid #fde68a', borderRadius: 8,
                    padding: 12, fontSize: 12, color: '#854d0e', lineHeight: 1.5,
                  }}>
                    <strong>Who can reveal originals:</strong> Org owners and admins by default. To extend reveal access to a project role, go to the <em>Roles</em> tab and grant the <em>View original (redacted)</em> permission. Anyone without it sees only the redacted view.
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Role Modal */}
      {showRoleModal && (
        <div className="custom-dialog-overlay" onClick={() => setShowRoleModal(false)}>
          <div className="custom-dialog" style={{ maxWidth: '650px' }} onClick={(e) => e.stopPropagation()}>
            <div className="custom-dialog-header">
              <div className="custom-dialog-icon default">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  {!editingRole && <line x1="12" y1="9" x2="12" y2="13"/>}
                  {!editingRole && <line x1="10" y1="11" x2="14" y2="11"/>}
                </svg>
              </div>
              <h3 className="custom-dialog-title">{editingRole ? 'Edit Role' : 'New Role'}</h3>
            </div>
            <div className="custom-dialog-body">
              <p className="custom-dialog-message" style={{ marginBottom: '16px' }}>
                {editingRole ? 'Update the role settings below.' : 'Create a custom role with specific permissions.'}
              </p>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                  Role Name
                </label>
                <input
                  type="text"
                  className="custom-dialog-input"
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e.target.value)}
                  placeholder="e.g., Content Manager"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') setShowRoleModal(false);
                  }}
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                  Description
                </label>
                <input
                  type="text"
                  className="custom-dialog-input"
                  value={newRoleDescription}
                  onChange={(e) => setNewRoleDescription(e.target.value)}
                  placeholder="Brief description of this role"
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') setShowRoleModal(false);
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '12px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                  Permissions
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  {PERMISSION_OPTIONS.map(perm => (
                    <label
                      key={perm.id}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '10px',
                        cursor: 'pointer',
                        padding: '10px 12px',
                        background: newRolePermissions.includes(perm.id) ? '#f0f4ff' : '#f8fafc',
                        borderRadius: '8px',
                        border: newRolePermissions.includes(perm.id) ? '1px solid #c7d2fe' : '1px solid transparent',
                        transition: 'all 0.15s'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={newRolePermissions.includes(perm.id)}
                        onChange={() => togglePermission(perm.id)}
                        style={{ marginTop: '2px', accentColor: theme.primary }}
                      />
                      <div>
                        <div style={{ fontWeight: 500, fontSize: '13px', color: '#1e293b' }}>{perm.label}</div>
                        <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>{perm.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="custom-dialog-footer">
              <button
                className="custom-dialog-btn custom-dialog-btn-cancel"
                onClick={() => setShowRoleModal(false)}
              >
                Cancel
              </button>
              <button
                className="custom-dialog-btn custom-dialog-btn-confirm"
                onClick={saveRole}
                disabled={!newRoleName.trim()}
              >
                {editingRole ? 'Save Changes' : 'Create Role'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Project Modal */}
      {showProjectModal && (
        <div className="custom-dialog-overlay" onClick={() => setShowProjectModal(false)}>
          <div className="custom-dialog" style={{ maxWidth: '380px' }} onClick={(e) => e.stopPropagation()}>
            <div className="custom-dialog-header">
              <div className="custom-dialog-icon prompt">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                  {!editingProject && <line x1="12" y1="11" x2="12" y2="17"/>}
                  {!editingProject && <line x1="9" y1="14" x2="15" y2="14"/>}
                </svg>
              </div>
              <h3 className="custom-dialog-title">{editingProject ? 'Edit Project' : 'New Project'}</h3>
            </div>
            <div className="custom-dialog-body">
              <p className="custom-dialog-message" style={{ marginBottom: '16px' }}>
                {editingProject ? 'Update your project details below.' : 'Create a new project to organize your guides.'}
              </p>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                  Project Name
                </label>
                <input
                  type="text"
                  className="custom-dialog-input"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  placeholder="e.g., Marketing Guides"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newProjectName.trim()) {
                      saveProject();
                    } else if (e.key === 'Escape') {
                      setShowProjectModal(false);
                    }
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                  Description (optional)
                </label>
                <textarea
                  className="custom-dialog-input"
                  value={newProjectDescription}
                  onChange={(e) => setNewProjectDescription(e.target.value)}
                  placeholder="Brief description of this project"
                  rows={3}
                  style={{ resize: 'vertical' }}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setShowProjectModal(false);
                    }
                  }}
                />
              </div>
            </div>
            <div className="custom-dialog-footer">
              <button
                className="custom-dialog-btn custom-dialog-btn-cancel"
                onClick={() => setShowProjectModal(false)}
              >
                Cancel
              </button>
              <button
                className="custom-dialog-btn custom-dialog-btn-confirm"
                onClick={saveProject}
                disabled={!newProjectName.trim()}
              >
                {editingProject ? 'Save Changes' : 'Create Project'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Reject favicon sources that the prod page can't usefully load:
// - http:// (would trigger a Mixed Content warning on https://app.instaguide.ai)
// - localhost / 127.0.0.1 / private IPs (won't be reachable from a public client)
// Used to filter both stored faviconUrl and step-derived domains so leftover
// dev-env data doesn't pollute the prod UI.
const isUnsafeFaviconSource = (str) => {
  if (!str) return true;
  const s = String(str).toLowerCase();
  // Block insecure HTTP when the app itself is HTTPS
  if (typeof window !== 'undefined' && window.location.protocol === 'https:' && s.startsWith('http://')) return true;
  // Block local-network hostnames in any case
  return /(?:^|\/\/|@)(?:localhost|127\.0\.0\.1|0\.0\.0\.0|\[::1\])(?:[:/]|$)/.test(s);
};

// Extract a hostname from any URL string. Returns null if the input isn't
// parseable as a URL (e.g. it's already just a bare domain).
const hostnameFromUrl = (str) => {
  try { return new URL(str).hostname; } catch { return null; }
};

// Collapse a hostname to its apex (eTLD+1). Google's S2 favicon service
// returns a generic globe icon for custom subdomains like
// `java-connect-8891.my.salesforce.com` even though `salesforce.com` has a
// perfectly good favicon. Querying the apex gives the recognizable brand
// icon for almost every SaaS subdomain pattern (Salesforce orgs, Zendesk
// instances, AWS apps, GitHub Pages, etc.). Pure heuristic — full PSL
// support isn't worth the bundle weight for this UI sugar.
const apexDomain = (hostname) => {
  if (!hostname) return hostname;
  const parts = hostname.split('.').filter(Boolean);
  if (parts.length <= 2) return hostname;
  const last2 = parts.slice(-2).join('.');
  // Common multi-label public TLDs — for these, the apex is the last 3 labels
  // (e.g. example.co.uk, example.com.au). Not exhaustive, but covers most
  // English-speaking SaaS audiences. Falls back to last-2 for everything else.
  const multiLabelTlds = new Set([
    'co.uk', 'co.jp', 'co.in', 'co.kr', 'co.nz', 'co.za',
    'com.au', 'com.br', 'com.mx', 'com.sg', 'com.cn', 'com.tr',
    'org.uk', 'ac.uk', 'gov.uk', 'net.au'
  ]);
  if (multiLabelTlds.has(last2)) return parts.slice(-3).join('.');
  return last2;
};

// Session-level cache of apex domains whose Google S2 favicon has 404'd
// at least once this session. Captured guides occasionally carry CDN /
// internal-tool hostnames (gstatic.com, wsimg.com, cdn.something) that S2
// has no favicon for — every render of every guide tile that referenced
// one would re-trigger the same 404, spamming the devtools console.
// Once we've seen a domain fail, we return null from getFaviconUrl so the
// fallback letter/globe renders without a network request. Persisted to
// sessionStorage so navigating between pages (or a soft reload) doesn't
// replay the requests. The FIRST 404 per domain per browser session is
// unavoidable — the browser logs network failures regardless of any JS
// handler — but everything after that is silent.
const FAVICON_FAILED_KEY = 'instaguide-favicon-failed-v1';
const _faviconFailedSet = (() => {
  try {
    if (typeof sessionStorage === 'undefined') return new Set();
    const raw = sessionStorage.getItem(FAVICON_FAILED_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch { return new Set(); }
})();
const _persistFaviconFailed = () => {
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(FAVICON_FAILED_KEY, JSON.stringify([..._faviconFailedSet]));
    }
  } catch { /* sessionStorage blocked / full — keep in-memory only */ }
};
const markFaviconFailed = (domain) => {
  if (!domain) return;
  const apex = apexDomain(domain);
  if (apex && !_faviconFailedSet.has(apex)) {
    _faviconFailedSet.add(apex);
    _persistFaviconFailed();
  }
};
const isFaviconFailed = (domain) => {
  if (!domain) return false;
  return _faviconFailedSet.has(apexDomain(domain));
};

// Drop-in onError handler for <img> tags whose src is a Google S2 favicon
// URL. Pulls the `domain` query param off the failed URL and records it
// in the failed-favicon Set so future renders skip the network request.
// Safe to wire into any onError chain — silently no-ops for non-S2 URLs
// (e.g. desktop guides' base64 data: icons).
const handleFaviconError = (e) => {
  try {
    const src = e && e.target && e.target.src;
    if (!src || !src.includes('s2/favicons')) return;
    const u = new URL(src);
    const d = u.searchParams.get('domain');
    if (d) markFaviconFailed(d);
  } catch { /* ignore — best-effort */ }
};

// Helper to get favicon URL with fallbacks. We deliberately route every
// favicon through Google's S2 proxy (`/s2/favicons?domain=...`) instead of
// loading the origin's favicon directly, because many sites now serve their
// favicon with `Cross-Origin-Resource-Policy: same-origin` (claude.ai, github,
// stripe, etc.). A direct load triggers `ERR_BLOCKED_BY_RESPONSE.NotSameOrigin`
// in the console and the icon doesn't render — the proxy bypasses CORP and
// always returns a usable PNG.
const getFaviconWithFallback = (guide) => {
  // Desktop-captured guides store the real Windows app icon directly
  // as a base64 PNG data URL on guide.faviconUrl — extracted at
  // capture time via ExtractAssociatedIcon. There's no hostname to
  // proxy through Google S2 (the "domain" for desktop is an app slug
  // like "excel" / "winword" — not a real domain), so we have to
  // return the data URL as-is. Web-captured guides keep going through
  // the S2 proxy below for cross-origin safety + brand-icon coverage.
  if (guide.source === 'desktop'
      && typeof guide.faviconUrl === 'string'
      && guide.faviconUrl.startsWith('data:image/')) {
    return guide.faviconUrl;
  }

  // Resolve a clean hostname from the most reliable source available.
  let domain = null;

  // 1. Stored faviconUrl — extract its hostname (we don't load the URL itself)
  if (guide.faviconUrl && !isUnsafeFaviconSource(guide.faviconUrl)) {
    domain = hostnameFromUrl(guide.faviconUrl);
  }

  // 2. guide.domain (already a hostname)
  if (!domain && guide.domain && !isUnsafeFaviconSource(guide.domain)) {
    domain = guide.domain;
  }

  // 3. Walk steps' clickData URLs as a last resort
  if (!domain && guide.steps && guide.steps.length > 0) {
    for (const step of guide.steps) {
      const url = step.clickData?.capturedOnUrl || step.clickData?.url;
      if (!url) continue;
      const h = hostnameFromUrl(url);
      if (h && !isUnsafeFaviconSource(h)) { domain = h; break; }
    }
  }

  if (!domain) return null;
  const apex = apexDomain(domain);
  // Skip the network request entirely for domains we've already seen
  // S2 return 404 for this session — caller renders the fallback letter/globe.
  if (_faviconFailedSet.has(apex)) return null;
  // Use the apex so subdomain-heavy SaaS URLs (Salesforce my.salesforce.com,
  // Zendesk *.zendesk.com, AWS *.amazonaws.com, etc.) all render their
  // brand favicon instead of S2's default globe.
  return `https://www.google.com/s2/favicons?domain=${apex}&sz=64`;
};

// SVG Icons for professional look
const Icons = {
  home: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
  check: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
  folder: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>,
  file: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  fileText: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>,
  users: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  star: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
  starFilled: <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
  settings: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
  user: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  logout: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>,
  chevronDown: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>,
  chevronLeft: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>,
  chevronRight: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>,
  zap: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
  clock: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  download: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>,
  trash: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
  arrowLeft: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>,
  inbox: <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/></svg>,
  folderOpen: <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/><line x1="9" y1="14" x2="15" y2="14"/></svg>,
  share: <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>,
  shareSmall: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>,
  starLarge: <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
  grid: <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>,
  building: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"/><line x1="9" y1="6" x2="9" y2="6"/><line x1="15" y1="6" x2="15" y2="6"/><line x1="9" y1="10" x2="9" y2="10"/><line x1="15" y1="10" x2="15" y2="10"/><line x1="9" y1="14" x2="9" y2="14"/><line x1="15" y1="14" x2="15" y2="14"/><line x1="9" y1="18" x2="15" y2="18"/></svg>,
  barChart: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></svg>,
  alertCircle: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
  sparkles: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.912 5.813a2 2 0 0 0 1.275 1.275L21 12l-5.813 1.912a2 2 0 0 0-1.275 1.275L12 21l-1.912-5.813a2 2 0 0 0-1.275-1.275L3 12l5.813-1.912a2 2 0 0 0 1.275-1.275L12 3z"/><path d="M5 3v4"/><path d="M3 5h4"/><path d="M19 17v4"/><path d="M17 19h4"/></svg>,
  search: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
  bookOpen: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>,
  edit: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  link: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>,
  keyboard: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" ry="2"/><line x1="6" y1="8" x2="6" y2="8"/><line x1="10" y1="8" x2="10" y2="8"/><line x1="14" y1="8" x2="14" y2="8"/><line x1="18" y1="8" x2="18" y2="8"/><line x1="6" y1="12" x2="6" y2="12"/><line x1="10" y1="12" x2="10" y2="12"/><line x1="14" y1="12" x2="14" y2="12"/><line x1="18" y1="12" x2="18" y2="12"/><line x1="8" y1="16" x2="16" y2="16"/></svg>,
  verticalArrows: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="7 3 12 8 17 3"/><polyline points="17 21 12 16 7 21"/><line x1="12" y1="8" x2="12" y2="16"/></svg>,
  arrowDown: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/></svg>,
  arrowUp: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>,
  package: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="16.5" y1="9.4" x2="7.5" y2="4.21"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>,
  globe: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
  messageCircle: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>,
  usersGroup: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  clipboard: <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/></svg>,
  handshake: <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 17a1 1 0 0 1-1-1v-1a5 5 0 0 1 10 0v1a1 1 0 0 1-1 1"/><path d="M5 17a1 1 0 0 1-1-1v-1a5 5 0 0 1 10 0v1a1 1 0 0 1-1 1"/><path d="M12 14v4"/><path d="M8 11l4 3 4-3"/></svg>,
  desktop: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>,
  chrome: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="4"/><line x1="21.17" y1="8" x2="12" y2="8"/><line x1="3.95" y1="6.06" x2="8.54" y2="14"/><line x1="10.88" y1="21.94" x2="15.46" y2="14"/></svg>,
};

// Icon component helper for sizing
const Icon = ({ icon, size = 20, className = '' }) => {
  return React.cloneElement(icon, { width: size, height: size, className });
};

// Main Layout with Left Sidebar Navigation
function MainLayout({ user, currentPage, setCurrentPage, sidebarCollapsed, setSidebarCollapsed, onLogout, planFeatures = {}, children }) {
  const isOrgAdmin = user?.organizationId && ['owner', 'admin'].includes(user?.orgRole);
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  // Sprint K Phase 2 — "Import recording" sidebar item only renders when
  // the user's effective plan includes meetingMode. Free / Starter never
  // see it; Pro+ can upload recordings and watch them turn into guides.
  const canImportMeetings = !!planFeatures?.meetingMode;
  const navItems = [
    { id: 'home', icon: Icons.home, label: 'Home' },
    { id: 'projects', icon: Icons.folder, label: 'Projects' },
    { id: 'myguides', icon: Icons.file, label: 'My Guides' },
    { id: 'shared', icon: Icons.users, label: 'Shared With Me' },
    { id: 'favorites', icon: Icons.star, label: 'Favorites' },
    ...(canImportMeetings ? [{
      id: 'imports',
      // Inline SVG — upload-cloud glyph, distinct from the chrome/desktop
      // source badges so "this is uploaded video, not a live capture" reads.
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M16 16l-4-4-4 4" />
          <path d="M12 12v9" />
          <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
          <path d="M16 16l-4-4-4 4" />
        </svg>
      ),
      label: 'Import recording',
    }] : []),
  ];

  return (
    <div className="main-layout">
      <style>{`
        .main-layout {
          display: flex;
          min-height: 100vh;
          background: #f8fafc;
        }
        .sidebar {
          width: 260px;
          background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
          display: flex;
          flex-direction: column;
          transition: width 0.3s ease;
          position: fixed;
          left: 0;
          top: 0;
          bottom: 0;
          z-index: 100;
        }
        .sidebar.collapsed {
          width: 64px;
        }
        .sidebar-header {
          padding: 20px 16px;
          display: flex;
          align-items: center;
          gap: 12px;
          border-bottom: 1px solid rgba(255,255,255,0.08);
        }
        .sidebar-logo {
          width: 34px;
          height: 34px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .sidebar-logo img {
          display: block;
        }
        .sidebar-title {
          font-size: 18px;
          font-weight: 700;
          color: white;
          white-space: nowrap;
          overflow: hidden;
          letter-spacing: -0.5px;
        }
        .sidebar.collapsed .sidebar-title {
          display: none;
        }
        .sidebar-toggle {
          position: absolute;
          right: -12px;
          top: 24px;
          width: 24px;
          height: 24px;
          background: #1e293b;
          border: 1px solid #334155;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #94a3b8;
          transition: all 0.2s;
          z-index: 10;
        }
        .sidebar-toggle:hover {
          background: #334155;
          color: white;
        }
        .sidebar-nav {
          flex: 1;
          padding: 16px 12px;
          overflow-y: auto;
        }
        .nav-section {
          margin-bottom: 24px;
        }
        .nav-section-title {
          font-size: 11px;
          text-transform: uppercase;
          color: #64748b;
          padding: 0 12px 8px;
          letter-spacing: 0.5px;
          font-weight: 600;
        }
        .sidebar.collapsed .nav-section-title {
          display: none;
        }
        .nav-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 12px;
          color: #94a3b8;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.15s ease;
          margin-bottom: 2px;
        }
        .nav-item:hover {
          background: rgba(255,255,255,0.05);
          color: #e2e8f0;
        }
        .nav-item.active {
          background: rgba(255,255,255,0.1);
          color: white;
        }
        .nav-item.active .nav-item-icon {
          color: white;
        }
        .nav-item-icon {
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          color: #64748b;
          transition: color 0.15s ease;
        }
        .nav-item:hover .nav-item-icon {
          color: #94a3b8;
        }
        .nav-item-label {
          font-size: 14px;
          font-weight: 500;
          white-space: nowrap;
          overflow: hidden;
        }
        .sidebar.collapsed .nav-item-label {
          display: none;
        }
        .sidebar.collapsed .nav-item {
          justify-content: center;
          padding: 10px;
        }
        .sidebar-footer {
          padding: 12px;
          border-top: 1px solid rgba(255,255,255,0.08);
          position: relative;
        }
        .user-profile-btn {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 12px;
          background: transparent;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          width: 100%;
          text-align: left;
          transition: all 0.15s ease;
        }
        .user-profile-btn:hover {
          background: rgba(255,255,255,0.05);
        }
        .user-avatar {
          width: 36px;
          height: 36px;
          background: ${theme.gradient};
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: White;
          font-weight: 600;
          font-size: 14px;
          flex-shrink: 0;
        }
        .user-info {
          flex: 1;
          overflow: hidden;
        }
        .user-name {
          font-size: 14px;
          font-weight: 600;
          color: #e2e8f0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .user-email {
          font-size: 12px;
          color: #64748b;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .user-chevron {
          color: #64748b;
          flex-shrink: 0;
        }
        .sidebar.collapsed .user-info,
        .sidebar.collapsed .user-chevron {
          display: none;
        }
        .sidebar.collapsed .user-profile-btn {
          justify-content: center;
          padding: 8px;
        }
        .profile-menu {
          position: absolute;
          bottom: 100%;
          left: 12px;
          right: 12px;
          background: #1e293b;
          border: 1px solid #334155;
          border-radius: 8px;
          padding: 4px;
          margin-bottom: 8px;
          box-shadow: 0 10px 40px rgba(0,0,0,0.3);
        }
        .sidebar.collapsed .profile-menu {
          left: 64px;
          right: auto;
          bottom: 12px;
          width: 200px;
        }
        .profile-menu-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 12px;
          color: #94a3b8;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.15s ease;
          border: none;
          background: none;
          width: 100%;
          text-align: left;
        }
        .profile-menu-item:hover {
          background: rgba(255,255,255,0.05);
          color: #e2e8f0;
        }
        .profile-menu-item.danger:hover {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        }
        .profile-menu-item svg {
          width: 18px;
          height: 18px;
        }
        .profile-menu-divider {
          height: 1px;
          background: #334155;
          margin: 4px 0;
        }
        .main-content {
          flex: 1;
          margin-left: 260px;
          transition: margin-left 0.3s ease;
          min-height: 100vh;
        }
        .sidebar.collapsed + .main-content,
        .main-layout:has(.sidebar.collapsed) .main-content {
          margin-left: 64px;
        }
        .main-header {
          background: white;
          padding: 20px 32px;
          border-bottom: 1px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          position: sticky;
          top: 0;
          z-index: 50;
        }
        .main-header-title {
          font-size: 24px;
          font-weight: 700;
          color: #0f172a;
          margin: 0;
        }
        .main-header-actions {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .header-btn {
          padding: 8px 16px;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }
        .header-btn-primary {
          background: ${theme.gradient};
          color: white;
        }
        .header-btn-primary:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
        }
        .page-content {
          padding: 24px 32px;
        }
        /* Custom Dialog Styles for MainLayout */
        .custom-dialog-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          animation: dialogFadeIn 0.2s ease;
        }
        @keyframes dialogFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .custom-dialog {
          background: white;
          border-radius: 16px;
          width: 400px;
          max-width: 90vw;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          animation: dialogSlideIn 0.2s ease;
          overflow: hidden;
        }
        @keyframes dialogSlideIn {
          from { transform: scale(0.95) translateY(-10px); opacity: 0; }
          to { transform: scale(1) translateY(0); opacity: 1; }
        }
        .custom-dialog-header {
          padding: 20px 24px 16px;
          display: flex;
          align-items: center;
          gap: 12px;
        }
        /* Softer confirm-dialog icon treatment (see note in the
           earlier block for the full rationale). Tint background +
           color-carrying inline SVG icon; no big text glyph. */
        .custom-dialog-icon {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .custom-dialog-icon.default {
          background: #eef2ff;
          color: #4f46e5;
        }
        .custom-dialog-icon.danger {
          background: #fef2f2;
          color: #dc2626;
        }
        .custom-dialog-icon.prompt {
          background: #ecfdf5;
          color: #059669;
        }
        .custom-dialog-icon.success {
          background: #ecfdf5;
          color: #059669;
        }
        .custom-dialog-title {
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
        }
        .custom-dialog-body {
          padding: 0 24px 20px;
        }
        .custom-dialog-message {
          font-size: 14px;
          color: #64748b;
          line-height: 1.5;
          margin: 0;
        }
        .custom-dialog-input {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          font-size: 15px;
          transition: all 0.2s;
          box-sizing: border-box;
          font-family: inherit;
        }
        .custom-dialog-input:focus {
          outline: none;
          border-color: ${theme.primary};
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }
        .custom-dialog-footer {
          padding: 16px 24px;
          background: #f8fafc;
          display: flex;
          justify-content: flex-end;
          gap: 12px;
        }
        .custom-dialog-btn {
          padding: 10px 20px;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          border: none;
        }
        .custom-dialog-btn-cancel {
          background: white;
          color: #64748b;
          border: 1px solid #e2e8f0;
        }
        .custom-dialog-btn-cancel:hover {
          background: #f1f5f9;
          color: #475569;
        }
        .custom-dialog-btn-confirm {
          background: ${theme.gradient};
          color: white;
        }
        .custom-dialog-btn-confirm:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }
        .custom-dialog-btn-danger {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
        }
        .custom-dialog-btn-danger:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
        }
      `}</style>

      <nav className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          <span className="sidebar-logo"><img src="/favicon.png" alt="InstaGuide" width="34" height="34" style={{ borderRadius: 9 }} /></span>
          <span className="sidebar-title">InstaGuide</span>
        </div>
        <button className="sidebar-toggle" onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
          {sidebarCollapsed ? Icons.chevronRight : Icons.chevronLeft}
        </button>

        <div className="sidebar-nav">
          <div className="nav-section">
            <div className="nav-section-title">Menu</div>
            {navItems.map(item => (
              <div
                key={item.id}
                className={`nav-item ${currentPage === item.id ? 'active' : ''}`}
                onClick={() => setCurrentPage(item.id)}
                title={sidebarCollapsed ? item.label : ''}
              >
                <span className="nav-item-icon">{item.icon}</span>
                <span className="nav-item-label">{item.label}</span>
              </div>
            ))}
          </div>

          {(isOrgAdmin || !user?.organizationId) && (
            <div className="nav-section">
              <div className="nav-section-title">{user?.accountType === 'organization' ? 'Organization' : 'Account'}</div>
              <div
                className={`nav-item ${currentPage === 'admin' ? 'active' : ''}`}
                onClick={() => setCurrentPage('admin')}
                title={sidebarCollapsed ? 'Administration' : ''}
              >
                <span className="nav-item-icon">{Icons.settings}</span>
                <span className="nav-item-label">Administration</span>
              </div>
              <div
                className={`nav-item ${currentPage === 'pricing' ? 'active' : ''}`}
                onClick={() => setCurrentPage('pricing')}
                title={sidebarCollapsed ? 'Plans & Billing' : ''}
              >
                <span className="nav-item-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
                </span>
                <span className="nav-item-label">Plans &amp; Billing</span>
              </div>
            </div>
          )}

          <div className="nav-section">
            <div className="nav-section-title">Resources</div>
            <div
              className="nav-item"
              onClick={() => window.open('https://docs.instaguide.ai', '_blank')}
              title={sidebarCollapsed ? 'Documentation' : ''}
            >
              <span className="nav-item-icon">{Icons.bookOpen}</span>
              <span className="nav-item-label">Documentation</span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginLeft: 'auto', opacity: 0.4 }}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
            </div>
          </div>
        </div>

        <div className="sidebar-footer">
          {showProfileMenu && (
            <div className="profile-menu">
              <button
                className="profile-menu-item"
                onClick={() => { setCurrentPage('profile'); setShowProfileMenu(false); }}
              >
                {Icons.user}
                <span>Profile</span>
              </button>
              <div className="profile-menu-divider" />
              <button
                className="profile-menu-item danger"
                onClick={() => { onLogout(); setShowProfileMenu(false); }}
              >
                {Icons.logout}
                <span>Sign out</span>
              </button>
            </div>
          )}
          <button
            className="user-profile-btn"
            onClick={() => setShowProfileMenu(!showProfileMenu)}
            title={sidebarCollapsed ? user?.name : ''}
          >
            <div className="user-avatar">
              {user?.name?.charAt(0)?.toUpperCase() || '?'}
            </div>
            <div className="user-info">
              <div className="user-name">{user?.name}</div>
              <div className="user-email">{user?.email}</div>
            </div>
            <span className="user-chevron">{Icons.chevronDown}</span>
          </button>
        </div>
      </nav>

      <main className="main-content" onClick={() => showProfileMenu && setShowProfileMenu(false)}>
        {children}
      </main>
    </div>
  );
}

// Home Page - Recent Guides
function HomePage({ guides, sharedGuides, recentGuideIds, onSelectGuide, onDeleteGuide, deletingGuideId, onExport, exportingGuide, favoriteGuideIds, onToggleFavorite, projects, onQuickShare, user, loadingGuides }) {
  const [showRecordingTooltip, setShowRecordingTooltip] = useState(false);

  // Dedupe by id when merging — a user's own guide can also appear in
  // sharedGuides when they share it with their org and themselves
  // (or when the backend includes them as a share target on their own
  // guide). Without this, the recent-guides row showed the SAME guide
  // twice — once from `guides`, once from `sharedGuides`.
  const allGuidesMap = new Map();
  [...guides, ...sharedGuides].forEach(g => {
    if (g && g.id && !allGuidesMap.has(g.id)) allGuidesMap.set(g.id, g);
  });
  const allGuides = Array.from(allGuidesMap.values());
  // Also dedupe the recent-guide-IDs list itself defensively. If the
  // user's `recentGuideIds` row in the DB ever picked up duplicates
  // from a race or a buggy write, we still want to render each guide
  // at most once.
  const seenRecentIds = new Set();
  const recentGuides = recentGuideIds
    .filter(id => {
      if (seenRecentIds.has(id)) return false;
      seenRecentIds.add(id);
      return true;
    })
    .map(id => allGuidesMap.get(id))
    .filter(Boolean)
    .slice(0, 6);

  const projectMap = projects.reduce((acc, p) => ({ ...acc, [p.id]: p }), {});

  const handleStartRecording = () => {
    setShowRecordingTooltip(true);
    // Auto-hide after 5 seconds
    setTimeout(() => setShowRecordingTooltip(false), 5000);
  };

  return (
    <>
      <div className="main-header">
        <h1 className="main-header-title">Home</h1>
      </div>
      <div className="page-content">
        <style>{`
          .section-title {
            font-size: 18px;
            font-weight: 600;
            color: #1e293b;
            margin-bottom: 16px;
            display: flex;
            align-items: center;
            gap: 8px;
          }
          .guides-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
            gap: 20px;
          }
          .guide-card {
            background: white;
            border-radius: 12px;
            overflow: hidden;
            border: 1px solid #e2e8f0;
            transition: all 0.2s ease;
            cursor: pointer;
          }
          .guide-card:hover {
            transform: translateY(-2px);
            border-color: ${theme.primary}30;
            box-shadow: 0 8px 24px ${theme.primary}10;
          }
          .guide-preview {
            background: linear-gradient(145deg, ${theme.primary}15 0%, ${theme.secondary}0a 60%, #f1f5f9 100%);
            padding: 20px 24px;
            display: flex;
            align-items: center;
            gap: 16px;
            border-bottom: 1px solid #e2e8f0;
          }
          .guide-preview-favicon {
            width: 48px;
            height: 48px;
            background: white;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          }
          .guide-preview-favicon img {
            width: 32px;
            height: 32px;
            object-fit: contain;
          }
          .guide-preview-icon {
            font-size: 32px;
          }
          .guide-preview-icon-svg {
            width: 48px;
            height: 48px;
            background: white;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 2px 8px rgba(0,0,0,0.08);
            color: #94a3b8;
          }
          .guide-preview-icon-svg svg {
            width: 24px;
            height: 24px;
          }
          .guide-domain {
            font-size: 12px;
            color: #64748b;
            background: white;
            padding: 4px 10px;
            border-radius: 20px;
            max-width: 140px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .guide-preview-favicon,
          .guide-preview-icon-svg {
            flex-shrink: 0;
          }
          .guide-source-badge {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 4px 8px;
            border-radius: 12px;
            font-size: 11px;
            background: rgba(255,255,255,0.9);
            color: #64748b;
            flex-shrink: 0;
            margin-left: auto;
          }
          .guide-source-badge.desktop {
            color: #7c3aed;
            background: rgba(124, 58, 237, 0.1);
          }
          .guide-source-badge.extension {
            color: #6366f1;
            background: rgba(99, 102, 241, 0.1);
          }
          /* Sprint K — Meeting Mode badge. Amber chip mirrors the extension
             floater's in-progress meeting badge so the same visual carries
             from recording → library → detail page. Compact variant is
             icon-only to fit narrow list-view columns. */
          .guide-meeting-badge {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            padding: 3px 8px 3px 6px;
            margin-left: 6px;
            border-radius: 999px;
            background: rgba(245, 158, 11, 0.14);
            color: #b45309;
            font-size: 10px;
            font-weight: 600;
            line-height: 1;
            letter-spacing: 0.3px;
            text-transform: uppercase;
            border: 1px solid rgba(245, 158, 11, 0.32);
            flex-shrink: 0;
            white-space: nowrap;
          }
          .guide-meeting-badge.compact {
            gap: 0;
            padding: 4px;
            margin-left: 4px;
            border-radius: 6px;
          }
          .guide-meeting-badge svg {
            flex-shrink: 0;
          }
          .guide-info {
            padding: 16px 20px;
          }
          .guide-name {
            font-size: 16px;
            font-weight: 600;
            color: #1e293b;
            margin: 0 0 6px 0;
          }
          .guide-steps {
            font-size: 14px;
            color: #64748b;
            margin: 0;
            display: flex;
            align-items: center;
            gap: 8px;
          }
          .guide-modified {
            font-size: 12px;
            color: #94a3b8;
            white-space: nowrap;
            margin-left: auto;
          }
          .guide-meta-row {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 8px;
            margin-top: 8px;
          }
          .guide-project-badge {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            font-size: 12px;
            color: ${theme.primary};
            background: #f1f5f9;
            padding: 4px 10px;
            border-radius: 12px;
            font-weight: 500;
          }
          .guide-project-badge-icon {
            display: inline-flex;
            align-items: center;
          }
          .guide-owner-name {
            font-size: 11px;
            color: #94a3b8;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 120px;
          }
          .guide-actions {
            padding: 0 20px 16px;
            display: flex;
            gap: 8px;
          }
          .guide-action-btn {
            padding: 8px 12px;
            background: #f1f5f9;
            border: none;
            border-radius: 8px;
            font-size: 13px;
            cursor: pointer;
            transition: all 0.2s;
          }
          .guide-action-btn:hover {
            background: #e2e8f0;
          }
          .guide-action-btn:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
          .guide-action-btn:disabled:hover {
            background: #f1f5f9;
          }
          .guide-action-btn.favorite {
            color: #f59e0b;
          }
          .guide-action-btn.favorite.active {
            background: #fef3c7;
          }
          .empty-state {
            text-align: center;
            padding: 60px 20px;
            color: #64748b;
          }
          .empty-state-icon {
            margin-bottom: 16px;
            color: #cbd5e1;
            display: flex;
            justify-content: center;
          }
          .empty-state-icon svg {
            width: 48px;
            height: 48px;
          }
          .empty-state h3 {
            font-size: 18px;
            color: #1e293b;
            margin: 0 0 8px 0;
          }
          .empty-state p {
            margin: 0;
          }
          .section-title-icon {
            display: inline-flex;
            align-items: center;
            vertical-align: middle;
            margin-right: 8px;
            color: #64748b;
          }
          .section-title-icon svg {
            width: 20px;
            height: 20px;
          }
          .guide-action-icon {
            display: inline-flex;
            align-items: center;
            gap: 4px;
          }
          .guide-action-btn svg {
            width: 16px;
            height: 16px;
            vertical-align: middle;
          }
          .start-recording-banner {
            background: linear-gradient(135deg, ${theme.primary}06 0%, ${theme.secondary}06 100%);
            border: 1px solid ${theme.primary}20;
            border-radius: 16px;
            padding: 28px 36px;
            margin-bottom: 32px;
            display: flex;
            align-items: center;
            gap: 24px;
            transition: all 0.25s ease;
            justify-content: flex-start;
            cursor: pointer;
            position: relative;
            overflow: hidden;
          }
          .start-recording-banner::before {
            content: '';
            position: absolute;
            top: 0;
            right: 0;
            width: 200px;
            height: 200px;
            background: radial-gradient(circle, ${theme.primary}08 0%, transparent 70%);
            border-radius: 50%;
            transform: translate(30%, -30%);
            pointer-events: none;
          }
          .start-recording-banner:hover {
            border-color: ${theme.primary}40;
            background: linear-gradient(135deg, ${theme.primary}0c 0%, ${theme.secondary}0c 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 30px ${theme.primary}12;
          }
          .start-recording-banner:active {
            transform: translateY(0);
          }
          .start-recording-icon {
            width: 72px;
            height: 72px;
            background: ${theme.gradient};
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            cursor: pointer;
            position: relative;
            transition: all 0.3s ease;
            box-shadow: 0 4px 15px ${theme.primary}40;
          }
          .start-recording-icon::before {
            content: '';
            position: absolute;
            inset: -4px;
            border-radius: 50%;
            border: 2px solid ${theme.primary}30;
            animation: recording-pulse 2s ease-in-out infinite;
          }
          .start-recording-icon::after {
            content: 'Click to Start';
            position: absolute;
            bottom: -28px;
            left: 50%;
            transform: translateX(-50%);
            font-size: 11px;
            font-weight: 600;
            color: ${theme.primary};
            white-space: nowrap;
            opacity: 0;
            transition: opacity 0.2s;
          }
          .start-recording-icon:hover::after {
            opacity: 1;
          }
          .start-recording-icon:hover {
            transform: scale(1.08);
            box-shadow: 0 8px 25px ${theme.primary}50;
          }
          .start-recording-icon:active {
            transform: scale(0.95);
          }
          .start-recording-icon svg {
            width: 36px;
            height: 36px;
            color: white;
          }
          @keyframes recording-pulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.15); opacity: 0.5; }
          }
          .start-recording-content {
            flex: 0 1 auto;
          }
          .start-recording-content h3 {
            font-size: 18px;
            font-weight: 600;
            color: #1e293b;
            margin: 0 0 4px 0;
          }
          .start-recording-content p {
            font-size: 14px;
            color: #64748b;
            margin: 0 0 10px 0;
          }
          .start-recording-cta {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            background: ${theme.gradient};
            color: white;
            font-size: 13px;
            font-weight: 600;
            padding: 6px 14px;
            border-radius: 20px;
            transition: all 0.2s;
          }
          .start-recording-banner:hover .start-recording-cta {
            transform: scale(1.05);
            box-shadow: 0 4px 12px ${theme.primary}40;
          }
          .start-recording-cta-icon {
            display: inline-flex;
            align-items: center;
          }
          .start-recording-steps {
            display: flex;
            gap: 24px;
            margin-top: 12px;
          }
          .start-recording-step {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 13px;
            color: #475569;
          }
          .start-recording-step-num {
            width: 20px;
            height: 20px;
            background: ${theme.gradient};
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 11px;
            font-weight: 600;
            color: white;
          }
          .start-recording-tooltip {
            position: fixed;
            top: 80px;
            right: 20px;
            background: #0f172a;
            color: white;
            padding: 16px 18px 14px;
            border: 1px solid rgba(99, 102, 241, 0.25);
            border-radius: 12px;
            font-size: 13px;
            z-index: 10000;
            animation: tooltipSlide 0.3s ease;
            box-shadow: 0 12px 40px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(255, 255, 255, 0.04) inset;
            max-width: 300px;
            width: 300px;
          }
          .start-recording-tooltip::before {
            content: '';
            position: absolute;
            top: -7px;
            right: 24px;
            width: 12px;
            height: 12px;
            background: #0f172a;
            border-top: 1px solid rgba(99, 102, 241, 0.25);
            border-left: 1px solid rgba(99, 102, 241, 0.25);
            transform: rotate(45deg);
          }
          .start-recording-tooltip-head {
            display: flex;
            align-items: flex-start;
            gap: 12px;
            margin-bottom: 10px;
            padding-right: 18px;
          }
          .start-recording-tooltip-icon {
            width: 34px;
            height: 34px;
            border-radius: 9px;
            background: linear-gradient(135deg, ${theme.primary} 0%, ${theme.secondary} 100%);
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            box-shadow: 0 4px 12px rgba(99, 102, 241, 0.35);
          }
          .start-recording-tooltip-title {
            font-size: 13px;
            font-weight: 600;
            color: #f8fafc;
            line-height: 1.35;
            margin-bottom: 2px;
          }
          .start-recording-tooltip-title strong {
            color: ${theme.primary};
            font-weight: 600;
          }
          .start-recording-tooltip-body {
            font-size: 12px;
            color: #94a3b8;
            line-height: 1.5;
            margin: 0 0 0 0;
          }
          .start-recording-tooltip-divider {
            margin: 12px -18px 10px;
            border-top: 1px solid rgba(148, 163, 184, 0.12);
          }
          .start-recording-tooltip-install {
            font-size: 12px;
            color: #94a3b8;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 8px;
          }
          .start-recording-tooltip-install a {
            color: ${theme.primary};
            font-weight: 600;
            text-decoration: none;
            display: inline-flex;
            align-items: center;
            gap: 4px;
            transition: color 0.15s;
          }
          .start-recording-tooltip-install a:hover {
            color: #a5b4fc;
          }
          .start-recording-tooltip-close {
            position: absolute;
            top: 8px;
            right: 8px;
            background: none;
            border: none;
            color: #64748b;
            cursor: pointer;
            padding: 4px;
            font-size: 16px;
            line-height: 1;
            transition: color 0.15s;
          }
          .start-recording-tooltip-close:hover {
            color: #cbd5e1;
          }
          @keyframes tooltipSlide {
            from { opacity: 0; transform: translateY(-10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>

        <div className="start-recording-banner" onClick={handleStartRecording}>
          <div className="start-recording-icon" title="Start Recording">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <circle cx="12" cy="12" r="3" fill="currentColor"/>
            </svg>
          </div>
          <div className="start-recording-content">
            <h3>Ready to create a new guide?</h3>
            <p>Use the InstaGuide Chrome extension to capture your workflow automatically.</p>
            <span className="start-recording-cta">
              <span className="start-recording-cta-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg></span>
              Click anywhere to get started
            </span>
            <div className="start-recording-steps">
              <span className="start-recording-step">
                <span className="start-recording-step-num">1</span>
                Click the extension icon
              </span>
              <span className="start-recording-step">
                <span className="start-recording-step-num">2</span>
                Start recording
              </span>
              <span className="start-recording-step">
                <span className="start-recording-step-num">3</span>
                Perform your actions
              </span>
            </div>
          </div>
        </div>

        {showRecordingTooltip && (
          <div className="start-recording-tooltip">
            <button className="start-recording-tooltip-close" onClick={() => setShowRecordingTooltip(false)} aria-label="Dismiss">×</button>
            <div className="start-recording-tooltip-head">
              <div className="start-recording-tooltip-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v6a3 3 0 0 1-3 3v3a2 2 0 0 1-2 2h-2a3 3 0 1 1-6 0H6a2 2 0 0 1-2-2V5z"/>
                </svg>
              </div>
              <div>
                <div className="start-recording-tooltip-title">Find the <strong>InstaGuide</strong> icon in your toolbar</div>
                <p className="start-recording-tooltip-body">Top-right of the browser. Click it to open the extension and start recording.</p>
              </div>
            </div>
            <div className="start-recording-tooltip-divider"></div>
            <div className="start-recording-tooltip-install">
              <span>Don't have it yet?</span>
              <a href={CHROME_STORE_URL} target="_blank" rel="noopener noreferrer">
                Install
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M7 17L17 7M17 7H7M17 7V17"/>
                </svg>
              </a>
            </div>
          </div>
        )}

        <h3 className="section-title">
          <span className="section-title-icon">{Icons.clock}</span>
          Recent Guides
        </h3>
        {recentGuides.length === 0 ? (
          loadingGuides ? (
            <GuideSkeletonGrid count={3} />
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">{Icons.inbox}</div>
              <h3>No recent guides</h3>
              <p>Guides you open will appear here for quick access</p>
            </div>
          )
        ) : (
          <div className="guides-grid">
            {recentGuides.map(guide => (
              <div key={guide.id} className="guide-card" onClick={() => onSelectGuide(guide.id)}>
                <div className="guide-preview">
                  <div className="guide-preview-favicon">
                    {(() => {
                      const faviconUrl = getFaviconWithFallback(guide);
                      return faviconUrl ? (
                        <img src={faviconUrl} alt="" onError={(e) => { handleFaviconError(e); e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }} />
                      ) : null;
                    })()}
                    <span className="guide-preview-icon-svg" style={{ display: getFaviconWithFallback(guide) ? 'none' : 'flex' }}>{Icons.globe}</span>
                  </div>
                  {guide.domain && <span className="guide-domain">{guide.domain}</span>}
                  <span className={`guide-source-badge ${guide.source === 'desktop' ? 'desktop' : 'extension'}`} title={guide.source === 'desktop' ? 'Created with Desktop App' : 'Created with Chrome Extension'}>
                    {guide.source === 'desktop' ? Icons.desktop : Icons.chrome}
                  </span>
                  <MeetingBadge guide={guide} />
                </div>
                <div className="guide-info">
                  <h3 className="guide-name">{guide.name}</h3>
                  <p className="guide-steps">
                    <span>{guide.stepCount ?? guide.steps?.length ?? 0} steps</span>
                    {guide.updatedAt && <span className="guide-modified" title={formatGuideDateTooltip(guide.updatedAt)}>{formatGuideDateShort(guide.updatedAt)}</span>}
                  </p>
                  {(guide.projectId || guide.creatorName) && (
                    <div className="guide-meta-row">
                      {guide.projectId && projectMap[guide.projectId] ? (
                        <span className="guide-project-badge">
                          <span className="guide-project-badge-icon">{React.cloneElement(Icons.folder, { width: 12, height: 12 })}</span>
                          {projectMap[guide.projectId].name}
                        </span>
                      ) : <span />}
                      {guide.creatorName && <span className="guide-owner-name" title={guide.creatorName}>{guide.creatorName}</span>}
                    </div>
                  )}
                </div>
                <div className="guide-actions">
                  <button
                    className={`guide-action-btn favorite ${favoriteGuideIds.includes(guide.id) ? 'active' : ''}`}
                    onClick={(e) => { e.stopPropagation(); onToggleFavorite(guide.id); }}
                  >
                    {favoriteGuideIds.includes(guide.id) ? Icons.starFilled : Icons.star}
                  </button>
                  {user?.accountType === 'organization' && (
                    <button
                      className="guide-action-btn guide-action-icon"
                      onClick={(e) => { e.stopPropagation(); onQuickShare?.(guide.id); }}
                      title="Share guide"
                      style={{ color: theme.primary, ...(guide.shareCount > 0 ? { background: '#dbeafe' } : {}) }}
                    >
                      {Icons.shareSmall}
                    </button>
                  )}
                  <button className="guide-action-btn guide-action-icon" onClick={(e) => { e.stopPropagation(); onExport(guide.id, 'pdf'); }} disabled={exportingGuide.id === guide.id}>
                    {exportingGuide.id === guide.id && exportingGuide.format === 'pdf' ? <span className="btn-spinner-sm" /> : Icons.download} {exportingGuide.id === guide.id && exportingGuide.format === 'pdf' ? '...' : 'PDF'}
                  </button>
                  <button
                    className="guide-action-btn guide-action-icon"
                    style={{ color: '#ef4444' }}
                    onClick={(e) => { e.stopPropagation(); onDeleteGuide(guide.id); }}
                    disabled={deletingGuideId === guide.id}
                    title="Delete guide"
                  >
                    {deletingGuideId === guide.id ? '...' : Icons.trash}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

// Shared Guide Filter Bar Component
function GuideFilterBar({ searchQuery, setSearchQuery, sourceFilter, setSourceFilter, ownerFilter, setOwnerFilter, ownershipFilter, setOwnershipFilter, projectFilter, setProjectFilter, sortBy, setSortBy, projects = [], ownerNames = [], resultCount, hasActiveFilters, viewMode, setViewMode, userName }) {
  const [showFilters, setShowFilters] = useState(false);
  const filterRef = useRef(null);

  const activeCount = [
    sourceFilter !== 'all' ? 1 : 0,
    ownerFilter && ownerFilter !== 'all' ? 1 : 0,
    ownershipFilter && ownershipFilter !== 'all' ? 1 : 0,
    projectFilter && projectFilter !== 'all' ? 1 : 0,
    sortBy !== 'newest' ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setShowFilters(false);
      }
    };
    if (showFilters) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFilters]);

  const clearAllFilters = () => {
    setSourceFilter('all');
    if (setOwnerFilter) setOwnerFilter('all');
    if (setOwnershipFilter) setOwnershipFilter('all');
    if (setProjectFilter) setProjectFilter('all');
    setSortBy('newest');
  };

  return (
    <>
      <style>{`
        .gfb {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 20px;
        }
        .gfb-search {
          position: relative;
          flex: 1;
          max-width: 420px;
        }
        .gfb-search svg {
          position: absolute;
          left: 13px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
          width: 15px;
          height: 15px;
          pointer-events: none;
        }
        .gfb-search input {
          width: 100%;
          padding: 8px 12px 8px 38px;
          border: 1.5px solid #e2e8f0;
          border-radius: 10px;
          font-size: 13.5px;
          color: #1e293b;
          background: white;
          transition: all 0.2s;
        }
        .gfb-search input:focus {
          outline: none;
          border-color: ${theme.primary};
          box-shadow: 0 0 0 3px ${theme.primary}12;
        }
        .gfb-search input::placeholder { color: #a0aec0; }
        .gfb-filter-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          border: 1.5px solid #e2e8f0;
          border-radius: 10px;
          background: white;
          font-size: 13.5px;
          font-weight: 500;
          color: #475569;
          cursor: pointer;
          transition: all 0.2s;
          position: relative;
          white-space: nowrap;
        }
        .gfb-filter-btn:hover {
          border-color: #cbd5e1;
          background: #f8fafc;
        }
        .gfb-filter-btn.active {
          border-color: ${theme.primary};
          color: ${theme.primary};
          background: ${theme.primary}08;
        }
        .gfb-filter-btn svg {
          width: 15px;
          height: 15px;
        }
        .gfb-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 18px;
          height: 18px;
          padding: 0 5px;
          border-radius: 9px;
          background: ${theme.primary};
          color: white;
          font-size: 11px;
          font-weight: 700;
          line-height: 1;
        }
        .gfb-result-count {
          font-size: 13px;
          color: #94a3b8;
          margin-left: auto;
          white-space: nowrap;
        }
        .gfb-popover-wrap {
          position: relative;
        }
        .gfb-popover {
          position: absolute;
          top: calc(100% + 8px);
          right: 0;
          z-index: 100;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 14px;
          box-shadow: 0 12px 40px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06);
          min-width: 320px;
          max-height: 420px;
          overflow-y: auto;
          padding: 0;
          animation: gfbSlideIn 0.15s ease-out;
        }
        @keyframes gfbSlideIn {
          from { opacity: 0; transform: translateY(-6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .gfb-popover-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 18px 12px;
          border-bottom: 1px solid #f1f5f9;
        }
        .gfb-popover-title {
          font-size: 13px;
          font-weight: 700;
          color: #1e293b;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .gfb-clear-btn {
          font-size: 12px;
          color: ${theme.primary};
          background: none;
          border: none;
          cursor: pointer;
          font-weight: 600;
          padding: 2px 6px;
          border-radius: 4px;
          transition: background 0.15s;
        }
        .gfb-clear-btn:hover {
          background: ${theme.primary}10;
        }
        .gfb-popover-body {
          padding: 14px 18px 18px;
          display: flex;
          flex-direction: column;
          gap: 14px;
        }
        .gfb-field > label {
          display: block;
          font-size: 11.5px;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.4px;
          margin-bottom: 6px;
        }
        .gfb-field select {
          width: 100%;
          padding: 8px 32px 8px 12px;
          border: 1.5px solid #e2e8f0;
          border-radius: 8px;
          font-size: 13.5px;
          color: #1e293b;
          background: white;
          cursor: pointer;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%2394a3b8' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 12px center;
          transition: border-color 0.15s;
        }
        .gfb-field select:focus {
          outline: none;
          border-color: ${theme.primary};
        }
        .gfb-field select.has-value {
          border-color: ${theme.primary}60;
          background-color: ${theme.primary}05;
        }
        .gfb-multi-select {
          max-height: 150px;
          overflow-y: auto;
          border: 1.5px solid #e2e8f0;
          border-radius: 8px;
          padding: 6px 0;
          background: white;
        }
        .gfb-checkbox-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 7px 12px;
          cursor: pointer;
          font-size: 13.5px;
          color: #1e293b;
          transition: background 0.15s;
          user-select: none;
        }
        .gfb-checkbox-item:hover {
          background: ${theme.primary}08;
        }
        .gfb-checkbox-item input[type="checkbox"] {
          width: 16px;
          height: 16px;
          accent-color: ${theme.primary};
          cursor: pointer;
          flex-shrink: 0;
          margin: 0;
        }
        .gfb-checkbox-name {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .gfb-checkbox-divider {
          height: 1px;
          background: #e2e8f0;
          margin: 4px 0;
        }
        .gfb-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        .gfb-active-tags {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
        }
        .gfb-tag {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 500;
          color: ${theme.primary};
          background: ${theme.primary}0c;
          border: 1px solid ${theme.primary}20;
          white-space: nowrap;
        }
        .gfb-tag button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: none;
          border: none;
          cursor: pointer;
          color: ${theme.primary};
          padding: 0;
          margin-left: 2px;
          opacity: 0.6;
          transition: opacity 0.15s;
        }
        .gfb-tag button:hover { opacity: 1; }
        .gfb-tag button svg { width: 12px; height: 12px; }
        .gfb-view-toggle {
          display: flex;
          align-items: center;
          border: 1.5px solid #e2e8f0;
          border-radius: 10px;
          overflow: hidden;
          background: white;
        }
        .gfb-view-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 34px;
          background: white;
          border: none;
          cursor: pointer;
          color: #94a3b8;
          transition: all 0.15s;
          padding: 0;
        }
        .gfb-view-btn:first-child {
          border-right: 1px solid #e2e8f0;
        }
        .gfb-view-btn:hover {
          color: #64748b;
          background: #f8fafc;
        }
        .gfb-view-btn.active {
          color: ${theme.primary};
          background: ${theme.primary}0a;
        }
        .gfb-view-btn svg {
          width: 16px;
          height: 16px;
        }
      `}</style>
      <div className="gfb">
        <div className="gfb-search">
          {Icons.search}
          <input
            type="text"
            placeholder="Search guides..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="gfb-popover-wrap" ref={filterRef}>
          <button
            className={`gfb-filter-btn ${activeCount > 0 ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
            Filters
            {activeCount > 0 && <span className="gfb-badge">{activeCount}</span>}
          </button>
          {showFilters && (
            <div className="gfb-popover">
              <div className="gfb-popover-header">
                <span className="gfb-popover-title">Filters</span>
                {activeCount > 0 && (
                  <button className="gfb-clear-btn" onClick={clearAllFilters}>Clear all</button>
                )}
              </div>
              <div className="gfb-popover-body">
                <div className="gfb-row">
                  <div className="gfb-field">
                    <label>Source</label>
                    <select className={sourceFilter !== 'all' ? 'has-value' : ''} value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
                      <option value="all">All Sources</option>
                      <option value="extension">Chrome Extension</option>
                      <option value="desktop">Desktop App</option>
                    </select>
                  </div>
                  <div className="gfb-field">
                    <label>Sort By</label>
                    <select className={sortBy !== 'newest' ? 'has-value' : ''} value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                      <option value="newest">Newest First</option>
                      <option value="oldest">Oldest First</option>
                      <option value="name">Name A-Z</option>
                      <option value="steps">Most Steps</option>
                    </select>
                  </div>
                </div>
                {setOwnershipFilter && ownerNames.length > 0 && (
                  <div className="gfb-field">
                    <label>Created By</label>
                    <select className={ownershipFilter !== 'all' ? 'has-value' : ''} value={ownershipFilter} onChange={(e) => setOwnershipFilter(e.target.value)}>
                      <option value="all">Everyone</option>
                      {userName && ownerNames.includes(userName) && (
                        <option value={userName}>Me</option>
                      )}
                      {ownerNames.map(name => (
                        <option key={name} value={name}>{name}{name === userName ? ' (Me)' : ''}</option>
                      ))}
                    </select>
                  </div>
                )}
                {setProjectFilter && projects.length > 0 && (
                  <div className="gfb-field">
                    <label>Project</label>
                    <select className={projectFilter !== 'all' ? 'has-value' : ''} value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)}>
                      <option value="all">All Projects</option>
                      {projects.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        {setViewMode && (
          <div className="gfb-view-toggle">
            <button className={`gfb-view-btn ${viewMode === 'tile' ? 'active' : ''}`} onClick={() => setViewMode('tile')} title="Tile view">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
            </button>
            <button className={`gfb-view-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')} title="List view">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
            </button>
          </div>
        )}
        {hasActiveFilters && (
          <span className="gfb-result-count">{resultCount} result{resultCount !== 1 ? 's' : ''}</span>
        )}
      </div>
      {hasActiveFilters && activeCount > 0 && (
        <div className="gfb-active-tags" style={{ marginTop: -12, marginBottom: 16 }}>
          {sourceFilter !== 'all' && (
            <span className="gfb-tag">
              {sourceFilter === 'extension' ? 'Chrome Extension' : 'Desktop App'}
              <button onClick={() => setSourceFilter('all')}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
            </span>
          )}
          {ownershipFilter && ownershipFilter !== 'all' && (
            <span className="gfb-tag">
              By {ownershipFilter}
              <button onClick={() => setOwnershipFilter('all')}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
            </span>
          )}
          {projectFilter && projectFilter !== 'all' && projects.length > 0 && (
            <span className="gfb-tag">
              {projects.find(p => p.id === projectFilter)?.name || 'Project'}
              <button onClick={() => setProjectFilter('all')}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
            </span>
          )}
          {ownerFilter && ownerFilter !== 'all' && (
            <span className="gfb-tag">
              {ownerFilter}
              <button onClick={() => setOwnerFilter('all')}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
            </span>
          )}
          {sortBy !== 'newest' && (
            <span className="gfb-tag">
              {sortBy === 'oldest' ? 'Oldest' : sortBy === 'name' ? 'A-Z' : 'Most Steps'}
              <button onClick={() => setSortBy('newest')}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
            </span>
          )}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Sortable list-view columns (My Guides, Projects Explorer, Favorites,
// Shared With Me)
//
// Users click any column header in the list view to sort by that column.
// Click again toggles asc↔desc. State is local to each page — the external
// Filters-panel `sortBy` is used as a fallback default (newest first) when
// no column-header sort is active. Grid view continues to use `sortBy`
// exclusively (no column headers to click).
// ---------------------------------------------------------------------------

// Extract the sort key for a given guide + column. Returns a comparable
// primitive so a single sort comparator handles every column type.
function guideSortValue(guide, column, projectMap) {
  switch (column) {
    case 'name':    return (guide.name || '').toLowerCase();
    case 'steps':   return Number(guide.stepCount ?? guide.steps?.length ?? 0);
    case 'domain':  return (guide.domain || '').toLowerCase();
    case 'project': {
      const p = guide.projectId && projectMap ? projectMap[guide.projectId] : null;
      return (p?.name || '').toLowerCase();
    }
    case 'owner':   return (guide.creatorName || guide.ownerName || '').toLowerCase();
    case 'source':  return (guide.source || 'extension').toLowerCase();
    case 'modified': {
      const raw = guide.updatedAt || guide.createdAt;
      return raw ? new Date(raw).getTime() : 0;
    }
    default: return '';
  }
}

// Return a new sorted array. `direction` is 'asc' or 'desc'. Handles
// mixed types (string vs number) via typeof branching so we don't get
// weird lexical comparisons on numeric fields.
function sortGuidesByColumn(guides, column, direction, projectMap) {
  const dir = direction === 'desc' ? -1 : 1;
  return [...guides].sort((a, b) => {
    const va = guideSortValue(a, column, projectMap);
    const vb = guideSortValue(b, column, projectMap);
    if (typeof va === 'number' && typeof vb === 'number') {
      return (va - vb) * dir;
    }
    // String compare — localeCompare so accented characters + case fold correctly
    const sa = String(va);
    const sb = String(vb);
    return sa.localeCompare(sb) * dir;
  });
}

// Column-header cell rendered as a clickable sort control. `activeColumn`
// and `activeDirection` mark the currently-sorted column; clicking a
// different column resets direction to asc (or `defaultDir` if supplied
// — e.g. Modified defaults to desc so the newest guides show first).
function SortableColumnHeader({ column, label, className, activeColumn, activeDirection, defaultDir = 'asc', onSort }) {
  const isActive = activeColumn === column;
  const handleClick = () => {
    if (!isActive) {
      onSort(column, defaultDir);
    } else {
      onSort(column, activeDirection === 'asc' ? 'desc' : 'asc');
    }
  };
  return (
    <button
      type="button"
      className={`${className} gl-sortable ${isActive ? 'gl-sortable-active' : ''}`}
      onClick={handleClick}
      aria-sort={isActive ? (activeDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <span>{label}</span>
      <span className="gl-sort-arrow" aria-hidden="true">
        {isActive
          ? (activeDirection === 'asc'
              ? <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 15 12 9 18 15"/></svg>
              : <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>)
          : <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.35"><polyline points="8 9 12 5 16 9"/><polyline points="8 15 12 19 16 15"/></svg>}
      </span>
    </button>
  );
}

// Projects Explorer Page
function ProjectsExplorerPage({ projects, guides, onSelectGuide, onDeleteGuide, deletingGuideId, onExport, exportingGuide, favoriteGuideIds, onToggleFavorite, onMoveGuides, showConfirm, showToast, loadingGuides, onCreateProject, onUpdateProject, onDeleteProject, onQuickShare, user, sourceFilter, setSourceFilter, ownershipFilter, setOwnershipFilter, sortBy, setSortBy, searchQuery, setSearchQuery }) {
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedGuides, setSelectedGuides] = useState([]);
  const [selectedAction, setSelectedAction] = useState('');
  const [moveToProjectId, setMoveToProjectId] = useState('');
  const [isMoving, setIsMoving] = useState(false);

  // Prune selection of any IDs that no longer exist in the guides list
  // (e.g., after the user deletes a single selected guide via its row trash icon —
  // otherwise the toolbar shows "1 of 0 selected" with a stale ID).
  useEffect(() => {
    setSelectedGuides(prev => {
      const ids = new Set(guides.map(g => g.id));
      const filtered = prev.filter(id => ids.has(id));
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [guides]);

  // Project management state
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');
  const [savingProject, setSavingProject] = useState(false);

  // Auto-select first project on load
  useEffect(() => {
    if (projects.length > 0 && !selectedProject) {
      setSelectedProject(projects[0]);
    }
  }, [projects, selectedProject]);

  const [ownerFilter, setOwnerFilter] = useState('all');
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('guideViewMode') || 'tile');
  const handleSetViewMode = (mode) => { setViewMode(mode); localStorage.setItem('guideViewMode', mode); };
  // Per-column sort for the list view. `null` means "use the external
  // sortBy default from the Filters panel". Set by clicking a column
  // header in the list view; cleared when user picks a value from the
  // Filters-panel Sort dropdown (see setSortBy proxy below).
  const [listSort, setListSort] = useState({ column: null, direction: 'asc' });
  const projectMap = useMemo(() => projects.reduce((acc, p) => ({ ...acc, [p.id]: p }), {}), [projects]);

  const projectGuidesRaw = selectedProject
    ? guides.filter(g => g.projectId === selectedProject.id)
    : [];

  const ownerNames = useMemo(() => {
    const names = new Set();
    projectGuidesRaw.forEach(g => {
      const name = g.creatorName || g.ownerName;
      if (name) names.add(name);
    });
    return [...names].sort();
  }, [projectGuidesRaw]);

  const projectGuides = useMemo(() => {
    let result = projectGuidesRaw;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(g => g.name?.toLowerCase().includes(q) || g.domain?.toLowerCase().includes(q));
    }
    if (sourceFilter !== 'all') {
      result = result.filter(g => g.source === sourceFilter);
    }
    if (ownerFilter !== 'all') {
      result = result.filter(g => (g.creatorName || g.ownerName) === ownerFilter);
    }
    if (ownershipFilter && ownershipFilter !== 'all') {
      result = result.filter(g => (g.creatorName || g.ownerName) === ownershipFilter);
    }
    // Column-header sort (list view) takes precedence over the Filters-
    // panel sortBy dropdown. When no column-header sort is active, fall
    // back to the classic dropdown ordering.
    if (listSort.column) {
      result = sortGuidesByColumn(result, listSort.column, listSort.direction, projectMap);
    } else {
      result = [...result].sort((a, b) => {
        if (sortBy === 'newest') return new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt);
        if (sortBy === 'oldest') return new Date(a.updatedAt || a.createdAt) - new Date(b.updatedAt || b.createdAt);
        if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
        if (sortBy === 'steps') return (b.stepCount ?? b.steps?.length ?? 0) - (a.stepCount ?? a.steps?.length ?? 0);
        return 0;
      });
    }
    return result;
  }, [projectGuidesRaw, searchQuery, sourceFilter, ownerFilter, ownershipFilter, sortBy, listSort, projectMap, user]);

  const startMoveMode = () => {
    setSelectMode(true);
    setSelectedGuides([]);
    setSelectedAction('');
    setMoveToProjectId('');
  };

  const cancelMoveMode = () => {
    setSelectMode(false);
    setSelectedGuides([]);
    setSelectedAction('');
    setMoveToProjectId('');
  };

  const toggleGuideSelection = (guideId) => {
    setSelectedGuides(prev =>
      prev.includes(guideId)
        ? prev.filter(id => id !== guideId)
        : [...prev, guideId]
    );
  };

  const selectAll = () => {
    if (selectedGuides.length === projectGuides.length) {
      setSelectedGuides([]);
    } else {
      setSelectedGuides(projectGuides.map(g => g.id));
    }
  };

  const handleBulkMove = async () => {
    if (!moveToProjectId || selectedGuides.length === 0 || !onMoveGuides) return;
    setIsMoving(true);
    const success = await onMoveGuides(selectedGuides, moveToProjectId);
    if (success) {
      cancelMoveMode();
    }
    setIsMoving(false);
  };

  const handleBulkDelete = () => {
    if (selectedGuides.length === 0 || !onDeleteGuide) return;
    const count = selectedGuides.length;
    const message = `Are you sure you want to delete ${count} guide${count > 1 ? 's' : ''}? This action cannot be undone.`;

    showConfirm('Delete Guides', message, async () => {
      setIsMoving(true);
      const idsToDelete = [...selectedGuides];
      for (const guideId of idsToDelete) {
        await onDeleteGuide(guideId, true, true); // skipConfirm + silent (suppress per-guide toasts)
      }
      cancelMoveMode();
      setIsMoving(false);
      if (showToast) showToast(`Deleted ${idsToDelete.length} guide${idsToDelete.length !== 1 ? 's' : ''}`, 'success');
    }, { type: 'danger', confirmText: 'Delete' });
  };

  const handleApply = async () => {
    if (selectedAction === 'move') {
      await handleBulkMove();
    } else if (selectedAction === 'delete') {
      handleBulkDelete();
    }
  };

  // Other projects to move to (exclude current)
  const otherProjects = projects.filter(p => p.id !== selectedProject?.id);

  // Project management functions
  const openCreateProjectModal = () => {
    setEditingProject(null);
    setNewProjectName('');
    setNewProjectDescription('');
    setShowProjectModal(true);
  };

  const openEditProjectModal = (project) => {
    setEditingProject(project);
    setNewProjectName(project.name);
    setNewProjectDescription(project.description || '');
    setShowProjectModal(true);
  };

  const handleSaveProject = async () => {
    if (!newProjectName.trim()) return;
    setSavingProject(true);

    if (editingProject) {
      const updated = await onUpdateProject(editingProject.id, newProjectName.trim(), newProjectDescription.trim());
      if (updated) {
        setSelectedProject(prev => prev?.id === updated.id ? { ...prev, ...updated } : prev);
        setShowProjectModal(false);
      }
    } else {
      const created = await onCreateProject(newProjectName.trim(), newProjectDescription.trim());
      if (created) {
        setSelectedProject(created);
        setShowProjectModal(false);
      }
    }
    setSavingProject(false);
  };

  const handleProjectDelete = (projectId) => {
    onDeleteProject(projectId);
    if (selectedProject?.id === projectId) {
      setSelectedProject(projects.find(p => p.id !== projectId) || null);
    }
  };

  return (
    <>
      <div className="main-header">
        <h1 className="main-header-title">Projects</h1>
      </div>
      <div className="page-content">
        <style>{`
          .projects-layout {
            display: grid;
            grid-template-columns: 280px 1fr;
            gap: 24px;
            min-height: calc(100vh - 200px);
          }
          @media (max-width: 900px) {
            .projects-layout {
              grid-template-columns: 1fr;
            }
          }
          .projects-list {
            background: white;
            border-radius: 12px;
            padding: 20px;
            box-shadow: 0 1px 3px rgba(0,0,0,0.08);
            border: 1px solid #e2e8f0;
            height: fit-content;
          }
          .projects-list-header {
            font-size: 12px;
            font-weight: 600;
            color: #64748b;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 16px;
            padding-bottom: 12px;
            border-bottom: 1px solid #e2e8f0;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          .project-add-btn {
            width: 24px;
            height: 24px;
            background: ${theme.gradient};
            border: none;
            border-radius: 6px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            color: white;
            font-size: 16px;
            transition: all 0.2s;
          }
          .project-add-btn:hover {
            transform: scale(1.05);
            box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
          }
          .project-item-actions {
            display: none;
            gap: 4px;
            margin-left: auto;
          }
          .project-item:hover .project-item-actions {
            display: flex;
          }
          .project-item.active .project-item-actions {
            display: flex;
          }
          .project-item-action-btn {
            width: 24px;
            height: 24px;
            background: transparent;
            border: none;
            border-radius: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            color: inherit;
            opacity: 0.6;
            transition: all 0.15s;
          }
          .project-item-action-btn:hover {
            opacity: 1;
            background: rgba(255,255,255,0.15);
          }
          .project-item:not(.active) .project-item-action-btn:hover {
            background: #e2e8f0;
          }
          .project-item-action-btn.danger:hover {
            background: rgba(239, 68, 68, 0.1);
            color: #ef4444;
          }
          .project-item.active .project-item-action-btn.danger:hover {
            background: rgba(255,255,255,0.2);
            color: #fef2f2;
          }
          .project-item-action-btn svg {
            width: 14px;
            height: 14px;
          }
          .project-item-content {
            flex: 1;
            min-width: 0;
          }
          .project-item {
            padding: 14px 16px;
            border-radius: 8px;
            cursor: pointer;
            transition: all 0.15s ease;
            margin-bottom: 4px;
            border: 1px solid transparent;
            display: flex;
            align-items: center;
            gap: 8px;
          }
          .project-item:hover {
            background: #f8fafc;
            border-color: #e2e8f0;
          }
          .project-item.active {
            background: ${theme.gradient};
            color: white;
            border-color: transparent;
          }
          .project-item-name {
            font-weight: 600;
            font-size: 14px;
            margin-bottom: 4px;
            display: flex;
            align-items: center;
            gap: 8px;
            min-width: 0;
          }
          .project-item-name-text {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            min-width: 0;
            flex: 1;
          }
          .project-item-count {
            font-size: 13px;
            opacity: 0.7;
          }
          .project-guides {
            background: white;
            border-radius: 12px;
            padding: 24px;
            box-shadow: 0 1px 3px rgba(0,0,0,0.08);
            border: 1px solid #e2e8f0;
            min-height: 400px;
            display: flex;
            flex-direction: column;
          }
          .project-guides .empty-state {
            flex: 1;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            text-align: center;
            color: #64748b;
          }
          .project-guides .empty-state-icon {
            margin-bottom: 16px;
            color: #cbd5e1;
          }
          .project-guides .empty-state-icon svg {
            width: 48px;
            height: 48px;
          }
          .project-guides .empty-state h3 {
            font-size: 18px;
            font-weight: 600;
            color: #1e293b;
            margin: 0 0 8px 0;
          }
          .project-guides .empty-state p {
            margin: 0;
            font-size: 14px;
          }
          .project-guides .guide-filters-bar {
            display: flex;
            align-items: center;
            gap: 12px;
            flex-wrap: wrap;
          }
          .project-guides .guide-search-wrapper {
            position: relative;
            flex: 1;
            min-width: 200px;
            max-width: 400px;
          }
          .project-guides .guide-search-wrapper svg {
            position: absolute;
            left: 12px;
            top: 50%;
            transform: translateY(-50%);
            color: #94a3b8;
            width: 16px;
            height: 16px;
          }
          .project-guides .guide-search-input {
            width: 100%;
            padding: 9px 12px 9px 38px;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            font-size: 14px;
            color: #1e293b;
            background: white;
            transition: all 0.2s;
          }
          .project-guides .guide-search-input:focus {
            outline: none;
            border-color: ${theme.primary};
            box-shadow: 0 0 0 3px ${theme.primary}15;
          }
          .project-guides .guide-search-input::placeholder {
            color: #94a3b8;
          }
          .project-guides .guide-filter-select {
            padding: 9px 12px;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            font-size: 13px;
            color: #1e293b;
            background: white;
            cursor: pointer;
          }
          .project-guides .guide-filter-select:focus {
            outline: none;
            border-color: ${theme.primary};
            box-shadow: 0 0 0 3px ${theme.primary}15;
          }
          .project-guides .guide-filter-count {
            font-size: 13px;
            color: #94a3b8;
            margin-left: auto;
            white-space: nowrap;
          }
          .project-guides-header {
            margin-bottom: 24px;
            padding-bottom: 16px;
            border-bottom: 1px solid #e2e8f0;
          }
          .project-guides-title {
            font-size: 20px;
            font-weight: 600;
            color: #0f172a;
            margin: 0 0 4px 0;
          }
          .project-guides-desc {
            font-size: 14px;
            color: #64748b;
            margin: 0;
          }
          .project-guides-header-row {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
          }
          .project-guides-settings-btn {
            width: 36px;
            height: 36px;
            background: #f1f5f9;
            border: none;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            color: #64748b;
            transition: all 0.2s;
          }
          .project-guides-settings-btn:hover {
            background: #e2e8f0;
            color: #1e293b;
          }
          .project-guides-settings-btn svg {
            width: 18px;
            height: 18px;
          }
          .project-select-toolbar {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 12px 16px;
            background: #f8fafc;
            border-radius: 10px;
            margin-bottom: 20px;
            border: 1px solid #e2e8f0;
          }
          .project-select-toolbar-info {
            font-weight: 500;
            font-size: 13px;
            color: #334155;
          }
          .project-select-toolbar-actions {
            display: flex;
            align-items: center;
            gap: 10px;
            margin-left: auto;
          }
          .project-select-all-btn {
            padding: 6px 12px;
            background: white;
            border: 1px solid #e2e8f0;
            border-radius: 6px;
            font-size: 12px;
            font-weight: 500;
            cursor: pointer;
            color: #475569;
          }
          .project-select-all-btn:hover {
            background: #f1f5f9;
          }
          .project-action-dropdown,
          .project-move-dropdown {
            padding: 6px 10px;
            border-radius: 6px;
            border: 1px solid #e2e8f0;
            font-size: 12px;
            color: #334155;
            background: white;
          }
          .project-apply-btn {
            padding: 6px 14px;
            background: ${theme.gradient};
            border: none;
            border-radius: 6px;
            color: white;
            font-size: 12px;
            font-weight: 600;
            cursor: pointer;
          }
          .project-apply-btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
          .project-apply-btn.danger {
            background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          }
          .project-cancel-btn {
            padding: 6px 12px;
            background: transparent;
            border: 1px solid #e2e8f0;
            border-radius: 6px;
            font-size: 12px;
            cursor: pointer;
            color: #64748b;
          }
          .guide-card {
            position: relative;
          }
          .guide-checkbox {
            position: absolute;
            top: 12px;
            right: 12px;
            width: 22px;
            height: 22px;
            background: white;
            border: 2px solid #d1d5db;
            border-radius: 5px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            z-index: 10;
            transition: all 0.2s;
          }
          .guide-checkbox:hover {
            border-color: #64748b;
          }
          .guide-checkbox.checked {
            background: #f1f5f9;
            border-color: #64748b;
          }
          .guide-checkbox.checked svg {
            color: #64748b;
          }
          .guide-card.selected {
            box-shadow: 0 0 0 2px ${theme.primary}, 0 4px 12px rgba(0,0,0,0.1);
          }
          .guides-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
            gap: 20px;
          }
          .guide-card {
            background: white;
            border-radius: 12px;
            overflow: hidden;
            border: 1px solid #e2e8f0;
            transition: all 0.2s ease;
            cursor: pointer;
          }
          .guide-card:hover {
            transform: translateY(-2px);
            border-color: ${theme.primary}30;
            box-shadow: 0 8px 24px ${theme.primary}10;
          }
          .guide-preview {
            background: linear-gradient(145deg, ${theme.primary}15 0%, ${theme.secondary}0a 60%, #f1f5f9 100%);
            padding: 20px 24px;
            display: flex;
            align-items: center;
            gap: 16px;
            border-bottom: 1px solid #e2e8f0;
          }
          .guide-preview-favicon {
            width: 48px;
            height: 48px;
            background: white;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          }
          .guide-preview-favicon img {
            width: 32px;
            height: 32px;
            object-fit: contain;
          }
          .guide-preview-icon-svg {
            width: 48px;
            height: 48px;
            background: white;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 2px 8px rgba(0,0,0,0.08);
            color: #94a3b8;
          }
          .guide-preview-icon-svg svg {
            width: 24px;
            height: 24px;
          }
          .guide-domain {
            font-size: 12px;
            color: #64748b;
            background: white;
            padding: 4px 10px;
            border-radius: 20px;
            max-width: 140px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .guide-preview-favicon,
          .guide-preview-icon-svg {
            flex-shrink: 0;
          }
          .guide-source-badge {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 4px 8px;
            border-radius: 12px;
            font-size: 11px;
            background: rgba(255,255,255,0.9);
            color: #64748b;
            flex-shrink: 0;
            margin-left: auto;
          }
          .guide-source-badge.desktop {
            color: #7c3aed;
            background: rgba(124, 58, 237, 0.1);
          }
          .guide-source-badge.extension {
            color: #6366f1;
            background: rgba(99, 102, 241, 0.1);
          }
          /* Sprint K — Meeting Mode badge. Amber chip mirrors the extension
             floater's in-progress meeting badge so the same visual carries
             from recording → library → detail page. Compact variant is
             icon-only to fit narrow list-view columns. */
          .guide-meeting-badge {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            padding: 3px 8px 3px 6px;
            margin-left: 6px;
            border-radius: 999px;
            background: rgba(245, 158, 11, 0.14);
            color: #b45309;
            font-size: 10px;
            font-weight: 600;
            line-height: 1;
            letter-spacing: 0.3px;
            text-transform: uppercase;
            border: 1px solid rgba(245, 158, 11, 0.32);
            flex-shrink: 0;
            white-space: nowrap;
          }
          .guide-meeting-badge.compact {
            gap: 0;
            padding: 4px;
            margin-left: 4px;
            border-radius: 6px;
          }
          .guide-meeting-badge svg {
            flex-shrink: 0;
          }
          .guide-info {
            padding: 16px 20px;
          }
          .guide-name {
            font-size: 16px;
            font-weight: 600;
            color: #1e293b;
            margin: 0 0 6px 0;
          }
          .guide-steps {
            font-size: 14px;
            color: #64748b;
            margin: 0;
            display: flex;
            align-items: center;
            gap: 8px;
          }
          .guide-modified {
            font-size: 12px;
            color: #94a3b8;
            white-space: nowrap;
            margin-left: auto;
          }
          .guide-actions {
            padding: 0 20px 16px;
            display: flex;
            gap: 8px;
          }
          .guide-action-btn {
            padding: 8px 12px;
            background: #f1f5f9;
            border: none;
            border-radius: 8px;
            font-size: 13px;
            cursor: pointer;
            transition: all 0.2s;
            display: inline-flex;
            align-items: center;
            gap: 4px;
          }
          .guide-action-btn:hover {
            background: #e2e8f0;
          }
          .guide-action-btn:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
          .guide-action-btn:disabled:hover {
            background: #f1f5f9;
          }
          .guide-action-btn.favorite {
            color: #f59e0b;
          }
          .guide-action-btn.favorite.active {
            background: #fef3c7;
          }
          .guide-action-btn svg {
            width: 16px;
            height: 16px;
          }
          /* List View Styles for Projects Explorer */
          .guides-list-container {
            background: white;
            border-radius: 12px;
            border: 1px solid #e2e8f0;
            overflow: hidden;
          }
          .guides-list-header {
            display: grid;
            grid-template-columns: 2.5fr 0.5fr 1.5fr 1fr 0.5fr 0.7fr 1.2fr;
            gap: 10px;
            padding: 10px 20px;
            background: #f8fafc;
            border-bottom: 1px solid #e2e8f0;
            font-size: 11.5px;
            font-weight: 600;
            color: #64748b;
            text-transform: uppercase;
            letter-spacing: 0.4px;
          }
          /* Sortable column headers — make the <button> look like a header
             cell but with a hover state + arrow to signal "click to sort".
             The button inherits the parent grid cell's positioning via
             the gl-col-* class the caller passes in. */
          .guides-list-header .gl-sortable {
            background: transparent;
            border: 0;
            padding: 0;
            font: inherit;
            color: inherit;
            text-transform: inherit;
            letter-spacing: inherit;
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            gap: 4px;
            width: 100%;
            text-align: left;
            transition: color 0.12s;
          }
          .guides-list-header .gl-sortable:hover { color: ${theme.primary}; }
          .guides-list-header .gl-sortable-active { color: ${theme.primary}; }
          .guides-list-header .gl-sort-arrow {
            display: inline-flex; align-items: center; justify-content: center;
            width: 12px; height: 12px; flex-shrink: 0;
          }
          .guides-list-header .gl-sortable:focus-visible {
            outline: 2px solid ${theme.primary}66;
            outline-offset: 2px;
            border-radius: 3px;
          }
          .guides-list-body {
            max-height: calc(100vh - 300px);
            overflow-y: auto;
          }
          .guides-list-body::-webkit-scrollbar { width: 6px; }
          .guides-list-body::-webkit-scrollbar-track { background: transparent; }
          .guides-list-body::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
          .guides-list-row {
            display: grid;
            grid-template-columns: 2.5fr 0.5fr 1.5fr 1fr 0.5fr 0.7fr 1.2fr;
            gap: 10px;
            padding: 12px 20px;
            align-items: center;
            border-bottom: 1px solid #f1f5f9;
            cursor: pointer;
            transition: background 0.15s;
          }
          .guides-list-row:hover { background: ${theme.primary}04; }
          .guides-list-row:last-child { border-bottom: none; }
          .guides-list-row.selected { background: ${theme.primary}08; box-shadow: inset 3px 0 0 ${theme.primary}; }
          .gl-col-name { display: flex; align-items: center; gap: 10px; min-width: 0; }
          .gl-favicon { width: 32px; height: 32px; background: white; border-radius: 8px; display: flex; align-items: center; justify-content: center; box-shadow: 0 1px 4px rgba(0,0,0,0.06); border: 1px solid #e2e8f0; flex-shrink: 0; }
          .gl-favicon img { width: 20px; height: 20px; object-fit: contain; }
          .gl-favicon-fallback { width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; color: #94a3b8; }
          .gl-favicon-fallback svg { width: 16px; height: 16px; }
          .gl-name-text { font-size: 14px; font-weight: 600; color: #1e293b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 1; min-width: 0; }
          .gl-col-owner { font-size: 12px; color: #64748b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
          .gl-col-domain { font-size: 13px; color: #64748b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
          .gl-col-steps { font-size: 13px; color: #64748b; font-weight: 500; }
          .gl-col-source { display: flex; align-items: center; }
          .gl-source-badge { display: inline-flex; align-items: center; padding: 3px 6px; border-radius: 6px; font-size: 11px; color: #64748b; background: #f1f5f9; }
          .gl-source-badge.desktop { color: #7c3aed; background: rgba(124, 58, 237, 0.08); }
          .gl-source-badge.extension { color: #6366f1; background: rgba(99, 102, 241, 0.08); }
          .gl-source-badge svg { width: 14px; height: 14px; }
          .gl-col-date { font-size: 12px; color: #94a3b8; white-space: nowrap; }
          .gl-col-actions { display: flex; align-items: center; gap: 4px; }
          .gl-col-actions .guide-action-btn { padding: 5px 8px; font-size: 12px; }
          .gl-col-actions .guide-action-btn svg { width: 14px; height: 14px; }
          .guide-checkbox-list { width: 20px; height: 20px; background: white; border: 2px solid #d1d5db; border-radius: 5px; display: flex; align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0; margin-right: 4px; }
          .guide-checkbox-list:hover { border-color: #64748b; }
          .guide-checkbox-list.checked { background: #f1f5f9; border-color: #64748b; }
          .guide-checkbox-list.checked svg { color: #64748b; }
          /* Custom Dialog Styles */
          .custom-dialog-overlay {
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 10000;
            animation: dialogFadeIn 0.2s ease;
          }
          @keyframes dialogFadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          .custom-dialog {
            background: white;
            border-radius: 16px;
            width: 400px;
            max-width: 90vw;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
            animation: dialogSlideIn 0.2s ease;
            overflow: hidden;
          }
          @keyframes dialogSlideIn {
            from { transform: scale(0.95) translateY(-10px); opacity: 0; }
            to { transform: scale(1) translateY(0); opacity: 1; }
          }
          .custom-dialog-header {
            padding: 20px 24px 16px;
            display: flex;
            align-items: center;
            gap: 12px;
          }
          .custom-dialog-icon {
            width: 36px;
            height: 36px;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
          }
          .custom-dialog-icon.default {
            background: #eef2ff;
            color: #4f46e5;
          }
          .custom-dialog-icon.danger {
            background: #fef2f2;
            color: #dc2626;
          }
          .custom-dialog-icon.prompt {
            background: #ecfdf5;
            color: #059669;
          }
          .custom-dialog-title {
            font-size: 18px;
            font-weight: 600;
            color: #1e293b;
            margin: 0;
          }
          .custom-dialog-body {
            padding: 0 24px 20px;
          }
          .custom-dialog-message {
            font-size: 14px;
            color: #64748b;
            line-height: 1.5;
            margin: 0;
          }
          .custom-dialog-input {
            width: 100%;
            padding: 12px 16px;
            border: 2px solid #e2e8f0;
            border-radius: 10px;
            font-size: 15px;
            transition: all 0.2s;
            box-sizing: border-box;
            font-family: inherit;
          }
          .custom-dialog-input:focus {
            outline: none;
            border-color: #64748b;
            box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
          }
          .custom-dialog-footer {
            padding: 16px 24px;
            background: #f8fafc;
            display: flex;
            justify-content: flex-end;
            gap: 12px;
          }
          .custom-dialog-btn {
            padding: 10px 20px;
            border-radius: 10px;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
            border: none;
          }
          .custom-dialog-btn-cancel {
            background: white;
            color: #64748b;
            border: 1px solid #e2e8f0;
          }
          .custom-dialog-btn-cancel:hover {
            background: #f1f5f9;
            color: #475569;
          }
          .custom-dialog-btn-confirm {
            background: ${theme.gradient};
            color: white;
          }
          .custom-dialog-btn-confirm:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
          }
          .custom-dialog-btn-confirm:disabled {
            opacity: 0.5;
            cursor: not-allowed;
            transform: none;
            box-shadow: none;
          }
          .custom-dialog-btn-danger {
            background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
            color: white;
          }
          .custom-dialog-btn-danger:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
          }
        `}</style>

        <div className="projects-layout">
          <div className="projects-list">
            <div className="projects-list-header">
              <span>All Projects</span>
              {onCreateProject && (
                <button
                  className="project-add-btn"
                  onClick={openCreateProjectModal}
                  title="New Project"
                >
                  +
                </button>
              )}
            </div>
            {projects.length === 0 ? (
              loadingGuides ? (
                <div style={{ padding: '12px 0', display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[1,2,3,4].map(i => (
                    <div key={i} className="skeleton-block" style={{ height: 48, borderRadius: 8, background: 'linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%)', backgroundSize: '200% 100%', animation: 'skeleton-shine 1.4s ease-in-out infinite' }} />
                  ))}
                </div>
              ) : (
                <div className="empty-state" style={{ padding: '40px 20px' }}>
                  <div className="empty-state-icon">{Icons.folderOpen}</div>
                  <h3>No projects yet</h3>
                  <p>Click + to create your first project</p>
                </div>
              )
            ) : (
              projects.map(project => (
                <div
                  key={project.id}
                  className={`project-item ${selectedProject?.id === project.id ? 'active' : ''}`}
                  onClick={() => { setSelectedProject(project); setSearchQuery(''); setSourceFilter('all'); setOwnerFilter('all'); setOwnershipFilter('all'); }}
                >
                  <div className="project-item-content">
                    <div className="project-item-name">
                      <span style={{ display: 'inline-flex', flexShrink: 0 }}>{React.cloneElement(Icons.folder, { width: 16, height: 16 })}</span>
                      <span className="project-item-name-text" title={project.name}>{project.name}</span>
                    </div>
                    <div className="project-item-count">
                      {guides.filter(g => g.projectId === project.id).length} guides
                    </div>
                  </div>
                  {!project.isDefault && onUpdateProject && onDeleteProject && (
                    <div className="project-item-actions">
                      <button
                        className="project-item-action-btn"
                        onClick={(e) => { e.stopPropagation(); openEditProjectModal(project); }}
                        title="Edit project"
                      >
                        {Icons.edit}
                      </button>
                      <button
                        className="project-item-action-btn danger"
                        onClick={(e) => { e.stopPropagation(); handleProjectDelete(project.id); }}
                        title="Delete project"
                      >
                        {Icons.trash}
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          <div className="project-guides">
            {projects.length === 0 ? (
              loadingGuides ? (
                <div style={{ padding: 32 }}><GuideSkeletonGrid count={3} /></div>
              ) : (
                <div className="empty-state">
                  <div className="empty-state-icon">{Icons.folderOpen}</div>
                  <h3>No projects yet</h3>
                  <p>Create a project using the + button to organize your guides</p>
                </div>
              )
            ) : !selectedProject ? (
              <div className="empty-state">
                <div className="empty-state-icon">{Icons.arrowLeft}</div>
                <h3>Select a project</h3>
                <p>Choose a project from the list to view its guides</p>
              </div>
            ) : (
              <>
                <div className="project-guides-header">
                  <div className="project-guides-header-row">
                    <div>
                      <h2 className="project-guides-title">{selectedProject.name}</h2>
                      <p className="project-guides-desc">{selectedProject.description || 'No description'}</p>
                    </div>
                    {projectGuides.length > 0 && viewMode === 'tile' && !selectMode && (
                      <button
                        className="project-guides-settings-btn"
                        onClick={startMoveMode}
                        title="Select guides"
                      >
                        {Icons.settings}
                      </button>
                    )}
                  </div>
                </div>
                {/* Selection Toolbar — appears whenever any guides are selected (list view checkboxes are always visible) or grid view enters select mode */}
                {(selectMode || selectedGuides.length > 0) && (
                  <div className="project-select-toolbar">
                    <span className="project-select-toolbar-info">
                      {selectedGuides.length} of {projectGuides.length} selected
                    </span>
                    <button className="project-select-all-btn" onClick={selectAll}>
                      {selectedGuides.length === projectGuides.length ? 'Deselect All' : 'Select All'}
                    </button>
                    <div className="project-select-toolbar-actions">
                      <select
                        className="project-action-dropdown"
                        value={selectedAction}
                        onChange={(e) => { setSelectedAction(e.target.value); setMoveToProjectId(''); }}
                      >
                        <option value="">Select action...</option>
                        {otherProjects.length > 0 && <option value="move">Move to Project</option>}
                        <option value="delete">Delete</option>
                      </select>
                      {selectedAction === 'move' && (
                        <select
                          className="project-move-dropdown"
                          value={moveToProjectId}
                          onChange={(e) => setMoveToProjectId(e.target.value)}
                        >
                          <option value="">Select project...</option>
                          {otherProjects.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                      )}
                      <button
                        className={`project-apply-btn ${selectedAction === 'delete' ? 'danger' : ''}`}
                        onClick={handleApply}
                        disabled={
                          selectedGuides.length === 0 ||
                          isMoving ||
                          !selectedAction ||
                          (selectedAction === 'move' && !moveToProjectId)
                        }
                      >
                        {isMoving ? 'Applying...' : 'Apply'}
                      </button>
                      <button className="project-cancel-btn" onClick={cancelMoveMode}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                {projectGuidesRaw.length > 0 && (
                  <GuideFilterBar
                    searchQuery={searchQuery} setSearchQuery={setSearchQuery}
                    sourceFilter={sourceFilter} setSourceFilter={setSourceFilter}
                    ownerFilter={ownerFilter} setOwnerFilter={ownerNames.length > 1 ? setOwnerFilter : null}
                    ownershipFilter={ownershipFilter} setOwnershipFilter={setOwnershipFilter}
                    sortBy={sortBy} setSortBy={setSortBy}
                    ownerNames={ownerNames} userName={user?.name}
                    resultCount={projectGuides.length}
                    hasActiveFilters={searchQuery || sourceFilter !== 'all' || ownerFilter !== 'all'}
                    viewMode={viewMode} setViewMode={handleSetViewMode}
                  />
                )}
                {projectGuidesRaw.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-state-icon">{Icons.inbox}</div>
                    <h3>No guides in this project</h3>
                    <p>Create a guide and assign it to this project</p>
                  </div>
                ) : projectGuides.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-state-icon">{Icons.search}</div>
                    <h3>No matching guides</h3>
                    <p>Try adjusting your search or filters</p>
                  </div>
                ) : viewMode === 'list' ? (
                  <div className="guides-list-container">
                    <div className="guides-list-header">
                      <SortableColumnHeader column="name"     label="Name"     className="gl-col-name"    activeColumn={listSort.column} activeDirection={listSort.direction} onSort={(c,d) => setListSort({ column: c, direction: d })} />
                      <SortableColumnHeader column="steps"    label="Steps"    className="gl-col-steps"   activeColumn={listSort.column} activeDirection={listSort.direction} defaultDir="desc" onSort={(c,d) => setListSort({ column: c, direction: d })} />
                      <SortableColumnHeader column="domain"   label="Domain"   className="gl-col-domain"  activeColumn={listSort.column} activeDirection={listSort.direction} onSort={(c,d) => setListSort({ column: c, direction: d })} />
                      <SortableColumnHeader column="owner"    label="Owner"    className="gl-col-owner"   activeColumn={listSort.column} activeDirection={listSort.direction} onSort={(c,d) => setListSort({ column: c, direction: d })} />
                      <SortableColumnHeader column="source"   label="Source"   className="gl-col-source"  activeColumn={listSort.column} activeDirection={listSort.direction} onSort={(c,d) => setListSort({ column: c, direction: d })} />
                      <SortableColumnHeader column="modified" label="Modified" className="gl-col-date"    activeColumn={listSort.column} activeDirection={listSort.direction} defaultDir="desc" onSort={(c,d) => setListSort({ column: c, direction: d })} />
                      <span className="gl-col-actions">Actions</span>
                    </div>
                    <div className="guides-list-body">
                      {projectGuides.map(guide => (
                        <div
                          key={guide.id}
                          className={`guides-list-row ${selectedGuides.includes(guide.id) ? 'selected' : ''}`}
                          onClick={() => onSelectGuide(guide.id)}
                        >
                          <span className="gl-col-name">
                            <div className={`guide-checkbox-list ${selectedGuides.includes(guide.id) ? 'checked' : ''}`} onClick={(e) => { e.stopPropagation(); toggleGuideSelection(guide.id); }}>
                              {selectedGuides.includes(guide.id) && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                            </div>
                            <span className="gl-favicon">
                              {(() => { const f = getFaviconWithFallback(guide); return f ? <img src={f} alt="" onError={(e) => { handleFaviconError(e); e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }} /> : null; })()}
                              <span className="gl-favicon-fallback" style={{ display: getFaviconWithFallback(guide) ? 'none' : 'flex' }}>{Icons.globe}</span>
                            </span>
                            <span className="gl-name-text" title={guide.name}>{guide.name}</span>
                          </span>
                          <span className="gl-col-steps">{guide.stepCount ?? guide.steps?.length ?? 0}</span>
                          <span className="gl-col-domain" title={guide.domain}>{guide.domain || '-'}</span>
                          <span className="gl-col-owner" title={guide.creatorName}>{guide.creatorName || '-'}</span>
                          <span className="gl-col-source">
                            <span className={`gl-source-badge ${guide.source === 'desktop' ? 'desktop' : 'extension'}`}>{guide.source === 'desktop' ? Icons.desktop : Icons.chrome}</span>
                            <MeetingBadge guide={guide} compact />
                          </span>
                          <span className="gl-col-date" title={guide.updatedAt ? formatGuideDateTooltip(guide.updatedAt) : ''}>{guide.updatedAt ? formatGuideDateWithTime(guide.updatedAt) : '-'}</span>
                          <span className="gl-col-actions" onClick={(e) => e.stopPropagation()}>
                            <button className={`guide-action-btn favorite ${favoriteGuideIds.includes(guide.id) ? 'active' : ''}`} onClick={() => onToggleFavorite(guide.id)}>{favoriteGuideIds.includes(guide.id) ? Icons.starFilled : Icons.star}</button>
                            {user?.accountType === 'organization' && <button className="guide-action-btn guide-action-icon" style={{ color: theme.primary, ...(guide.shareCount > 0 ? { background: '#dbeafe' } : {}) }} onClick={() => onQuickShare?.(guide.id)} title="Share guide">{Icons.shareSmall}</button>}
                            <button className="guide-action-btn guide-action-icon" onClick={() => onExport(guide.id, 'pdf')} disabled={exportingGuide.id === guide.id}>{exportingGuide.id === guide.id && exportingGuide.format === 'pdf' ? <span className="btn-spinner-sm" /> : Icons.download}</button>
                            <button className="guide-action-btn guide-action-icon" style={{ color: '#ef4444' }} onClick={() => onDeleteGuide(guide.id)} disabled={deletingGuideId === guide.id}>{deletingGuideId === guide.id ? '...' : Icons.trash}</button>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="guides-grid">
                    {projectGuides.map(guide => (
                      <div
                        key={guide.id}
                        className={`guide-card ${selectedGuides.includes(guide.id) ? 'selected' : ''}`}
                        onClick={() => selectMode ? toggleGuideSelection(guide.id) : onSelectGuide(guide.id)}
                      >
                        {/* Checkbox in select mode */}
                        {selectMode && (
                          <div
                            className={`guide-checkbox ${selectedGuides.includes(guide.id) ? 'checked' : ''}`}
                            onClick={(e) => { e.stopPropagation(); toggleGuideSelection(guide.id); }}
                          >
                            {selectedGuides.includes(guide.id) && (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12"></polyline>
                              </svg>
                            )}
                          </div>
                        )}
                        <div className="guide-preview">
                          <div className="guide-preview-favicon">
                            {(() => {
                              const faviconUrl = getFaviconWithFallback(guide);
                              return faviconUrl ? (
                                <img src={faviconUrl} alt="" onError={(e) => { handleFaviconError(e); e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }} />
                              ) : null;
                            })()}
                            <span className="guide-preview-icon-svg" style={{ display: getFaviconWithFallback(guide) ? 'none' : 'flex' }}>{Icons.globe}</span>
                          </div>
                          {guide.domain && <span className="guide-domain">{guide.domain}</span>}
                          <span className={`guide-source-badge ${guide.source === 'desktop' ? 'desktop' : 'extension'}`} title={guide.source === 'desktop' ? 'Created with Desktop App' : 'Created with Chrome Extension'}>
                            {guide.source === 'desktop' ? Icons.desktop : Icons.chrome}
                          </span>
                          <MeetingBadge guide={guide} />
                        </div>
                        <div className="guide-info">
                          <h3 className="guide-name">{guide.name}</h3>
                          <p className="guide-steps">
                    <span>{guide.stepCount ?? guide.steps?.length ?? 0} steps</span>
                    {guide.updatedAt && <span className="guide-modified" title={formatGuideDateTooltip(guide.updatedAt)}>{formatGuideDateShort(guide.updatedAt)}</span>}
                  </p>
                  {guide.creatorName && (
                    <div className="guide-meta-row">
                      <span /><span className="guide-owner-name" title={guide.creatorName}>{guide.creatorName}</span>
                    </div>
                  )}
                        </div>
                        <div className="guide-actions">
                          <button
                            className={`guide-action-btn favorite ${favoriteGuideIds.includes(guide.id) ? 'active' : ''}`}
                            onClick={(e) => { e.stopPropagation(); onToggleFavorite(guide.id); }}
                          >
                            {favoriteGuideIds.includes(guide.id) ? Icons.starFilled : Icons.star}
                          </button>
                          {user?.accountType === 'organization' && (
                            <button
                              className="guide-action-btn guide-action-icon"
                              onClick={(e) => { e.stopPropagation(); onQuickShare?.(guide.id); }}
                              title="Share guide"
                              style={{ color: theme.primary, ...(guide.shareCount > 0 ? { background: '#dbeafe' } : {}) }}
                            >
                              {Icons.shareSmall}
                            </button>
                          )}
                          <button className="guide-action-btn guide-action-icon" onClick={(e) => { e.stopPropagation(); onExport(guide.id, 'pdf'); }} disabled={exportingGuide.id === guide.id}>
                            {exportingGuide.id === guide.id && exportingGuide.format === 'pdf' ? <span className="btn-spinner-sm" /> : Icons.download} {exportingGuide.id === guide.id && exportingGuide.format === 'pdf' ? '...' : 'PDF'}
                          </button>
                          <button
                            className="guide-action-btn guide-action-icon"
                            style={{ color: '#ef4444' }}
                            onClick={(e) => { e.stopPropagation(); onDeleteGuide(guide.id); }}
                            disabled={deletingGuideId === guide.id}
                            title="Delete guide"
                          >
                            {deletingGuideId === guide.id ? '...' : Icons.trash}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Project Modal */}
      {showProjectModal && (
        <div className="custom-dialog-overlay" onClick={() => setShowProjectModal(false)}>
          <div className="custom-dialog" onClick={e => e.stopPropagation()} style={{ maxWidth: '380px' }}>
            <div className="custom-dialog-header">
              <div className="custom-dialog-icon prompt">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                  {!editingProject && <line x1="12" y1="11" x2="12" y2="17"/>}
                  {!editingProject && <line x1="9" y1="14" x2="15" y2="14"/>}
                </svg>
              </div>
              <h3 className="custom-dialog-title">{editingProject ? 'Edit Project' : 'New Project'}</h3>
            </div>
            <div className="custom-dialog-body">
              <p className="custom-dialog-message" style={{ marginBottom: '16px' }}>
                {editingProject ? 'Update your project details below.' : 'Create a new project to organize your guides.'}
              </p>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                  Project Name
                </label>
                <input
                  type="text"
                  className="custom-dialog-input"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  placeholder="e.g., Marketing Guides"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newProjectName.trim() && !savingProject) {
                      handleSaveProject();
                    } else if (e.key === 'Escape') {
                      setShowProjectModal(false);
                    }
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                  Description (optional)
                </label>
                <textarea
                  className="custom-dialog-input"
                  value={newProjectDescription}
                  onChange={(e) => setNewProjectDescription(e.target.value)}
                  placeholder="Brief description of this project"
                  rows={3}
                  style={{ resize: 'vertical' }}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setShowProjectModal(false);
                    }
                  }}
                />
              </div>
            </div>
            <div className="custom-dialog-footer">
              <button
                className="custom-dialog-btn custom-dialog-btn-cancel"
                onClick={() => setShowProjectModal(false)}
              >
                Cancel
              </button>
              <button
                className="custom-dialog-btn custom-dialog-btn-confirm"
                onClick={handleSaveProject}
                disabled={!newProjectName.trim() || savingProject}
              >
                {savingProject ? 'Saving...' : (editingProject ? 'Save Changes' : 'Create Project')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Guides List Page (reusable for My Guides, Shared, Favorites)
function GuidesListPage({ title, icon, guides, onSelectGuide, onDeleteGuide, deletingGuideId, onExport, exportingGuide, favoriteGuideIds, onToggleFavorite, projects, emptyMessage, emptyIcon, showDelete = true, onMoveGuides, showConfirm, showToast, loadingGuides, onQuickShare, user, sourceFilter, setSourceFilter, ownershipFilter, setOwnershipFilter, projectFilter, setProjectFilter, sortBy, setSortBy, searchQuery, setSearchQuery }) {
  const projectMap = useMemo(() => projects.reduce((acc, p) => ({ ...acc, [p.id]: p }), {}), [projects]);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedGuides, setSelectedGuides] = useState([]);
  const [selectedAction, setSelectedAction] = useState('');
  const [moveToProjectId, setMoveToProjectId] = useState('');
  const [isMoving, setIsMoving] = useState(false);
  const [ownerFilter, setOwnerFilter] = useState('all');
  // Per-column sort for the list view — same shape + semantics as
  // ProjectsExplorerPage. `null` column ⇒ fall back to Filters-panel sortBy.
  const [listSort, setListSort] = useState({ column: null, direction: 'asc' });

  // Prune selection of any IDs no longer present in the guides list (e.g.,
  // after a single-row delete via the trash icon — prevents "1 of 0 selected").
  useEffect(() => {
    setSelectedGuides(prev => {
      const ids = new Set(guides.map(g => g.id));
      const filtered = prev.filter(id => ids.has(id));
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [guides]);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('guideViewMode') || 'tile');
  const handleSetViewMode = (mode) => { setViewMode(mode); localStorage.setItem('guideViewMode', mode); };

  const ownerNames = useMemo(() => {
    const names = new Set();
    guides.forEach(g => {
      const name = g.creatorName || g.ownerName;
      if (name) names.add(name);
    });
    return [...names].sort();
  }, [guides]);

  const filteredGuides = useMemo(() => {
    let result = guides;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(g => g.name?.toLowerCase().includes(q) || g.domain?.toLowerCase().includes(q));
    }
    if (sourceFilter !== 'all') {
      result = result.filter(g => g.source === sourceFilter);
    }
    if (ownerFilter !== 'all') {
      result = result.filter(g => (g.creatorName || g.ownerName) === ownerFilter);
    }
    if (ownershipFilter && ownershipFilter !== 'all') {
      result = result.filter(g => (g.creatorName || g.ownerName) === ownershipFilter);
    }
    if (projectFilter !== 'all') {
      result = result.filter(g => g.projectId === projectFilter);
    }
    if (listSort.column) {
      result = sortGuidesByColumn(result, listSort.column, listSort.direction, projectMap);
    } else {
      result = [...result].sort((a, b) => {
        if (sortBy === 'newest') return new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt);
        if (sortBy === 'oldest') return new Date(a.updatedAt || a.createdAt) - new Date(b.updatedAt || b.createdAt);
        if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
        if (sortBy === 'steps') return (b.stepCount ?? b.steps?.length ?? 0) - (a.stepCount ?? a.steps?.length ?? 0);
        return 0;
      });
    }
    return result;
  }, [guides, searchQuery, sourceFilter, ownerFilter, ownershipFilter, projectFilter, sortBy, listSort, projectMap, user]);

  const hasActiveFilters = searchQuery || sourceFilter !== 'all' || ownerFilter !== 'all' || ownershipFilter !== 'all' || projectFilter !== 'all';

  const startMoveMode = () => {
    setSelectMode(true);
    setSelectedGuides([]);
    setSelectedAction('');
    setMoveToProjectId('');
  };

  const cancelMoveMode = () => {
    setSelectMode(false);
    setSelectedGuides([]);
    setSelectedAction('');
    setMoveToProjectId('');
  };

  const toggleGuideSelection = (guideId) => {
    setSelectedGuides(prev =>
      prev.includes(guideId)
        ? prev.filter(id => id !== guideId)
        : [...prev, guideId]
    );
  };

  const selectAll = () => {
    if (selectedGuides.length === guides.length) {
      setSelectedGuides([]);
    } else {
      setSelectedGuides(guides.map(g => g.id));
    }
  };

  const handleBulkMove = async () => {
    if (!moveToProjectId || selectedGuides.length === 0 || !onMoveGuides) return;
    setIsMoving(true);
    const success = await onMoveGuides(selectedGuides, moveToProjectId);
    if (success) {
      cancelMoveMode();
    }
    setIsMoving(false);
  };

  const handleBulkDelete = async () => {
    if (selectedGuides.length === 0 || !onDeleteGuide) return;
    const count = selectedGuides.length;
    const message = `Are you sure you want to delete ${count} guide${count > 1 ? 's' : ''}? This action cannot be undone.`;

    showConfirm('Delete Guides', message, async () => {
      setIsMoving(true);
      const idsToDelete = [...selectedGuides];
      for (const guideId of idsToDelete) {
        await onDeleteGuide(guideId, true, true); // skipConfirm + silent (suppress per-guide toasts)
      }
      cancelMoveMode();
      setIsMoving(false);
      if (showToast) showToast(`Deleted ${idsToDelete.length} guide${idsToDelete.length !== 1 ? 's' : ''}`, 'success');
    }, { type: 'danger', confirmText: 'Delete' });
  };

  const handleApply = async () => {
    if (selectedAction === 'move') {
      await handleBulkMove();
    } else if (selectedAction === 'delete') {
      handleBulkDelete();
    }
  };

  return (
    <>
      <style>{`
        .guides-list-page .guide-filters-bar {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }
        .guides-list-page .guide-search-wrapper {
          position: relative;
          flex: 1;
          min-width: 200px;
          max-width: 400px;
        }
        .guides-list-page .guide-search-wrapper svg {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
          width: 16px;
          height: 16px;
        }
        .guides-list-page .guide-search-input {
          width: 100%;
          padding: 9px 12px 9px 38px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 14px;
          color: #1e293b;
          background: white;
          transition: all 0.2s;
        }
        .guides-list-page .guide-search-input:focus {
          outline: none;
          border-color: ${theme.primary};
          box-shadow: 0 0 0 3px ${theme.primary}15;
        }
        .guides-list-page .guide-search-input::placeholder {
          color: #94a3b8;
        }
        .guides-list-page .guide-filter-select {
          padding: 9px 12px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 13px;
          color: #1e293b;
          background: white;
          cursor: pointer;
        }
        .guides-list-page .guide-filter-select:focus {
          outline: none;
          border-color: ${theme.primary};
          box-shadow: 0 0 0 3px ${theme.primary}15;
        }
        .guides-list-page .guide-filter-count {
          font-size: 13px;
          color: #94a3b8;
          margin-left: auto;
          white-space: nowrap;
        }
        .guides-list-page .guides-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 20px;
        }
        .guides-list-page .guide-card {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          border: 1px solid #e2e8f0;
          transition: all 0.2s;
          cursor: pointer;
        }
        .guides-list-page .guide-card:hover {
          transform: translateY(-2px);
          border-color: ${theme.primary}30;
          box-shadow: 0 8px 24px ${theme.primary}10;
        }
        .guides-list-page .guide-preview {
          background: linear-gradient(145deg, ${theme.primary}15 0%, ${theme.secondary}0a 60%, #f1f5f9 100%);
          padding: 24px;
          display: flex;
          align-items: center;
          gap: 16px;
          border-bottom: 1px solid #e2e8f0;
        }
        .guides-list-page .guide-preview-favicon {
          width: 48px;
          height: 48px;
          background: white;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }
        .guides-list-page .guide-preview-favicon img {
          width: 32px;
          height: 32px;
          object-fit: contain;
        }
        .guides-list-page .guide-preview-icon-svg {
          width: 48px;
          height: 48px;
          background: white;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          color: #94a3b8;
        }
        .guides-list-page .guide-preview-icon-svg svg {
          width: 24px;
          height: 24px;
        }
        .guides-list-page .guide-domain {
          font-size: 12px;
          color: #64748b;
          background: white;
          padding: 4px 10px;
          border-radius: 20px;
          max-width: 140px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .guides-list-page .guide-preview-favicon,
        .guides-list-page .guide-preview-icon-svg {
          flex-shrink: 0;
        }
        .guides-list-page .guide-source-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 11px;
          background: rgba(255,255,255,0.9);
          color: #64748b;
          flex-shrink: 0;
          margin-left: auto;
        }
        .guides-list-page .guide-source-badge.desktop {
          color: #7c3aed;
          background: rgba(124, 58, 237, 0.1);
        }
        .guides-list-page .guide-source-badge.extension {
          color: #6366f1;
          background: rgba(99, 102, 241, 0.1);
        }
        .guides-list-page .guide-info {
          padding: 16px 20px;
        }
        .guides-list-page .guide-name {
          font-size: 16px;
          font-weight: 600;
          color: #1e293b;
          margin: 0 0 6px 0;
        }
        .guides-list-page .guide-steps {
          font-size: 14px;
          color: #64748b;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .guides-list-page .guide-modified {
          font-size: 12px;
          color: #94a3b8;
          white-space: nowrap;
          margin-left: auto;
        }
        .guides-list-page .guide-meta-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          margin-top: 8px;
        }
        .guides-list-page .guide-project-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          color: ${theme.primary};
          background: #f1f5f9;
          padding: 4px 10px;
          border-radius: 12px;
          font-weight: 500;
        }
        .guides-list-page .guide-project-badge-icon {
          display: inline-flex;
          align-items: center;
        }
        .guides-list-page .guide-owner-name {
          font-size: 11px;
          color: #94a3b8;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 120px;
        }
        .guides-list-page .guide-actions {
          padding: 0 20px 16px;
          display: flex;
          gap: 8px;
        }
        .guides-list-page .guide-action-btn {
          padding: 8px 12px;
          background: #f1f5f9;
          border: none;
          border-radius: 8px;
          font-size: 13px;
          cursor: pointer;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }
        .guides-list-page .guide-action-btn:hover {
          background: #e2e8f0;
        }
        .guides-list-page .guide-action-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .guides-list-page .guide-action-btn:disabled:hover {
          background: #f1f5f9;
        }
        .guides-list-page .guide-action-btn.favorite {
          color: #f59e0b;
        }
        .guides-list-page .guide-action-btn.favorite.active {
          background: #fef3c7;
        }
        .guides-list-page .guide-action-btn svg {
          width: 16px;
          height: 16px;
        }
        .guides-list-page .empty-state {
          text-align: center;
          padding: 60px 20px;
          color: #64748b;
        }
        .guides-list-page .empty-state-icon {
          margin-bottom: 16px;
          color: #cbd5e1;
          display: flex;
          justify-content: center;
        }
        .guides-list-page .empty-state-icon svg {
          width: 48px;
          height: 48px;
        }
        .guides-list-page .empty-state h3 {
          font-size: 18px;
          color: #1e293b;
          margin: 0 0 8px 0;
        }
        .guides-list-page .title-icon {
          color: #64748b;
        }
        .guides-list-page .title-icon svg {
          width: 24px;
          height: 24px;
        }
        .guides-list-page .header-settings-btn {
          width: 40px;
          height: 40px;
          background: #f1f5f9;
          border: none;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #64748b;
          transition: all 0.2s;
        }
        .guides-list-page .header-settings-btn:hover {
          background: #e2e8f0;
          color: #1e293b;
        }
        .guides-list-page .header-settings-btn svg {
          width: 20px;
          height: 20px;
        }
        .guides-list-page .select-toolbar {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 20px;
          background: white;
          border-radius: 12px;
          margin-bottom: 20px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          border: 1px solid #e2e8f0;
        }
        .guides-list-page .select-toolbar-info {
          font-weight: 500;
          font-size: 14px;
          color: #334155;
        }
        .guides-list-page .select-toolbar-actions {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-left: auto;
        }
        .guides-list-page .select-all-btn {
          padding: 8px 14px;
          background: #f1f5f9;
          border: none;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          color: #475569;
        }
        .guides-list-page .select-all-btn:hover {
          background: #e2e8f0;
        }
        .guides-list-page .action-dropdown,
        .guides-list-page .project-dropdown {
          padding: 8px 12px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          font-size: 13px;
          min-width: 150px;
          color: #334155;
          background: white;
        }
        .guides-list-page .move-btn {
          padding: 8px 16px;
          background: ${theme.gradient};
          border: none;
          border-radius: 8px;
          color: white;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
        }
        .guides-list-page .move-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .guides-list-page .move-btn.danger {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
        }
        .guides-list-page .cancel-btn {
          padding: 8px 14px;
          background: transparent;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 13px;
          cursor: pointer;
          color: #64748b;
        }
        .guides-list-page .guide-card {
          position: relative;
        }
        .guides-list-page .guide-checkbox {
          position: absolute;
          top: 12px;
          right: 12px;
          width: 24px;
          height: 24px;
          background: white;
          border: 2px solid #d1d5db;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 10;
          transition: all 0.2s;
        }
        .guides-list-page .guide-checkbox:hover {
          border-color: #64748b;
        }
        .guides-list-page .guide-checkbox.checked {
          background: #f1f5f9;
          border-color: #64748b;
        }
        .guides-list-page .guide-checkbox.checked svg {
          color: #64748b;
        }
        .guides-list-page .guide-card.selected {
          box-shadow: 0 0 0 2px ${theme.primary}, 0 8px 25px rgba(0,0,0,0.1);
        }
        /* List View Styles */
        .guides-list-page .guides-list-container {
          background: white;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
        }
        .guides-list-page .guides-list-header {
          display: grid;
          grid-template-columns: 2.5fr 0.5fr 1.5fr 1fr 1fr 0.5fr 0.7fr 1.2fr;
          gap: 10px;
          padding: 10px 20px;
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
          font-size: 11.5px;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.4px;
        }
        /* Sortable column-header buttons — same visual as ProjectsExplorer.
           Prefix-scoped to .guides-list-page so the rules only fire on
           My Guides / Favorites / Shared With Me. */
        .guides-list-page .guides-list-header .gl-sortable {
          background: transparent;
          border: 0;
          padding: 0;
          font: inherit;
          color: inherit;
          text-transform: inherit;
          letter-spacing: inherit;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          width: 100%;
          text-align: left;
          transition: color 0.12s;
        }
        .guides-list-page .guides-list-header .gl-sortable:hover { color: ${theme.primary}; }
        .guides-list-page .guides-list-header .gl-sortable-active { color: ${theme.primary}; }
        .guides-list-page .guides-list-header .gl-sort-arrow {
          display: inline-flex; align-items: center; justify-content: center;
          width: 12px; height: 12px; flex-shrink: 0;
        }
        .guides-list-page .guides-list-header .gl-sortable:focus-visible {
          outline: 2px solid ${theme.primary}66;
          outline-offset: 2px;
          border-radius: 3px;
        }
        .guides-list-page .guides-list-body {
          max-height: calc(100vh - 280px);
          overflow-y: auto;
        }
        .guides-list-page .guides-list-body::-webkit-scrollbar {
          width: 6px;
        }
        .guides-list-page .guides-list-body::-webkit-scrollbar-track {
          background: transparent;
        }
        .guides-list-page .guides-list-body::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }
        .guides-list-page .guides-list-body::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
        .guides-list-page .guides-list-row {
          display: grid;
          grid-template-columns: 2.5fr 0.5fr 1.5fr 1fr 1fr 0.5fr 0.7fr 1.2fr;
          gap: 10px;
          padding: 12px 20px;
          align-items: center;
          border-bottom: 1px solid #f1f5f9;
          cursor: pointer;
          transition: background 0.15s;
        }
        .guides-list-page .guides-list-row:hover {
          background: ${theme.primary}04;
        }
        .guides-list-page .guides-list-row:last-child {
          border-bottom: none;
        }
        .guides-list-page .guides-list-row.selected {
          background: ${theme.primary}08;
          box-shadow: inset 3px 0 0 ${theme.primary};
        }
        .guides-list-page .gl-col-name {
          display: flex;
          align-items: center;
          gap: 10px;
          min-width: 0;
        }
        .guides-list-page .gl-favicon {
          width: 32px;
          height: 32px;
          background: white;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 1px 4px rgba(0,0,0,0.06);
          border: 1px solid #e2e8f0;
          flex-shrink: 0;
        }
        .guides-list-page .gl-favicon img {
          width: 20px;
          height: 20px;
          object-fit: contain;
        }
        .guides-list-page .gl-favicon-fallback {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #94a3b8;
        }
        .guides-list-page .gl-favicon-fallback svg {
          width: 16px;
          height: 16px;
        }
        .guides-list-page .gl-name-text {
          font-size: 14px;
          font-weight: 600;
          color: #1e293b;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          flex: 1;
          min-width: 0;
        }
        .guides-list-page .gl-col-owner {
          font-size: 12px;
          color: #64748b;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .guides-list-page .gl-col-domain {
          font-size: 13px;
          color: #64748b;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          min-width: 0;
        }
        .guides-list-page .gl-col-steps {
          font-size: 13px;
          color: #64748b;
          font-weight: 500;
        }
        .guides-list-page .gl-col-source {
          display: flex;
          align-items: center;
        }
        .guides-list-page .gl-source-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 3px 6px;
          border-radius: 6px;
          font-size: 11px;
          color: #64748b;
          background: #f1f5f9;
        }
        .guides-list-page .gl-source-badge.desktop {
          color: #7c3aed;
          background: rgba(124, 58, 237, 0.08);
        }
        .guides-list-page .gl-source-badge.extension {
          color: #6366f1;
          background: rgba(99, 102, 241, 0.08);
        }
        .guides-list-page .gl-source-badge svg {
          width: 14px;
          height: 14px;
        }
        .guides-list-page .gl-col-project {
          font-size: 12px;
          color: #64748b;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          min-width: 0;
        }
        .guides-list-page .gl-project-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          font-size: 12px;
          color: ${theme.primary};
          font-weight: 500;
        }
        .guides-list-page .gl-project-badge svg {
          width: 12px;
          height: 12px;
        }
        .guides-list-page .gl-col-date {
          font-size: 12px;
          color: #94a3b8;
          white-space: nowrap;
        }
        .guides-list-page .gl-col-actions {
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .guides-list-page .gl-col-actions .guide-action-btn {
          padding: 5px 8px;
          font-size: 12px;
        }
        .guides-list-page .gl-col-actions .guide-action-btn svg {
          width: 14px;
          height: 14px;
        }
        .guides-list-page .guide-checkbox-list {
          width: 20px;
          height: 20px;
          background: white;
          border: 2px solid #d1d5db;
          border-radius: 5px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          flex-shrink: 0;
          margin-right: 4px;
          transition: all 0.2s;
        }
        .guides-list-page .guide-checkbox-list:hover { border-color: #64748b; }
        .guides-list-page .guide-checkbox-list.checked { background: #f1f5f9; border-color: #64748b; }
        .guides-list-page .guide-checkbox-list.checked svg { color: #64748b; }
      `}</style>
      <div className="main-header">
        <h1 className="main-header-title" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span className="title-icon">{icon}</span>
          {title}
        </h1>
        {guides.length > 0 && viewMode === 'tile' && (projects.length > 0 || showDelete) && !selectMode && (
          <button
            className="header-settings-btn"
            onClick={startMoveMode}
            title="Select guides"
          >
            {Icons.settings}
          </button>
        )}
      </div>
      <div className="page-content guides-list-page">
        {/* Selection Toolbar */}
        {(selectMode || selectedGuides.length > 0) && (
          <div className="select-toolbar">
            <span className="select-toolbar-info">
              {selectedGuides.length} of {guides.length} selected
            </span>
            <button className="select-all-btn" onClick={selectAll}>
              {selectedGuides.length === guides.length ? 'Deselect All' : 'Select All'}
            </button>
            <div className="select-toolbar-actions">
              <select
                className="action-dropdown"
                value={selectedAction}
                onChange={(e) => { setSelectedAction(e.target.value); setMoveToProjectId(''); }}
              >
                <option value="">Select action...</option>
                {projects.length > 0 && <option value="move">Move to Project</option>}
                {showDelete && <option value="delete">Delete</option>}
              </select>
              {selectedAction === 'move' && (
                <select
                  className="project-dropdown"
                  value={moveToProjectId}
                  onChange={(e) => setMoveToProjectId(e.target.value)}
                >
                  <option value="">Select project...</option>
                  {projects.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              )}
              <button
                className={`move-btn ${selectedAction === 'delete' ? 'danger' : ''}`}
                onClick={handleApply}
                disabled={
                  selectedGuides.length === 0 ||
                  isMoving ||
                  !selectedAction ||
                  (selectedAction === 'move' && !moveToProjectId)
                }
              >
                {isMoving ? 'Applying...' : 'Apply'}
              </button>
              <button className="cancel-btn" onClick={cancelMoveMode}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {guides.length > 0 && (
          <GuideFilterBar
            searchQuery={searchQuery} setSearchQuery={setSearchQuery}
            sourceFilter={sourceFilter} setSourceFilter={setSourceFilter}
            ownerFilter={ownerFilter} setOwnerFilter={ownerNames.length > 1 ? setOwnerFilter : null}
            ownershipFilter={ownershipFilter} setOwnershipFilter={setOwnershipFilter}
            projectFilter={projectFilter} setProjectFilter={projects.length > 0 ? setProjectFilter : null}
            sortBy={sortBy} setSortBy={setSortBy}
            projects={projects} ownerNames={ownerNames} userName={user?.name}
            resultCount={filteredGuides.length} hasActiveFilters={hasActiveFilters}
            viewMode={viewMode} setViewMode={handleSetViewMode}
          />
        )}

        {guides.length === 0 ? (
          loadingGuides ? (
            viewMode === 'list' ? <GuideSkeletonList count={6} /> : <GuideSkeletonGrid count={6} />
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">{emptyIcon || Icons.inbox}</div>
              <h3>{emptyMessage || 'No guides found'}</h3>
            </div>
          )
        ) : filteredGuides.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">{Icons.search}</div>
            <h3>No matching guides</h3>
            <p style={{ color: '#64748b', marginTop: 8 }}>Try adjusting your search or filters</p>
          </div>
        ) : viewMode === 'list' ? (
          <div className="guides-list-container">
            <div className="guides-list-header">
              <SortableColumnHeader column="name"     label="Name"     className="gl-col-name"    activeColumn={listSort.column} activeDirection={listSort.direction} onSort={(c,d) => setListSort({ column: c, direction: d })} />
              <SortableColumnHeader column="steps"    label="Steps"    className="gl-col-steps"   activeColumn={listSort.column} activeDirection={listSort.direction} defaultDir="desc" onSort={(c,d) => setListSort({ column: c, direction: d })} />
              <SortableColumnHeader column="domain"   label="Domain"   className="gl-col-domain"  activeColumn={listSort.column} activeDirection={listSort.direction} onSort={(c,d) => setListSort({ column: c, direction: d })} />
              <SortableColumnHeader column="project"  label="Project"  className="gl-col-project" activeColumn={listSort.column} activeDirection={listSort.direction} onSort={(c,d) => setListSort({ column: c, direction: d })} />
              <SortableColumnHeader column="owner"    label="Owner"    className="gl-col-owner"   activeColumn={listSort.column} activeDirection={listSort.direction} onSort={(c,d) => setListSort({ column: c, direction: d })} />
              <SortableColumnHeader column="source"   label="Source"   className="gl-col-source"  activeColumn={listSort.column} activeDirection={listSort.direction} onSort={(c,d) => setListSort({ column: c, direction: d })} />
              <SortableColumnHeader column="modified" label="Modified" className="gl-col-date"    activeColumn={listSort.column} activeDirection={listSort.direction} defaultDir="desc" onSort={(c,d) => setListSort({ column: c, direction: d })} />
              <span className="gl-col-actions">Actions</span>
            </div>
            <div className="guides-list-body">
              {filteredGuides.map(guide => (
                <div
                  key={guide.id}
                  className={`guides-list-row ${selectedGuides.includes(guide.id) ? 'selected' : ''}`}
                  onClick={() => onSelectGuide(guide.id)}
                >
                  <span className="gl-col-name">
                    <div
                      className={`guide-checkbox-list ${selectedGuides.includes(guide.id) ? 'checked' : ''}`}
                      onClick={(e) => { e.stopPropagation(); toggleGuideSelection(guide.id); }}
                    >
                      {selectedGuides.includes(guide.id) && (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      )}
                    </div>
                    <span className="gl-favicon">
                      {(() => {
                        const faviconUrl = getFaviconWithFallback(guide);
                        return faviconUrl ? (
                          <img src={faviconUrl} alt="" onError={(e) => { handleFaviconError(e); e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }} />
                        ) : null;
                      })()}
                      <span className="gl-favicon-fallback" style={{ display: getFaviconWithFallback(guide) ? 'none' : 'flex' }}>{Icons.globe}</span>
                    </span>
                    <span className="gl-name-text" title={guide.name}>{guide.name}</span>
                  </span>
                  <span className="gl-col-steps">{guide.stepCount ?? guide.steps?.length ?? 0}</span>
                  <span className="gl-col-domain" title={guide.domain}>{guide.domain || '-'}</span>
                  <span className="gl-col-project">
                    {guide.projectId && projectMap[guide.projectId] ? (
                      <span className="gl-project-badge">{React.cloneElement(Icons.folder, { width: 12, height: 12 })} {projectMap[guide.projectId].name}</span>
                    ) : '-'}
                  </span>
                  <span className="gl-col-owner" title={guide.creatorName}>{guide.creatorName || '-'}</span>
                  <span className="gl-col-source">
                    <span className={`gl-source-badge ${guide.source === 'desktop' ? 'desktop' : 'extension'}`}>
                      {guide.source === 'desktop' ? Icons.desktop : Icons.chrome}
                    </span>
                    <MeetingBadge guide={guide} compact />
                  </span>
                  <span className="gl-col-date" title={guide.updatedAt ? formatGuideDateTooltip(guide.updatedAt) : ''}>{guide.updatedAt ? formatGuideDateWithTime(guide.updatedAt) : '-'}</span>
                  <span className="gl-col-actions" onClick={(e) => e.stopPropagation()}>
                    <button
                      className={`guide-action-btn favorite ${favoriteGuideIds.includes(guide.id) ? 'active' : ''}`}
                      onClick={() => onToggleFavorite(guide.id)}
                    >
                      {favoriteGuideIds.includes(guide.id) ? Icons.starFilled : Icons.star}
                    </button>
                    {user?.accountType === 'organization' && (
                      <button className="guide-action-btn guide-action-icon" style={{ color: theme.primary, ...(guide.shareCount > 0 ? { background: '#dbeafe' } : {}) }} onClick={() => onQuickShare?.(guide.id)} title="Share guide">
                        {Icons.shareSmall}
                      </button>
                    )}
                    <button className="guide-action-btn guide-action-icon" onClick={() => onExport(guide.id, 'pdf')} disabled={exportingGuide.id === guide.id}>
                      {exportingGuide.id === guide.id && exportingGuide.format === 'pdf' ? <span className="btn-spinner-sm" /> : Icons.download}
                    </button>
                    {showDelete && (
                      <button className="guide-action-btn guide-action-icon" style={{ color: '#ef4444' }} disabled={deletingGuideId === guide.id} onClick={() => onDeleteGuide(guide.id)} title="Delete guide">
                        {deletingGuideId === guide.id ? '...' : Icons.trash}
                      </button>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="guides-grid">
            {filteredGuides.map(guide => (
              <div
                key={guide.id}
                className={`guide-card ${selectedGuides.includes(guide.id) ? 'selected' : ''}`}
                onClick={() => selectMode ? toggleGuideSelection(guide.id) : onSelectGuide(guide.id)}
              >
                {/* Checkbox in select mode */}
                {selectMode && (
                  <div
                    className={`guide-checkbox ${selectedGuides.includes(guide.id) ? 'checked' : ''}`}
                    onClick={(e) => { e.stopPropagation(); toggleGuideSelection(guide.id); }}
                  >
                    {selectedGuides.includes(guide.id) && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    )}
                  </div>
                )}
                <div className="guide-preview">
                  <div className="guide-preview-favicon">
                    {(() => {
                      const faviconUrl = getFaviconWithFallback(guide);
                      return faviconUrl ? (
                        <img src={faviconUrl} alt="" onError={(e) => { handleFaviconError(e); e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }} />
                      ) : null;
                    })()}
                    <span className="guide-preview-icon-svg" style={{ display: getFaviconWithFallback(guide) ? 'none' : 'flex' }}>{Icons.globe}</span>
                  </div>
                  {guide.domain && <span className="guide-domain">{guide.domain}</span>}
                  <span className={`guide-source-badge ${guide.source === 'desktop' ? 'desktop' : 'extension'}`} title={guide.source === 'desktop' ? 'Created with Desktop App' : 'Created with Chrome Extension'}>
                    {guide.source === 'desktop' ? Icons.desktop : Icons.chrome}
                  </span>
                  <MeetingBadge guide={guide} />
                </div>
                <div className="guide-info">
                  <h3 className="guide-name">{guide.name}</h3>
                  <p className="guide-steps">
                    <span>{guide.stepCount ?? guide.steps?.length ?? 0} steps</span>
                    {guide.updatedAt && <span className="guide-modified" title={formatGuideDateTooltip(guide.updatedAt)}>{formatGuideDateShort(guide.updatedAt)}</span>}
                  </p>
                  {(guide.projectId || guide.creatorName) && (
                    <div className="guide-meta-row">
                      {guide.projectId && projectMap[guide.projectId] ? (
                        <span className="guide-project-badge">
                          <span className="guide-project-badge-icon">{React.cloneElement(Icons.folder, { width: 12, height: 12 })}</span>
                          {projectMap[guide.projectId].name}
                        </span>
                      ) : <span />}
                      {guide.creatorName && <span className="guide-owner-name" title={guide.creatorName}>{guide.creatorName}</span>}
                    </div>
                  )}
                </div>
                <div className="guide-actions">
                  <button
                    className={`guide-action-btn favorite ${favoriteGuideIds.includes(guide.id) ? 'active' : ''}`}
                    onClick={(e) => { e.stopPropagation(); onToggleFavorite(guide.id); }}
                  >
                    {favoriteGuideIds.includes(guide.id) ? Icons.starFilled : Icons.star}
                  </button>
                  {user?.accountType === 'organization' && (
                    <button
                      className="guide-action-btn guide-action-icon"
                      onClick={(e) => { e.stopPropagation(); onQuickShare?.(guide.id); }}
                      title="Share guide"
                      style={{ color: theme.primary, ...(guide.shareCount > 0 ? { background: '#dbeafe' } : {}) }}
                    >
                      {Icons.shareSmall}
                    </button>
                  )}
                  <button className="guide-action-btn guide-action-icon" onClick={(e) => { e.stopPropagation(); onExport(guide.id, 'pdf'); }} disabled={exportingGuide.id === guide.id}>
                    {exportingGuide.id === guide.id && exportingGuide.format === 'pdf' ? <span className="btn-spinner-sm" /> : Icons.download} {exportingGuide.id === guide.id && exportingGuide.format === 'pdf' ? '...' : 'PDF'}
                  </button>
                  {showDelete && (
                    <button
                      className="guide-action-btn guide-action-icon"
                      style={{ color: '#ef4444' }}
                      disabled={deletingGuideId === guide.id}
                      onClick={(e) => { e.stopPropagation(); onDeleteGuide(guide.id); }}
                      title="Delete guide"
                    >
                      {deletingGuideId === guide.id ? '...' : Icons.trash}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

// ============================================
// TRIAL BANNER
// ============================================
// Surfaces the in-progress free trial state. Two trial flavors:
//   - Personal accounts: 15-day Pro trial (granted at signup).
//   - Org accounts: 30-day Business trial (granted at signup).
//
// Cadence rules (per the product spec):
//   • Days 1-7 of the trial: SILENT. The user is onboarding;
//     don't nag.
//   • Days 8+ : visible. Once dismissed, re-shows on a 3-day
//     cooldown so the user sees it again but not every page load.
//   • Last 3 days of a Pro trial: more urgent copy (no cadence
//     change — once dismissed, still 3-day cooldown).
//   • trial_status='converted' or 'expired' → hide entirely.
//
// Account-type resolution: organization members read the trial
// state off the organization row (org.trialPlan / org.trialEndsAt);
// personal accounts use their own user.trialPlan. The component
// auto-picks the right source based on accountType.
function TrialBanner({ user, setCurrentPage }) {
  // Bumping `tick` re-evaluates on dismiss so we don't have to
  // mutate user/org props to hide the banner.
  const [tick, setTick] = useState(0);

  // Pick trial fields from the right row. Org members see the org's
  // Business trial; personal accounts see their own Pro trial.
  // /api/auth/me returns the org as user.organization so we don't
  // need a separate fetch.
  const isOrgAccount = user?.accountType === 'organization';
  const organization = user?.organization;
  const trialPlan = isOrgAccount ? organization?.trialPlan : user?.trialPlan;
  const trialStatus = isOrgAccount ? organization?.trialStatus : user?.trialStatus;
  const trialStartedAtRaw = isOrgAccount ? organization?.trialStartedAt : user?.trialStartedAt;
  const trialEndsAtRaw = isOrgAccount ? organization?.trialEndsAt : user?.trialEndsAt;

  // Bail when there's no live trial. Anything other than 'active' is
  // either pre-feature (null), already converted (paid customer), or
  // expired (downgraded to Free by the auth-time sweep).
  if (trialStatus !== 'active' || !trialEndsAtRaw || !trialStartedAtRaw) return null;
  if (isOrgAccount && user?.orgRole === 'member') return null; // members don't control billing

  const now = Date.now();
  const startedAt = new Date(trialStartedAtRaw).getTime();
  const endsAt = new Date(trialEndsAtRaw).getTime();
  const totalDays = Math.round((endsAt - startedAt) / (24 * 60 * 60 * 1000));
  const daysIn = Math.floor((now - startedAt) / (24 * 60 * 60 * 1000));
  const daysRemaining = Math.max(0, Math.ceil((endsAt - now) / (24 * 60 * 60 * 1000)));

  // Quiet window — first 7 days of every trial.
  if (daysIn < 7) return null;

  // 3-day dismissal cooldown. Cooldown key includes trial end date
  // so an admin-extended trial re-prompts after the extension lands.
  const cooldownKey = `instaguide-trial-banner-dismiss-${trialPlan}-${trialEndsAtRaw}`;
  const lastDismissedAt = parseInt(localStorage.getItem(cooldownKey) || '0', 10);
  const cooldownMs = 3 * 24 * 60 * 60 * 1000;
  if (lastDismissedAt && (now - lastDismissedAt) < cooldownMs) return null;

  const isUrgent = trialPlan === 'pro' && daysRemaining <= 3;
  const planLabel = trialPlan === 'pro' ? 'Pro' : trialPlan === 'business' ? 'Business' : 'paid';
  const dayLabel = daysRemaining === 1 ? '1 day' : `${daysRemaining} days`;
  const title = isUrgent
    ? `Your ${planLabel} trial ends in ${dayLabel}`
    : `${dayLabel} left in your ${planLabel} trial`;
  const subtitle = isUrgent
    ? 'Upgrade now to keep your unlocked features — otherwise your account moves to Free at the end of the trial.'
    : `You're getting full ${planLabel} access. Upgrade any time to lock it in before the trial ends.`;

  const handleDismiss = () => {
    localStorage.setItem(cooldownKey, String(now));
    setTick((t) => t + 1);
  };
  const handleUpgrade = () => {
    if (typeof setCurrentPage === 'function') setCurrentPage('pricing');
  };

  // Use tick to avoid unused-var lint
  void tick;

  // Visual scale shifts in the last-3-days window: darker indigo,
  // bolder border, urgency tag.
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'relative',
        margin: '0 0 16px 0',
        padding: '14px 18px',
        borderRadius: 12,
        background: isUrgent
          ? 'linear-gradient(135deg, #ede9fe 0%, #f5f3ff 100%)'
          : 'linear-gradient(135deg, #eef2ff 0%, #f5f3ff 100%)',
        border: `1px solid ${isUrgent ? '#a78bfa' : '#c7d2fe'}`,
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        boxShadow: isUrgent ? '0 2px 8px rgba(124,58,237,0.10)' : 'none',
      }}
    >
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 36,
        height: 36,
        borderRadius: 10,
        background: isUrgent ? 'linear-gradient(135deg, #7c3aed, #6d28d9)' : 'linear-gradient(135deg, #6366f1, #4f46e5)',
        color: 'white',
        flexShrink: 0,
      }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9"/>
          <polyline points="12 7 12 12 15 14"/>
        </svg>
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b', marginBottom: 2 }}>
          {title}
          {isUrgent && (
            <span style={{
              marginLeft: 8,
              fontSize: 10,
              padding: '2px 6px',
              borderRadius: 4,
              background: '#7c3aed',
              color: 'white',
              textTransform: 'uppercase',
              letterSpacing: 0.6,
              verticalAlign: 'middle',
            }}>Last days</span>
          )}
        </div>
        <div style={{ fontSize: 12.5, color: '#475569', lineHeight: 1.45 }}>
          {subtitle}
          <span style={{ marginLeft: 6, color: '#64748b' }}>
            (Day {Math.min(daysIn + 1, totalDays)} of {totalDays})
          </span>
        </div>
      </div>
      <button
        type="button"
        onClick={handleUpgrade}
        style={{
          background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
          color: 'white',
          border: 'none',
          padding: '9px 16px',
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 700,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          boxShadow: '0 2px 6px rgba(99,102,241,0.30)',
        }}
      >
        {trialPlan === 'business' ? 'Upgrade Business' : 'Upgrade to keep Pro'}
      </button>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss for 3 days"
        title="Dismiss for 3 days"
        style={{
          background: 'transparent',
          border: 'none',
          color: '#64748b',
          padding: 4,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"/>
          <line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    </div>
  );
}

// ============================================
// BILLING REMINDER BANNER
// ============================================
// Renders a single highest-priority reminder at the top of the dashboard:
//   1. Free plan within 20% of guide limit → upgrade prompt
//   2. Subscription canceling within 14 days → resume prompt
// Dismissals are persisted per-reminder-key for a cooldown window so the
// banner does not nag on every page load.
// License Admin "Sign in as user" banner. Persistent amber bar shown on
// EVERY page while an impersonation session is active. Includes a
// "Return to admin" button that calls /api/admin/impersonation/end to
// blacklist the impersonation token, restores the admin's stashed
// original session token (if any), and reloads. Self-hides when
// user.impersonation is null (regular session).
function ImpersonationBanner({ user }) {
  const [returning, setReturning] = React.useState(false);
  const imp = user?.impersonation;
  if (!imp) return null;

  const handleReturn = async () => {
    if (returning) return;
    setReturning(true);
    try {
      const token = localStorage.getItem('authToken');
      // Best-effort revocation — even if this fails, we still swap tokens
      // so the admin gets out of the impersonation session locally.
      try {
        await fetch(`${API_URL}/api/admin/impersonation/end`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
        });
      } catch (_) { /* fall through — token swap below still runs */ }

      const prior = sessionStorage.getItem('preImpersonationToken');
      if (prior) {
        localStorage.setItem('authToken', prior);
        sessionStorage.removeItem('preImpersonationToken');
        window.location.replace('/');
      } else {
        // No stashed admin session — most likely the admin opened the
        // impersonation link in a fresh tab. Clear auth and route to
        // the License Admin panel so they can log back in there.
        localStorage.removeItem('authToken');
        window.location.replace('/licenseadmin');
      }
    } finally {
      setReturning(false);
    }
  };

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12, padding: '10px 16px', marginBottom: 12,
        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
        border: '1px solid #f59e0b', borderRadius: 10, color: '#78350f',
        fontSize: 13, fontWeight: 500,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
          <path d="M12 9v4"/>
          <path d="M12 17h.01"/>
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
        </svg>
        <div style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          Signed in as <strong>{user?.email || user?.name || 'user'}</strong>
          {imp.adminEmail ? <> — impersonating from License Admin (<strong>{imp.adminEmail}</strong>)</> : ' — impersonating from License Admin'}
          . Sensitive writes (password, 2FA, billing) are disabled.
        </div>
      </div>
      <button
        onClick={handleReturn}
        disabled={returning}
        style={{
          background: '#78350f', color: 'white', border: 'none',
          borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600,
          cursor: returning ? 'not-allowed' : 'pointer', opacity: returning ? 0.6 : 1,
          flexShrink: 0,
        }}
      >
        {returning ? 'Returning…' : 'Return to admin'}
      </button>
    </div>
  );
}

function BillingReminderBanner({ user, guideCount, planLimit, setCurrentPage }) {
  const [tick, setTick] = useState(0); // bump to re-evaluate after dismiss

  if (!user) return null;
  // Skip for org members — they don't control billing
  if (user.organizationId && user.orgRole !== 'owner') return null;

  // Highest priority first: subscription canceling soon
  let reminder = null;
  if (user.stripeCancelAtPeriodEnd && user.stripePeriodEnd) {
    const daysLeft = Math.ceil((new Date(user.stripePeriodEnd) - Date.now()) / 86400000);
    if (daysLeft >= 0 && daysLeft <= 14) {
      reminder = {
        key: `cancel-${user.stripePeriodEnd}`,
        cooldownHours: 24,
        tone: 'danger',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        ),
        title: daysLeft === 0
          ? `Your ${user.plan} plan ends today.`
          : `Your ${user.plan} plan ends in ${daysLeft} day${daysLeft === 1 ? '' : 's'}.`,
        message: 'Resume your subscription to keep premium features.',
        ctaLabel: 'Manage Billing',
        ctaAction: 'portal'
      };
    }
  }

  // Else: free plan close to limit. Gate on the EFFECTIVE limit being
  // finite, not on `user.plan === 'free'` — an Enterprise org MEMBER
  // carries `user.plan='free'` while `org.plan='enterprise'`. The old
  // gate fired this branch for Enterprise members and pinned them to
  // the 10-guide free limit even though they actually had unlimited.
  // null and -1 are also unlimited sentinels (Enterprise / custom).
  const isUnlimited = planLimit === Infinity || planLimit == null || planLimit === -1;
  if (!reminder && !isUnlimited && (user.plan === 'free' || !user.plan)) {
    const limit = (typeof planLimit === 'number' && planLimit > 0) ? planLimit : 10;
    if (guideCount != null) {
      const ratio = guideCount / limit;
      if (ratio >= 0.8 && guideCount < limit) {
        reminder = {
          key: `free-near-limit-${limit}`,
          cooldownHours: 24,
          tone: 'warn',
          icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
          ),
          title: `You've used ${guideCount} of ${limit} guides on your Free plan.`,
          message: 'Upgrade to keep creating without limits.',
          ctaLabel: 'View Plans',
          ctaAction: 'pricing'
        };
      } else if (guideCount >= limit) {
        reminder = {
          key: `free-at-limit-${limit}`,
          cooldownHours: 12,
          tone: 'danger',
          icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
          ),
          title: `You've reached your Free plan's ${limit}-guide limit.`,
          message: 'Upgrade to keep creating new guides.',
          ctaLabel: 'View Plans',
          ctaAction: 'pricing'
        };
      }
    }
  }

  if (!reminder) return null;

  // Honor dismissal cooldown
  const dismissKey = `instaguide-reminder-dismissed-${reminder.key}`;
  const dismissedAt = parseInt(localStorage.getItem(dismissKey) || '0', 10);
  const cooldownMs = reminder.cooldownHours * 3600 * 1000;
  if (dismissedAt && Date.now() - dismissedAt < cooldownMs) return null;
  // Hidden useState read so eslint doesn't flag tick as unused
  void tick;

  const dismiss = () => {
    localStorage.setItem(dismissKey, String(Date.now()));
    setTick(t => t + 1);
  };

  const handleCta = async () => {
    if (reminder.ctaAction === 'pricing') {
      setCurrentPage?.('pricing');
    } else if (reminder.ctaAction === 'portal') {
      const token = localStorage.getItem('authToken');
      try {
        const res = await fetch(`${API_URL}/api/billing/portal`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          window.location.href = data.url;
        } else {
          setCurrentPage?.('pricing');
        }
      } catch {
        setCurrentPage?.('pricing');
      }
    }
  };

  const colors = reminder.tone === 'danger'
    ? { bg: '#fef2f2', border: '#fecaca', text: '#991b1b', accent: '#dc2626' }
    : { bg: '#fff7ed', border: '#fed7aa', text: '#9a3412', accent: '#ea580c' };

  return (
    <div style={{
      background: colors.bg,
      borderBottom: `1px solid ${colors.border}`,
      padding: '12px 24px',
      display: 'flex',
      alignItems: 'center',
      gap: 14,
      fontSize: 14,
      color: colors.text
    }}>
      <span style={{ color: colors.accent, flexShrink: 0, display: 'inline-flex' }}>{reminder.icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <strong style={{ marginRight: 6 }}>{reminder.title}</strong>
        <span>{reminder.message}</span>
      </div>
      <button
        onClick={handleCta}
        style={{
          background: colors.accent,
          color: 'white',
          border: 'none',
          borderRadius: 8,
          padding: '7px 14px',
          fontSize: 13,
          fontWeight: 600,
          cursor: 'pointer',
          flexShrink: 0
        }}
      >{reminder.ctaLabel}</button>
      <button
        onClick={dismiss}
        title="Dismiss"
        aria-label="Dismiss"
        style={{
          background: 'transparent',
          color: colors.text,
          border: 'none',
          padding: 4,
          cursor: 'pointer',
          opacity: 0.6,
          display: 'inline-flex',
          flexShrink: 0
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
    </div>
  );
}

// ============================================
// PRICING PAGE — self-serve upgrades via Stripe Checkout
// ============================================
// Fetches plan data from /api/billing/plans (live values from CONFIG.PLANS,
// not the hardcoded fallback). Personal plans (Free/Starter/Pro) get a
// per-card upgrade button; Enterprise links to the sales mailto. The user's
// current plan is highlighted with a "Current plan" badge.
function PricingPage({ user, showToast, onPlanError }) {
  const API_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:3001';
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState('annual'); // default to annual to surface the discount
  const [checkoutLoading, setCheckoutLoading] = useState(null); // planKey while in flight
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    fetch(`${API_URL}/api/billing/plans`)
      .then(r => r.json())
      .then(d => { if (alive) { setPlans(d.plans); setLoading(false); } })
      .catch(() => { if (alive) { setLoading(false); showToast?.('Could not load plans', 'error'); } });
    return () => { alive = false; };
  }, [API_URL, showToast]);

  // Prefer the canonical effectivePlanKey from /api/auth/me (returns the
  // resolved plan for the user — org plan for org members, user plan for
  // personal accounts). Falls back to user.plan for legacy clients that
  // haven't reloaded /api/auth/me since this field was added.
  // Without this, org members whose `user.plan` was never individually
  // set (the typical "joined an Enterprise org but the user row still
  // says free" case) saw "Current plan: Free" on the pricing page.
  const currentPlan = user?.effectivePlanKey || user?.organization?.plan || user?.plan || 'free';

  const handleUpgrade = async (planKey) => {
    setCheckoutLoading(planKey);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/billing/checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ planKey, billingCycle })
      });
      if (!response.ok) {
        if (onPlanError && await onPlanError(response)) return;
        const body = await response.json().catch(() => ({}));
        showToast?.(body.error || 'Could not start checkout', 'error');
        return;
      }
      const data = await response.json();
      // Hand off to Stripe-hosted checkout. Webhook will update plan after payment.
      window.location.href = data.url;
    } catch (e) {
      showToast?.('Network error starting checkout', 'error');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleManageBilling = async () => {
    setPortalLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/billing/portal`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        showToast?.(body.error || 'Could not open billing portal', 'error');
        return;
      }
      const data = await response.json();
      window.location.href = data.url;
    } finally {
      setPortalLoading(false);
    }
  };

  if (loading || !plans) {
    return (
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '40px 24px', textAlign: 'center', color: '#64748b' }}>
        Loading plans…
      </div>
    );
  }

  const order = ['free', 'starter', 'pro', 'business', 'enterprise'];
  const orderedPlans = order.map(k => plans[k]).filter(Boolean);

  // Subscription state for the in-page cancellation banner.
  const subscriptionCanceling = !!(user?.stripeCancelAtPeriodEnd && user?.stripePeriodEnd);
  const subscriptionEndDate = subscriptionCanceling
    ? new Date(user.stripePeriodEnd).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
    : null;
  const subscriptionEndDaysLeft = subscriptionCanceling
    ? Math.ceil((new Date(user.stripePeriodEnd) - Date.now()) / 86400000)
    : null;

  return (
    <div style={{ maxWidth: 1240, margin: '0 auto', padding: '32px 24px 64px' }}>
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <h1 style={{ fontSize: 32, fontWeight: 700, color: '#0f172a', margin: '0 0 10px' }}>Plans &amp; Billing</h1>
        <p style={{ fontSize: 16, color: '#64748b', margin: 0 }}>Choose the plan that fits your team. Upgrade anytime.</p>
      </div>

      {/* Cancellation notice — shown when the user has scheduled a cancel at period end.
          Mirrors what Stripe says in the customer portal so the user always knows where they stand. */}
      {subscriptionCanceling && (
        <div style={{
          background: '#fef2f2',
          border: '1px solid #fecaca',
          color: '#991b1b',
          borderRadius: 12,
          padding: '14px 18px',
          marginBottom: 28,
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          maxWidth: 760,
          margin: '0 auto 28px'
        }}>
          <span style={{ color: '#dc2626', flexShrink: 0, display: 'inline-flex' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          </span>
          <div style={{ flex: 1, minWidth: 0, fontSize: 14, lineHeight: 1.5 }}>
            <strong style={{ display: 'block', marginBottom: 2 }}>
              Your <span style={{ textTransform: 'capitalize' }}>{user.plan}</span> subscription has been canceled.
            </strong>
            <span>
              You'll keep access until <strong>{subscriptionEndDate}</strong>
              {subscriptionEndDaysLeft !== null && subscriptionEndDaysLeft >= 0
                ? ` (${subscriptionEndDaysLeft} day${subscriptionEndDaysLeft === 1 ? '' : 's'} left)`
                : ''}
              . After that you'll be moved to the Free plan.
            </span>
          </div>
          <button
            onClick={handleManageBilling}
            disabled={portalLoading}
            style={{
              background: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '8px 16px',
              fontSize: 13,
              fontWeight: 600,
              cursor: portalLoading ? 'wait' : 'pointer',
              flexShrink: 0
            }}
          >
            {portalLoading ? 'Opening…' : 'Resume Subscription'}
          </button>
        </div>
      )}

      {/* Monthly / Annual toggle */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 36 }}>
        <div style={{
          display: 'inline-flex',
          background: '#f1f5f9',
          borderRadius: 999,
          padding: 4,
          gap: 4
        }}>
          {['monthly', 'annual'].map(cycle => (
            <button
              key={cycle}
              onClick={() => setBillingCycle(cycle)}
              style={{
                background: billingCycle === cycle ? 'white' : 'transparent',
                color: billingCycle === cycle ? '#0f172a' : '#64748b',
                border: 'none',
                borderRadius: 999,
                padding: '8px 20px',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                boxShadow: billingCycle === cycle ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                transition: 'all 0.15s'
              }}
            >
              {cycle === 'monthly' ? 'Monthly' : (
                <>Annual <span style={{ marginLeft: 6, fontSize: 11, color: '#22c55e', background: '#ecfdf5', padding: '2px 8px', borderRadius: 999, fontWeight: 700 }}>Save 10%</span></>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Plan cards — five tiers (Free / Starter / Pro / Business /
          Enterprise) in one row on wide screens. minmax floor of
          210px keeps cards readable while letting the row collapse
          gracefully on narrower viewports (auto-fit drops to 3 / 2
          / 1 columns naturally as width shrinks). */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
        gap: 16,
        alignItems: 'stretch'
      }}>
        {orderedPlans.map((plan, planIdx) => {
          // Predecessor for the "Everything in X, plus..." diff
          // anchor. orderedPlans is the canonical sequence Free →
          // Starter → Pro → Business → Enterprise.
          const prevPlan = planIdx > 0 ? orderedPlans[planIdx - 1] : null;
          const isCurrent = plan.key === currentPlan;
          const isEnterprise = plan.key === 'enterprise';
          const isFree = plan.key === 'free';
          // Sales-only path = Enterprise only. Business is a
          // self-serve plan at $39 — same Upgrade flow as Pro /
          // Starter once Stripe price IDs are provisioned via
          // `backend/scripts/stripe-init.js`. Until then, the
          // "Coming soon" disabled state shows for Business —
          // which is the same behaviour as any plan missing its
          // Stripe IDs.
          const isContactSalesOnly = isEnterprise;
          const price = billingCycle === 'annual' ? plan.priceAnnual : plan.priceMonthly;
          const periodLabel = billingCycle === 'annual' ? '/year' : '/month';
          const featuredHighlight = plan.key === 'pro';

          // Feature highlight list — DIFF against the previous tier
          // so each card only shows what's NEW vs the one to its
          // left. Free shows its full feature set (no predecessor);
          // every other tier shows the guide+steps counts, an
          // "Everything in <prev>, plus..." anchor, and only the
          // features that newly flip on at this tier.
          //
          // Keep the FEATURE_KEYS order in sync with the
          // FEATURE_LABELS array in landing/Pricing.tsx so the
          // marketing site and the in-app card surface the same
          // value progression. apiAccess is OFF the list — see the
          // marketing-honesty note next to landing's FEATURE_LABELS.
          const FEATURE_KEYS = [
            ['aiDescriptions', 'Step titles, descriptions & summaries'],
            ['autoCorrection', 'Auto-correction & review'],
            ['exportPdf', 'PDF Export'],
            ['exportHtml', 'HTML Export'],
            ['voiceRecording', 'Voice recording'],
            ['processFlow', 'Visual process flow'],
            ['askInstaChatbot', 'Ask Insta Assistant'],
            ['embed', 'Public Embed (iframe)'],
            ['changeTracking', 'Change tracking'],
            ['guideSharing', 'Guide sharing & teams'],
            ['prioritySupport', 'Priority support'],
            ['customBranding', 'Custom branding'],
            ['ssoSaml', 'SSO / SAML'],
            ['customStorage', 'BYO Cloud Storage'],
            ['customAiProvider', 'BYO AI Provider'],
          ];
          const featureRows = [];
          featureRows.push({
            label: plan.unlimited ? 'Unlimited guides' : `${plan.guides} guides`,
            on: true,
          });
          if (plan.unlimited) {
            featureRows.push({ label: 'Unlimited steps per guide', on: true });
          } else if (plan.stepsPerGuide) {
            featureRows.push({ label: `${plan.stepsPerGuide} steps per guide`, on: true });
          }
          if (prevPlan && prevPlan.name) {
            featureRows.push({ label: `Everything in ${prevPlan.name}, plus:`, on: true, anchor: true });
            for (const [key, label] of FEATURE_KEYS) {
              const nowOn = !!plan.features[key];
              const wasOn = !!prevPlan.features?.[key];
              if (nowOn && !wasOn) featureRows.push({ label, on: true });
            }
          } else {
            for (const [key, label] of FEATURE_KEYS) {
              if (plan.features[key]) featureRows.push({ label, on: true });
            }
          }

          return (
            <div key={plan.key} style={{
              background: 'white',
              border: featuredHighlight ? '2px solid #6366f1' : '1px solid #e2e8f0',
              borderRadius: 16,
              padding: 24,
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              boxShadow: featuredHighlight ? '0 8px 24px rgba(99,102,241,0.12)' : '0 1px 2px rgba(0,0,0,0.04)'
            }}>
              {featuredHighlight && (
                <div style={{
                  position: 'absolute',
                  top: -12,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                  color: 'white',
                  fontSize: 11,
                  fontWeight: 700,
                  padding: '4px 12px',
                  borderRadius: 999,
                  letterSpacing: 0.4,
                  textTransform: 'uppercase'
                }}>Most popular</div>
              )}

              <div style={{ marginBottom: 16 }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', margin: '0 0 4px' }}>
                  {plan.name}
                  {isCurrent && (
                    <span style={{
                      marginLeft: 8,
                      fontSize: 11,
                      fontWeight: 700,
                      background: '#ecfdf5',
                      color: '#15803d',
                      padding: '2px 8px',
                      borderRadius: 999,
                      verticalAlign: 'middle'
                    }}>Current</span>
                  )}
                </h2>
                <div style={{ minHeight: 56, display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  {isEnterprise ? (
                    <span style={{ fontSize: 18, fontWeight: 600, color: '#475569' }}>Custom pricing</span>
                  ) : isFree ? (
                    <>
                      <span style={{ fontSize: 32, fontWeight: 700, color: '#0f172a' }}>$0</span>
                      <span style={{ fontSize: 14, color: '#64748b' }}>forever</span>
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: 32, fontWeight: 700, color: '#0f172a' }}>${price}</span>
                      <span style={{ fontSize: 14, color: '#64748b' }}>{periodLabel}</span>
                    </>
                  )}
                </div>
                {/* Minimum-users note for org plans — Business / Enterprise
                    commit the buyer to a license-count floor at checkout
                    even though the headline price reads per-user. */}
                {plan.minUsers ? (
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>
                    {plan.minUsers} user minimum
                  </div>
                ) : null}
              </div>

              {/* CTA */}
              <div style={{ marginBottom: 18 }}>
                {isContactSalesOnly ? (
                  <a
                    href={`mailto:${plan.contactSalesEmail || 'hello@instaguide.ai'}?subject=${encodeURIComponent(plan.name + ' plan inquiry')}`}
                    style={{
                      display: 'block',
                      textAlign: 'center',
                      background: 'white',
                      color: '#4f46e5',
                      border: '1.5px solid #6366f1',
                      borderRadius: 10,
                      padding: '11px 16px',
                      fontSize: 14,
                      fontWeight: 600,
                      textDecoration: 'none',
                      cursor: 'pointer'
                    }}
                  >Contact Sales</a>
                ) : isFree ? (
                  <button disabled style={{
                    width: '100%',
                    background: '#f1f5f9',
                    color: '#64748b',
                    border: 'none',
                    borderRadius: 10,
                    padding: '11px 16px',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'not-allowed'
                  }}>{isCurrent ? 'Current plan' : 'Free forever'}</button>
                ) : isCurrent ? (
                  <button
                    onClick={handleManageBilling}
                    disabled={portalLoading}
                    style={{
                      width: '100%',
                      background: 'white',
                      color: '#4f46e5',
                      border: '1.5px solid #6366f1',
                      borderRadius: 10,
                      padding: '11px 16px',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: portalLoading ? 'wait' : 'pointer'
                    }}
                  >{portalLoading ? 'Opening…' : 'Manage Billing'}</button>
                ) : (
                  <button
                    onClick={() => handleUpgrade(plan.key)}
                    disabled={checkoutLoading === plan.key || !plan.purchasable}
                    style={{
                      width: '100%',
                      background: featuredHighlight
                        ? 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)'
                        : '#0f172a',
                      color: 'white',
                      border: 'none',
                      borderRadius: 10,
                      padding: '11px 16px',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: checkoutLoading === plan.key ? 'wait' : (plan.purchasable ? 'pointer' : 'not-allowed'),
                      opacity: plan.purchasable ? 1 : 0.5
                    }}
                  >
                    {checkoutLoading === plan.key
                      ? 'Redirecting…'
                      : plan.purchasable
                        ? `Upgrade to ${plan.name}`
                        : 'Coming soon'}
                  </button>
                )}
              </div>

              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {featureRows.map((f, i) => {
                  // Anchor rows ("Everything in <prev>, plus:") get
                  // a distinct treatment — no checkmark, bold + slight
                  // top-margin so they read as a section header inside
                  // the list rather than as another shipped feature.
                  if (f.anchor) {
                    return (
                      <li key={i} style={{
                        fontSize: 12.5,
                        fontWeight: 700,
                        color: '#475569',
                        marginTop: i === 0 ? 0 : 6,
                        marginBottom: 2,
                        textTransform: 'none',
                        letterSpacing: 0,
                      }}>{f.label}</li>
                    );
                  }
                  return (
                    <li key={i} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 13,
                      color: f.on ? '#0f172a' : '#cbd5e1',
                      textDecoration: f.on ? 'none' : 'line-through',
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={f.on ? '#22c55e' : '#cbd5e1'} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                        {f.on
                          ? <polyline points="20 6 9 17 4 12" />
                          : <line x1="6" y1="6" x2="18" y2="18" />}
                      </svg>
                      <span>{f.label}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>

      {/* Footnote */}
      <div style={{ marginTop: 32, textAlign: 'center', fontSize: 13, color: '#64748b' }}>
        Payments are processed securely by Stripe. Cancel anytime via the billing portal.
      </div>
    </div>
  );
}

// ============================================
// BILLING SUCCESS — landing page after Stripe checkout redirect.
// We poll the user record for a few seconds in case the webhook hasn't fired
// yet. Stripe usually delivers the event within ~2 seconds.
// ============================================
function BillingSuccessPage({ user, onContinue, refreshUser }) {
  const [waiting, setWaiting] = useState(true);
  const [resolvedPlan, setResolvedPlan] = useState(user?.plan || 'free');

  useEffect(() => {
    let alive = true;
    let attempts = 0;
    const tick = async () => {
      if (!alive) return;
      attempts++;
      try {
        const fresh = await refreshUser?.();
        if (fresh && fresh.plan && fresh.plan !== 'free') {
          setResolvedPlan(fresh.plan);
          setWaiting(false);
          return;
        }
      } catch {}
      if (attempts < 8) setTimeout(tick, 1500);
      else setWaiting(false);
    };
    tick();
    return () => { alive = false; };
  }, [refreshUser]);

  return (
    <div style={{ maxWidth: 560, margin: '80px auto', padding: '40px 32px', background: 'white', borderRadius: 16, boxShadow: '0 8px 32px rgba(0,0,0,0.06)', textAlign: 'center' }}>
      <div style={{
        width: 64, height: 64, margin: '0 auto 20px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #22c55e 0%, #15803d 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
      <h1 style={{ fontSize: 26, fontWeight: 700, color: '#0f172a', margin: '0 0 12px' }}>Payment successful</h1>
      <p style={{ fontSize: 15, color: '#475569', margin: '0 0 24px', lineHeight: 1.6 }}>
        {waiting
          ? 'Activating your subscription… this usually takes a few seconds.'
          : (resolvedPlan && resolvedPlan !== 'free')
            ? <>Your <strong style={{ color: '#0f172a', textTransform: 'capitalize' }}>{resolvedPlan}</strong> plan is now active. Welcome aboard!</>
            : 'Your payment was received. Your plan will activate momentarily.'}
      </p>
      <button
        onClick={onContinue}
        style={{
          background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
          color: 'white',
          border: 'none',
          borderRadius: 10,
          padding: '12px 28px',
          fontSize: 14,
          fontWeight: 600,
          cursor: 'pointer'
        }}
      >Go to Dashboard</button>
    </div>
  );
}

// Profile Page
// Profile-page 2FA management section. For accounts WITH 2FA enabled, allows
// disabling it (after re-verifying with a current code) and regenerating
// the 8 backup codes. For accounts WITHOUT 2FA enabled... well, that
// shouldn't happen — the login flow forces setup. Kept as a defensive
// fallback in case a user's totp_enabled was cleared by an admin.
function TwoFactorSection({ user, showToast }) {
  const [busy, setBusy] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  const [showDisableForm, setShowDisableForm] = useState(false);
  const [regenCode, setRegenCode] = useState('');
  const [showRegenForm, setShowRegenForm] = useState(false);
  const [newBackupCodes, setNewBackupCodes] = useState(null);
  const enabled = !!user?.totpEnabled;

  const apiCall = async (path, body) => {
    const token = localStorage.getItem('authToken');
    const res = await fetch(`${API_URL}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(body)
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Request failed');
    return data;
  };

  const handleDisable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await apiCall('/api/auth/2fa/disable', { code: disableCode });
      showToast?.('Two-factor authentication disabled', 'success');
      setShowDisableForm(false);
      setDisableCode('');
      // Force a reload so user.totpEnabled refreshes from /api/auth/me
      setTimeout(() => window.location.reload(), 800);
    } catch (err) {
      showToast?.(err.message || 'Disable failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleRegen = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await apiCall('/api/auth/2fa/regenerate-backup-codes', { code: regenCode });
      setNewBackupCodes(data.backupCodes || []);
      setShowRegenForm(false);
      setRegenCode('');
    } catch (err) {
      showToast?.(err.message || 'Regenerate failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const downloadBackupCodes = () => {
    const blob = new Blob([
      `InstaGuide.ai — 2FA Backup Codes\nAccount: ${user?.email}\nGenerated: ${new Date().toISOString()}\n\nEach code can be used ONCE. Keep them secret.\n\n` + (newBackupCodes || []).join('\n') + '\n'
    ], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `instaguide-backup-codes-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!enabled) {
    return (
      <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412', padding: 14, borderRadius: 10, fontSize: 13 }}>
        Two-factor authentication is required for this account but is not currently enabled. Sign out and sign back in to complete setup.
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <span style={{ display: 'inline-flex', width: 36, height: 36, borderRadius: 10, background: '#ecfdf5', color: '#15803d', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg>
        </span>
        <div>
          <div style={{ fontWeight: 600, color: '#0f172a', fontSize: 14 }}>Enabled</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>Your account is protected with an authenticator app.</div>
        </div>
      </div>

      {!newBackupCodes && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button
            onClick={() => { setShowRegenForm(!showRegenForm); setShowDisableForm(false); }}
            style={{ background: 'white', color: '#4f46e5', border: '1.5px solid #6366f1', borderRadius: 8, padding: '9px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          >Regenerate backup codes</button>
          <button
            onClick={() => { setShowDisableForm(!showDisableForm); setShowRegenForm(false); }}
            style={{ background: 'white', color: '#dc2626', border: '1.5px solid #fecaca', borderRadius: 8, padding: '9px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          >Disable 2FA</button>
        </div>
      )}

      {showRegenForm && (
        <form onSubmit={handleRegen} style={{ marginTop: 14, padding: 14, background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: 12, color: '#475569', marginBottom: 8 }}>Enter a current 6-digit code from your authenticator to regenerate. Old backup codes will stop working.</div>
          <input type="text" inputMode="numeric" maxLength={6} placeholder="123456" value={regenCode} onChange={e => setRegenCode(e.target.value.replace(/\D/g, '').slice(0, 6))} style={{ width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 15, fontFamily: 'monospace', letterSpacing: '0.3em', textAlign: 'center', boxSizing: 'border-box' }} />
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button type="button" onClick={() => { setShowRegenForm(false); setRegenCode(''); }} style={{ flex: 1, padding: '9px 12px', background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
            <button type="submit" disabled={busy || regenCode.length !== 6} style={{ flex: 1, padding: '9px 12px', background: '#6366f1', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: regenCode.length === 6 && !busy ? 1 : 0.6 }}>{busy ? 'Working…' : 'Regenerate'}</button>
          </div>
        </form>
      )}

      {newBackupCodes && (
        <div style={{ marginTop: 14, padding: 14, background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10 }}>
          <div style={{ fontSize: 12, color: '#9a3412', marginBottom: 10, fontWeight: 600 }}>
            Save these new backup codes — they will not be shown again. Old codes are now invalid.
          </div>
          <ul style={{ background: 'white', border: '1px solid #fed7aa', borderRadius: 8, padding: 12, fontFamily: 'monospace', fontSize: 13, lineHeight: 1.9, listStyle: 'none', margin: 0 }}>
            {newBackupCodes.map(c => <li key={c}>{c}</li>)}
          </ul>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button type="button" onClick={downloadBackupCodes} style={{ flex: 1, padding: '9px 12px', background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Download .txt</button>
            <button type="button" onClick={() => setNewBackupCodes(null)} style={{ flex: 1, padding: '9px 12px', background: '#6366f1', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>I've saved them</button>
          </div>
        </div>
      )}

      {showDisableForm && (
        <form onSubmit={handleDisable} style={{ marginTop: 14, padding: 14, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10 }}>
          <div style={{ fontSize: 12, color: '#991b1b', marginBottom: 8 }}>
            <strong>Warning:</strong> Disabling 2FA reduces your account security. Enter a current 6-digit code to confirm.
          </div>
          <input type="text" inputMode="numeric" maxLength={6} placeholder="123456" value={disableCode} onChange={e => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))} style={{ width: '100%', padding: '9px 12px', border: '1px solid #fecaca', borderRadius: 8, fontSize: 15, fontFamily: 'monospace', letterSpacing: '0.3em', textAlign: 'center', boxSizing: 'border-box' }} />
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button type="button" onClick={() => { setShowDisableForm(false); setDisableCode(''); }} style={{ flex: 1, padding: '9px 12px', background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
            <button type="submit" disabled={busy || disableCode.length !== 6} style={{ flex: 1, padding: '9px 12px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: disableCode.length === 6 && !busy ? 1 : 0.6 }}>{busy ? 'Working…' : 'Disable 2FA'}</button>
          </div>
        </form>
      )}
    </div>
  );
}

function ProfilePage({ user, setCurrentPage, showToast }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMsg, setPasswordMsg] = useState({ type: '', text: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  const handleManageBilling = async () => {
    setPortalLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/api/billing/portal`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        showToast?.(body.error || 'Could not open billing portal', 'error');
        return;
      }
      const data = await res.json();
      window.location.href = data.url;
    } finally {
      setPortalLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordMsg({ type: '', text: '' });

    if (newPassword.length < 6) {
      setPasswordMsg({ type: 'error', text: 'New password must be at least 6 characters' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordMsg({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    setChangingPassword(true);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/api/user/password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ currentPassword, newPassword })
      });
      const data = await res.json();
      if (res.ok) {
        setPasswordMsg({ type: 'success', text: 'Password updated successfully' });
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setPasswordMsg({ type: 'error', text: data.error || 'Failed to change password' });
      }
    } catch {
      setPasswordMsg({ type: 'error', text: 'Failed to connect to server' });
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <>
      <div className="main-header">
        <h1 className="main-header-title">Profile</h1>
      </div>
      <div className="page-content">
        <style>{`
          .profile-card {
            background: white;
            border-radius: 16px;
            padding: 32px;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
            max-width: 600px;
          }
          .profile-card + .profile-card {
            margin-top: 24px;
          }
          .profile-header {
            display: flex;
            align-items: center;
            gap: 24px;
            margin-bottom: 32px;
            padding-bottom: 24px;
            border-bottom: 1px solid #e2e8f0;
          }
          .profile-avatar-large {
            width: 80px;
            height: 80px;
            background: ${theme.gradient};
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: White;
            font-size: 32px;
            font-weight: 600;
          }
          .profile-name {
            font-size: 24px;
            font-weight: 700;
            color: #1e293b;
            margin: 0 0 4px 0;
          }
          .profile-email {
            font-size: 16px;
            color: #64748b;
            margin: 0;
          }
          .profile-info-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 24px;
          }
          .profile-info-item {
            padding: 16px;
            background: #f8fafc;
            border-radius: 12px;
          }
          .profile-info-label {
            font-size: 12px;
            text-transform: uppercase;
            color: #64748b;
            margin-bottom: 4px;
            letter-spacing: 0.5px;
          }
          .profile-info-value {
            font-size: 16px;
            font-weight: 600;
            color: #1e293b;
          }
          .plan-badge {
            display: inline-block;
            padding: 4px 12px;
            background: ${theme.gradient};
            color: white;
            border-radius: 20px;
            font-size: 14px;
            font-weight: 600;
            text-transform: capitalize;
          }
          .profile-section-title {
            font-size: 18px;
            font-weight: 700;
            color: #1e293b;
            margin: 0 0 20px 0;
          }
          .password-form .form-group {
            margin-bottom: 16px;
          }
          .password-form .form-group label {
            display: block;
            font-size: 13px;
            font-weight: 600;
            color: #475569;
            margin-bottom: 6px;
          }
          .password-form .form-group input {
            width: 100%;
            padding: 10px 14px;
            border: 2px solid #e2e8f0;
            border-radius: 8px;
            font-size: 14px;
            transition: border-color 0.2s;
          }
          .password-form .form-group input:focus {
            outline: none;
            border-color: ${theme.primary};
            box-shadow: 0 0 0 3px ${theme.primary}20;
          }
          .password-msg {
            padding: 10px 14px;
            border-radius: 8px;
            font-size: 13px;
            margin-bottom: 16px;
          }
          .password-msg.error {
            background: #fef2f2;
            border: 1px solid #fecaca;
            color: #dc2626;
          }
          .password-msg.success {
            background: #f0fdf4;
            border: 1px solid #bbf7d0;
            color: #16a34a;
          }
          .btn-change-password {
            padding: 10px 24px;
            background: ${theme.gradient};
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
          }
          .btn-change-password:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px ${theme.primary}40;
          }
          .btn-change-password:disabled {
            opacity: 0.6;
            cursor: not-allowed;
            transform: none;
          }
        `}</style>

        <div className="profile-card">
          <div className="profile-header">
            <div className="profile-avatar-large">
              {user?.name?.charAt(0)?.toUpperCase() || '?'}
            </div>
            <div>
              <h2 className="profile-name">{user?.name}</h2>
              <p className="profile-email">{user?.email}</p>
            </div>
          </div>

          <div className="profile-info-grid">
            <div className="profile-info-item">
              <div className="profile-info-label">Plan</div>
              <div className="profile-info-value">
                <span className="plan-badge">{user?.plan || 'Free'}</span>
              </div>
            </div>
            <div className="profile-info-item">
              <div className="profile-info-label">Account Type</div>
              <div className="profile-info-value" style={{ textTransform: 'capitalize' }}>
                {user?.accountType || 'Personal'}
              </div>
            </div>
            {user?.organizationId && (
              <>
                <div className="profile-info-item">
                  <div className="profile-info-label">Organization Role</div>
                  <div className="profile-info-value" style={{ textTransform: 'capitalize' }}>
                    {user?.orgRole || 'Member'}
                  </div>
                </div>
                <div className="profile-info-item">
                  <div className="profile-info-label">Member Since</div>
                  <div className="profile-info-value">
                    {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : '-'}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Subscription card — only for personal accounts (org plans are admin-managed).
            Personal accounts auto-create a hidden org row, so check accountType
            directly rather than the presence of organizationId. */}
        {user?.accountType !== 'organization' && (
          <div className="profile-card">
            <h3 className="profile-section-title">Subscription</h3>
            <div className="profile-info-grid">
              <div className="profile-info-item">
                <div className="profile-info-label">Current Plan</div>
                <div className="profile-info-value" style={{ textTransform: 'capitalize' }}>
                  {user?.plan || 'free'}
                  {user?.stripeBillingCycle && (
                    <span style={{ marginLeft: 8, fontSize: 12, color: '#64748b', fontWeight: 500 }}>
                      ({user.stripeBillingCycle})
                    </span>
                  )}
                </div>
              </div>
              {user?.stripePeriodEnd && (
                <div className="profile-info-item">
                  <div className="profile-info-label">
                    {user?.stripeCancelAtPeriodEnd ? 'Cancels on' : 'Renews on'}
                  </div>
                  <div className="profile-info-value">
                    {new Date(user.stripePeriodEnd).toLocaleDateString()}
                  </div>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20, flexWrap: 'wrap' }}>
              <button
                onClick={() => setCurrentPage?.('pricing')}
                style={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: 8,
                  padding: '10px 18px',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                {user?.plan === 'free' || !user?.plan ? 'View Plans' : 'Change Plan'}
              </button>
              {user?.stripeCustomerId && (
                <button
                  onClick={handleManageBilling}
                  disabled={portalLoading}
                  style={{
                    background: 'white',
                    color: '#4f46e5',
                    border: '1.5px solid #6366f1',
                    borderRadius: 8,
                    padding: '10px 18px',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: portalLoading ? 'wait' : 'pointer'
                  }}
                >
                  {portalLoading ? 'Opening…' : 'Manage Billing'}
                </button>
              )}
            </div>
          </div>
        )}

        <div className="profile-card">
          <h3 className="profile-section-title">Two-Factor Authentication</h3>
          <TwoFactorSection user={user} showToast={showToast} />
        </div>

        <div className="profile-card">
          <h3 className="profile-section-title">Change Password</h3>
          <form className="password-form" onSubmit={handleChangePassword}>
            {passwordMsg.text && (
              <div className={`password-msg ${passwordMsg.type}`}>{passwordMsg.text}</div>
            )}
            <div className="form-group">
              <label>Current Password</label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                required
              />
            </div>
            <div className="form-group">
              <label>New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
                required
                minLength={6}
              />
            </div>
            <div className="form-group">
              <label>Confirm New Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
              />
            </div>
            <button
              type="submit"
              className="btn-change-password"
              disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
            >
              {changingPassword ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        </div>
      </div>
    </>
  );
}

// Dashboard Component (now used as content within MainLayout)
function DashboardView({ user, guides, sharedGuides, showSharedGuides, setShowSharedGuides, onLogout, onSelectGuide, onDeleteGuide, deletingGuideId, loadingGuides, onExport, exportingGuide, onManageOrg, projects, selectedProjectId, setSelectedProjectId }) {
  const planLimitsFallback = { free: 10, starter: 50, pro: 200, enterprise: Infinity };
  const serverLimit = user?.planLimits?.guides;
  // `-1` and `null` both mean unlimited (Enterprise / custom plans). The
  // backend serializes `null` → `-1` post 2026-06-23; clients hitting an
  // older backend would see raw `null` so we treat both as Infinity here.
  // `0` is a real (if unusual) cap so we don't lump it into the unlimited
  // bucket — use explicit equality, not truthiness.
  const limit = (serverLimit === -1 || serverLimit === null)
    ? Infinity
    : (typeof serverLimit === 'number' ? serverLimit : (planLimitsFallback[user?.plan] || 10));
  const usagePercent = limit === Infinity ? 0 : (guides.length / limit) * 100;
  const isOrgAdmin = user.organizationId && ['owner', 'admin'].includes(user.orgRole);

  const [searchQuery, setSearchQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [ownershipFilter, setOwnershipFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('guideViewMode') || 'tile');
  const handleSetViewMode = (mode) => { setViewMode(mode); localStorage.setItem('guideViewMode', mode); };

  // Filter guides by selected project
  const filteredGuides = selectedProjectId === 'all'
    ? guides
    : guides.filter(g => g.projectId === selectedProjectId);

  // Get unique owners for filter dropdown
  const allGuides = showSharedGuides ? sharedGuides : filteredGuides;
  const ownerNames = useMemo(() => {
    const names = new Set();
    allGuides.forEach(g => {
      const name = g.creatorName || g.ownerName;
      if (name) names.add(name);
    });
    return [...names].sort();
  }, [allGuides]);

  // Apply search, source filter, owner filter, ownership filter, and sort
  const applyFilters = (list) => {
    let result = list;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(g => g.name?.toLowerCase().includes(q) || g.domain?.toLowerCase().includes(q));
    }
    if (sourceFilter !== 'all') {
      result = result.filter(g => g.source === sourceFilter);
    }
    if (ownerFilter !== 'all') {
      result = result.filter(g => (g.creatorName || g.ownerName) === ownerFilter);
    }
    if (ownershipFilter && ownershipFilter !== 'all') {
      result = result.filter(g => (g.creatorName || g.ownerName) === ownershipFilter);
    }
    result = [...result].sort((a, b) => {
      if (sortBy === 'newest') return new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt);
      if (sortBy === 'oldest') return new Date(a.updatedAt || a.createdAt) - new Date(b.updatedAt || b.createdAt);
      if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
      if (sortBy === 'steps') return (b.stepCount ?? b.steps?.length ?? 0) - (a.stepCount ?? a.steps?.length ?? 0);
      return 0;
    });
    return result;
  };
  const displayedGuides = applyFilters(showSharedGuides ? sharedGuides : filteredGuides);
  const hasActiveFilters = searchQuery || sourceFilter !== 'all' || ownerFilter !== 'all' || ownershipFilter !== 'all';

  // Create a map for quick project name lookup
  const projectMap = projects.reduce((acc, p) => ({ ...acc, [p.id]: p }), {});

  return (
    <div className="dashboard">
      <style>{`
        .dashboard {
          min-height: 100vh;
          background: #f8fafc;
        }
        .header {
          background: ${theme.gradient};
          padding: 20px 32px;
        }
        .header-content {
          max-width: 1600px;
          margin: 0 auto;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .header-left {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .header-logo {
          width: 36px;
          height: 36px;
          background: ${theme.gradient};
          border-radius: 9px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          box-shadow: 0 2px 8px ${theme.primary}30;
        }
        .header-title {
          font-size: 24px;
          font-weight: 800;
          color: #64748b;
          margin: 0;
        }
        .header-right {
          display: flex;
          align-items: center;
          gap: 16px;
        }
        .user-name {
          color: #64748b;
          font-size: 14px;
        }
        .settings-btn {
          background: rgba(255,255,255,0.2);
          color: #64748b;
          border: none;
          padding: 8px 12px;
          border-radius: 8px;
          font-size: 20px;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .settings-btn:hover {
          background: rgba(255,255,255,0.35);
          transform: rotate(135deg);
        }
        .logout-btn {
          background: rgba(255,255,255,0.2);
          color: #64748b;
          border: none;
          padding: 10px 18px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
        }
        .main {
          max-width: 1600px;
          margin: 0 auto;
          padding: 32px;
        }
        .usage-card {
          background: white;
          border-radius: 20px;
          padding: 28px;
          margin-bottom: 28px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.06);
        }
        .usage-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }
        .usage-title {
          font-size: 18px;
          font-weight: 700;
          color: #1e293b;
          margin: 0 0 4px 0;
        }
        .usage-text {
          font-size: 14px;
          color: #64748b;
          margin: 0;
        }
        .progress-bar {
          width: 100%;
          height: 10px;
          background: #e2e8f0;
          border-radius: 5px;
          overflow: hidden;
        }
        .progress-fill {
          height: 100%;
          background: ${theme.gradient};
          border-radius: 5px;
          transition: width 0.3s;
        }
        .cta-card {
          background: ${theme.gradient};
          border-radius: 20px;
          padding: 36px;
          margin-bottom: 36px;
          display: flex;
          align-items: center;
          gap: 28px;
          color: white;
        }
        .cta-icon {
          font-size: 56px;
          background: rgba(255,255,255,0.2);
          width: 100px;
          height: 100px;
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .cta-content h3 {
          font-size: 22px;
          font-weight: 700;
          margin: 0 0 8px 0;
        }
        .cta-content p {
          margin: 0 0 20px 0;
          opacity: 0.9;
          font-size: 15px;
        }
        .cta-btn {
          display: inline-block;
          background: white;
          color: #64748b;
          padding: 14px 28px;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 700;
          text-decoration: none;
          border: none;
          cursor: pointer;
        }
        .section-title {
          font-size: 22px;
          font-weight: 700;
          color: #1e293b;
          margin: 0 0 20px 0;
        }
        .empty-state {
          text-align: center;
          padding: 80px 40px;
          background: white;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          position: relative;
          overflow: hidden;
        }
        .empty-state::before {
          content: '';
          position: absolute;
          top: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 200px;
          height: 200px;
          background: radial-gradient(circle, ${theme.primary}08 0%, transparent 70%);
          border-radius: 50%;
          pointer-events: none;
        }
        .empty-icon {
          font-size: 64px;
          display: flex;
          justify-content: center;
          margin-bottom: 20px;
          color: ${theme.primary}40;
        }
        .empty-icon svg {
          width: 56px;
          height: 56px;
        }
        .empty-state h3 {
          font-size: 18px;
          font-weight: 600;
          color: #334155;
          margin: 0 0 8px 0;
        }
        .empty-state p {
          color: #94a3b8;
          font-size: 14px;
          margin: 0;
        }
        .guides-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 24px;
        }
        .guide-card {
          background: white;
          border-radius: 14px;
          overflow: hidden;
          cursor: pointer;
          border: 1px solid #e2e8f0;
          transition: all 0.2s ease;
        }
        .guide-card:hover {
          transform: translateY(-3px);
          border-color: ${theme.primary}30;
          box-shadow: 0 12px 32px ${theme.primary}12;
        }
        .guide-preview {
          height: 120px;
          background: linear-gradient(145deg, ${theme.primary}18 0%, ${theme.secondary}0d 60%, #f1f5f9 100%);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          position: relative;
          gap: 8px;
          border-bottom: 1px solid #e2e8f0;
        }
        .guide-preview-icon {
          font-size: 48px;
          opacity: 0.4;
        }
        .guide-preview-favicon {
          width: 56px;
          height: 56px;
          background: white;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          border: 1px solid #e2e8f0;
        }
        .guide-preview-favicon img {
          width: 32px;
          height: 32px;
          object-fit: contain;
        }
        .guide-preview-icon-svg {
          width: 56px;
          height: 56px;
          background: white;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          border: 1px solid #e2e8f0;
          color: #94a3b8;
        }
        .guide-preview-icon-svg svg {
          width: 28px;
          height: 28px;
        }
        .guide-domain {
          font-size: 11px;
          color: #64748b;
          background: white;
          padding: 4px 10px;
          border-radius: 8px;
          max-width: 200px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          border: 1px solid #e2e8f0;
          font-weight: 500;
        }
        .guide-source-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 4px 8px;
          border-radius: 8px;
          font-size: 11px;
          background: white;
          color: #64748b;
          border: 1px solid #e2e8f0;
        }
        .guide-source-badge.desktop {
          color: #7c3aed;
          background: #faf5ff;
          border-color: #e9d5ff;
        }
        .guide-source-badge.extension {
          color: #6366f1;
          background: #eef2ff;
          border-color: #c7d2fe;
        }
        .guide-info {
          padding: 20px;
        }
        .guide-name {
          font-size: 16px;
          font-weight: 700;
          color: #1e293b;
          margin: 0 0 6px 0;
        }
        .guide-steps {
          font-size: 14px;
          color: #64748b;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .guide-modified {
          font-size: 12px;
          color: #94a3b8;
          white-space: nowrap;
          margin-left: auto;
        }
        .guide-meta-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          margin-top: 8px;
        }
        .guide-project-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          color: ${theme.primary};
          background: #f1f5f9;
          padding: 4px 10px;
          border-radius: 12px;
          font-weight: 500;
        }
        .guide-owner-name {
          font-size: 11px;
          color: #94a3b8;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 120px;
        }
        .guide-actions {
          padding: 12px 20px 16px;
          display: flex;
          gap: 6px;
          border-top: 1px solid #f1f5f9;
        }
        .export-btn {
          flex: 1;
          padding: 8px 12px;
          background: transparent;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          color: #64748b;
          transition: all 0.15s;
        }
        .export-btn:hover {
          background: #f8fafc;
          border-color: #cbd5e1;
          color: #334155;
        }
        .export-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .export-btn:disabled:hover {
          background: transparent;
          border-color: #e2e8f0;
          color: #64748b;
        }
        .export-btn svg {
          width: 14px;
          height: 14px;
          vertical-align: middle;
          margin-right: 4px;
        }
        .delete-btn {
          padding: 8px 12px;
          background: transparent;
          border: 1px solid #fecaca;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          min-width: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.15s;
        }
        .delete-btn:hover:not(:disabled) {
          background: #fef2f2;
          border-color: #f87171;
        }
        .delete-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
        .delete-btn svg {
          width: 16px;
          height: 16px;
          color: #dc2626;
        }
        .delete-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid #fecaca;
          border-top-color: #dc2626;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        /* Loading states */
        .loading-guides, .loading-guide-detail {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          color: #64748b;
        }
        .loading-guides p, .loading-guide-detail p {
          margin-top: 16px;
          font-size: 14px;
        }
        .loading-spinner-large {
          width: 40px;
          height: 40px;
          border: 3px solid #e2e8f0;
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
        .dashboard-tabs-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          flex-wrap: wrap;
          gap: 12px;
        }
        .dashboard-tabs {
          display: flex;
          gap: 8px;
        }
        .project-filter {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .project-filter label {
          font-size: 14px;
          font-weight: 500;
          color: #64748b;
        }
        .project-filter-select {
          padding: 8px 12px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 14px;
          color: #1e293b;
          background: white;
          cursor: pointer;
          min-width: 180px;
        }
        .project-filter-select:hover {
          border-color: #64748b;
        }
        .project-filter-select:focus {
          outline: none;
          border-color: #64748b;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }
        .tab-btn {
          padding: 12px 24px;
          border: none;
          background: #e2e8f0;
          color: #64748b;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }
        .tab-btn:hover {
          background: #cbd5e1;
        }
        .tab-btn.active {
          background: ${theme.gradient};
          color: white;
        }
        .tab-btn .tab-count {
          background: rgba(255,255,255,0.3);
          padding: 2px 8px;
          border-radius: 10px;
          margin-left: 8px;
          font-size: 13px;
        }
        .tab-btn.active .tab-count {
          background: rgba(255,255,255,0.25);
        }
        .guide-filters-bar {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 20px;
          flex-wrap: wrap;
        }
        .guide-search-wrapper {
          position: relative;
          flex: 1;
          min-width: 200px;
          max-width: 400px;
        }
        .guide-search-wrapper svg {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
          width: 16px;
          height: 16px;
        }
        .guide-search-input {
          width: 100%;
          padding: 9px 12px 9px 38px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 14px;
          color: #1e293b;
          background: white;
          transition: all 0.2s;
        }
        .guide-search-input:focus {
          outline: none;
          border-color: ${theme.primary};
          box-shadow: 0 0 0 3px ${theme.primary}15;
        }
        .guide-search-input::placeholder {
          color: #94a3b8;
        }
        .guide-filter-select {
          padding: 9px 12px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 13px;
          color: #1e293b;
          background: white;
          cursor: pointer;
        }
        .guide-filter-select:focus {
          outline: none;
          border-color: ${theme.primary};
          box-shadow: 0 0 0 3px ${theme.primary}15;
        }
        .guide-filter-count {
          font-size: 13px;
          color: #94a3b8;
          margin-left: auto;
          white-space: nowrap;
        }
        .shared-by {
          font-size: 12px;
          color: #64748b;
          margin-top: 4px;
        }
        .shared-by-name {
          color: #64748b;
          font-weight: 600;
        }
        /* Dashboard List View */
        .dv-list-container {
          background: white;
          border-radius: 14px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
        }
        .dv-list-header {
          display: grid;
          grid-template-columns: 2.5fr 0.5fr 1.5fr 1fr 1fr 0.5fr 0.7fr 1.2fr;
          gap: 10px;
          padding: 10px 20px;
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
          font-size: 11.5px;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.4px;
        }
        .dv-list-body {
          max-height: calc(100vh - 380px);
          overflow-y: auto;
        }
        .dv-list-body::-webkit-scrollbar { width: 6px; }
        .dv-list-body::-webkit-scrollbar-track { background: transparent; }
        .dv-list-body::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
        .dv-list-body::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
        .dv-list-row {
          display: grid;
          grid-template-columns: 2.5fr 0.5fr 1.5fr 1fr 1fr 0.5fr 0.7fr 1.2fr;
          gap: 10px;
          padding: 12px 20px;
          align-items: center;
          border-bottom: 1px solid #f1f5f9;
          cursor: pointer;
          transition: background 0.15s;
        }
        .dv-list-row:hover { background: ${theme.primary}04; }
        .dv-list-row:last-child { border-bottom: none; }
        .dv-col-name {
          display: flex;
          align-items: center;
          gap: 10px;
          min-width: 0;
        }
        .dv-favicon {
          width: 32px;
          height: 32px;
          background: white;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 1px 4px rgba(0,0,0,0.06);
          border: 1px solid #e2e8f0;
          flex-shrink: 0;
        }
        .dv-favicon img { width: 20px; height: 20px; object-fit: contain; }
        .dv-favicon-fallback {
          width: 32px; height: 32px;
          display: flex; align-items: center; justify-content: center;
          color: #94a3b8;
        }
        .dv-favicon-fallback svg { width: 16px; height: 16px; }
        .dv-name-text {
          font-size: 14px;
          font-weight: 600;
          color: #1e293b;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          flex: 1;
          min-width: 0;
        }
        .dv-col-owner {
          font-size: 12px;
          color: #64748b;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .dv-col-domain {
          font-size: 13px;
          color: #64748b;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          min-width: 0;
        }
        .dv-col-steps { font-size: 13px; color: #64748b; font-weight: 500; }
        .dv-col-source { display: flex; align-items: center; }
        .dv-source-badge {
          display: inline-flex;
          align-items: center;
          padding: 3px 6px;
          border-radius: 6px;
          font-size: 11px;
          color: #64748b;
          background: #f1f5f9;
        }
        .dv-source-badge.desktop { color: #7c3aed; background: #faf5ff; }
        .dv-source-badge.extension { color: #6366f1; background: #eef2ff; }
        .dv-source-badge svg { width: 14px; height: 14px; }
        .dv-col-project {
          font-size: 12px; color: #64748b;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
          min-width: 0;
        }
        .dv-project-badge {
          display: inline-flex;
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          color: ${theme.primary};
          font-weight: 500;
        }
        .dv-project-badge svg { width: 12px; height: 12px; }
        .dv-col-date { font-size: 12px; color: #94a3b8; white-space: nowrap; }
        .dv-col-actions { display: flex; align-items: center; gap: 4px; }
        .dv-col-actions .export-btn {
          padding: 5px 10px;
          font-size: 12px;
        }
        .dv-col-actions .delete-btn {
          padding: 5px 8px;
          min-width: 30px;
        }
        .dv-col-actions .delete-btn svg { width: 14px; height: 14px; }
      `}</style>

      <header className="header">
        <div className="header-content">
          <div className="header-left">
            <span className="header-logo"><img src="/favicon.png" alt="InstaGuide" width="28" height="28" style={{ borderRadius: 6 }} /></span>
            <h1 className="header-title">InstaGuide</h1>
          </div>
          <div className="header-right">
            {isOrgAdmin && (
              <button onClick={onManageOrg} className="settings-btn" title="Settings">{Icons.settings}</button>
            )}
            <span className="user-name">{user.name}</span>
            <button onClick={onLogout} className="logout-btn">Logout</button>
          </div>
        </div>
      </header>

      <main className="main">
        <div className="usage-card">
          <div className="usage-header">
            <div>
              <h3 className="usage-title">Usage</h3>
              <p className="usage-text">{guides.length} of {limit} guides used</p>
            </div>
          </div>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${Math.min(usagePercent, 100)}%` }}></div>
          </div>
        </div>

        <div className="cta-card">
          <div className="cta-icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg></div>
          <div className="cta-content">
            <h3>Start Creating Guides Instantly</h3>
            <p>Install the Chrome extension to capture any process automatically. InstaGuide generates descriptions for every step!</p>
            <a href={CHROME_STORE_URL} target="_blank" rel="noopener noreferrer" className="cta-btn">Install Chrome Extension</a>
          </div>
        </div>

        <div className="dashboard-tabs-row">
          <div className="dashboard-tabs">
            <button
              className={`tab-btn ${!showSharedGuides ? 'active' : ''}`}
              onClick={() => setShowSharedGuides(false)}
            >
              My Guides <span className="tab-count">{filteredGuides.length}</span>
            </button>
            {user.organizationId && (
              <button
                className={`tab-btn ${showSharedGuides ? 'active' : ''}`}
                onClick={() => setShowSharedGuides(true)}
              >
                Shared with Me <span className="tab-count">{sharedGuides.length}</span>
              </button>
            )}
          </div>
        </div>

        <GuideFilterBar
          searchQuery={searchQuery} setSearchQuery={setSearchQuery}
          sourceFilter={sourceFilter} setSourceFilter={setSourceFilter}
          ownerFilter={ownerFilter} setOwnerFilter={ownerNames.length > 1 ? setOwnerFilter : null}
          ownershipFilter={ownershipFilter} setOwnershipFilter={setOwnershipFilter}
          projectFilter={!showSharedGuides ? selectedProjectId : null} setProjectFilter={!showSharedGuides ? setSelectedProjectId : null}
          sortBy={sortBy} setSortBy={setSortBy}
          projects={!showSharedGuides ? projects : []} ownerNames={ownerNames} userName={user?.name}
          resultCount={displayedGuides.length} hasActiveFilters={hasActiveFilters}
          viewMode={viewMode} setViewMode={handleSetViewMode}
        />

        {loadingGuides ? (
          <div className="loading-guides">
            <div className="loading-spinner-large" />
            <p>Loading guides...</p>
          </div>
        ) : displayedGuides.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">{hasActiveFilters ? Icons.search : (showSharedGuides ? Icons.handshake : Icons.clipboard)}</span>
            <h3>{hasActiveFilters ? 'No matching guides' : (showSharedGuides ? 'No shared guides' : 'No guides yet')}</h3>
            <p>{hasActiveFilters ? 'Try adjusting your search or filters' : (showSharedGuides ? 'Guides shared with you by team members will appear here' : 'Install the extension and start recording to create your first guide')}</p>
          </div>
        ) : viewMode === 'list' ? (
          <div className="dv-list-container">
            <div className="dv-list-header">
              <span>Name</span>
              <span>Steps</span>
              <span>Domain</span>
              <span>Project</span>
              <span>Owner</span>
              <span>Source</span>
              <span>Modified</span>
              <span>Actions</span>
            </div>
            <div className="dv-list-body">
              {displayedGuides.map((guide) => (
                <div key={guide.id} className="dv-list-row" onClick={() => onSelectGuide(guide.id)}>
                  <span className="dv-col-name">
                    <span className="dv-favicon">
                      {(() => {
                        const faviconUrl = getFaviconWithFallback(guide);
                        return faviconUrl ? (
                          <img src={faviconUrl} alt="" onError={(e) => { handleFaviconError(e); e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }} />
                        ) : null;
                      })()}
                      <span className="dv-favicon-fallback" style={{ display: getFaviconWithFallback(guide) ? 'none' : 'flex' }}>{Icons.globe}</span>
                    </span>
                    <span className="dv-name-text" title={guide.name}>{guide.name}</span>
                  </span>
                  <span className="dv-col-steps">{guide.stepCount ?? guide.steps?.length ?? 0}</span>
                  <span className="dv-col-domain" title={guide.domain}>{guide.domain || '-'}</span>
                  <span className="dv-col-project">
                    {guide.projectId && projectMap[guide.projectId] ? (
                      <span className="dv-project-badge">{React.cloneElement(Icons.folder, { width: 12, height: 12 })} {projectMap[guide.projectId].name}</span>
                    ) : '-'}
                  </span>
                  <span className="dv-col-owner" title={guide.creatorName || guide.ownerName}>{guide.creatorName || guide.ownerName || '-'}</span>
                  <span className="dv-col-source">
                    <span className={`dv-source-badge ${guide.source === 'desktop' ? 'desktop' : 'extension'}`}>
                      {guide.source === 'desktop' ? Icons.desktop : Icons.chrome}
                    </span>
                    <MeetingBadge guide={guide} compact />
                  </span>
                  <span className="dv-col-date" title={guide.updatedAt ? formatGuideDateTooltip(guide.updatedAt) : ''}>{guide.updatedAt ? formatGuideDateWithTime(guide.updatedAt) : '-'}</span>
                  <span className="dv-col-actions" onClick={(e) => e.stopPropagation()}>
                    <button className="export-btn" onClick={() => onExport(guide.id, 'pdf')} disabled={exportingGuide.id === guide.id}>
                      {exportingGuide.id === guide.id && exportingGuide.format === 'pdf' ? '...' : 'PDF'}
                    </button>
                    <button className="export-btn" onClick={() => onExport(guide.id, 'html')} disabled={exportingGuide.id === guide.id}>
                      {exportingGuide.id === guide.id && exportingGuide.format === 'html' ? '...' : 'HTML'}
                    </button>
                    {!showSharedGuides && (
                      <button className="delete-btn" disabled={deletingGuideId === guide.id} onClick={() => onDeleteGuide(guide.id)}>
                        {deletingGuideId === guide.id ? <div className="delete-spinner" /> : Icons.trash}
                      </button>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="guides-grid">
            {displayedGuides.map((guide) => (
              <div key={guide.id} className="guide-card" onClick={() => onSelectGuide(guide.id)}>
                <div className="guide-preview">
                  <div className="guide-preview-favicon">
                    {(() => {
                      const faviconUrl = getFaviconWithFallback(guide);
                      return faviconUrl ? (
                        <img src={faviconUrl} alt={guide.domain || 'Site'} onError={(e) => { handleFaviconError(e); e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }} />
                      ) : null;
                    })()}
                    <span className="guide-preview-icon-svg" style={{ display: getFaviconWithFallback(guide) ? 'none' : 'flex' }}>{Icons.globe}</span>
                  </div>
                  {guide.domain && <span className="guide-domain">{guide.domain}</span>}
                  <span className={`guide-source-badge ${guide.source === 'desktop' ? 'desktop' : 'extension'}`} title={guide.source === 'desktop' ? 'Created with Desktop App' : 'Created with Chrome Extension'}>
                    {guide.source === 'desktop' ? Icons.desktop : Icons.chrome}
                  </span>
                  <MeetingBadge guide={guide} />
                </div>
                <div className="guide-info">
                  <h3 className="guide-name">{guide.name}</h3>
                  <p className="guide-steps">
                    <span>{guide.stepCount ?? guide.steps?.length ?? 0} steps</span>
                    {guide.updatedAt && <span className="guide-modified" title={formatGuideDateTooltip(guide.updatedAt)}>{formatGuideDateShort(guide.updatedAt)}</span>}
                  </p>
                  {(guide.projectId || guide.creatorName || (showSharedGuides && guide.ownerName)) && (
                    <div className="guide-meta-row">
                      {guide.projectId && projectMap[guide.projectId] ? (
                        <span className="guide-project-badge">{React.cloneElement(Icons.folder, { width: 12, height: 12 })} {projectMap[guide.projectId].name}</span>
                      ) : <span />}
                      {(guide.creatorName || (showSharedGuides && guide.ownerName)) && (
                        <span className="guide-owner-name" title={guide.creatorName || guide.ownerName}>{guide.creatorName || guide.ownerName}</span>
                      )}
                    </div>
                  )}
                </div>
                <div className="guide-actions">
                  <button
                    className="export-btn"
                    onClick={(e) => { e.stopPropagation(); onExport(guide.id, 'pdf'); }}
                    disabled={exportingGuide.id === guide.id}
                  >
                    {exportingGuide.id === guide.id && exportingGuide.format === 'pdf' ? <span className="btn-spinner-sm" /> : Icons.download} {exportingGuide.id === guide.id && exportingGuide.format === 'pdf' ? '...' : 'PDF'}
                  </button>
                  <button
                    className="export-btn"
                    onClick={(e) => { e.stopPropagation(); onExport(guide.id, 'html'); }}
                    disabled={exportingGuide.id === guide.id}
                  >
                    {exportingGuide.id === guide.id && exportingGuide.format === 'html' ? <span className="btn-spinner-sm" /> : Icons.globe} {exportingGuide.id === guide.id && exportingGuide.format === 'html' ? '...' : 'HTML'}
                  </button>
                  {!showSharedGuides && (
                    <button
                      className="delete-btn"
                      disabled={deletingGuideId === guide.id}
                      onClick={(e) => { e.stopPropagation(); onDeleteGuide(guide.id); }}
                    >
                      {deletingGuideId === guide.id ? <div className="delete-spinner" /> : Icons.trash}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

// In-browser audio recorder + Web Speech dictation buttons.
// MUST live at module scope (not inside GuideDetailView) so React preserves
// their identity across parent re-renders. When defined inside GuideDetailView
// every parent state change created a new component type at the JSX position,
// triggering unmount→cleanup→remount. For these components that's destructive:
// MicDictateButton.cleanup wipes the `wantsRef` user-intent latch and calls
// rec.stop() — so dictation appeared to "turn off by itself" whenever a
// sibling component caused a re-render (toast notifications, typing in the
// callout textarea while dictating into rich-text, etc.). Same hazard for
// AudioRecorderControl which tears down the MediaRecorder on cleanup.
const AudioRecorderControl = ({ onRecorded, disabled }) => {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState('');
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const tickRef = useRef(null);

  const cleanup = () => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    recorderRef.current = null;
    chunksRef.current = [];
  };

  useEffect(() => () => cleanup(), []);

  const start = async () => {
    setError('');
    if (recording) return;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setError('Mic recording isn\'t supported in this browser.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const recorder = new MediaRecorder(stream);
      recorderRef.current = recorder;
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        cleanup();
        setRecording(false);
        setElapsed(0);
        const reader = new FileReader();
        reader.onload = () => onRecorded(reader.result);
        reader.onerror = () => setError('Failed to read recording.');
        reader.readAsDataURL(blob);
      };
      recorder.start();
      setRecording(true);
      setElapsed(0);
      tickRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch (e) {
      setError(e.name === 'NotAllowedError' ? 'Mic permission denied.' : (e.message || 'Could not start recording.'));
      cleanup();
      setRecording(false);
    }
  };

  const stop = () => {
    try { recorderRef.current?.stop(); } catch (e) { /* ignore */ }
  };

  const mm = String(Math.floor(elapsed / 60)).padStart(2, '0');
  const ss = String(elapsed % 60).padStart(2, '0');

  const micIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="9" y="2" width="6" height="12" rx="3"/>
      <path d="M5 10v2a7 7 0 0 0 14 0v-2"/>
      <line x1="12" y1="19" x2="12" y2="22"/>
      <line x1="8" y1="22" x2="16" y2="22"/>
    </svg>
  );
  const stopIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <rect x="6" y="6" width="12" height="12" rx="2"/>
    </svg>
  );

  return (
    <div className="audio-recorder">
      {!recording ? (
        <button
          type="button"
          className="audio-recorder-btn record"
          onClick={start}
          disabled={disabled}
        >
          {micIcon}
          <span>Record</span>
        </button>
      ) : (
        <button
          type="button"
          className="audio-recorder-btn stop"
          onClick={stop}
        >
          {stopIcon}
          <span>Stop • {mm}:{ss}</span>
        </button>
      )}
      {error && <span className="audio-recorder-error">{error}</span>}
    </div>
  );
};

// Web Speech API dictation. continuous: true tells the browser to keep
// listening across pauses, but Chrome still auto-stops after ~5s of silence;
// we work around that with the wantsRef latch + onend auto-restart so the
// user keeps the button enabled until THEY click Stop.
const MicDictateButton = ({ onAppend, disabled }) => {
  const [recording, setRecording] = useState(false);
  const wantsRef = useRef(false);
  const recRef = useRef(null);
  const onAppendRef = useRef(onAppend);
  const SR = (typeof window !== 'undefined') && (window.SpeechRecognition || window.webkitSpeechRecognition);

  useEffect(() => { onAppendRef.current = onAppend; }, [onAppend]);

  useEffect(() => () => {
    wantsRef.current = false;
    try { recRef.current?.stop(); } catch {} // eslint-disable-line no-empty
    recRef.current = null;
  }, []);

  if (!SR) return null;

  const start = () => {
    if (recRef.current) return;
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'en-US';
    rec.onresult = (event) => {
      let finalText = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) finalText += event.results[i][0].transcript;
      }
      if (finalText) onAppendRef.current(finalText);
    };
    rec.onend = () => {
      recRef.current = null;
      if (wantsRef.current) {
        setTimeout(() => { if (wantsRef.current) start(); }, 120);
      } else {
        setRecording(false);
      }
    };
    rec.onerror = (e) => {
      if (['not-allowed', 'audio-capture', 'service-not-allowed'].includes(e.error)) {
        wantsRef.current = false;
        setRecording(false);
      }
    };
    try {
      recRef.current = rec;
      rec.start();
    } catch {
      recRef.current = null;
    }
  };

  const toggle = () => {
    if (wantsRef.current) {
      wantsRef.current = false;
      try { recRef.current?.stop(); } catch {} // eslint-disable-line no-empty
      setRecording(false);
      return;
    }
    wantsRef.current = true;
    setRecording(true);
    start();
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={disabled}
      className={`mic-dictate-btn ${recording ? 'recording' : ''}`}
      title={recording ? 'Stop dictation' : 'Click to dictate'}
      aria-label={recording ? 'Stop dictation' : 'Click to dictate'}
    >
      {recording ? (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <rect x="6" y="6" width="12" height="12" rx="2"/>
        </svg>
      ) : (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z"/>
          <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
          <line x1="12" y1="19" x2="12" y2="23"/>
          <line x1="8" y1="23" x2="16" y2="23"/>
        </svg>
      )}
      <span className="mic-dictate-label">{recording ? 'Stop' : 'Dictate'}</span>
    </button>
  );
};

// ============================================
// Embed Modal — owner-facing "Get embed code" UI
// ============================================
//
// Lives at module scope so it doesn't unmount on every GuideDetailView
// re-render (same lesson we learned with MicDictateButton).
//
// Renders one of two states:
//   (a) Not yet enabled → "Enable embed" CTA + branding hints (Pro/Enterprise)
//   (b) Enabled → iframe snippet (copy), URL (copy), settings form
//                 (theme, hide flow, height, optional branding for Enterprise),
//                 plus "Regenerate link" and "Disable embed" actions.
//
// Branding fields only render when the user's effective plan has
// customBranding=true (Enterprise). Otherwise we hide them so we're not
// teasing a feature they can't use.
// Inline button styles — the `.primary-btn` / `.secondary-btn` classes used
// elsewhere live inside view-scoped <style> blocks (LoginView, etc.) that
// aren't mounted when the EmbedModal renders. Reaching for them here would
// produce an unstyled-link look, so we use inline styles for portability.
const EMBED_BTN_PRIMARY = {
  padding: '10px 18px',
  borderRadius: 8,
  background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
  color: 'white',
  border: 'none',
  fontSize: 14,
  fontWeight: 700,
  cursor: 'pointer',
  letterSpacing: 0.3,
  boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)',
};
const EMBED_BTN_SECONDARY = {
  padding: '10px 18px',
  borderRadius: 8,
  border: '1px solid #cbd5e1',
  background: 'white',
  color: '#475569',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};
const EMBED_BTN_DANGER = {
  padding: '10px 18px',
  borderRadius: 8,
  border: '1px solid #fecaca',
  background: 'white',
  color: '#b91c1c',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

function EmbedModal({ guide, data, loading, planFeatures = {}, onSave, onDisable, onClose, showToast }) {
  const enabled = !!data?.enabled;
  const settings = data?.settings || {};
  const hasBranding = !!planFeatures.customBranding;

  // Local form state, hydrated from the saved settings.
  const [theme, setTheme] = useState(settings.theme || 'light');
  const [hideFlow, setHideFlow] = useState(!!settings.hideFlow);
  const [defaultHeight, setDefaultHeight] = useState(settings.defaultHeight || 720);
  const [brandingLogoUrl, setBrandingLogoUrl] = useState(settings.brandingLogoUrl || '');
  const [brandingPrimaryColor, setBrandingPrimaryColor] = useState(settings.brandingPrimaryColor || '#6366f1');

  // Re-hydrate when the parent reloads embed data (e.g. after Regenerate).
  // `settings` is derived from `data?.settings`, so depending on `data` is
  // sufficient — adding `settings` would cause an infinite loop because the
  // `|| {}` fallback produces a new object reference every render.
  useEffect(() => {
    setTheme(settings.theme || 'light');
    setHideFlow(!!settings.hideFlow);
    setDefaultHeight(settings.defaultHeight || 720);
    setBrandingLogoUrl(settings.brandingLogoUrl || '');
    setBrandingPrimaryColor(settings.brandingPrimaryColor || '#6366f1');
    // Intentionally not depending on `settings` (see comment above).
  }, [data]);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast?.(`${label} copied to clipboard`, 'success');
    } catch {
      showToast?.('Copy failed — select and copy manually', 'error');
    }
  };

  const commitSave = ({ regenerate = false } = {}) => {
    const payload = {
      settings: {
        theme,
        hideFlow,
        defaultHeight: Math.max(200, Math.min(4000, Number(defaultHeight) || 720)),
      },
      regenerate,
    };
    if (hasBranding) {
      payload.settings.brandingLogoUrl = brandingLogoUrl || null;
      payload.settings.brandingPrimaryColor = brandingPrimaryColor || null;
    }
    onSave(payload);
  };

  return (
    <div className="share-modal-overlay" onClick={onClose}>
      {/* Self-contained input styling — `.custom-dialog-input` is defined
          inside other components' style blocks that aren't mounted while
          EmbedModal renders. Scoping the styles here keeps the modal
          looking consistent regardless of where it's invoked from. */}
      <style>{`
        .embed-input, .embed-textarea {
          width: 100%; box-sizing: border-box;
          padding: 10px 14px; border-radius: 8px;
          border: 1px solid #e2e8f0; background: white;
          font-size: 14px; color: #0f172a;
          font-family: inherit; transition: all 0.15s;
        }
        .embed-input:focus, .embed-textarea:focus {
          outline: none; border-color: #6366f1;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
        }
        .embed-textarea {
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          font-size: 12px; resize: vertical; min-height: 70px;
        }
      `}</style>
      <div className="share-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }}>
        <div className="share-modal-header">
          <h3>Embed "{guide.name}"</h3>
          <button className="share-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="share-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {loading && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '32px 0', color: '#64748b' }}>
              <svg width="28" height="28" viewBox="0 0 50 50" aria-hidden="true">
                <circle cx="25" cy="25" r="20" fill="none" stroke="#6366f1" strokeOpacity="0.18" strokeWidth="4"/>
                <circle cx="25" cy="25" r="20" fill="none" stroke="#6366f1" strokeWidth="4" strokeLinecap="round" strokeDasharray="80 200">
                  <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="0.9s" repeatCount="indefinite"/>
                </circle>
              </svg>
              <span style={{ fontSize: 13 }}>Loading…</span>
            </div>
          )}

          {!loading && !enabled && (
            <>
              <p style={{ margin: 0, color: '#475569', fontSize: 14, lineHeight: 1.6 }}>
                Enable embed to get an iframe snippet you can paste into your help center,
                Notion page, or any website. Anyone with the link can view — no sign-in required.
              </p>
              <button
                type="button"
                onClick={() => commitSave()}
                style={{ ...EMBED_BTN_PRIMARY, alignSelf: 'flex-start' }}
              >
                Enable embed
              </button>
            </>
          )}

          {!loading && enabled && (
            <>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6 }}>Iframe snippet</label>
                {/* rows={4} fits the typical snippet without dominating
                    the modal. overflow: auto puts the scrollbar inside the
                    textarea on longer snippets; resize: vertical lets the
                    user drag it taller if needed. */}
                <textarea
                  readOnly
                  value={data.snippet || ''}
                  rows={4}
                  onClick={(e) => e.target.select()}
                  style={{ width: '100%', boxSizing: 'border-box', padding: 10, borderRadius: 8, border: '1px solid #e2e8f0', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12, background: '#f8fafc', resize: 'vertical', lineHeight: 1.5, overflow: 'auto', display: 'block' }}
                />
                <button
                  type="button"
                  onClick={() => copy(data.snippet, 'Snippet')}
                  style={{ marginTop: 6, padding: '6px 12px', borderRadius: 6, border: '1px solid #cbd5e1', background: 'white', color: '#475569', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                >Copy snippet</button>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6 }}>Direct link</label>
                <input
                  type="text"
                  readOnly
                  value={data.url || ''}
                  onClick={(e) => e.target.select()}
                  className="embed-input"
                  style={{ background: '#f8fafc', fontSize: 13 }}
                />
                <button
                  type="button"
                  onClick={() => copy(data.url, 'Link')}
                  style={{ marginTop: 6, padding: '6px 12px', borderRadius: 6, border: '1px solid #cbd5e1', background: 'white', color: '#475569', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                >Copy link</button>
              </div>

              <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 16, display: 'grid', gap: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6 }}>Theme</label>
                    <select value={theme} onChange={(e) => setTheme(e.target.value)} className="embed-input" style={{ background: 'white' }}>
                      <option value="light">Light</option>
                      <option value="dark">Dark</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6 }}>Default height (px)</label>
                    <input
                      type="number"
                      min={200} max={4000} step={20}
                      value={defaultHeight}
                      onChange={(e) => setDefaultHeight(e.target.value)}
                      className="embed-input"
                      style={{ background: 'white' }}
                    />
                  </div>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#475569', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={hideFlow}
                    onChange={(e) => setHideFlow(e.target.checked)}
                  />
                  Hide the process flow diagram
                </label>

                {hasBranding && (
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 14 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 10 }}>
                      Branding <span style={{ marginLeft: 6, color: '#94a3b8' }}>· Enterprise</span>
                    </div>

                    {/* Logo — accepts either a hosted URL (paste) OR a file
                        upload that we read as a base64 data URL. Either way
                        the value lands in `brandingLogoUrl` and gets saved
                        with the embed settings. Preview always reflects the
                        current value. */}
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6 }}>Logo</label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                      <input
                        type="url"
                        placeholder="Paste image URL (https://...)"
                        value={brandingLogoUrl?.startsWith('data:') ? '' : (brandingLogoUrl || '')}
                        onChange={(e) => setBrandingLogoUrl(e.target.value)}
                        className="embed-input"
                        style={{ background: 'white', flex: 1 }}
                      />
                      <label
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          padding: '8px 12px', borderRadius: 8,
                          border: '1px solid #cbd5e1', background: 'white',
                          color: '#475569', fontSize: 12, fontWeight: 600,
                          cursor: 'pointer', whiteSpace: 'nowrap',
                        }}
                        title="Upload a PNG, JPG, or SVG"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                          <polyline points="17 8 12 3 7 8"/>
                          <line x1="12" y1="3" x2="12" y2="15"/>
                        </svg>
                        Upload
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/svg+xml,image/webp"
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            // Hard-cap at ~500 KB so we don't bloat the
                            // embed_settings JSON. Most logos comfortably
                            // fit under 100 KB.
                            if (file.size > 500 * 1024) {
                              showToast?.('Logo is larger than 500 KB. Please resize or host externally and paste the URL.', 'error');
                              return;
                            }
                            const reader = new FileReader();
                            reader.onload = () => setBrandingLogoUrl(reader.result);
                            reader.onerror = () => showToast?.('Could not read the file', 'error');
                            reader.readAsDataURL(file);
                          }}
                        />
                      </label>
                      {brandingLogoUrl && (
                        <button
                          type="button"
                          onClick={() => setBrandingLogoUrl('')}
                          style={{
                            padding: '8px 10px', borderRadius: 8,
                            border: '1px solid #fecaca', background: 'white',
                            color: '#b91c1c', fontSize: 12, fontWeight: 600,
                            cursor: 'pointer',
                          }}
                          title="Remove logo"
                          aria-label="Remove logo"
                        >×</button>
                      )}
                    </div>
                    {brandingLogoUrl && (
                      <div style={{ marginTop: 8, padding: 10, borderRadius: 6, background: 'white', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 10 }}>
                        <img
                          src={brandingLogoUrl}
                          alt="Logo preview"
                          style={{ height: 32, maxWidth: 120, objectFit: 'contain' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <span style={{ fontSize: 11, color: '#94a3b8' }}>
                          {brandingLogoUrl.startsWith('data:') ? 'Uploaded file' : 'External URL'}
                        </span>
                      </div>
                    )}

                    {/* Primary color */}
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', margin: '14px 0 6px' }}>Primary color</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input
                        type="color"
                        value={brandingPrimaryColor}
                        onChange={(e) => setBrandingPrimaryColor(e.target.value)}
                        style={{ width: 48, height: 38, border: '1px solid #e2e8f0', borderRadius: 8, padding: 2, cursor: 'pointer', background: 'white', flexShrink: 0 }}
                      />
                      <input
                        type="text"
                        value={brandingPrimaryColor}
                        onChange={(e) => setBrandingPrimaryColor(e.target.value)}
                        placeholder="#6366f1"
                        className="embed-input"
                        style={{ background: 'white', flex: 1, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13 }}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', borderTop: '1px solid #e2e8f0', paddingTop: 16 }}>
                <button type="button" onClick={onDisable} style={EMBED_BTN_DANGER}>
                  Disable embed
                </button>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" onClick={() => commitSave({ regenerate: true })} style={EMBED_BTN_SECONDARY}>
                    Regenerate link
                  </button>
                  <button type="button" onClick={() => commitSave()} style={EMBED_BTN_PRIMARY}>
                    Save settings
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// Lightweight Markdown → React renderer. Used for AI-generated guide
// descriptions (which the backend prompt asks to return as Markdown so
// they read as scannable, formatted text instead of a single run-on
// sentence). Supports:
//   **bold**   → <strong>
//   `code`     → inline <code>
//   - bullets  → <ul><li>
//   blank-line blocks → separate <p>
//   single line breaks → <br>
// Anything fancier (headings, links, tables) is intentionally not handled —
// the prompt steers the model away from those, and a heavy markdown lib
// isn't worth the bundle weight for this surface.
function renderInlineMarkdown(text) {
  // Split on bold/code markers; rebuild as React fragments.
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return parts.map((p, i) => {
    if (/^\*\*.+\*\*$/.test(p)) return <strong key={i}>{p.slice(2, -2)}</strong>;
    if (/^`.+`$/.test(p)) return (
      <code key={i} style={{ background: '#f1f5f9', padding: '1px 5px', borderRadius: 4, fontSize: '0.92em', fontFamily: 'var(--font-mono, ui-monospace, SFMono-Regular, monospace)' }}>
        {p.slice(1, -1)}
      </code>
    );
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

function MarkdownText({ text, style }) {
  if (!text) return null;
  const blocks = text.replace(/\r\n/g, '\n').split(/\n\s*\n/).map(b => b.trim()).filter(Boolean);
  return (
    <div style={style}>
      {blocks.map((block, i) => {
        const lines = block.split('\n').map(l => l.replace(/\s+$/, '')).filter(l => l.length > 0);
        // Whole block is a bullet list when every line starts with - or *
        if (lines.length > 0 && lines.every(l => /^\s*[-*]\s+/.test(l))) {
          return (
            <ul key={i} style={{ margin: '0 0 12px', paddingLeft: 22, lineHeight: 1.6 }}>
              {lines.map((l, j) => (
                <li key={j} style={{ marginBottom: 4 }}>{renderInlineMarkdown(l.replace(/^\s*[-*]\s+/, ''))}</li>
              ))}
            </ul>
          );
        }
        return (
          <p key={i} style={{ margin: i === blocks.length - 1 ? 0 : '0 0 12px', lineHeight: 1.65 }}>
            {lines.map((l, j) => (
              <React.Fragment key={j}>
                {renderInlineMarkdown(l)}
                {j < lines.length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
}

// ============================================
// MEETING SUMMARY CARDS — structured summary for meeting-generated guides
//
// Renders the meeting brief attached to guide fetch responses on the
// dashboard's guide-detail page. Replaces the flattened markdown prose
// with the same card language the Review Segments UI uses so the two
// surfaces feel like the same product. Fallback: when the brief is
// absent (regular click-recorded guides) the caller uses MarkdownText
// on guide.description instead.
// ============================================

function formatSegmentTime(seconds) {
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

function formatDurationLabel(seconds) {
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  if (s < 60) return `~${Math.max(1, s)}s`;
  return `~${Math.round(s / 60)} min`;
}

function MeetingSummaryCards({ brief, onSegmentClick }) {
  // Segments collapsed by default — the list can be 8-12 rows tall on
  // long meetings, and the important info (overview + section headings
  // + participants) is already visible. User expands when they want
  // to jump to a specific topic.
  const [segmentsOpen, setSegmentsOpen] = React.useState(false);
  if (!brief) return null;
  const {
    source, sourceFilename, durationSeconds,
    overview, topLevelSections, shortRecap,
    segments, participants,
    scoped, selectedTopicCount, totalMeetingTopics,
  } = brief;

  const hasSections = Array.isArray(topLevelSections) && topLevelSections.length > 0;
  // When this guide only covers a single topic (per-segment mode), the
  // segments list would be a redundant one-row list. Hide it entirely
  // in that case — the topic IS the guide.
  const rawHasSegments = Array.isArray(segments) && segments.length > 0;
  const hasSegments = rawHasSegments && !(scoped && segments.length === 1);
  const hasParticipants = Array.isArray(participants) && participants.length > 0;
  // Subset combined guide: show a hint so the user knows this guide
  // was extracted from a larger meeting. Suppressed when only 1 topic
  // is covered (already reads as scoped from the empty segments list).
  const showSubsetHint = scoped
    && typeof selectedTopicCount === 'number'
    && typeof totalMeetingTopics === 'number'
    && selectedTopicCount > 1
    && totalMeetingTopics > selectedTopicCount;
  // Note: `outcomes` from the brief is intentionally NOT rendered here.
  // The Meeting Highlights card on the guide-detail page already shows
  // the same recap content (guide.meetingSummary is built from the
  // same outcomesOrActionItems payload), so re-rendering it here would
  // be a duplicate.

  const totalDurationLabel = durationSeconds
    ? (durationSeconds >= 60
        ? `${Math.floor(durationSeconds / 60)} min ${durationSeconds % 60}s`
        : `${durationSeconds}s`)
    : null;

  return (
    <div className="meeting-brief">
      {/* Overview card — meeting-level metadata + narrative overview */}
      <div className="mb-card">
        <div className="mb-card-header">
          <div className="mb-card-eyebrow">Meeting overview</div>
          <div className="mb-meta-row">
            {totalDurationLabel && (
              <span className="mb-meta">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                {totalDurationLabel}
              </span>
            )}
            {hasParticipants && (
              <span className="mb-meta">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
                {participants.length} {participants.length === 1 ? 'participant' : 'participants'}
              </span>
            )}
            {source === 'upload' && (
              <span className="mb-meta mb-source-tag">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                {sourceFilename || 'Uploaded recording'}
              </span>
            )}
            {source === 'live' && (
              <span className="mb-meta mb-source-tag mb-source-live">
                <span className="mb-live-dot" />
                Recorded live
              </span>
            )}
            {showSubsetHint && (
              <span className="mb-meta mb-scope-pill" title="This guide was extracted from a larger meeting recording">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
                {selectedTopicCount} of {totalMeetingTopics} topics
              </span>
            )}
          </div>
        </div>
        {overview && (
          <p className="mb-overview">{overview}</p>
        )}
        {hasSections && (
          <div className="mb-sections">
            {topLevelSections.map((sec, i) => (
              <div key={i} className="mb-section">
                <div className="mb-section-heading">{sec.heading}</div>
                {Array.isArray(sec.bullets) && sec.bullets.length > 0 && (
                  <ul className="mb-section-bullets">
                    {sec.bullets.map((b, j) => (
                      <li key={j}>{b}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}
        {!overview && !hasSections && shortRecap && (
          <p className="mb-overview">{shortRecap}</p>
        )}
        {/* Empty-state prompt — the meeting overview / segments / recap
            all come from analyzing an uploaded meeting transcript. For
            live-recorded meetings where a transcript hasn't been added
            yet, the card would otherwise appear blank (just a "Recorded
            live" pill floating alone). Prompt the user to upload the
            transcript so this card can populate. Per-step voice
            transcripts (from live mic capture) still render on each
            step card — this section is meeting-level only. */}
        {!overview && !hasSections && !shortRecap && (
          <p className="mb-overview mb-overview-empty" style={{ color: '#64748b', fontStyle: 'italic' }}>
            {source === 'live'
              ? 'Meeting overview + topic segments populate after you add a transcript. Use the “Add meeting transcript” button above to attach a Zoom / Teams / Meet export — the AI will produce a summary, sections, and clickable topic segments. Per-step voice transcripts from live recording still appear on individual step cards below.'
              : 'Meeting overview will populate once the transcript has been analyzed.'}
          </p>
        )}
      </div>

      {/* Segments card — collapsible, chronological topic list. Header
          is a button that toggles the list open/closed; count shown
          inline so the user knows the density before expanding. */}
      {hasSegments && (
        <div className="mb-card">
          <button
            type="button"
            className="mb-segments-toggle"
            onClick={() => setSegmentsOpen((v) => !v)}
            aria-expanded={segmentsOpen}
          >
            <span className="mb-card-eyebrow mb-card-eyebrow-inline">
              Segments <span className="mb-segments-count">· {segments.length}</span>
            </span>
            <svg
              width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
              className={`mb-chevron ${segmentsOpen ? 'mb-chevron-open' : ''}`}
              aria-hidden="true"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          {segmentsOpen && (
            <div className="mb-segments-list">
              {segments.map((seg) => (
                <div
                  key={seg.id}
                  className={`mb-segment ${onSegmentClick ? 'mb-segment-clickable' : ''}`}
                  onClick={onSegmentClick ? () => onSegmentClick(seg) : undefined}
                  role={onSegmentClick ? 'button' : undefined}
                  tabIndex={onSegmentClick ? 0 : undefined}
                >
                  <div className="mb-segment-header">
                    <div className="mb-segment-title">{seg.title}</div>
                  </div>
                  <div className="mb-segment-meta">
                    <span className={`mb-tag mb-tag-${seg.kind}`}>{seg.kind}</span>
                    <span className="mb-segment-time">
                      {formatSegmentTime(seg.startSeconds)} – {formatSegmentTime(seg.endSeconds)}
                    </span>
                    <span className="mb-segment-duration">{formatDurationLabel(seg.durationSeconds)}</span>
                    {seg.speaker && <span className="mb-segment-speaker">{seg.speaker}</span>}
                  </div>
                  {seg.summary && (
                    <p className="mb-segment-summary">{seg.summary}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Participants pill row */}
      {hasParticipants && (
        <div className="mb-card mb-participants-card">
          <div className="mb-card-eyebrow">Participants</div>
          <div className="mb-participants">
            {participants.map((p) => (
              <div key={p.id || p.displayName} className="mb-participant">
                <span
                  className="mb-participant-dot"
                  style={{ background: p.color || '#8b5cf6' }}
                >
                  {String(p.displayName || '?').slice(0, 1).toUpperCase()}
                </span>
                <div className="mb-participant-body">
                  <div className="mb-participant-name">
                    {p.displayName}
                    {p.isHost && <span className="mb-participant-host">Host</span>}
                  </div>
                  {p.role && <div className="mb-participant-role">{p.role}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <style>{`
        .meeting-brief {
          display: flex; flex-direction: column; gap: 14px;
        }
        .mb-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 18px 20px;
        }
        .mb-card-header {
          display: flex; flex-direction: column; gap: 8px;
          margin-bottom: 12px;
        }
        .mb-card-eyebrow {
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: #6366f1;
        }
        .mb-segments-count { color: #94a3b8; font-weight: 500; letter-spacing: 0; }
        .mb-meta-row {
          display: flex; flex-wrap: wrap; gap: 12px;
          align-items: center;
        }
        .mb-meta {
          display: inline-flex; align-items: center; gap: 5px;
          font-size: 12px; color: #64748b; font-weight: 500;
        }
        .mb-source-tag {
          background: #f1f5f9; border-radius: 6px;
          padding: 3px 8px;
          font-family: 'SFMono-Regular', Consolas, monospace;
          font-size: 11px;
          color: #475569;
        }
        .mb-source-live { background: #fef3c7; color: #92400e; font-family: inherit; }
        .mb-scope-pill {
          background: #eef2ff; color: #4338ca;
          border-radius: 6px; padding: 3px 8px;
          font-size: 11px; font-weight: 600;
        }
        .mb-live-dot {
          width: 6px; height: 6px; border-radius: 50%;
          background: #ef4444;
          box-shadow: 0 0 0 3px rgba(239,68,68,0.20);
          animation: mb-pulse 1.6s ease-in-out infinite;
        }
        @keyframes mb-pulse {
          0%, 100% { box-shadow: 0 0 0 3px rgba(239,68,68,0.20); }
          50% { box-shadow: 0 0 0 6px rgba(239,68,68,0.10); }
        }
        .mb-overview {
          margin: 0; font-size: 14px; line-height: 1.65; color: #1e293b;
        }
        .mb-sections {
          display: flex; flex-direction: column; gap: 12px;
          margin-top: 14px;
        }
        .mb-section-heading {
          font-size: 13.5px; font-weight: 700; color: #0f172a;
          margin-bottom: 6px;
        }
        .mb-section-bullets {
          margin: 0; padding-left: 20px;
          font-size: 13.5px; line-height: 1.6; color: #475569;
        }
        .mb-section-bullets li { margin: 3px 0; }

        .mb-segments-toggle {
          background: none; border: none; padding: 0;
          width: 100%;
          display: flex; align-items: center; justify-content: space-between;
          cursor: pointer;
          color: inherit;
          font: inherit;
        }
        .mb-segments-toggle:hover .mb-card-eyebrow-inline { color: #4f46e5; }
        .mb-card-eyebrow-inline { margin-bottom: 0; }
        .mb-chevron {
          color: #94a3b8;
          transition: transform 0.15s ease;
        }
        .mb-chevron-open { transform: rotate(180deg); color: #6366f1; }
        .mb-segments-list {
          display: flex; flex-direction: column; gap: 10px;
          margin-top: 12px;
        }
        .mb-segment {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          padding: 12px 14px;
          transition: all 0.15s;
        }
        .mb-segment-clickable { cursor: pointer; }
        .mb-segment-clickable:hover {
          border-color: #a5b4fc;
          background: #eef2ff;
        }
        .mb-segment-header { margin-bottom: 4px; }
        .mb-segment-title {
          font-size: 14px; font-weight: 700; color: #0f172a;
          line-height: 1.3;
        }
        .mb-segment-meta {
          display: flex; flex-wrap: wrap; align-items: center; gap: 10px;
          font-size: 11.5px; color: #64748b;
          margin: 4px 0 6px;
        }
        .mb-tag {
          display: inline-flex; align-items: center;
          padding: 2px 8px; border-radius: 999px;
          font-size: 11px; font-weight: 600; text-transform: capitalize;
        }
        .mb-tag-demo { background: #dcfce7; color: #166534; }
        .mb-tag-discussion { background: #e0e7ff; color: #4338ca; }
        .mb-tag-mixed { background: #fef3c7; color: #92400e; }
        .mb-segment-speaker {
          color: #64748b;
        }
        .mb-segment-summary {
          margin: 0; font-size: 13px; line-height: 1.55; color: #475569;
        }

        .mb-outcomes {
          margin: 0; padding-left: 20px;
          font-size: 13.5px; line-height: 1.6; color: #1e293b;
        }
        .mb-outcomes li { margin: 4px 0; }

        .mb-participants-card { padding: 16px 18px; }
        .mb-participants {
          display: flex; flex-wrap: wrap; gap: 8px;
        }
        .mb-participant {
          display: flex; align-items: center; gap: 8px;
          padding: 6px 10px 6px 6px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 999px;
        }
        .mb-participant-dot {
          display: inline-flex; align-items: center; justify-content: center;
          width: 22px; height: 22px; border-radius: 50%;
          color: white; font-size: 11px; font-weight: 700;
        }
        .mb-participant-body { line-height: 1.15; }
        .mb-participant-name {
          font-size: 12.5px; font-weight: 600; color: #0f172a;
          display: inline-flex; align-items: center; gap: 6px;
        }
        .mb-participant-host {
          font-size: 9.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em;
          background: #6366f1; color: white;
          padding: 1px 6px; border-radius: 4px;
        }
        .mb-participant-role {
          font-size: 10.5px; color: #64748b;
        }
      `}</style>
    </div>
  );
}

// ============================================
// SUGGESTION UI — auto-cleanup review surface (Sprint A)
//
// Banner + slide-in panel + per-suggestion card. Each component lives at
// module scope so re-renders of GuideDetailView don't unmount them (the
// same lesson that bit MicDictateButton / AudioRecorderControl pre-1.15.0).
//
// State + apply/dismiss/regenerate handlers live inside GuideDetailView
// because that's where `selectedGuide` is in scope. The UI components are
// pure — they receive data + callbacks and render.
// ============================================

// Full descriptive labels — used on each suggestion card as the type pill.
const SUGGESTION_TYPE_LABELS = {
  merge: 'Merge duplicates',
  rewrite: 'Rewrite description',
  add_context: 'Add context',
  attribute: 'Tag attribution',
  section_break: 'Add section break',
  normalize_voice: 'Normalize voice',
};

// Short noun forms — used in the banner breakdown ("2 merges, 1 rewrite").
// Plural is always the same noun with an 's' suffix; singular omits the 's'.
// Kept separate from the full labels so we don't run into the
// "Merge duplicatess" pluralization bug.
const SUGGESTION_TYPE_NOUN = {
  merge: 'merge',
  rewrite: 'rewrite',
  add_context: 'context insertion',
  attribute: 'attribution tag',
  section_break: 'section break',
  normalize_voice: 'voice fix',
};

const SUGGESTION_TYPE_COLORS = {
  merge: '#6366f1',
  rewrite: '#0ea5e9',
  add_context: '#10b981',
  attribute: '#a855f7',
  section_break: '#f59e0b',
  normalize_voice: '#06b6d4',
};

// Banner shown above the Process Flow when pending suggestions exist.
// Receives:
//   count       — total pending suggestions (already computed by parent)
//   breakdown   — array of { type, n } so we can render chips with per-type
//                 colors instead of a single comma-joined string. The string
//                 form previously had a pluralization bug ("merge duplicatess").
//   onReview    — open the review modal
//   onDismiss   — hide the banner for this guide (localStorage in parent)
//
// Design intent: feel like a "ready to act" surface, not a flat marketing
// banner. Soft inner glow + diagonal pattern + animated icon to suggest the
// content is freshly generated and worth attention.
function SuggestionBanner({ count, breakdown, onReview, onDismiss }) {
  return (
    <div
      style={{
        position: 'relative',
        background: 'linear-gradient(135deg, #4338ca 0%, #6366f1 55%, #818cf8 100%)',
        color: 'white',
        borderRadius: 14,
        padding: '16px 18px',
        margin: '0 0 20px 0',
        boxShadow: '0 6px 24px rgba(99,102,241,0.22), inset 0 1px rgba(255,255,255,0.18)',
        overflow: 'hidden',
      }}
    >
      {/* Decorative diagonal hatch — adds depth without competing for
          attention. Pointer events disabled so it doesn't intercept clicks. */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          background: 'repeating-linear-gradient(135deg, rgba(255,255,255,0.04) 0px, rgba(255,255,255,0.04) 1px, transparent 1px, transparent 22px)',
          pointerEvents: 'none',
        }}
      />
      {/* Soft glow blob behind the icon for an organic highlight */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: -30,
          left: -20,
          width: 140,
          height: 140,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.22) 0%, transparent 65%)',
          pointerEvents: 'none',
        }}
      />

      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        {/* Animated sparkle icon */}
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 44,
            height: 44,
            borderRadius: 12,
            background: 'rgba(255,255,255,0.18)',
            border: '1px solid rgba(255,255,255,0.22)',
            flexShrink: 0,
            animation: 'ig-banner-pulse 2.8s ease-in-out infinite',
          }}
          aria-hidden="true"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/>
            <path d="M20 3v4M22 5h-4M4 17v2M5 18H3"/>
          </svg>
        </span>

        {/* Heading + chip-style breakdown */}
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6, lineHeight: 1.25, letterSpacing: -0.1 }}>
            {count === 1 ? 'Insta found 1 improvement' : `Insta found ${count} improvements`}
          </div>
          {Array.isArray(breakdown) && breakdown.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {breakdown.map(({ type, n }) => {
                const noun = SUGGESTION_TYPE_NOUN[type] || type;
                return (
                  <span
                    key={type}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 11.5,
                      fontWeight: 700,
                      background: 'rgba(255,255,255,0.16)',
                      border: '1px solid rgba(255,255,255,0.18)',
                      borderRadius: 999,
                      padding: '3px 10px',
                      color: 'white',
                      letterSpacing: 0.1,
                    }}
                  >
                    <span style={{ fontWeight: 800, fontSize: 12 }}>{n}</span>
                    <span style={{ opacity: 0.88 }}>{n === 1 ? noun : noun + 's'}</span>
                  </span>
                );
              })}
            </div>
          )}
        </div>

        {/* Primary action + close */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <button
            onClick={onReview}
            style={{
              background: 'white',
              color: theme.secondary,
              border: 'none',
              borderRadius: 10,
              padding: '9px 18px',
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 18px rgba(0,0,0,0.22)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.18)'; }}
          >
            Review
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
            </svg>
          </button>
          <button
            onClick={onDismiss}
            aria-label="Dismiss suggestions"
            style={{
              background: 'rgba(255,255,255,0.10)',
              color: 'rgba(255,255,255,0.85)',
              border: '1px solid rgba(255,255,255,0.18)',
              borderRadius: 8,
              padding: 7,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              transition: 'background 0.15s ease, color 0.15s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.20)'; e.currentTarget.style.color = 'white'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.10)'; e.currentTarget.style.color = 'rgba(255,255,255,0.85)'; }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      <style>{`
        @keyframes ig-banner-pulse {
          0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255,255,255,0.22); }
          50%      { transform: scale(1.05); box-shadow: 0 0 0 6px rgba(255,255,255,0); }
        }
      `}</style>
    </div>
  );
}

function SuggestionCard({ suggestion, decision, onAccept, onReject, stepsById }) {
  const [showAffected, setShowAffected] = useState(false);
  const accent = SUGGESTION_TYPE_COLORS[suggestion.type] || theme.primary;
  const typeLabel = SUGGESTION_TYPE_LABELS[suggestion.type] || suggestion.type;
  const confidencePct = Math.round((suggestion.confidence || 0) * 100);

  // Pull a "current" and "proposed" view from the payload — shape depends
  // on suggestion type. For Sprint A the only type is `merge`, but the
  // case statements anticipate the others.
  let currentText = '';
  let proposedText = '';
  let affectedStepIds = [];
  let headlineText = '';

  if (suggestion.type === 'merge') {
    const { stepIds = [], survivorStepNumber, runLength, proposedDescription, survivorId } = suggestion.payload || {};
    affectedStepIds = stepIds;
    const survivor = stepsById?.[survivorId];
    headlineText = `Merge ${runLength || stepIds.length} duplicate ${runLength === 1 ? 'step' : 'steps'}${survivorStepNumber ? ` starting at step ${survivorStepNumber}` : ''}`;
    currentText = survivor?.description || survivor?.action || '(no current description)';
    proposedText = proposedDescription || '(keep first description, append others)';
  } else if (suggestion.type === 'rewrite' || suggestion.type === 'normalize_voice') {
    const { stepId, originalDescription, proposedDescription, proposedAction, stepNumber } = suggestion.payload || {};
    affectedStepIds = [stepId].filter(Boolean);
    const num = stepNumber ?? stepsById?.[stepId]?.number ?? '';
    // Title-only rewrite (Sprint B empty-action-title detector): proposedAction
    // is set, proposedDescription matches original. Render this as a "Retitle"
    // card so users understand WHICH field is changing.
    if (proposedAction && (!proposedDescription || proposedDescription === originalDescription)) {
      headlineText = `Retitle step ${num}`.trim();
      currentText = stepsById?.[stepId]?.action || '(empty title)';
      proposedText = proposedAction;
    } else {
      headlineText = `Rewrite step ${num}`.trim();
      currentText = originalDescription || stepsById?.[stepId]?.description || '';
      proposedText = proposedDescription || '';
    }
  } else if (suggestion.type === 'add_context') {
    const { afterStepId, proposedContent } = suggestion.payload || {};
    affectedStepIds = [afterStepId].filter(Boolean);
    headlineText = `Insert context after step ${stepsById?.[afterStepId]?.number ?? ''}`.trim();
    currentText = '(no context paragraph here today)';
    proposedText = proposedContent || '';
  } else if (suggestion.type === 'attribute') {
    const { stepId, proposedAttribution } = suggestion.payload || {};
    affectedStepIds = [stepId].filter(Boolean);
    headlineText = `Tag attribution on step ${stepsById?.[stepId]?.number ?? ''}`.trim();
    currentText = stepsById?.[stepId]?.attribution || '(no attribution)';
    proposedText = proposedAttribution || '';
  } else if (suggestion.type === 'section_break') {
    const { afterStepId, title } = suggestion.payload || {};
    affectedStepIds = [afterStepId].filter(Boolean);
    headlineText = `Insert "${title || 'Section'}" break after step ${stepsById?.[afterStepId]?.number ?? ''}`.trim();
    currentText = '(no section break here)';
    proposedText = title || '';
  }

  const isAccepted = decision === 'accept';
  const isRejected = decision === 'reject';
  const decided = isAccepted || isRejected;

  return (
    <div
      style={{
        border: `1px solid ${decided ? '#e2e8f0' : '#cbd5e1'}`,
        borderRadius: 12,
        padding: 14,
        marginBottom: 12,
        background: decided ? '#f8fafc' : 'white',
        opacity: decided ? 0.7 : 1,
        transition: 'opacity 0.2s, background 0.2s',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <span
          style={{
            background: `${accent}18`,
            color: accent,
            padding: '3px 8px',
            borderRadius: 6,
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
          }}
        >
          {typeLabel}
        </span>
        <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>
          confidence {confidencePct}%
        </span>
        {isAccepted && (
          <span style={{ fontSize: 11, color: '#059669', fontWeight: 700, marginLeft: 'auto' }}>
            ✓ Accepted
          </span>
        )}
        {isRejected && (
          <span style={{ fontSize: 11, color: '#64748b', fontWeight: 700, marginLeft: 'auto' }}>
            Rejected
          </span>
        )}
      </div>

      <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 8, lineHeight: 1.35 }}>
        {headlineText}
      </div>

      {currentText && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.5, color: '#94a3b8', fontWeight: 700, marginBottom: 3 }}>
            Current
          </div>
          <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.5, fontStyle: currentText.startsWith('(') ? 'italic' : 'normal' }}>
            {currentText.length > 220 ? currentText.slice(0, 220) + '…' : currentText}
          </div>
        </div>
      )}

      {proposedText && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.5, color: accent, fontWeight: 700, marginBottom: 3 }}>
            Proposed
          </div>
          <div style={{ fontSize: 13, color: '#0f172a', lineHeight: 1.5 }}>
            {proposedText.length > 280 ? proposedText.slice(0, 280) + '…' : proposedText}
          </div>
        </div>
      )}

      {suggestion.reason && (
        <div
          style={{
            fontSize: 11.5,
            color: '#64748b',
            background: '#f8fafc',
            padding: '8px 10px',
            borderRadius: 6,
            marginBottom: 10,
            lineHeight: 1.5,
          }}
        >
          <strong style={{ color: '#475569', marginRight: 6 }}>Why:</strong>
          {suggestion.reason}
        </div>
      )}

      {affectedStepIds.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <button
            type="button"
            onClick={() => setShowAffected(v => !v)}
            style={{
              background: 'transparent',
              border: 'none',
              color: theme.primary,
              fontSize: 11.5,
              fontWeight: 600,
              cursor: 'pointer',
              padding: 0,
            }}
          >
            {showAffected ? 'Hide' : 'Show'} affected step{affectedStepIds.length === 1 ? '' : 's'} ({affectedStepIds.length})
          </button>
          {showAffected && (
            <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {affectedStepIds.map(sid => {
                const s = stepsById?.[sid];
                if (!s) return (
                  <span key={sid} style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>
                    Step {sid.slice(0, 8)}… (not loaded)
                  </span>
                );
                return (
                  <div
                    key={sid}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 11,
                      color: '#475569',
                      background: '#f1f5f9',
                      padding: '3px 8px',
                      borderRadius: 6,
                    }}
                  >
                    <strong style={{ color: '#0f172a' }}>#{s.number}</strong>
                    <span style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.action || s.description?.slice(0, 50) || '(no label)'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button
          onClick={onReject}
          disabled={isRejected}
          style={{
            background: 'transparent',
            color: isRejected ? '#94a3b8' : '#475569',
            border: '1px solid #cbd5e1',
            borderRadius: 8,
            padding: '6px 14px',
            fontSize: 12.5,
            fontWeight: 600,
            cursor: isRejected ? 'default' : 'pointer',
          }}
        >
          {isRejected ? 'Rejected' : 'Reject'}
        </button>
        <button
          onClick={onAccept}
          disabled={isAccepted}
          style={{
            background: isAccepted ? '#10b98118' : theme.primary,
            color: isAccepted ? '#059669' : 'white',
            border: 'none',
            borderRadius: 8,
            padding: '6px 14px',
            fontSize: 12.5,
            fontWeight: 700,
            cursor: isAccepted ? 'default' : 'pointer',
          }}
        >
          {isAccepted ? 'Accepted' : 'Accept'}
        </button>
      </div>
    </div>
  );
}

function SuggestionReviewPanel({
  open,
  suggestions,
  stepsById,
  decisions,
  onDecide,
  onAcceptAllHighConfidence,
  onAcceptAll,
  onRejectAll,
  onResetDecisions,
  onApply,
  applying,
  onRegenerate,
  regenerating,
  onClose,
}) {
  const acceptedCount = Object.values(decisions).filter(d => d === 'accept').length;
  const rejectedCount = Object.values(decisions).filter(d => d === 'reject').length;
  const pendingCount = suggestions.length - acceptedCount - rejectedCount;
  const highConfCount = suggestions.filter(s => (s.confidence || 0) >= 0.8 && !decisions[s.id]).length;
  const anyDecided = acceptedCount > 0 || rejectedCount > 0;

  if (!open) return null;

  return (
    <>
      {/* Backdrop — click outside the modal to close. Higher z-index than
          regular modals so it sits on top of, say, the Embed modal if both
          were open (which shouldn't happen but is defensible). */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15,23,42,0.42)',
          backdropFilter: 'blur(3px)',
          zIndex: 998,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '4vh 4vw',
        }}
      >
        {/* Modal — centered, scrollable body. stopPropagation so clicking
            inside the modal doesn't bubble to the backdrop's close handler. */}
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="suggestion-panel-title"
          onClick={(e) => e.stopPropagation()}
          style={{
            background: '#f8fafc',
            borderRadius: 16,
            boxShadow: '0 24px 80px rgba(15,23,42,0.28)',
            width: 'min(640px, 100%)',
            maxHeight: '92vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            zIndex: 999,
          }}
        >
        {/* Header */}
        <div
          style={{
            padding: '16px 20px',
            background: 'white',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
          }}
        >
          <div style={{ flex: 1 }}>
            <h2 id="suggestion-panel-title" style={{ margin: 0, fontSize: 17, fontWeight: 800, color: '#0f172a' }}>
              Suggested improvements
            </h2>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
              {pendingCount} pending · {acceptedCount} accepted · {rejectedCount} rejected
            </div>
            <button
              type="button"
              onClick={onRegenerate}
              disabled={regenerating}
              style={{
                background: 'transparent',
                border: 'none',
                color: theme.primary,
                fontSize: 11.5,
                fontWeight: 600,
                cursor: regenerating ? 'wait' : 'pointer',
                padding: 0,
                marginTop: 6,
                textDecoration: 'underline',
                textUnderlineOffset: 2,
              }}
            >
              {regenerating ? 'Insta is re-analyzing…' : 'Ask Insta to re-analyze'}
            </button>
          </div>
          <button
            onClick={onClose}
            aria-label="Close suggestions"
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              borderRadius: 6,
              color: '#64748b',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body — scrollable card list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {suggestions.length === 0 && (
            <div
              style={{
                textAlign: 'center',
                color: '#64748b',
                fontSize: 13,
                padding: '40px 20px',
                background: 'white',
                border: '1px dashed #cbd5e1',
                borderRadius: 12,
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>
                Insta has nothing to suggest right now
              </div>
              <div>
                This guide looks good. Use <strong>Ask Insta to re-analyze</strong> above to scan again — useful after manual edits.
              </div>
            </div>
          )}
          {suggestions.map(s => (
            <SuggestionCard
              key={s.id}
              suggestion={s}
              decision={decisions[s.id]}
              stepsById={stepsById}
              onAccept={() => onDecide(s.id, 'accept')}
              onReject={() => onDecide(s.id, 'reject')}
            />
          ))}
        </div>

        {/* Footer — sticky bulk actions */}
        {suggestions.length > 0 && (
          <div
            style={{
              padding: '14px 20px',
              background: 'white',
              borderTop: '1px solid #e2e8f0',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            {/* Bulk action row — comes FIRST so users see their decision
                tools before the final commit. Apply is the LAST action,
                rendered below as the primary CTA. */}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
              {highConfCount > 0 && (
                <button
                  onClick={onAcceptAllHighConfidence}
                  style={{
                    background: 'transparent',
                    color: theme.primary,
                    border: `1.5px solid ${theme.primary}`,
                    borderRadius: 8,
                    padding: '8px 14px',
                    fontSize: 12.5,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  Accept high-confidence ({highConfCount})
                </button>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={onAcceptAll}
                  title="Accept every suggestion in this batch"
                  style={{
                    background: 'transparent',
                    color: '#15803d',
                    border: '1.5px solid #86efac',
                    borderRadius: 8,
                    padding: '8px 14px',
                    fontSize: 12.5,
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#f0fdf4'; e.currentTarget.style.borderColor = '#22c55e'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = '#86efac'; }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Accept all
                </button>
                <button
                  onClick={onRejectAll}
                  title="Reject every suggestion in this batch"
                  style={{
                    background: 'transparent',
                    color: '#b91c1c',
                    border: '1.5px solid #fca5a5',
                    borderRadius: 8,
                    padding: '8px 14px',
                    fontSize: 12.5,
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.borderColor = '#ef4444'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = '#fca5a5'; }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                  Reject all
                </button>
                <button
                  onClick={onResetDecisions}
                  title="Clear all decisions and start over"
                  style={{
                    background: 'transparent',
                    color: '#64748b',
                    border: '1.5px solid #cbd5e1',
                    borderRadius: 8,
                    padding: '8px 14px',
                    fontSize: 12.5,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#94a3b8'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                >
                  Reset
                </button>
              </div>
            </div>
            {/* Apply — the final commit action, placed at the BOTTOM of the
                footer so it reads as "do this last" after the user has made
                their per-card and bulk decisions above. */}
            <button
              onClick={onApply}
              disabled={!anyDecided || applying}
              style={{
                background: !anyDecided || applying
                  ? '#cbd5e1'
                  : 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                padding: '10px 16px',
                fontSize: 13.5,
                fontWeight: 700,
                cursor: !anyDecided || applying ? 'not-allowed' : 'pointer',
                boxShadow: !anyDecided || applying
                  ? 'none'
                  : '0 4px 14px rgba(124,58,237,0.32)',
                transition: 'transform 0.12s ease, box-shadow 0.12s ease',
              }}
              onMouseEnter={(e) => {
                if (anyDecided && !applying) {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 6px 18px rgba(124,58,237,0.40)';
                }
              }}
              onMouseLeave={(e) => {
                if (anyDecided && !applying) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 14px rgba(124,58,237,0.32)';
                }
              }}
            >
              {applying
                ? 'Applying…'
                : `Apply changes — ${acceptedCount} accepted, ${rejectedCount} rejected`}
            </button>
          </div>
        )}
        </div>
      </div>
    </>
  );
}

// Guide Detail Component
function GuideDetailView({ guide, loading, user, planFeatures = {}, projects = [], onBack, onExport, exportingGuide, onDelete, deletingGuideId, onUpdateGuide, onMoveGuide, onUpdateStep, onDeleteStep, deletingStepId, onReorderSteps, onShareGuide, getGuideShares, onRemoveShare, getOrgMembers, getGuideComments, getGuideChanges, onAddComment, onDeleteComment, showConfirm, showToast, setSelectedGuide, autoOpenShare, onClearAutoOpenShare, onPlanError }) {
  // Show loading state while guide is being fetched
  if (loading || !guide) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        gap: '20px',
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: '3px solid #e2e8f0',
          borderTopColor: theme.primary,
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }} />
        <p style={{
          color: '#64748b',
          fontSize: '15px',
          fontWeight: '500',
          margin: 0,
        }}>Loading guide...</p>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  const [editingStep, setEditingStep] = useState(null);
  const [editAction, setEditAction] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [zoomedStep, setZoomedStep] = useState(null);
  // Sprint J P2 — set of step IDs currently showing the un-redacted
  // original. Only meaningful when `guide.canViewOriginal === true`
  // AND the step itself has `hasOriginal === true`. Backend already
  // gated whether `screenshotOriginal` is even on the payload; this
  // toggle just swaps which of the two embedded assets renders.
  const [revealedSteps, setRevealedSteps] = useState(() => new Set());
  const toggleStepReveal = (stepId) => {
    setRevealedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(stepId)) next.delete(stepId);
      else next.add(stepId);
      return next;
    });
  };

  // Guide name editing state
  const [isEditingName, setIsEditingName] = useState(false);
  const [guideName, setGuideName] = useState(guide.name || '');

  // Guide description editing state
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [guideDescription, setGuideDescription] = useState(guide.description || '');
  const [generatingDescription, setGeneratingDescription] = useState(false);

  // Sprint K Phase 3 — VTT enrichment modal state. Only ever opened on
  // Meeting Mode guides; the trigger button only renders when meetingMode
  // is true, so this flag stays false on every other surface.
  const [showEnrichModal, setShowEnrichModal] = useState(false);
  // Sprint K Phase 3 — Participants editor modal (rename / recolor / host).
  // Trigger lives inside the participants strip on the meeting recap block.
  const [showParticipantsPanel, setShowParticipantsPanel] = useState(false);
  // Sprint K Phase 3 — Speaker filter. null = show all; speakerId string =
  // hide every step whose meetingAttribution.speakerId doesn't match. We
  // hide via CSS rather than filtering the array so InsertStepButton
  // positions, step numbers, and the React-Fragment keys all stay stable.
  const [speakerFilter, setSpeakerFilter] = useState(null);

  // Sharing state
  const [showShareModal, setShowShareModal] = useState(false);
  const [shares, setShares] = useState([]);
  const [orgMembers, setOrgMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [sharePermission, setSharePermission] = useState('view');
  const [sharingLoading, setSharingLoading] = useState(false);

  // Embed state — modal toggle + the response from GET/POST embed endpoints.
  // Lazy-loaded the first time the user opens the modal so we don't slow
  // every guide-detail page load with an embed status fetch.
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [embedData, setEmbedData] = useState(null);
  const [embedLoading, setEmbedLoading] = useState(false);

  // ── Improvement-suggestion state ──
  // `pendingSuggestions` is hydrated from the guide payload (the GET /guides/:id
  // endpoint now inlines pending suggestions so the banner appears on the
  // same paint as the guide itself — previously a separate fetch caused a
  // visible "banner pops in late" delay). A focused refetch happens after
  // apply / regenerate.
  // `suggestionDecisions` is local-only until the user clicks Apply — that's
  // what lets us show "Accepted" pills on cards without mutating data yet.
  // `bannerDismissed` reads from localStorage so a dismissed banner stays
  // hidden through page reloads (suggestions are still in the DB though).
  const [pendingSuggestions, setPendingSuggestions] = useState(Array.isArray(guide?.pendingSuggestions) ? guide.pendingSuggestions : []);
  const [showSuggestionPanel, setShowSuggestionPanel] = useState(false);
  const [suggestionDecisions, setSuggestionDecisions] = useState({});
  const [applyingSuggestions, setApplyingSuggestions] = useState(false);
  const [regeneratingSuggestions, setRegeneratingSuggestions] = useState(false);
  // Flag set right after a successful Apply so the slim "Want Insta to scan
  // this guide?" banner doesn't immediately re-appear when the panel closes
  // — that combination reads as "you just applied; want to do it again?"
  // which is confusing. Reset when the user navigates to a different guide
  // (the useEffect below watches guide.id).
  const [justAppliedSuggestions, setJustAppliedSuggestions] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(`ig-suggestions-dismissed-${guide?.id || ''}`) === '1';
  });

  // Lookup map for step IDs → step objects, used by SuggestionCard to render
  // affected-step thumbnails without an extra fetch.
  const stepsById = (guide?.steps || []).reduce((acc, s) => { acc[s.id] = s; return acc; }, {});

  // Sync inline suggestions from the guide payload + re-check dismissed
  // state when the guide id changes. The fetch is no longer needed — the
  // guide endpoint inlines pendingSuggestions on every load.
  useEffect(() => {
    if (!guide?.id) return;
    setPendingSuggestions(Array.isArray(guide?.pendingSuggestions) ? guide.pendingSuggestions : []);
    setBannerDismissed(
      typeof window !== 'undefined' &&
      window.localStorage.getItem(`ig-suggestions-dismissed-${guide.id}`) === '1'
    );
    // Reset the just-applied suppression flag when the user navigates to
    // a different guide — fresh guide, fresh state.
    setJustAppliedSuggestions(false);
    // eslint-disable-next-line
  }, [guide?.id, guide?.pendingSuggestions]);

  // Build the per-type breakdown for the banner chips. Array of
  // { type, n } so the banner renders one chip per type with the correct
  // singular/plural noun (avoids the "merge duplicatess" bug).
  const suggestionBreakdown = (() => {
    if (pendingSuggestions.length === 0) return [];
    const counts = {};
    pendingSuggestions.forEach(s => { counts[s.type] = (counts[s.type] || 0) + 1; });
    return Object.entries(counts).map(([type, n]) => ({ type, n }));
  })();

  const handleDismissBanner = () => {
    if (typeof window !== 'undefined' && guide?.id) {
      window.localStorage.setItem(`ig-suggestions-dismissed-${guide.id}`, '1');
    }
    setBannerDismissed(true);
  };

  const handleOpenSuggestionPanel = () => {
    setSuggestionDecisions({});
    setShowSuggestionPanel(true);
  };

  const handleSuggestionDecide = (id, decision) => {
    setSuggestionDecisions(prev => {
      const next = { ...prev };
      if (next[id] === decision) {
        delete next[id];   // clicking twice un-decides
      } else {
        next[id] = decision;
      }
      return next;
    });
  };

  const acceptAllHighConfidence = () => {
    const next = { ...suggestionDecisions };
    pendingSuggestions.forEach(s => {
      if ((s.confidence || 0) >= 0.8 && !next[s.id]) next[s.id] = 'accept';
    });
    setSuggestionDecisions(next);
  };
  const acceptAllSuggestions = () => {
    const next = { ...suggestionDecisions };
    pendingSuggestions.forEach(s => { next[s.id] = 'accept'; });
    setSuggestionDecisions(next);
  };
  const rejectAllSuggestions = () => {
    const next = { ...suggestionDecisions };
    pendingSuggestions.forEach(s => { next[s.id] = 'reject'; });
    setSuggestionDecisions(next);
  };
  const resetSuggestionDecisions = () => setSuggestionDecisions({});

  const handleApplySuggestions = async () => {
    const acceptedIds = Object.entries(suggestionDecisions).filter(([_, d]) => d === 'accept').map(([id]) => id);
    const rejectedIds = Object.entries(suggestionDecisions).filter(([_, d]) => d === 'reject').map(([id]) => id);
    if (acceptedIds.length + rejectedIds.length === 0) return;

    setApplyingSuggestions(true);
    try {
      const r = await fetch(`${API_URL}/api/guides/${guide.id}/suggestions/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
        body: JSON.stringify({ acceptedIds, rejectedIds }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        showToast(body?.error || `Apply failed (HTTP ${r.status})`, 'error');
        return;
      }
      const data = await r.json();
      // Refresh the guide in the parent state. The apply endpoint returned a
      // fully-hydrated payload, identical in shape to GET /api/guides/:id.
      if (data.guide && setSelectedGuide) {
        setSelectedGuide(data.guide);
      }
      // Remove applied/rejected suggestions from the local pending list.
      const handledIds = new Set([
        ...(data.applied || []).map(a => a.id),
        ...rejectedIds,
        ...(data.skipped || []).map(s => s.id),
      ]);
      setPendingSuggestions(prev => prev.filter(s => !handledIds.has(s.id)));
      setSuggestionDecisions({});

      // Toast — show applied count + warn if any got skipped
      const appliedCount = (data.applied || []).length;
      const skippedCount = (data.skipped || []).length;
      let msg = `Applied ${appliedCount} change${appliedCount === 1 ? '' : 's'}`;
      if (rejectedIds.length > 0) msg += `, rejected ${rejectedIds.length}`;
      if (skippedCount > 0) msg += ` — ${skippedCount} skipped (steps may have changed)`;
      showToast(msg, appliedCount > 0 ? 'success' : 'info');

      // Always close the panel after Apply. The toast above gives the user
      // their confirmation; keeping the panel open with a 0/0/0 "nothing to
      // suggest" empty state right after they clicked Apply is jarring.
      setShowSuggestionPanel(false);
      setJustAppliedSuggestions(true);
    } catch (e) {
      showToast(e.message || 'Network error applying suggestions', 'error');
    } finally {
      setApplyingSuggestions(false);
    }
  };

  const handleRegenerateSuggestions = async () => {
    if (!guide?.id) return;
    setRegeneratingSuggestions(true);
    try {
      const authHeader = { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` };
      // Re-analyze fires TWO requests in parallel:
      //   1. /suggestions/regenerate — the detector pipeline (merges, rewrites, etc.)
      //   2. /generate-description   — a fresh guide-level summary using the AI
      // The description regen is best-effort: if it fails (no AI provider,
      // plan gate, etc.) we still surface the suggestions result.
      const [suggestionResp, descriptionResp] = await Promise.all([
        fetch(`${API_URL}/api/guides/${guide.id}/suggestions/regenerate`, {
          method: 'POST',
          headers: authHeader,
        }),
        fetch(`${API_URL}/api/guides/${guide.id}/generate-description`, {
          method: 'POST',
          headers: authHeader,
        }).catch(() => null),  // network errors here shouldn't fail the whole re-analyze
      ]);
      if (!suggestionResp.ok) {
        const body = await suggestionResp.json().catch(() => ({}));
        showToast(body?.error || `Re-analyze failed (HTTP ${suggestionResp.status})`, 'error');
        return;
      }
      const data = await suggestionResp.json();
      const fresh = Array.isArray(data.suggestions) ? data.suggestions : [];
      setPendingSuggestions(fresh);
      setSuggestionDecisions({});

      // Update local guide.description if the description regen succeeded.
      // Silent failure for description is intentional — the suggestions path
      // is the primary outcome of "re-analyze."
      let descriptionUpdated = false;
      if (descriptionResp && descriptionResp.ok) {
        try {
          const dd = await descriptionResp.json();
          if (dd?.description && typeof onUpdateGuide === 'function') {
            // updateGuide signature varies; just patch local state via the
            // existing setSelectedGuide hook so the description block re-renders.
            setGuideDescription(dd.description);
            if (setSelectedGuide) {
              setSelectedGuide(prev => prev ? { ...prev, description: dd.description } : prev);
            }
            descriptionUpdated = true;
          }
        } catch { /* ignore parse errors — keep current description */ }
      }

      // Re-show the banner since this is a deliberate user action.
      if (typeof window !== 'undefined' && guide?.id) {
        window.localStorage.removeItem(`ig-suggestions-dismissed-${guide.id}`);
      }
      setBannerDismissed(false);

      // Auto-apply branch: high-confidence suggestions were applied silently
      // by the backend. If any auto-applied AND there are step mutations,
      // refresh the guide payload so the dashboard shows the post-apply state.
      const autoAppliedCount = data.autoAppliedCount || 0;
      if (autoAppliedCount > 0) {
        try {
          const guideResp = await fetch(`${API_URL}/api/guides/${guide.id}`, { headers: authHeader });
          if (guideResp.ok) {
            const refreshedGuide = await guideResp.json();
            if (setSelectedGuide) setSelectedGuide(refreshedGuide);
          }
        } catch { /* non-fatal — user can refresh manually */ }
      }

      // Compose toast — auto-applied + for-review counts in one message.
      const summaryNote = descriptionUpdated ? ' Summary refreshed.' : '';
      const reviewNote = fresh.length > 0
        ? ` ${fresh.length} more for review.`
        : '';
      const autoNote = autoAppliedCount > 0
        ? `Insta auto-applied ${autoAppliedCount} fix${autoAppliedCount === 1 ? '' : 'es'}.`
        : '';
      let msg;
      if (autoAppliedCount > 0 && fresh.length > 0) {
        msg = `${autoNote}${reviewNote}${summaryNote}`;
      } else if (autoAppliedCount > 0) {
        msg = `${autoNote}${summaryNote} Guide looks good!`;
      } else if (fresh.length > 0) {
        msg = `Insta found ${fresh.length} improvement${fresh.length === 1 ? '' : 's'}.${summaryNote}`;
      } else {
        msg = `Insta found no improvements — your guide looks good!${summaryNote}`;
      }
      showToast(msg, fresh.length === 0 && autoAppliedCount === 0 ? 'success' : 'info');
    } catch (e) {
      showToast(e.message || 'Network error', 'error');
    } finally {
      setRegeneratingSuggestions(false);
    }
  };

  // Auto-open share modal if requested (from quick share button on guide cards)
  useEffect(() => {
    if (autoOpenShare && guide && user?.accountType === 'organization') {
      openShareModal();
      onClearAutoOpenShare?.();
    }
  }, [autoOpenShare, guide]);

  // Comments state
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [showComments, setShowComments] = useState(true);
  const [stepCommentingOn, setStepCommentingOn] = useState(null);
  const [stepComment, setStepComment] = useState('');

  // Change history state
  const [changes, setChanges] = useState([]);
  const [showChangesPanel, setShowChangesPanel] = useState(false);
  const [changesLoading, setChangesLoading] = useState(false);

  // Insert step state
  const [showInsertModal, setShowInsertModal] = useState(false);
  const [insertingAtPosition, setInsertingAtPosition] = useState(0);
  // null = picker view; otherwise the type the user is composing:
  // 'click' | 'rich_text' | 'audio_note' | 'callout' | 'image' | 'section_divider'
  const [insertStepType, setInsertStepType] = useState(null);
  // Draft for manual step insertion. Per-type fields are reused as needed.
  const [manualDraft, setManualDraft] = useState({
    content: '', title: '', severity: 'tip', audioUrl: '', imageData: ''
  });
  const [manualSubmitting, setManualSubmitting] = useState(false);

  // Project move state
  const [movingProject, setMovingProject] = useState(false);

  // Scroll to top button state
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Load shares, comments, and changes when guide changes
  useEffect(() => {
    if (guide?.id) {
      loadShares();
      loadComments();
      loadChanges();
    }
  }, [guide?.id]);

  // Auto-generate description if guide doesn't have one and has steps
  useEffect(() => {
    if (guide?.id && !guide.description && guide.steps?.length > 0 && !generatingDescription) {
      handleGenerateDescription();
    }
  }, [guide?.id, guide.steps?.length]);

  // Track scroll position for scroll-to-top button
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const loadShares = async () => {
    if (getGuideShares) {
      const data = await getGuideShares(guide.id);
      setShares(data);
    }
  };

  const loadComments = async () => {
    if (getGuideComments) {
      const data = await getGuideComments(guide.id);
      setComments(data);
    }
  };

  const loadChanges = async () => {
    if (getGuideChanges) {
      setChangesLoading(true);
      const data = await getGuideChanges(guide.id);
      setChanges(data);
      setChangesLoading(false);
    }
  };

  const getChangeDescription = (change) => {
    switch (change.action) {
      case 'guide_created': return 'Created this guide';
      case 'name_updated': return `Renamed to "${change.details?.newName || 'Untitled'}"`;
      case 'description_updated': return 'Updated guide description';
      case 'description_generated': return 'InstaGuide generated description';
      case 'workflow_generated': return 'Generated workflow summaries';
      case 'step_added': return `Added step ${change.details?.stepNumber || ''}`;
      case 'step_inserted': return `Inserted step at position ${change.details?.stepNumber || ''}`;
      case 'step_updated': return `Edited step ${change.details?.stepNumber || ''}`;
      case 'step_deleted': return 'Deleted a step';
      case 'steps_reordered': return 'Reordered steps';
      case 'steps_renumbered': return 'Renumbered steps';
      case 'duplicates_removed': return `Removed ${change.details?.removedCount || ''} duplicate steps`;
      case 'guide_shared': return 'Shared this guide';
      case 'share_updated': return 'Updated share permissions';
      case 'share_removed': return 'Removed a share';
      case 'guide_moved': return 'Moved to a different project';
      case 'suggestions_reviewed': {
        const d = change.details || {};
        const accepted = d.acceptedCount || 0;
        const rejected = d.rejectedCount || 0;
        const parts = [];
        if (accepted > 0) parts.push(`${accepted} applied`);
        if (rejected > 0) parts.push(`${rejected} rejected`);
        // Type breakdown if we have it — e.g. "2 merges, 5 rewrites"
        const byType = d.acceptedByType || {};
        const typeLabels = {
          merge: 'merge',
          rewrite: 'rewrite',
          add_context: 'context add',
          attribute: 'attribution',
          section_break: 'section break',
          normalize_voice: 'voice fix',
        };
        const breakdown = Object.entries(byType)
          .map(([t, n]) => `${n} ${typeLabels[t] || t}${n === 1 ? '' : 's'}`)
          .join(', ');
        const head = `Reviewed Insta improvements — ${parts.join(', ')}`;
        return breakdown ? `${head} (${breakdown})` : head;
      }
      case 'suggestions_auto_applied': {
        const d = change.details || {};
        const count = d.autoAppliedCount || 0;
        const byType = d.autoAppliedByType || {};
        const typeLabels = {
          merge: 'merge',
          rewrite: 'rewrite',
          add_context: 'context add',
          section_break: 'section break',
        };
        const breakdown = Object.entries(byType)
          .map(([t, n]) => `${n} ${typeLabels[t] || t}${n === 1 ? '' : 's'}`)
          .join(', ');
        const head = `Insta auto-applied ${count} high-confidence fix${count === 1 ? '' : 'es'}`;
        return breakdown ? `${head} (${breakdown})` : head;
      }
      default: return change.action.replace(/_/g, ' ');
    }
  };

  const getChangeIcon = (action) => {
    if (action.startsWith('step_') || action === 'steps_reordered' || action === 'steps_renumbered' || action === 'duplicates_removed') return '📝';
    if (action.includes('share')) return '👥';
    if (action.includes('name') || action.includes('description')) return '✏️';
    if (action.includes('generated') || action.includes('workflow')) return '🤖';
    if (action === 'guide_created') return '✨';
    if (action === 'guide_moved') return '📁';
    return '📋';
  };

  const formatTimeAgo = (dateStr) => {
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMs / 3600000);
    const diffDay = Math.floor(diffMs / 86400000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHr < 24) return `${diffHr}h ago`;
    if (diffDay < 7) return `${diffDay}d ago`;
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  // Scroll to a specific step when clicking on flowchart node
  const scrollToStep = (stepId) => {
    const stepElement = document.getElementById(`step-${stepId}`);
    if (stepElement) {
      stepElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      stepElement.classList.add('step-card-highlight');
      setTimeout(() => stepElement.classList.remove('step-card-highlight'), 1500);
    }
  };

  // Ask Insta step-thumbnail cards link to /guide/<id>?step=<N> and the
  // App-level checkGuideUrl() drops the N into sessionStorage. Once the
  // guide + its steps are loaded, translate the stepNumber (1-indexed
  // position in the guide) into the internal step.id, then scroll to
  // that step card and briefly highlight it. sessionStorage is cleared
  // after consumption so a subsequent direct-nav to /guide/<id> without
  // a step param opens at the top like before.
  useEffect(() => {
    if (!guide?.steps?.length) return;
    const pendingRaw = sessionStorage.getItem('pendingStepNumber');
    if (!pendingRaw) return;
    const pendingNum = Number(pendingRaw);
    if (!Number.isFinite(pendingNum) || pendingNum <= 0) {
      sessionStorage.removeItem('pendingStepNumber');
      return;
    }
    // Match on step.number (server-emitted 1-indexed position).
    const target = guide.steps.find(s => Number(s.number) === pendingNum);
    if (target) {
      // Defer one frame so the step card is actually painted before
      // scrollIntoView tries to find it — the map above runs on the
      // same render but geometry hasn't settled yet.
      requestAnimationFrame(() => scrollToStep(target.id));
    }
    sessionStorage.removeItem('pendingStepNumber');
  }, [guide?.id, guide?.steps?.length]);

  const openShareModal = async () => {
    setSharingLoading(true);
    if (getOrgMembers) {
      const members = await getOrgMembers();
      // Filter out current user and already shared users
      const sharedUserIds = shares.map(s => s.sharedWithUserId);
      const availableMembers = members.filter(m => m.id !== user?.id && !sharedUserIds.includes(m.id));
      setOrgMembers(availableMembers);
    }
    setSharingLoading(false);
    setShowShareModal(true);
  };

  // ── Embed handlers ────────────────────────────────────────────────────────
  // Open the modal + fetch current embed status. Plan-gated by the backend
  // — Pro / Enterprise return the status; lower plans 403 with the standard
  // plan-error shape, which we surface as the existing UpgradePromptDialog.
  const openEmbedModal = async () => {
    setShowEmbedModal(true);
    setEmbedLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/guides/${guide.id}/embed`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
      });
      if (r.ok) {
        setEmbedData(await r.json());
      } else if (r.status === 403) {
        const body = await r.json().catch(() => ({}));
        if (body?.code === 'FEATURE_NOT_AVAILABLE' && onPlanError) onPlanError(body);
        setShowEmbedModal(false);
      } else {
        showToast(`Failed to load embed status (HTTP ${r.status})`, 'error');
        setShowEmbedModal(false);
      }
    } catch (e) {
      showToast(e.message || 'Network error', 'error');
      setShowEmbedModal(false);
    } finally {
      setEmbedLoading(false);
    }
  };

  // Enable / update settings. `regenerate=true` mints a fresh token, which
  // invalidates any iframe URL that's already out in the wild.
  const saveEmbed = async ({ settings, regenerate = false } = {}) => {
    setEmbedLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/guides/${guide.id}/embed/enable`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
        body: JSON.stringify({ settings, regenerate }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        if (r.status === 403 && body?.code === 'FEATURE_NOT_AVAILABLE' && onPlanError) {
          onPlanError(body);
        } else {
          showToast(body?.error || `Failed (HTTP ${r.status})`, 'error');
        }
        return;
      }
      const data = await r.json();
      setEmbedData(data);
      showToast(regenerate ? 'Embed link regenerated' : 'Embed enabled', 'success');
    } catch (e) {
      showToast(e.message || 'Network error', 'error');
    } finally {
      setEmbedLoading(false);
    }
  };

  const disableEmbed = async () => {
    setEmbedLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/guides/${guide.id}/embed`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
      });
      if (!r.ok) {
        showToast(`Failed to disable (HTTP ${r.status})`, 'error');
        return;
      }
      setEmbedData({ enabled: false, settings: {} });
      showToast('Embed disabled', 'success');
    } catch (e) {
      showToast(e.message || 'Network error', 'error');
    } finally {
      setEmbedLoading(false);
    }
  };

  const handleShare = async () => {
    if (selectedMembers.length === 0) return;
    setSharingLoading(true);
    await onShareGuide(guide.id, selectedMembers, sharePermission);
    await loadShares();
    setSelectedMembers([]);
    setShowShareModal(false);
    setSharingLoading(false);
  };

  const handleRemoveShareClick = (userId) => {
    showConfirm('Remove Access', 'Are you sure you want to remove this user\'s access to the guide?', async () => {
      await onRemoveShare(guide.id, userId);
      await loadShares();
    }, { type: 'danger', confirmText: 'Remove Access', cancelText: 'Cancel' });
  };

  const handleUpdateSharePermission = async (userId, permission) => {
    try {
      const response = await fetch(`${API_URL}/api/guides/${guide.id}/shares/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ permission })
      });
      if (response.ok) {
        await loadShares();
      }
    } catch (error) {
      console.error('Failed to update share permission:', error);
    }
  };

  const handleAddCommentClick = async (stepId = null) => {
    const content = stepId ? stepComment : newComment;
    if (!content.trim()) return;
    setCommentLoading(true);
    const comment = await onAddComment(guide.id, content, stepId);
    if (comment) {
      setComments([...comments, comment]);
      if (stepId) {
        setStepComment('');
        setStepCommentingOn(null);
      } else {
        setNewComment('');
      }
    }
    setCommentLoading(false);
  };

  // Get comments for a specific step
  const getStepComments = (stepId) => {
    return comments.filter(c => c.stepId === stepId);
  };

  // Get general comments (not attached to any step)
  const getGeneralComments = () => {
    return comments.filter(c => !c.stepId);
  };

  const handleDeleteCommentClick = (commentId) => {
    showConfirm('Delete Comment', 'Are you sure you want to delete this comment?', async () => {
      const success = await onDeleteComment(guide.id, commentId);
      if (success) {
        setComments(comments.filter(c => c.id !== commentId));
      }
    }, { type: 'danger', confirmText: 'Delete', cancelText: 'Cancel' });
  };

  const isOwner = guide.userId === user?.id;
  const canEdit = isOwner || guide.sharePermission === 'edit';

  const handleSaveName = async () => {
    if (onUpdateGuide && guideName.trim()) {
      const success = await onUpdateGuide(guide.id, { name: guideName.trim() });
      if (success) {
        setIsEditingName(false);
      }
    }
  };

  const handleSaveDescription = async () => {
    if (onUpdateGuide) {
      const success = await onUpdateGuide(guide.id, { description: guideDescription });
      if (success) {
        setIsEditingDescription(false);
      }
    }
  };

  const handleProjectChange = async (e) => {
    const targetProjectId = e.target.value;
    if (!targetProjectId || targetProjectId === guide.projectId) return;

    setMovingProject(true);
    if (onMoveGuide) {
      await onMoveGuide(guide.id, targetProjectId);
    }
    setMovingProject(false);
  };

  const handleGenerateDescription = async () => {
    setGeneratingDescription(true);
    // Hard 60s ceiling. If the request hangs (slow provider, dying
    // backend mid-recording, dropped connection), we still clear the
    // "Insta is analyzing…" banner so the page becomes usable. The
    // user can retry from the Auto-generate button.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60_000);
    try {
      const API_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:3001';
      const response = await fetch(`${API_URL}/api/guides/${guide.id}/generate-description`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        signal: controller.signal,
      });
      if (response.ok) {
        const data = await response.json();
        setGuideDescription(data.description);
        // Also update the guide in parent
        if (onUpdateGuide) {
          await onUpdateGuide(guide.id, { description: data.description });
        }
      } else {
        if (onPlanError && await onPlanError(response)) {
          // Plan-error modal shown; bail without console noise
        } else {
          console.error('Failed to generate description');
        }
      }
    } catch (error) {
      if (error?.name === 'AbortError') {
        console.warn('Description generation timed out after 60s — clearing banner');
      } else {
        console.error('Failed to generate description', error);
      }
    } finally {
      // finally guarantees the banner clears even if a render between
      // try and the closing brace throws — defensive belt-and-suspenders.
      clearTimeout(timeoutId);
      setGeneratingDescription(false);
    }
  };

  const handleEditStart = (step) => {
    setEditingStep(step.id);
    setEditAction(step.action);
    setEditDescription(step.description);
  };

  const handleEditSave = async (stepId) => {
    if (onUpdateStep) {
      await onUpdateStep(guide.id, stepId, { action: editAction, description: editDescription });
    }
    setEditingStep(null);
  };

  const handleEditCancel = () => {
    setEditingStep(null);
  };

  // Handle insert step - communicate with extension
  const handleInsertStep = (insertAfterPosition) => {
    // Store insertion context for the extension
    const insertionData = {
      guideId: guide.id,
      guideName: guide.name,
      insertAfterPosition,
      timestamp: Date.now()
    };

    console.log('[Dashboard] handleInsertStep called:', insertionData);

    // Store in sessionStorage for cross-tab communication
    sessionStorage.setItem('instaguide_insertion', JSON.stringify(insertionData));

    // Method 1: Try window.postMessage
    window.postMessage({
      type: 'INSTAGUIDE_CONTINUE_RECORDING',
      data: insertionData
    }, '*');

    // Method 2: Dispatch custom event (more reliable)
    const customEvent = new CustomEvent('instaguide-insert-step', {
      detail: insertionData
    });
    document.dispatchEvent(customEvent);

    // Method 3: Set a data attribute on body for content script to read
    document.body.setAttribute('data-instaguide-pending', JSON.stringify(insertionData));

    console.log('[Dashboard] Insert step request sent via multiple methods');

    // Show instruction modal
    setInsertingAtPosition(insertAfterPosition);
    setInsertStepType(null); // open in picker view
    setShowInsertModal(true);
  };

  // Submit a manual (non-click) step. Posts to the same /steps/insert
  // endpoint but with a stepType in the body so the backend skips AI
  // description / voice transcription.
  const handleInsertManualStep = async () => {
    if (!insertStepType || insertStepType === 'click') return;

    // Per-type required-field validation. Mirrors the server-side check.
    if (insertStepType === 'rich_text' && !manualDraft.content.trim()) {
      showToast('Add some text before saving', 'error');
      return;
    }
    if (insertStepType === 'callout' && !manualDraft.content.trim()) {
      showToast('Add some text before saving', 'error');
      return;
    }
    if (insertStepType === 'audio_note' && !manualDraft.audioUrl) {
      showToast('Record or upload audio before saving', 'error');
      return;
    }
    if (insertStepType === 'image' && !manualDraft.imageData) {
      showToast('Upload an image before saving', 'error');
      return;
    }
    if (insertStepType === 'section_divider' && !manualDraft.title.trim()) {
      showToast('Section needs a title', 'error');
      return;
    }

    setManualSubmitting(true);
    // 30s timeout so a hung backend doesn't lock the button in "Inserting…"
    // state forever. AbortController on the fetch fires the catch with a
    // clear message instead.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    try {
      const body = {
        stepType: insertStepType,
        insertAfterPosition: insertingAtPosition,
        content: manualDraft.content,
        title: manualDraft.title,
        severity: insertStepType === 'callout' ? manualDraft.severity : undefined,
        audioUrl: insertStepType === 'audio_note' ? manualDraft.audioUrl : undefined,
        imageData: insertStepType === 'image' ? manualDraft.imageData : undefined,
      };
      const response = await fetch(`${API_URL}/api/guides/${guide.id}/steps/insert`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to insert step');
      }

      const newStep = await response.json();

      // Splice the new step into the guide in place and renumber the rest.
      setSelectedGuide(prev => {
        if (!prev) return prev;
        const before = (prev.steps || []).filter(s => s.number < newStep.number);
        const after = (prev.steps || [])
          .filter(s => s.number >= newStep.number)
          .map(s => ({ ...s, number: s.number + 1 }));
        return { ...prev, steps: [...before, newStep, ...after] };
      });

      // Reset the draft and close.
      setManualDraft({ content: '', title: '', severity: 'tip', audioUrl: '', imageData: '' });
      setInsertStepType(null);
      setShowInsertModal(false);
      showToast('Step inserted', 'success');
    } catch (error) {
      if (error.name === 'AbortError') {
        showToast('Insert timed out — check that the backend is running and try again.', 'error');
      } else {
        showToast(error.message || 'Failed to insert step', 'error');
      }
    } finally {
      clearTimeout(timeoutId);
      setManualSubmitting(false);
    }
  };

  // Read a File as a base64 data URL — used by the image and audio uploaders
  // inside the Insert Step modal.
  const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  // AudioRecorderControl + MicDictateButton are defined at module scope
  // (above GuideDetailView). They MUST live outside this component or React
  // remounts them on every parent re-render and tears down the active
  // recording / dictation session.

  // Helper that appends dictated text to a manualDraft field with a space
  // separator. Uses functional setState to stay correct under rapid updates.
  const appendDraft = (field) => (text) => {
    setManualDraft((d) => ({
      ...d,
      [field]: (d[field] ? d[field].replace(/\s+$/, '') + ' ' : '') + text.trim()
    }));
  };

  // AI Enhance button — sends the current text to the backend, gets a polished
  // version back, replaces the field. Opt-in (user clicks): no surprise edits.
  // Gated behind the same `aiDescriptions` plan feature as step descriptions.
  const AIEnhanceButton = ({ text, onEnhanced, disabled }) => {
    const [busy, setBusy] = useState(false);

    const enhance = async () => {
      if (!text || !text.trim()) {
        showToast('Add some text first, then enhance', 'error');
        return;
      }
      setBusy(true);
      try {
        const response = await fetch(`${API_URL}/api/ai/enhance-text`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          },
          body: JSON.stringify({ text }),
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.error || 'Enhance with Insta failed');
        }
        const data = await response.json();
        if (data.enhancedText) {
          onEnhanced(data.enhancedText);
          showToast('Text enhanced', 'success');
        }
      } catch (error) {
        showToast(error.message, 'error');
      } finally {
        setBusy(false);
      }
    };

    return (
      <button
        type="button"
        onClick={enhance}
        disabled={disabled || busy}
        className={`ai-enhance-btn ${busy ? 'busy' : ''}`}
        title="Polish this text with Insta"
        aria-label="Enhance with Insta"
      >
        {busy ? (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="ai-enhance-spinner" aria-hidden="true">
            <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
          </svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M11.5 3 13.5 7.5 18 9.5 13.5 11.5 11.5 16 9.5 11.5 5 9.5 9.5 7.5 11.5 3z"/>
            <path d="M18 14 18.75 16 21 16.75 18.75 17.5 18 19.5 17.25 17.5 15 16.75 17.25 16 18 14z"/>
          </svg>
        )}
        <span>{busy ? 'Enhancing…' : 'Enhance with Insta'}</span>
      </button>
    );
  };

  // InsertStepButton component
  const InsertStepButton = ({ insertAfterPosition }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <div
        className="insert-step-container"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className={`insert-step-line ${isHovered ? 'visible' : ''}`} />
        <button
          className={`insert-step-btn ${isHovered ? 'visible' : ''}`}
          onClick={() => handleInsertStep(insertAfterPosition)}
          title={insertAfterPosition === 0 ? 'Insert step at beginning' : `Insert step after step ${insertAfterPosition}`}
        >
          <span className="insert-icon">+</span>
          <span>Insert Step</span>
        </button>
        <div className={`insert-step-line ${isHovered ? 'visible' : ''}`} />
      </div>
    );
  };

  return (
    <div className="detail">
      <style>{`
        .detail {
          min-height: 100vh;
          background: #f8fafc;
        }
        .detail-header {
          background: white;
          padding: 20px 32px;
          border-bottom: 1px solid #e2e8f0;
          display: flex;
          align-items: center;
          gap: 20px;
          position: sticky;
          top: 0;
          z-index: 100;
        }
        .back-btn {
          background: #f1f5f9;
          border: none;
          font-size: 14px;
          font-weight: 600;
          color: #64748b;
          cursor: pointer;
          padding: 10px 16px;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .back-btn:hover {
          background: #e2e8f0;
        }
        .detail-title {
          flex: 1;
          font-size: 22px;
          font-weight: 700;
          color: #1e293b;
          margin: 0;
        }
        .title-display {
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .title-display:hover .title-edit-icon {
          opacity: 1;
        }
        .title-edit-icon {
          font-size: 14px;
          opacity: 0.3;
          transition: opacity 0.2s;
        }
        .title-edit-container {
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .title-edit-input {
          font-size: 22px;
          font-weight: 700;
          color: #1e293b;
          border: 2px solid ${theme.primary};
          border-radius: 8px;
          padding: 4px 12px;
          outline: none;
          min-width: 200px;
        }
        .title-edit-btn {
          width: 32px;
          height: 32px;
          border-radius: 6px;
          border: none;
          cursor: pointer;
          font-size: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .title-edit-btn.save {
          background: #10b981;
          color: #fff;
        }
        .title-edit-btn.save:hover {
          background: #059669;
        }
        .title-edit-btn.cancel {
          background: #f1f5f9;
          color: #64748b;
        }
        .title-edit-btn.cancel:hover {
          background: #e2e8f0;
        }
        .detail-actions {
          display: flex;
          gap: 12px;
        }
        .detail-btn {
          padding: 10px 16px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          border: none;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          white-space: nowrap;
        }
        .detail-btn svg {
          width: 16px;
          height: 16px;
          flex-shrink: 0;
        }
        .detail-btn-primary {
          background: ${theme.gradient};
          color: white;
        }
        .detail-btn-secondary {
          background: #f1f5f9;
          color: #475569;
        }
        .detail-btn-danger {
          background: #fef2f2;
          color: #dc2626;
        }
        .detail-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .detail-btn-danger:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
        .detail-delete-spinner {
          width: 14px;
          height: 14px;
          border: 2px solid #fecaca;
          border-top-color: #dc2626;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
        .btn-spinner {
          display: inline-block;
          width: 14px;
          height: 14px;
          border: 2px solid rgba(255,255,255,0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          vertical-align: middle;
          flex-shrink: 0;
        }
        .btn-spinner-sm {
          display: inline-block;
          width: 14px;
          height: 14px;
          border: 2.5px solid #e2e8f0;
          border-top-color: ${theme.primary};
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          vertical-align: middle;
          flex-shrink: 0;
        }
        .detail-main {
          max-width: 1400px;
          margin: 0 auto;
          padding: 40px 32px;
        }
        .step-card {
          background: white;
          border-radius: 14px;
          padding: 28px;
          margin-bottom: 12px;
          display: flex;
          gap: 24px;
          border: 1px solid #e2e8f0;
          transition: border-color 0.15s;
          /* scroll-margin-top clears the sticky top nav + guide header
             when scrollIntoView lands here (segment card click → step
             jump). Without this, the step's top edge lines up with the
             viewport top and the sticky header covers the top half of
             the card. Value picked to clear both header layers with a
             small breathing gap above. */
          scroll-margin-top: 96px;
        }
        .step-card:hover {
          border-color: #cbd5e1;
        }
        /* Section divider — Notion-style H2. NOT a step card. No sidebar
           (no numbered circle, no reorder / delete buttons in the rail).
           Reorder / delete controls surface on hover via .section-divider-actions
           so the section reads as a chapter heading, not a step. Same shape
           applied to the embed viewer + HTML/PDF exports for parity. */
        .step-card.step-type-section_divider {
          background: transparent;
          border: none;
          border-radius: 0;
          box-shadow: none;
          padding: 0;
          margin: 32px 0 12px 0;
          position: relative;
          display: block;    /* Cancel the flex layout of the base step-card */
        }
        .step-card.step-type-section_divider:hover { border: none; }
        /* Hide the numbered-circle + reorder rail entirely on section dividers. */
        .step-card.step-type-section_divider .step-sidebar { display: none; }
        .step-card.step-type-section_divider .step-content {
          padding: 6px 0 6px 16px;
          border-left: 4px solid #6366f1;
          position: relative;
        }
        .step-card.step-type-section_divider .step-header {
          margin: 0;
          padding: 0;
          background: transparent;
        }
        .step-card.step-type-section_divider .step-content h3 {
          font-size: 22px;
          font-weight: 800;
          color: #0f172a;
          letter-spacing: -0.3px;
          margin: 0;
          padding: 0;
        }
        .step-card.step-type-section_divider .step-content p {
          margin: 4px 0 0;
          font-size: 13.5px;
          color: #64748b;
          line-height: 1.5;
        }
        /* Reorder / delete controls appear as a small hover-only cluster
           to the right so we don't lose them entirely. Positioned
           absolutely so the section heading itself doesn't shift. */
        .step-card.step-type-section_divider .section-divider-actions {
          position: absolute;
          top: 6px;
          right: 0;
          display: flex;
          gap: 4px;
          opacity: 0;
          transition: opacity 0.15s;
        }
        .step-card.step-type-section_divider:hover .section-divider-actions {
          opacity: 1;
        }
        .step-card.step-type-section_divider .section-divider-actions button {
          width: 28px; height: 28px;
          border-radius: 6px;
          background: #f1f5f9;
          border: 1px solid #e2e8f0;
          color: #64748b;
          cursor: pointer;
          font-size: 13px;
          display: flex; align-items: center; justify-content: center;
          transition: all 0.15s;
        }
        .step-card.step-type-section_divider .section-divider-actions button:hover:not(:disabled) {
          background: #e2e8f0;
          color: #0f172a;
        }
        .step-card.step-type-section_divider .section-divider-actions button:disabled {
          opacity: 0.4; cursor: not-allowed;
        }
        .step-card.step-type-section_divider .section-divider-actions .delete-btn:hover {
          background: #fee2e2; border-color: #fecaca; color: #dc2626;
        }

        /* Insert Step Button */
        .insert-step-container {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 4px 0;
          margin: 0 0 12px 0;
          min-height: 32px;
        }
        .insert-step-line {
          flex: 1;
          height: 2px;
          background: linear-gradient(135deg, transparent, ${theme.primary}, transparent);
          opacity: 0;
          transition: opacity 0.2s;
        }
        .insert-step-line.visible {
          opacity: 0.4;
        }
        .insert-step-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 14px;
          background: ${theme.gradient};
          color: white;
          border: none;
          border-radius: 20px;
          cursor: pointer;
          font-size: 12px;
          font-weight: 600;
          opacity: 0.3;
          transform: scale(0.9);
          transition: all 0.2s;
          box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
          white-space: nowrap;
        }
        .insert-step-btn.visible,
        .insert-step-btn:hover {
          opacity: 1;
          transform: scale(1);
        }
        .insert-step-btn:hover {
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
          transform: scale(1.05);
        }
        .insert-icon {
          font-size: 14px;
          font-weight: bold;
        }

        /* Insert Modal */
        .insert-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        .insert-modal {
          background: white;
          border-radius: 16px;
          width: 100%;
          max-width: 480px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          overflow: hidden;
        }
        .insert-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid #e2e8f0;
        }
        .insert-modal-header h3 {
          margin: 0;
          font-size: 18px;
          font-weight: 700;
          color: #1e293b;
        }
        .insert-modal-close {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: #64748b;
          padding: 0;
          line-height: 1;
        }
        .insert-modal-close:hover {
          color: #1e293b;
        }
        .insert-modal-body {
          padding: 24px;
        }
        .insert-modal-body p {
          margin: 0 0 20px 0;
          color: #64748b;
          font-size: 14px;
        }
        .insert-modal-instructions {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .instruction-step {
          display: flex;
          align-items: flex-start;
          gap: 12px;
        }
        .instruction-number {
          width: 24px;
          height: 24px;
          background: ${theme.gradient};
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: 700;
          flex-shrink: 0;
        }
        .instruction-step span:last-child {
          color: #1e293b;
          font-size: 14px;
          line-height: 1.5;
        }
        .insert-modal-footer {
          padding: 16px 24px;
          border-top: 1px solid #e2e8f0;
          display: flex;
          justify-content: flex-end;
        }
        .insert-modal-btn {
          padding: 10px 24px;
          background: ${theme.gradient};
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }
        .insert-modal-btn:hover {
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
          transform: translateY(-1px);
        }
        .insert-modal-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }
        /* Wider modal when the user is inside a per-type form. */
        .insert-modal { max-width: 560px; }

        /* Picker view */
        .insert-type-picker { display: flex; flex-direction: column; gap: 8px; }
        .insert-type-option {
          display: flex; align-items: center; gap: 14px;
          padding: 12px 14px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          cursor: pointer;
          text-align: left;
          transition: all 0.15s;
        }
        .insert-type-option:hover {
          background: #ffffff;
          border-color: #6366f1;
          box-shadow: 0 4px 12px rgba(99,102,241,0.08);
        }
        .insert-type-icon {
          width: 36px; height: 36px;
          display: flex; align-items: center; justify-content: center;
          background: rgba(99,102,241,0.10);
          border-radius: 8px;
          flex-shrink: 0;
          color: #6366f1;            /* SVGs inherit via currentColor */
        }
        .insert-type-option:hover .insert-type-icon {
          background: rgba(99,102,241,0.16);
        }
        .insert-type-text { display: flex; flex-direction: column; gap: 2px; }
        .insert-type-label { font-size: 14px; font-weight: 700; color: #0f172a; }
        .insert-type-sub { font-size: 12px; color: #64748b; }

        /* Manual form (rich text / callout / image / audio / section) */
        .manual-form { display: flex; flex-direction: column; gap: 8px; }
        .manual-label { font-size: 12px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.4px; margin-top: 4px; }
        .manual-label-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: 4px; }
        .manual-label-row .manual-label { margin-top: 0; }
        .manual-or-divider {
          display: flex; align-items: center; gap: 10px;
          margin: 8px 0 2px;
          font-size: 11px; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.4px;
        }
        .manual-or-divider::before, .manual-or-divider::after {
          content: ''; flex: 1; height: 1px; background: #e2e8f0;
        }
        .manual-hint { margin: 6px 0 0; font-size: 12px; color: #64748b; }
        .manual-clear-btn {
          margin-top: 6px; padding: 4px 10px; border-radius: 6px;
          background: #f8fafc; border: 1px solid #e2e8f0; color: #475569;
          font-size: 12px; cursor: pointer;
        }
        .manual-clear-btn:hover { background: #f1f5f9; }
        .manual-clear-btn:disabled { opacity: 0.5; cursor: not-allowed; }

        /* Mic recorder for audio_note */
        .audio-recorder { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .audio-recorder-btn {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 8px 14px; border-radius: 10px;
          border: 1px solid transparent;
          font-size: 13px; font-weight: 700;
          cursor: pointer; transition: all 0.15s;
        }
        .audio-recorder-btn .material-symbols-outlined { font-size: 18px; line-height: 1; }
        .audio-recorder-btn.record {
          background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
          color: white;
          box-shadow: 0 2px 8px rgba(99, 102, 241, 0.35);
        }
        .audio-recorder-btn.record:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(99, 102, 241, 0.5); }
        .audio-recorder-btn.record:disabled { opacity: 0.5; cursor: not-allowed; transform: none; box-shadow: none; }
        .audio-recorder-btn.stop {
          background: #fee2e2; color: #b91c1c; border-color: #fca5a5;
          animation: mic-pulse 1.4s ease-in-out infinite;
        }
        .audio-recorder-error { color: #b91c1c; font-size: 12px; }

        /* Styled file picker for the audio_note / image upload paths.
         * The native <input type="file"> is hidden; the <label> is the
         * visible button so it can match the rest of the form. */
        .manual-file-label {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 8px 14px; border-radius: 10px;
          border: 1px solid #e2e8f0; background: white;
          color: #475569; font-size: 13px; font-weight: 600;
          cursor: pointer; transition: all 0.15s;
          user-select: none; width: fit-content;
        }
        .manual-file-label:hover { border-color: #c7d2fe; background: #f8fafc; color: #4f46e5; }
        .manual-file-name {
          display: inline-block; margin-top: 6px;
          font-size: 12px; color: #64748b;
          font-style: italic;
        }

        /* Mic dictation button (Web Speech API) */
        .mic-dictate-btn {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 5px 10px; border-radius: 999px;
          background: #eef2ff; border: 1px solid #c7d2fe;
          color: #4f46e5; font-size: 11px; font-weight: 700; letter-spacing: 0.3px;
          text-transform: uppercase; cursor: pointer;
          transition: all 0.15s;
        }
        .mic-dictate-btn:hover { background: #e0e7ff; border-color: #a5b4fc; }
        .mic-dictate-btn.recording {
          background: #fee2e2; border-color: #fca5a5; color: #b91c1c;
          animation: mic-pulse 1.4s ease-in-out infinite;
        }
        .mic-dictate-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .mic-dictate-label { line-height: 1; }
        @keyframes mic-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.45); }
          50% { box-shadow: 0 0 0 8px rgba(239, 68, 68, 0); }
        }

        /* AI Enhance button (sits next to Dictate) */
        .manual-input-actions { display: flex; align-items: center; gap: 8px; }
        .ai-enhance-btn {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 5px 10px; border-radius: 999px;
          background: linear-gradient(135deg, #ede9fe 0%, #f3e8ff 100%);
          border: 1px solid #c4b5fd; color: #6d28d9;
          font-size: 11px; font-weight: 700; letter-spacing: 0.3px;
          text-transform: uppercase; cursor: pointer;
          transition: all 0.15s;
        }
        .ai-enhance-btn:hover:not(:disabled) {
          background: linear-gradient(135deg, #ddd6fe 0%, #e9d5ff 100%);
          border-color: #a78bfa;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px -2px rgba(139, 92, 246, 0.35);
        }
        .ai-enhance-btn:disabled { opacity: 0.55; cursor: not-allowed; }
        .ai-enhance-btn.busy { opacity: 0.85; cursor: progress; }
        .ai-enhance-spinner {
          animation: ai-spin 0.9s linear infinite;
          transform-origin: center;
        }
        @keyframes ai-spin {
          to { transform: rotate(360deg); }
        }
        .manual-input,
        .manual-textarea {
          width: 100%; box-sizing: border-box;
          padding: 9px 12px; border: 1px solid #e2e8f0; border-radius: 8px;
          font-size: 14px; color: #0f172a; font-family: inherit;
          background: white;
        }
        .manual-input:focus,
        .manual-textarea:focus { outline: none; border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99,102,241,0.15); }
        .manual-textarea { resize: vertical; min-height: 80px; line-height: 1.5; }

        /* Severity picker (callouts) */
        .severity-picker { display: flex; gap: 8px; }
        .severity-pill {
          padding: 6px 14px;
          border: 1px solid #e2e8f0; background: white;
          border-radius: 999px; cursor: pointer;
          font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px;
          color: #64748b;
        }
        .severity-pill.severity-tip.active { background: #dbeafe; color: #1d4ed8; border-color: #93c5fd; }
        .severity-pill.severity-warning.active { background: #fef3c7; color: #b45309; border-color: #fcd34d; }
        .severity-pill.severity-critical.active { background: #fee2e2; color: #b91c1c; border-color: #fca5a5; }

        /* Non-click step content styles */
        .step-rich-text {
          white-space: pre-wrap;
          font-size: 15px; line-height: 1.65; color: #1e293b;
          padding: 4px 0 8px;
        }
        .step-callout {
          display: flex; gap: 12px; align-items: flex-start;
          padding: 14px 16px; border-radius: 10px;
          border-left: 4px solid #93c5fd;
          background: #eff6ff; color: #1e3a8a;
          font-size: 14.5px; line-height: 1.55;
        }
        .step-callout.severity-warning {
          background: #fffbeb; border-left-color: #f59e0b; color: #78350f;
        }
        .step-callout.severity-critical {
          background: #fef2f2; border-left-color: #ef4444; color: #7f1d1d;
        }
        .step-callout-icon {
          flex-shrink: 0;
          width: 26px; height: 26px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          font-weight: 700; font-size: 14px;
          background: rgba(255,255,255,0.7);
        }
        .step-callout-body { flex: 1; }
        .step-image {
          max-width: 100%; height: auto; display: block;
          border-radius: 10px; border: 1px solid #e2e8f0;
          margin-bottom: 8px;
        }
        .step-audio {
          width: 100%; margin-bottom: 8px;
        }
        .step-section-subtitle {
          font-size: 14px; color: #64748b; margin: 4px 0 8px; line-height: 1.5;
        }

        .scroll-to-top-btn {
          position: fixed;
          bottom: 32px;
          right: 32px;
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: ${theme.gradient};
          color: white;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          transition: all 0.3s ease;
          z-index: 1000;
          opacity: 0;
          transform: translateY(20px);
          pointer-events: none;
        }
        .scroll-to-top-btn.visible {
          opacity: 1;
          transform: translateY(0);
          pointer-events: auto;
        }
        .scroll-to-top-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
        }
        .scroll-to-top-btn svg {
          width: 24px;
          height: 24px;
        }
        /* Changes Sidebar (inline, right of steps) */
        .detail-main.with-changes-panel {
          display: flex;
          gap: 0;
          max-width: 100%;
          padding: 0;
        }
        .detail-main.with-changes-panel .detail-main-content {
          flex: 1;
          min-width: 0;
          max-width: 1400px;
          margin: 0 auto;
          padding: 40px 32px;
        }
        .changes-sidebar {
          width: 340px;
          flex-shrink: 0;
          border-left: 1px solid #e2e8f0;
          background: #fafbfc;
          height: calc(100vh - 73px);
          position: sticky;
          top: 73px;
          overflow-y: auto;
          animation: fadeInSidebar 0.2s ease;
        }
        @keyframes fadeInSidebar {
          from { opacity: 0; transform: translateX(20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .changes-sidebar-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 20px 16px;
          border-bottom: 1px solid #e2e8f0;
          background: #fafbfc;
          position: sticky;
          top: 0;
          z-index: 1;
        }
        .changes-sidebar-header h3 {
          margin: 0;
          font-size: 14px;
          font-weight: 700;
          color: #1e293b;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .changes-sidebar-header h3 svg { color: #64748b; }
        .changes-sidebar-close {
          width: 28px; height: 28px;
          border: none; background: #f1f5f9;
          border-radius: 6px; cursor: pointer;
          font-size: 16px; color: #64748b;
          display: flex; align-items: center; justify-content: center;
          transition: background 0.15s;
        }
        .changes-sidebar-close:hover { background: #e2e8f0; }
        .changes-sidebar-body {
          padding: 8px 0;
        }
        .changes-loading, .changes-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 10px;
          padding: 48px 20px;
          color: #94a3b8;
          font-size: 13px;
          text-align: center;
        }
        .changes-empty p { margin: 0; font-weight: 600; color: #64748b; }
        .changes-empty span { font-size: 12px; }
        .changes-timeline {
          display: flex;
          flex-direction: column;
        }
        .change-item {
          display: flex;
          gap: 12px;
          padding: 10px 20px;
          position: relative;
          transition: background 0.15s;
        }
        .change-item:hover { background: #f1f5f9; }
        .change-line {
          position: absolute;
          left: 25px;
          top: 28px;
          bottom: -2px;
          width: 2px;
          background: #e2e8f0;
        }
        .change-item:last-child .change-line { display: none; }
        .change-dot-indicator {
          width: 10px; height: 10px;
          border-radius: 50%;
          flex-shrink: 0;
          margin-top: 4px;
          z-index: 1;
          box-shadow: 0 0 0 3px #fafbfc;
        }
        .change-content {
          flex: 1;
          min-width: 0;
        }
        .change-description {
          font-size: 13px;
          color: #1e293b;
          font-weight: 500;
          line-height: 1.4;
        }
        .change-meta {
          display: flex;
          align-items: center;
          gap: 4px;
          margin-top: 3px;
          font-size: 11px;
          color: #94a3b8;
        }
        .change-user { font-weight: 600; color: #64748b; }
        .change-dot { color: #cbd5e1; }
        @media (max-width: 1200px) {
          .changes-sidebar { width: 280px; }
        }
        @media (max-width: 900px) {
          .detail-main.with-changes-panel { flex-direction: column; }
          .changes-sidebar {
            width: 100%;
            height: auto;
            max-height: 400px;
            position: relative;
            top: auto;
            border-left: none;
            border-top: 1px solid #e2e8f0;
          }
        }

        .step-card-highlight {
          animation: highlightStep 1.5s ease;
        }
        @keyframes highlightStep {
          0% { box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.5); }
          100% { box-shadow: 0 2px 8px rgba(0,0,0,0.06); }
        }
        .step-number {
          width: 56px;
          height: 56px;
          background: ${theme.gradient};
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
          font-weight: 700;
          flex-shrink: 0;
        }
        .step-number.keyboard {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
        }
        .step-number.scroll {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
        }
        .step-content {
          flex: 1;
        }
        .step-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 16px;
        }
        .step-action {
          font-size: 18px;
          font-weight: 700;
          color: #1e293b;
          margin: 0;
          flex: 1;
        }
        .step-edit-btn {
          background: none;
          border: none;
          color: #64748b;
          cursor: pointer;
          padding: 4px 8px;
          font-size: 14px;
          border-radius: 4px;
        }
        .step-edit-btn:hover {
          background: #f1f5f9;
          color: #64748b;
        }
        /* Small red "Required" pill next to step title when the action
           label contained a trailing required-field asterisk. Rendered
           via parseRequiredFromAction() so the DB action stays
           unchanged and existing guides retroactively get the badge. */
        .step-required-badge {
          display: inline-flex;
          padding: 2px 7px;
          border-radius: 4px;
          background: #fef2f2;
          color: #dc2626;
          font-size: 10px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-left: 8px;
          border: 1px solid #fecaca;
          vertical-align: middle;
        }
        .step-type-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
          margin-left: 12px;
        }
        .step-type-badge.click {
          background: #ede9fe;
          color: #7c3aed;
        }
        .step-type-badge.keyboard {
          background: #fef3c7;
          color: #d97706;
        }
        .step-type-badge.scroll {
          background: #d1fae5;
          color: #059669;
        }
        /* Sprint K Phase 3 — Step-level speaker badge. Colored border +
           text from the participant's assigned palette color (kept light
           so the step header stays scannable). Sits inline next to the
           step action title; renders only when meetingAttribution is set.
           Tooltip carries the speaker name + confidence + quoted text. */
        .step-speaker-badge {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 3px 9px 3px 7px;
          margin-left: 12px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.2px;
          background: #fff;
          border: 1px solid currentColor;
          line-height: 1.4;
          max-width: 200px;
        }
        .step-speaker-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          flex-shrink: 0;
        }
        .step-speaker-name {
          color: inherit;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .screenshot-container {
          position: relative;
          margin-bottom: 16px;
          border-radius: 12px;
          overflow: hidden;
          border: 1px solid #e2e8f0;
          cursor: zoom-in;
        }
        .screenshot-container:hover {
          border-color: #64748b;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }
        .screenshot-container:hover::after {
          content: '🔍 Click to zoom';
          position: absolute;
          bottom: 8px;
          right: 8px;
          background: rgba(0, 0, 0, 0.75);
          color: #fff;
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 500;
          pointer-events: none;
        }
        .step-image {
          width: 100%;
          display: block;
        }
        .zoom-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.9);
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: zoom-out;
          animation: fadeIn 0.2s ease;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .zoom-content {
          position: relative;
          max-width: 95vw;
          max-height: 95vh;
          animation: zoomIn 0.2s ease;
        }
        @keyframes zoomIn {
          from { transform: scale(0.9); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .zoom-image {
          max-width: 95vw;
          max-height: 90vh;
          object-fit: contain;
          border-radius: 8px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
        }
        .zoom-close {
          position: absolute;
          top: -40px;
          right: 0;
          background: none;
          border: none;
          color: #64748b;
          font-size: 32px;
          cursor: pointer;
          padding: 8px;
          opacity: 0.8;
        }
        .zoom-close:hover {
          opacity: 1;
        }
        .zoom-step-info {
          position: absolute;
          bottom: -50px;
          left: 0;
          right: 0;
          text-align: center;
          color: #64748b;
          font-size: 16px;
        }
        .zoom-step-number {
          display: inline-block;
          background: ${theme.gradient};
          color: white;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          line-height: 32px;
          font-weight: 700;
          margin-right: 12px;
        }
        .zoom-nav {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(255, 255, 255, 0.9);
          border: none;
          width: 50px;
          height: 50px;
          border-radius: 50%;
          font-size: 24px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }
        .zoom-nav:hover {
          background: white;
          transform: translateY(-50%) scale(1.1);
        }
        .zoom-nav-prev {
          left: 20px;
        }
        .zoom-nav-next {
          right: 20px;
        }
        .click-indicator {
          position: absolute;
          pointer-events: none;
          z-index: 10;
        }
        .click-callout {
          position: absolute;
          pointer-events: none;
          display: flex;
          flex-direction: column;
          align-items: center;
          transform: translate(-50%, -100%);
          filter: drop-shadow(0 2px 8px rgba(22,163,74,0.4));
          z-index: 20;
        }
        .click-callout-badge {
          background: #16a34a;
          color: white;
          font-size: 11px;
          font-weight: 700;
          padding: 4px 12px;
          border-radius: 5px;
          letter-spacing: 0.5px;
          white-space: nowrap;
          animation: calloutBadgePulse 2s ease-in-out infinite;
        }
        .click-callout-arrow {
          width: 0;
          height: 0;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-top: 7px solid #16a34a;
        }
        .click-callout-dot {
          width: 10px;
          height: 10px;
          background: #16a34a;
          border-radius: 50%;
          margin-top: 3px;
          box-shadow: 0 0 0 2.5px white, 0 0 8px rgba(22,163,74,0.5);
          animation: calloutDotPulse 2s ease-in-out infinite;
        }
        /* Editable click indicator styles */
        .click-indicator.editable {
          pointer-events: auto;
          cursor: grab;
        }
        /* Large invisible hit area for easier dragging */
        .click-hit-area {
          position: absolute;
          width: 80px;
          height: 80px;
          border-radius: 50%;
          transform: translate(-50%, -50%);
          cursor: grab;
          z-index: 1;
        }
        .click-indicator.editable:hover .click-hit-area {
          background: rgba(59, 130, 246, 0.1);
          border: 2px dashed rgba(59, 130, 246, 0.4);
        }
        .click-indicator.editable:hover .click-callout-dot {
          background: #3b82f6;
          box-shadow: 0 0 0 2.5px white, 0 0 10px rgba(59,130,246,0.5);
        }
        .click-indicator.editable:hover .click-callout-badge { background: #3b82f6; }
        .click-indicator.editable:hover .click-callout-arrow { border-top-color: #3b82f6; }
        .click-indicator.editable:hover .click-callout { filter: drop-shadow(0 2px 8px rgba(59,130,246,0.4)); }
        .click-indicator.dragging {
          cursor: grabbing;
        }
        .click-indicator.dragging .click-hit-area {
          background: rgba(34, 197, 94, 0.15);
          border: 2px solid rgba(34, 197, 94, 0.5);
        }
        .click-indicator.dragging .click-callout-dot {
          background: #22c55e;
          box-shadow: 0 0 0 2.5px white, 0 0 10px rgba(34,197,94,0.6);
          animation: none;
        }
        .click-indicator.dragging .click-callout-badge { background: #22c55e; animation: none; }
        .click-indicator.dragging .click-callout-arrow { border-top-color: #22c55e; }
        .click-indicator.dragging .click-callout { filter: drop-shadow(0 2px 8px rgba(34,197,94,0.4)); }
        /* Drag handle with label */
        .drag-handle {
          position: absolute;
          top: 30px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 2px;
          opacity: 0;
          transition: opacity 0.2s;
          pointer-events: none;
        }
        .click-indicator.editable:hover .drag-handle {
          opacity: 1;
        }
        .drag-icon {
          font-size: 18px;
          color: #3b82f6;
          text-shadow: 0 1px 2px rgba(0,0,0,0.3), 0 0 8px rgba(255,255,255,0.8);
        }
        .drag-label {
          font-size: 10px;
          color: #3b82f6;
          background: rgba(255,255,255,0.9);
          padding: 2px 6px;
          border-radius: 4px;
          white-space: nowrap;
          font-weight: 600;
          box-shadow: 0 1px 3px rgba(0,0,0,0.2);
        }
        .click-indicator.dragging .drag-handle {
          opacity: 1;
        }
        .click-indicator.dragging .drag-icon {
          color: #22c55e;
        }
        .click-indicator.dragging .drag-label {
          color: #22c55e;
          background: rgba(255,255,255,0.95);
        }
        .annotation-canvas.dragging-indicator {
          cursor: grabbing !important;
        }
        .click-badge {
          position: absolute;
          transform: translate(-50%, -150%);
          background: linear-gradient(135deg, #ef4444, #dc2626);
          color: #fff;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          font-weight: 700;
          box-shadow: 0 3px 10px rgba(239, 68, 68, 0.4);
          border: 2px solid white;
        }
        .click-arrow {
          position: absolute;
          transform: translate(-50%, -100%);
          width: 0;
          height: 0;
          border-left: 8px solid transparent;
          border-right: 8px solid transparent;
          border-top: 12px solid #ef4444;
          filter: drop-shadow(0 2px 4px rgba(0,0,0,0.2));
        }
        .click-label {
          position: absolute;
          background: rgba(0, 0, 0, 0.85);
          color: #fff;
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 500;
          white-space: nowrap;
          max-width: 200px;
          overflow: hidden;
          text-overflow: ellipsis;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
          top: 25px;
        }
        .click-label.label-left {
          left: 0;
        }
        .click-label.label-center {
          left: 50%;
          transform: translateX(-50%);
        }
        .click-label.label-right {
          right: 0;
        }
        .click-label::before {
          content: '';
          position: absolute;
          top: -6px;
          width: 0;
          height: 0;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-bottom: 6px solid rgba(0, 0, 0, 0.85);
        }
        .click-label.label-left::before {
          left: 15px;
        }
        .click-label.label-center::before {
          left: 50%;
          transform: translateX(-50%);
        }
        .click-label.label-right::before {
          right: 15px;
        }
        .element-highlight {
          position: absolute;
          border: 3px solid ${theme.primary};
          background: rgba(102, 126, 234, 0.12);
          border-radius: 6px;
          pointer-events: none;
          box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.2), inset 0 0 20px rgba(102, 126, 234, 0.1);
          animation: highlightPulse 2s ease-in-out infinite;
        }
        /* Editable variant — while step is being annotated the box
           accepts a mouse-drag so users can nudge it to align with
           the actual UI element it represents. The dashed border +
           paused pulse make it visibly different from the read-only
           box so users know they can move it BEFORE they hover.
           z-index sits ABOVE the annotation-canvas (which covers the
           whole screenshot for drawing) so hover + mousedown land on
           the box first — otherwise the canvas's crosshair cursor
           wins and the drag handler never fires. */
        .element-highlight.editable {
          pointer-events: auto;
          animation: none;
          border-style: dashed;
          border-width: 3px;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.25), inset 0 0 20px rgba(102, 126, 234, 0.12);
          transition: box-shadow 0.15s, border-color 0.15s, background 0.15s;
          z-index: 5;
        }
        .element-highlight.editable:hover {
          background: rgba(102, 126, 234, 0.22);
          box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.45), inset 0 0 24px rgba(102, 126, 234, 0.22);
          border-color: ${theme.secondary};
        }
        .element-highlight.editable.dragging {
          background: rgba(102, 126, 234, 0.28);
          box-shadow: 0 0 0 5px rgba(102, 126, 234, 0.55), inset 0 0 26px rgba(102, 126, 234, 0.28);
          border-color: ${theme.secondary};
        }
        /* Resize handles — 8 small indigo squares on the box border.
           Visible only in edit mode (parent .editable). Each handle
           sits at its cardinal / intercardinal anchor point and
           carries its own cursor. Positioned with translate(-50%,
           -50%) on the corner handles + centered along the edges so
           they straddle the border cleanly. */
        .element-highlight .element-resize-handle {
          display: none;
        }
        .element-highlight.editable .element-resize-handle {
          display: block;
          position: absolute;
          width: 10px;
          height: 10px;
          background: white;
          border: 2px solid ${theme.primary};
          border-radius: 2px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
          z-index: 6;
        }
        .element-highlight.editable .element-resize-handle:hover {
          background: ${theme.primary};
        }
        .element-highlight.editable .rh-nw { top: -6px;  left: -6px;  cursor: nw-resize; }
        .element-highlight.editable .rh-n  { top: -6px;  left: 50%; transform: translateX(-50%); cursor: n-resize; }
        .element-highlight.editable .rh-ne { top: -6px;  right: -6px; cursor: ne-resize; }
        .element-highlight.editable .rh-e  { top: 50%; right: -6px;  transform: translateY(-50%); cursor: e-resize; }
        .element-highlight.editable .rh-se { bottom: -6px; right: -6px; cursor: se-resize; }
        .element-highlight.editable .rh-s  { bottom: -6px; left: 50%; transform: translateX(-50%); cursor: s-resize; }
        .element-highlight.editable .rh-sw { bottom: -6px; left: -6px;  cursor: sw-resize; }
        .element-highlight.editable .rh-w  { top: 50%; left: -6px;   transform: translateY(-50%); cursor: w-resize; }
        .element-highlight-label {
          position: absolute;
          top: -28px;
          left: 0;
          background: ${theme.gradient};
          color: white;
          padding: 4px 10px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 600;
          white-space: nowrap;
          box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4);
        }
        @keyframes clickPulse {
          0% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
          50% { opacity: 0.6; }
          100% { transform: translate(-50%, -50%) scale(2.2); opacity: 0; }
        }
        @keyframes calloutDotPulse {
          0%, 100% { box-shadow: 0 0 0 2.5px white, 0 0 8px rgba(22,163,74,0.4); }
          50% { box-shadow: 0 0 0 2.5px white, 0 0 16px rgba(22,163,74,0.7), 0 0 24px rgba(22,163,74,0.3); }
        }
        @keyframes calloutBadgePulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.06); }
        }
        @keyframes highlightPulse {
          0%, 100% { box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.2), inset 0 0 20px rgba(102, 126, 234, 0.1); }
          50% { box-shadow: 0 0 0 6px rgba(102, 126, 234, 0.3), inset 0 0 30px rgba(102, 126, 234, 0.15); }
        }
        .keyboard-indicator {
          position: absolute;
          bottom: 16px;
          left: 16px;
          background: rgba(0, 0, 0, 0.8);
          color: #fff;
          padding: 8px 16px;
          border-radius: 8px;
          font-family: monospace;
          font-size: 14px;
          font-weight: 600;
        }
        .scroll-indicator {
          position: absolute;
          right: 16px;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(16, 185, 129, 0.9);
          color: #fff;
          padding: 12px;
          border-radius: 8px;
          font-size: 24px;
        }
        .step-description {
          font-size: 16px;
          line-height: 1.7;
          color: #475569;
          margin: 0 0 14px 0;
        }
        .edit-form {
          margin-bottom: 16px;
        }
        .edit-input {
          width: 100%;
          padding: 10px 14px;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          font-size: 16px;
          margin-bottom: 12px;
        }
        .edit-input:focus {
          outline: none;
          border-color: #64748b;
        }
        .edit-textarea {
          width: 100%;
          padding: 10px 14px;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          font-size: 14px;
          min-height: 80px;
          resize: vertical;
          margin-bottom: 12px;
        }
        .edit-textarea:focus {
          outline: none;
          border-color: #64748b;
        }
        .edit-actions {
          display: flex;
          gap: 8px;
        }
        .edit-save-btn {
          background: #f1f5f9;
          color: #64748b;
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 14px;
          cursor: pointer;
        }
        .edit-cancel-btn {
          background: #f1f5f9;
          color: #64748b;
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 14px;
          cursor: pointer;
        }
        .step-sidebar {
          display: flex;
          flex-direction: column;
          gap: 8px;
          align-items: center;
        }
        .step-reorder-btn {
          background: #f1f5f9;
          border: none;
          width: 32px;
          height: 32px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #64748b;
          transition: all 0.2s;
        }
        .step-reorder-btn:hover:not(:disabled) {
          background: #e2e8f0;
          color: #64748b;
        }
        .step-reorder-btn:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }
        .step-delete-btn {
          background: #fef2f2;
          border: none;
          width: 32px;
          height: 32px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #dc2626;
          transition: all 0.2s;
        }
        .step-delete-btn:hover:not(:disabled) {
          background: #fee2e2;
        }
        .step-delete-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
        .step-delete-btn.deleting {
          background: #fef2f2;
        }
        .step-delete-spinner {
          width: 14px;
          height: 14px;
          border: 2px solid #fecaca;
          border-top-color: #dc2626;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
        .step-actions-row {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        /* Sharing styles */
        .detail-btn-share {
          background: #f0fdf4;
          color: #16a34a;
        }
        .detail-btn-share:hover {
          background: #dcfce7;
        }
        .share-badge {
          background: #f1f5f9;
          color: #64748b;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 12px;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .share-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        .share-modal {
          background: white;
          border-radius: 16px;
          width: 500px;
          max-height: 80vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        .share-modal-header {
          padding: 20px;
          border-bottom: 1px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .share-modal-header h3 {
          margin: 0;
          font-size: 18px;
          color: #1e293b;
        }
        .share-modal-close {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: #64748b;
        }
        .share-modal-body {
          padding: 20px;
          overflow-y: auto;
          flex: 1;
        }
        .share-section-title {
          font-size: 13px;
          font-weight: 600;
          color: #64748b;
          margin: 0 0 12px 0;
        }
        .share-member-list {
          margin-bottom: 20px;
        }
        .share-member-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px;
          border-radius: 8px;
          transition: background 0.2s;
        }
        .share-member-item:hover {
          background: #f8fafc;
        }
        .share-member-item input[type="checkbox"] {
          width: 18px;
          height: 18px;
        }
        .share-member-avatar {
          width: 36px;
          height: 36px;
          background: ${theme.gradient};
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 600;
          font-size: 14px;
        }
        .share-member-info {
          flex: 1;
        }
        .share-member-name {
          font-weight: 600;
          color: #1e293b;
          font-size: 14px;
        }
        .share-member-email {
          color: #64748b;
          font-size: 12px;
        }
        .share-member-remove {
          background: #fef2f2;
          border: none;
          color: #dc2626;
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 12px;
          cursor: pointer;
        }
        .share-permission-select {
          width: 100%;
          padding: 10px;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          font-size: 14px;
          margin-bottom: 16px;
        }
        .share-modal-footer {
          padding: 16px 20px;
          border-top: 1px solid #e2e8f0;
          display: flex;
          justify-content: flex-end;
          gap: 12px;
        }
        .share-btn-cancel {
          background: #f1f5f9;
          border: none;
          padding: 10px 20px;
          border-radius: 8px;
          font-size: 14px;
          cursor: pointer;
          color: #475569;
        }
        .share-btn-share {
          background: ${theme.gradient};
          border: none;
          padding: 10px 20px;
          border-radius: 8px;
          font-size: 14px;
          cursor: pointer;
          color: white;
          font-weight: 600;
        }
        .share-btn-share:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .share-empty {
          text-align: center;
          padding: 20px;
          color: #64748b;
        }
        .current-shares {
          margin-bottom: 24px;
        }

        /* Custom Dialog Styles */
        .custom-dialog-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          animation: dialogFadeIn 0.2s ease;
        }
        @keyframes dialogFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .custom-dialog {
          background: white;
          border-radius: 16px;
          width: 400px;
          max-width: 90vw;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          animation: dialogSlideIn 0.2s ease;
          overflow: hidden;
        }
        @keyframes dialogSlideIn {
          from { transform: scale(0.95) translateY(-10px); opacity: 0; }
          to { transform: scale(1) translateY(0); opacity: 1; }
        }
        .custom-dialog-header {
          padding: 20px 24px 16px;
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .custom-dialog-icon {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .custom-dialog-icon.default {
          background: #eef2ff;
          color: #4f46e5;
        }
        .custom-dialog-icon.danger {
          background: #fef2f2;
          color: #dc2626;
        }
        .custom-dialog-icon.prompt {
          background: #ecfdf5;
          color: #059669;
        }
        .custom-dialog-icon.success {
          background: #ecfdf5;
          color: #059669;
        }
        .custom-dialog-title {
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
        }
        .custom-dialog-body {
          padding: 0 24px 20px;
        }
        .custom-dialog-message {
          font-size: 14px;
          color: #64748b;
          line-height: 1.5;
          margin: 0;
        }
        .custom-dialog-input {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          font-size: 15px;
          margin-top: 16px;
          transition: all 0.2s;
          box-sizing: border-box;
        }
        .custom-dialog-input:focus {
          outline: none;
          border-color: #64748b;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }
        .custom-dialog-footer {
          padding: 16px 24px;
          background: #f8fafc;
          display: flex;
          justify-content: flex-end;
          gap: 12px;
        }
        .custom-dialog-btn {
          padding: 10px 20px;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          border: none;
        }
        .custom-dialog-btn-cancel {
          background: white;
          color: #64748b;
          border: 1px solid #e2e8f0;
        }
        .custom-dialog-btn-cancel:hover {
          background: #f1f5f9;
          color: #475569;
        }
        .custom-dialog-btn-confirm {
          background: ${theme.gradient};
          color: white;
        }
        .custom-dialog-btn-confirm:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }
        .custom-dialog-btn-danger {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
        }
        .custom-dialog-btn-danger:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
        }

        /* Comments styles */
        .comments-section {
          background: white;
          border-radius: 16px;
          padding: 24px;
          margin-top: 32px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.06);
        }
        .comments-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }
        .comments-header h3 {
          margin: 0;
          font-size: 18px;
          color: #1e293b;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .comments-count {
          background: #f1f5f9;
          color: #64748b;
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 13px;
          font-weight: normal;
        }
        .comments-toggle {
          background: none;
          border: none;
          color: #64748b;
          cursor: pointer;
          font-size: 14px;
        }
        .comment-input-box {
          display: flex;
          gap: 12px;
          margin-bottom: 24px;
        }
        .comment-avatar {
          width: 40px;
          height: 40px;
          background: ${theme.gradient};
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 600;
          font-size: 16px;
          flex-shrink: 0;
        }
        .comment-input-wrapper {
          flex: 1;
        }
        .comment-textarea {
          width: 100%;
          padding: 12px;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          font-size: 14px;
          resize: none;
          min-height: 60px;
          font-family: inherit;
        }
        .comment-textarea:focus {
          outline: none;
          border-color: #64748b;
        }
        .comment-submit-row {
          display: flex;
          justify-content: flex-end;
          margin-top: 8px;
        }
        .comment-submit-btn {
          background: ${theme.gradient};
          color: white;
          border: none;
          padding: 8px 20px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
        }
        .comment-submit-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .comments-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .comment-item {
          display: flex;
          gap: 12px;
        }
        .comment-body {
          flex: 1;
          background: #f8fafc;
          border-radius: 10px;
          padding: 12px 16px;
        }
        .comment-meta {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 6px;
        }
        .comment-author {
          font-weight: 600;
          color: #1e293b;
          font-size: 14px;
        }
        .comment-time {
          color: #94a3b8;
          font-size: 12px;
        }
        .comment-content {
          color: #475569;
          font-size: 14px;
          line-height: 1.5;
        }
        .comment-delete {
          background: none;
          border: none;
          color: #94a3b8;
          cursor: pointer;
          font-size: 16px;
          padding: 4px;
        }
        .comment-delete:hover {
          color: #dc2626;
        }
        .no-comments {
          text-align: center;
          padding: 32px;
          color: #94a3b8;
        }
        .shared-badge {
          background: #f0fdf4;
          color: #16a34a;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 12px;
          margin-left: 8px;
        }
        .guide-intro {
          background: white;
          border-radius: 16px;
          padding: 20px;
          margin-bottom: 20px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.06);
        }
        .guide-intro-header {
          display: flex;
          align-items: center;
          gap: 14px;
        }
        .guide-favicon {
          width: 44px;
          height: 44px;
          background: #f1f5f9;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          flex-shrink: 0;
        }
        .guide-favicon img {
          width: 28px;
          height: 28px;
          object-fit: contain;
        }
        .guide-favicon-fallback {
          width: 100%;
          height: 100%;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          font-weight: 700;
          color: #64748b;
          background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%);
        }
        .guide-intro-meta {
          flex: 1;
        }
        .guide-domain-link {
          font-size: 13px;
          color: #64748b;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }
        .guide-domain-link:hover {
          text-decoration: underline;
        }
        .guide-creator {
          font-size: 13px;
          color: #64748b;
          margin-top: 2px;
        }
        .guide-intro-description {
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid #f1f5f9;
        }
        .guide-description-text {
          font-size: 14px;
          color: #475569;
          line-height: 1.6;
          white-space: pre-wrap;
          margin: 0;
        }
        /* Sprint K — Meeting Mode recap block. Soft amber tint mirrors the
           floater + card badges; left accent rule signals "this is a
           secondary contextual note", not the primary guide description.
           Sits between the description card and the workflow flowchart. */
        .meeting-summary-block {
          margin: 16px 0;
          padding: 14px 16px 14px 18px;
          background: linear-gradient(180deg, rgba(245, 158, 11, 0.06) 0%, rgba(245, 158, 11, 0.02) 100%);
          border: 1px solid rgba(245, 158, 11, 0.22);
          border-left: 3px solid #f59e0b;
          border-radius: 10px;
        }
        .meeting-summary-head {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 6px;
          flex-wrap: wrap;
        }
        .meeting-summary-icon {
          display: inline-flex;
          color: #b45309;
        }
        .meeting-summary-title {
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.4px;
          text-transform: uppercase;
          color: #b45309;
        }
        .meeting-summary-topic {
          font-size: 12px;
          color: #78350f;
          padding: 2px 8px;
          background: rgba(245, 158, 11, 0.12);
          border-radius: 999px;
          max-width: 320px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .meeting-summary-body {
          font-size: 14px;
          color: #1e293b;
          line-height: 1.6;
          margin: 0;
        }
        /* Sprint K Phase 3 — Participants strip + enrich CTA. Lives at
           the bottom of the meeting recap block. Wraps gracefully on
           narrow viewports; chips truncate before the action button. */
        .meeting-enrich-row {
          margin-top: 12px;
          padding-top: 10px;
          border-top: 1px solid rgba(245, 158, 11, 0.22);
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }
        .meeting-participants-strip {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
          flex: 1 1 auto;
          min-width: 0;
          /* Now a <button> — reset native UA styling so the strip still
             reads as a row of chips, just clickable. */
          background: transparent;
          border: none;
          padding: 4px;
          margin: -4px;
          border-radius: 8px;
          cursor: pointer;
          text-align: left;
          font: inherit;
          color: inherit;
          transition: background 0.12s;
        }
        .meeting-participants-strip:hover {
          background: rgba(245, 158, 11, 0.08);
        }
        .meeting-participants-label {
          font-size: 11px;
          font-weight: 600;
          color: #78350f;
          letter-spacing: 0.3px;
          text-transform: uppercase;
          margin-right: 4px;
        }
        .meeting-participant-chip {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 3px 8px 3px 7px;
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 999px;
          font-size: 12px;
          color: #1e293b;
          max-width: 180px;
        }
        .meeting-participant-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          flex-shrink: 0;
        }
        .meeting-participant-name {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-weight: 500;
        }
        .meeting-participant-host {
          font-size: 9px;
          font-weight: 700;
          text-transform: uppercase;
          color: #b45309;
          background: rgba(245, 158, 11, 0.16);
          padding: 1px 5px;
          border-radius: 4px;
          letter-spacing: 0.4px;
        }
        .meeting-participants-overflow {
          font-size: 11px;
          color: #78350f;
          font-weight: 600;
        }
        .meeting-enrich-empty {
          font-size: 12px;
          color: #78350f;
          font-style: italic;
          flex: 1 1 auto;
        }
        .meeting-enrich-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 11px 6px 10px;
          border-radius: 8px;
          background: #fff;
          border: 1px solid rgba(245, 158, 11, 0.45);
          color: #b45309;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s;
          flex-shrink: 0;
        }
        .meeting-enrich-btn:hover {
          background: rgba(245, 158, 11, 0.08);
          border-color: #f59e0b;
          color: #92400e;
          transform: translateY(-1px);
        }
        .meeting-enrich-btn svg {
          flex-shrink: 0;
        }
        .guide-description-placeholder {
          color: #94a3b8;
          font-style: italic;
          cursor: pointer;
          font-size: 14px;
          margin: 0;
        }
        .guide-description-placeholder:hover {
          color: #64748b;
        }
        .guide-description-textarea {
          width: 100%;
          min-height: 140px;
          padding: 12px 14px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 14px;
          line-height: 1.6;
          resize: vertical;
          font-family: inherit;
        }
        .guide-description-textarea:focus {
          outline: none;
          border-color: #64748b;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }
        .guide-description-actions {
          display: flex;
          gap: 8px;
          margin-top: 8px;
        }
        .desc-btn {
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          border: none;
          transition: background 0.12s ease, box-shadow 0.12s ease;
        }
        /* Save is the primary action — indigo fill, white text. Previously
           it was light gray (identical to Cancel) AND its hover ramped to
           a dark indigo bg without changing the gray text, so the label
           became invisible on hover. */
        .desc-btn-save {
          background: ${theme.primary};
          color: #ffffff;
          box-shadow: 0 1px 2px rgba(99,102,241,0.20), 0 4px 12px rgba(99,102,241,0.25);
        }
        .desc-btn-save:hover {
          background: ${theme.secondary};
          color: #ffffff;
          box-shadow: 0 2px 4px rgba(79,70,229,0.25), 0 6px 16px rgba(79,70,229,0.30);
        }
        .desc-btn-cancel {
          background: #f1f5f9;
          color: #475569;
        }
        .desc-btn-cancel:hover {
          background: #e2e8f0;
          color: #1e293b;
        }
        .desc-btn-generate {
          background: ${theme.gradient};
          color: #ffffff;
          margin-left: auto;
        }
        .desc-btn-generate:hover:not(:disabled) {
          opacity: 0.9;
        }
        .desc-btn-generate:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .guide-description-generating {
          color: #64748b;
          font-style: italic;
          font-size: 14px;
          margin: 0;
          animation: pulse-text 1.5s infinite;
        }
        @keyframes pulse-text {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .guide-description-empty {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .desc-btn-generate-inline {
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          border: none;
          background: ${theme.gradient};
          color: white;
        }
        .desc-btn-generate-inline:hover:not(:disabled) {
          opacity: 0.9;
        }
        .desc-btn-generate-inline:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .step-comments {
          margin-top: 16px;
          padding-top: 16px;
          border-top: 1px solid #e2e8f0;
        }
        .step-comments-toggle {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 13px;
          color: #64748b;
          cursor: pointer;
          transition: all 0.2s;
        }
        .step-comments-toggle:hover {
          background: #f1f5f9;
          color: #475569;
        }
        .step-comments-badge {
          background: #f1f5f9;
          color: #64748b;
          font-size: 11px;
          padding: 2px 6px;
          border-radius: 10px;
          font-weight: 600;
        }
        .step-comment-input {
          margin-top: 12px;
        }
        .step-comment-input textarea {
          width: 100%;
          padding: 10px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 13px;
          resize: none;
          font-family: inherit;
        }
        .step-comment-input textarea:focus {
          outline: none;
          border-color: #64748b;
        }
        .step-comment-actions {
          display: flex;
          gap: 8px;
          margin-top: 8px;
        }
        .step-comment-list {
          margin-top: 12px;
        }
        .step-comment-item {
          display: flex;
          gap: 10px;
          padding: 10px 0;
          border-bottom: 1px solid #f1f5f9;
        }
        .step-comment-item:last-child {
          border-bottom: none;
        }
        .step-comment-avatar {
          width: 28px;
          height: 28px;
          background: ${theme.gradient};
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: 600;
          flex-shrink: 0;
        }
        .step-comment-body {
          flex: 1;
        }
        .step-comment-meta {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 4px;
        }
        .step-comment-author {
          font-weight: 600;
          font-size: 13px;
          color: #1e293b;
        }
        .step-comment-time {
          font-size: 11px;
          color: #94a3b8;
        }
        .step-comment-text {
          font-size: 13px;
          color: #475569;
          line-height: 1.5;
        }
        .step-comment-delete {
          background: none;
          border: none;
          color: #94a3b8;
          cursor: pointer;
          font-size: 14px;
          padding: 2px;
        }
        .step-comment-delete:hover {
          color: #dc2626;
        }
      `}</style>

      <header className="detail-header">
        <button onClick={onBack} className="back-btn">← Back</button>
        <h1 className="detail-title">
          {isEditingName ? (
            <span className="title-edit-container">
              <input
                type="text"
                className="title-edit-input"
                value={guideName}
                onChange={(e) => setGuideName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveName();
                  if (e.key === 'Escape') { setIsEditingName(false); setGuideName(guide.name || ''); }
                }}
                autoFocus
              />
              <button className="title-edit-btn save" onClick={handleSaveName}>✓</button>
              <button className="title-edit-btn cancel" onClick={() => { setIsEditingName(false); setGuideName(guide.name || ''); }}>✕</button>
            </span>
          ) : (
            <span className="title-display" onClick={() => isOwner && setIsEditingName(true)} style={isOwner ? { cursor: 'pointer' } : {}}>
              {guide.name}
              {isOwner && <span className="title-edit-icon" title="Edit name">{Icons.edit}</span>}
            </span>
          )}
          {guide.isShared && <span className="shared-badge">Shared with you</span>}
        </h1>
        {shares.length > 0 && (
          <span className="share-badge">{Icons.usersGroup} Shared with {shares.length}</span>
        )}
        <div className="detail-actions">
          {isOwner && user?.organizationId && (
            <button onClick={openShareModal} className="detail-btn detail-btn-share">
              {Icons.link} Share
            </button>
          )}
          {isOwner && planFeatures?.embed && (
            <button onClick={openEmbedModal} className="detail-btn detail-btn-secondary" title="Get an embeddable link for this guide">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="16 18 22 12 16 6"/>
                <polyline points="8 6 2 12 8 18"/>
              </svg>
              Embed
            </button>
          )}
          <button onClick={() => onExport(guide.id, 'html')} className="detail-btn detail-btn-secondary" disabled={exportingGuide.id === guide.id}>
            {exportingGuide.id === guide.id && exportingGuide.format === 'html' ? <span className="btn-spinner" /> : Icons.globe}
            {exportingGuide.id === guide.id && exportingGuide.format === 'html' ? 'Exporting...' : 'HTML'}
          </button>
          <button onClick={() => onExport(guide.id, 'pdf')} className="detail-btn detail-btn-primary" disabled={exportingGuide.id === guide.id}>
            {exportingGuide.id === guide.id && exportingGuide.format === 'pdf' ? <span className="btn-spinner" /> : Icons.download}
            {exportingGuide.id === guide.id && exportingGuide.format === 'pdf' ? 'Exporting...' : 'PDF'}
          </button>
          <button onClick={() => { setShowChangesPanel(!showChangesPanel); if (!showChangesPanel) loadChanges(); }} className={`detail-btn ${showChangesPanel ? 'detail-btn-primary' : 'detail-btn-secondary'}`}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            History
          </button>
          {isOwner && (
            <button
              onClick={() => onDelete(guide.id)}
              className="detail-btn detail-btn-danger"
              disabled={deletingGuideId === guide.id}
            >
              {deletingGuideId === guide.id ? <div className="detail-delete-spinner" /> : Icons.trash}
              {deletingGuideId === guide.id ? 'Deleting...' : 'Delete'}
            </button>
          )}
        </div>
      </header>

      <main className={`detail-main ${showChangesPanel ? 'with-changes-panel' : ''}`}>
       <div className="detail-main-content">
        {/* Ask Insta block — MOVED to the very top of the content area so it's
            the first thing users see on a fresh capture (or when revisiting an
            older guide). Three mutually-exclusive states, in priority order:
              1. Initial AI processing in progress — show analyzing banner.
                 Triggered when the description is still being regenerated
                 (fresh capture, no cached summary yet). Suppresses the
                 suggestion banner + slim prompt during this window so the
                 page doesn't read as "Insta wants to improve" before Insta
                 has finished its own first-pass analysis.
              2. Pending suggestions — show suggestion banner.
              3. No pending suggestions — show slim "Ask Insta to analyze" prompt. */}
        {generatingDescription ? (
          <div
            style={{
              position: 'relative',
              background: 'linear-gradient(135deg, #4338ca 0%, #6366f1 55%, #818cf8 100%)',
              color: 'white',
              borderRadius: 14,
              padding: '16px 18px',
              margin: '0 0 20px 0',
              boxShadow: '0 6px 24px rgba(99,102,241,0.22), inset 0 1px rgba(255,255,255,0.18)',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              gap: 14,
            }}
            role="status"
            aria-live="polite"
          >
            {/* Diagonal hatch — matches the SuggestionBanner so the morph
                feels like the same surface refreshing, not two separate UIs. */}
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                inset: 0,
                background: 'repeating-linear-gradient(135deg, rgba(255,255,255,0.04) 0px, rgba(255,255,255,0.04) 1px, transparent 1px, transparent 22px)',
                pointerEvents: 'none',
              }}
            />
            {/* Animated spinner inside the same square chip the SuggestionBanner
                uses for its sparkle icon — visual continuity is what makes the
                morph read as "same banner, different state". */}
            <span
              style={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 44,
                height: 44,
                borderRadius: 12,
                background: 'rgba(255,255,255,0.18)',
                border: '1px solid rgba(255,255,255,0.22)',
                flexShrink: 0,
              }}
              aria-hidden="true"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ animation: 'ig-ai-processing-spin 1s linear infinite' }}>
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
            </span>
            <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4, lineHeight: 1.25, letterSpacing: -0.1 }}>
                Insta is analyzing your guide…
              </div>
              <div style={{ fontSize: 12.5, opacity: 0.88, lineHeight: 1.4 }}>
                Drafting the summary and scanning for improvements. This usually takes a few seconds.
              </div>
            </div>
            {/* Self-contained keyframe — the SuggestionBanner's keyframes
                aren't mounted in this branch, so we define our own. Unique
                name avoids any chance of collision with the global spin
                keyframe defined inside the LoadingView component. */}
            <style>{`@keyframes ig-ai-processing-spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : pendingSuggestions.length > 0 && !bannerDismissed ? (
          <SuggestionBanner
            count={pendingSuggestions.length}
            breakdown={suggestionBreakdown}
            onReview={handleOpenSuggestionPanel}
            onDismiss={handleDismissBanner}
          />
        ) : null}
        {!generatingDescription && !(pendingSuggestions.length > 0 && !bannerDismissed) && guide.steps.length > 0 && !justAppliedSuggestions && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '12px 18px',
            marginBottom: 16,
            // Soft indigo wash + matching border tint signals "AI feature
            // callout" instead of "TODO placeholder" (which is what the
            // earlier dashed-gray border read like).
            background: 'linear-gradient(135deg, rgba(139,92,246,0.06), rgba(124,58,237,0.04))',
            border: '1px solid rgba(139,92,246,0.18)',
            borderRadius: 12,
            fontSize: 13.5,
            color: '#475569',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {/* Sparkles icon — same two-star composition as the button.
                  Filled (not stroked) so it doesn't read as a loading
                  spinner the way the earlier sun-ray icon did. */}
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 26,
                height: 26,
                borderRadius: 8,
                background: 'linear-gradient(135deg, rgba(139,92,246,0.18), rgba(124,58,237,0.14))',
                color: '#7c3aed',
                flexShrink: 0,
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M11 3 L13 8.5 L18.5 10.5 L13 12.5 L11 18 L9 12.5 L3.5 10.5 L9 8.5 Z" />
                  <path d="M18 14.5 L18.9 17 L21.5 17.9 L18.9 18.8 L18 21.3 L17.1 18.8 L14.5 17.9 L17.1 17 Z" />
                </svg>
              </span>
              <span style={{ fontWeight: 500, color: '#334155' }}>
                {regeneratingSuggestions
                  ? 'Insta is checking this guide for improvements…'
                  : 'Want Insta to scan this guide for improvements?'}
              </span>
            </div>
            {/* Primary CTA — purple gradient + sparkles icon + lift-on-hover.
                Matches the visual weight of the SuggestionBanner's Review
                button, so users see "this is the thing to click" at a glance. */}
            <button
              type="button"
              onClick={handleRegenerateSuggestions}
              disabled={regeneratingSuggestions}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                background: regeneratingSuggestions
                  ? 'linear-gradient(135deg, #c4b5fd, #a78bfa)'
                  : 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                border: 'none',
                color: 'white',
                padding: '9px 18px',
                borderRadius: 10,
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: 0.1,
                cursor: regeneratingSuggestions ? 'wait' : 'pointer',
                whiteSpace: 'nowrap',
                boxShadow: regeneratingSuggestions
                  ? 'none'
                  : '0 2px 6px rgba(124,58,237,0.25), 0 6px 18px rgba(124,58,237,0.30)',
                transition: 'transform 0.12s ease, box-shadow 0.12s ease, filter 0.12s ease',
              }}
              onMouseEnter={(e) => {
                if (!regeneratingSuggestions) {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 3px 8px rgba(124,58,237,0.30), 0 10px 24px rgba(124,58,237,0.36)';
                  e.currentTarget.style.filter = 'brightness(1.06)';
                }
              }}
              onMouseLeave={(e) => {
                if (!regeneratingSuggestions) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 6px rgba(124,58,237,0.25), 0 6px 18px rgba(124,58,237,0.30)';
                  e.currentTarget.style.filter = 'none';
                }
              }}
            >
              {/* Sparkles icon — the universal "AI" visual cue. Two-star
                  composition matches Material Symbols' auto_awesome. */}
              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M11 3 L13 8.5 L18.5 10.5 L13 12.5 L11 18 L9 12.5 L3.5 10.5 L9 8.5 Z" />
                <path d="M18 14.5 L18.9 17 L21.5 17.9 L18.9 18.8 L18 21.3 L17.1 18.8 L14.5 17.9 L17.1 17 Z" />
              </svg>
              {regeneratingSuggestions ? 'Insta is analyzing…' : 'Ask Insta to analyze'}
            </button>
          </div>
        )}

        {/* Sprint K V3.1 — Meeting recap card moved to the TOP of the
            guide detail body. High-leverage content (outcomes / action
            items, or fallback short recap) is the FIRST thing a reader
            sees. Header reads "Meeting Highlights" — neutral phrasing
            that fits both the outcomes-bulleted case (upload pipeline)
            and the shortRecap-prose case (live captures without
            actionable outcomes). */}
        {guide.meetingMode && (
          <div className="meeting-summary-block">
            <div className="meeting-summary-head">
              <span className="meeting-summary-icon" aria-hidden="true">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="9" cy="8" r="3.2" />
                  <path d="M2.5 19c.6-3.2 3.3-5.3 6.5-5.3s5.9 2.1 6.5 5.3" />
                  <circle cx="17" cy="6.5" r="2.4" />
                  <path d="M15 12.4c2.5-.2 4.6 1.3 5.5 3.6" />
                </svg>
              </span>
              <span className="meeting-summary-title">Meeting Highlights</span>
              {guide?.meetingMetadata?.topic && (
                <span className="meeting-summary-topic" title={guide.meetingMetadata.topic}>{guide.meetingMetadata.topic}</span>
              )}
            </div>
            {guide.meetingSummary && (
              <div className="meeting-summary-body">
                <MarkdownText text={guide.meetingSummary} style={{ fontSize: 14, color: '#1e293b', lineHeight: 1.6 }} />
              </div>
            )}

            {/* Sprint K Phase 3 — Participants strip + enrich CTA.
                For uploaded meetings the enrich CTA doesn't apply
                (transcript already ingested), and the empty-state
                "upload one" copy would be misleading without a
                button — so we render the row only when there's
                something meaningful to show. */}
            {isOwner && (
              (Array.isArray(guide.participants) && guide.participants.length > 0)
              || guide.source !== 'meeting_import'
            ) && (
              <div className="meeting-enrich-row">
                {Array.isArray(guide.participants) && guide.participants.length > 0 ? (
                  <button
                    type="button"
                    className="meeting-participants-strip"
                    onClick={() => setShowParticipantsPanel(true)}
                    title="Edit participants (rename, recolor, mark host)"
                  >
                    <span className="meeting-participants-label">
                      {guide.participants.length} participant{guide.participants.length === 1 ? '' : 's'}:
                    </span>
                    {guide.participants.slice(0, 6).map((p) => (
                      <span key={p.id} className="meeting-participant-chip" title={p.displayName}>
                        <span className="meeting-participant-dot" style={{ background: p.color || '#94a3b8' }} />
                        <span className="meeting-participant-name">{p.displayName}</span>
                        {p.isHost && <span className="meeting-participant-host">host</span>}
                      </span>
                    ))}
                    {guide.participants.length > 6 && (
                      <span className="meeting-participants-overflow">
                        +{guide.participants.length - 6} more
                      </span>
                    )}
                  </button>
                ) : (
                  <span className="meeting-enrich-empty">
                    No transcript yet — upload one to attribute steps to speakers.
                  </span>
                )}
                {/* Only offer the transcript-enrichment CTA on
                    live-captured meeting guides. Uploaded meetings
                    (source='meeting_import') already ingested a
                    transcript (either the user's VTT or Gemini's
                    lite-video transcription) at pipeline time, so a
                    post-hoc transcript upload wouldn't improve
                    anything and just confuses the user. */}
                {guide.source !== 'meeting_import' && (
                  <button
                    type="button"
                    className="meeting-enrich-btn"
                    onClick={() => setShowEnrichModal(true)}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="17 8 12 3 7 8"/>
                      <line x1="12" y1="3" x2="12" y2="15"/>
                    </svg>
                    {Array.isArray(guide.participants) && guide.participants.length > 0
                      ? 'Update transcript'
                      : 'Add meeting transcript'}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Guide Info - Above Steps */}
        <div className="guide-intro">
          <div className="guide-intro-header">
            <div className="guide-favicon">
              {(() => {
                const faviconUrl = getFaviconWithFallback(guide);
                return faviconUrl ? (
                  <img
                    src={faviconUrl}
                    alt={guide.domain || 'Site'}
                    onError={(e) => { handleFaviconError(e); e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }}
                  />
                ) : null;
              })()}
              <span className="guide-favicon-fallback" style={{ display: getFaviconWithFallback(guide) ? 'none' : 'flex' }}>
                {React.cloneElement(Icons.globe, { width: 24, height: 24 })}
              </span>
            </div>
            <div className="guide-intro-meta">
              {guide.sourceUrl && (
                <a href={guide.sourceUrl} target="_blank" rel="noopener noreferrer" className="guide-domain-link">
                  {guide.domain || guide.sourceUrl}
                  <span style={{ fontSize: '12px' }}>↗</span>
                </a>
              )}
              <div className="guide-creator">
                Created by {guide.creatorName || (guide.creatorEmail ? guide.creatorEmail.split('@')[0] : 'Unknown')} • {guide.steps.length} steps
              </div>
              {/* Project selector - only show if user is owner and has projects */}
              {isOwner && projects.length > 0 && (
                <div className="guide-project-selector" style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '13px', color: '#64748b', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>{React.cloneElement(Icons.folder, { width: 14, height: 14 })} Project:</span>
                  <select
                    value={guide.projectId || ''}
                    onChange={handleProjectChange}
                    disabled={movingProject}
                    style={{
                      padding: '4px 8px',
                      fontSize: '13px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      background: 'white',
                      color: '#334155',
                      cursor: movingProject ? 'wait' : 'pointer',
                      minWidth: '140px',
                    }}
                  >
                    {!guide.projectId && <option value="">Unassigned</option>}
                    {projects.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                  {movingProject && <span style={{ fontSize: '12px', color: '#64748b' }}>Moving...</span>}
                </div>
              )}
            </div>
          </div>
          <div className="guide-intro-description">
            {isEditingDescription ? (
              <div>
                <textarea
                  className="guide-description-textarea"
                  value={guideDescription}
                  onChange={(e) => setGuideDescription(e.target.value)}
                  placeholder="Add a description for this guide..."
                  autoFocus
                />
                <div className="guide-description-actions">
                  <button className="desc-btn desc-btn-save" onClick={handleSaveDescription}>Save</button>
                  <button className="desc-btn desc-btn-cancel" onClick={() => { setIsEditingDescription(false); setGuideDescription(guide.description || ''); }}>Cancel</button>
                  <button
                    className="desc-btn desc-btn-generate"
                    onClick={handleGenerateDescription}
                    disabled={generatingDescription || guide.steps.length === 0}
                  >
                    {generatingDescription ? 'Generating...' : <><span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>{React.cloneElement(Icons.sparkles, { width: 14, height: 14 })} Auto-generate</span></>}
                  </button>
                </div>
              </div>
            ) : (
              <div className="guide-description-display">
                {generatingDescription ? (
                  <p className="guide-description-generating" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>{React.cloneElement(Icons.sparkles, { width: 16, height: 16 })} Generating description...</p>
                ) : guide.meetingBrief ? (
                  // Meeting-generated guides get structured Meeting Summary
                  // cards instead of the flat markdown prose. Clicking a
                  // segment scrolls the step list to the first step
                  // whose meetingAttribution.segmentId matches, or
                  // no-ops if no segment mapping is available.
                  <MeetingSummaryCards
                    brief={guide.meetingBrief}
                    onSegmentClick={(seg) => {
                      // Prefer the backend-resolved firstStepNumber
                      // (exact when steps carry segmentId, timing-
                      // approximated for legacy guides). Fall back to
                      // scanning step meetingAttribution locally in
                      // case the brief didn't resolve one.
                      let step = null;
                      if (seg.firstStepNumber != null) {
                        step = (guide.steps || []).find((s) => Number(s.number) === Number(seg.firstStepNumber));
                      }
                      if (!step) {
                        step = (guide.steps || []).find((s) => s.meetingAttribution?.segmentId === seg.id);
                      }
                      if (step) {
                        const el = document.getElementById(`step-${step.id}`);
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                  />
                ) : guideDescription ? (
                  // Description renders as Markdown — the backend prompt asks the AI
                  // to emit **bold**, paragraphs, and optional bullets so the output
                  // is scannable instead of a wall of prose. Click anywhere on the
                  // block (when owner) to switch into the textarea edit mode.
                  <div
                    className="guide-description-text"
                    onClick={() => isOwner && setIsEditingDescription(true)}
                    style={isOwner ? { cursor: 'pointer' } : {}}
                  >
                    <MarkdownText text={guideDescription} />
                  </div>
                ) : (
                  isOwner && (
                    <p className="guide-description-placeholder" onClick={() => setIsEditingDescription(true)}>
                      Click to add a description...
                    </p>
                  )
                )}
              </div>
            )}
          </div>
        </div>

        {/* Sprint K Phase 3 — VTT/SRT enrichment modal. Mounted at the
            GuideDetailView level so it survives parent state churn during
            upload. On success, reload selectedGuide so the new
            participants[] (and pending attribution suggestions in the
            Improvements panel) show immediately. */}
        {showEnrichModal && (
          <MeetingEnrichModal
            guideId={guide.id}
            hasParticipants={Array.isArray(guide.participants) && guide.participants.length > 0}
            onClose={() => setShowEnrichModal(false)}
            onComplete={(data) => {
              // Patch participants in place — saves a full guide refetch
              // for the visible chip strip. Pending attribution
              // suggestions surface naturally next time the Improvements
              // panel polls.
              if (setSelectedGuide && data?.participants) {
                setSelectedGuide(prev => prev ? ({ ...prev, participants: data.participants }) : prev);
              }
            }}
            showToast={showToast}
          />
        )}

        {/* Sprint K Phase 3 — Participants editor. Patches in-place on
            save so the chip strip + speaker badges on steps reflect the
            new names/colors immediately. */}
        {showParticipantsPanel && (
          <ParticipantsPanel
            guideId={guide.id}
            participants={guide.participants || []}
            onClose={() => setShowParticipantsPanel(false)}
            onSaved={(updated) => {
              if (setSelectedGuide) {
                setSelectedGuide(prev => prev ? ({ ...prev, participants: updated }) : prev);
              }
            }}
            showToast={showToast}
          />
        )}

        {/* Workflow Flowchart - Visual overview of all steps */}
        {guide.steps.length > 0 && (
          <WorkflowFlowchart
            steps={guide.steps}
            guideId={guide.id}
            workflowData={guide.workflowData}
            guide={guide}
            onStepClick={scrollToStep}
            onPlanError={onPlanError}
          />
        )}

        {/* Steps Section Header */}
        {guide.steps.length > 0 && (() => {
          // Sprint K Phase 3 — Speaker filter scaffolding. Only renders
          // when the guide is a Meeting Mode capture with ≥2 distinct
          // participants. The filter uses CSS to hide non-matching step
          // cards rather than slicing the array, so insertion buttons,
          // step numbering, and refs stay stable.
          const showSpeakerFilter =
            guide.meetingMode &&
            Array.isArray(guide.participants) &&
            guide.participants.length >= 2;
          const visibleCount = showSpeakerFilter && speakerFilter
            ? guide.steps.filter(s => s.meetingAttribution?.speakerId === speakerFilter).length
            : guide.steps.length;
          return (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              padding: '16px 24px',
              background: 'linear-gradient(to right, #f8fafc, #f1f5f9)',
              border: '1px solid #e2e8f0',
              borderRadius: '16px 16px 0 0',
              marginBottom: '-1px',
              flexWrap: 'wrap',
            }}>
              <h3 style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                fontSize: '13px',
                fontWeight: '600',
                color: '#475569',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                margin: 0,
              }}>
                <svg style={{ width: '20px', height: '20px', color: theme.primary }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/>
                  <rect x="9" y="3" width="6" height="4" rx="1"/>
                  <path d="M9 12h6M9 16h6"/>
                </svg>
                Step Details
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                {showSpeakerFilter && (
                  <select
                    value={speakerFilter || ''}
                    onChange={(e) => setSpeakerFilter(e.target.value || null)}
                    title="Filter steps by speaker"
                    style={{
                      padding: '6px 30px 6px 12px',
                      borderRadius: 999,
                      border: '1px solid #e2e8f0',
                      background: '#fff',
                      color: '#1e293b',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                      appearance: 'none',
                      backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 10px center',
                    }}
                  >
                    <option value="">All speakers</option>
                    {guide.participants.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.displayName}{p.isHost ? ' (host)' : ''}
                      </option>
                    ))}
                  </select>
                )}
                <span style={{
                  background: theme.gradient,
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: '600',
                  padding: '6px 16px',
                  borderRadius: '9999px',
                  boxShadow: '0 2px 4px rgba(102, 126, 234, 0.3)',
                }}>
                  {showSpeakerFilter && speakerFilter
                    ? `${visibleCount} of ${guide.steps.length} Steps`
                    : `${guide.steps.length} Steps`}
                </span>
              </div>
            </div>
          );
        })()}

        {/* Insert button at the beginning. Hidden while a speaker
            filter is active — a filtered view is read-only in
            practice, and stacking Insert Step buttons between hidden
            steps confused users (they appeared as a cluster of
            buttons with no visible neighbours). */}
        {canEdit && !speakerFilter && <InsertStepButton insertAfterPosition={0} />}

        {/* Non-consuming numbering — section dividers don't increment
            the visible step counter. A guide with 24 clicks and 3
            section headings reads as steps 1..24 with the headings as
            breaks between them. Purely presentational; step.number in
            the DB is unchanged. Written inline as an IIFE that mutates
            each step's displayNumber field so the map below can read
            it without a wrapping array copy. */}
        {(() => {
          let n = 0;
          for (const s of guide.steps) {
            const sType = s.stepType || 'click';
            if (sType !== 'section_divider') { n += 1; s.displayNumber = n; }
            else { s.displayNumber = null; }
          }
          return null;
        })()}

        {/* Empty-divider guard. When the analyzer / auto-apply produces
            two section_break suggestions at the same insertion point
            (both proposed different chapter titles for the same
            position → both got applied), the reader sees two
            back-to-back headings with nothing between them. Same
            treatment for a trailing divider with no content after it.
            Hidden in-place so step numbering + reorder buttons +
            step ids stay stable; the underlying data isn't touched
            (delete via the divider's × button removes it for real). */}
        {(() => {
          const isEmptyDivider = (i) => {
            if ((guide.steps[i].stepType || 'click') !== 'section_divider') return false;
            for (let j = i + 1; j < guide.steps.length; j++) {
              return (guide.steps[j].stepType || 'click') === 'section_divider';
            }
            return true;
          };
          guide.steps.forEach((s, i) => { s._skipEmptyDivider = isEmptyDivider(i); });
          return null;
        })()}

        {guide.steps.map((step, index) => (step._skipEmptyDivider ? null :
          <React.Fragment key={step.id}>
            <div
              id={`step-${step.id}`}
              className={`step-card step-type-${step.stepType || 'click'}`}
              style={
                /* Sprint K Phase 3 — speaker filter hides non-matching
                   steps via display:none rather than slicing the array,
                   so step numbers, refs, and insertion buttons stay
                   stable. */
                speakerFilter && step.meetingAttribution?.speakerId !== speakerFilter
                  ? { display: 'none' }
                  : undefined
              }
            >
              <div className="step-sidebar">
                <div className={`step-number ${step.eventType || 'click'}`}>
                  {step.eventType === 'keyboard' ? Icons.keyboard : step.eventType === 'scroll' ? Icons.verticalArrows : (step.displayNumber != null ? step.displayNumber : step.number)}
                </div>
                <button
                  className="step-reorder-btn"
                  onClick={() => onReorderSteps && onReorderSteps(guide.id, step.id, 'up')}
                  disabled={index === 0}
                  title="Move up"
                >
                  ↑
                </button>
                <button
                  className="step-reorder-btn"
                  onClick={() => onReorderSteps && onReorderSteps(guide.id, step.id, 'down')}
                  disabled={index === guide.steps.length - 1}
                  title="Move down"
                >
                  ↓
                </button>
                <button
                  className={`step-delete-btn ${deletingStepId === step.id ? 'deleting' : ''}`}
                  onClick={() => onDeleteStep && onDeleteStep(guide.id, step.id)}
                  disabled={deletingStepId === step.id}
                  title="Delete step"
                >
                  {deletingStepId === step.id ? <div className="step-delete-spinner" /> : '×'}
                </button>
              </div>
              {/* Hover-only actions for section dividers — the sidebar
                  rail is hidden via CSS on this step type, but reorder /
                  delete still needs to be reachable. Rendered here (not
                  in .step-sidebar) so it can be positioned to the right
                  of the heading independently. */}
              {(step.stepType === 'section_divider') && (
                <div className="section-divider-actions">
                  <button
                    onClick={() => onReorderSteps && onReorderSteps(guide.id, step.id, 'up')}
                    disabled={index === 0}
                    title="Move up"
                  >↑</button>
                  <button
                    onClick={() => onReorderSteps && onReorderSteps(guide.id, step.id, 'down')}
                    disabled={index === guide.steps.length - 1}
                    title="Move down"
                  >↓</button>
                  <button
                    className="delete-btn"
                    onClick={() => onDeleteStep && onDeleteStep(guide.id, step.id)}
                    disabled={deletingStepId === step.id}
                    title="Delete section"
                  >×</button>
                </div>
              )}
              <div className="step-content">
                <div className="step-header">
                {editingStep === step.id ? (
                  <div className="edit-form" style={{ width: '100%' }}>
                    <input
                      type="text"
                      className="edit-input"
                      value={editAction}
                      onChange={(e) => setEditAction(e.target.value)}
                      placeholder="Action title"
                    />
                    <textarea
                      className="edit-textarea"
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      placeholder="Step description"
                    />
                    <div className="edit-actions">
                      <button className="edit-save-btn" onClick={() => handleEditSave(step.id)}>Save</button>
                      <button className="edit-cancel-btn" onClick={handleEditCancel}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <h3 className="step-action">
                      {(() => {
                        const { text, isRequired } = parseRequiredFromAction(step.action);
                        return (
                          <>
                            {text}
                            {isRequired && <span className="step-required-badge">Required</span>}
                          </>
                        );
                      })()}
                      {step.eventType && step.eventType !== 'click' && (
                        <span className={`step-type-badge ${step.eventType}`}>
                          {step.eventType === 'keyboard' ? <>{Icons.keyboard} Keyboard</> : <>{Icons.verticalArrows} Scroll</>}
                        </span>
                      )}
                      {/* Sprint K Phase 3 — speaker badge. Populated by
                          accepting a meeting_attribute suggestion from
                          the Improvements panel (suggestions are
                          generated by uploading a meeting transcript via
                          MeetingEnrichModal). Color + name resolved from
                          guide.participants; missing-participant
                          fallback shows a generic gray dot. The quoted
                          text from the transcript window lives in the
                          tooltip. */}
                      {step.meetingAttribution && Array.isArray(guide.participants) && (() => {
                        const speaker = guide.participants.find(p => p.id === step.meetingAttribution.speakerId);
                        const label = speaker?.displayName || 'Unknown speaker';
                        const color = speaker?.color || '#94a3b8';
                        const quote = step.meetingAttribution.quotedText
                          ? `"${step.meetingAttribution.quotedText}"`
                          : '';
                        const conf = Math.round((step.meetingAttribution.confidence || 0) * 100);
                        const tooltip = `${label} (${conf}% confidence)${quote ? '\n\n' + quote : ''}`;
                        return (
                          <span
                            className="step-speaker-badge"
                            style={{ borderColor: color, color }}
                            title={tooltip}
                          >
                            <span className="step-speaker-dot" style={{ background: color }} />
                            <span className="step-speaker-name">{label}</span>
                          </span>
                        );
                      })()}
                      {/* Sprint J — redaction badge. Counts ONLY zones
                          that produced a visible blur on the
                          screenshot (i.e. carry a bbox). Tier-0 zones
                          that matched in metadata (captured URL,
                          action label, voice transcript) have no bbox
                          because there's nothing to pixel-modify —
                          they live in the audit trail but the badge
                          shouldn't claim visual redaction when the
                          user looks at the screenshot and sees no
                          blur. Pure metadata matches show as nothing
                          in the UI; they're visible only in the
                          server-side audit log + the JSON column. */}
                      {Array.isArray(step.redactionZones) && (() => {
                        const visible = step.redactionZones.filter((z) => z && z.bbox);
                        if (visible.length === 0) return null;
                        const types = [...new Set(visible.map((z) => z.type))].join(', ');
                        return (
                          <span
                            className="step-type-badge"
                            title={`${visible.length} sensitive item${visible.length === 1 ? '' : 's'} redacted: ${types}`}
                            style={{
                              background: 'rgba(99, 102, 241, 0.12)',
                              color: '#4f46e5',
                              border: '1px solid rgba(99, 102, 241, 0.3)',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                            }}
                          >
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <rect x="3" y="11" width="18" height="11" rx="2" />
                              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                            </svg>
                            Redacted ({visible.length})
                          </span>
                        );
                      })()}
                      {/* Sprint J P2 — reveal-original button. Shown only when:
                          (1) the backend permitted this viewer to see originals
                          (`guide.canViewOriginal`), (2) the step actually
                          has an original stashed (`step.hasOriginal`), AND
                          (3) the original payload was included on this step
                          (defense in depth — backend already gated it).
                          Toggle adds/removes from `revealedSteps`. */}
                      {guide.canViewOriginal && step.hasOriginal && step.screenshotOriginal && (
                        <button
                          type="button"
                          className="step-type-badge"
                          onClick={() => toggleStepReveal(step.id)}
                          title={revealedSteps.has(step.id)
                            ? 'Hide the original screenshot for this step'
                            : 'Reveal the original (un-redacted) screenshot for this step'}
                          style={{
                            background: revealedSteps.has(step.id) ? 'rgba(245, 158, 11, 0.14)' : 'rgba(34, 197, 94, 0.14)',
                            color: revealedSteps.has(step.id) ? '#b45309' : '#15803d',
                            border: revealedSteps.has(step.id) ? '1px solid rgba(245, 158, 11, 0.35)' : '1px solid rgba(34, 197, 94, 0.35)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            cursor: 'pointer',
                            font: 'inherit',
                          }}
                        >
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            {revealedSteps.has(step.id) ? (
                              <>
                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                                <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                                <line x1="1" y1="1" x2="23" y2="23" />
                              </>
                            ) : (
                              <>
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                <circle cx="12" cy="12" r="3" />
                              </>
                            )}
                          </svg>
                          {revealedSteps.has(step.id) ? 'Hide original' : 'Reveal original'}
                        </button>
                      )}
                    </h3>
                    {canEdit && (
                      <button className="step-edit-btn" onClick={() => handleEditStart(step)}>{Icons.edit} Edit</button>
                    )}
                  </>
                )}
              </div>

              {/* CONTENT AREA — branches on stepType. Click steps render the
                  existing AnnotatedScreenshot + description path (unchanged).
                  Non-click types render their own content block. */}
              {editingStep !== step.id && (() => {
                const t = step.stepType || 'click';
                // Sprint J P2 — swap the displayed screenshot to the
                // original when the viewer toggled Reveal AND the
                // backend included `screenshotOriginal` on the payload.
                // The original has no AnnotatedScreenshot edits — it
                // predates the redaction step entirely — so disable
                // editing while revealed (annotations made on the
                // un-redacted view would be lost on toggle-back).
                const isRevealed = revealedSteps.has(step.id) && !!step.screenshotOriginal;
                const renderStep = isRevealed
                  ? { ...step, screenshot: step.screenshotOriginal }
                  : step;
                const canEditNow = canEdit && !isRevealed;

                if (t === 'click') {
                  return (
                    <>
                      {step.description && (
                        // Sprint K Phase 2 — meeting-mode guides ship step
                        // descriptions as markdown ("**Action.** ... **Context.** ...
                        // **What to observe.** ...") so the structured SOP
                        // narrative renders with bold section headers. Self-mode
                        // guides keep the plain <p> render — their descriptions
                        // are single-paragraph prose and don't need markdown
                        // parsing (kept as-is to avoid any layout drift on
                        // existing guides).
                        guide.meetingMode
                          ? <MarkdownText text={step.description} style={{ fontSize: 16, lineHeight: 1.7, color: '#475569', marginBottom: 14 }} />
                          : <p className="step-description">{step.description}</p>
                      )}
                      {renderStep.screenshot && (
                        <AnnotatedScreenshot
                          step={renderStep}
                          editable={canEditNow}
                          onAnnotationsChange={(annotations) => {
                            if (onUpdateStep && canEditNow) {
                              onUpdateStep(guide.id, step.id, { annotations });
                            }
                          }}
                          onClickDataChange={(clickData) => {
                            if (onUpdateStep && canEditNow) {
                              onUpdateStep(guide.id, step.id, { clickData });
                            }
                          }}
                          onScreenshotChange={canEditNow && onUpdateStep
                            ? (dataUrlOrNull) => onUpdateStep(guide.id, step.id, {
                                screenshot: dataUrlOrNull,
                                // Wipe annotations + click marker when the image
                                // itself changes — they were positioned for the
                                // old image and would be visually wrong on the
                                // new one. User can re-annotate.
                                annotations: [],
                                clickData: null,
                              })
                            : undefined}
                          onZoom={() => setZoomedStep(renderStep)}
                        />
                      )}
                      {!renderStep.screenshot && canEditNow && onUpdateStep && (
                        <AddScreenshotButton
                          onPick={(dataUrl) => onUpdateStep(guide.id, step.id, { screenshot: dataUrl })}
                        />
                      )}
                    </>
                  );
                }

                if (t === 'rich_text') {
                  return (
                    <div className="step-rich-text">{step.description}</div>
                  );
                }

                if (t === 'callout') {
                  const sev = step.severity || 'tip';
                  return (
                    <div className={`step-callout severity-${sev}`}>
                      <span className="step-callout-icon" aria-hidden="true">
                        {sev === 'critical' ? '!' : sev === 'warning' ? '⚠' : 'ⓘ'}
                      </span>
                      <div className="step-callout-body">{step.description}</div>
                    </div>
                  );
                }

                if (t === 'image') {
                  return (
                    <>
                      {step.description && (
                        <p className="step-description">{step.description}</p>
                      )}
                      {step.screenshot && (
                        <img
                          src={step.screenshot}
                          alt={step.action || 'Reference image'}
                          className="step-image"
                        />
                      )}
                    </>
                  );
                }

                if (t === 'audio_note') {
                  return (
                    <>
                      {step.description && (
                        <p className="step-description">{step.description}</p>
                      )}
                      {step.audioUrl && (
                        <audio src={step.audioUrl} controls className="step-audio" />
                      )}
                    </>
                  );
                }

                if (t === 'section_divider') {
                  return step.description ? (
                    <p className="step-section-subtitle">{step.description}</p>
                  ) : null;
                }

                return null;
              })()}

              {/* Step-specific comments */}
              <div className="step-comments">
                <button
                  className="step-comments-toggle"
                  onClick={() => setStepCommentingOn(stepCommentingOn === step.id ? null : step.id)}
                >
                  {Icons.messageCircle} {getStepComments(step.id).length > 0 ? (
                    <>
                      <span className="step-comments-badge">{getStepComments(step.id).length}</span>
                      {stepCommentingOn === step.id ? 'Hide comments' : 'View comments'}
                    </>
                  ) : (
                    'Add comment on this step'
                  )}
                </button>

                {stepCommentingOn === step.id && (
                  <>
                    {getStepComments(step.id).length > 0 && (
                      <div className="step-comment-list">
                        {getStepComments(step.id).map(comment => (
                          <div key={comment.id} className="step-comment-item">
                            <div className="step-comment-avatar">
                              {comment.userName?.charAt(0).toUpperCase() || 'U'}
                            </div>
                            <div className="step-comment-body">
                              <div className="step-comment-meta">
                                <span className="step-comment-author">{comment.userName || 'Unknown'}</span>
                                <span className="step-comment-time">
                                  {new Date(comment.createdAt).toLocaleDateString()}
                                </span>
                                {(comment.userId === user?.id || isOwner) && (
                                  <button
                                    className="step-comment-delete"
                                    onClick={() => handleDeleteCommentClick(comment.id)}
                                  >
                                    ×
                                  </button>
                                )}
                              </div>
                              <p className="step-comment-text">{comment.content}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="step-comment-input">
                      <textarea
                        placeholder="Add a comment on this step..."
                        value={stepComment}
                        onChange={(e) => setStepComment(e.target.value)}
                        rows={2}
                      />
                      <div className="step-comment-actions">
                        <button
                          className="desc-btn desc-btn-save"
                          onClick={() => handleAddCommentClick(step.id)}
                          disabled={!stepComment.trim() || commentLoading}
                        >
                          {commentLoading ? 'Posting...' : 'Post'}
                        </button>
                        <button
                          className="desc-btn desc-btn-cancel"
                          onClick={() => { setStepCommentingOn(null); setStepComment(''); }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          {/* Insert button after each step — hidden while a speaker
              filter is active. See the note on the top Insert button. */}
          {canEdit && !speakerFilter && <InsertStepButton insertAfterPosition={step.number} />}
        </React.Fragment>
        ))}

        {/* General Comments Section (not attached to specific steps) */}
        <div className="comments-section">
          <div className="comments-header">
            <h3>
              {Icons.messageCircle} General Comments
              <span className="comments-count">{getGeneralComments().length}</span>
            </h3>
            <button className="comments-toggle" onClick={() => setShowComments(!showComments)}>
              {showComments ? 'Hide' : 'Show'}
            </button>
          </div>

          {showComments && (
            <>
              <div className="comment-input-box">
                <div className="comment-avatar">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div className="comment-input-wrapper">
                  <textarea
                    className="comment-textarea"
                    placeholder="Add a general comment about this guide..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                  />
                  <div className="comment-submit-row">
                    <button
                      className="comment-submit-btn"
                      onClick={() => handleAddCommentClick(null)}
                      disabled={!newComment.trim() || commentLoading}
                    >
                      {commentLoading ? 'Posting...' : 'Post Comment'}
                    </button>
                  </div>
                </div>
              </div>

              {getGeneralComments().length === 0 ? (
                <div className="no-comments">
                  No general comments yet. Step-specific comments appear under each step above.
                </div>
              ) : (
                <div className="comments-list">
                  {getGeneralComments().map(comment => (
                    <div key={comment.id} className="comment-item">
                      <div className="comment-avatar">
                        {comment.userName?.charAt(0).toUpperCase() || 'U'}
                      </div>
                      <div className="comment-body">
                        <div className="comment-meta">
                          <span className="comment-author">{comment.userName}</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span className="comment-time">
                              {new Date(comment.createdAt).toLocaleDateString()}
                            </span>
                            {(comment.userId === user?.id || isOwner) && (
                              <button
                                className="comment-delete"
                                onClick={() => handleDeleteCommentClick(comment.id)}
                              >
                                ×
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="comment-content">{comment.content}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
       </div>{/* end detail-main-content */}

        {/* Change History Sidebar */}
        {showChangesPanel && (
          <aside className="changes-sidebar">
            <div className="changes-sidebar-header">
              <h3>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                Change History
              </h3>
              <button className="changes-sidebar-close" onClick={() => setShowChangesPanel(false)}>×</button>
            </div>
            <div className="changes-sidebar-body">
              {changesLoading ? (
                <div className="changes-loading">
                  <div className="btn-spinner" style={{ width: 24, height: 24, borderTopColor: theme.primary }} />
                  <span>Loading...</span>
                </div>
              ) : changes.length === 0 ? (
                <div className="changes-empty">
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  <p>No change history yet</p>
                  <span>Changes will appear here as you edit this guide</span>
                </div>
              ) : (
                <div className="changes-timeline">
                  {changes.map((change) => (
                    <div key={change.id} className="change-item">
                      <div className="change-line" />
                      <div className="change-dot-indicator" style={{ background: change.action.includes('step') || change.action.includes('reorder') || change.action.includes('renumber') || change.action.includes('duplicate') ? theme.primary : change.action.includes('share') ? '#8b5cf6' : change.action.includes('generated') || change.action.includes('workflow') ? '#f59e0b' : change.action === 'guide_created' ? '#10b981' : '#64748b' }} />
                      <div className="change-content">
                        <div className="change-description">{getChangeDescription(change)}</div>
                        <div className="change-meta">
                          <span className="change-user">{change.userName || 'Unknown'}</span>
                          <span className="change-dot">·</span>
                          <span className="change-time">{formatTimeAgo(change.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </aside>
        )}
      </main>

      {/* Embed Modal */}
      {showEmbedModal && (
        <EmbedModal
          guide={guide}
          data={embedData}
          loading={embedLoading}
          planFeatures={planFeatures}
          onSave={saveEmbed}
          onDisable={disableEmbed}
          onClose={() => setShowEmbedModal(false)}
          showToast={showToast}
        />
      )}

      {/* Suggestion review slide-in. Renders the backdrop + panel only when
          showSuggestionPanel is true (component returns null otherwise). */}
      <SuggestionReviewPanel
        open={showSuggestionPanel}
        suggestions={pendingSuggestions}
        stepsById={stepsById}
        decisions={suggestionDecisions}
        onDecide={handleSuggestionDecide}
        onAcceptAllHighConfidence={acceptAllHighConfidence}
        onAcceptAll={acceptAllSuggestions}
        onRejectAll={rejectAllSuggestions}
        onResetDecisions={resetSuggestionDecisions}
        onApply={handleApplySuggestions}
        applying={applyingSuggestions}
        onRegenerate={handleRegenerateSuggestions}
        regenerating={regeneratingSuggestions}
        onClose={() => setShowSuggestionPanel(false)}
      />

      {/* Share Modal */}
      {showShareModal && (
        <div className="share-modal-overlay" onClick={() => setShowShareModal(false)}>
          <div className="share-modal" onClick={(e) => e.stopPropagation()}>
            <div className="share-modal-header">
              <h3>Share "{guide.name}"</h3>
              <button className="share-modal-close" onClick={() => setShowShareModal(false)}>×</button>
            </div>
            <div className="share-modal-body">
              {/* Current shares */}
              {shares.length > 0 && (
                <div className="current-shares">
                  <p className="share-section-title">Currently shared with</p>
                  <div className="share-member-list">
                    {shares.map(share => (
                      <div key={share.id} className="share-member-item">
                        <div className="share-member-avatar">
                          {share.sharedWithName?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <div className="share-member-info">
                          <div className="share-member-name">{share.sharedWithName}</div>
                          <div className="share-member-email">{share.sharedWithEmail}</div>
                        </div>
                        <select
                          className="share-permission-inline"
                          value={share.permission || 'view'}
                          onChange={(e) => handleUpdateSharePermission(share.sharedWithUserId, e.target.value)}
                          style={{
                            padding: '6px 10px',
                            borderRadius: '6px',
                            border: '1px solid #e2e8f0',
                            fontSize: '12px',
                            color: '#64748b',
                            background: 'white',
                            cursor: 'pointer',
                            marginRight: '8px'
                          }}
                        >
                          <option value="view">Can view</option>
                          <option value="edit">Can edit</option>
                        </select>
                        <button
                          className="share-member-remove"
                          onClick={() => handleRemoveShareClick(share.sharedWithUserId)}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add new members */}
              <p className="share-section-title">Share with team members</p>
              {orgMembers.length === 0 ? (
                <div className="share-empty">
                  {sharingLoading ? 'Loading...' : 'No more team members to share with'}
                </div>
              ) : (
                <>
                  <div className="share-member-list">
                    {orgMembers.map(member => (
                      <div key={member.id} className="share-member-item">
                        <input
                          type="checkbox"
                          checked={selectedMembers.includes(member.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedMembers([...selectedMembers, member.id]);
                            } else {
                              setSelectedMembers(selectedMembers.filter(id => id !== member.id));
                            }
                          }}
                        />
                        <div className="share-member-avatar">
                          {member.name?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <div className="share-member-info">
                          <div className="share-member-name">{member.name}</div>
                          <div className="share-member-email">{member.email}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <select
                    className="share-permission-select"
                    value={sharePermission}
                    onChange={(e) => setSharePermission(e.target.value)}
                  >
                    <option value="view">Can view</option>
                    <option value="edit">Can edit</option>
                  </select>
                </>
              )}
            </div>
            <div className="share-modal-footer">
              <button className="share-btn-cancel" onClick={() => setShowShareModal(false)}>
                Cancel
              </button>
              <button
                className="share-btn-share"
                onClick={handleShare}
                disabled={selectedMembers.length === 0 || sharingLoading}
              >
                {sharingLoading ? 'Sharing...' : 'Share'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Zoom Modal */}
      {zoomedStep && (
        <div
          className="zoom-overlay"
          onClick={() => setZoomedStep(null)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setZoomedStep(null);
            if (e.key === 'ArrowLeft') {
              const idx = guide.steps.findIndex(s => s.id === zoomedStep.id);
              if (idx > 0) setZoomedStep(guide.steps[idx - 1]);
            }
            if (e.key === 'ArrowRight') {
              const idx = guide.steps.findIndex(s => s.id === zoomedStep.id);
              if (idx < guide.steps.length - 1) setZoomedStep(guide.steps[idx + 1]);
            }
          }}
          tabIndex={0}
          ref={(el) => el && el.focus()}
        >
          <div className="zoom-content" onClick={(e) => e.stopPropagation()}>
            <button className="zoom-close" onClick={() => setZoomedStep(null)}>×</button>
            <img
              src={zoomedStep.screenshot}
              alt={`Step ${zoomedStep.number}`}
              className="zoom-image"
            />
            <div className="zoom-step-info">
              <span className="zoom-step-number">{zoomedStep.number}</span>
              {zoomedStep.action}
            </div>
          </div>

          {/* Navigation buttons */}
          {guide.steps.findIndex(s => s.id === zoomedStep.id) > 0 && (
            <button
              className="zoom-nav zoom-nav-prev"
              onClick={(e) => {
                e.stopPropagation();
                const idx = guide.steps.findIndex(s => s.id === zoomedStep.id);
                setZoomedStep(guide.steps[idx - 1]);
              }}
            >
              ←
            </button>
          )}
          {guide.steps.findIndex(s => s.id === zoomedStep.id) < guide.steps.length - 1 && (
            <button
              className="zoom-nav zoom-nav-next"
              onClick={(e) => {
                e.stopPropagation();
                const idx = guide.steps.findIndex(s => s.id === zoomedStep.id);
                setZoomedStep(guide.steps[idx + 1]);
              }}
            >
              →
            </button>
          )}
        </div>
      )}

      {/* Scroll to Top Button */}
      <button
        className={`scroll-to-top-btn ${showScrollTop ? 'visible' : ''}`}
        onClick={scrollToTop}
        title="Scroll to top"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 15l-6-6-6 6"/>
        </svg>
      </button>

      {/* Insert Step Modal — picker view, then a per-type form/instructions.
          Backdrop-click only dismisses on the picker view; once the user has
          opened a per-type form, only the X / Close / Back buttons can close
          it so accidental clicks outside the modal don't wipe drafts. */}
      {showInsertModal && (
        <div
          className="insert-modal-overlay"
          onClick={() => {
            if (!insertStepType) {
              setShowInsertModal(false);
              setInsertStepType(null);
            }
          }}
        >
          <div className="insert-modal" onClick={e => e.stopPropagation()}>
            <div className="insert-modal-header">
              <h3>
                {insertStepType ? (
                  <>
                    <button
                      className="insert-modal-back"
                      onClick={() => setInsertStepType(null)}
                      title="Back to step types"
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', marginRight: 8, fontSize: 18 }}
                    >←</button>
                    {insertStepType === 'click' && 'Capture with extension'}
                    {insertStepType === 'rich_text' && 'Add rich text'}
                    {insertStepType === 'audio_note' && 'Add audio note'}
                    {insertStepType === 'callout' && 'Add callout'}
                    {insertStepType === 'image' && 'Add image'}
                    {insertStepType === 'section_divider' && 'Add section divider'}
                  </>
                ) : 'Insert New Step'}
              </h3>
              <button className="insert-modal-close" onClick={() => { setShowInsertModal(false); setInsertStepType(null); }}>×</button>
            </div>

            <div className="insert-modal-body">
              <p style={{ marginBottom: 16, color: '#64748b', fontSize: 13 }}>
                {insertingAtPosition === 0
                  ? 'This will be inserted at the beginning of the guide.'
                  : `This will be inserted after step ${insertingAtPosition}.`}
              </p>

              {/* ── PICKER VIEW ── */}
              {!insertStepType && (
                <div className="insert-type-picker">
                  {[
                    {
                      type: 'click', label: 'Capture with extension', sub: 'Continue recording clicks in the browser',
                      icon: (
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"/>
                          <circle cx="12" cy="12" r="6"/>
                          <circle cx="12" cy="12" r="2" fill="currentColor"/>
                        </svg>
                      )
                    },
                    {
                      type: 'rich_text', label: 'Rich text', sub: 'Add a paragraph, script, or context note',
                      icon: (
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                          <polyline points="14 2 14 8 20 8"/>
                          <line x1="8" y1="13" x2="16" y2="13"/>
                          <line x1="8" y1="17" x2="14" y2="17"/>
                        </svg>
                      )
                    },
                    {
                      type: 'callout', label: 'Callout', sub: 'Tip, warning, or critical notice',
                      icon: (
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"/>
                          <line x1="12" y1="8" x2="12" y2="12"/>
                          <line x1="12" y1="16" x2="12.01" y2="16"/>
                        </svg>
                      )
                    },
                    {
                      type: 'image', label: 'Image', sub: 'Upload a reference photo or diagram',
                      icon: (
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="3" width="18" height="18" rx="2"/>
                          <circle cx="8.5" cy="8.5" r="1.5"/>
                          <polyline points="21 15 16 10 5 21"/>
                        </svg>
                      )
                    },
                    {
                      type: 'audio_note', label: 'Audio note', sub: 'Record or upload a voice clip',
                      icon: (
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                          <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
                          <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
                        </svg>
                      )
                    },
                    {
                      type: 'section_divider', label: 'Section divider', sub: 'Chapter heading for long guides',
                      icon: (
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M6 4v16"/>
                          <path d="M18 4v16"/>
                          <path d="M6 12h12"/>
                        </svg>
                      )
                    },
                  ].map(opt => (
                    <button
                      key={opt.type}
                      type="button"
                      className="insert-type-option"
                      onClick={() => setInsertStepType(opt.type)}
                    >
                      <span className="insert-type-icon">{opt.icon}</span>
                      <span className="insert-type-text">
                        <span className="insert-type-label">{opt.label}</span>
                        <span className="insert-type-sub">{opt.sub}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {/* ── CLICK CAPTURE (original instructions) ── */}
              {insertStepType === 'click' && (
                <div className="insert-modal-instructions">
                  <div className="instruction-step">
                    <span className="instruction-number">1</span>
                    <span>Navigate to the page where you want to record new steps</span>
                  </div>
                  <div className="instruction-step">
                    <span className="instruction-number">2</span>
                    <span>Click the InstaGuide extension icon in your browser toolbar</span>
                  </div>
                  <div className="instruction-step">
                    <span className="instruction-number">3</span>
                    <span>Click "Continue Recording" to start capturing steps</span>
                  </div>
                  <div className="instruction-step">
                    <span className="instruction-number">4</span>
                    <span>Click "Stop Recording" when done — new steps will be inserted at the chosen position</span>
                  </div>
                </div>
              )}

              {/* ── RICH TEXT ── */}
              {insertStepType === 'rich_text' && (
                <div className="manual-form">
                  <label className="manual-label">Optional title</label>
                  <input
                    type="text"
                    className="manual-input"
                    placeholder="e.g. Greeting script"
                    value={manualDraft.title}
                    onChange={(e) => setManualDraft({ ...manualDraft, title: e.target.value })}
                  />
                  <div className="manual-label-row">
                    <label className="manual-label">Body</label>
                    <div className="manual-input-actions">
                      <MicDictateButton onAppend={appendDraft('content')} />
                      <AIEnhanceButton
                        text={manualDraft.content}
                        onEnhanced={(t) => setManualDraft((d) => ({ ...d, content: t }))}
                      />
                    </div>
                  </div>
                  <textarea
                    className="manual-textarea"
                    rows={8}
                    placeholder="Write the text or click Dictate to speak it…"
                    value={manualDraft.content}
                    onChange={(e) => setManualDraft({ ...manualDraft, content: e.target.value })}
                  />
                </div>
              )}

              {/* ── CALLOUT ── */}
              {insertStepType === 'callout' && (
                <div className="manual-form">
                  <label className="manual-label">Severity</label>
                  <div className="severity-picker">
                    {['tip', 'warning', 'critical'].map(sev => (
                      <button
                        key={sev}
                        type="button"
                        className={`severity-pill severity-${sev} ${manualDraft.severity === sev ? 'active' : ''}`}
                        onClick={() => setManualDraft({ ...manualDraft, severity: sev })}
                      >
                        {sev}
                      </button>
                    ))}
                  </div>
                  <label className="manual-label">Optional title</label>
                  <input
                    type="text"
                    className="manual-input"
                    placeholder="e.g. Don't skip this step"
                    value={manualDraft.title}
                    onChange={(e) => setManualDraft({ ...manualDraft, title: e.target.value })}
                  />
                  <div className="manual-label-row">
                    <label className="manual-label">Message</label>
                    <div className="manual-input-actions">
                      <MicDictateButton onAppend={appendDraft('content')} />
                      <AIEnhanceButton
                        text={manualDraft.content}
                        onEnhanced={(t) => setManualDraft((d) => ({ ...d, content: t }))}
                      />
                    </div>
                  </div>
                  <textarea
                    className="manual-textarea"
                    rows={4}
                    placeholder="What the reader needs to know — type or dictate…"
                    value={manualDraft.content}
                    onChange={(e) => setManualDraft({ ...manualDraft, content: e.target.value })}
                  />
                </div>
              )}

              {/* ── IMAGE ── */}
              {insertStepType === 'image' && (
                <div className="manual-form">
                  <label className="manual-label">Image file</label>
                  <label className="manual-file-label">
                    {/* Image-shaped icon — distinguishes this picker from the
                        audio one which uses an upload arrow. */}
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <rect x="3" y="3" width="18" height="18" rx="2"/>
                      <circle cx="8.5" cy="8.5" r="1.5"/>
                      <polyline points="21 15 16 10 5 21"/>
                    </svg>
                    <span>Choose image</span>
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const dataUrl = await readFileAsDataUrl(file);
                        setManualDraft({ ...manualDraft, imageData: dataUrl, imageFileName: file.name });
                      }}
                    />
                  </label>
                  {manualDraft.imageFileName && !manualDraft.imageData && (
                    <span className="manual-file-name">{manualDraft.imageFileName}</span>
                  )}
                  {manualDraft.imageData && (
                    <img
                      src={manualDraft.imageData}
                      alt="preview"
                      style={{ marginTop: 10, maxWidth: '100%', maxHeight: 220, borderRadius: 8, border: '1px solid #e2e8f0' }}
                    />
                  )}
                  <label className="manual-label" style={{ marginTop: 12 }}>Optional title</label>
                  <input
                    type="text"
                    className="manual-input"
                    placeholder="e.g. Reference diagram"
                    value={manualDraft.title}
                    onChange={(e) => setManualDraft({ ...manualDraft, title: e.target.value })}
                  />
                  <label className="manual-label">Optional caption</label>
                  <input
                    type="text"
                    className="manual-input"
                    placeholder="What this image shows…"
                    value={manualDraft.content}
                    onChange={(e) => setManualDraft({ ...manualDraft, content: e.target.value })}
                  />
                </div>
              )}

              {/* ── AUDIO NOTE ── */}
              {insertStepType === 'audio_note' && (
                <div className="manual-form">
                  <label className="manual-label">Record from mic</label>
                  <AudioRecorderControl
                    onRecorded={(dataUrl) => setManualDraft({ ...manualDraft, audioUrl: dataUrl })}
                    disabled={manualSubmitting}
                  />
                  <div className="manual-or-divider"><span>or upload a file</span></div>
                  {/* Styled file-upload — native input is hidden, the label
                      becomes the visible clickable surface so it matches the
                      Record button's aesthetic. We track the picked filename
                      in component state so it shows next to the button. */}
                  <label className="manual-file-label">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="17 8 12 3 7 8"/>
                      <line x1="12" y1="3" x2="12" y2="15"/>
                    </svg>
                    <span>Choose audio file</span>
                    <input
                      type="file"
                      accept="audio/*"
                      style={{ display: 'none' }}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const dataUrl = await readFileAsDataUrl(file);
                        setManualDraft({ ...manualDraft, audioUrl: dataUrl, audioFileName: file.name });
                      }}
                    />
                  </label>
                  {manualDraft.audioFileName && (
                    <span className="manual-file-name">{manualDraft.audioFileName}</span>
                  )}
                  {manualDraft.audioUrl && (
                    <div style={{ marginTop: 10 }}>
                      <audio controls src={manualDraft.audioUrl} style={{ width: '100%' }} />
                      <button
                        type="button"
                        className="manual-clear-btn"
                        onClick={() => setManualDraft({ ...manualDraft, audioUrl: '', audioFileName: '' })}
                        disabled={manualSubmitting}
                      >
                        Clear recording
                      </button>
                      <p className="manual-hint">A transcript will be generated automatically and shown in exports.</p>
                    </div>
                  )}
                  <label className="manual-label" style={{ marginTop: 12 }}>Optional title</label>
                  <input
                    type="text"
                    className="manual-input"
                    placeholder="e.g. Pronunciation guide"
                    value={manualDraft.title}
                    onChange={(e) => setManualDraft({ ...manualDraft, title: e.target.value })}
                  />
                </div>
              )}

              {/* ── SECTION DIVIDER ── */}
              {insertStepType === 'section_divider' && (
                <div className="manual-form">
                  <label className="manual-label">Section title</label>
                  <input
                    type="text"
                    className="manual-input"
                    placeholder="e.g. New Patient Workflow"
                    value={manualDraft.title}
                    onChange={(e) => setManualDraft({ ...manualDraft, title: e.target.value })}
                  />
                  <label className="manual-label">Optional subtitle</label>
                  <input
                    type="text"
                    className="manual-input"
                    placeholder="What this section covers…"
                    value={manualDraft.content}
                    onChange={(e) => setManualDraft({ ...manualDraft, content: e.target.value })}
                  />
                </div>
              )}
            </div>

            <div className="insert-modal-footer">
              {insertStepType && insertStepType !== 'click' ? (
                <>
                  <button
                    className="insert-modal-btn"
                    onClick={() => setInsertStepType(null)}
                    style={{ background: '#f1f5f9', color: '#475569', marginRight: 8 }}
                  >
                    Back
                  </button>
                  <button
                    className="insert-modal-btn"
                    onClick={handleInsertManualStep}
                    disabled={manualSubmitting}
                  >
                    {manualSubmitting ? 'Inserting…' : 'Insert step'}
                  </button>
                </>
              ) : (
                <button className="insert-modal-btn" onClick={() => { setShowInsertModal(false); setInsertStepType(null); }}>
                  {insertStepType === 'click' ? 'Got it' : 'Close'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// Annotated Screenshot Component with click indicators and manual annotations
// Small "add screenshot" affordance shown under a text-only step when
// the user is editable. Opens a file picker, reads the file client-side
// into a base64 data URL, and hands it back via `onPick`. Meeting-mode
// imports frequently produce steps without screenshots (discussion
// scenes, verifier-rejected demo scenes) — this lets users attach one
// manually without dropping into the extension's insert flow.
function AddScreenshotButton({ onPick }) {
  const inputRef = useRef(null);
  return (
    <div style={{ marginTop: 6 }}>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '7px 14px',
          borderRadius: 8,
          border: '1px dashed #cbd5e1',
          background: '#f8fafc',
          color: '#475569',
          fontSize: 13,
          fontWeight: 500,
          cursor: 'pointer',
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2"/>
          <circle cx="8.5" cy="8.5" r="1.5"/>
          <polyline points="21 15 16 10 5 21"/>
        </svg>
        Add screenshot
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (!f) return;
          if (f.size > 8 * 1024 * 1024) {
            alert('Image is larger than 8 MB — pick a smaller file.');
            return;
          }
          const reader = new FileReader();
          reader.onload = () => {
            const dataUrl = String(reader.result || '');
            if (dataUrl.startsWith('data:image/')) onPick(dataUrl);
          };
          reader.onerror = () => alert('Could not read the selected file.');
          reader.readAsDataURL(f);
        }}
      />
    </div>
  );
}

function AnnotatedScreenshot({ step, onAnnotationsChange, onClickDataChange, onScreenshotChange, editable = false, onZoom }) {
  const imgRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const [imgSize, setImgSize] = useState({ width: 0, height: 0 });
  const [isEditing, setIsEditing] = useState(false);
  // Default to null (Select mode) — clicking a shape tool activates
  // drawing. Prevents accidental circles/rects on every click when the
  // user's intent was to drag the blue element-rect box or just view
  // the screenshot in edit mode. Users pick a shape tool to draw.
  const [tool, setTool] = useState(null);
  const [color, setColor] = useState('#ef4444');
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPos, setStartPos] = useState(null);
  const [annotations, setAnnotations] = useState(step.annotations || []);
  const [isDraggingIndicator, setIsDraggingIndicator] = useState(false);
  const [indicatorPos, setIndicatorPos] = useState(null);
  // Blue element-rect drag + resize state. Separate from the click-
  // indicator drag so users can adjust each independently (the
  // extension used to resolve label → hidden input, which put the rect
  // nowhere near the click for existing guides — this lets them fix
  // those without re-recording). `rectPos` holds { left, top, width,
  // height } in canvas display px while a move OR resize is in flight;
  // downstream renderer prefers it over the stored elementRect.
  const [isDraggingRect, setIsDraggingRect] = useState(false);
  const [rectPos, setRectPos] = useState(null);
  const rectPosRef = useRef(null);
  const rectDragOffsetRef = useRef(null);
  // resizeHandle is one of 'n'|'s'|'e'|'w'|'nw'|'ne'|'sw'|'se' while a
  // resize is active; null when idle. Mirrors the CSS resize cursors.
  const [resizeHandle, setResizeHandle] = useState(null);
  const resizeStartRef = useRef(null);
  const MIN_RECT_PX = 12;  // don't let users shrink the box to nothing
  const [localClickData, setLocalClickData] = useState(step.clickData);
  const [showTextInput, setShowTextInput] = useState(false);
  const [pendingTextPos, setPendingTextPos] = useState(null);
  const [textInputValue, setTextInputValue] = useState('');
  const textInputRef = useRef(null);

  // Update local clickData when step changes
  useEffect(() => {
    setLocalClickData(step.clickData);
    setIndicatorPos(null);
  }, [step.clickData]);

  useEffect(() => {
    if (imgRef.current) {
      const updateSize = () => {
        setImgSize({
          width: imgRef.current.offsetWidth,
          height: imgRef.current.offsetHeight
        });
      };
      imgRef.current.onload = updateSize;
      updateSize();
    }
  }, [step.screenshot]);

  useEffect(() => {
    setAnnotations(step.annotations || []);
  }, [step.annotations]);

  // Keep ref in sync with indicatorPos state for use in event handlers
  useEffect(() => {
    indicatorPosRef.current = indicatorPos;
  }, [indicatorPos]);
  useEffect(() => { rectPosRef.current = rectPos; }, [rectPos]);

  // Calculate indicator position based on original viewport vs displayed size
  const getIndicatorPosition = () => {
    // If we have a custom position from dragging, use that
    if (indicatorPos) {
      return {
        clickX: indicatorPos.x,
        clickY: indicatorPos.y,
        elementRect: null,
        isDragged: true
      };
    }

    if (!localClickData || !imgSize.width) return null;

    const { x, y, viewportSize, elementRect, capturedOnUrl } = localClickData;
    if (!viewportSize) return null;
    // Zero/missing viewport dimensions would make scaleX/scaleY = Infinity
    // (division by zero), producing `left: Infinity` inline styles that React
    // rejects with a console warning. Treat as missing data.
    if (!(viewportSize.width > 0) || !(viewportSize.height > 0)) return null;

    // If capturedOnUrl is null, the page navigated during capture - hide indicator
    // The screenshot shows a different page than where the click happened
    if (capturedOnUrl === null) {
      return null;
    }

    // Validate click coordinates are reasonable (not at origin or negative)
    // Clicks near (0,0) usually indicate capture issues
    if (x <= 5 && y <= 5) {
      return null;
    }

    // Validate coordinates are within viewport bounds
    if (x < 0 || y < 0 || x > viewportSize.width || y > viewportSize.height) {
      return null;
    }

    // Account for device pixel ratio - screenshot is captured at DPR resolution
    const dpr = viewportSize.devicePixelRatio || 1;
    const actualViewportWidth = viewportSize.width * dpr;
    const actualViewportHeight = viewportSize.height * dpr;

    const scaleX = imgSize.width / actualViewportWidth;
    const scaleY = imgSize.height / actualViewportHeight;

    // Click coordinates need to be scaled by DPR first, then by display scale
    const clickX = x * dpr * scaleX;
    const clickY = y * dpr * scaleY;

    return {
      clickX,
      clickY,
      elementRect: elementRect ? {
        left: elementRect.left * dpr * scaleX,
        top: elementRect.top * dpr * scaleY,
        width: elementRect.width * dpr * scaleX,
        height: elementRect.height * dpr * scaleY
      } : null
    };
  };

  const pos = getIndicatorPosition();

  const getMousePos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const handleMouseDown = (e) => {
    if (!isEditing) return;
    // No shape tool selected → Select mode. Canvas clicks do nothing
    // so the blue element-rect can be dragged + read-only inspection
    // works without accidental shape drops. User picks a tool from
    // the toolbar to draw.
    if (!tool) return;
    setIsDrawing(true);
    setStartPos(getMousePos(e));
  };

  const handleMouseUp = (e) => {
    if (!isEditing || !isDrawing) return;
    setIsDrawing(false);
    const endPos = getMousePos(e);

    if (!startPos) return;

    let newAnnotation = null;
    const dx = endPos.x - startPos.x;
    const dy = endPos.y - startPos.y;

    if (tool === 'circle') {
      const radius = Math.sqrt(dx * dx + dy * dy);
      if (radius > 5) {
        newAnnotation = {
          type: 'circle',
          x: startPos.x,
          y: startPos.y,
          radius,
          color
        };
      }
    } else if (tool === 'rect') {
      if (Math.abs(dx) > 5 && Math.abs(dy) > 5) {
        newAnnotation = {
          type: 'rect',
          x: Math.min(startPos.x, endPos.x),
          y: Math.min(startPos.y, endPos.y),
          width: Math.abs(dx),
          height: Math.abs(dy),
          color
        };
      }
    } else if (tool === 'arrow') {
      if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
        newAnnotation = {
          type: 'arrow',
          x1: startPos.x,
          y1: startPos.y,
          x2: endPos.x,
          y2: endPos.y,
          color
        };
      }
    } else if (tool === 'blur') {
      if (Math.abs(dx) > 10 && Math.abs(dy) > 10) {
        newAnnotation = {
          type: 'blur',
          x: Math.min(startPos.x, endPos.x),
          y: Math.min(startPos.y, endPos.y),
          width: Math.abs(dx),
          height: Math.abs(dy)
        };
      }
    } else if (tool === 'text') {
      // Show text input dialog instead of browser prompt
      setPendingTextPos({ x: startPos.x, y: startPos.y, color });
      setTextInputValue('');
      setShowTextInput(true);
      // Focus will be set by useEffect
      return; // Don't add annotation yet, wait for text input
    }

    if (newAnnotation) {
      const newAnnotations = [...annotations, newAnnotation];
      setAnnotations(newAnnotations);
      if (onAnnotationsChange) {
        onAnnotationsChange(newAnnotations);
      }
    }
    setStartPos(null);
  };

  const clearAnnotations = () => {
    setAnnotations([]);
    if (onAnnotationsChange) {
      onAnnotationsChange([]);
    }
  };

  const undoAnnotation = () => {
    const newAnnotations = annotations.slice(0, -1);
    setAnnotations(newAnnotations);
    if (onAnnotationsChange) {
      onAnnotationsChange(newAnnotations);
    }
  };

  // Handle text annotation submission
  const handleTextSubmit = () => {
    if (textInputValue.trim() && pendingTextPos) {
      const newAnnotation = {
        type: 'text',
        x: pendingTextPos.x,
        y: pendingTextPos.y,
        text: textInputValue.trim(),
        color: pendingTextPos.color
      };
      const newAnnotations = [...annotations, newAnnotation];
      setAnnotations(newAnnotations);
      if (onAnnotationsChange) {
        onAnnotationsChange(newAnnotations);
      }
    }
    setShowTextInput(false);
    setPendingTextPos(null);
    setTextInputValue('');
  };

  // Focus text input when shown
  useEffect(() => {
    if (showTextInput && textInputRef.current) {
      textInputRef.current.focus();
    }
  }, [showTextInput]);

  // Reference to track if we've added document listeners
  const dragStartPosRef = useRef(null);
  // Ref to track current indicator position during drag (avoids stale closure)
  const indicatorPosRef = useRef(null);

  // Handle indicator drag start - use document-level events for smooth dragging
  const handleIndicatorMouseDown = (e) => {
    if (!isEditing) return;
    e.stopPropagation();
    e.preventDefault();

    // Store initial position
    if (canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      dragStartPosRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
        rect
      };
      setIndicatorPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    }
    setIsDraggingIndicator(true);
  };

  // Use effect to handle document-level mouse events when dragging
  useEffect(() => {
    if (!isDraggingIndicator) return;

    const handleMouseMove = (e) => {
      if (!canvasRef.current) return;
      const rect = canvasRef.current.getBoundingClientRect();
      // Calculate position relative to canvas
      let x = e.clientX - rect.left;
      let y = e.clientY - rect.top;
      // Clamp to canvas bounds
      x = Math.max(0, Math.min(x, rect.width));
      y = Math.max(0, Math.min(y, rect.height));
      setIndicatorPos({ x, y });
    };

    const handleMouseUp = () => {
      finishDrag();
    };

    // Add document-level listeners for smooth dragging
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingIndicator]);

  // ── Blue-box (element rect) drag ────────────────────────────────────
  // Mirrors the click-indicator drag flow. mousedown captures the
  // offset between cursor + rect top-left so the rect translates
  // under the cursor without snapping its origin. mousemove updates
  // rectPos in canvas display px. mouseup persists back to the stored
  // clickData.elementRect (in CSS pixels, viewport-relative — same
  // coord space downstream renderers already expect).
  const handleRectMouseDown = (rectDisplay) => (e) => {
    if (!isEditing) return;
    e.stopPropagation();
    e.preventDefault();
    if (!canvasRef.current) return;
    const canvasRect = canvasRef.current.getBoundingClientRect();
    const cursorX = e.clientX - canvasRect.left;
    const cursorY = e.clientY - canvasRect.top;
    // Remember cursor→rect offset so the rect doesn't jump to align
    // its top-left with the cursor position when the drag starts.
    rectDragOffsetRef.current = {
      offsetX: cursorX - rectDisplay.left,
      offsetY: cursorY - rectDisplay.top,
      canvasWidth: canvasRect.width,
      canvasHeight: canvasRect.height,
    };
    // Move preserves current width/height; resize will replace them.
    setRectPos({ left: rectDisplay.left, top: rectDisplay.top, width: rectDisplay.width, height: rectDisplay.height });
    setIsDraggingRect(true);
  };

  // Resize handler — mousedown on a corner/edge handle. Captures the
  // rect + cursor origin so mousemove can compute new dimensions based
  // on which handle direction is dragged. The 8 handles carry the 8
  // cardinal + intercardinal directions (n/s/e/w/nw/ne/sw/se).
  const handleRectResizeMouseDown = (handle, rectDisplay) => (e) => {
    if (!isEditing) return;
    e.stopPropagation();
    e.preventDefault();
    if (!canvasRef.current) return;
    const canvasRect = canvasRef.current.getBoundingClientRect();
    resizeStartRef.current = {
      handle,
      startCursorX: e.clientX - canvasRect.left,
      startCursorY: e.clientY - canvasRect.top,
      startLeft: rectDisplay.left,
      startTop: rectDisplay.top,
      startWidth: rectDisplay.width,
      startHeight: rectDisplay.height,
      canvasWidth: canvasRect.width,
      canvasHeight: canvasRect.height,
    };
    setRectPos({ left: rectDisplay.left, top: rectDisplay.top, width: rectDisplay.width, height: rectDisplay.height });
    setResizeHandle(handle);
  };

  useEffect(() => {
    if (!isDraggingRect && !resizeHandle) return;
    const handleMove = (e) => {
      if (!canvasRef.current) return;
      const canvasRect = canvasRef.current.getBoundingClientRect();
      const curX = e.clientX - canvasRect.left;
      const curY = e.clientY - canvasRect.top;

      if (isDraggingRect) {
        const off = rectDragOffsetRef.current;
        if (!off) return;
        const w = rectPosRef.current?.width ?? 0;
        const h = rectPosRef.current?.height ?? 0;
        let left = curX - off.offsetX;
        let top = curY - off.offsetY;
        left = Math.max(0, Math.min(left, off.canvasWidth - w));
        top = Math.max(0, Math.min(top, off.canvasHeight - h));
        setRectPos({ left, top, width: w, height: h });
        return;
      }

      // Resize path — compute new left/top/width/height from which
      // handle is dragged. West/North handles move the origin AND
      // shrink/grow width/height in the opposite direction; East/South
      // just grow width/height. Corner handles do both axes.
      const st = resizeStartRef.current;
      if (!st) return;
      const dx = curX - st.startCursorX;
      const dy = curY - st.startCursorY;
      let left = st.startLeft;
      let top = st.startTop;
      let width = st.startWidth;
      let height = st.startHeight;
      const h = st.handle;
      if (h.includes('w')) {
        // West edge — origin.left moves with cursor, width shrinks
        // by same amount. Cap so width stays ≥ MIN_RECT_PX.
        const maxDx = st.startWidth - MIN_RECT_PX;
        const clampedDx = Math.max(-st.startLeft, Math.min(dx, maxDx));
        left = st.startLeft + clampedDx;
        width = st.startWidth - clampedDx;
      }
      if (h.includes('e')) {
        // East edge — width grows with cursor. Clamp to canvas edge.
        const maxWidth = st.canvasWidth - st.startLeft;
        width = Math.max(MIN_RECT_PX, Math.min(st.startWidth + dx, maxWidth));
      }
      if (h.includes('n')) {
        const maxDy = st.startHeight - MIN_RECT_PX;
        const clampedDy = Math.max(-st.startTop, Math.min(dy, maxDy));
        top = st.startTop + clampedDy;
        height = st.startHeight - clampedDy;
      }
      if (h.includes('s')) {
        const maxHeight = st.canvasHeight - st.startTop;
        height = Math.max(MIN_RECT_PX, Math.min(st.startHeight + dy, maxHeight));
      }
      setRectPos({ left, top, width, height });
    };
    const handleUp = () => finishRectDrag();
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };
  }, [isDraggingRect, resizeHandle]);

  const finishRectDrag = () => {
    if (!isDraggingRect && !resizeHandle) return;
    const current = rectPosRef.current;
    setIsDraggingRect(false);
    setResizeHandle(null);
    rectDragOffsetRef.current = null;
    resizeStartRef.current = null;
    if (!current || !localClickData?.viewportSize || !imgSize.width) {
      setRectPos(null);
      return;
    }
    // Convert canvas display px → CSS pixels (viewport-relative) so
    // the stored elementRect stays in the same coord space downstream
    // renders / exports already read (see getIndicatorPosition above).
    const { viewportSize } = localClickData;
    const dpr = viewportSize.devicePixelRatio || 1;
    const actualViewportWidth = viewportSize.width * dpr;
    const actualViewportHeight = viewportSize.height * dpr;
    const scaleX = imgSize.width / actualViewportWidth;
    const scaleY = imgSize.height / actualViewportHeight;
    const newRect = {
      left: current.left / scaleX / dpr,
      top: current.top / scaleY / dpr,
      width: current.width / scaleX / dpr,
      height: current.height / scaleY / dpr,
    };
    const newClickData = {
      ...localClickData,
      elementRect: newRect,
      // Mark as manually adjusted so the sanity-null gates elsewhere
      // (e.g. capturedOnUrl === null) don't hide the corrected box.
      capturedOnUrl: localClickData.capturedOnUrl || 'manually-adjusted',
    };
    setLocalClickData(newClickData);
    setRectPos(null);
    if (onClickDataChange) onClickDataChange(newClickData);
  };

  // Finish dragging and save position
  const finishDrag = () => {
    if (!isDraggingIndicator) return;

    // Use ref to get the current position (avoids stale closure issue)
    const currentIndicatorPos = indicatorPosRef.current;
    setIsDraggingIndicator(false);
    dragStartPosRef.current = null;

    if (!currentIndicatorPos || !localClickData) {
      return;
    }

    // Convert display position back to original coordinates
    const { viewportSize } = localClickData;
    if (!viewportSize || !imgSize.width) return;

    const dpr = viewportSize.devicePixelRatio || 1;
    const actualViewportWidth = viewportSize.width * dpr;
    const actualViewportHeight = viewportSize.height * dpr;

    const scaleX = imgSize.width / actualViewportWidth;
    const scaleY = imgSize.height / actualViewportHeight;

    // Reverse the scaling to get original coordinates
    const originalX = currentIndicatorPos.x / scaleX / dpr;
    const originalY = currentIndicatorPos.y / scaleY / dpr;

    // Update local state
    const newClickData = {
      ...localClickData,
      x: originalX,
      y: originalY,
      // Mark as manually adjusted so we don't hide it
      capturedOnUrl: localClickData.capturedOnUrl || 'manually-adjusted'
    };
    setLocalClickData(newClickData);
    setIndicatorPos(null);

    // Save to backend
    if (onClickDataChange) {
      onClickDataChange(newClickData);
    }
  };

  // Legacy handler for canvas events (kept for compatibility)
  const handleIndicatorDrag = (e) => {
    // Now handled by document-level events
  };

  const handleIndicatorMouseUp = () => {
    finishDrag();
  };

  // Render annotation
  const renderAnnotation = (ann, index) => {
    if (ann.type === 'circle') {
      return (
        <div
          key={index}
          style={{
            position: 'absolute',
            left: ann.x - ann.radius,
            top: ann.y - ann.radius,
            width: ann.radius * 2,
            height: ann.radius * 2,
            border: `3px solid ${ann.color}`,
            borderRadius: '50%',
            pointerEvents: 'none'
          }}
        />
      );
    } else if (ann.type === 'rect') {
      return (
        <div
          key={index}
          style={{
            position: 'absolute',
            left: ann.x,
            top: ann.y,
            width: ann.width,
            height: ann.height,
            border: `3px solid ${ann.color}`,
            borderRadius: '4px',
            pointerEvents: 'none'
          }}
        />
      );
    } else if (ann.type === 'arrow') {
      const angle = Math.atan2(ann.y2 - ann.y1, ann.x2 - ann.x1);
      const length = Math.sqrt((ann.x2 - ann.x1) ** 2 + (ann.y2 - ann.y1) ** 2);
      return (
        <svg
          key={index}
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none'
          }}
        >
          <defs>
            <marker id={`arrowhead-${index}`} markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
              <polygon points="0 0, 10 3.5, 0 7" fill={ann.color} />
            </marker>
          </defs>
          <line
            x1={ann.x1}
            y1={ann.y1}
            x2={ann.x2}
            y2={ann.y2}
            stroke={ann.color}
            strokeWidth="3"
            markerEnd={`url(#arrowhead-${index})`}
          />
        </svg>
      );
    } else if (ann.type === 'text') {
      return (
        <div
          key={index}
          style={{
            position: 'absolute',
            left: ann.x,
            top: ann.y,
            backgroundColor: ann.color,
            color: 'white',
            padding: '6px 12px',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '600',
            pointerEvents: 'none',
            whiteSpace: 'nowrap'
          }}
        >
          {ann.text}
        </div>
      );
    } else if (ann.type === 'blur') {
      return (
        <div
          key={index}
          style={{
            position: 'absolute',
            left: ann.x,
            top: ann.y,
            width: ann.width,
            height: ann.height,
            backgroundColor: 'rgba(0,0,0,1)',
            borderRadius: '4px',
            pointerEvents: 'none'
          }}
        />
      );
    }
    return null;
  };

  return (
    <div className="annotated-screenshot-wrapper">
      <style>{`
        .annotated-screenshot-wrapper {
          position: relative;
        }
        .annotation-toolbar {
          display: flex;
          gap: 8px;
          padding: 10px;
          background: #f1f5f9;
          border-radius: 8px 8px 0 0;
          border: 1px solid #e2e8f0;
          border-bottom: none;
          flex-wrap: wrap;
          align-items: center;
        }
        .annotation-toolbar button {
          padding: 6px 12px;
          border: none;
          border-radius: 6px;
          background: white;
          cursor: pointer;
          font-size: 13px;
          transition: all 0.2s;
        }
        .annotation-toolbar button:hover {
          background: #e2e8f0;
        }
        .annotation-toolbar button.active {
          background: #f1f5f9;
          color: #64748b;
        }
        .annotation-toolbar .color-picker {
          width: 32px;
          height: 32px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
        }
        .annotation-toolbar .divider {
          width: 1px;
          height: 24px;
          background: #e2e8f0;
          margin: 0 4px;
        }
        .annotation-canvas {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          cursor: crosshair;
        }
        /* Select mode — no drawing tool active. Canvas surfaces the
           default cursor so users don't think everywhere is a
           drawable surface; the crosshair returns when a shape tool
           is picked. */
        .annotation-canvas.tool-select {
          cursor: default;
        }
        /* Compact icon-only toolbar. Hidden by default so the
           screenshot itself is the visual focus of the step, then
           fades in when the user hovers the screenshot. On touch
           devices (no :hover), the always-visible-but-subtle
           fallback below keeps them reachable. */
        .screenshot-edit-toolbar {
          position: absolute;
          top: 8px;
          right: 8px;
          z-index: 10;
          display: flex;
          gap: 4px;
          padding: 3px;
          background: rgba(15, 23, 42, 0.55);
          backdrop-filter: blur(4px);
          -webkit-backdrop-filter: blur(4px);
          border-radius: 8px;
          opacity: 0;
          transform: translateY(-2px);
          transition: opacity 0.15s ease, transform 0.15s ease;
          pointer-events: none;
        }
        .screenshot-container:hover .screenshot-edit-toolbar,
        .screenshot-container:focus-within .screenshot-edit-toolbar {
          opacity: 1;
          transform: translateY(0);
          pointer-events: auto;
        }
        /* Touch devices — reveal the toolbar at low opacity so users
           without a hover state can still find it. */
        @media (hover: none) {
          .screenshot-edit-toolbar { opacity: 0.7; pointer-events: auto; }
        }
        .ss-tool-btn {
          width: 28px;
          height: 28px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          background: transparent;
          color: #fff;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          transition: background 0.12s;
        }
        .ss-tool-btn:hover {
          background: rgba(255, 255, 255, 0.18);
        }
        .ss-tool-btn:focus-visible {
          outline: 2px solid #a5b4fc;
          outline-offset: 1px;
        }
        .ss-tool-btn-danger:hover {
          background: rgba(220, 38, 38, 0.85);
        }
      `}</style>

      {editable && isEditing && (
        <div className="annotation-toolbar">
          {/* Select mode — no drawing tool active. Canvas clicks do
              nothing so the blue element-rect box can be dragged
              without laying down accidental circles/rects. Default
              state when the user first enters annotate mode. */}
          <button className={tool === null ? 'active' : ''} onClick={() => setTool(null)} title="Select — move the click / element boxes">Select</button>
          <div className="divider" />
          <button className={tool === 'circle' ? 'active' : ''} onClick={() => setTool('circle')}>Circle</button>
          <button className={tool === 'rect' ? 'active' : ''} onClick={() => setTool('rect')}>Rectangle</button>
          <button className={tool === 'arrow' ? 'active' : ''} onClick={() => setTool('arrow')}>Arrow</button>
          <button className={tool === 'text' ? 'active' : ''} onClick={() => setTool('text')}>Text</button>
          <button className={tool === 'blur' ? 'active' : ''} onClick={() => setTool('blur')}>Blur</button>
          <div className="divider" />
          <input type="color" className="color-picker" value={color} onChange={(e) => setColor(e.target.value)} />
          <div className="divider" />
          <button onClick={undoAnnotation} disabled={annotations.length === 0}>Undo</button>
          <button onClick={clearAnnotations} disabled={annotations.length === 0}>Clear All</button>
          <div className="divider" />
          <button onClick={() => setIsEditing(false)}>Done</button>
        </div>
      )}

      <div
        className="screenshot-container"
        style={{ position: 'relative', cursor: isEditing ? 'default' : 'zoom-in' }}
        onClick={() => {
          if (!isEditing && onZoom) {
            onZoom();
          }
        }}
      >
        {editable && !isEditing && (
          <div
            className="screenshot-edit-toolbar"
            // Stops the programmatic click that fileInputRef.click()
            // dispatches on the hidden file input from bubbling up to
            // the screenshot-container's onClick (which opens the zoom
            // modal). Same reason we `stopPropagation` on every
            // button below — belt-and-suspenders.
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="ss-tool-btn"
              onClick={(e) => { e.stopPropagation(); setIsEditing(true); }}
              title="Annotate — draw on this screenshot"
              aria-label="Annotate"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20h9"/>
                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/>
              </svg>
            </button>
            {onScreenshotChange && (
              <>
                <button
                  className="ss-tool-btn"
                  onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                  title="Replace this screenshot"
                  aria-label="Replace screenshot"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="23 4 23 10 17 10"/>
                    <polyline points="1 20 1 14 7 14"/>
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                  </svg>
                </button>
                <button
                  className="ss-tool-btn ss-tool-btn-danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    const ok = typeof window !== 'undefined' && typeof window.confirm === 'function'
                      ? window.confirm('Delete the screenshot for this step? The step will still exist — just with no image.')
                      : true;
                    if (!ok) return;
                    onScreenshotChange(null);
                  }}
                  title="Delete this screenshot"
                  aria-label="Delete screenshot"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6"/>
                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                    <path d="M10 11v6M14 11v6"/>
                    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                  </svg>
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    if (!f) return;
                    if (f.size > 8 * 1024 * 1024) {
                      alert('Image is larger than 8 MB — pick a smaller file.');
                      return;
                    }
                    const reader = new FileReader();
                    reader.onload = () => {
                      const dataUrl = String(reader.result || '');
                      if (dataUrl.startsWith('data:image/')) onScreenshotChange(dataUrl);
                    };
                    reader.onerror = () => alert('Could not read the selected file.');
                    reader.readAsDataURL(f);
                  }}
                />
              </>
            )}
          </div>
        )}

        <img
          ref={imgRef}
          src={step.screenshot}
          alt={`Step ${step.number}`}
          className="step-image"
          style={{ borderRadius: isEditing ? '0 0 12px 12px' : '12px' }}
        />

        {/* Manual annotations */}
        {annotations.map((ann, i) => renderAnnotation(ann, i))}

        {/* Click indicator */}
        {step.eventType === 'click' && pos && (
          <>
            {/* Element highlight box — draggable in edit mode so users
                can nudge an off-alignment rect back onto its real UI
                element without re-recording the step. Prefers the
                in-drag rectPos over the stored elementRect while a
                drag is in flight. Hidden while the click indicator is
                being dragged (same as before) so the two controls
                don't fight for cursor focus. */}
            {pos.elementRect && !pos.isDragged && (() => {
              const displayRect = rectPos
                ? { left: rectPos.left, top: rectPos.top, width: rectPos.width, height: rectPos.height }
                : pos.elementRect;
              const isActive = isDraggingRect || !!resizeHandle;
              return (
                <div
                  className={`element-highlight ${isEditing ? 'editable' : ''} ${isActive ? 'dragging' : ''}`}
                  style={{
                    left: displayRect.left,
                    top: displayRect.top,
                    width: displayRect.width,
                    height: displayRect.height,
                    cursor: isEditing ? (isDraggingRect ? 'grabbing' : 'grab') : undefined,
                    pointerEvents: isEditing ? 'auto' : 'none',
                  }}
                  onMouseDown={isEditing && !resizeHandle ? handleRectMouseDown(displayRect) : undefined}
                  title={isEditing ? 'Drag to reposition · use handles to resize' : ''}
                >
                  {/* Eight resize handles — 4 corners + 4 edge
                      midpoints. Each carries its cardinal/inter-
                      cardinal direction so the shared mousemove
                      handler knows which sides to grow/shrink. */}
                  {isEditing && ['nw','n','ne','e','se','s','sw','w'].map((h) => (
                    <div
                      key={h}
                      className={`element-resize-handle rh-${h}`}
                      onMouseDown={handleRectResizeMouseDown(h, displayRect)}
                      title={`Drag to resize (${h.toUpperCase()})`}
                    />
                  ))}
                </div>
              );
            })()}
            {/* Click point indicator - draggable in edit mode */}
            <div
              className={`click-indicator ${isEditing ? 'editable' : ''} ${isDraggingIndicator ? 'dragging' : ''}`}
              style={{ left: pos.clickX, top: pos.clickY }}
              onMouseDown={handleIndicatorMouseDown}
              title={isEditing ? 'Drag to reposition click indicator' : ''}
            >
              {/* Large invisible hit area for easier dragging */}
              {isEditing && <div className="click-hit-area" />}
              <div className="click-callout">
                <div className="click-callout-badge">CLICK HERE</div>
                <div className="click-callout-arrow" />
                <div className="click-callout-dot" />
              </div>
              {isEditing && (
                <div className="drag-handle">
                  <span className="drag-icon">✥</span>
                  <span className="drag-label">Drag to move</span>
                </div>
              )}
            </div>
          </>
        )}

        {/* Keyboard indicator */}
        {step.eventType === 'keyboard' && step.keyboardData && (
          <div className="keyboard-indicator">
            {step.keyboardData.modifiers?.length > 0
              ? `${step.keyboardData.modifiers.join('+')}+${step.keyboardData.key}`
              : step.keyboardData.key}
          </div>
        )}

        {/* Scroll indicator */}
        {step.eventType === 'scroll' && step.scrollData && (
          <div className="scroll-indicator">
            {step.scrollData.direction === 'down' ? Icons.arrowDown : Icons.arrowUp}
          </div>
        )}

        {/* Drawing canvas for editing */}
        {isEditing && (
          <div
            ref={canvasRef}
            className={`annotation-canvas ${(isDraggingIndicator || isDraggingRect || resizeHandle) ? 'dragging-indicator' : ''} ${!tool ? 'tool-select' : ''}`}
            onMouseDown={(isDraggingIndicator || isDraggingRect || resizeHandle) ? null : handleMouseDown}
            onMouseUp={(e) => {
              if (isDraggingIndicator) {
                handleIndicatorMouseUp();
              } else if (isDraggingRect) {
                // no-op — document mouseup handler finishes the rect drag
              } else {
                handleMouseUp(e);
              }
            }}
            onMouseMove={isDraggingIndicator ? handleIndicatorDrag : null}
            onMouseLeave={(e) => {
              if (isDraggingIndicator) {
                handleIndicatorMouseUp();
              } else {
                handleMouseUp(e);
              }
            }}
          />
        )}
      </div>

      {/* Text annotation input modal */}
      {showTextInput && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={() => {
            setShowTextInput(false);
            setPendingTextPos(null);
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: '24px',
              width: '360px',
              maxWidth: '90vw',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px', fontSize: '18px', color: '#1e293b' }}>Add Text Annotation</h3>
            <input
              ref={textInputRef}
              type="text"
              value={textInputValue}
              onChange={(e) => setTextInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleTextSubmit();
                if (e.key === 'Escape') {
                  setShowTextInput(false);
                  setPendingTextPos(null);
                }
              }}
              placeholder="Enter text..."
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e2e8f0',
                borderRadius: '10px',
                fontSize: '15px',
                boxSizing: 'border-box'
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
              <button
                onClick={() => {
                  setShowTextInput(false);
                  setPendingTextPos(null);
                }}
                style={{
                  padding: '10px 20px',
                  borderRadius: '10px',
                  border: '1px solid #e2e8f0',
                  background: 'white',
                  color: '#64748b',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleTextSubmit}
                disabled={!textInputValue.trim()}
                style={{
                  padding: '10px 20px',
                  borderRadius: '10px',
                  border: 'none',
                  background: theme.gradient,
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  opacity: textInputValue.trim() ? 1 : 0.5
                }}
              >
                Add Text
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper to extract domain from URL
const getDomainFromUrl = (url) => {
  if (!url) return null;
  try {
    const urlObj = new URL(url);
    return urlObj.hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
};

// Helper to get favicon URL for a domain
const getFaviconUrl = (domain) => {
  if (!domain) return null;
  const apex = apexDomain(domain);
  // Suppress the S2 request for domains we've already seen 404 this session
  // (see markFaviconFailed). Caller's onError fallback handles the null.
  if (_faviconFailedSet.has(apex)) return null;
  // Route every favicon through Google's S2 service. Direct /favicon.ico
  // requests get blocked by `Cross-Origin-Resource-Policy: same-origin`
  // headers on many modern sites (claude.ai, github, stripe, etc.) and
  // produce ERR_BLOCKED_BY_RESPONSE.NotSameOrigin console spam. The S2
  // proxy handles the fetch server-side and always returns a usable PNG.
  // sz=64 keeps quality reasonable for the 16-32px renders we use.
  // Collapse to the apex domain so custom subdomains (Salesforce orgs,
  // Zendesk instances, etc.) get the recognizable brand icon instead of
  // the generic-globe placeholder S2 returns for unregistered subdomains.
  return `https://www.google.com/s2/favicons?domain=${apex}&sz=64`;
};

// Custom Node Component for React Flow - Vertical Layout with Inline Styles
// Domain color palette - visually distinct, professional colors
const DOMAIN_COLORS = [
  { bg: '#e0e7ff', border: '#6366f1', borderHover: '#4f46e5', badge: 'linear-gradient(135deg, #6366f1, #4f46e5)', light: '#eef2ff' },     // Indigo
  { bg: '#dbeafe', border: '#3b82f6', borderHover: '#2563eb', badge: 'linear-gradient(135deg, #3b82f6, #2563eb)', light: '#eff6ff' },     // Blue
  { bg: '#fce7f3', border: '#ec4899', borderHover: '#db2777', badge: 'linear-gradient(135deg, #ec4899, #db2777)', light: '#fdf2f8' },     // Pink
  { bg: '#d1fae5', border: '#10b981', borderHover: '#059669', badge: 'linear-gradient(135deg, #10b981, #059669)', light: '#ecfdf5' },     // Emerald
  { bg: '#fef3c7', border: '#f59e0b', borderHover: '#d97706', badge: 'linear-gradient(135deg, #f59e0b, #d97706)', light: '#fffbeb' },     // Amber
  { bg: '#e0f2fe', border: '#0ea5e9', borderHover: '#0284c7', badge: 'linear-gradient(135deg, #0ea5e9, #0284c7)', light: '#f0f9ff' },     // Sky
  { bg: '#ede9fe', border: '#8b5cf6', borderHover: '#7c3aed', badge: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', light: '#f5f3ff' },     // Violet
  { bg: '#fae8ff', border: '#d946ef', borderHover: '#c026d3', badge: 'linear-gradient(135deg, #d946ef, #c026d3)', light: '#fdf4ff' },     // Fuchsia
  { bg: '#ccfbf1', border: '#14b8a6', borderHover: '#0d9488', badge: 'linear-gradient(135deg, #14b8a6, #0d9488)', light: '#f0fdfa' },     // Teal
  { bg: '#fee2e2', border: '#ef4444', borderHover: '#dc2626', badge: 'linear-gradient(135deg, #ef4444, #dc2626)', light: '#fef2f2' },     // Red
];

// Event type badge overlays (small indicator, not full node color)
const EVENT_BADGE_COLORS = {
  keyboard: { bg: '#fef3c7', color: '#d97706', icon: '⌨' },
  scroll: { bg: '#d1fae5', color: '#059669', icon: '↕' },
  click: { bg: null, color: null, icon: null },
};

const WorkflowNode = ({ data }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [faviconError, setFaviconError] = useState(false);

  // Use domain color if available, fall back to indigo
  const domainColorIndex = data.domainColorIndex ?? 0;
  const colors = DOMAIN_COLORS[domainColorIndex % DOMAIN_COLORS.length];
  const isDomain = data.domain && data.domain.includes('.');
  // Desktop guides carry the real Windows app icon as a base64 data
  // URL on data.faviconUrl (set by createNodeData → guide.faviconUrl
  // fallback). When that's present, render it directly instead of
  // routing through Google S2 — S2 only works for real hostnames and
  // would 404 for desktop slugs like "excel" or "winword". Web guides
  // (no data: URL) keep using the existing isDomain → getFaviconUrl
  // path unchanged.
  const dataUrlIcon = typeof data.faviconUrl === 'string'
    && data.faviconUrl.startsWith('data:image/')
    ? data.faviconUrl
    : null;

  const nodeStyle = {
    padding: '14px 16px',
    borderRadius: '12px',
    border: `2px solid ${isHovered ? colors.borderHover : colors.border}`,
    backgroundColor: colors.bg,
    cursor: 'pointer',
    width: '220px',
    height: '90px',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    transition: 'all 0.15s ease',
    boxShadow: isHovered
      ? '0 6px 16px -4px rgba(0, 0, 0, 0.15)'
      : '0 2px 4px rgba(0, 0, 0, 0.08)',
    transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
    position: 'relative',
    overflow: 'hidden',
  };

  const appIconStyle = {
    position: 'absolute',
    top: '8px',
    left: '8px',
    width: '20px',
    height: '20px',
    borderRadius: '4px',
    backgroundColor: 'white',
    border: '1px solid #e2e8f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '10px',
    fontWeight: '700',
    color: theme.primary,
  };

  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '4px',
    marginBottom: '6px',
  };

  // Resolve badge color from step type first, then event type, then domain
  // fallback. Keeps parity with the PDF / HTML export step-number colors so
  // a callout step looks like a callout step everywhere (dashboard process
  // flow, HTML export step card, PDF process flow, PDF step card).
  const stepType = data.stepType || 'click';
  let badgeBackground = colors.badge;
  if (stepType === 'rich_text')      badgeBackground = 'linear-gradient(135deg, #818cf8, #6366f1)';
  else if (stepType === 'callout')   badgeBackground = 'linear-gradient(135deg, #fbbf24, #d97706)';
  else if (stepType === 'image')     badgeBackground = 'linear-gradient(135deg, #2dd4bf, #0f766e)';
  else if (stepType === 'audio_note') badgeBackground = 'linear-gradient(135deg, #a78bfa, #7c3aed)';
  else if (stepType === 'section_divider') badgeBackground = 'linear-gradient(135deg, #94a3b8, #475569)';
  else if (data.eventType === 'keyboard') badgeBackground = 'linear-gradient(135deg, #f59e0b, #d97706)';
  else if (data.eventType === 'scroll')   badgeBackground = 'linear-gradient(135deg, #10b981, #059669)';

  const badgeStyle = {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    background: badgeBackground,
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: '700',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
  };

  const labelStyle = {
    fontSize: '13px',
    fontWeight: '600',
    color: '#1e293b',
    lineHeight: '1.4',
    marginBottom: data.domain ? '6px' : '0',
  };

  const domainStyle = {
    fontSize: '10px',
    fontWeight: '500',
    color: '#64748b',
    backgroundColor: 'rgba(255,255,255,0.7)',
    padding: '2px 8px',
    borderRadius: '4px',
    display: 'inline-block',
  };

  return (
    <div
      onClick={data.onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={nodeStyle}
    >
      {/* App icon in top-left corner */}
      {data.domain && (
        <div style={appIconStyle}>
          {(dataUrlIcon || isDomain) && !faviconError ? (
            <img
              src={dataUrlIcon || getFaviconUrl(data.domain)}
              alt=""
              style={{ width: '16px', height: '16px', borderRadius: '2px', objectFit: 'contain' }}
              onError={() => { if (!dataUrlIcon) markFaviconFailed(data.domain); setFaviconError(true); }}
            />
          ) : (
            data.domain.charAt(0).toUpperCase()
          )}
        </div>
      )}
      {/* Event type indicator for keyboard/scroll */}
      {data.eventType && data.eventType !== 'click' && EVENT_BADGE_COLORS[data.eventType] && (
        <div style={{
          position: 'absolute',
          top: '6px',
          right: '6px',
          fontSize: '11px',
          padding: '1px 5px',
          borderRadius: '4px',
          backgroundColor: EVENT_BADGE_COLORS[data.eventType].bg,
          color: EVENT_BADGE_COLORS[data.eventType].color,
          fontWeight: '600',
          lineHeight: '1.4',
        }}>
          {EVENT_BADGE_COLORS[data.eventType].icon}
        </div>
      )}

      {/* React Flow handles - all handles always rendered, visibility based on usage */}
      {/* Target handles */}
      <Handle
        type="target"
        position={Position.Top}
        id="top"
        style={{
          background: data.targetHandle === 'top' ? theme.primary : 'transparent',
          width: 8,
          height: 8,
          border: data.targetHandle === 'top' ? 'none' : 'none',
        }}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="left-target"
        style={{
          background: data.targetHandle === 'left-target' ? theme.primary : 'transparent',
          width: 8,
          height: 8,
          top: '50%',
          border: 'none',
        }}
      />
      <Handle
        type="target"
        position={Position.Right}
        id="right-target"
        style={{
          background: data.targetHandle === 'right-target' ? theme.primary : 'transparent',
          width: 8,
          height: 8,
          top: '50%',
          border: 'none',
        }}
      />

      {/* Source handles */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom"
        style={{
          background: data.sourceHandle === 'bottom' ? theme.primary : 'transparent',
          width: 8,
          height: 8,
          border: 'none',
        }}
      />
      <Handle
        type="source"
        position={Position.Left}
        id="left-source"
        style={{
          background: data.sourceHandle === 'left-source' ? theme.primary : 'transparent',
          width: 8,
          height: 8,
          top: '50%',
          border: 'none',
        }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="right-source"
        style={{
          background: data.sourceHandle === 'right-source' ? theme.primary : 'transparent',
          width: 8,
          height: 8,
          top: '50%',
          border: 'none',
        }}
      />

      <div style={headerStyle}>
        <span style={badgeStyle}>
          {data.stepNumber}
        </span>
      </div>
      <div style={labelStyle}>
        {data.label}
      </div>
      {data.domain && (
        <div style={domainStyle}>
          {data.domain}
        </div>
      )}
    </div>
  );
};

// Navigate to App Node - shown when switching applications
const AppChangeNode = ({ data }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [faviconError, setFaviconError] = useState(false);
  const isDomain = data.toApp && data.toApp.includes('.');

  const nodeStyle = {
    padding: '12px 16px',
    borderRadius: '12px',
    border: `2px solid ${isHovered ? '#0d9488' : '#5eead4'}`,
    backgroundColor: '#f0fdfa',
    cursor: 'default',
    width: '200px',
    height: '90px',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    transition: 'all 0.15s ease',
    boxShadow: isHovered
      ? '0 4px 12px -4px rgba(13, 148, 136, 0.2)'
      : '0 1px 3px rgba(0, 0, 0, 0.05)',
    position: 'relative',
  };

  const iconContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    marginBottom: '6px',
  };

  const switchIconStyle = {
    width: '24px',
    height: '24px',
    borderRadius: '6px',
    background: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 4px rgba(20, 184, 166, 0.3)',
  };

  const appIconStyle = {
    width: '28px',
    height: '28px',
    borderRadius: '6px',
    backgroundColor: 'white',
    border: '1px solid #99f6e4',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: '700',
    color: '#0d9488',
  };

  const labelStyle = {
    fontSize: '11px',
    fontWeight: '500',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '4px',
  };

  const appNameStyle = {
    fontSize: '14px',
    fontWeight: '700',
    color: '#1e293b',
  };

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={nodeStyle}
    >
      {/* React Flow handles for edges */}
      <Handle type="target" position={Position.Top} style={{ background: '#14b8a6', width: 8, height: 8 }} />
      <Handle type="source" position={Position.Bottom} style={{ background: '#14b8a6', width: 8, height: 8 }} />

      <div style={iconContainerStyle}>
        <div style={switchIconStyle}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8L22 12L18 16"/>
            <path d="M2 12H22"/>
          </svg>
        </div>
        <div style={appIconStyle}>
          {isDomain && !faviconError ? (
            <img
              src={getFaviconUrl(data.toApp)}
              alt=""
              style={{ width: '20px', height: '20px', borderRadius: '4px' }}
              onError={(e) => { handleFaviconError(e); setFaviconError(true); }}
            />
          ) : (
            data.toApp ? data.toApp.charAt(0).toUpperCase() : '?'
          )}
        </div>
      </div>
      <div style={labelStyle}>Navigate to</div>
      <div style={appNameStyle}>{data.toApp}</div>
    </div>
  );
};

const nodeTypes = {
  workflowNode: WorkflowNode,
  appChangeNode: AppChangeNode,
};

// Layout options for workflow flowchart
const LAYOUT_OPTIONS = {
  minimized: {
    label: 'Minimized',
    icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="1" y="2" width="4" height="4" rx="1"/><rect x="6" y="2" width="4" height="4" rx="1"/><rect x="11" y="2" width="4" height="4" rx="1"/><rect x="1" y="8" width="4" height="4" rx="1"/><rect x="6" y="8" width="4" height="4" rx="1"/></svg>,
    color: '#8b5cf6',
    activeBg: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
  },
  mixed: {
    label: 'Domain Based',
    icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="1" y="1" width="5" height="14" rx="1.5"/><rect x="8" y="1" width="5" height="8" rx="1.5"/><circle cx="10.5" cy="13" r="1.5" fill="currentColor" stroke="none"/></svg>,
    color: '#6366f1',
    activeBg: 'linear-gradient(135deg, #6366f1, #4f46e5)',
  },
  vertical: {
    label: 'Vertical',
    icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="4" y="1" width="8" height="3" rx="1"/><rect x="4" y="6.5" width="8" height="3" rx="1"/><rect x="4" y="12" width="8" height="3" rx="1"/><line x1="8" y1="4" x2="8" y2="6.5"/></svg>,
    color: '#10b981',
    activeBg: 'linear-gradient(135deg, #10b981, #059669)',
  },
  horizontal: {
    label: 'Horizontal',
    icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="1" y="5" width="3.5" height="6" rx="1"/><rect x="6.25" y="5" width="3.5" height="6" rx="1"/><rect x="11.5" y="5" width="3.5" height="6" rx="1"/><line x1="4.5" y1="8" x2="6.25" y2="8"/></svg>,
    color: '#3b82f6',
    activeBg: 'linear-gradient(135deg, #3b82f6, #2563eb)',
  },
};

// Workflow Flowchart Component - Vertical React Flow with Inline Styles
function WorkflowFlowchart({ steps, guideId, workflowData, guide, onStepClick, onPlanError }) {
  const [summaries, setSummaries] = useState({});
  const [appChanges, setAppChanges] = useState([]); // AI-detected app changes
  const [loading, setLoading] = useState(true);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [layout, setLayout] = useState('minimized'); // minimized, mixed, vertical, horizontal

  // Handle escape key to close fullscreen
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isFullscreen]);

  // Use stored workflow data or fetch from API
  useEffect(() => {
    const loadOrFetchSummaries = async () => {
      if (!guideId || steps.length === 0) {
        setLoading(false);
        return;
      }

      // Check if we have stored workflow data with domain favicon map
      const hasValidCache = workflowData &&
        workflowData.summaries &&
        workflowData.summaries.length > 0 &&
        workflowData.domainFaviconMap; // Require domain map for favicon support

      if (hasValidCache) {
        console.log('[FLOWCHART] Using cached workflow data from database');
        const summaryMap = {};
        workflowData.summaries.forEach(s => {
          summaryMap[s.stepId] = s.summary;
        });
        setSummaries(summaryMap);

        if (workflowData.appChanges && workflowData.appChanges.length > 0) {
          console.log('[FLOWCHART] Using cached app changes:', workflowData.appChanges);
          setAppChanges(workflowData.appChanges);
        }
        setLoading(false);
        return;
      }

      // No cached data, fetch from API (this also saves to database)
      console.log('[FLOWCHART] No cached data, generating workflow summaries...');
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${API_URL}/api/guides/${guideId}/generate-workflow-summaries`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          const summaryMap = {};
          data.summaries.forEach(s => {
            summaryMap[s.stepId] = s.summary;
          });
          setSummaries(summaryMap);

          // Store AI-detected app changes
          if (data.appChanges && data.appChanges.length > 0) {
            console.log('[FLOWCHART] AI detected app changes:', data.appChanges);
            setAppChanges(data.appChanges);
          }
        } else if (onPlanError) {
          await onPlanError(response);
        }
      } catch (error) {
        console.error('Error fetching workflow summaries:', error);
      } finally {
        setLoading(false);
      }
    };

    loadOrFetchSummaries();
  }, [guideId, steps.length, workflowData]);

  // Fallback summary generator
  const getFallbackSummary = useCallback((step) => {
    const desc = step.description || '';
    if (desc) {
      const cleaned = desc
        .replace(/^(Click|Press|Select|Enter|Type|Navigate|Open|Close|Scroll)\s+(the\s+|on\s+|to\s+)?/i, '')
        .split(/[.,!]/)[0]
        .trim();
      return cleaned.length > 30 ? cleaned.substring(0, 27) + '...' : cleaned;
    }
    return `Step ${step.number}`;
  }, []);

  // Build nodes and edges based on selected layout
  useEffect(() => {
    if (loading || steps.length === 0) {
      setNodes([]);
      setEdges([]);
      return;
    }

    const nodeWidth = 220;
    const horizontalGap = 60;
    const verticalGap = 120;
    const colWidth = nodeWidth + horizontalGap;

    const newNodes = [];
    const newEdges = [];

    // Sort steps by step number
    const sortedSteps = [...steps].sort((a, b) => a.number - b.number);

    // Group steps by domain for mixed layout - new column when domain changes
    // For scroll/keyboard events without URL, inherit domain from previous step
    const domainGroups = [];
    let currentGroup = [];
    let lastDomain = null;

    sortedSteps.forEach((step) => {
      const pageUrl = step.clickData?.capturedOnUrl || step.clickData?.pageUrl ||
                      step.scrollData?.capturedOnUrl || step.keyboardData?.capturedOnUrl;
      let domain = getDomainFromUrl(pageUrl);

      // If no domain found (e.g., scroll/keyboard without URL), use previous domain
      if (!domain && lastDomain) {
        domain = lastDomain;
      }

      // Start new column when domain actually changes (and we have a valid new domain)
      if (lastDomain !== null && domain && domain !== lastDomain && currentGroup.length > 0) {
        domainGroups.push({ steps: currentGroup, domain: lastDomain });
        currentGroup = [];
      }

      currentGroup.push(step);
      if (domain) {
        lastDomain = domain;
      }
    });
    if (currentGroup.length > 0) {
      domainGroups.push({ steps: currentGroup, domain: lastDomain });
    }

    let prevNodeId = null;
    let prevSourceHandle = null;

    // Build domain → color index map (assign colors in order of first appearance)
    const domainColorMap = {};
    let colorIdx = 0;
    sortedSteps.forEach(step => {
      const pageUrl = step.clickData?.capturedOnUrl || step.clickData?.pageUrl ||
                      step.scrollData?.capturedOnUrl || step.keyboardData?.capturedOnUrl;
      const d = getDomainFromUrl(pageUrl);
      if (d && !(d in domainColorMap)) {
        domainColorMap[d] = colorIdx++;
      }
    });

    // Desktop-captured guides store the real Windows app icon as a
    // base64 PNG data URL on guide.faviconUrl. The cloud's
    // domainFaviconMap is keyed by the per-step app slug ("excel",
    // "winword") which the backend tries to proxy through Google
    // S2 — those proxies 404 because the slug isn't a real domain.
    // For desktop guides, fall back to the guide-level data URL so
    // every node renders the actual brand icon (Excel green X, etc.)
    // Web-captured guides keep using the per-domain map untouched.
    const desktopGuideIcon = (guide?.source === 'desktop'
        && typeof guide?.faviconUrl === 'string'
        && guide.faviconUrl.startsWith('data:image/'))
      ? guide.faviconUrl
      : null;

    // Helper to create node data
    const createNodeData = (step, sourceHandle, targetHandle) => {
      const pageUrl = step.clickData?.capturedOnUrl || step.clickData?.pageUrl ||
                      step.scrollData?.capturedOnUrl || step.keyboardData?.capturedOnUrl;
      const domain = getDomainFromUrl(pageUrl);
      const stepFaviconUrl = (domain && workflowData?.domainFaviconMap?.[domain]) || desktopGuideIcon;
      return {
        label: summaries[step.id] || getFallbackSummary(step),
        stepNumber: step.number,
        eventType: step.eventType || 'click',
        // Step type drives the badge color in WorkflowNode so manual step
        // types (rich_text / callout / image / audio_note / section_divider)
        // are visually distinguishable from captured clicks.
        stepType: step.stepType || 'click',
        description: step.description,
        domain: domain,
        domainColorIndex: domain ? (domainColorMap[domain] ?? 0) : 0,
        faviconUrl: stepFaviconUrl,
        onClick: () => onStepClick(step.id),
        sourceHandle,
        targetHandle,
        layout, // Pass layout to node for conditional handle rendering
      };
    };

    // Helper to create edge
    const createEdge = (sourceId, targetId, srcHandle, tgtHandle) => ({
      id: `e-${sourceId}-${targetId}`,
      source: sourceId,
      target: targetId,
      sourceHandle: srcHandle,
      targetHandle: tgtHandle,
      type: 'smoothstep',
      animated: true,
      style: { stroke: theme.primary, strokeWidth: 2 },
      markerEnd: { type: MarkerType.ArrowClosed, color: theme.primary, width: 12, height: 12 },
    });

    if (layout === 'vertical') {
      // Full Vertical Layout - single column
      sortedSteps.forEach((step, index) => {
        const isFirst = index === 0;
        const isLast = index === sortedSteps.length - 1;
        const sourceHandle = isLast ? null : 'bottom';
        const targetHandle = isFirst ? null : 'top';

        newNodes.push({
          id: step.id,
          type: 'workflowNode',
          position: { x: 0, y: index * verticalGap },
          data: createNodeData(step, sourceHandle, targetHandle),
        });

        if (prevNodeId) {
          newEdges.push(createEdge(prevNodeId, step.id, 'bottom', 'top'));
        }
        prevNodeId = step.id;
      });

    } else if (layout === 'horizontal') {
      // Full Horizontal Layout - single row
      sortedSteps.forEach((step, index) => {
        const isFirst = index === 0;
        const isLast = index === sortedSteps.length - 1;
        const sourceHandle = isLast ? null : 'right-source';
        const targetHandle = isFirst ? null : 'left-target';

        newNodes.push({
          id: step.id,
          type: 'workflowNode',
          position: { x: index * colWidth, y: 0 },
          data: createNodeData(step, sourceHandle, targetHandle),
        });

        if (prevNodeId) {
          newEdges.push(createEdge(prevNodeId, step.id, 'right-source', 'left-target'));
        }
        prevNodeId = step.id;
      });

    } else if (layout === 'mixed') {
      // Mixed Layout - vertical within same domain, horizontal on domain change
      let xOffset = 0;
      let globalIndex = 0;
      const totalSteps = domainGroups.reduce((sum, g) => sum + g.steps.length, 0);

      domainGroups.forEach((group, groupIndex) => {
        group.steps.forEach((step, stepIndex) => {
          const yPos = stepIndex * verticalGap;
          const isLastInGroup = stepIndex === group.steps.length - 1;
          const isLastGroup = groupIndex === domainGroups.length - 1;
          const isFirst = globalIndex === 0;
          const isLast = globalIndex === totalSteps - 1;

          // Source: null if last node, bottom if more steps in group, right if domain change
          let sourceHandle = null;
          if (!isLast) {
            if (isLastInGroup && !isLastGroup) {
              sourceHandle = 'right-source';
            } else {
              sourceHandle = 'bottom';
            }
          }

          // Target: null if first node, top if not first in group, left if first in group after domain change
          let targetHandle = null;
          if (!isFirst) {
            if (stepIndex === 0 && groupIndex > 0) {
              targetHandle = 'left-target';
            } else {
              targetHandle = 'top';
            }
          }

          newNodes.push({
            id: step.id,
            type: 'workflowNode',
            position: { x: xOffset, y: yPos },
            data: createNodeData(step, sourceHandle, targetHandle),
          });

          if (prevNodeId) {
            newEdges.push(createEdge(prevNodeId, step.id, prevSourceHandle, targetHandle));
          }

          prevNodeId = step.id;
          prevSourceHandle = sourceHandle;
          globalIndex++;
        });

        // Move right for next domain group
        xOffset += colWidth;
      });

    } else {
      // Minimized Layout (default) - snake pattern with 5 nodes per row
      const nodesPerRow = 5;
      const rowHeight = 120;

      sortedSteps.forEach((step, nodeIndex) => {
        const row = Math.floor(nodeIndex / nodesPerRow);
        const colInRow = nodeIndex % nodesPerRow;
        const isEvenRow = row % 2 === 0;
        const col = isEvenRow ? colInRow : (nodesPerRow - 1 - colInRow);

        const xPos = col * colWidth;
        const yPos = row * rowHeight;

        const isFirstNode = nodeIndex === 0;
        const isFirstInRow = colInRow === 0;
        const isLastInRow = colInRow === nodesPerRow - 1;
        const isLastNode = nodeIndex === sortedSteps.length - 1;

        let sourceHandle = null;
        let targetHandle = null;

        // Source handle (outgoing) - null for last node
        if (!isLastNode) {
          if (isLastInRow) {
            sourceHandle = 'bottom';
          } else {
            sourceHandle = isEvenRow ? 'right-source' : 'left-source';
          }
        }

        // Target handle (incoming) - null for first node
        if (!isFirstNode) {
          if (isFirstInRow) {
            targetHandle = 'top';
          } else {
            targetHandle = isEvenRow ? 'left-target' : 'right-target';
          }
        }

        newNodes.push({
          id: step.id,
          type: 'workflowNode',
          position: { x: xPos, y: yPos },
          data: createNodeData(step, sourceHandle, targetHandle),
        });

        if (prevNodeId && prevSourceHandle) {
          newEdges.push(createEdge(prevNodeId, step.id, prevSourceHandle, targetHandle));
        }

        prevNodeId = step.id;
        prevSourceHandle = sourceHandle;
      });
    }

    setNodes(newNodes);
    setEdges(newEdges);
  }, [loading, steps, summaries, appChanges, workflowData, getFallbackSummary, onStepClick, setNodes, setEdges, layout]);

  // Calculate container height based on layout
  const containerHeight = useMemo(() => {
    const verticalGap = 120;
    const rowHeight = 120;
    const nodesPerRow = 5;

    if (layout === 'vertical') {
      const height = steps.length * verticalGap + 80;
      return Math.min(Math.max(250, height), 800);
    } else if (layout === 'horizontal') {
      return 200;
    } else if (layout === 'mixed') {
      // Find max steps in any domain group
      let maxStepsInGroup = 1;
      let currentGroupSize = 0;
      let lastDomain = null;
      const sortedSteps = [...steps].sort((a, b) => a.number - b.number);
      sortedSteps.forEach((step) => {
        const pageUrl = step.clickData?.capturedOnUrl || step.clickData?.pageUrl ||
                        step.scrollData?.capturedOnUrl || step.keyboardData?.capturedOnUrl;
        let domain = getDomainFromUrl(pageUrl);
        // Inherit domain from previous step if not available
        if (!domain && lastDomain) {
          domain = lastDomain;
        }
        if (lastDomain !== null && domain && domain !== lastDomain && currentGroupSize > 0) {
          maxStepsInGroup = Math.max(maxStepsInGroup, currentGroupSize);
          currentGroupSize = 0;
        }
        currentGroupSize++;
        if (domain) {
          lastDomain = domain;
        }
      });
      maxStepsInGroup = Math.max(maxStepsInGroup, currentGroupSize);
      const height = maxStepsInGroup * verticalGap + 80;
      return Math.min(Math.max(250, height), 600);
    } else {
      // Minimized - 5 nodes per row
      const numRows = Math.ceil(steps.length / nodesPerRow);
      const height = numRows * rowHeight + 80;
      return Math.min(Math.max(250, height), 600);
    }
  }, [steps, layout]);

  const containerStyle = {
    backgroundColor: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '16px',
    marginBottom: '28px',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  };

  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 24px',
    background: 'linear-gradient(to right, #f8fafc, #f1f5f9)',
    borderBottom: '1px solid #e2e8f0',
  };

  const titleStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '13px',
    fontWeight: '600',
    color: '#475569',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  };

  const badgeStyle = {
    background: theme.gradient,
    color: 'white',
    fontSize: '12px',
    fontWeight: '600',
    padding: '6px 16px',
    borderRadius: '9999px',
    boxShadow: '0 2px 4px rgba(102, 126, 234, 0.3)',
  };

  const loadingStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '192px',
    backgroundColor: '#f8fafc',
    color: '#64748b',
    fontSize: '14px',
    gap: '12px',
  };

  const spinnerStyle = {
    width: '20px',
    height: '20px',
    border: '2px solid #e2e8f0',
    borderTopColor: theme.primary,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  };

  const canvasStyle = {
    backgroundColor: '#fafbfe',
    backgroundImage: 'radial-gradient(circle, #e2e8f0 1px, transparent 1px)',
    backgroundSize: '20px 20px',
    height: containerHeight,
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <h3 style={titleStyle}>
          <svg style={{ width: '20px', height: '20px', color: theme.primary }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 3v18M12 3l-4 4M12 3l4 4M12 21l-4-4M12 21l4-4"/>
          </svg>
          Process Flow
          <span style={badgeStyle}>
            {steps.length} Steps
          </span>
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Layout selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '3px', backgroundColor: '#f1f5f9', borderRadius: '10px', padding: '3px' }}>
            {Object.entries(LAYOUT_OPTIONS).map(([key, { label, icon, color, activeBg }]) => (
              <button
                key={key}
                onClick={() => setLayout(key)}
                title={label}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: layout === key ? '6px 12px' : '6px 8px',
                  border: 'none',
                  borderRadius: '8px',
                  background: layout === key ? activeBg : 'transparent',
                  cursor: 'pointer',
                  color: layout === key ? 'white' : '#94a3b8',
                  fontSize: '11px',
                  fontWeight: '600',
                  transition: 'all 0.2s ease',
                  boxShadow: layout === key ? `0 2px 8px ${color}40` : 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                {icon}
                {layout === key && <span>{label}</span>}
              </button>
            ))}
          </div>
          <button
            onClick={() => setIsFullscreen(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '32px',
              height: '32px',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              backgroundColor: 'white',
              cursor: 'pointer',
              color: '#64748b',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f1f5f9';
              e.target.style.color = theme.primary;
              e.target.style.borderColor = theme.primary;
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'white';
              e.target.style.color = '#64748b';
              e.target.style.borderColor = '#e2e8f0';
            }}
            title="View fullscreen"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
            </svg>
          </button>
          {/* Download as PNG — hits the backend renderer so the downloaded
              image is identical to what the PDF export embeds. Placed AFTER
              the fullscreen button per user request. */}
          <button
            onClick={async () => {
              try {
                const token = localStorage.getItem('authToken');
                const r = await fetch(`${API_URL}/api/guides/${guideId}/flow-image`, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                const blob = await r.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `process-flow.png`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
              } catch (err) {
                console.error('Flow image download failed:', err);
              }
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '32px',
              height: '32px',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              backgroundColor: 'white',
              cursor: 'pointer',
              color: '#64748b',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f1f5f9';
              e.target.style.color = theme.primary;
              e.target.style.borderColor = theme.primary;
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'white';
              e.target.style.color = '#64748b';
              e.target.style.borderColor = '#e2e8f0';
            }}
            title="Download as image"
          >
            {/* Stroke-width 1.75 + rounded caps match the visual weight of
                the fullscreen-brackets icon next to it. Arrow shaft + head
                share the same path so they read as one continuous stroke. */}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 4v12m0 0l-5-5m5 5l5-5"/>
              <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Flow Canvas */}
      {loading ? (
        <div style={loadingStyle}>
          <div style={spinnerStyle} />
          Generating workflow summary...
        </div>
      ) : (
        <div style={canvasStyle}>
          <ReactFlow
            key={`${layout}-${steps.length}`}
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            onNodeClick={(event, node) => {
              if (node.data && node.data.onClick) {
                node.data.onClick();
              }
            }}
            onInit={(reactFlowInstance) => {
              // Fit view after a short delay to ensure all nodes are rendered
              setTimeout(() => {
                reactFlowInstance.fitView({ padding: 0.2, includeHiddenNodes: true });
              }, 100);
            }}
            fitView
            fitViewOptions={{ padding: 0.2, minZoom: 0.1, maxZoom: 1, includeHiddenNodes: true }}
            minZoom={0.1}
            maxZoom={1.5}
            nodesDraggable={true}
            nodesConnectable={false}
            elementsSelectable={false}
            panOnDrag={true}
            zoomOnScroll={true}
            zoomOnPinch={true}
            zoomOnDoubleClick={false}
            preventScrolling={true}
            proOptions={{ hideAttribution: true }}
          >
            <Background color="#e2e8f0" gap={20} size={1} />
            <Controls
              showZoom={true}
              showFitView={true}
              showInteractive={false}
              position="top-right"
              style={{
                backgroundColor: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
            />
            <MiniMap
              nodeColor={(node) => node.type === 'appChangeNode' ? '#14b8a6' : theme.primary}
              maskColor="rgba(248, 250, 252, 0.8)"
              style={{
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                width: 120,
                height: 80,
              }}
              position="bottom-right"
              pannable
              zoomable
            />
          </ReactFlow>
        </div>
      )}

      {/* Keyframe animation and spinner styles (global) */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .btn-spinner {
          display: inline-block;
          width: 14px;
          height: 14px;
          border: 2.5px solid rgba(255,255,255,0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          vertical-align: middle;
          flex-shrink: 0;
        }
        .btn-spinner-sm {
          display: inline-block;
          width: 14px;
          height: 14px;
          border: 2.5px solid #e2e8f0;
          border-top-color: ${theme.primary};
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          vertical-align: middle;
          flex-shrink: 0;
        }
      `}</style>

      {/* Fullscreen Modal */}
      {isFullscreen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsFullscreen(false);
          }}
        >
          {/* Modal Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 24px',
            backgroundColor: 'white',
            borderBottom: '1px solid #e2e8f0',
          }}>
            <h3 style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: '16px',
              fontWeight: '600',
              color: '#1e293b',
              margin: 0,
            }}>
              <svg style={{ width: '24px', height: '24px', color: theme.primary }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 3v18M12 3l-4 4M12 3l4 4M12 21l-4-4M12 21l4-4"/>
              </svg>
              Process Flow
              <span style={{
                background: theme.gradient,
                color: 'white',
                fontSize: '12px',
                fontWeight: '600',
                padding: '4px 12px',
                borderRadius: '9999px',
              }}>
                {steps.length} Steps
              </span>
            </h3>
            {/* Layout selector in fullscreen */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '3px', backgroundColor: '#f1f5f9', borderRadius: '10px', padding: '3px' }}>
              {Object.entries(LAYOUT_OPTIONS).map(([key, { label, icon, color, activeBg }]) => (
                <button
                  key={key}
                  onClick={() => setLayout(key)}
                  title={label}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: layout === key ? '6px 12px' : '6px 8px',
                    border: 'none',
                    borderRadius: '8px',
                    background: layout === key ? activeBg : 'transparent',
                    cursor: 'pointer',
                    color: layout === key ? 'white' : '#94a3b8',
                    fontSize: '11px',
                    fontWeight: '600',
                    transition: 'all 0.2s ease',
                    boxShadow: layout === key ? `0 2px 8px ${color}40` : 'none',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {icon}
                  {layout === key && <span>{label}</span>}
                </button>
              ))}
            </div>
            <button
              onClick={() => setIsFullscreen(false)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '40px',
                height: '40px',
                border: 'none',
                borderRadius: '8px',
                backgroundColor: '#f1f5f9',
                cursor: 'pointer',
                color: '#64748b',
                fontSize: '20px',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#fee2e2';
                e.target.style.color = '#ef4444';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#f1f5f9';
                e.target.style.color = '#64748b';
              }}
              title="Close (Esc)"
            >
              ✕
            </button>
          </div>

          {/* Fullscreen Flow Canvas */}
          <div style={{ flex: 1, backgroundColor: '#f8fafc' }}>
            <ReactFlow
              key={`fullscreen-${layout}-${steps.length}`}
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              nodeTypes={nodeTypes}
              onNodeClick={(event, node) => {
                if (node.data && node.data.onClick) {
                  setIsFullscreen(false);
                  setTimeout(() => node.data.onClick(), 100);
                }
              }}
              onInit={(reactFlowInstance) => {
                setTimeout(() => {
                  reactFlowInstance.fitView({ padding: 0.15, includeHiddenNodes: true });
                }, 100);
              }}
              fitView
              fitViewOptions={{ padding: 0.15, minZoom: 0.1, maxZoom: 1.5, includeHiddenNodes: true }}
              minZoom={0.1}
              maxZoom={2}
              nodesDraggable={true}
              nodesConnectable={false}
              elementsSelectable={false}
              panOnDrag={true}
              zoomOnScroll={true}
              zoomOnPinch={true}
              zoomOnDoubleClick={false}
              preventScrolling={true}
              proOptions={{ hideAttribution: true }}
            >
              <Background color="#e2e8f0" gap={20} size={1} />
              <Controls
                showZoom={true}
                showFitView={true}
                showInteractive={false}
                position="top-right"
                style={{
                  backgroundColor: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                }}
              />
              <MiniMap
                nodeColor={(node) => node.type === 'appChangeNode' ? '#14b8a6' : theme.primary}
                maskColor="rgba(248, 250, 252, 0.8)"
                style={{
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  width: 150,
                  height: 100,
                }}
                position="bottom-right"
                pannable
                zoomable
              />
            </ReactFlow>
          </div>

          {/* Keyboard hint */}
          <div style={{
            padding: '8px 16px',
            backgroundColor: 'white',
            borderTop: '1px solid #e2e8f0',
            textAlign: 'center',
            fontSize: '12px',
            color: '#64748b',
          }}>
            Press <kbd style={{ padding: '2px 6px', backgroundColor: '#f1f5f9', borderRadius: '4px', fontFamily: 'monospace' }}>Esc</kbd> to close • Click a node to navigate to step
          </div>
        </div>
      )}
    </div>
  );
}
